/**
 * @file adminTranslations.ts
 * @description Dedicated Venue Admin UI translations covering all 19 supported languages.
 * Designed for restaurant & venue managers using clear, non-technical hospitality terms.
 * Completely independent of public venue and guest menu localization.
 */

import type { Language } from './i18n';

export type AdminPhraseDictionary = Record<Language, Record<string, string>>;

export const adminPhraseTranslations: AdminPhraseDictionary = {
  en: {
    '123 Main St, City': '123 Main St, City',
    'Balance': 'Balance',
    'Lifetime': 'Lifetime',
    'Source': 'Source',
    'Menu': 'Menu',
    'Advanced': 'Advanced',
    'Core Suite': 'Core Suite',
    'Privacy': 'Privacy',
    'Support': 'Support',
    'Save Layout': 'Save Layout',
    'Shape:': 'Shape:',
    'SEARCH LIBRARY...': 'SEARCH LIBRARY...',
    'Search library...': 'Search library...',
    'None': 'None',
    'Type': 'Type',
    'Target': 'Target',
    'Value / Target': 'Value / Target',
    'London (GMT/BST)': 'London (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tokyo (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Start Color',
    'Depth Shade': 'Depth Shade',
    'Matrix Geometry': 'Matrix Geometry',
    'Eye Architecture': 'Eye Architecture',
    'Brand Centerpiece': 'Brand Centerpiece',
    'Scannability Shield': 'Scannability Shield',
    'Environment Blur': 'Environment Blur',
    'Color Target:': 'Color Target:',
    'DPI:': 'DPI:',
    '3mm Bleed Box': '3mm Bleed Box',
    '300 DPI': '300 DPI',
    'PRO+': 'PRO+',
    'SAML 2.0': 'SAML 2.0',
    'OpenID Connect': 'OpenID Connect',
    'IdP Entity ID': 'IdP Entity ID',
    'SSO URL': 'SSO URL',
    'X.509 Certificate': 'X.509 Certificate',
    'ACS URL': 'ACS URL',
    'Entity ID': 'Entity ID',
    'Navigate': 'Navigate',
    'Select': 'Select',
    'OmniSearch v1.0': 'OmniSearch v1.0',
    'Back to order review': 'Back to order review',
    'Return to Intelligence Studio': 'Return to Intelligence Studio',
    'View Full Notification Center': 'View Full Notification Center',
    'Read all': 'Read all',
    'Cancel department': 'Cancel department',
    'Save department': 'Save department',
    'Venue Segmentation': 'Venue Segmentation',
    'Collaborate on your menu together.': 'Collaborate on your menu together.',
    'Timeline': 'Timeline',
    'Calendar': 'Calendar',
    'Book': 'Book',
    'Duration': 'Duration',
    'Time & Duration': 'Time & Duration',
    'Seating Preference': 'Seating Preference',
    'Section': 'Section',
    'Min Capacity': 'Min Capacity',
    'Max Capacity': 'Max Capacity',
    'Phone (Optional)': 'Phone (Optional)',
    'Name or Walk-in': 'Name or Walk-in',
    'Next day': 'Next day',
    'Next month': 'Next month',
    'Live Connection': 'Live Connection',
    'Live Inventory': 'Live Inventory',
    'Main Venue (Full Menu)': 'Main Venue (Full Menu)',
    'Preview Menu': 'Preview Menu',
    'Manual Setup Guide': 'Manual Setup Guide',
    'Connected & Active': 'Connected & Active',
    'CTA Action': 'CTA Action',
    'Phone Contact': 'Phone Contact',
    'Menu URL': 'Menu URL',
    'Google Location': 'Google Location',
    'Field': 'Field',
    'Linked': 'Linked',
    'Synchronizing Hub...': 'Synchronizing Hub...',
    'Data Integrity': 'Data Integrity',
    'Station Governance Suite': 'Station Governance Suite',
    'Open audit log': 'Open audit log',
    'Signal / Time': 'Signal / Time',
    'Identity': 'Identity',
    'Invitation Invalid': 'Invitation Invalid',
    'Invited To': 'Invited To',
    'Team Limit Reached': 'Team Limit Reached',
    'Venue Limit Reached': 'Venue Limit Reached',
    'Venue Setup Required': 'Venue Setup Required',
    'Create Venue': 'Create Venue',
    'TableGreet Onboarding': 'TableGreet Onboarding',
    'No payment required': 'No payment required',
    'Selected Plan': 'Selected Plan',
    'Payment Method': 'Payment Method',
    'Invoice ID': 'Invoice ID',
    'Verified': 'Verified',
    'Accent color': 'Accent color',
    'Set the voice and overall light level.': 'Set the voice and overall light level.',
    'Layout architecture': 'Layout architecture',
    'Navigation & motion': 'Navigation & motion',
    'Loading preview engine': 'Loading preview engine',
    'Real Live Preview': 'Real Live Preview',
    'Included with Pro & Max': 'Included with Pro & Max',
    'Daypart Scheduling': 'Daypart Scheduling',
    'Operational Daypart Architecture': 'Operational Daypart Architecture',
    'Service Protocol': 'Service Protocol',
    'Configuration Engine': 'Configuration Engine',
    'Linked Department': 'Linked Department',
    'Team Hub': 'Team Hub',
    'Team Size': 'Team Size',
    'Joined': 'Joined',
    'Cancel Invitation': 'Cancel Invitation',
    'Initialize New Organization': 'Initialize New Organization',
    'Operational': 'Operational',
    'Pro Feature': 'Pro Feature',
    'Current State': 'Current State',
    'Open on the big TV': 'Open on the big TV',
    'Menu Board': 'Menu Board',
    'Reward': 'Reward',
    'Reward name': 'Reward name',
    'Cancel & refund': 'Cancel & refund',
    'Code': 'Code',
    'Find': 'Find',
    'Complete': 'Complete',
    'Seated': 'Seated',
    'No-Show': 'No-Show',
    'Walk-in': 'Walk-in',
    'Party': 'Party',
    'Turnover': 'Turnover',
    'We couldn\'t load the menu': 'We couldn\'t load the menu',
    'Venue Not Found': 'Venue Not Found',
    'Live Availability': 'Live Availability',
    'Multi-Language Suite': 'Multi-Language Suite',
    'Key name': 'Key name',
    'Revoke API key': 'Revoke API key',
    'Delete webhook': 'Delete webhook',
    'Identity Provider': 'Identity Provider',
    'Provider': 'Provider',
    'Mandatory MFA': 'Mandatory MFA',
    'Account Identity': 'Account Identity',
    'Login Email': 'Login Email',
    'New Login Email': 'New Login Email',
    'Phone Login': 'Phone Login',
    'SMS Verification Code': 'SMS Verification Code',
    'Integration Method': 'Integration Method',
    'Stripe Connect': 'Stripe Connect',
    'Stripe Secret Key': 'Stripe Secret Key',
    'Stripe Webhook Secret': 'Stripe Webhook Secret',
    'Client-Owned': 'Client-Owned',
    'Verify connection': 'Verify connection',
    'Setup Health': 'Setup Health',
    'Privacy Protected': 'Privacy Protected',
    'Privacy Policy': 'Privacy Policy',
    'Continue': 'Continue',
    'Sign In': 'Sign In',
    'Didn\'t get it?': 'Didn\'t get it?',
    'Logged In Account': 'Logged In Account',
    'Annual': 'Annual',
    'Unread': 'Unread',
    'Starred': 'Starred',
    'High / Urgent': 'High / Urgent',
    'Mark Read': 'Mark Read',
    'Delete notification': 'Delete notification',
    'Urgent': 'Urgent',
    'High': 'High',
    'Normal': 'Normal',
    'Low': 'Low',
    'Select a Ticket': 'Select a Ticket',
    'Urgent (Immediate attention)': 'Urgent (Immediate attention)',
    'Type your reply here...': 'Type your reply here...',
    'Print Card (PDF)': 'Print Card (PDF)',
    'Advanced Printing Engine': 'Advanced Printing Engine',
    'Multi-Venue Governance': 'Multi-Venue Governance',
    'Network Revenue': 'Network Revenue',
    'Live Network Total': 'Live Network Total',
    'Audit Log Hub': 'Audit Log Hub',
    'Export Log': 'Export Log',
    'CUSTOM ROLE SCHEMAS': 'CUSTOM ROLE SCHEMAS',
    'Unique internal role identifier.': 'Unique internal role identifier.',
    'Human-friendly role label.': 'Human-friendly role label.',
    'Marketing Hub': 'Marketing Hub',
    'Revenue Generation Suite': 'Revenue Generation Suite',
    'Active Campaign Limit Reached (3/3)': 'Active Campaign Limit Reached (3/3)',
    'Banner Content': 'Banner Content',
    'Style Profile': 'Style Profile',
    'Action URL (Optional)': 'Action URL (Optional)',
    'Promotion Label': 'Promotion Label',
    'Target Selection': 'Target Selection',
    'Edit Promotion': 'Edit Promotion',
    'Delete Promotion': 'Delete Promotion',
    'Loyalty Engine': 'Loyalty Engine',
    'Reward catalog': 'Reward catalog',
    'Add reward': 'Add reward',
    'Loyalty ID': 'Loyalty ID',
    'No point activity yet': 'No point activity yet',
    'Scan or enter QR value': 'Scan or enter QR value',
    'TableGreet Admin': 'TableGreet Admin',
    'Capacity Pacing': 'Capacity Pacing',
    'Cancellation Policy': 'Cancellation Policy',
    'Booking Experience': 'Booking Experience',
    'Floor Occupancy': 'Floor Occupancy',
    'Currently Seated': 'Currently Seated',
    'Seat Party': 'Seat Party',
    'Seat Table': 'Seat Table',
    'Select Table': 'Select Table',
    'Table / Capacity': 'Table / Capacity',
    'Table Name/Number': 'Table Name/Number',
    'Mark Arrived': 'Mark Arrived',
    'Mark No-Show': 'Mark No-Show',
    'Complete Dining': 'Complete Dining',
    'Standard Dining (Default)': 'Standard Dining (Default)',
    'Walk-In Dining Log': 'Walk-In Dining Log',
    'Notification Center': 'Notification Center',
    'Single Sign-On': 'Single Sign-On',
    'Welcome Aboard!': 'Welcome Aboard!',
    'Select your TableGreet plan': 'Select your TableGreet plan',
    'Free Plan Activation': 'Free Plan Activation',
    'Venue Created!': 'Venue Created!',
    'Upgrade for Unlimited': 'Upgrade for Unlimited',
    'Auto-Tag Menu': 'Auto-Tag Menu',
    'Start Auto-Tagging': 'Start Auto-Tagging',
    'Launch Auto-Tagger': 'Launch Auto-Tagger',
    'Launch PDF Importer': 'Launch PDF Importer',
    'Analyzing Menu...': 'Analyzing Menu...',
    'Analyzing Recipe...': 'Analyzing Recipe...',
    'Manual Availability': 'Manual Availability',
    'Technical Breakdown': 'Technical Breakdown',
    'Dietary Taxonomy': 'Dietary Taxonomy',
    'Dietary & Allergen AI': 'Dietary & Allergen AI',
    'Inherit from Category': 'Inherit from Category',
    'Ingredient Library': 'Ingredient Library',
    'Ingredient Name': 'Ingredient Name',
    'Menu Organization': 'Menu Organization',
    'Inventory Taxonomy Hub': 'Inventory Taxonomy Hub',
    'Category Intelligence': 'Category Intelligence',
    'Service Period': 'Service Period',
    'Manual Configuration / All Day': 'Manual Configuration / All Day',
    'Operational Department': 'Operational Department',
    'Global / No Department': 'Global / No Department',
    'Cancel category': 'Cancel category',
    'New category name...': 'New category name...',
    'Save category': 'Save category',
    'QR Matrix Only': 'QR Matrix Only',
    'Vector & PDF': 'Vector & PDF',
    'Vector QR (SVG)': 'Vector QR (SVG)',
    'Vector QR (PDF)': 'Vector QR (PDF)',
    'Save QR Style': 'Save QR Style',
    'Share Link': 'Share Link',
    'High Fidelity 300DPI': 'High Fidelity 300DPI',
    'Generating 300 DPI PDF...': 'Generating 300 DPI PDF...',
    'Give your venue a point of view.': 'Give your venue a point of view.',
    'Signage Menu Not Found': 'Signage Menu Not Found',
    'Scan Menu': 'Scan Menu',
    'Open on your phone': 'Open on your phone',
    'Full Screen': 'Full Screen',
    'Lock TV board': 'Lock TV board',
    'Big-Screen Menu': 'Big-Screen Menu',
    'TV Signage': 'TV Signage',
    'General Information': 'General Information',
    'Venue Name *': 'Venue Name *',
    'Public Slug *': 'Public Slug *',
    'Venue Logo': 'Venue Logo',
    'Hero Cover Image': 'Hero Cover Image',
    'Venue Timezone': 'Venue Timezone',
    'Primary Brand Color': 'Primary Brand Color',
    'TableGreet footer credit': 'TableGreet footer credit',
    'White-Label Branding': 'White-Label Branding',
    'Client DNS Setup': 'Client DNS Setup',
    'Copy CNAME target': 'Copy CNAME target',
    'Copy TableGreet URL': 'Copy TableGreet URL',
    'Included TableGreet URL': 'Included TableGreet URL',
    'Open TableGreet URL': 'Open TableGreet URL',
    'Public menu': 'Public menu',
    'Public venue delivery': 'Public venue delivery',
    'Zone name': 'Zone name',
    'Coverage note': 'Coverage note',
    'Default ETA min': 'Default ETA min',
    'Default ETA max': 'Default ETA max',
    'Packaging fee': 'Packaging fee',
    'Courier handled by': 'Courier handled by',
    'Third-party courier': 'Third-party courier',
    'Unified Experience': 'Unified Experience',
    'Save WiFi to Venue': 'Save WiFi to Venue',
    'Network Name (SSID)': 'Network Name (SSID)',
    'Audio Alert Chime': 'Audio Alert Chime',
    'Time Format': 'Time Format',
    'Auto / Default': 'Auto / Default',
    'Syncing SEO & Google Data...': 'Syncing SEO & Google Data...',
    'Google Connection': 'Google Connection',
    'Search Engine Metadata': 'Search Engine Metadata',
    'Social card preview': 'Social card preview',
    'Allow Indexing': 'Allow Indexing',
    'Canonical URL Override': 'Canonical URL Override',
    'Price Range': 'Price Range',
    'Latitude': 'Latitude',
    'Longitude': 'Longitude',
    'Google Place ID / Location ID': 'Google Place ID / Location ID',
    'Google Search Preview': 'Google Search Preview',
    'Linked Google Location': 'Linked Google Location',
    'SEO Documentation': 'SEO Documentation',
    ', and': ', and',
    'and': 'and',
    'Delete Department?': 'Delete Department?',
    'Delete Department': 'Delete Department',
    'Live URL': 'Live URL',
    'Copy full live URL': 'Copy full live URL',
    'Open live department menu': 'Open live department menu',
    'Loading departments...': 'Loading departments...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Multi-department venue segmentations are premium features available on PRO and above.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Segment your menu by rooms, tables, bars, or physical locations.',
    'Targeted Ordering': 'Targeted Ordering',
    'Route guests and orders to specific preparation areas.': 'Route guests and orders to specific preparation areas.',
    'Operational Departments': 'Operational Departments',
    'OPERATIONAL DEPARTMENTS': 'OPERATIONAL DEPARTMENTS',
    'Venue Segmentation Hub': 'Venue Segmentation Hub',
    'VENUE SEGMENTATION HUB': 'VENUE SEGMENTATION HUB',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Create departments to group your menu sections. For example, a large venue might have a',
    'Restaurant': 'Restaurant',
    'Poolside': 'Poolside',
    'department, each with its own categories.': 'department, each with its own categories.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.',
    'Department name': 'Department name',
    'New department name...': 'New department name...',
    'No Linked Daypart (Always Available)': 'No Linked Daypart (Always Available)',
    'Linked Daypart': 'Linked Daypart',
    'Localizations': 'Localizations',
    'LOCALIZATIONS': 'LOCALIZATIONS',
    'AI Auto-Translate': 'AI Auto-Translate',
    'AI AUTO-TRANSLATE': 'AI AUTO-TRANSLATE',
    'Translated name': 'Translated name',
    'No Departments Yet': 'No Departments Yet',
    'Add First Department': 'Add First Department',
    'Avg Order Value': 'Avg Order Value',
    'AVG ORDER VALUE': 'AVG ORDER VALUE',
    'item taps': 'item taps',
    'ITEM TAPS': 'ITEM TAPS',
    'Avg Rating': 'Avg Rating',
    'AVG RATING': 'AVG RATING',
    'Revenue (Last 7 Days)': 'Revenue (Last 7 Days)',
    'REVENUE (LAST 7 DAYS)': 'REVENUE (LAST 7 DAYS)',
    'Daily breakdown': 'Daily breakdown',
    'DAILY BREAKDOWN': 'DAILY BREAKDOWN',
    'Menu Views (Last 7 Days)': 'Menu Views (Last 7 Days)',
    'MENU VIEWS (LAST 7 DAYS)': 'MENU VIEWS (LAST 7 DAYS)',
    'Guest traffic': 'Guest traffic',
    'GUEST TRAFFIC': 'GUEST TRAFFIC',
    'Most Viewed Items': 'Most Viewed Items',
    'MOST VIEWED ITEMS': 'MOST VIEWED ITEMS',
    'Top Rated Items': 'Top Rated Items',
    'TOP RATED ITEMS': 'TOP RATED ITEMS',
    'Waiter Requests': 'Waiter Requests',
    'WAITER REQUESTS': 'WAITER REQUESTS',
    'Total': 'Total',
    'TOTAL': 'TOTAL',
    'Order Status (30 days)': 'Order Status (30 days)',
    'ORDER STATUS (30 DAYS)': 'ORDER STATUS (30 DAYS)',
    'Recent Guest Feedback': 'Recent Guest Feedback',
    'RECENT GUEST FEEDBACK': 'RECENT GUEST FEEDBACK',
    'Conversion Rate': 'Conversion Rate',
    'CONVERSION RATE': 'CONVERSION RATE',
    'of menu visitors placed an order': 'of menu visitors placed an order',
    'Refreshed': 'Refreshed',
    'REFRESHED': 'REFRESHED',
    'Last 30 days': 'Last 30 days',
    'LAST 30 DAYS': 'LAST 30 DAYS',
    'cash': 'cash',
    'Cash': 'Cash',
    'CASH': 'CASH',
    'bill': 'bill',
    'Bill': 'Bill',
    'BILL': 'BILL',
    'water': 'water',
    'Water': 'Water',
    'WATER': 'WATER',
    'service': 'service',
    'Service': 'Service',
    'SERVICE': 'SERVICE',
    'No interaction data yet': 'No interaction data yet',
    'No ratings collected yet': 'No ratings collected yet',
    'No requests in period': 'No requests in period',
    'No feedback collected yet': 'No feedback collected yet',
    'No menu views recorded yet': 'No menu views recorded yet',
    'Dish Rating Breakdown': 'Dish Rating Breakdown',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.',
    'Customer Sentiment & Reviews': 'Customer Sentiment & Reviews',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.',
    'Reviews AI': 'Reviews AI',
    'Guest Feedback': 'Guest Feedback',
    'Upgrade to unlock rating analytics →': 'Upgrade to unlock rating analytics →',
    'Upgrade to Max': 'Upgrade to Max',
    'UPGRADE TO MAX': 'UPGRADE TO MAX',
    'MAX PLAN': 'MAX PLAN',
    'No Data Yet': 'No Data Yet',
    'Share your QR code to start collecting real venue insights.': 'Share your QR code to start collecting real venue insights.',
    'Revenue Analytics': 'Revenue Analytics',
    'REVENUE ANALYTICS': 'REVENUE ANALYTICS',
    'Track your earnings, orders, and average ticket values over time.': 'Track your earnings, orders, and average ticket values over time.',
    'Operational Velocity': 'Operational Velocity',
    'OPERATIONAL VELOCITY': 'OPERATIONAL VELOCITY',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Monitor guest traffic, popular menu items, and waiter assistance load.',
    'Upgrade to Pro': 'Upgrade to Pro',
    'UPGRADE TO PRO': 'UPGRADE TO PRO',
    'Loading Insights…': 'Loading Insights…',
    'Loading Insights...': 'Loading Insights...',
    'Telemetry Unavailable': 'Telemetry Unavailable',
    'TELEMETRY UNAVAILABLE': 'TELEMETRY UNAVAILABLE',
    'No Data Captured': 'No Data Captured',
    'NO DATA CAPTURED': 'NO DATA CAPTURED',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Share your QR code to start collecting enterprise-grade analytics in real-time.',
    'INTELLIGENCE STUDIO': 'INTELLIGENCE STUDIO',
    'Insights Engine Locked': 'Insights Engine Locked',
    'INSIGHTS ENGINE LOCKED': 'INSIGHTS ENGINE LOCKED',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Advanced trend analysis, bulk governance commands, and data exports are reserved for',
    'Unlock Pro Features': 'Unlock Pro Features',
    'UNLOCK PRO FEATURES': 'UNLOCK PRO FEATURES',
    'MENU VIEWS': 'MENU VIEWS',
    'ITEM INTERACTIONS': 'ITEM INTERACTIONS',
    'GUEST RATING': 'GUEST RATING',
    'DELIVERY REVENUE': 'DELIVERY REVENUE',
    'AVERAGE DELIVERY ORDER': 'AVERAGE DELIVERY ORDER',
    'AVERAGE DELIVERY FEE': 'AVERAGE DELIVERY FEE',
    'INCLUDING FEES': 'INCLUDING FEES',
    'ZONE AVERAGE': 'ZONE AVERAGE',
    'TRAFFIC PROJECTIONS': 'TRAFFIC PROJECTIONS',
    'TELEMETRY SIGNAL TRACKING': 'TELEMETRY SIGNAL TRACKING',
    'VEGETARIAN': 'VEGETARIAN',
    'VEGAN': 'VEGAN',
    'SPICY': 'SPICY',
    'GLUTEN-FREE': 'GLUTEN-FREE',
    'Reset: All In-Stock': 'Reset: All In-Stock',
    'Emergency Stop (All Out)': 'Emergency Stop (All Out)',
    'No compliance issues': 'No compliance issues',
    'NO COMPLIANCE ISSUES': 'NO COMPLIANCE ISSUES',
    'detected in current menu': 'detected in current menu',
    'DETECTED IN CURRENT MENU': 'DETECTED IN CURRENT MENU',
    'Telemetry History (24h)': 'Telemetry History (24h)',
    'TELEMETRY HISTORY (24H)': 'TELEMETRY HISTORY (24H)',
    'Telemetry History (30d)': 'Telemetry History (30d)',
    'TELEMETRY HISTORY (30D)': 'TELEMETRY HISTORY (30D)',
    'Save': 'Save',
    'Saved': 'Saved',
    'Saving...': 'Saving...',
    'Save Changes': 'Save Changes',
    'Save Settings': 'Save Settings',
    'Cancel': 'Cancel',
    'Close': 'Close',
    'Create': 'Create',
    'Add': 'Add',
    'Edit': 'Edit',
    'Delete': 'Delete',
    'Remove': 'Remove',
    'Update': 'Update',
    'Refresh': 'Refresh',
    'Search': 'Search',
    'Search...': 'Search...',
    'Search items...': 'Search items...',
    'Search categories...': 'Search categories...',
    'Filter': 'Filter',
    'All': 'All',
    'Export': 'Export',
    'Import': 'Import',
    'Download': 'Download',
    'Upload': 'Upload',
    'Copy': 'Copy',
    'Copied': 'Copied',
    'Copied!': 'Copied!',
    'Preview': 'Preview',
    'Open': 'Open',
    'View': 'View',
    'Manage': 'Manage',
    'Configure': 'Configure',
    'Enable': 'Enable',
    'Disable': 'Disable',
    'Enabled': 'Enabled',
    'Disabled': 'Disabled',
    'Active': 'Active',
    'Inactive': 'Inactive',
    'Available': 'Available',
    'Unavailable': 'Unavailable',
    'Visible': 'Visible',
    'Hidden': 'Hidden',
    'Required': 'Required',
    'Optional': 'Optional',
    'Confirm': 'Confirm',
    'Back': 'Back',
    'Next': 'Next',
    'Done': 'Done',
    'Upgrade': 'Upgrade',
    'Sign Out': 'Sign Out',
    'Exit to Main Admin': 'Exit to Main Admin',
    'Change Language': 'Change Language',
    'Add Category': 'Add Category',
    'Add Menu Item': 'Add Menu Item',
    'Add Item': 'Add Item',
    'Add Ingredient': 'Add Ingredient',
    'Add Department': 'Add Department',
    'Delete Item': 'Delete Item',
    'Delete Category': 'Delete Category',
    'Upload Image': 'Upload Image',
    'Upload Photo': 'Upload Photo',
    'Select Category': 'Select Category',
    'Select Department': 'Select Department',
    'Add translation...': 'Add translation...',
    'Clear': 'Clear',
    'Reset': 'Reset',
    'Apply': 'Apply',
    'Submit': 'Submit',
    'Core & Operations': 'Core & Operations',
    'Menu & Experience': 'Menu & Experience',
    'Enterprise & Security': 'Enterprise & Security',
    'System & Control': 'System & Control',
    'Intelligence Studio': 'Intelligence Studio',
    'Venue Insights': 'Venue Insights',
    'Reservations': 'Reservations',
    'Floor Plan': 'Floor Plan',
    'Live Waitlist': 'Live Waitlist',
    'Walk-Ins': 'Walk-Ins',
    'Walk-ins': 'Walk-ins',
    'Departments': 'Departments',
    'Service Hours': 'Service Hours',
    'Categories': 'Categories',
    'Menu Categories': 'Menu Categories',
    'Menu Items': 'Menu Items',
    'Items': 'Items',
    'Ingredients': 'Ingredients',
    'Loyalty Program': 'Loyalty Program',
    'Campaign Studio': 'Campaign Studio',
    'Signage': 'Signage',
    'QR Code': 'QR Code',
    'Kitchen Ops': 'Kitchen Ops',
    'Theme Engine': 'Theme Engine',
    'Governance': 'Governance',
    'Audit Log': 'Audit Log',
    'API & Webhooks': 'API & Webhooks',
    'SSO & Security': 'SSO & Security',
    'Team': 'Team',
    'Billing': 'Billing',
    'SEO & Google': 'SEO & Google',
    'Notifications': 'Notifications',
    'Support Center': 'Support Center',
    'Settings': 'Settings',
    'Name': 'Name',
    'Description': 'Description',
    'Price': 'Price',
    'Status': 'Status',
    'Actions': 'Actions',
    'Role': 'Role',
    'Email': 'Email',
    'Phone': 'Phone',
    'Address': 'Address',
    'City': 'City',
    'Currency': 'Currency',
    'Language': 'Language',
    'Theme': 'Theme',
    'General': 'General',
    'Total Revenue': 'Total Revenue',
    'Total Orders': 'Total Orders',
    'Average Order': 'Average Order',
    'Active Sessions': 'Active Sessions',
    'Recent Orders': 'Recent Orders',
    'Live Orders': 'Live Orders',
    'Table': 'Table',
    'Date': 'Date',
    'Time': 'Time',
    'Amount': 'Amount',
    'Customer': 'Customer',
    'In Stock': 'In Stock',
    'Out of Stock': 'Out of Stock',
    'Sold Out': 'Sold Out',
    'Dine In': 'Dine In',
    'Pickup': 'Pickup',
    'Delivery': 'Delivery',
    'Paid': 'Paid',
    'Unpaid': 'Unpaid',
    'Pending': 'Pending',
    'Preparing': 'Preparing',
    'Ready': 'Ready',
    'Completed': 'Completed',
    'Cancelled': 'Cancelled',
    'Loading...': 'Loading...',
    'No data': 'No data',
    'No results found': 'No results found',
    'No items found': 'No items found',
    'No translations defined yet': 'No translations defined yet',
    'Are you sure?': 'Are you sure?',
    'This action cannot be undone.': 'This action cannot be undone.',
    'Settings saved successfully!': 'Settings saved successfully!',
    'Administrator': 'Administrator',
    'Manager': 'Manager',
    'Owner': 'Owner',
    'Chef': 'Chef',
    'Waiter': 'Waiter',
    'Cashier': 'Cashier',
    'Type a command or search...': 'Type a command or search...',
    'Command Palette': 'Command Palette',
    'Demand Velocity': 'Demand Velocity',
    'DEMAND VELOCITY': 'DEMAND VELOCITY',
    'Top Performing Entities': 'Top Performing Entities',
    'TOP PERFORMING ENTITIES': 'TOP PERFORMING ENTITIES',
    'Hourly Load Analysis': 'Hourly Load Analysis',
    'HOURLY LOAD ANALYSIS': 'HOURLY LOAD ANALYSIS',
    'Bulk Command Center': 'Bulk Command Center',
    'BULK COMMAND CENTER': 'BULK COMMAND CENTER',
    'Governance Scan': 'Governance Scan',
    'GOVERNANCE SCAN': 'GOVERNANCE SCAN',
    'No compliance issues detected in current menu': 'No compliance issues detected in current menu',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU',
    'Procedural Tagging': 'Procedural Tagging',
    'PROCEDURAL TAGGING': 'PROCEDURAL TAGGING',
    'Name match...': 'Name match...',
    '...Name match': '...Name match',
    'Healthy': 'Healthy',
    'HEALTHY': 'HEALTHY',
    'Apply Tags': 'Apply Tags',
    'APPLY TAGS': 'APPLY TAGS',
    'Global Price Shift': 'Global Price Shift',
    'GLOBAL PRICE SHIFT': 'GLOBAL PRICE SHIFT',
    'Adjust': 'Adjust',
    'ADJUST': 'ADJUST',
    'Availability Override': 'Availability Override',
    'AVAILABILITY OVERRIDE': 'AVAILABILITY OVERRIDE',
    'Reset: ALL IN-STOCK': 'Reset: ALL IN-STOCK',
    'RESET: ALL IN-STOCK': 'RESET: ALL IN-STOCK',
    'EMERGENCY STOP (ALL OUT)': 'EMERGENCY STOP (ALL OUT)',
    'Integrity Engine': 'Integrity Engine',
    'INTEGRITY ENGINE': 'INTEGRITY ENGINE',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.',
    'Inventory Audit Export': 'Inventory Audit Export',
    'INVENTORY AUDIT EXPORT': 'INVENTORY AUDIT EXPORT',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Generate a comprehensive menu performance and metadata snapshot for offline auditing.',
    'Enterprise Data Export': 'Enterprise Data Export',
    'ENTERPRISE DATA EXPORT': 'ENTERPRISE DATA EXPORT',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.',
    'JSON Snapshot': 'JSON Snapshot',
    'JSON SNAPSHOT': 'JSON SNAPSHOT',
    'Compile CSV Report': 'Compile CSV Report',
    'COMPILE CSV REPORT': 'COMPILE CSV REPORT',
    'Date Segment': 'Date Segment',
    'DATE SEGMENT': 'DATE SEGMENT',
    'Inbound Views': 'Inbound Views',
    'INBOUND VIEWS': 'INBOUND VIEWS',
    'Asset Interactions': 'Asset Interactions',
    'ASSET INTERACTIONS': 'ASSET INTERACTIONS',
    'Telemetry History': 'Telemetry History',
    'TELEMETRY HISTORY': 'TELEMETRY HISTORY',
    'Telemetry History (7d)': 'Telemetry History (7d)',
    'TELEMETRY HISTORY (7D)': 'TELEMETRY HISTORY (7D)',
    'Growth Trajectory': 'Growth Trajectory',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Your menu interaction frequency is trending upward in the current 7-day rolling window.',
    'Deep Analysis Engine': 'Deep Analysis Engine',
    'DEEP ANALYSIS ENGINE': 'DEEP ANALYSIS ENGINE',
    'Asset Velocity': 'Asset Velocity',
    'ASSET VELOCITY': 'ASSET VELOCITY',
    'Traffic Projections': 'Traffic Projections',
    'Telemetry Signal Tracking': 'Telemetry Signal Tracking',
    'Service Hour Update': 'Service Hour Update',
    'Late Night Lounge Hours': 'Late Night Lounge Hours',
    'Bar & Lounge Hours': 'Bar & Lounge Hours',
    'Breakfast & Brunch Hours': 'Breakfast & Brunch Hours',
    'Switch Location': 'Switch Location',
    'SWITCH LOCATION': 'SWITCH LOCATION',
    'Global Admin': 'Global Admin',
    'GLOBAL ADMIN': 'GLOBAL ADMIN',
    'Staff Access': 'Staff Access',
    'STAFF ACCESS': 'STAFF ACCESS',
    'Venue Overview': 'Venue Overview',
    'VENUE OVERVIEW': 'VENUE OVERVIEW',
    'Unified Telemetry Engine': 'Unified Telemetry Engine',
    'UNIFIED TELEMETRY ENGINE': 'UNIFIED TELEMETRY ENGINE',
    'Pulse': 'Pulse',
    'PULSE': 'PULSE',
    'Insights': 'Insights',
    'INSIGHTS': 'INSIGHTS',
    'Reports': 'Reports',
    'REPORTS': 'REPORTS',
    'GOVERNANCE': 'GOVERNANCE',
    'Multi-Venue': 'Multi-Venue',
    'MULTI-VENUE': 'MULTI-VENUE',
    'Menu Views': 'Menu Views',
    'Item Interactions': 'Item Interactions',
    'Guest Rating': 'Guest Rating',
    'Revenue (7d)': 'Revenue (7d)',
    'Revenue (24h)': 'Revenue (24h)',
    'Revenue (30d)': 'Revenue (30d)',
    'Delivery orders (7d)': 'Delivery orders (7d)',
    'Delivery orders (24h)': 'Delivery orders (24h)',
    'Delivery orders (30d)': 'Delivery orders (30d)',
    'Delivery revenue': 'Delivery revenue',
    'Average delivery order': 'Average delivery order',
    'Average delivery fee': 'Average delivery fee',
    'Zone average': 'Zone average',
    'Including fees': 'Including fees',
    'No deliveries': 'No deliveries',
    'No orders': 'No orders',
    'No reviews': 'No reviews',
    'Live': 'Live',
    'LIVE': 'LIVE',
    'reviews': 'reviews',
    'orders': 'orders',
    'deliveries': 'deliveries',
    'of orders': 'of orders',
    'rate': 'rate',
    'Recent Activity': 'Recent Activity',
    'Real-time Venue Telemetry': 'Real-time Venue Telemetry',
    'REAL-TIME VENUE TELEMETRY': 'REAL-TIME VENUE TELEMETRY',
    'Rapid Pulse View': 'Rapid Pulse View',
    'Anonymous Guest': 'Anonymous Guest',
    'scanned menu': 'scanned menu',
    'viewed item': 'viewed item',
    'interacted with': 'interacted with',
    'placed order': 'placed order',
    'searched menu': 'searched menu',
    'Main Landing': 'Main Landing',
    'Trace': 'Trace',
    'TRACE': 'TRACE',
    'Gaining Telemetry...': 'Gaining Telemetry...',
    'No recent traffic detected': 'No recent traffic detected',
    'Activities will appear here as guests scan your QR code': 'Activities will appear here as guests scan your QR code',
    'Analyze All Logs': 'Analyze All Logs',
    'Just now': 'Just now',
    'less than a minute ago': 'less than a minute ago',
    '1 minute ago': '1 minute ago',
    '1 hour ago': '1 hour ago',
    'about 1 hour ago': 'about 1 hour ago',
    '1 day ago': '1 day ago',
    'DAY AGO 1': 'DAY AGO 1',
    '2 days ago': '2 days ago',
    'DAY AGO 2': 'DAY AGO 2',
    '3 days ago': '3 days ago',
    'DAY AGO 3': 'DAY AGO 3',
    '4 days ago': '4 days ago',
    'DAY AGO 4': 'DAY AGO 4',
    '5 days ago': '5 days ago',
    'DAY AGO 5': 'DAY AGO 5',
    '6 days ago': '6 days ago',
    'DAY AGO 6': 'DAY AGO 6',
    '7 days ago': '7 days ago',
    'DAY AGO 7': 'DAY AGO 7',
    'about 1 month ago': 'about 1 month ago',
    'Monday': 'Monday',
    'Tuesday': 'Tuesday',
    'Wednesday': 'Wednesday',
    'Thursday': 'Thursday',
    'Friday': 'Friday',
    'Saturday': 'Saturday',
    'Sunday': 'Sunday',
    'Mon': 'Mon',
    'Tue': 'Tue',
    'Wed': 'Wed',
    'Thu': 'Thu',
    'Fri': 'Fri',
    'Sat': 'Sat',
    'Sun': 'Sun',
    'Opening Hours': 'Opening Hours',
    'Start Time': 'Start Time',
    'End Time': 'End Time',
    'Opening Time': 'Opening Time',
    'Closing Time': 'Closing Time',
    'All Day': 'All Day',
    'Closed': 'Closed',
    'Active Days': 'Active Days',
    'Active Schedule': 'Active Schedule',
    'Always Available': 'Always Available',
    'Always Visible': 'Always Visible',
    'New Category': 'New Category',
    'Category': 'Category',
    'Category Name': 'Category Name',
    'Category Description': 'Category Description',
    'Display Order': 'Display Order',
    'Item Name': 'Item Name',
    'Item Description': 'Item Description',
    'Base Price': 'Base Price',
    'Preparation Time': 'Preparation Time',
    'Calories': 'Calories',
    'Featured Item': 'Featured Item',
    'Chef\'s Recommendation': 'Chef\'s Recommendation',
    'Spicy': 'Spicy',
    'Vegetarian': 'Vegetarian',
    'Vegan': 'Vegan',
    'Gluten-Free': 'Gluten-Free',
    'Halal': 'Halal',
    'Kosher': 'Kosher',
    'Contains Nuts': 'Contains Nuts',
    'Dairy-Free': 'Dairy-Free',
    'Allergen Exclusions': 'Allergen Exclusions',
    'Table Number': 'Table Number',
    'Assigned Table': 'Assigned Table',
    'Seats': 'Seats',
    'Capacity': 'Capacity',
    'Indoor': 'Indoor',
    'Outdoor': 'Outdoor',
    'Terrace': 'Terrace',
    'Bar': 'Bar',
    'Add Table': 'Add Table',
    'Edit Table': 'Edit Table',
    'Delete Table': 'Delete Table',
    'Bookings': 'Bookings',
    'Add to Waitlist': 'Add to Waitlist',
    'Guest Name': 'Guest Name',
    'Party Size': 'Party Size',
    'Arrival Time': 'Arrival Time',
    'Special Requests': 'Special Requests',
    'Confirmed': 'Confirmed',
    'No Show': 'No Show',
    'Add Staff Member': 'Add Staff Member',
    'Invite Staff': 'Invite Staff',
    'Email Address': 'Email Address',
    'Phone Number': 'Phone Number',
    'Download QR Code': 'Download QR Code',
    'Print Table Stands': 'Print Table Stands',
    'Today': 'Today',
    'Yesterday': 'Yesterday',
    'This Week': 'This Week',
    'This Month': 'This Month',
    'Last 30 Days': 'Last 30 Days',
    'Access Denied': 'Access Denied',
    'Advanced Settings': 'Advanced Settings',
    'Change Image': 'Change Image',
    'Remove Image': 'Remove Image',
    'Announcement Banner': 'Announcement Banner',
    'Please fill in all required fields.': 'Please fill in all required fields.',
    'Changes saved successfully': 'Changes saved successfully',
    '(You)': '(You)',
    'AI Auto-Tag': 'AI Auto-Tag',
    'Analyzing:': 'Analyzing:',
    'Apple (Fruit/Vegan)': 'Apple (Fruit/Vegan)',
    'Cancel editing': 'Cancel editing',
    'Capacity:': 'Capacity:',
    'Carrot (Veggie)': 'Carrot (Veggie)',
    'CircleDot (Organic)': 'CircleDot (Organic)',
    'Coffee (Caffeine)': 'Coffee (Caffeine)',
    'Configuration': 'Configuration',
    'Copy verification record name': 'Copy verification record name',
    'Copy verification record value': 'Copy verification record value',
    'Critical exception intercepted': 'Critical exception intercepted',
    'Kernel Panic': 'System Error',
    'DNA (Organic/Bio)': 'DNA (Organic/Bio)',
    'Egg (Ovo/Allergen)': 'Egg (Ovo/Allergen)',
    'Empty': 'Empty',
    'Engraving...': 'Generating...',
    'Flame (Spicy)': 'Flame (Spicy)',
    'Free': 'Free',
    'From': 'From',
    'Generating': 'Generating',
    'Governance & Traceability Studio': 'Venue Management & Activity Hub',
    'Greet': 'Welcome',
    'Heart (Healthy)': 'Heart (Healthy)',
    'In-App': 'In-App',
    'Infinity SVG': 'Vector SVG (Unlimited)',
    'Leaf (Default)': 'Leaf (Default)',
    'MAX Feature': 'Enterprise Feature',
    'Milk (Dairy)': 'Milk (Dairy)',
    'Network': 'Network',
    'Notify SMS': 'SMS Notification',
    'Order': 'Order',
    'Original': 'Original',
    'Overview': 'Overview',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Standard Roles',
    'Shield (Safety)': 'Shield (Safety)',
    'Soup (Warm/Soup)': 'Soup (Warm/Soup)',
    'Standard Pro View': 'Standard Display',
    'Star': 'Star',
    'Synchronized with Venue Identity': 'Styled to match your venue',
    'Synchronizing Security Context...': 'Connecting securely...',
    'Targeted Timing': 'Service Schedule',
    'Targeting': 'Applying to',
    'To': 'To',
    'Toggle Navigation': 'Toggle Navigation',
    'Venue': 'Venue',
    'Venue Governance Suite': 'Venue Operations & Settings',
    'Volume:': 'Volume:',
    'WHITEBOARD NOTE...': 'Staff Note or memo...',
    'Warning (Alert)': 'Warning (Alert)',
    'Wheat (Flour/Gluten)': 'Wheat (Flour/Gluten)',
    'Wine (Alcohol)': 'Wine (Alcohol)',
    'YouTube/Vimeo/Direct Link': 'Video URL (YouTube, Vimeo or MP4)',
    'Your name': 'Your name',
    '2. Table Number': '2. Table Number',
    'for Morocco or': 'for Morocco, or',
    'in': 'in',
    'to': 'to',
    'e.g. 12 or Patio-1': 'e.g. 12 or Patio-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...',
    'e.g. Head Sommelier & Wine Lead': 'e.g. Head Sommelier & Wine Lead',
    'e.g. London': 'e.g. London',
    'e.g. SOMMELIER, HOST, BARISTA': 'e.g. SOMMELIER, HOST, BARISTA',
    'e.g. Skyline Lounge': 'e.g. Skyline Lounge',
    'e.g. TRUFFLE OIL': 'e.g. TRUFFLE OIL',
    'e.g. The Grand Cafe': 'e.g. The Grand Cafe',
    'e.g. Window preference, high chair': 'e.g. Window preference, high chair',
    'Copied to clipboard!': 'Copied to clipboard!',
    'Copy failed.': 'Copy failed.',
    'Link copied!': 'Link copied!',
    'Logo uploaded': 'Logo uploaded',
    'Cover image uploaded': 'Cover image uploaded',
    'Profile saved and synced successfully': 'Profile saved and synced successfully',
    'Category Created': 'Category Created',
    'Category Updated': 'Category Updated',
    'Category Deleted': 'Category Deleted',
    'Item Created': 'Item Created',
    'Item Updated': 'Item Updated',
    'Item Deleted': 'Item Deleted',
    'Menu Published': 'Menu Published',
    'Delete Category?': 'Delete Category?',
    'Delete Menu Item?': 'Delete Menu Item?',
    'Delete Ingredient?': 'Delete Ingredient?',
    'Delete Service Period?': 'Delete Service Period?',
    'Remove team member?': 'Remove team member?',
    'Cancel invitation?': 'Cancel invitation?',
    'Revoke API key?': 'Revoke API key?',
    'Delete webhook endpoint?': 'Delete webhook endpoint?',
    'Clear all notifications?': 'Clear all notifications?',
    'Disconnect Stripe account?': 'Disconnect Stripe account?',
    'Disconnect Google Business Profile?': 'Disconnect Google Business Profile?',
    'Campaigns published successfully!': 'Campaigns published successfully!',
    'Changes successfully synchronized with Google Business Profile!': 'Changes successfully synchronized with Google Business Profile!',
    'Google Business Profile successfully connected!': 'Google Business Profile successfully connected!',
    'Google Business Profile disconnected.': 'Google Business Profile disconnected.',
    'Google Post draft saved successfully!': 'Google Post draft saved successfully!',
    'Post draft deleted.': 'Post draft deleted.',
    'Review response published successfully!': 'Review response published successfully!',
    'SEO configuration saved successfully!': 'SEO configuration saved successfully!',
    'SEO metadata translated across all languages!': 'SEO metadata translated across all languages!',
    'Venue profile & bio translated across all languages!': 'Venue profile & bio translated across all languages!',
    'Reservation configuration saved successfully': 'Reservation configuration saved successfully',
    'Stripe settings saved successfully.': 'Stripe settings saved successfully.',
    'Print-Ready PDF downloaded successfully': 'Print-Ready PDF downloaded successfully',
    'Vector QR PDF downloaded successfully': 'Vector QR PDF downloaded successfully',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Enterprise 300 DPI Pre-Press Package generated successfully',
    'At least one language must be enabled.': 'At least one language must be enabled.',
    'Enter a non-zero percentage before adjusting prices.': 'Enter a non-zero percentage before adjusting prices.',
    'Please enter a Venue Name before translating.': 'Please enter a Venue Name before translating.',
    'Please enter an item name before running dietary and allergen analysis.': 'Please enter an item name before running dietary and allergen analysis.',
    'Please write a reply before submitting.': 'Please write a reply before submitting.',
    'Table 3 Called Waiter': 'Table 3 Called Waiter',
    'Table Call Bell': 'Table Call Bell',
    'Waiter Calls': 'Waiter Calls',
    'Waiter Dispatch & Calls': 'Waiter Dispatch & Calls',
    'Kitchen Display & Whiteboard': 'Kitchen Display & Whiteboard',
    'Customer Orders': 'Customer Orders',
    'Bakery': 'Bakery',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Café / Coffee Shop',
    'Fast Food': 'Fast Food',
    'Gourmet / Fine Dining': 'Gourmet / Fine Dining',
    'Hotel': 'Hotel',
    'General Local Business': 'General Local Business',
    'Classic': 'Classic',
    'Clean': 'Clean',
    'Modern': 'Modern',
    'Elegant': 'Elegant',
    'Energetic': 'Energetic',
    'Glass': 'Glass',
    'Playful & Casual': 'Playful & Casual',
    'Sophisticated & Gourmet': 'Sophisticated & Gourmet',
    'Warm & Welcoming': 'Warm & Welcoming',
    'Executive': 'Executive',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Upgrade required: Multi-language menus are available on the Pro plan.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.',
    'Add Menu Categories & Items': 'Add Menu Categories & Items',
    'Add Menu Items': 'Add Menu Items',
    'Organize your menu sections and food items.': 'Organize your menu sections and food items.',
    'Add and manage your food & beverage offerings.': 'Add and manage your food & beverage offerings.',
    'Customize Venue Branding': 'Customize Venue Branding',
    'Set your brand colors, logo, and presentation.': 'Set your brand colors, logo, and presentation.',
    'Get Your QR Code & Go Live': 'Get Your QR Code & Go Live',
    'Download table QR codes and preview your live menu.': 'Download table QR codes and preview your live menu.',
    'Account & Venue Created Successfully!': 'Account & Venue Created Successfully!',
    '⚡ Off-Peak Hours': '⚡ Off-Peak Hours',
    '🍽️ Lunch Crowd': '🍽️ Lunch Crowd',
    '🚀 Weekend Rush': '🚀 Weekend Rush',
    '🌟 Seasonal Special': '🌟 Seasonal Special',
    '📣 Engagement Buzz': '📣 Engagement Buzz',
    'Festive & Energetic': 'Festive & Energetic',
    'Urgent & High-Conversion': 'Urgent & High-Conversion',
    'Generated 3 tailored campaign strategies': 'Generated 3 tailored campaign strategies',
    'Could not generate campaign ideas. Please try again.': 'Could not generate campaign ideas. Please try again.',
    'Venue Themes': 'Venue Themes',
    'Choose the character of your public menu': 'Choose the character of your public menu',
    'Glassmorphism & Depth': 'Glassmorphism & Depth',
    'Independent XML Sitemap': 'Independent XML Sitemap',
    'Structured Schema & Sync': 'Structured Schema & Sync',
    'Improve how your venue appears on Google and in local search results.': 'Improve how your venue appears on Google and in local search results.',
    'Terms of Service': 'Terms of Service',
    'Confirm your email': 'Confirm your email',
    'Check your spam / junk folder': 'Check your spam / junk folder',
    'Commercial pre-press print production package': 'Commercial pre-press print production package',
    'Over-bleed safety margin': 'Over-bleed safety margin',
    'Crop Marks': 'Crop Marks',
    'Guillotine cutting guides': 'Guillotine cutting guides',
    'Global Banners': 'Global Banners',
    'Featured Items': 'Featured Items',
    'AI Assistant': 'AI Assistant',
    'Custom Role Schemas': 'Custom Role Schemas',
    'Notification Preferences': 'Notification Preferences',
    'Preferences saved automatically': 'Preferences saved automatically',
    'Menu Extractor (PDF & Images)': 'Menu Extractor (PDF & Images)',
    'Multi-Language Translation': 'Multi-Language Translation',
    'Start Menu Translation': 'Start Menu Translation',
    'Menu Items Hub': 'Menu Items Hub',
    'Open Menu Items': 'Open Menu Items',
    'Enterprise Traceability': 'Enterprise Traceability',
    'Full History': 'Full History',
    'Billing & Plans': 'Billing & Plans',
    'Subscription Governance': 'Subscription Governance',
    'Platform Usage': 'Platform Usage',
    'Menu Page Views': 'Menu Page Views',
    'Enterprise Identity': 'Enterprise Identity',
    'Access Governance': 'Access Governance',
    'Required Fields *': 'Required Fields *',
    'Enabled Languages': 'Enabled Languages',
    'Venue Info & Bio Localizations': 'Venue Info & Bio Localizations',
    'Public Address': 'Public Address',
    'Public URL Slug': 'Public URL Slug',
    'Contact Phone': 'Contact Phone',
    'Business Address': 'Business Address',
    'No Categories Yet': 'No Categories Yet',
    'No Custom Roles Configured': 'No Custom Roles Configured',
    'No Google reviews retrieved yet': 'No Google reviews retrieved yet',
    'No Guests Found': 'No Guests Found',
    'No Menu Items Yet': 'No Menu Items Yet',
    'No Promotions Configured': 'No Promotions Configured',
    'No Service Periods Yet': 'No Service Periods Yet',
    'No Support Tickets': 'No Support Tickets',
    'No Tables on Floor Plan': 'No Tables on Floor Plan',
    'No Team Members Found': 'No Team Members Found',
    'No Upcoming Events': 'No Upcoming Events',
    'No Walk-Ins Recorded': 'No Walk-Ins Recorded',
    'No items are available right now': 'No items are available right now',
    'No notifications found': 'No notifications found',
    'No reservations matching your filters': 'No reservations matching your filters',
    'No tables currently occupied': 'No tables currently occupied',
    'No venue selected': 'No venue selected',
    'Waitlist': 'Waitlist',
    'Waitlist is Empty': 'Waitlist is Empty',
    'Waves (Gluten)': 'Waves (Gluten)',
    'We sent a temporary verification code to': 'We sent a temporary verification code to',
    'Website URL': 'Website URL',
    'What the customer receives': 'What the customer receives',
    'White-label control is available on eligible plans.': 'White-label control is available on eligible plans.',
    'Why this works:': 'Why this works:',
    'WiFi Broadcast': 'WiFi Broadcast',
    'WiFi Password': 'WiFi Password',
    'Window seat, dietary request, birthday celebration...': 'Window seat, dietary request, birthday celebration...',
    'Your Published Response': 'Your Published Response',
    'Your Venues': 'Your Venues',
    '🎉 Ready to publish!': '🎉 Ready to publish!',
    'e.g. Grill, Salad, Pastry': 'e.g. Grill, Salad, Pastry',
    'e.g. Guest called, emergency': 'e.g. Guest called, emergency',
    'e.g. Italian, Moroccan, Burgers': 'e.g. Italian, Moroccan, Burgers',
    'e.g. Mains, Pasta, Starters': 'e.g. Mains, Pasta, Starters',
    'e.g. Signature Truffle Pasta': 'e.g. Signature Truffle Pasta',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'e.g., Afternoon Coffee Special, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'e.g., Breakfast, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'e.g., Join us for Live Jazz every Friday night at 8 PM!',
  },
  fr: {
    '123 Main St, City': '123 Rue Principale, Ville',
    'Balance': 'Solde de points',
    'Lifetime': 'Total cumulé',
    'Source': 'Origine',
    'Menu': 'Menu',
    'Advanced': 'Avancé',
    'Core Suite': 'Fonctionnalités essentielles',
    'Privacy': 'Confidentialité',
    'Support': 'Support',
    'Save Layout': 'Enregistrer le plan de salle',
    'Shape:': 'Forme :',
    'SEARCH LIBRARY...': 'RECHERCHER DANS LA BIBLIOTHÈQUE...',
    'Search library...': 'Rechercher dans la bibliothèque...',
    'None': 'Aucun',
    'Type': 'Type',
    'Target': 'Cible',
    'Value / Target': 'Valeur / Cible',
    'London (GMT/BST)': 'Londres (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubaï (GST)',
    'Tokyo (JST)': 'Tokyo (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Couleur de départ',
    'Depth Shade': 'Ombrage et profondeur',
    'Matrix Geometry': 'Motif du code QR',
    'Eye Architecture': 'Coins de repère du QR',
    'Brand Centerpiece': 'Logo au centre du QR',
    'Scannability Shield': 'Protection de lisibilité',
    'Environment Blur': 'Flou d\'arrière-plan',
    'Color Target:': 'Cible de couleur :',
    'DPI:': 'DPI (Résolution) :',
    '3mm Bleed Box': 'Marge de découpe de 3 mm',
    '300 DPI': '300 DPI (Haute définition)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Protocole SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'Identifiant de l\'entité IdP (Entity ID)',
    'SSO URL': 'URL de connexion SSO',
    'X.509 Certificate': 'Certificat de sécurité X.509',
    'ACS URL': 'URL du service ACS (Assertion Consumer)',
    'Entity ID': 'Identifiant d\'entité',
    'Navigate': 'Naviguer',
    'Select': 'Sélectionner',
    'OmniSearch v1.0': 'Recherche universelle OmniSearch',
    'Back to order review': 'Retour au récapitulatif de commande',
    'Return to Intelligence Studio': 'Retour au studio analytique',
    'View Full Notification Center': 'Voir toutes les notifications',
    'Read all': 'Tout marquer comme lu',
    'Cancel department': 'Annuler le département',
    'Save department': 'Enregistrer le département',
    'Venue Segmentation': 'Segmentation des espaces',
    'Collaborate on your menu together.': 'Collaborez sur votre menu en équipe.',
    'Timeline': 'Chronologie des services',
    'Calendar': 'Calendrier des réservations',
    'Book': 'Réserver une table',
    'Duration': 'Durée du repas',
    'Time & Duration': 'Heure et durée',
    'Seating Preference': 'Préférence de placement',
    'Section': 'Zone de la salle',
    'Min Capacity': 'Capacité minimale',
    'Max Capacity': 'Capacité maximale',
    'Phone (Optional)': 'Téléphone (Optionnel)',
    'Name or Walk-in': 'Nom du client ou sans réservation',
    'Next day': 'Jour suivant',
    'Next month': 'Mois suivant',
    'Live Connection': 'Connexion en direct',
    'Live Inventory': 'Stock en temps réel',
    'Main Venue (Full Menu)': 'Établissement principal (Menu complet)',
    'Preview Menu': 'Aperçu du menu',
    'Manual Setup Guide': 'Guide de configuration manuelle',
    'Connected & Active': 'Connecté et actif',
    'CTA Action': 'Bouton d\'action (CTA)',
    'Phone Contact': 'Numéro de contact',
    'Menu URL': 'Adresse du menu en ligne',
    'Google Location': 'Emplacement Google',
    'Field': 'Champ',
    'Linked': 'Lié',
    'Synchronizing Hub...': 'Synchronisation en cours...',
    'Data Integrity': 'Intégrité des données',
    'Station Governance Suite': 'Gestion des postes de travail',
    'Open audit log': 'Ouvrir le journal d\'audit',
    'Signal / Time': 'Événement / Date et heure',
    'Identity': 'Utilisateur / Identité',
    'Invitation Invalid': 'Lien d\'invitation expiré ou invalide',
    'Invited To': 'Invité à rejoindre',
    'Team Limit Reached': 'Limite de membres d\'équipe atteinte',
    'Venue Limit Reached': 'Limite d\'établissements atteinte',
    'Venue Setup Required': 'Configuration initiale requise',
    'Create Venue': 'Créer un établissement',
    'TableGreet Onboarding': 'Bienvenue sur TableGreet',
    'No payment required': 'Aucun paiement requis',
    'Selected Plan': 'Formule sélectionnée',
    'Payment Method': 'Moyen de paiement',
    'Invoice ID': 'Numéro de facture',
    'Verified': 'Vérifié',
    'Accent color': 'Couleur d\'accentuation',
    'Set the voice and overall light level.': 'Définissez le style et l\'ambiance lumineuse.',
    'Layout architecture': 'Architecture de la mise en page',
    'Navigation & motion': 'Navigation et animations',
    'Loading preview engine': 'Chargement du moteur de prévisualisation',
    'Real Live Preview': 'Aperçu en temps réel',
    'Included with Pro & Max': 'Inclus avec Pro et Max',
    'Daypart Scheduling': 'Planification par moments de la journée',
    'Operational Daypart Architecture': 'Architecture des créneaux opérationnels',
    'Service Protocol': 'Protocole de service',
    'Configuration Engine': 'Moteur de configuration',
    'Linked Department': 'Département associé',
    'Team Hub': 'Gestion de l\'équipe',
    'Team Size': 'Taille de l\'équipe',
    'Joined': 'Inscrit le',
    'Cancel Invitation': 'Annuler l\'invitation',
    'Initialize New Organization': 'Créer une nouvelle organisation',
    'Operational': 'Opérationnel',
    'Pro Feature': 'Fonctionnalité Pro',
    'Current State': 'État actuel',
    'Open on the big TV': 'Ouvrir sur le grand écran TV',
    'Menu Board': 'Panneau d\'affichage du menu',
    'Reward': 'Récompense',
    'Reward name': 'Nom de la récompense',
    'Cancel & refund': 'Annuler et rembourser',
    'Code': 'Code',
    'Find': 'Rechercher',
    'Complete': 'Terminé',
    'Seated': 'Installés à table',
    'No-Show': 'Non présenté',
    'Walk-in': 'Sans réservation',
    'Party': 'Groupe / Couverts',
    'Turnover': 'Rotation des tables',
    'We couldn\'t load the menu': 'Impossible de charger le menu',
    'Venue Not Found': 'Établissement introuvable',
    'Live Availability': 'Disponibilité en direct',
    'Multi-Language Suite': 'Gestion multilingue',
    'Key name': 'Nom de la clé',
    'Revoke API key': 'Révoquer la clé d\'API',
    'Delete webhook': 'Supprimer le webhook',
    'Identity Provider': 'Fournisseur d\'identité (IdP)',
    'Provider': 'Fournisseur',
    'Mandatory MFA': 'Authentification à deux facteurs obligatoire (MFA)',
    'Account Identity': 'Identité du compte',
    'Login Email': 'E-mail de connexion',
    'New Login Email': 'Nouvel e-mail de connexion',
    'Phone Login': 'Connexion par téléphone',
    'SMS Verification Code': 'Code de vérification par SMS',
    'Integration Method': 'Méthode d\'intégration',
    'Stripe Connect': 'Paiements Stripe Connect',
    'Stripe Secret Key': 'Clé secrète Stripe',
    'Stripe Webhook Secret': 'Secret de webhook Stripe',
    'Client-Owned': 'Géré par vos soins',
    'Verify connection': 'Vérifier la connexion',
    'Setup Health': 'État de configuration',
    'Privacy Protected': 'Données protégées et confidentielles',
    'Privacy Policy': 'Politique de confidentialité',
    'Continue': 'Continuer',
    'Sign In': 'Connexion',
    'Didn\'t get it?': 'Vous n\'avez rien reçu ?',
    'Logged In Account': 'Compte connecté',
    'Annual': 'Annuel',
    'Unread': 'Non lu',
    'Starred': 'Favoris',
    'High / Urgent': 'Élevé / Urgent',
    'Mark Read': 'Marquer comme lu',
    'Delete notification': 'Supprimer la notification',
    'Urgent': 'Urgent',
    'High': 'Élevé',
    'Normal': 'Normal',
    'Low': 'Faible',
    'Select a Ticket': 'Sélectionner un ticket',
    'Urgent (Immediate attention)': 'Urgent (Attention immédiate)',
    'Type your reply here...': 'Rédigez votre réponse ici...',
    'Print Card (PDF)': 'Imprimer la fiche de table (PDF)',
    'Advanced Printing Engine': 'Moteur d\'impression haute définition',
    'Multi-Venue Governance': 'Gestion multi-établissements',
    'Network Revenue': 'Chiffre d’affaires global du réseau',
    'Live Network Total': 'Total du réseau en direct',
    'Audit Log Hub': 'Journal d\'audit et de traçabilité',
    'Export Log': 'Exporter le journal',
    'CUSTOM ROLE SCHEMAS': 'RÔLES ET AUTORISATIONS PERSONNALISÉS',
    'Unique internal role identifier.': 'Identifiant interne unique du rôle.',
    'Human-friendly role label.': 'Nom clair du rôle affiché pour l’équipe.',
    'Marketing Hub': 'Centre marketing',
    'Revenue Generation Suite': 'Outils de croissance des ventes',
    'Active Campaign Limit Reached (3/3)': 'Limite de campagnes actives atteinte (3/3)',
    'Banner Content': 'Texte de la bannière',
    'Style Profile': 'Style visuel',
    'Action URL (Optional)': 'Lien d\'action (Optionnel)',
    'Promotion Label': 'Libellé de la promotion',
    'Target Selection': 'Cible de la promotion',
    'Edit Promotion': 'Modifier la promotion',
    'Delete Promotion': 'Supprimer la promotion',
    'Loyalty Engine': 'Programme de fidélité',
    'Reward catalog': 'Catalogue des récompenses',
    'Add reward': 'Ajouter une récompense',
    'Loyalty ID': 'Identifiant fidélité',
    'No point activity yet': 'Aucune activité de points pour l\'instant',
    'Scan or enter QR value': 'Scannez ou saisissez la valeur du QR',
    'TableGreet Admin': 'Administration TableGreet',
    'Capacity Pacing': 'Régulation de la capacité',
    'Cancellation Policy': 'Politique d\'annulation',
    'Booking Experience': 'Expérience de réservation',
    'Floor Occupancy': 'Taux d’occupation des salles',
    'Currently Seated': 'Actuellement installés',
    'Seat Party': 'Installer les clients',
    'Seat Table': 'Assigner la table',
    'Select Table': 'Choisir la table',
    'Table / Capacity': 'Table / Capacité',
    'Table Name/Number': 'Nom ou numéro de table',
    'Mark Arrived': 'Marquer comme arrivé',
    'Mark No-Show': 'Marquer comme non présenté',
    'Complete Dining': 'Terminer le repas',
    'Standard Dining (Default)': 'Repas standard (Par défaut)',
    'Walk-In Dining Log': 'Registre des clients sans réservation',
    'Notification Center': 'Centre de notifications',
    'Single Sign-On': 'Connexion unique (SSO)',
    'Welcome Aboard!': 'Bienvenue chez TableGreet !',
    'Select your TableGreet plan': 'Choisissez votre formule TableGreet',
    'Free Plan Activation': 'Activation de la formule gratuite',
    'Venue Created!': 'Établissement créé avec succès !',
    'Upgrade for Unlimited': 'Passez à la formule illimitée',
    'Auto-Tag Menu': 'Étiquetage automatique de la carte',
    'Start Auto-Tagging': 'Démarrer l\'étiquetage automatique',
    'Launch Auto-Tagger': 'Ouvrir l\'assistant d\'étiquetage',
    'Launch PDF Importer': 'Importer depuis un menu PDF',
    'Analyzing Menu...': 'Analyse du menu en cours...',
    'Analyzing Recipe...': 'Analyse de la recette...',
    'Manual Availability': 'Disponibilité manuelle',
    'Technical Breakdown': 'Détails techniques',
    'Dietary Taxonomy': 'Régimes et allergènes',
    'Dietary & Allergen AI': 'IA pour régimes et allergènes',
    'Inherit from Category': 'Hériter de la catégorie',
    'Ingredient Library': 'Bibliothèque des ingrédients',
    'Ingredient Name': 'Nom de l\'ingrédient',
    'Menu Organization': 'Organisation du menu',
    'Inventory Taxonomy Hub': 'Gestionnaire des catégories de stock',
    'Category Intelligence': 'Analyse intelligente de la catégorie',
    'Service Period': 'Période de service',
    'Manual Configuration / All Day': 'Configuration manuelle / Toute la journée',
    'Operational Department': 'Département opérationnel',
    'Global / No Department': 'Global / Aucun département',
    'Cancel category': 'Annuler la catégorie',
    'New category name...': 'Nom de la nouvelle catégorie...',
    'Save category': 'Enregistrer la catégorie',
    'QR Matrix Only': 'Code QR seul',
    'Vector & PDF': 'Vectoriel et PDF',
    'Vector QR (SVG)': 'QR Code vectoriel (SVG)',
    'Vector QR (PDF)': 'QR Code pour impression (PDF)',
    'Save QR Style': 'Enregistrer le style du QR',
    'Share Link': 'Partager le lien',
    'High Fidelity 300DPI': 'Haute résolution 300 DPI pour impression',
    'Generating 300 DPI PDF...': 'Génération du PDF 300 DPI...',
    'Give your venue a point of view.': 'Donnez une identité visuelle unique à votre établissement.',
    'Signage Menu Not Found': 'Menu d\'affichage numérique introuvable',
    'Scan Menu': 'Scanner le menu',
    'Open on your phone': 'Ouvrir sur votre smartphone',
    'Full Screen': 'Plein écran',
    'Lock TV board': 'Verrouiller l\'écran TV',
    'Big-Screen Menu': 'Menu sur grand écran',
    'TV Signage': 'Affichage numérique TV',
    'General Information': 'Informations générales',
    'Venue Name *': 'Nom de l\'établissement *',
    'Public Slug *': 'Identifiant public (Slug) *',
    'Venue Logo': 'Logo de l\'établissement',
    'Hero Cover Image': 'Image de couverture principale',
    'Venue Timezone': 'Fuseau horaire de l\'établissement',
    'Primary Brand Color': 'Couleur principale de la marque',
    'TableGreet footer credit': 'Mention TableGreet en bas de page',
    'White-Label Branding': 'Marque blanche personnalisée',
    'Client DNS Setup': 'Configuration DNS du domaine',
    'Copy CNAME target': 'Copier la cible CNAME',
    'Copy TableGreet URL': 'Copier l\'URL TableGreet',
    'Included TableGreet URL': 'Lien TableGreet inclus',
    'Open TableGreet URL': 'Ouvrir le lien TableGreet',
    'Public menu': 'Menu public des clients',
    'Public venue delivery': 'Livraison depuis la carte en ligne',
    'Zone name': 'Nom de la zone',
    'Coverage note': 'Note sur la zone couverte',
    'Default ETA min': 'Délai estimé min (minutes)',
    'Default ETA max': 'Délai estimé max (minutes)',
    'Packaging fee': 'Frais d\'emballage',
    'Courier handled by': 'Livraison gérée par',
    'Third-party courier': 'Coursier tiers partenaire',
    'Unified Experience': 'Expérience unifiée',
    'Save WiFi to Venue': 'Enregistrer le Wi-Fi de l\'établissement',
    'Network Name (SSID)': 'Nom du réseau Wi-Fi (SSID)',
    'Audio Alert Chime': 'Sonnerie des notifications',
    'Time Format': 'Format de l\'heure',
    'Auto / Default': 'Automatique / Par défaut',
    'Syncing SEO & Google Data...': 'Synchronisation SEO et Google...',
    'Google Connection': 'Connexion Google My Business',
    'Search Engine Metadata': 'Métadonnées pour moteurs de recherche',
    'Social card preview': 'Aperçu sur les réseaux sociaux',
    'Allow Indexing': 'Autoriser l\'indexation',
    'Canonical URL Override': 'Remplacement de l\'URL canonique',
    'Price Range': 'Gamme de prix',
    'Latitude': 'Latitude',
    'Longitude': 'Longitude',
    'Google Place ID / Location ID': 'Identifiant du lieu Google (Place ID)',
    'Google Search Preview': 'Aperçu dans la recherche Google',
    'Linked Google Location': 'Établissement Google associé',
    'SEO Documentation': 'Documentation SEO',
    ', and': ', et',
    'and': 'et',
    'Delete Department?': 'Supprimer le département ?',
    'Delete Department': 'Supprimer le département',
    'Live URL': 'URL en direct',
    'Copy full live URL': 'Copier l\'URL complète en direct',
    'Open live department menu': 'Ouvrir le menu du département en direct',
    'Loading departments...': 'Chargement des départements...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'La segmentation multi-départements est une fonctionnalité premium disponible à partir de l\'offre PRO.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Segmentez votre carte par salles, tables, bars ou espaces physiques.',
    'Targeted Ordering': 'Commandes ciblées',
    'Route guests and orders to specific preparation areas.': 'Acheminez les clients et les commandes vers des zones de préparation précises.',
    'Operational Departments': 'Départements opérationnels',
    'OPERATIONAL DEPARTMENTS': 'DÉPARTEMENTS OPÉRATIONNELS',
    'Venue Segmentation Hub': 'Gestion des zones de l\'établissement',
    'VENUE SEGMENTATION HUB': 'GESTION DES ZONES DE L\'ÉTABLISSEMENT',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Créez des départements pour organiser vos menus. Par exemple, un grand établissement peut avoir un',
    'Restaurant': 'Restaurant',
    'Poolside': 'Piscine',
    'department, each with its own categories.': 'avec chacun ses propres catégories.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Les départements vous permettent de diviser votre établissement en sections comme Restaurant, Bar ou Piscine — chacune avec ses propres catégories.',
    'Department name': 'Nom du département',
    'New department name...': 'Nom du nouveau département...',
    'No Linked Daypart (Always Available)': 'Non lié à un créneau (toujours disponible)',
    'Linked Daypart': 'Créneau horaire associé',
    'Localizations': 'Traductions',
    'LOCALIZATIONS': 'TRADUCTIONS',
    'AI Auto-Translate': 'Traduction auto par IA',
    'AI AUTO-TRANSLATE': 'TRADUCTION AUTO PAR IA',
    'Translated name': 'Nom traduit',
    'No Departments Yet': 'Aucun département pour le moment',
    'Add First Department': 'Ajouter le premier département',
    'Avg Order Value': 'Panier moyen',
    'AVG ORDER VALUE': 'PANIER MOYEN',
    'item taps': 'clics sur les plats',
    'ITEM TAPS': 'CLICS SUR LES PLATS',
    'Avg Rating': 'Note moyenne',
    'AVG RATING': 'NOTE MOYENNE',
    'Revenue (Last 7 Days)': 'Revenus (7 derniers jours)',
    'REVENUE (LAST 7 DAYS)': 'REVENUS (7 DERNIERS JOURS)',
    'Daily breakdown': 'Détail quotidien',
    'DAILY BREAKDOWN': 'DÉTAIL QUOTIDIEN',
    'Menu Views (Last 7 Days)': 'Vues du menu (7 derniers jours)',
    'MENU VIEWS (LAST 7 DAYS)': 'VUES DU MENU (7 DERNIERS JOURS)',
    'Guest traffic': 'Fréquentation des clients',
    'GUEST TRAFFIC': 'FRÉQUENTATION DES CLIENTS',
    'Most Viewed Items': 'Plats les plus consultés',
    'MOST VIEWED ITEMS': 'PLATS LES PLUS CONSULTÉS',
    'Top Rated Items': 'Plats les mieux notés',
    'TOP RATED ITEMS': 'PLATS LES MIEUX NOTÉS',
    'Waiter Requests': 'Appels serveur',
    'WAITER REQUESTS': 'APPELS SERVEUR',
    'Total': 'Total',
    'TOTAL': 'TOTAL',
    'Order Status (30 days)': 'État des commandes (30 jours)',
    'ORDER STATUS (30 DAYS)': 'ÉTAT DES COMMANDES (30 JOURS)',
    'Recent Guest Feedback': 'Avis récents des clients',
    'RECENT GUEST FEEDBACK': 'AVIS RÉCENTS DES CLIENTS',
    'Conversion Rate': 'Taux de conversion',
    'CONVERSION RATE': 'TAUX DE CONVERSION',
    'of menu visitors placed an order': 'des visiteurs du menu ont passé commande',
    'Refreshed': 'Actualisé',
    'REFRESHED': 'ACTUALISÉ',
    'Last 30 days': '30 derniers jours',
    'LAST 30 DAYS': '30 DERNIERS JOURS',
    'cash': 'espèces',
    'Cash': 'Espèces',
    'CASH': 'ESPÈCES',
    'bill': 'l\'addition',
    'Bill': 'L\'addition',
    'BILL': 'L\'ADDITION',
    'water': 'de l\'eau',
    'Water': 'De l\'eau',
    'WATER': 'DE L\'EAU',
    'service': 'service',
    'Service': 'Service',
    'SERVICE': 'SERVICE',
    'No interaction data yet': 'Aucune donnée d\'interaction pour le moment',
    'No ratings collected yet': 'Aucune évaluation enregistrée pour le moment',
    'No requests in period': 'Aucune demande sur cette période',
    'No feedback collected yet': 'Aucun avis client reçu pour le moment',
    'No menu views recorded yet': 'Aucune vue du menu enregistrée pour le moment',
    'Dish Rating Breakdown': 'Détail des notes par plat',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Le classement des plats et les scores de satisfaction client sont réservés aux forfaits MAX et Enterprise.',
    'Customer Sentiment & Reviews': 'Avis et satisfaction des clients',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Les avis directs, l\'analyse des sentiments et les commentaires nécessitent un abonnement MAX ou Enterprise.',
    'Reviews AI': 'IA d\'analyse des avis',
    'Guest Feedback': 'Avis des clients',
    'Upgrade to unlock rating analytics →': 'Mettez à niveau pour débloquer l\'analyse des notes →',
    'Upgrade to Max': 'Passer au forfait Max',
    'UPGRADE TO MAX': 'PASSER AU FORFAIT MAX',
    'MAX PLAN': 'FORFAIT MAX',
    'No Data Yet': 'Aucune donnée pour le moment',
    'Share your QR code to start collecting real venue insights.': 'Partagez votre QR code pour commencer à collecter des statistiques d\'activité réelles.',
    'Revenue Analytics': 'Analyse des revenus',
    'REVENUE ANALYTICS': 'ANALYSE DES REVENUS',
    'Track your earnings, orders, and average ticket values over time.': 'Suivez vos revenus, vos commandes et la valeur moyenne du ticket dans le temps.',
    'Operational Velocity': 'Rythme opérationnel',
    'OPERATIONAL VELOCITY': 'RYTHME OPÉRATIONNEL',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Surveillez l\'affluence des clients, les plats populaires et la charge des serveurs.',
    'Upgrade to Pro': 'Passer à Pro',
    'UPGRADE TO PRO': 'PASSER AU FORFAIT PRO',
    'Loading Insights…': 'Chargement des analyses…',
    'Loading Insights...': 'Chargement des analyses...',
    'Telemetry Unavailable': 'Données d\'activité indisponibles',
    'TELEMETRY UNAVAILABLE': 'DONNÉES D\'ACTIVITÉ INDISPONIBLES',
    'No Data Captured': 'Aucune donnée enregistrée',
    'NO DATA CAPTURED': 'AUCUNE DONNÉE ENREGISTRÉE',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Partagez votre QR code pour commencer à suivre la fréquentation et les commandes en temps réel.',
    'INTELLIGENCE STUDIO': 'CENTRE D\'ANALYSE',
    'Insights Engine Locked': 'Analyses avancées verrouillées',
    'INSIGHTS ENGINE LOCKED': 'ANALYSES AVANCÉES VERROUILLÉES',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Les tendances avancées, les commandes de gestion en bloc et les exports sont réservés aux',
    'Unlock Pro Features': 'Débloquer les fonctionnalités Pro',
    'UNLOCK PRO FEATURES': 'DÉBLOQUER LES FONCTIONNALITÉS PRO',
    'MENU VIEWS': 'VUES DU MENU',
    'ITEM INTERACTIONS': 'INTERACTIONS AVEC LES PLATS',
    'GUEST RATING': 'ÉVALUATION DES CLIENTS',
    'DELIVERY REVENUE': 'REVENUS DES LIVRAISONS',
    'AVERAGE DELIVERY ORDER': 'COMMANDE LIVRÉE MOYENNE',
    'AVERAGE DELIVERY FEE': 'FRAIS DE LIVRAISON MOYENS',
    'INCLUDING FEES': 'FRAIS COMPRIS',
    'ZONE AVERAGE': 'MOYENNE DE LA ZONE',
    'TRAFFIC PROJECTIONS': 'PROJECTIONS DU FLUX CLIENTS',
    'TELEMETRY SIGNAL TRACKING': 'SUIVI DES SIGNAUX D\'ACTIVITÉ',
    'VEGETARIAN': 'VÉGÉTARIEN',
    'VEGAN': 'VÉGÉTALIEN',
    'SPICY': 'ÉPICÉ',
    'GLUTEN-FREE': 'SANS GLUTEN',
    'Reset: All In-Stock': 'Réinitialiser : Tout en stock',
    'Emergency Stop (All Out)': 'Arrêt d\'urgence : Tout épuisé',
    'No compliance issues': 'Aucun problème détecté',
    'NO COMPLIANCE ISSUES': 'AUCUN PROBLÈME DÉTECTÉ',
    'detected in current menu': 'dans le menu actuel',
    'DETECTED IN CURRENT MENU': 'DANS LE MENU ACTUEL',
    'Telemetry History (24h)': 'Historique d\'activité (24h)',
    'TELEMETRY HISTORY (24H)': 'HISTORIQUE D\'ACTIVITÉ (24H)',
    'Telemetry History (30d)': 'Historique d\'activité (30j)',
    'TELEMETRY HISTORY (30D)': 'HISTORIQUE D\'ACTIVITÉ (30J)',
    'Save': 'Enregistrer',
    'Saved': 'Enregistré',
    'Saving...': 'Enregistrement...',
    'Save Changes': 'Enregistrer les modifications',
    'Save Settings': 'Enregistrer les paramètres',
    'Cancel': 'Annuler',
    'Close': 'Fermer',
    'Create': 'Créer',
    'Add': 'Ajouter',
    'Edit': 'Modifier',
    'Delete': 'Supprimer',
    'Remove': 'Retirer',
    'Update': 'Mettre à jour',
    'Refresh': 'Actualiser',
    'Search': 'Rechercher',
    'Search...': 'Rechercher...',
    'Search items...': 'Rechercher des articles...',
    'Search categories...': 'Rechercher des catégories...',
    'Filter': 'Filtrer',
    'All': 'Tout',
    'Export': 'Exporter',
    'Import': 'Importer',
    'Download': 'Télécharger',
    'Upload': 'Téléverser',
    'Copy': 'Copier',
    'Copied': 'Copié',
    'Copied!': 'Copié !',
    'Preview': 'Aperçu',
    'Open': 'Ouvert',
    'View': 'Voir',
    'Manage': 'Gérer',
    'Configure': 'Configurer',
    'Enable': 'Activer',
    'Disable': 'Désactiver',
    'Enabled': 'Activé',
    'Disabled': 'Désactivé',
    'Active': 'Actif',
    'Inactive': 'Inactif',
    'Available': 'Disponible',
    'Unavailable': 'Indisponible',
    'Visible': 'Visible',
    'Hidden': 'Masqué',
    'Required': 'Obligatoire',
    'Optional': 'Facultatif',
    'Confirm': 'Confirmer',
    'Back': 'Retour',
    'Next': 'Suivant',
    'Done': 'Terminé',
    'Upgrade': 'Passer à l’offre supérieure',
    'Sign Out': 'Déconnexion',
    'Exit to Main Admin': 'Retour à l’administration',
    'Change Language': 'Changer de langue',
    'Add Category': 'Ajouter une catégorie',
    'Add Menu Item': 'Ajouter un plat',
    'Add Item': 'Ajouter un article',
    'Add Ingredient': 'Ajouter un ingrédient',
    'Add Department': 'Ajouter un département',
    'Delete Item': 'Supprimer le plat',
    'Delete Category': 'Supprimer la catégorie',
    'Upload Image': 'Téléverser une image',
    'Upload Photo': 'Téléverser une photo',
    'Select Category': 'Sélectionner une catégorie',
    'Select Department': 'Sélectionner un département',
    'Add translation...': 'Ajouter une traduction...',
    'Clear': 'Effacer',
    'Reset': 'Réinitialiser',
    'Apply': 'Appliquer',
    'Submit': 'Envoyer',
    'Core & Operations': 'Opérations & Salle',
    'Menu & Experience': 'Carte & Expérience',
    'Enterprise & Security': 'Établissement & Équipe',
    'System & Control': 'Paramètres & Aide',
    'Intelligence Studio': 'Centre d\'analyse',
    'Venue Insights': 'Rapports & Ventes',
    'Reservations': 'Réservations',
    'Floor Plan': 'Plan de salle',
    'Live Waitlist': 'Liste d’attente',
    'Walk-Ins': 'Clients sans réservation',
    'Walk-ins': 'Clients sans réservation',
    'Departments': 'Départements',
    'Service Hours': 'Heures de service',
    'Categories': 'Catégories',
    'Menu Categories': 'Catégories du menu',
    'Menu Items': 'Plats du menu',
    'Items': 'Articles',
    'Ingredients': 'Ingrédients',
    'Loyalty Program': 'Programme fidélité',
    'Campaign Studio': 'Offres & Promotions',
    'Signage': 'Affichage écran TV',
    'QR Code': 'Codes QR',
    'Kitchen Ops': 'Écran Cuisine',
    'Theme Engine': 'Couleurs & Style',
    'Governance': 'Gestion multi-sites',
    'Audit Log': 'Historique des activités',
    'API & Webhooks': 'Intégrations & Connexions',
    'SSO & Security': 'Sécurité & Connexion',
    'Team': 'Équipe & Employés',
    'Billing': 'Abonnement & Facturation',
    'SEO & Google': 'Visibilité Google',
    'Notifications': 'Notifications',
    'Support Center': 'Aide & Assistance',
    'Settings': 'Paramètres du restaurant',
    'Name': 'Nom',
    'Description': 'Description',
    'Price': 'Prix',
    'Status': 'Statut',
    'Actions': 'Actions',
    'Role': 'Rôle',
    'Email': 'E-mail',
    'Phone': 'Téléphone',
    'Address': 'Adresse',
    'City': 'Ville',
    'Currency': 'Devise',
    'Language': 'Langue',
    'Theme': 'Thème',
    'General': 'Général',
    'Total Revenue': 'Chiffre d’affaires total',
    'Total Orders': 'Total des commandes',
    'Average Order': 'Panier moyen',
    'Active Sessions': 'Tables occupées',
    'Recent Orders': 'Dernières commandes',
    'Live Orders': 'Commandes en direct',
    'Table': 'Table',
    'Date': 'Date',
    'Time': 'Heure',
    'Amount': 'Montant',
    'Customer': 'Client',
    'In Stock': 'En stock',
    'Out of Stock': 'Épuisé',
    'Sold Out': 'Épuisé',
    'Dine In': 'Sur place',
    'Pickup': 'À emporter',
    'Delivery': 'Livraison',
    'Paid': 'Payé',
    'Unpaid': 'Non payé',
    'Pending': 'En attente',
    'Preparing': 'En préparation',
    'Ready': 'Prêt',
    'Completed': 'Terminé',
    'Cancelled': 'Annulé',
    'Loading...': 'Chargement...',
    'No data': 'Aucune donnée',
    'No results found': 'Aucun résultat trouvé',
    'No items found': 'Aucun article trouvé',
    'No translations defined yet': 'Aucune traduction définie pour le moment',
    'Are you sure?': 'Êtes-vous sûr ?',
    'This action cannot be undone.': 'Cette action est irréversible.',
    'Settings saved successfully!': 'Paramètres enregistrés avec succès !',
    'Administrator': 'Administrateur',
    'Manager': 'Gérant',
    'Owner': 'Propriétaire',
    'Chef': 'Chef cuisinier',
    'Waiter': 'Serveur',
    'Cashier': 'Caissier',
    'Type a command or search...': 'Tapez une commande ou recherchez...',
    'Command Palette': 'Recherche rapide',
    'Demand Velocity': 'Vitesse de la demande',
    'DEMAND VELOCITY': 'VITESSE DE LA DEMANDE',
    'Top Performing Entities': 'Plats les plus performants',
    'TOP PERFORMING ENTITIES': 'PLATS LES PLUS PERFORMANTS',
    'Hourly Load Analysis': 'Analyse horaire de l\'affluence',
    'HOURLY LOAD ANALYSIS': 'ANALYSE HORAIRE DE L\'AFFLUENCE',
    'Bulk Command Center': 'Centre d\'actions groupées',
    'BULK COMMAND CENTER': 'CENTRE D\'ACTIONS GROUPÉES',
    'Governance Scan': 'Contrôle qualité du menu',
    'GOVERNANCE SCAN': 'CONTRÔLE QUALITÉ DU MENU',
    'No compliance issues detected in current menu': 'Aucun problème détecté dans le menu actuel',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'AUCUN PROBLÈME DÉTECTÉ DANS LE MENU ACTUEL',
    'Procedural Tagging': 'Attribution automatique de badges',
    'PROCEDURAL TAGGING': 'ATTRIBUTION AUTOMATIQUE DE BADGES',
    'Name match...': 'Recherche par nom...',
    '...Name match': 'Recherche par nom...',
    'Healthy': 'Sain',
    'HEALTHY': 'SAIN',
    'Apply Tags': 'Appliquer les badges',
    'APPLY TAGS': 'APPLIQUER LES BADGES',
    'Global Price Shift': 'Ajustement global des prix',
    'GLOBAL PRICE SHIFT': 'AJUSTEMENT GLOBAL DES PRIX',
    'Adjust': 'Ajuster',
    'ADJUST': 'AJUSTER',
    'Availability Override': 'Gestion d\'urgence des stocks',
    'AVAILABILITY OVERRIDE': 'GESTION D\'URGENCE DES STOCKS',
    'Reset: ALL IN-STOCK': 'Rétablir : TOUT EST EN STOCK',
    'RESET: ALL IN-STOCK': 'RÉTABLIR : TOUT EST EN STOCK',
    'EMERGENCY STOP (ALL OUT)': 'Arrêt d\'urgence (TOUT ÉPUISÉ)',
    'Integrity Engine': 'Moteur d\'intégrité du menu',
    'INTEGRITY ENGINE': 'MOTEUR D\'INTÉGRITÉ DU MENU',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Ce système intelligent contrôle la visibilité des plats, les anomalies de prix et l\'exactitude des descriptions dans tout votre établissement.',
    'Inventory Audit Export': 'Exportation pour inventaire',
    'INVENTORY AUDIT EXPORT': 'EXPORTATION POUR INVENTAIRE',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Générez un rapport complet sur les performances du menu et les métadonnées pour consultation hors-ligne.',
    'Enterprise Data Export': 'Exportation avancée des données',
    'ENTERPRISE DATA EXPORT': 'EXPORTATION AVANCÉE DES DONNÉES',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Générez et téléchargez des rapports de performance complets aux formats standards. Comprend l\'affluence, la popularité des plats et les taux de conversion.',
    'JSON Snapshot': 'Instantané JSON',
    'JSON SNAPSHOT': 'INSTANTANÉ JSON',
    'Compile CSV Report': 'Générer le rapport CSV',
    'COMPILE CSV REPORT': 'GÉNÉRER LE RAPPORT CSV',
    'Date Segment': 'Période',
    'DATE SEGMENT': 'PÉRIODE',
    'Inbound Views': 'Visites reçues',
    'INBOUND VIEWS': 'VISITES REÇUES',
    'Asset Interactions': 'Interactions avec les plats',
    'ASSET INTERACTIONS': 'INTERACTIONS AVEC LES PLATS',
    'Telemetry History': 'Historique d\'activité',
    'TELEMETRY HISTORY': 'HISTORIQUE D\'ACTIVITÉ',
    'Telemetry History (7d)': 'Historique d\'activité (7j)',
    'TELEMETRY HISTORY (7D)': 'HISTORIQUE D\'ACTIVITÉ (7J)',
    'Growth Trajectory': 'Trajectoire de croissance',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'La fréquence d\'interaction avec votre menu est en hausse sur les 7 derniers jours.',
    'Deep Analysis Engine': 'Moteur d\'analyse approfondie',
    'DEEP ANALYSIS ENGINE': 'MOTEUR D\'ANALYSE APPROFONDIE',
    'Asset Velocity': 'Performance des plats',
    'ASSET VELOCITY': 'PERFORMANCE DES PLATS',
    'Traffic Projections': 'Projections de fréquentation',
    'Telemetry Signal Tracking': 'Suivi des signaux d\'activité',
    'Service Hour Update': 'Mise à jour des horaires de service',
    'Late Night Lounge Hours': 'Horaires du bar de nuit',
    'Bar & Lounge Hours': 'Horaires bar & lounge',
    'Breakfast & Brunch Hours': 'Horaires petit-déjeuner & brunch',
    'Switch Location': 'Changer d\'établissement',
    'SWITCH LOCATION': 'CHANGER D\'ÉTABLISSEMENT',
    'Global Admin': 'Administrateur général',
    'GLOBAL ADMIN': 'ADMINISTRATEUR GÉNÉRAL',
    'Staff Access': 'Accès personnel',
    'STAFF ACCESS': 'ACCÈS PERSONNEL',
    'Venue Overview': 'Aperçu de l\'établissement',
    'VENUE OVERVIEW': 'APERÇU DE L\'ÉTABLISSEMENT',
    'Unified Telemetry Engine': 'Moteur de suivi d\'activité',
    'UNIFIED TELEMETRY ENGINE': 'MOTEUR DE SUIVI D\'ACTIVITÉ',
    'Pulse': 'En direct',
    'PULSE': 'EN DIRECT',
    'Insights': 'Tendances',
    'INSIGHTS': 'TENDANCES',
    'Reports': 'Rapports',
    'REPORTS': 'RAPPORTS',
    'GOVERNANCE': 'GESTION MULTI-SITES',
    'Multi-Venue': 'Multi-établissements',
    'MULTI-VENUE': 'MULTI-ÉTABLISSEMENTS',
    'Menu Views': 'Vues du menu',
    'Item Interactions': 'Interactions plats',
    'Guest Rating': 'Note des clients',
    'Revenue (7d)': 'Revenus (7j)',
    'Revenue (24h)': 'Revenus (24h)',
    'Revenue (30d)': 'Revenus (30j)',
    'Delivery orders (7d)': 'Commandes de livraison (7j)',
    'Delivery orders (24h)': 'Commandes de livraison (24h)',
    'Delivery orders (30d)': 'Commandes de livraison (30j)',
    'Delivery revenue': 'Revenus de livraison',
    'Average delivery order': 'Commande moyenne livraison',
    'Average delivery fee': 'Frais de livraison moyens',
    'Zone average': 'Moyenne de zone',
    'Including fees': 'Frais inclus',
    'No deliveries': 'Aucune livraison',
    'No orders': 'Aucune commande',
    'No reviews': 'Aucun avis',
    'Live': 'En direct',
    'LIVE': 'EN DIRECT',
    'reviews': 'avis',
    'orders': 'commandes',
    'deliveries': 'livraisons',
    'of orders': 'des commandes',
    'rate': 'taux',
    'Recent Activity': 'Activité récente',
    'Real-time Venue Telemetry': 'Activité de l\'établissement en direct',
    'REAL-TIME VENUE TELEMETRY': 'ACTIVITÉ DE L\'ÉTABLISSEMENT EN DIRECT',
    'Rapid Pulse View': 'Vue rapide en direct',
    'Anonymous Guest': 'Client anonyme',
    'scanned menu': 'a scanné le menu',
    'viewed item': 'a consulté le plat',
    'interacted with': 'a interagi avec',
    'placed order': 'a passé commande',
    'searched menu': 'a cherché dans le menu',
    'Main Landing': 'Page principale',
    'Trace': 'Vérifié',
    'TRACE': 'VÉRIFIÉ',
    'Gaining Telemetry...': 'Connexion aux données...',
    'No recent traffic detected': 'Aucune activité récente détectée',
    'Activities will appear here as guests scan your QR code': 'Les activités apparaîtront ici dès que les clients scanneront vos QR codes',
    'Analyze All Logs': 'Analyser tout le journal',
    'Just now': 'À l\'instant',
    'less than a minute ago': 'il y a moins d\'une minute',
    '1 minute ago': 'il y a 1 minute',
    '1 hour ago': 'il y a 1 heure',
    'about 1 hour ago': 'il y a environ 1 heure',
    '1 day ago': 'il y a 1 jour',
    'DAY AGO 1': 'il y a 1 jour',
    '2 days ago': 'il y a 2 jours',
    'DAY AGO 2': 'il y a 2 jours',
    '3 days ago': 'il y a 3 jours',
    'DAY AGO 3': 'il y a 3 jours',
    '4 days ago': 'il y a 4 jours',
    'DAY AGO 4': 'il y a 4 jours',
    '5 days ago': 'il y a 5 jours',
    'DAY AGO 5': 'il y a 5 jours',
    '6 days ago': 'il y a 6 jours',
    'DAY AGO 6': 'il y a 6 jours',
    '7 days ago': 'il y a 7 jours',
    'DAY AGO 7': 'il y a 7 jours',
    'about 1 month ago': 'il y a environ 1 mois',
    'Monday': 'Lundi',
    'Tuesday': 'Mardi',
    'Wednesday': 'Mercredi',
    'Thursday': 'Jeudi',
    'Friday': 'Vendredi',
    'Saturday': 'Samedi',
    'Sunday': 'Dimanche',
    'Mon': 'Lun',
    'Tue': 'Mar',
    'Wed': 'Mer',
    'Thu': 'Jeu',
    'Fri': 'Ven',
    'Sat': 'Sam',
    'Sun': 'Dim',
    'Opening Hours': 'Heures d’ouverture',
    'Start Time': 'Heure de début',
    'End Time': 'Heure de fin',
    'Opening Time': 'Heure d’ouverture',
    'Closing Time': 'Heure de fermeture',
    'All Day': 'Toute la journée',
    'Closed': 'Fermé',
    'Active Days': 'Jours actifs',
    'Active Schedule': 'Horaires actifs',
    'Always Available': 'Toujours disponible',
    'Always Visible': 'Toujours visible',
    'New Category': 'Nouvelle catégorie',
    'Category': 'Catégorie',
    'Category Name': 'Nom de la catégorie',
    'Category Description': 'Description de la catégorie',
    'Display Order': 'Ordre d’affichage',
    'Item Name': 'Nom du plat',
    'Item Description': 'Description du plat',
    'Base Price': 'Prix de base',
    'Preparation Time': 'Temps de préparation',
    'Calories': 'Calories',
    'Featured Item': 'Plat vedette',
    'Chef\'s Recommendation': 'Recommandation du chef',
    'Spicy': 'Épicé',
    'Vegetarian': 'Végétarien',
    'Vegan': 'Végétalien',
    'Gluten-Free': 'Sans gluten',
    'Halal': 'Halal',
    'Kosher': 'Cacher',
    'Contains Nuts': 'Contient des fruits à coque',
    'Dairy-Free': 'Sans produits laitiers',
    'Allergen Exclusions': 'Allergènes',
    'Table Number': 'Numéro de table',
    'Assigned Table': 'Table attribuée',
    'Seats': 'Places',
    'Capacity': 'Capacité',
    'Indoor': 'Intérieur',
    'Outdoor': 'Extérieur',
    'Terrace': 'Terrasse',
    'Bar': 'Bar',
    'Add Table': 'Ajouter une table',
    'Edit Table': 'Modifier la table',
    'Delete Table': 'Supprimer la table',
    'Bookings': 'Réservations',
    'Add to Waitlist': 'Ajouter à la liste d’attente',
    'Guest Name': 'Nom du client',
    'Party Size': 'Nombre de personnes',
    'Arrival Time': 'Heure d’arrivée',
    'Special Requests': 'Demandes spéciales',
    'Confirmed': 'Confirmé',
    'No Show': 'Non présenté',
    'Add Staff Member': 'Ajouter un employé',
    'Invite Staff': 'Inviter un membre',
    'Email Address': 'Adresse e-mail',
    'Phone Number': 'Numéro de téléphone',
    'Download QR Code': 'Télécharger le code QR',
    'Print Table Stands': 'Imprimer les chevalets de table',
    'Today': 'Aujourd’hui',
    'Yesterday': 'Hier',
    'This Week': 'Cette semaine',
    'This Month': 'Ce mois-ci',
    'Last 30 Days': '30 derniers jours',
    'Access Denied': 'Accès refusé',
    'Advanced Settings': 'Paramètres avancés',
    'Change Image': 'Changer l’image',
    'Remove Image': 'Supprimer l’image',
    'Announcement Banner': 'Bannière d’annonce',
    'Please fill in all required fields.': 'Veuillez remplir tous les champs obligatoires.',
    'Changes saved successfully': 'Modifications enregistrées avec succès',
    '(You)': '(Vous)',
    'AI Auto-Tag': 'Étiquetage auto par IA',
    'Analyzing:': 'Analyse en cours :',
    'Apple (Fruit/Vegan)': 'Pomme (Fruit/Végétalien)',
    'Cancel editing': 'Annuler les modifications',
    'Capacity:': 'Capacité :',
    'Carrot (Veggie)': 'Carotte (Légume)',
    'CircleDot (Organic)': 'Cercle (Biologique)',
    'Coffee (Caffeine)': 'Café (Caféine)',
    'Configuration': 'Configuration',
    'Copy verification record name': 'Copier le nom de l\'enregistrement',
    'Copy verification record value': 'Copier la valeur de l\'enregistrement',
    'Critical exception intercepted': 'Erreur système interceptée',
    'Kernel Panic': 'Erreur Système',
    'DNA (Organic/Bio)': 'ADN (Biologique/Bio)',
    'Egg (Ovo/Allergen)': 'Œuf (Ovo/Allergène)',
    'Empty': 'Vide',
    'Engraving...': 'Génération en cours...',
    'Flame (Spicy)': 'Flamme (Épicé)',
    'Free': 'Gratuit',
    'From': 'De',
    'Generating': 'Génération',
    'Governance & Traceability Studio': 'Centre de gestion et traçabilité',
    'Greet': 'Bienvenue',
    'Heart (Healthy)': 'Cœur (Sain)',
    'In-App': 'Dans l\'application',
    'Infinity SVG': 'SVG Vectoriel (Illimité)',
    'Leaf (Default)': 'Feuille (Par défaut)',
    'MAX Feature': 'Fonctionnalité Enterprise',
    'Milk (Dairy)': 'Lait (Produits laitiers)',
    'Network': 'Réseau',
    'Notify SMS': 'Notification SMS',
    'Order': 'Commande',
    'Original': 'Original',
    'Overview': 'Vue d\'ensemble',
    'PRO: 4 PREDEFINED ROLES': 'Pro : 4 Rôles Standards',
    'Shield (Safety)': 'Bouclier (Sécurité)',
    'Soup (Warm/Soup)': 'Soupe (Chaud/Soupe)',
    'Standard Pro View': 'Affichage Standard',
    'Star': 'Étoile',
    'Synchronized with Venue Identity': 'Adapté à l\'image de votre établissement',
    'Synchronizing Security Context...': 'Connexion sécurisée en cours...',
    'Targeted Timing': 'Horaires du service',
    'Targeting': 'S\'applique à',
    'To': 'À',
    'Toggle Navigation': 'Basculer le menu',
    'Venue': 'Établissement',
    'Venue Governance Suite': 'Paramètres et opérations de l\'établissement',
    'Volume:': 'Volume :',
    'WHITEBOARD NOTE...': 'Note pour l\'équipe...',
    'Warning (Alert)': 'Attention (Alerte)',
    'Wheat (Flour/Gluten)': 'Blé (Farine/Gluten)',
    'Wine (Alcohol)': 'Vin (Alcool)',
    'YouTube/Vimeo/Direct Link': 'Lien vidéo (YouTube, Vimeo ou MP4)',
    'Your name': 'Votre nom',
    '2. Table Number': '2. Numéro de table',
    'for Morocco or': 'pour le Maroc, ou',
    'in': 'dans',
    'to': 'à',
    'e.g. 12 or Patio-1': 'par ex. 12 ou Terrasse-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'par ex. Fettuccine maison, truffe noire d\'hiver, parmesan affiné 36 mois...',
    'e.g. Head Sommelier & Wine Lead': 'par ex. Chef sommelier',
    'e.g. London': 'par ex. Paris',
    'e.g. SOMMELIER, HOST, BARISTA': 'par ex. SOMMELIER, HÔTE, BARISTA',
    'e.g. Skyline Lounge': 'par ex. Le Salon Panoramique',
    'e.g. TRUFFLE OIL': 'par ex. HUILE DE TRUFFE',
    'e.g. The Grand Cafe': 'par ex. Le Grand Café',
    'e.g. Window preference, high chair': 'par ex. Table près de la fenêtre, chaise haute',
    'Copied to clipboard!': 'Copié dans le presse-papiers !',
    'Copy failed.': 'Échec de la copie.',
    'Link copied!': 'Lien copié !',
    'Logo uploaded': 'Logo téléversé',
    'Cover image uploaded': 'Image de couverture téléversée',
    'Profile saved and synced successfully': 'Profil enregistré et synchronisé avec succès',
    'Category Created': 'Catégorie créée',
    'Category Updated': 'Catégorie mise à jour',
    'Category Deleted': 'Catégorie supprimée',
    'Item Created': 'Article créé',
    'Item Updated': 'Article mis à jour',
    'Item Deleted': 'Article supprimé',
    'Menu Published': 'Menu publié',
    'Delete Category?': 'Supprimer la catégorie ?',
    'Delete Menu Item?': 'Supprimer cet article ?',
    'Delete Ingredient?': 'Supprimer cet ingrédient ?',
    'Delete Service Period?': 'Supprimer la période de service ?',
    'Remove team member?': 'Retirer ce membre de l\'équipe ?',
    'Cancel invitation?': 'Annuler l\'invitation ?',
    'Revoke API key?': 'Révoquer la clé API ?',
    'Delete webhook endpoint?': 'Supprimer ce point de terminaison webhook ?',
    'Clear all notifications?': 'Effacer toutes les notifications ?',
    'Disconnect Stripe account?': 'Déconnecter le compte Stripe ?',
    'Disconnect Google Business Profile?': 'Déconnecter la fiche Google Business ?',
    'Campaigns published successfully!': 'Campagnes publiées avec succès !',
    'Changes successfully synchronized with Google Business Profile!': 'Modifications synchronisées avec succès avec Google Business Profile !',
    'Google Business Profile successfully connected!': 'Fiche Google Business connectée avec succès !',
    'Google Business Profile disconnected.': 'Fiche Google Business déconnectée.',
    'Google Post draft saved successfully!': 'Brouillon d\'actualité Google enregistré avec succès !',
    'Post draft deleted.': 'Brouillon de publication supprimé.',
    'Review response published successfully!': 'Réponse à l\'avis publiée avec succès !',
    'SEO configuration saved successfully!': 'Configuration SEO enregistrée avec succès !',
    'SEO metadata translated across all languages!': 'Métadonnées SEO traduites dans toutes les langues !',
    'Venue profile & bio translated across all languages!': 'Profil et description traduits dans toutes les langues !',
    'Reservation configuration saved successfully': 'Paramètres de réservation enregistrés avec succès',
    'Stripe settings saved successfully.': 'Paramètres Stripe enregistrés avec succès.',
    'Print-Ready PDF downloaded successfully': 'PDF haute définition téléchargé avec succès',
    'Vector QR PDF downloaded successfully': 'QR Code vectoriel PDF téléchargé avec succès',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Pack d\'impression 300 DPI généré avec succès',
    'At least one language must be enabled.': 'Au moins une langue doit être activée.',
    'Enter a non-zero percentage before adjusting prices.': 'Entrez un pourcentage différent de zéro avant d\'ajuster les prix.',
    'Please enter a Venue Name before translating.': 'Veuillez entrer le nom de votre établissement avant de traduire.',
    'Please enter an item name before running dietary and allergen analysis.': 'Veuillez entrer le nom du plat avant de lancer l\'analyse des allergènes.',
    'Please write a reply before submitting.': 'Veuillez rédiger une réponse avant d\'envoyer.',
    'Table 3 Called Waiter': 'La table 3 appelle le serveur',
    'Table Call Bell': 'Appel de table',
    'Waiter Calls': 'Appels serveurs',
    'Waiter Dispatch & Calls': 'Gestion des appels serveurs',
    'Kitchen Display & Whiteboard': 'Écran cuisine & Tableau blanc',
    'Customer Orders': 'Commandes clients',
    'Bakery': 'Boulangerie / Pâtisserie',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Café / Salon de thé',
    'Fast Food': 'Restauration rapide',
    'Gourmet / Fine Dining': 'Haute gastronomie',
    'Hotel': 'Hôtel',
    'General Local Business': 'Commerce local général',
    'Classic': 'Classique',
    'Clean': 'Épuré',
    'Modern': 'Moderne',
    'Elegant': 'Élégant',
    'Energetic': 'Dynamique',
    'Glass': 'Translucide',
    'Playful & Casual': 'Convivial & Décontracté',
    'Sophisticated & Gourmet': 'Raffiné & Gastronomique',
    'Warm & Welcoming': 'Chaleureux & Accueillant',
    'Executive': 'Exécutif',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Mise à niveau requise : Les menus multilingues sont disponibles avec le forfait Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Mise à niveau requise : Les commandes et appels serveurs ne sont pas disponibles sur le forfait Découverte.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'L\'export SVG vectoriel est réservé aux forfaits Pro & Max. Mettez votre forfait à niveau.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'L\'export PDF haute définition est réservé aux forfaits Pro & Max. Mettez votre forfait à niveau.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Le forfait Pro est limité à 3 campagnes actives. Mettez une campagne en pause ou passez au forfait Max.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Le forfait Découverte comprend jusqu\'à 20 tables (Tables 1 à 20). Passez à Pro pour un nombre illimité de tables.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Le forfait Découverte prend en charge les tables numérotées (1 à 20). Les noms personnalisés (ex. Terrasse, VIP) nécessitent le forfait Pro.',
    'Add Menu Categories & Items': 'Ajouter des catégories & articles',
    'Add Menu Items': 'Ajouter des articles au menu',
    'Organize your menu sections and food items.': 'Organisez les sections et les plats de votre carte.',
    'Add and manage your food & beverage offerings.': 'Ajoutez et gérez vos plats et boissons.',
    'Customize Venue Branding': 'Personnaliser l\'image de marque',
    'Set your brand colors, logo, and presentation.': 'Définissez vos couleurs, logo et apparence.',
    'Get Your QR Code & Go Live': 'Obtenez votre QR Code et lancez-vous',
    'Download table QR codes and preview your live menu.': 'Téléchargez les QR codes de table et visualisez votre menu en direct.',
    'Account & Venue Created Successfully!': 'Compte & établissement créés avec succès !',
    '⚡ Off-Peak Hours': '⚡ Heures creuses',
    '🍽️ Lunch Crowd': '🍽️ Pause déjeuner',
    '🚀 Weekend Rush': '🚀 Affluence du week-end',
    '🌟 Seasonal Special': '🌟 Spécialité de saison',
    '📣 Engagement Buzz': '📣 Visibilité & Réseaux',
    'Festive & Energetic': 'Festif & Dynamique',
    'Urgent & High-Conversion': 'Offre flash & Impact direct',
    'Generated 3 tailored campaign strategies': '3 stratégies de campagne sur mesure générées',
    'Could not generate campaign ideas. Please try again.': 'Impossible de générer des idées de campagne. Veuillez réessayer.',
    'Venue Themes': 'Thèmes de l\'établissement',
    'Choose the character of your public menu': 'Choisissez l\'ambiance visuelle de votre menu public',
    'Glassmorphism & Depth': 'Effet verre & Profondeur',
    'Independent XML Sitemap': 'Sitemap XML indépendant',
    'Structured Schema & Sync': 'Données structurées & Synchronisation',
    'Improve how your venue appears on Google and in local search results.': 'Améliorez la visibilité de votre établissement sur Google et dans les recherches locales.',
    'Terms of Service': 'Conditions d\'utilisation',
    'Confirm your email': 'Confirmez votre adresse e-mail',
    'Check your spam / junk folder': 'Vérifiez votre dossier de courriers indésirables (spam)',
    'Commercial pre-press print production package': 'Pack d\'impression professionnelle pré-presse',
    'Over-bleed safety margin': 'Marge de sécurité de fond perdu',
    'Crop Marks': 'Traits de coupe',
    'Guillotine cutting guides': 'Repères de découpe au massicot',
    'Global Banners': 'Bannières globales',
    'Featured Items': 'Articles à la une',
    'AI Assistant': 'Assistant IA',
    'Custom Role Schemas': 'Schémas de rôles personnalisés',
    'Notification Preferences': 'Préférences de notification',
    'Preferences saved automatically': 'Préférences enregistrées automatiquement',
    'Menu Extractor (PDF & Images)': 'Extracteur de menu (PDF & Images)',
    'Multi-Language Translation': 'Traduction multilingue',
    'Start Menu Translation': 'Démarrer la traduction du menu',
    'Menu Items Hub': 'Centre des articles du menu',
    'Open Menu Items': 'Ouvrir les articles du menu',
    'Enterprise Traceability': 'Traçabilité d\'entreprise',
    'Full History': 'Historique complet',
    'Billing & Plans': 'Facturation & Abonnements',
    'Subscription Governance': 'Gestion de l\'abonnement',
    'Platform Usage': 'Utilisation de la plateforme',
    'Menu Page Views': 'Vues des pages du menu',
    'Enterprise Identity': 'Identité d\'entreprise',
    'Access Governance': 'Contrôle des accès',
    'Required Fields *': 'Champs obligatoires *',
    'Enabled Languages': 'Langues activées',
    'Venue Info & Bio Localizations': 'Traductions de la description et des informations',
    'Public Address': 'Adresse publique',
    'Public URL Slug': 'Identifiant d\'URL public',
    'Contact Phone': 'Téléphone de contact',
    'Business Address': 'Adresse de l\'établissement',
    'No Categories Yet': 'Aucune catégorie pour le moment',
    'No Custom Roles Configured': 'Aucun rôle personnalisé configuré',
    'No Google reviews retrieved yet': 'Aucun avis Google récupéré pour le moment',
    'No Guests Found': 'Aucun client trouvé',
    'No Menu Items Yet': 'Aucun article au menu pour le moment',
    'No Promotions Configured': 'Aucune promotion configurée',
    'No Service Periods Yet': 'Aucune période de service définie',
    'No Support Tickets': 'Aucun ticket d\'assistance',
    'No Tables on Floor Plan': 'Aucune table sur le plan de salle',
    'No Team Members Found': 'Aucun membre d\'équipe trouvé',
    'No Upcoming Events': 'Aucun événement à venir',
    'No Walk-Ins Recorded': 'Aucun client sans réservation enregistré',
    'No items are available right now': 'Aucun article disponible pour le moment',
    'No notifications found': 'Aucune notification trouvée',
    'No reservations matching your filters': 'Aucune réservation ne correspond à vos filtres',
    'No tables currently occupied': 'Aucune table occupée actuellement',
    'No venue selected': 'Aucun établissement sélectionné',
    'Waitlist': 'Liste d\'attente',
    'Waitlist is Empty': 'La liste d\'attente est vide',
    'Waves (Gluten)': 'Épis (Gluten)',
    'We sent a temporary verification code to': 'Nous avons envoyé un code de vérification temporaire à',
    'Website URL': 'URL du site web',
    'What the customer receives': 'Ce que reçoit le client',
    'White-label control is available on eligible plans.': 'La personnalisation en marque blanche est disponible sur les forfaits éligibles.',
    'Why this works:': 'Pourquoi cette stratégie fonctionne :',
    'WiFi Broadcast': 'Diffusion du WiFi',
    'WiFi Password': 'Mot de passe WiFi',
    'Window seat, dietary request, birthday celebration...': 'Table près de la fenêtre, allergies, anniversaire...',
    'Your Published Response': 'Votre réponse publiée',
    'Your Venues': 'Vos établissements',
    '🎉 Ready to publish!': '🎉 Prêt à être publié !',
    'e.g. Grill, Salad, Pastry': 'par ex. Grillades, Salades, Pâtisserie',
    'e.g. Guest called, emergency': 'par ex. Appel du client, urgence',
    'e.g. Italian, Moroccan, Burgers': 'par ex. Italien, Marocain, Burgers',
    'e.g. Mains, Pasta, Starters': 'par ex. Plats principaux, Pâtes, Entrées',
    'e.g. Signature Truffle Pasta': 'par ex. Pâtes signature à la truffe',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'par ex. Pause café gourmande, Happy Hour -15%',
    'e.g., Breakfast, Happy Hour': 'par ex. Petit-déjeuner, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'par ex. Retrouvez-nous pour un concert de jazz en direct chaque vendredi à 20h !',
  },
  ar: {
    '123 Main St, City': 'شارع الحسن الثاني، الدار البيضاء',
    'Balance': 'رصيد النقاط',
    'Lifetime': 'إجمالي النقاط المكتسبة',
    'Source': 'المصدر',
    'Menu': 'قائمة الطعام',
    'Advanced': 'خيارات متقدمة',
    'Core Suite': 'الأدوات الأساسية',
    'Privacy': 'الخصوصية',
    'Support': 'الدعم والمساعدة',
    'Save Layout': 'حفظ مخطط وتوزيع الطاولات',
    'Shape:': 'شكل الطاولة:',
    'SEARCH LIBRARY...': 'البحث في المستودع...',
    'Search library...': 'البحث في المستودع...',
    'None': 'لا شيء',
    'Type': 'النوع',
    'Target': 'الهدف والمستهدف',
    'Value / Target': 'القيمة / المستهدف',
    'London (GMT/BST)': 'لندن (GMT/BST)',
    'New York (EST/EDT)': 'نيويورك (EST/EDT)',
    'Chicago (CST/CDT)': 'شيكاغو (CST/CDT)',
    'Denver (MST/MDT)': 'دنفر (MST/MDT)',
    'Dubai (GST)': 'دبي (GST)',
    'Tokyo (JST)': 'طوكيو (JST)',
    'Sydney (AEST/AEDT)': 'سيدني (AEST/AEDT)',
    'Start Color': 'لون البداية',
    'Depth Shade': 'عمق الظلال والتباين',
    'Matrix Geometry': 'هندسة ونمط نقاط الرمز',
    'Eye Architecture': 'شكل زوايا وأعين الرمز',
    'Brand Centerpiece': 'الشعار في منتصف الرمز',
    'Scannability Shield': 'حماية وضمان سرعة المسح',
    'Environment Blur': 'تأثير التمويه الضبابي',
    'Color Target:': 'تنسيق ألوان الطباعة:',
    'DPI:': 'دقة الطباعة (DPI):',
    '3mm Bleed Box': 'هامش أمان للقص (3 ملم)',
    '300 DPI': '300 DPI (فائق الدقة للطباعة)',
    'PRO+': 'PRO+ (باقة برو بلس)',
    'SAML 2.0': 'بروتوكول SAML 2.0 الموحد',
    'OpenID Connect': 'معيار OpenID Connect (OIDC)',
    'IdP Entity ID': 'معرّف الكيان لمزود الهوية (Entity ID)',
    'SSO URL': 'رابط تسجيل الدخول الموحد (SSO URL)',
    'X.509 Certificate': 'شهادة الأمان الرقمية X.509',
    'ACS URL': 'رابط خدمة استقبال المطالبات (ACS URL)',
    'Entity ID': 'معرّف الكيان المؤسسي (Entity ID)',
    'Navigate': 'التنقل',
    'Select': 'اختيار',
    'OmniSearch v1.0': 'البحث الشامل الذكي',
    'Back to order review': 'العودة لمراجعة الطلب',
    'Return to Intelligence Studio': 'العودة لمركز البيانات والتحليلات',
    'View Full Notification Center': 'عرض كامل الإشعارات والتنبيهات',
    'Read all': 'تحديد الكل كمقروء',
    'Cancel department': 'إلغاء القسم',
    'Save department': 'حفظ القسم',
    'Venue Segmentation': 'تقسيم وتنظيم مساحات المكان',
    'Collaborate on your menu together.': 'شارك فريقك في تنظيم وتطوير القائمة معاً.',
    'Timeline': 'الخط الزمني للحجوزات',
    'Calendar': 'تقويم الحجوزات',
    'Book': 'حجز طاولة',
    'Duration': 'مدة الجلوس / التواجد',
    'Time & Duration': 'الوقت والمدة المقدرة',
    'Seating Preference': 'الموقع أو الجلسة المفضلة',
    'Section': 'منطقة الصالة',
    'Min Capacity': 'الحد الأدنى للأفراد',
    'Max Capacity': 'الحد الأقصى للأفراد',
    'Phone (Optional)': 'رقم الهاتف (اختياري)',
    'Name or Walk-in': 'اسم الضيف أو بدون حجز (مباشر)',
    'Next day': 'اليوم التالي',
    'Next month': 'الشهر القادم',
    'Live Connection': 'اتصال حي ومباشر',
    'Live Inventory': 'المخزون والتوفر المباشر',
    'Main Venue (Full Menu)': 'المكان الرئيسي (كامل القائمة)',
    'Preview Menu': 'معاينة قائمة الطعام',
    'Manual Setup Guide': 'دليل الإعداد اليدوي خطوة بخطوة',
    'Connected & Active': 'متصل ويعمل بكفاءة',
    'CTA Action': 'زر الحث على اتخاذ إجراء (CTA)',
    'Phone Contact': 'رقم هاتف التواصل والخدمة',
    'Menu URL': 'رابط القائمة على الويب',
    'Google Location': 'الموقع على خرائط جوجل',
    'Field': 'الحقل',
    'Linked': 'مرتبط',
    'Synchronizing Hub...': 'جاري مزامنة وتحديث البيانات...',
    'Data Integrity': 'سلامة ودقة البيانات',
    'Station Governance Suite': 'إدارة محطات الخدمة والمطبخ',
    'Open audit log': 'فتح سجل العمليات والتدقيق',
    'Signal / Time': 'العملية / الوقت والتاريخ',
    'Identity': 'المستخدم / المنفذ',
    'Invitation Invalid': 'رابط الدعوة غير صالح أو منتهي الصلاحية',
    'Invited To': 'تمت دعوتك للانضمام إلى',
    'Team Limit Reached': 'تم الوصول للحد الأقصى لعدد الموظفين',
    'Venue Limit Reached': 'تم الوصول للحد الأقصى لعدد الفروع',
    'Venue Setup Required': 'يلزم استكمال إعدادات المطعم أولاً',
    'Create Venue': 'إضافة مطعم أو فرع جديد',
    'TableGreet Onboarding': 'تهيئة وتجهيز حساب TableGreet',
    'No payment required': 'مجاني تماماً وبدون بطاقة ائتمان',
    'Selected Plan': 'الخطة المختارة',
    'Payment Method': 'طريقة الدفع',
    'Invoice ID': 'رقم الفاتورة',
    'Verified': 'تم التحقق بنجاح',
    'Accent color': 'لون التمييز الثانوي',
    'Set the voice and overall light level.': 'حدد النبرة العامة ومستوى الإضاءة والمظهر.',
    'Layout architecture': 'هندسة وتخطيط عرض القائمة',
    'Navigation & motion': 'التنقل وحركات التصفح السلسة',
    'Loading preview engine': 'جاري تحميل محرك المعاينة الفورية',
    'Real Live Preview': 'معاينة حية ومباشرة',
    'Included with Pro & Max': 'مشمول في باقتي Pro و Max',
    'Daypart Scheduling': 'جدولة فترات العمل وأوقات الوجبات',
    'Operational Daypart Architecture': 'هيكلة الفترات التشغيلية والمناوبات',
    'Service Protocol': 'بروتوكول وقواعد تقديم الخدمة',
    'Configuration Engine': 'محرك الضبط والإعدادات المتقدمة',
    'Linked Department': 'القسم التشغيلي المربوط',
    'Team Hub': 'إدارة فريق العمل والموظفين',
    'Team Size': 'عدد أفراد الفريق',
    'Joined': 'انضم في',
    'Cancel Invitation': 'إلغاء دعوة الانضمام',
    'Initialize New Organization': 'إنشاء مؤسسة أو شركة جديدة',
    'Operational': 'نشط ويعمل بشكل طبيعي',
    'Pro Feature': 'ميزة متوفرة في باقة Pro',
    'Current State': 'الحالة الراهنة',
    'Open on the big TV': 'العرض على شاشة التلفاز الكبيرة',
    'Menu Board': 'لوحة القائمة الرقمية للزبائن',
    'Reward': 'المكافأة',
    'Reward name': 'اسم المكافأة',
    'Cancel & refund': 'إلغاء واسترداد المبلغ',
    'Code': 'الرمز / الكود',
    'Find': 'بحث سريع',
    'Complete': 'مكتمل',
    'Seated': 'جالسين بالصالة',
    'No-Show': 'لم يحضر (غياب)',
    'Walk-in': 'بدون حجز مسبق (مباشر)',
    'Party': 'عدد الضيوف (الأفراد)',
    'Turnover': 'معدل دوران وإشغال الطاولات',
    'We couldn\'t load the menu': 'تعذر تحميل قائمة الطعام حالياً',
    'Venue Not Found': 'المطعم أو المكان غير موجود',
    'Live Availability': 'حالة التوفر الفورية',
    'Multi-Language Suite': 'إدارة اللغات والترجمات المتعددة',
    'Key name': 'اسم مفتاح الربط (API)',
    'Revoke API key': 'إلغاء وتعطيل مفتاح الربط (API)',
    'Delete webhook': 'حذف رابط الإشعارات البرمجية (Webhook)',
    'Identity Provider': 'مزود خدمة الهوية المؤسسية (IdP)',
    'Provider': 'المزوّد',
    'Mandatory MFA': 'إلزامية التحقق بخطوتين (MFA)',
    'Account Identity': 'هوية وبيانات الحساب',
    'Login Email': 'البريد الإلكتروني لتسجيل الدخول',
    'New Login Email': 'البريد الإلكتروني الجديد لتسجيل الدخول',
    'Phone Login': 'تسجيل الدخول برقم الهاتف',
    'SMS Verification Code': 'رمز التحقق عبر الرسائل القصيرة (SMS)',
    'Integration Method': 'طريقة الربط والتكامل',
    'Stripe Connect': 'بوابة الدفع الإلكتروني (Stripe Connect)',
    'Stripe Secret Key': 'المفتاح السري لبوابة Stripe',
    'Stripe Webhook Secret': 'رمز أمان الإشعارات الفورية (Webhook Secret)',
    'Client-Owned': 'مملوك ومدار من قبلك',
    'Verify connection': 'اختبار وتأكيد صحة الاتصال',
    'Setup Health': 'جاهزية الإعدادات وتوافقها',
    'Privacy Protected': 'بيانات محمية وخاصة بالكامل',
    'Privacy Policy': 'سياسة الخصوصية',
    'Continue': 'المتابعة',
    'Sign In': 'تسجيل الدخول',
    'Didn\'t get it?': 'لم يصلك الرمز؟',
    'Logged In Account': 'الحساب النشط حالياً',
    'Annual': 'سنوي (وفر شهرين)',
    'Unread': 'غير مقروء',
    'Starred': 'المميزة بنجمة',
    'High / Urgent': 'عالي / عاجل',
    'Mark Read': 'تحديد كمقروء',
    'Delete notification': 'حذف الإشعار',
    'Urgent': 'عاجل جداً',
    'High': 'مرتفع',
    'Normal': 'عادي',
    'Low': 'منخفض',
    'Select a Ticket': 'اختر تذكرة دعم لعرض تفاصيلها',
    'Urgent (Immediate attention)': 'عاجل (يتطلب تدخلاً فورياً)',
    'Type your reply here...': 'اكتب ردك أو استفسارك هنا...',
    'Print Card (PDF)': 'طباعة كرت الطاولة (PDF)',
    'Advanced Printing Engine': 'محرك الطباعة الاحترافي عالي الدقة',
    'Multi-Venue Governance': 'إدارة الفروع والمواقع المتعددة',
    'Network Revenue': 'إجمالي إيرادات كامل الفروع',
    'Live Network Total': 'المجموع الفعلي لشبكة الفروع',
    'Audit Log Hub': 'سجل تدقيق ومراقبة العمليات',
    'Export Log': 'تصدير سجل العمليات',
    'CUSTOM ROLE SCHEMAS': 'مخططات الأدوار والصلاحيات المخصصة',
    'Unique internal role identifier.': 'المعرف البرمجي الداخلي الفريد للدور.',
    'Human-friendly role label.': 'المسمى الوظيفي الواضح المعروض للموظفين.',
    'Marketing Hub': 'مركز التسويق والعروض',
    'Revenue Generation Suite': 'منظومة زيادة المبيعات والإيرادات',
    'Active Campaign Limit Reached (3/3)': 'تم الوصول للحد الأقصى للحملات النشطة (3/3)',
    'Banner Content': 'نص ومحتوى الإعلان',
    'Style Profile': 'النمط والتنسيق البصري',
    'Action URL (Optional)': 'رابط الزر (اختياري)',
    'Promotion Label': 'عنوان أو شارة العرض',
    'Target Selection': 'تحديد الأطباق أو التصنيفات المشمولة',
    'Edit Promotion': 'تعديل العرض الترويجي',
    'Delete Promotion': 'حذف العرض الترويجي',
    'Loyalty Engine': 'برنامج ولاء ومكافآت الضيوف',
    'Reward catalog': 'قائمة المكافآت والجوائز',
    'Add reward': 'إضافة مكافأة جديدة',
    'Loyalty ID': 'رقم عضوية الولاء',
    'No point activity yet': 'لا توجد حركات نقاط مسجلة حتى الآن',
    'Scan or enter QR value': 'امسح الرمز أو أدخل الرقم يدوياً',
    'TableGreet Admin': 'لوحة إدارة TableGreet',
    'Capacity Pacing': 'تنظيم وضبط تدفق الحجوزات',
    'Cancellation Policy': 'سياسة وشروط إلغاء الحجز',
    'Booking Experience': 'تجربة حجز الطاولات للضيوف',
    'Floor Occupancy': 'نسبة إشغال الصالة والطاولات',
    'Currently Seated': 'جالسون في المطعم حالياً',
    'Seat Party': 'إجلاس الضيوف على الطاولة',
    'Seat Table': 'تسكين الطاولة',
    'Select Table': 'اختيار الطاولة',
    'Table / Capacity': 'الطاولة / السعة',
    'Table Name/Number': 'اسم أو رقم الطاولة',
    'Mark Arrived': 'تسجيل وصول الضيف',
    'Mark No-Show': 'تسجيل عدم الحضور',
    'Complete Dining': 'إنهاء الوجبة ومغادرة الطاولة',
    'Standard Dining (Default)': 'تناول الطعام المعتاد في الصالة (افتراضي)',
    'Walk-In Dining Log': 'سجل الضيوف الحاضرين مباشرة (بدون حجز)',
    'Notification Center': 'مركز الإشعارات والتنبيهات',
    'Single Sign-On': 'تسجيل الدخول الموحد للمؤسسات (SSO)',
    'Welcome Aboard!': 'أهلاً بك في منصة TableGreet!',
    'Select your TableGreet plan': 'اختر خطة الاشتراك المناسبة لمطعمك',
    'Free Plan Activation': 'تفعيل الباقة المجانية الدائمة',
    'Venue Created!': 'تم إنشاء وتجهيز مطعمك بنجاح!',
    'Upgrade for Unlimited': 'الترقية لفتح سعة غير محدودة',
    'Auto-Tag Menu': 'التصنيف التلقائي للقائمة',
    'Start Auto-Tagging': 'بدء التصنيف التلقائي الذكي',
    'Launch Auto-Tagger': 'تشغيل مصنف الأطباق الذكي',
    'Launch PDF Importer': 'استيراد القائمة من ملف PDF',
    'Analyzing Menu...': 'جاري تحليل وتدقيق القائمة...',
    'Analyzing Recipe...': 'جاري فحص المكونات والوصفة...',
    'Manual Availability': 'التحكم اليدوي في التوفر',
    'Technical Breakdown': 'البيانات والمواصفات التفصيلية',
    'Dietary Taxonomy': 'تصنيفات الحمية والأنظمة الغذائية',
    'Dietary & Allergen AI': 'الذكاء الاصطناعي للحمية والمحاذير',
    'Inherit from Category': 'الاعتماد على إعدادات القسم والتصنيف',
    'Ingredient Library': 'مستودع المكونات والمقادير',
    'Ingredient Name': 'اسم المكوّن',
    'Menu Organization': 'هيكلة وتنظيم القائمة',
    'Inventory Taxonomy Hub': 'مركز تصنيفات الأطباق والمخزون',
    'Category Intelligence': 'التحليل الذكي للتصنيف',
    'Service Period': 'فترة تقديم الخدمة (الوجبة)',
    'Manual Configuration / All Day': 'إعداد يدوي / متاح طوال اليوم',
    'Operational Department': 'القسم التشغيلي المرتبط',
    'Global / No Department': 'عام / غير مرتبط بقسم محدد',
    'Cancel category': 'إلغاء التصنيف',
    'New category name...': 'اسم التصنيف الجديد...',
    'Save category': 'حفظ التصنيف',
    'QR Matrix Only': 'رمز الاستجابة السريعة (QR) فقط',
    'Vector & PDF': 'ملفات فيكتور و PDF للطباعة',
    'Vector QR (SVG)': 'رمز QR بصيغة فيكتور (SVG)',
    'Vector QR (PDF)': 'رمز QR جاهز للطباعة (PDF)',
    'Save QR Style': 'حفظ تصميم وهيئة رمز QR',
    'Share Link': 'مشاركة الرابط',
    'High Fidelity 300DPI': 'دقة طباعة احترافية فائقة (300 DPI)',
    'Generating 300 DPI PDF...': 'جاري تجهيز ملف الطباعة الفائق (300 DPI)...',
    'Give your venue a point of view.': 'امنح مطعمك طابعاً بصرياً فريداً يعكس روح ضيافتك.',
    'Signage Menu Not Found': 'قائمة الشاشة الرقمية غير موجودة',
    'Scan Menu': 'امسح الرمز لفتح القائمة',
    'Open on your phone': 'فتح القائمة على هاتفك',
    'Full Screen': 'ملء الشاشة',
    'Lock TV board': 'تثبيت شاشة العرض التلفزيونية',
    'Big-Screen Menu': 'قائمة الشاشات الكبيرة والتلفاز',
    'TV Signage': 'لوحات العرض التلفزيونية',
    'General Information': 'المعلومات العامة',
    'Venue Name *': 'اسم المكان *',
    'Public Slug *': 'الرابط التعريفي المباشر *',
    'Venue Logo': 'شعار المكان',
    'Hero Cover Image': 'صورة الغلاف الرئيسية',
    'Venue Timezone': 'المنطقة الزمنية للمكان',
    'Primary Brand Color': 'لون الهوية الأساسي',
    'TableGreet footer credit': 'شارة TableGreet في أسفل الصفحة',
    'White-Label Branding': 'إخفاء شارات المنصة (العلامة البيضاء)',
    'Client DNS Setup': 'إعدادات ربط النطاق الخاص (DNS)',
    'Copy CNAME target': 'نسخ قيمة هدف CNAME',
    'Copy TableGreet URL': 'نسخ رابط TableGreet المخصص',
    'Included TableGreet URL': 'رابط TableGreet المضمّن مجاناً',
    'Open TableGreet URL': 'فتح رابط TableGreet',
    'Public menu': 'قائمة الطعام العامة للزبائن',
    'Public venue delivery': 'خدمة التوصيل من القائمة العامة',
    'Zone name': 'اسم منطقة التوصيل',
    'Coverage note': 'ملاحظة التغطية ونطاق التوصيل',
    'Default ETA min': 'الحد الأدنى للوقت المتوقع (بالدقائق)',
    'Default ETA max': 'الحد الأقصى للوقت المتوقع (بالدقائق)',
    'Packaging fee': 'رسوم التغليف والتعبئة',
    'Courier handled by': 'المسؤول عن التوصيل',
    'Third-party courier': 'شركة توصيل خارجية معتمدة',
    'Unified Experience': 'تجربة ضيافة موحدة',
    'Save WiFi to Venue': 'حفظ شبكة الواي فاي للضيوف',
    'Network Name (SSID)': 'اسم شبكة الواي فاي (SSID)',
    'Audio Alert Chime': 'نغمة تنبيه الطلبات والخدمة',
    'Time Format': 'صيغة عرض الوقت',
    'Auto / Default': 'تلقائي / الافتراضي',
    'Syncing SEO & Google Data...': 'جاري مزامنة بيانات محركات البحث وجوجل...',
    'Google Connection': 'الربط مع محرك بحث جوجل',
    'Search Engine Metadata': 'بيانات الظهور في محركات البحث',
    'Social card preview': 'معاينة المشاركة في وسائل التواصل',
    'Allow Indexing': 'السماح بالأرشفة في محركات البحث',
    'Canonical URL Override': 'تحديد الرابط الأساسي المعتمد (Canonical)',
    'Price Range': 'فئة الأسعار',
    'Latitude': 'خط العرض',
    'Longitude': 'خط الطول',
    'Google Place ID / Location ID': 'معرّف المكان على خرائط جوجل (Place ID)',
    'Google Search Preview': 'معاينة النتيجة في بحث جوجل',
    'Linked Google Location': 'الموقع المرتبط بخرائط جوجل',
    'SEO Documentation': 'دليل تحسين محركات البحث (SEO)',
    ', and': '، و',
    'and': 'و',
    'Delete Department?': 'حذف القسم؟',
    'Delete Department': 'حذف القسم',
    'Live URL': 'الرابط المباشر',
    'Copy full live URL': 'نسخ الرابط المباشر كاملاً',
    'Open live department menu': 'فتح قائمة طعام القسم مباشرة',
    'Loading departments...': 'جاري تحميل الأقسام...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'تقسيم المكان إلى أقسام متعددة هي ميزة متقدمة متوفرة في باقة PRO فما فوق.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'قسّم قائمة طعامك حسب الصالات أو الطاولات أو البارات أو المناطق المختلفة.',
    'Targeted Ordering': 'توجيه الطلبات الذكي',
    'Route guests and orders to specific preparation areas.': 'وجّه الضيوف والطلبات إلى مناطق وتجهيزات التحضير المخصصة بدقة.',
    'Operational Departments': 'الأقسام التشغيلية',
    'OPERATIONAL DEPARTMENTS': 'الأقسام التشغيلية',
    'Venue Segmentation Hub': 'مركز تقسيم وتنظيم مناطق المكان',
    'VENUE SEGMENTATION HUB': 'مركز تقسيم وتنظيم مناطق المكان',
    'Create departments to group your menu sections. For example, a large venue might have a': 'أنشئ أقساماً لتنظيم وتبويب قائمة طعامك. على سبيل المثال، قد يضم المكان الكبير',
    'Restaurant': 'مطعم',
    'Poolside': 'مسبح',
    'department, each with its own categories.': '، ولكل قسم تصنيفاته وقوائمه الخاصة.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'تتيح لك الأقسام تقسيم المكان إلى مناطق تشغيلية مثل المطعم أو البار أو المسبح — ولكل قسم تصنيفاته وقوائمه الخاصة.',
    'Department name': 'اسم القسم',
    'New department name...': 'اسم القسم الجديد...',
    'No Linked Daypart (Always Available)': 'غير مرتبط بفترة عمل (متاح دائماً)',
    'Linked Daypart': 'فترة العمل المرتبطة',
    'Localizations': 'الترجمات واللغات',
    'LOCALIZATIONS': 'الترجمات واللغات',
    'AI Auto-Translate': 'ترجمة تلقائية بالذكاء الاصطناعي',
    'AI AUTO-TRANSLATE': 'ترجمة تلقائية بالذكاء الاصطناعي',
    'Translated name': 'الاسم المترجم',
    'No Departments Yet': 'لا توجد أقسام بعد',
    'Add First Department': 'إضافة أول قسم',
    'Avg Order Value': 'متوسط قيمة الطلب',
    'AVG ORDER VALUE': 'متوسط قيمة الطلب',
    'item taps': 'نقرات على الأطباق',
    'ITEM TAPS': 'نقرات على الأطباق',
    'Avg Rating': 'متوسط التقييم',
    'AVG RATING': 'متوسط التقييم',
    'Revenue (Last 7 Days)': 'الإيرادات (آخر 7 أيام)',
    'REVENUE (LAST 7 DAYS)': 'الإيرادات (آخر 7 أيام)',
    'Daily breakdown': 'التوزيع اليومي',
    'DAILY BREAKDOWN': 'التوزيع اليومي',
    'Menu Views (Last 7 Days)': 'مشاهدات القائمة (آخر 7 أيام)',
    'MENU VIEWS (LAST 7 DAYS)': 'مشاهدات القائمة (آخر 7 أيام)',
    'Guest traffic': 'حركة وإقبال الضيوف',
    'GUEST TRAFFIC': 'حركة وإقبال الضيوف',
    'Most Viewed Items': 'الأطباق الأكثر تصفحاً',
    'MOST VIEWED ITEMS': 'الأطباق الأكثر تصفحاً',
    'Top Rated Items': 'الأطباق الأعلى تقييماً',
    'TOP RATED ITEMS': 'الأطباق الأعلى تقييماً',
    'Waiter Requests': 'نداءات وطلبات النادل',
    'WAITER REQUESTS': 'نداءات وطلبات النادل',
    'Total': 'الإجمالي',
    'TOTAL': 'الإجمالي',
    'Order Status (30 days)': 'حالة الطلبات (آخر 30 يوماً)',
    'ORDER STATUS (30 DAYS)': 'حالة الطلبات (آخر 30 يوماً)',
    'Recent Guest Feedback': 'أحدث آراء وتقييمات الضيوف',
    'RECENT GUEST FEEDBACK': 'أحدث آراء وتقييمات الضيوف',
    'Conversion Rate': 'معدل إتمام الطلبات',
    'CONVERSION RATE': 'معدل إتمام الطلبات',
    'of menu visitors placed an order': 'من زوار القائمة قاموا بإتمام طلب',
    'Refreshed': 'تم التحديث',
    'REFRESHED': 'تم التحديث',
    'Last 30 days': 'آخر 30 يوماً',
    'LAST 30 DAYS': 'آخر 30 يوماً',
    'cash': 'نقداً',
    'Cash': 'نقداً',
    'CASH': 'نقداً',
    'bill': 'الحساب والفاتورة',
    'Bill': 'الحساب والفاتورة',
    'BILL': 'الحساب والفاتورة',
    'water': 'ماء',
    'Water': 'ماء',
    'WATER': 'ماء',
    'service': 'خدمة',
    'Service': 'خدمة',
    'SERVICE': 'خدمة',
    'No interaction data yet': 'لا توجد بيانات تفاعل بعد',
    'No ratings collected yet': 'لم يتم تسجيل أي تقييمات بعد',
    'No requests in period': 'لا توجد طلبات خلال هذه الفترة',
    'No feedback collected yet': 'لم يتم استلام أي تعليقات أو ملاحظات بعد',
    'No menu views recorded yet': 'لم يتم تسجيل أي مشاهدات للقائمة بعد',
    'Dish Rating Breakdown': 'تفاصيل تقييم الأطباق',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'تصنيفات الجودة ومؤشرات رضا الضيوف عن الأطباق متاحة حصرياً لمشتركي باقات MAX و Enterprise.',
    'Customer Sentiment & Reviews': 'تحليل مشاعر وآراء الضيوف',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'الوصول المباشر لآراء الضيوف، والتحليل الذكي للتقييمات يتطلب اشتراكاً في باقة MAX أو Enterprise.',
    'Reviews AI': 'الذكاء الاصطناعي للتقييمات',
    'Guest Feedback': 'آراء وملاحظات الضيوف',
    'Upgrade to unlock rating analytics →': 'قم بالترقية لفتح تحليلات التقييمات ←',
    'Upgrade to Max': 'الترقية إلى باقة MAX',
    'UPGRADE TO MAX': 'الترقية إلى باقة MAX',
    'MAX PLAN': 'باقة MAX',
    'No Data Yet': 'لا توجد بيانات حتى الآن',
    'Share your QR code to start collecting real venue insights.': 'شارك رمز الاستجابة السريعة (QR) لبدء جمع تحليلات وإحصاءات دقيقة عن نشاط المكان.',
    'Revenue Analytics': 'تحليلات الإيرادات',
    'REVENUE ANALYTICS': 'تحليلات الإيرادات',
    'Track your earnings, orders, and average ticket values over time.': 'تابع أرباحك، وطلباتك، ومتوسط قيمة الفاتورة بمرور الوقت.',
    'Operational Velocity': 'سرعة وكفاءة العمليات',
    'OPERATIONAL VELOCITY': 'سرعة وكفاءة العمليات',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'راقب إقبال الضيوف، والأطباق الأكثر طلباً، وعبء نداءات وطلبات الخدمة.',
    'Upgrade to Pro': 'الترقية إلى برو',
    'UPGRADE TO PRO': 'الترقية إلى باقة PRO',
    'Loading Insights…': 'جاري تحميل مؤشرات الأداء…',
    'Loading Insights...': 'جاري تحميل مؤشرات الأداء...',
    'Telemetry Unavailable': 'بيانات النشاط غير متوفرة حالياً',
    'TELEMETRY UNAVAILABLE': 'بيانات النشاط غير متوفرة حالياً',
    'No Data Captured': 'لم يتم تسجيل أي بيانات بعد',
    'NO DATA CAPTURED': 'لم يتم تسجيل أي بيانات بعد',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'شارك رمز الاستجابة السريعة (QR) لبدء جمع تحليلات الزوار والطلبات مباشرة في الوقت الفعلي.',
    'INTELLIGENCE STUDIO': 'استوديو التحليلات',
    'Insights Engine Locked': 'محرك التحليلات المتقدمة مقفل',
    'INSIGHTS ENGINE LOCKED': 'محرك التحليلات المتقدمة مقفل',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'التحليلات المتقدمة للأداء، والعمليات الجماعية للقائمة، وتصدير التقارير مخصصة لمشتركي باقة',
    'Unlock Pro Features': 'ترقية وتفعيل ميزات Pro',
    'UNLOCK PRO FEATURES': 'ترقية وتفعيل ميزات PRO',
    'MENU VIEWS': 'مشاهدات القائمة',
    'ITEM INTERACTIONS': 'التفاعل مع الأطباق',
    'GUEST RATING': 'تقييم الضيوف',
    'DELIVERY REVENUE': 'إيرادات التوصيل',
    'AVERAGE DELIVERY ORDER': 'متوسط قيمة طلب التوصيل',
    'AVERAGE DELIVERY FEE': 'متوسط رسوم التوصيل',
    'INCLUDING FEES': 'شامل الرسوم',
    'ZONE AVERAGE': 'متوسط نطاق المنطقة',
    'TRAFFIC PROJECTIONS': 'توقعات حركة الزوار',
    'TELEMETRY SIGNAL TRACKING': 'تتبع مؤشرات النشاط',
    'VEGETARIAN': 'نباتي',
    'VEGAN': 'نباتي صرف',
    'SPICY': 'حار',
    'GLUTEN-FREE': 'خال من الغلوتين',
    'Reset: All In-Stock': 'إعادة ضبط: الكل متوفر بالكامل',
    'Emergency Stop (All Out)': 'إيقاف طارئ: إغلاق كل الأطباق (نفد بالكامل)',
    'No compliance issues': 'لا توجد أية ملاحظات',
    'NO COMPLIANCE ISSUES': 'لا توجد أية ملاحظات',
    'detected in current menu': 'في قائمة الطعام الحالية',
    'DETECTED IN CURRENT MENU': 'في قائمة الطعام الحالية',
    'Telemetry History (24h)': 'سجل النشاط (24 ساعة)',
    'TELEMETRY HISTORY (24H)': 'سجل النشاط (24 ساعة)',
    'Telemetry History (30d)': 'سجل النشاط (30 يوماً)',
    'TELEMETRY HISTORY (30D)': 'سجل النشاط (30 يوماً)',
    'Save': 'حفظ',
    'Saved': 'تم الحفظ',
    'Saving...': 'جاري الحفظ...',
    'Save Changes': 'حفظ التغييرات',
    'Save Settings': 'حفظ الإعدادات',
    'Cancel': 'إلغاء',
    'Close': 'إغلاق',
    'Create': 'إنشاء',
    'Add': 'إضافة',
    'Edit': 'تعديل',
    'Delete': 'حذف',
    'Remove': 'إزالة',
    'Update': 'تحديث',
    'Refresh': 'تحديث',
    'Search': 'بحث',
    'Search...': 'بحث...',
    'Search items...': 'البحث عن أطباق...',
    'Search categories...': 'البحث عن الفئات...',
    'Filter': 'تصفية',
    'All': 'الكل',
    'Export': 'تصدير',
    'Import': 'استيراد',
    'Download': 'تنزيل',
    'Upload': 'رفع',
    'Copy': 'نسخ',
    'Copied': 'تم النسخ',
    'Copied!': 'تم النسخ!',
    'Preview': 'معاينة',
    'Open': 'مفتوح',
    'View': 'عرض',
    'Manage': 'إدارة',
    'Configure': 'تهيئة',
    'Enable': 'تفعيل',
    'Disable': 'تعطيل',
    'Enabled': 'مفعل',
    'Disabled': 'معطل',
    'Active': 'نشط',
    'Inactive': 'غير نشط',
    'Available': 'متوفر',
    'Unavailable': 'غير متوفر',
    'Visible': 'مرئي',
    'Hidden': 'مخفي',
    'Required': 'مطلوب',
    'Optional': 'اختياري',
    'Confirm': 'تأكيد',
    'Back': 'رجوع',
    'Next': 'التالي',
    'Done': 'تم',
    'Upgrade': 'ترقية الخطة',
    'Sign Out': 'تسجيل الخروج',
    'Exit to Main Admin': 'العودة إلى الإدارة الرئيسية',
    'Change Language': 'تغيير اللغة',
    'Add Category': 'إضافة فئة',
    'Add Menu Item': 'إضافة عنصر إلى القائمة',
    'Add Item': 'إضافة عنصر',
    'Add Ingredient': 'إضافة مكون',
    'Add Department': 'إضافة قسم',
    'Delete Item': 'حذف العنصر',
    'Delete Category': 'حذف الفئة',
    'Upload Image': 'رفع صورة',
    'Upload Photo': 'رفع صورة',
    'Select Category': 'اختر الفئة',
    'Select Department': 'اختر القسم',
    'Add translation...': 'إضافة ترجمة...',
    'Clear': 'مسح',
    'Reset': 'إعادة ضبط',
    'Apply': 'تطبيق',
    'Submit': 'إرسال',
    'Core & Operations': 'العمليات والخدمة',
    'Menu & Experience': 'قائمة الطعام',
    'Enterprise & Security': 'الإدارة والأمان',
    'System & Control': 'النظام والإعدادات',
    'Intelligence Studio': 'استوديو التحليلات',
    'Venue Insights': 'المبيعات والتقارير',
    'Reservations': 'الحجوزات',
    'Floor Plan': 'مخطط الصالة',
    'Live Waitlist': 'قائمة الانتظار',
    'Walk-Ins': 'طلبات الحضور المباشر',
    'Walk-ins': 'طلبات الحضور المباشر',
    'Departments': 'الأقسام',
    'Service Hours': 'أوقات الخدمة',
    'Categories': 'الفئات',
    'Menu Categories': 'فئات القائمة',
    'Menu Items': 'أطباق القائمة',
    'Items': 'العناصر',
    'Ingredients': 'المكونات',
    'Loyalty Program': 'برنامج الولاء',
    'Campaign Studio': 'العروض والخصومات',
    'Signage': 'شاشات العرض',
    'QR Code': 'رموز الاستجابة السريعة QR',
    'Kitchen Ops': 'شاشة المطبخ',
    'Theme Engine': 'تصميم القائمة وألوانها',
    'Governance': 'الحوكمة والإدارة',
    'Audit Log': 'سجل النشاطات',
    'API & Webhooks': 'الربط والتكامل',
    'SSO & Security': 'أمان الحساب والدخول',
    'Team': 'فريق العمل',
    'Billing': 'الاشتراك والفواتير',
    'SEO & Google': 'الظهور في جوجل',
    'Notifications': 'الإشعارات',
    'Support Center': 'المساعدة والدعم',
    'Settings': 'إعدادات المطعم',
    'Name': 'الاسم',
    'Description': 'الوصف',
    'Price': 'السعر',
    'Status': 'الحالة',
    'Actions': 'الإجراءات',
    'Role': 'الدور',
    'Email': 'البريد الإلكتروني',
    'Phone': 'الهاتف',
    'Address': 'العنوان',
    'City': 'المدينة',
    'Currency': 'العملة',
    'Language': 'اللغة',
    'Theme': 'السمة',
    'General': 'عام',
    'Total Revenue': 'إجمالي المبيعات',
    'Total Orders': 'إجمالي الطلبات',
    'Average Order': 'متوسط الطلب',
    'Active Sessions': 'الجلسات النشطة',
    'Recent Orders': 'أحدث الطلبات',
    'Live Orders': 'الطلبات المباشرة',
    'Table': 'طاولة',
    'Date': 'التاريخ',
    'Time': 'الوقت',
    'Amount': 'المبلغ',
    'Customer': 'الزبون',
    'In Stock': 'متوفر',
    'Out of Stock': 'غير متوفر',
    'Sold Out': 'نفد',
    'Dine In': 'في المطعم',
    'Pickup': 'استلام',
    'Delivery': 'توصيل',
    'Paid': 'مدفوع',
    'Unpaid': 'غير مدفوع',
    'Pending': 'قيد الانتظار',
    'Preparing': 'قيد التحضير',
    'Ready': 'جاهز',
    'Completed': 'مكتمل',
    'Cancelled': 'ملغى',
    'Loading...': 'جاري التحميل...',
    'No data': 'لا توجد بيانات',
    'No results found': 'لم يتم العثور على نتائج',
    'No items found': 'لم يتم العثور على عناصر',
    'No translations defined yet': 'لم تتم إضافة ترجمات بعد',
    'Are you sure?': 'هل أنت متأكد؟',
    'This action cannot be undone.': 'لا يمكن التراجع عن هذا الإجراء.',
    'Settings saved successfully!': 'تم حفظ الإعدادات بنجاح!',
    'Administrator': 'المسؤول',
    'Manager': 'المدير',
    'Owner': 'المالك',
    'Chef': 'الشيف',
    'Waiter': 'النوادل',
    'Cashier': 'الكاشير',
    'Type a command or search...': 'اكتب أمراً أو ابحث...',
    'Command Palette': 'لوحة الأوامر',
    'Demand Velocity': 'معدل الإقبال على الأطباق',
    'DEMAND VELOCITY': 'معدل الإقبال على الأطباق',
    'Top Performing Entities': 'الأطباق الأكثر طلباً',
    'TOP PERFORMING ENTITIES': 'الأطباق الأكثر طلباً',
    'Hourly Load Analysis': 'تحليل الإقبال حسب ساعات اليوم',
    'HOURLY LOAD ANALYSIS': 'تحليل الإقبال حسب ساعات اليوم',
    'Bulk Command Center': 'مركز العمليات السريعة الجماعية',
    'BULK COMMAND CENTER': 'مركز العمليات السريعة الجماعية',
    'Governance Scan': 'فحص سلامة القائمة والأسعار',
    'GOVERNANCE SCAN': 'فحص سلامة القائمة والأسعار',
    'No compliance issues detected in current menu': 'القائمة مطابقة بالكامل ولا توجد أي مشاكل',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'القائمة مطابقة بالكامل ولا توجد أي مشاكل',
    'Procedural Tagging': 'إضافة الشارات التلقائية للأطباق',
    'PROCEDURAL TAGGING': 'إضافة الشارات التلقائية للأطباق',
    'Name match...': 'مطابقة الاسم...',
    '...Name match': 'مطابقة الاسم...',
    'Healthy': 'صحي',
    'HEALTHY': 'صحي',
    'Apply Tags': 'تطبيق الشارات',
    'APPLY TAGS': 'تطبيق الشارات',
    'Global Price Shift': 'تعديل الأسعار العام بنسبة مئوية',
    'GLOBAL PRICE SHIFT': 'تعديل الأسعار العام بنسبة مئوية',
    'Adjust': 'تطبيق التعديل',
    'ADJUST': 'تطبيق التعديل',
    'Availability Override': 'التحكم الفوري في توفر الأطباق',
    'AVAILABILITY OVERRIDE': 'التحكم الفوري في توفر الأطباق',
    'Reset: ALL IN-STOCK': 'إعادة ضبط: الكل متوفر بالكامل',
    'RESET: ALL IN-STOCK': 'إعادة ضبط: الكل متوفر بالكامل',
    'EMERGENCY STOP (ALL OUT)': 'إيقاف طارئ: إغلاق كل الأطباق (نفد بالكامل)',
    'Integrity Engine': 'محرك حماية وجودة القائمة',
    'INTEGRITY ENGINE': 'محرك حماية وجودة القائمة',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'يراقب هذا النظام الذكي توفر الأطباق بدقة، ويكتشف أخطاء الأسعار غير المعتادة، ويضمن اكتمال تفاصيل الأطباق عبر كامل فروعك.',
    'Inventory Audit Export': 'تصدير تدقيق المخزون والقائمة',
    'INVENTORY AUDIT EXPORT': 'تصدير تدقيق المخزون والقائمة',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'إنشاء وتنزيل نسخة شاملة لبيانات أداء القائمة والأسعار للمراجعة والتدقيق المكتبي.',
    'Enterprise Data Export': 'تصدير البيانات المتقدم للمؤسسات',
    'ENTERPRISE DATA EXPORT': 'تصدير البيانات المتقدم للمؤسسات',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'تنزيل تقارير تفصيلية شاملة بصيغ قياسية معتمدة تشمل حركة الزوار، وسرعة إقبال الضيوف على الأطباق، ومعدلات الطلب الفعلية.',
    'JSON Snapshot': 'نسخة JSON الفورية',
    'JSON SNAPSHOT': 'نسخة JSON الفورية',
    'Compile CSV Report': 'إنشاء تقرير CSV',
    'COMPILE CSV REPORT': 'إنشاء تقرير CSV',
    'Date Segment': 'الفترة الزمنية',
    'DATE SEGMENT': 'الفترة الزمنية',
    'Inbound Views': 'الزيارات والمشاهدات',
    'INBOUND VIEWS': 'الزيارات والمشاهدات',
    'Asset Interactions': 'التفاعل مع الأطباق',
    'ASSET INTERACTIONS': 'التفاعل مع الأطباق',
    'Telemetry History': 'سجل مؤشرات النشاط',
    'TELEMETRY HISTORY': 'سجل مؤشرات النشاط',
    'Telemetry History (7d)': 'سجل مؤشرات النشاط (7 أيام)',
    'TELEMETRY HISTORY (7D)': 'سجل مؤشرات النشاط (7 أيام)',
    'Growth Trajectory': 'مسار نمو التفاعل',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'معدل تفاعل الضيوف مع القائمة يتجه نحو الارتفاع خلال فترة الـ 7 أيام الأخيرة.',
    'Deep Analysis Engine': 'نظام التحليل العميق',
    'DEEP ANALYSIS ENGINE': 'نظام التحليل العميق',
    'Asset Velocity': 'أداء وسرعة رواج الأطباق',
    'ASSET VELOCITY': 'أداء وسرعة رواج الأطباق',
    'Traffic Projections': 'توقعات حركة الزوار',
    'Telemetry Signal Tracking': 'تتبع مؤشرات النشاط',
    'Service Hour Update': 'تحديث أوقات الخدمة',
    'Late Night Lounge Hours': 'أوقات السهرة الليلية',
    'Bar & Lounge Hours': 'أوقات البار والصالة',
    'Breakfast & Brunch Hours': 'أوقات الإفطار والبرانش',
    'Switch Location': 'تبديل الفرع',
    'SWITCH LOCATION': 'تبديل الفرع',
    'Global Admin': 'مسؤول عام',
    'GLOBAL ADMIN': 'مسؤول عام',
    'Staff Access': 'وصول الموظفين',
    'STAFF ACCESS': 'وصول الموظفين',
    'Venue Overview': 'نظرة عامة على الفرع',
    'VENUE OVERVIEW': 'نظرة عامة على الفرع',
    'Unified Telemetry Engine': 'نظام متابعة النشاط الموحد',
    'UNIFIED TELEMETRY ENGINE': 'نظام متابعة النشاط الموحد',
    'Pulse': 'النبض المباشر',
    'PULSE': 'النبض المباشر',
    'Insights': 'الرؤى والتحليلات',
    'INSIGHTS': 'الرؤى والتحليلات',
    'Reports': 'التقارير',
    'REPORTS': 'التقارير',
    'GOVERNANCE': 'الحوكمة والإدارة',
    'Multi-Venue': 'الفروع المتعددة',
    'MULTI-VENUE': 'الفروع المتعددة',
    'Menu Views': 'مشاهدات القائمة',
    'Item Interactions': 'تفاعل الزوار مع الأطباق',
    'Guest Rating': 'تقييم الضيوف',
    'Revenue (7d)': 'الإيرادات (7 أيام)',
    'Revenue (24h)': 'الإيرادات (24 ساعة)',
    'Revenue (30d)': 'الإيرادات (30 يوماً)',
    'Delivery orders (7d)': 'طلبات التوصيل (7 أيام)',
    'Delivery orders (24h)': 'طلبات التوصيل (24 ساعة)',
    'Delivery orders (30d)': 'طلبات التوصيل (30 يوماً)',
    'Delivery revenue': 'إيرادات التوصيل',
    'Average delivery order': 'متوسط قيمة طلب التوصيل',
    'Average delivery fee': 'متوسط رسوم التوصيل',
    'Zone average': 'متوسط المنطقة',
    'Including fees': 'شامل الرسوم',
    'No deliveries': 'لا توجد طلبات توصيل',
    'No orders': 'لا توجد طلبات',
    'No reviews': 'لا توجد تقييمات',
    'Live': 'مباشر',
    'LIVE': 'مباشر',
    'reviews': 'تقييم',
    'orders': 'طلبات',
    'deliveries': 'توصيلات',
    'of orders': 'من الطلبات',
    'rate': 'معدل',
    'Recent Activity': 'النشاط الأخير',
    'Real-time Venue Telemetry': 'متابعة نشاط الفرع لحظياً',
    'REAL-TIME VENUE TELEMETRY': 'متابعة نشاط الفرع لحظياً',
    'Rapid Pulse View': 'عرض النبض السريع',
    'Anonymous Guest': 'ضيف غير مسجل',
    'scanned menu': 'مسح القائمة',
    'viewed item': 'شاهد الطبق',
    'interacted with': 'تفاعل مع',
    'placed order': 'أرسل طلباً',
    'searched menu': 'بحث في القائمة',
    'Main Landing': 'الصفحة الرئيسية',
    'Trace': 'تتبع موثق',
    'TRACE': 'تتبع موثق',
    'Gaining Telemetry...': 'جاري جمع بيانات النشاط...',
    'No recent traffic detected': 'لم يتم رصد أي نشاط مؤخراً',
    'Activities will appear here as guests scan your QR code': 'ستظهر الأنشطة هنا بمجرد مسح الضيوف لرمز QR الخاص بك',
    'Analyze All Logs': 'عرض وتحليل السجل الكامل',
    'Just now': 'الآن',
    'less than a minute ago': 'منذ أقل من دقيقة',
    '1 minute ago': 'منذ دقيقة واحدة',
    '1 hour ago': 'منذ ساعة واحدة',
    'about 1 hour ago': 'منذ حوالي ساعة',
    '1 day ago': 'منذ يوم واحد',
    'DAY AGO 1': 'منذ يوم واحد',
    '2 days ago': 'منذ يومين',
    'DAY AGO 2': 'منذ يومين',
    '3 days ago': 'منذ 3 أيام',
    'DAY AGO 3': 'منذ 3 أيام',
    '4 days ago': 'منذ 4 أيام',
    'DAY AGO 4': 'منذ 4 أيام',
    '5 days ago': 'منذ 5 أيام',
    'DAY AGO 5': 'منذ 5 أيام',
    '6 days ago': 'منذ 6 أيام',
    'DAY AGO 6': 'منذ 6 أيام',
    '7 days ago': 'منذ 7 أيام',
    'DAY AGO 7': 'منذ 7 أيام',
    'about 1 month ago': 'منذ حوالي شهر',
    'Monday': 'الاثنين',
    'Tuesday': 'الثلاثاء',
    'Wednesday': 'الأربعاء',
    'Thursday': 'الخميس',
    'Friday': 'الجمعة',
    'Saturday': 'السبت',
    'Sunday': 'الأحد',
    'Mon': 'إثن',
    'Tue': 'ثلا',
    'Wed': 'أرب',
    'Thu': 'خمي',
    'Fri': 'جمع',
    'Sat': 'سبت',
    'Sun': 'أحد',
    'Opening Hours': 'ساعات العمل',
    'Start Time': 'وقت البدء',
    'End Time': 'وقت الانتهاء',
    'Opening Time': 'وقت الافتتاح',
    'Closing Time': 'وقت الإغلاق',
    'All Day': 'طوال اليوم',
    'Closed': 'مغلق',
    'Active Days': 'أيام العمل',
    'Active Schedule': 'الجدول المفعل',
    'Always Available': 'متاح دائماً',
    'Always Visible': 'ظاهر دائماً',
    'New Category': 'فئة جديدة',
    'Category': 'الفئة',
    'Category Name': 'اسم الفئة',
    'Category Description': 'وصف الفئة',
    'Display Order': 'ترتيب العرض',
    'Item Name': 'اسم الطبق',
    'Item Description': 'وصف الطبق',
    'Base Price': 'السعر الأساسي',
    'Preparation Time': 'وقت التحضير',
    'Calories': 'السعرات الحرارية',
    'Featured Item': 'طبق مميز',
    'Chef\'s Recommendation': 'توصية الشيف',
    'Spicy': 'حار',
    'Vegetarian': 'نباتي',
    'Vegan': 'نباتي صرف',
    'Gluten-Free': 'خالٍ من الغلوتين',
    'Halal': 'حلال',
    'Kosher': 'كوشر',
    'Contains Nuts': 'يحتوي على مكسرات',
    'Dairy-Free': 'خالٍ من منتجات الألبان',
    'Allergen Exclusions': 'مسببات الحساسية',
    'Table Number': 'رقم الطاولة',
    'Assigned Table': 'الطاولة المخصصة',
    'Seats': 'المقاعد',
    'Capacity': 'السعة',
    'Indoor': 'داخلي',
    'Outdoor': 'خارجي',
    'Terrace': 'شرفة (تراس)',
    'Bar': 'البار',
    'Add Table': 'إضافة طاولة',
    'Edit Table': 'تعديل الطاولة',
    'Delete Table': 'حذف الطاولة',
    'Bookings': 'الحجوزات',
    'Add to Waitlist': 'إضافة إلى قائمة الانتظار',
    'Guest Name': 'اسم الضيف',
    'Party Size': 'عدد الأفراد',
    'Arrival Time': 'وقت الوصول',
    'Special Requests': 'طلبات خاصة',
    'Confirmed': 'مؤكد',
    'No Show': 'لم يحضر',
    'Add Staff Member': 'إضافة موظف',
    'Invite Staff': 'دعوة موظف',
    'Email Address': 'البريد الإلكتروني',
    'Phone Number': 'رقم الهاتف',
    'Download QR Code': 'تحميل رمز QR',
    'Print Table Stands': 'طباعة حوامل الطاولات',
    'Today': 'اليوم',
    'Yesterday': 'أمس',
    'This Week': 'هذا الأسبوع',
    'This Month': 'هذا الشهر',
    'Last 30 Days': 'آخر 30 يومًا',
    'Access Denied': 'تم رفض الوصول',
    'Advanced Settings': 'إعدادات متقدمة',
    'Change Image': 'تغيير الصورة',
    'Remove Image': 'حذف الصورة',
    'Announcement Banner': 'شريط الإعلانات',
    'Please fill in all required fields.': 'يرجى ملء جميع الحقول المطلوبة.',
    'Changes saved successfully': 'تم حفظ التغييرات بنجاح',
    '(You)': '(أنت)',
    'AI Auto-Tag': 'تصنيف تلقائي بالذكاء الاصطناعي',
    'Analyzing:': 'جاري التحليل:',
    'Apple (Fruit/Vegan)': 'تفاح (فواكه/نباتي صرف)',
    'Cancel editing': 'إلغاء التعديل',
    'Capacity:': 'السعة:',
    'Carrot (Veggie)': 'جزر (خضار)',
    'CircleDot (Organic)': 'نقطة دائرية (عضوي)',
    'Coffee (Caffeine)': 'قهوة (كافيين)',
    'Configuration': 'الإعدادات والتهيئة',
    'Copy verification record name': 'نسخ اسم سجل التحقق',
    'Copy verification record value': 'نسخ قيمة سجل التحقق',
    'Critical exception intercepted': 'تم اعتراض خطأ استثنائي',
    'Kernel Panic': 'خطأ في النظام',
    'DNA (Organic/Bio)': 'حمض نووي (عضوي/طبيعي)',
    'Egg (Ovo/Allergen)': 'بيض (مشتقات البيض/مسبب حساسية)',
    'Empty': 'فارغ',
    'Engraving...': 'جاري الإنشاء...',
    'Flame (Spicy)': 'لهب (حار)',
    'Free': 'مجاني',
    'From': 'من',
    'Generating': 'إنشاء',
    'Governance & Traceability Studio': 'مركز إدارة النشاط وتتبع العمليات',
    'Greet': 'ترحيب',
    'Heart (Healthy)': 'قلب (صحي)',
    'In-App': 'داخل التطبيق',
    'Infinity SVG': 'صيغة SVG متجهة (عالية الدقة)',
    'Leaf (Default)': 'ورقة (افتراضي)',
    'MAX Feature': 'ميزة الخطة المتقدمة',
    'Milk (Dairy)': 'حليب (مشتقات الألبان)',
    'Network': 'الشبكة',
    'Notify SMS': 'إشعار عبر SMS',
    'Order': 'الطلب',
    'Original': 'الأصلي',
    'Overview': 'نظرة عامة',
    'PRO: 4 PREDEFINED ROLES': 'برو: 4 أدوار أساسية',
    'Shield (Safety)': 'درع (أمان وسلامة)',
    'Soup (Warm/Soup)': 'حساء (دافئ/شوربة)',
    'Standard Pro View': 'العرض القياسي',
    'Star': 'نجمة',
    'Synchronized with Venue Identity': 'مخصص ليتطابق مع هوية منشأتك',
    'Synchronizing Security Context...': 'جاري الاتصال الآمن...',
    'Targeted Timing': 'أوقات الخدمة المستهدفة',
    'Targeting': 'الفئات المشمولة',
    'To': 'إلى',
    'Toggle Navigation': 'تبديل القائمة',
    'Venue': 'المنشأة',
    'Venue Governance Suite': 'إعدادات وعمليات المنشأة',
    'Volume:': 'مستوى الصوت:',
    'WHITEBOARD NOTE...': 'ملاحظة أو تذكير للفريق...',
    'Warning (Alert)': 'تحذير (تنبيه)',
    'Wheat (Flour/Gluten)': 'قمح (دقيق/غلوتين)',
    'Wine (Alcohol)': 'نبيذ (كحول)',
    'YouTube/Vimeo/Direct Link': 'رابط الفيديو (يوتيوب، فيميو أو MP4)',
    'Your name': 'اسمك',
    '2. Table Number': '2. رقم الطاولة',
    'for Morocco or': 'للمغرب، أو',
    'in': 'في',
    'to': 'إلى',
    'e.g. 12 or Patio-1': 'مثال: 12 أو الشرفة-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'مثال: فيتوتشيني طازجة، كمأة سوداء، جبن بارميجيانو معتق 36 شهراً...',
    'e.g. Head Sommelier & Wine Lead': 'مثال: رئيس السقاة ومسؤول المشروبات',
    'e.g. London': 'مثال: لندن',
    'e.g. SOMMELIER, HOST, BARISTA': 'مثال: نادل المشروبات، المضيف، باريستا',
    'e.g. Skyline Lounge': 'مثال: لاونج الأفق',
    'e.g. TRUFFLE OIL': 'مثال: زيت الكمأة',
    'e.g. The Grand Cafe': 'مثال: المقهى الكبير',
    'e.g. Window preference, high chair': 'مثال: بالقرب من النافذة، مقعد أطفال مرتفع',
    'Copied to clipboard!': 'تم النسخ إلى الحافظة!',
    'Copy failed.': 'فشل النسخ.',
    'Link copied!': 'تم نسخ الرابط!',
    'Logo uploaded': 'تم تحميل الشعار',
    'Cover image uploaded': 'تم تحميل صورة الغلاف',
    'Profile saved and synced successfully': 'تم حفظ الملف التعريفي ومزامنته بنجاح',
    'Category Created': 'تم إنشاء الفئة',
    'Category Updated': 'تم تحديث الفئة',
    'Category Deleted': 'تم حذف الفئة',
    'Item Created': 'تم إنشاء الصنف',
    'Item Updated': 'تم تحديث الصنف',
    'Item Deleted': 'تم حذف الصنف',
    'Menu Published': 'تم نشر قائمة الطعام',
    'Delete Category?': 'حذف الفئة؟',
    'Delete Menu Item?': 'حذف هذا الصنف؟',
    'Delete Ingredient?': 'حذف هذا المكوّن؟',
    'Delete Service Period?': 'حذف فترة الخدمة؟',
    'Remove team member?': 'إزالة عضو الفريق هذا؟',
    'Cancel invitation?': 'إلغاء الدعوة؟',
    'Revoke API key?': 'إلغاء مفتاح API؟',
    'Delete webhook endpoint?': 'حذف نقطة نهاية الويب هوك هذه؟',
    'Clear all notifications?': 'مسح جميع الإشعارات؟',
    'Disconnect Stripe account?': 'فصل حساب Stripe؟',
    'Disconnect Google Business Profile?': 'فصل الملف التجاري في Google؟',
    'Campaigns published successfully!': 'تم نشر العروض بنجاح!',
    'Changes successfully synchronized with Google Business Profile!': 'تمت مزامنة التغييرات بنجاح مع الملف التجاري في Google!',
    'Google Business Profile successfully connected!': 'تم ربط الملف التجاري في Google بنجاح!',
    'Google Business Profile disconnected.': 'تم فصل الملف التجاري في Google.',
    'Google Post draft saved successfully!': 'تم حفظ مسودة منشور Google بنجاح!',
    'Post draft deleted.': 'تم حذف مسودة المنشور.',
    'Review response published successfully!': 'تم نشر الرد على التقييم بنجاح!',
    'SEO configuration saved successfully!': 'تم حفظ إعدادات تحسين محركات البحث بنجاح!',
    'SEO metadata translated across all languages!': 'تمت ترجمة بيانات تحسين محركات البحث إلى جميع اللغات!',
    'Venue profile & bio translated across all languages!': 'تمت ترجمة الملف التعريفي والوصف إلى جميع اللغات!',
    'Reservation configuration saved successfully': 'تم حفظ إعدادات الحجز بنجاح',
    'Stripe settings saved successfully.': 'تم حفظ إعدادات Stripe بنجاح.',
    'Print-Ready PDF downloaded successfully': 'تم تحميل ملف PDF الجاهز للطباعة بنجاح',
    'Vector QR PDF downloaded successfully': 'تم تحميل رمز QR المتجه بصيغة PDF بنجاح',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'تم إنشاء حزمة الطباعة الاحترافية بدقة 300 نقطة بنجاح',
    'At least one language must be enabled.': 'يجب تفعيل لغة واحدة على الأقل.',
    'Enter a non-zero percentage before adjusting prices.': 'يرجى إدخال نسبة مئوية لا تساوي صفراً قبل تعديل الأسعار.',
    'Please enter a Venue Name before translating.': 'يرجى إدخال اسم المنشأة قبل بدء الترجمة.',
    'Please enter an item name before running dietary and allergen analysis.': 'يرجى إدخال اسم الصنف قبل بدء تحليل المكونات ومسببات الحساسية.',
    'Please write a reply before submitting.': 'يرجى كتابة رد قبل الإرسال.',
    'Table 3 Called Waiter': 'الطاولة 3 طلبت النادل',
    'Table Call Bell': 'جرس نداء الطاولة',
    'Waiter Calls': 'نداءات النادل',
    'Waiter Dispatch & Calls': 'إدارة وتوجيه نداءات الخدمة',
    'Kitchen Display & Whiteboard': 'شاشة المطبخ ولوحة الملاحظات',
    'Customer Orders': 'طلبات الزبائن',
    'Bakery': 'مخبز وحلويات',
    'Bar / Pub': 'بار / حانة',
    'Café / Coffee Shop': 'مقهى / كوفي شوب',
    'Fast Food': 'وجبات سريعة',
    'Gourmet / Fine Dining': 'مطعم فاخر / فاين داينينغ',
    'Hotel': 'فندق',
    'General Local Business': 'نشاط تجاري عام',
    'Classic': 'كلاسيكي',
    'Clean': 'نقي وبسيط',
    'Modern': 'عصري',
    'Elegant': 'أنيق',
    'Energetic': 'حيوي ومميز',
    'Glass': 'زجاجي أنيق',
    'Playful & Casual': 'مرح وغير رسمي',
    'Sophisticated & Gourmet': 'راقٍ ومتقن',
    'Warm & Welcoming': 'دافئ ومرحّب',
    'Executive': 'تنفيذي راقٍ',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'الترقية مطلوبة: تتوفر قوائم الطعام متعددة اللغات في باقة برو.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'الترقية مطلوبة: لا تتوفر ميزات الطلب المباشر ونداء النادل في باقة البداية المجانية.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'تصدير صيغة SVG المتجهة متاح حصرياً في باقتي برو وماكس. يرجى الترقية لتحميل ملفات متجهة غير محدودة الدقة.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'تصدير ملفات PDF الجاهزة للطباعة متاح حصرياً في باقتي برو وماكس. يرجى الترقية للتصدير.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'باقة برو محدودة بـ 3 عروض ترويجية نشطة. قم بإيقاف عرض آخر مؤقتاً أو قم بالترقية إلى باقة ماكس لعروض غير محدودة.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'تشمل الباقة المبدئية ما يصل إلى 20 طاولة (الطاولات 1-20). قم بالترقية إلى باقة برو لطاولات غير محدودة.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'تدعم الباقة المبدئية الطاولات الرقمية (1-20). تتطلب أسماء الطاولات المخصصة (مثل التراس، VIP) باقة برو.',
    'Add Menu Categories & Items': 'إضافة فئات وأصناف قائمة الطعام',
    'Add Menu Items': 'إضافة أصناف إلى قائمة الطعام',
    'Organize your menu sections and food items.': 'نظّم أقسام قائمة الطعام والأطباق والمشروبات بسهولة.',
    'Add and manage your food & beverage offerings.': 'أضف وأدِر أطباقك ومشروباتك بكل سهولة.',
    'Customize Venue Branding': 'تخصيص الهوية البصرية للمنشأة',
    'Set your brand colors, logo, and presentation.': 'عيّن ألوان علامتك التجارية، وشعارك، وأسلوب العرض.',
    'Get Your QR Code & Go Live': 'احصل على رمز QR وابدأ استقبال الزوار',
    'Download table QR codes and preview your live menu.': 'حمّل رموز QR الخاصة بالطاولات وعاين قائمتك المباشرة.',
    'Account & Venue Created Successfully!': 'تم إنشاء الحساب والمنشأة بنجاح!',
    '⚡ Off-Peak Hours': '⚡ ساعات الهدوء والإقبال المنخفض',
    '🍽️ Lunch Crowd': '🍽️ زوار وجبة الغداء',
    '🚀 Weekend Rush': '🚀 ذروة عطلة نهاية الأسبوع',
    '🌟 Seasonal Special': '🌟 أطباق وعروض الموسم',
    '📣 Engagement Buzz': '📣 تفاعل الزوار والانتشار',
    'Festive & Energetic': 'احتفالي ومفعم بالحيوية',
    'Urgent & High-Conversion': 'عرض محدود الوقت ومؤثر',
    'Generated 3 tailored campaign strategies': 'تم إنشاء 3 استراتيجيات تسويقية مخصصة',
    'Could not generate campaign ideas. Please try again.': 'تعذر إنشاء أفكار العروض الترويجية. يرجى المحاولة مرة أخرى.',
    'Venue Themes': 'سمات ومظهر المنشأة',
    'Choose the character of your public menu': 'اختر الطابع والأسلوب البصري لقائمة طعامك العامة',
    'Glassmorphism & Depth': 'المظهر الزجاجي والعمق البصري',
    'Independent XML Sitemap': 'خريطة موقع XML مستقلة',
    'Structured Schema & Sync': 'البيانات المنظمة والمزامنة',
    'Improve how your venue appears on Google and in local search results.': 'حسّن ظهور منشأتك على Google وفي نتائج البحث المحلية للوصول إلى زوار جدد.',
    'Terms of Service': 'شروط الخدمة',
    'Confirm your email': 'تأكيد بريدك الإلكتروني',
    'Check your spam / junk folder': 'تحقق من مجلد الرسائل غير المرغوب فيها (البريد العشوائي)',
    'Commercial pre-press print production package': 'حزمة الطباعة التجارية الاحترافية عالية الدقة',
    'Over-bleed safety margin': 'هامش أمان لتجاوز حواف القص',
    'Crop Marks': 'علامات القص',
    'Guillotine cutting guides': 'أدلة قص المقصلة الورقية',
    'Global Banners': 'اللافتات العامة العلوية',
    'Featured Items': 'الأطباق المميزة',
    'AI Assistant': 'مساعد الذكاء الاصطناعي',
    'Custom Role Schemas': 'مخططات الأدوار المخصصة',
    'Notification Preferences': 'تفضيلات الإشعارات',
    'Preferences saved automatically': 'تم حفظ التفضيلات تلقائياً',
    'Menu Extractor (PDF & Images)': 'مستخرج قوائم الطعام (ملفات PDF والصور)',
    'Multi-Language Translation': 'الترجمة متعددة اللغات',
    'Start Menu Translation': 'بدء ترجمة قائمة الطعام',
    'Menu Items Hub': 'مركز إدارة أصناف القائمة',
    'Open Menu Items': 'فتح أصناف القائمة',
    'Enterprise Traceability': 'سجل العمليات والتدقيق الشامل',
    'Full History': 'السجل الكامل للنشاط',
    'Billing & Plans': 'الفواتير والباقات',
    'Subscription Governance': 'إدارة وتفاصيل الاشتراك',
    'Platform Usage': 'مستوى استخدام المنصة',
    'Menu Page Views': 'مشاهدات صفحات القائمة',
    'Enterprise Identity': 'إدارة الهوية والدخول المؤسسي',
    'Access Governance': 'حوكمة وسياسات الدخول',
    'Required Fields *': 'حقول مطلوبة *',
    'Enabled Languages': 'اللغات المفعلة',
    'Venue Info & Bio Localizations': 'ترجمات وصف ومعلومات المنشأة',
    'Public Address': 'العنوان الظاهر للزبائن',
    'Public URL Slug': 'رابط الوصول المباشر المختصر',
    'Contact Phone': 'رقم هاتف التواصل',
    'Business Address': 'عنوان النشاط التجاري',
    'No Categories Yet': 'لا توجد فئات بعد',
    'No Custom Roles Configured': 'لم يتم تكوين أدوار مخصصة بعد',
    'No Google reviews retrieved yet': 'لم يتم استرداد أي تقييمات من Google بعد',
    'No Guests Found': 'لم يتم العثور على زوار',
    'No Menu Items Yet': 'لا توجد أطباق في القائمة بعد',
    'No Promotions Configured': 'لا توجد عروض ترويجية مجهزة بعد',
    'No Service Periods Yet': 'لم يتم تحديد فترات خدمة بعد',
    'No Support Tickets': 'لا توجد تذاكر دعم فني',
    'No Tables on Floor Plan': 'لا توجد طاولات على مخطط القاعة',
    'No Team Members Found': 'لم يتم العثور على أعضاء في الفريق',
    'No Upcoming Events': 'لا توجد فعاليات قادمة',
    'No Walk-Ins Recorded': 'لم يتم تسجيل زوار بدون حجز',
    'No items are available right now': 'لا توجد أطباق متوفرة للطلب حالياً',
    'No notifications found': 'لا توجد إشعارات حالياً',
    'No reservations matching your filters': 'لا توجد حجوزات تطابق عوامل التصفية المحددة',
    'No tables currently occupied': 'لا توجد طاولات مشغولة في الوقت الحالي',
    'No venue selected': 'لم يتم تحديد أي منشأة',
    'Waitlist': 'قائمة الانتظار',
    'Waitlist is Empty': 'قائمة الانتظار فارغة',
    'Waves (Gluten)': 'سنابل (غلوتين)',
    'We sent a temporary verification code to': 'لقد أرسلنا رمز تحقق مؤقت إلى',
    'Website URL': 'رابط الموقع الإلكتروني',
    'What the customer receives': 'ما يحصل عليه الزبون',
    'White-label control is available on eligible plans.': 'إزالة حقوق المنصة والعلامة البيضاء متاحة في الباقات المؤهلة.',
    'Why this works:': 'لماذا تنجح هذه الاستراتيجية:',
    'WiFi Broadcast': 'مشاركة شبكة WiFi',
    'WiFi Password': 'كلمة مرور WiFi',
    'Window seat, dietary request, birthday celebration...': 'طاولة بجانب النافذة، طلبات غذائية، احتفال بعيد ميلاد...',
    'Your Published Response': 'ردك المنشور للزبون',
    'Your Venues': 'منشآتك ومطاعمك',
    '🎉 Ready to publish!': '🎉 جاهز للنشر الفوري!',
    'e.g. Grill, Salad, Pastry': 'مثال: المشاوي، السلطات، المعجنات والحلويات',
    'e.g. Guest called, emergency': 'مثال: اتصال من الزبون، حالة طارئة',
    'e.g. Italian, Moroccan, Burgers': 'مثال: إيطالي، مغربي، برغر',
    'e.g. Mains, Pasta, Starters': 'مثال: الأطباق الرئيسية، الباستا، المقبلات',
    'e.g. Signature Truffle Pasta': 'مثال: باستا الكمأة الفاخرة الخاصة بالمطعم',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'مثال: عرض قهوة المساء، ساعة التخفيضات 15%',
    'e.g., Breakfast, Happy Hour': 'مثال: الإفطار الصباحي، ساعة التخفيضات',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'مثال: انضم إلينا للاستمتاع بالموسيقى الحية مساء كل جمعة في تمام الساعة 8:00!',
  },
  es: {
    '123 Main St, City': 'Calle Mayor 123, Ciudad',
    'Balance': 'Saldo de puntos',
    'Lifetime': 'Total histórico',
    'Source': 'Origen',
    'Menu': 'Menú',
    'Advanced': 'Avanzado',
    'Core Suite': 'Herramientas básicas',
    'Privacy': 'Privacidad',
    'Support': 'Soporte',
    'Save Layout': 'Guardar plano de sala',
    'Shape:': 'Forma:',
    'SEARCH LIBRARY...': 'BUSCAR EN LA BIBLIOTECA...',
    'Search library...': 'Buscar en la biblioteca...',
    'None': 'Ninguno',
    'Type': 'Tipo',
    'Target': 'Destino',
    'Value / Target': 'Valor / Destino',
    'London (GMT/BST)': 'Londres (GMT/BST)',
    'New York (EST/EDT)': 'Nueva York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubái (GST)',
    'Tokyo (JST)': 'Tokio (JST)',
    'Sydney (AEST/AEDT)': 'Sídney (AEST/AEDT)',
    'Start Color': 'Color inicial',
    'Depth Shade': 'Sombra y profundidad',
    'Matrix Geometry': 'Geometría del patrón QR',
    'Eye Architecture': 'Esquinas de orientación del QR',
    'Brand Centerpiece': 'Logo central del QR',
    'Scannability Shield': 'Garantía de escaneabilidad',
    'Environment Blur': 'Desenfoque ambiental',
    'Color Target:': 'Objetivo de color:',
    'DPI:': 'DPI (Resolución):',
    '3mm Bleed Box': 'Margen de sangrado de 3 mm',
    '300 DPI': '300 DPI (Alta definición)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Protocolo SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'ID de entidad del IdP (Entity ID)',
    'SSO URL': 'URL de inicio de sesión SSO',
    'X.509 Certificate': 'Certificado de seguridad X.509',
    'ACS URL': 'URL del servicio ACS',
    'Entity ID': 'ID de entidad',
    'Navigate': 'Navegar',
    'Select': 'Seleccionar',
    'OmniSearch v1.0': 'Búsqueda universal OmniSearch',
    'Back to order review': 'Volver a la revisión del pedido',
    'Return to Intelligence Studio': 'Volver al estudio de inteligencia',
    'View Full Notification Center': 'Ver centro de notificaciones completo',
    'Read all': 'Marcar todo como leído',
    'Cancel department': 'Cancelar departamento',
    'Save department': 'Guardar departamento',
    'Venue Segmentation': 'Segmentación de espacios',
    'Collaborate on your menu together.': 'Colabore en su menú con su equipo.',
    'Timeline': 'Línea de tiempo',
    'Calendar': 'Calendario de reservas',
    'Book': 'Reservar mesa',
    'Duration': 'Duración de la estancia',
    'Time & Duration': 'Hora y duración',
    'Seating Preference': 'Preferencia de ubicación',
    'Section': 'Sección del comedor',
    'Min Capacity': 'Capacidad mínima',
    'Max Capacity': 'Capacidad máxima',
    'Phone (Optional)': 'Teléfono (Opcional)',
    'Name or Walk-in': 'Nombre o cliente directo',
    'Next day': 'Día siguiente',
    'Next month': 'Mes siguiente',
    'Live Connection': 'Conexión en vivo',
    'Live Inventory': 'Inventario en tiempo real',
    'Main Venue (Full Menu)': 'Local principal (Menú completo)',
    'Preview Menu': 'Vista previa del menú',
    'Manual Setup Guide': 'Guía de configuración manual',
    'Connected & Active': 'Conectado y activo',
    'CTA Action': 'Botón de llamada a la acción (CTA)',
    'Phone Contact': 'Teléfono de contacto',
    'Menu URL': 'Dirección web del menú',
    'Google Location': 'Ubicación de Google',
    'Field': 'Campo',
    'Linked': 'Vinculado',
    'Synchronizing Hub...': 'Sincronizando el centro...',
    'Data Integrity': 'Integridad de los datos',
    'Station Governance Suite': 'Gestión de estaciones de servicio',
    'Open audit log': 'Abrir registro de auditoría',
    'Signal / Time': 'Evento / Hora',
    'Identity': 'Usuario / Identidad',
    'Invitation Invalid': 'Invitación no válida o expirada',
    'Invited To': 'Invitado a unirse a',
    'Team Limit Reached': 'Límite de miembros del equipo alcanzado',
    'Venue Limit Reached': 'Límite de locales alcanzado',
    'Venue Setup Required': 'Configuración inicial requerida',
    'Create Venue': 'Crear local',
    'TableGreet Onboarding': 'Bienvenida a TableGreet',
    'No payment required': 'Sin pago requerido',
    'Selected Plan': 'Plan seleccionado',
    'Payment Method': 'Método de pago',
    'Invoice ID': 'Número de factura',
    'Verified': 'Verificado',
    'Accent color': 'Color de acento',
    'Set the voice and overall light level.': 'Defina el tono y el nivel general de luz.',
    'Layout architecture': 'Arquitectura del diseño',
    'Navigation & motion': 'Navegación y animaciones',
    'Loading preview engine': 'Cargando motor de vista previa',
    'Real Live Preview': 'Vista previa en tiempo real',
    'Included with Pro & Max': 'Incluido con Pro y Max',
    'Daypart Scheduling': 'Programación por turnos del día',
    'Operational Daypart Architecture': 'Arquitectura de turnos operativos',
    'Service Protocol': 'Protocolo de servicio',
    'Configuration Engine': 'Motor de configuración',
    'Linked Department': 'Departamento vinculado',
    'Team Hub': 'Centro de equipo',
    'Team Size': 'Tamaño del equipo',
    'Joined': 'Unido el',
    'Cancel Invitation': 'Cancelar invitación',
    'Initialize New Organization': 'Crear nueva organización',
    'Operational': 'Operativo',
    'Pro Feature': 'Función Pro',
    'Current State': 'Estado actual',
    'Open on the big TV': 'Abrir en la pantalla de TV grande',
    'Menu Board': 'Tablero digital del menú',
    'Reward': 'Recompensa',
    'Reward name': 'Nombre de la recompensa',
    'Cancel & refund': 'Cancelar y reembolsar',
    'Code': 'Código',
    'Find': 'Buscar',
    'Complete': 'Completado',
    'Seated': 'En mesa',
    'No-Show': 'No presentado',
    'Walk-in': 'Sin reserva',
    'Party': 'Comensales',
    'Turnover': 'Rotación de mesas',
    'We couldn\'t load the menu': 'No se pudo cargar el menú',
    'Venue Not Found': 'Local no encontrado',
    'Live Availability': 'Disponibilidad en vivo',
    'Multi-Language Suite': 'Gestión multilingüe',
    'Key name': 'Nombre de la clave',
    'Revoke API key': 'Revocar clave de API',
    'Delete webhook': 'Eliminar webhook',
    'Identity Provider': 'Proveedor de identidad (IdP)',
    'Provider': 'Proveedor',
    'Mandatory MFA': 'Autenticación multifactor obligatoria (MFA)',
    'Account Identity': 'Identidad de la cuenta',
    'Login Email': 'Correo de inicio de sesión',
    'New Login Email': 'Nuevo correo de inicio de sesión',
    'Phone Login': 'Inicio de sesión con teléfono',
    'SMS Verification Code': 'Código de verificación por SMS',
    'Integration Method': 'Método de integración',
    'Stripe Connect': 'Pagos con Stripe Connect',
    'Stripe Secret Key': 'Clave secreta de Stripe',
    'Stripe Webhook Secret': 'Secreto de webhook de Stripe',
    'Client-Owned': 'Propiedad del cliente',
    'Verify connection': 'Comprobar conexión',
    'Setup Health': 'Estado de la configuración',
    'Privacy Protected': 'Privacidad protegida',
    'Privacy Policy': 'Política de privacidad',
    'Continue': 'Continuar',
    'Sign In': 'Iniciar sesión',
    'Didn\'t get it?': '¿No lo recibió?',
    'Logged In Account': 'Cuenta conectada',
    'Annual': 'Anual',
    'Unread': 'No leído',
    'Starred': 'Destacados',
    'High / Urgent': 'Alto / Urgente',
    'Mark Read': 'Marcar como leído',
    'Delete notification': 'Eliminar notificación',
    'Urgent': 'Urgente',
    'High': 'Alto',
    'Normal': 'Normal',
    'Low': 'Bajo',
    'Select a Ticket': 'Seleccionar un ticket',
    'Urgent (Immediate attention)': 'Urgente (Atención inmediata)',
    'Type your reply here...': 'Escriba su respuesta aquí...',
    'Print Card (PDF)': 'Imprimir tarjeta de mesa (PDF)',
    'Advanced Printing Engine': 'Motor de impresión de alta fidelidad',
    'Multi-Venue Governance': 'Gestión de múltiples locales',
    'Network Revenue': 'Ingresos totales de la red',
    'Live Network Total': 'Total de la red en tiempo real',
    'Audit Log Hub': 'Centro de registro de auditoría',
    'Export Log': 'Exportar registro',
    'CUSTOM ROLE SCHEMAS': 'ESQUEMAS DE ROLES PERSONALIZADOS',
    'Unique internal role identifier.': 'Identificador interno único del rol.',
    'Human-friendly role label.': 'Nombre claro del rol para el equipo.',
    'Marketing Hub': 'Centro de marketing',
    'Revenue Generation Suite': 'Herramientas de aumento de ingresos',
    'Active Campaign Limit Reached (3/3)': 'Límite de campañas activas alcanzado (3/3)',
    'Banner Content': 'Contenido del banner',
    'Style Profile': 'Estilo visual',
    'Action URL (Optional)': 'Enlace de acción (Opcional)',
    'Promotion Label': 'Etiqueta de la promoción',
    'Target Selection': 'Selección de destino',
    'Edit Promotion': 'Editar promoción',
    'Delete Promotion': 'Eliminar promoción',
    'Loyalty Engine': 'Programa de fidelización',
    'Reward catalog': 'Catálogo de recompensas',
    'Add reward': 'Añadir recompensa',
    'Loyalty ID': 'ID de fidelización',
    'No point activity yet': 'Aún no hay actividad de puntos',
    'Scan or enter QR value': 'Escanee o introduzca el valor del QR',
    'TableGreet Admin': 'Administración TableGreet',
    'Capacity Pacing': 'Regulación de capacidad',
    'Cancellation Policy': 'Política de cancelación',
    'Booking Experience': 'Experiencia de reserva',
    'Floor Occupancy': 'Ocupación de sala',
    'Currently Seated': 'Sentados actualmente',
    'Seat Party': 'Sentar comensales',
    'Seat Table': 'Asignar mesa',
    'Select Table': 'Seleccionar mesa',
    'Table / Capacity': 'Mesa / Capacidad',
    'Table Name/Number': 'Nombre o número de mesa',
    'Mark Arrived': 'Marcar como llegado',
    'Mark No-Show': 'Marcar como no presentado',
    'Complete Dining': 'Finalizar servicio de mesa',
    'Standard Dining (Default)': 'Comedor estándar (Por defecto)',
    'Walk-In Dining Log': 'Registro de clientes sin reserva',
    'Notification Center': 'Centro de notificaciones',
    'Single Sign-On': 'Inicio de sesión único (SSO)',
    'Welcome Aboard!': '¡Bienvenido a TableGreet!',
    'Select your TableGreet plan': 'Elija su plan de TableGreet',
    'Free Plan Activation': 'Activación del plan gratuito',
    'Venue Created!': '¡Local creado con éxito!',
    'Upgrade for Unlimited': 'Mejorar para acceso ilimitado',
    'Auto-Tag Menu': 'Etiquetado automático del menú',
    'Start Auto-Tagging': 'Iniciar etiquetado automático',
    'Launch Auto-Tagger': 'Abrir etiquetador automático',
    'Launch PDF Importer': 'Importar desde menú PDF',
    'Analyzing Menu...': 'Analizando el menú...',
    'Analyzing Recipe...': 'Analizando la receta...',
    'Manual Availability': 'Disponibilidad manual',
    'Technical Breakdown': 'Desglose técnico',
    'Dietary Taxonomy': 'Dietas y alérgenos',
    'Dietary & Allergen AI': 'IA de dietas y alérgenos',
    'Inherit from Category': 'Heredar de la categoría',
    'Ingredient Library': 'Biblioteca de ingredientes',
    'Ingredient Name': 'Nombre del ingrediente',
    'Menu Organization': 'Organización del menú',
    'Inventory Taxonomy Hub': 'Centro de taxonomía del menú',
    'Category Intelligence': 'Inteligencia de categoría',
    'Service Period': 'Período de servicio',
    'Manual Configuration / All Day': 'Configuración manual / Todo el día',
    'Operational Department': 'Departamento operativo',
    'Global / No Department': 'Global / Sin departamento',
    'Cancel category': 'Cancelar categoría',
    'New category name...': 'Nombre de la nueva categoría...',
    'Save category': 'Guardar categoría',
    'QR Matrix Only': 'Solo matriz QR',
    'Vector & PDF': 'Vectorial y PDF',
    'Vector QR (SVG)': 'Código QR vectorial (SVG)',
    'Vector QR (PDF)': 'Código QR para imprenta (PDF)',
    'Save QR Style': 'Guardar diseño de QR',
    'Share Link': 'Compartir enlace',
    'High Fidelity 300DPI': 'Alta resolución 300 DPI para imprenta',
    'Generating 300 DPI PDF...': 'Generando PDF a 300 DPI...',
    'Give your venue a point of view.': 'Dé a su local una personalidad visual única.',
    'Signage Menu Not Found': 'Menú de pantalla digital no encontrado',
    'Scan Menu': 'Escanear menú',
    'Open on your phone': 'Abrir en su teléfono',
    'Full Screen': 'Pantalla completa',
    'Lock TV board': 'Bloquear pantalla de TV',
    'Big-Screen Menu': 'Menú para pantalla grande',
    'TV Signage': 'Pantalla digital de TV',
    'General Information': 'Información general',
    'Venue Name *': 'Nombre del local *',
    'Public Slug *': 'Enlace público (Slug) *',
    'Venue Logo': 'Logo del local',
    'Hero Cover Image': 'Imagen de portada principal',
    'Venue Timezone': 'Zona horaria del local',
    'Primary Brand Color': 'Color principal de la marca',
    'TableGreet footer credit': 'Crédito de TableGreet en el pie de página',
    'White-Label Branding': 'Marca blanca personalizada',
    'Client DNS Setup': 'Configuración DNS del dominio',
    'Copy CNAME target': 'Copiar destino CNAME',
    'Copy TableGreet URL': 'Copiar enlace de TableGreet',
    'Included TableGreet URL': 'Enlace TableGreet incluido',
    'Open TableGreet URL': 'Abrir enlace de TableGreet',
    'Public menu': 'Menú público de clientes',
    'Public venue delivery': 'Entrega a domicilio desde la carta',
    'Zone name': 'Nombre de la zona',
    'Coverage note': 'Nota de cobertura',
    'Default ETA min': 'Tiempo estimado mín. (minutos)',
    'Default ETA max': 'Tiempo estimado máx. (minutos)',
    'Packaging fee': 'Coste de empaquetado',
    'Courier handled by': 'Reparto gestionado por',
    'Third-party courier': 'Repartidor externo asociado',
    'Unified Experience': 'Experiencia unificada',
    'Save WiFi to Venue': 'Guardar Wi-Fi del local',
    'Network Name (SSID)': 'Nombre de la red Wi-Fi (SSID)',
    'Audio Alert Chime': 'Tono de alerta sonora',
    'Time Format': 'Formato de hora',
    'Auto / Default': 'Automático / Por defecto',
    'Syncing SEO & Google Data...': 'Sincronizando datos de SEO y Google...',
    'Google Connection': 'Conexión con Google',
    'Search Engine Metadata': 'Metadatos para motores de búsqueda',
    'Social card preview': 'Vista previa en redes sociales',
    'Allow Indexing': 'Permitir indexación',
    'Canonical URL Override': 'Anulación de URL canónica',
    'Price Range': 'Rango de precios',
    'Latitude': 'Latitud',
    'Longitude': 'Longitud',
    'Google Place ID / Location ID': 'ID de lugar de Google (Place ID)',
    'Google Search Preview': 'Vista previa en la búsqueda de Google',
    'Linked Google Location': 'Ubicación de Google vinculada',
    'SEO Documentation': 'Documentación de SEO',
    ', and': ', y',
    'and': 'y',
    'Delete Department?': '¿Eliminar departamento?',
    'Delete Department': 'Eliminar departamento',
    'Live URL': 'Enlace en vivo',
    'Copy full live URL': 'Copiar enlace completo en vivo',
    'Open live department menu': 'Abrir menú del departamento en vivo',
    'Loading departments...': 'Cargando departamentos...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'La segmentación en múltiples departamentos es una función prémium disponible en PRO y superior.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Segmente su menú por salas, mesas, barras o ubicaciones físicas.',
    'Targeted Ordering': 'Pedidos segmentados',
    'Route guests and orders to specific preparation areas.': 'Enrute clientes y pedidos a zonas de preparación específicas.',
    'Operational Departments': 'Departamentos operativos',
    'OPERATIONAL DEPARTMENTS': 'DEPARTAMENTOS OPERATIVOS',
    'Venue Segmentation Hub': 'Centro de segmentación del local',
    'VENUE SEGMENTATION HUB': 'CENTRO DE SEGMENTACIÓN DEL LOCAL',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Cree departamentos para organizar su carta. Por ejemplo, un local grande puede tener',
    'Restaurant': 'Restaurante',
    'Poolside': 'Piscina',
    'department, each with its own categories.': ', cada uno con sus propias categorías.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Los departamentos le permiten dividir su local en secciones como Restaurante, Bar o Piscina, cada uno con sus propias categorías.',
    'Department name': 'Nombre del departamento',
    'New department name...': 'Nombre del nuevo departamento...',
    'No Linked Daypart (Always Available)': 'Sin franja horaria vinculada (siempre disponible)',
    'Linked Daypart': 'Horario de servicio vinculado',
    'Localizations': 'Idiomas y traducciones',
    'LOCALIZATIONS': 'IDIOMAS Y TRADUCCIONES',
    'AI Auto-Translate': 'Traducción automática con IA',
    'AI AUTO-TRANSLATE': 'TRADUCCIÓN AUTOMÁTICA CON IA',
    'Translated name': 'Nombre traducido',
    'No Departments Yet': 'No hay departamentos aún',
    'Add First Department': 'Añadir primer departamento',
    'Avg Order Value': 'Ticket medio',
    'AVG ORDER VALUE': 'TICKET MEDIO',
    'item taps': 'toques en platos',
    'ITEM TAPS': 'TOQUES EN PLATOS',
    'Avg Rating': 'Puntuación media',
    'AVG RATING': 'PUNTUACIÓN MEDIA',
    'Revenue (Last 7 Days)': 'Ingresos (últimos 7 días)',
    'REVENUE (LAST 7 DAYS)': 'INGRESOS (ÚLTIMOS 7 DÍAS)',
    'Daily breakdown': 'Desglose diario',
    'DAILY BREAKDOWN': 'DESGLOSE DIARIO',
    'Menu Views (Last 7 Days)': 'Vistas de la carta (últimos 7 días)',
    'MENU VIEWS (LAST 7 DAYS)': 'VISTAS DE LA CARTA (ÚLTIMOS 7 DÍAS)',
    'Guest traffic': 'Afluencia de clientes',
    'GUEST TRAFFIC': 'AFLUENCIA DE CLIENTES',
    'Most Viewed Items': 'Platos más vistos',
    'MOST VIEWED ITEMS': 'PLATOS MÁS VISTOS',
    'Top Rated Items': 'Platos mejor valorados',
    'TOP RATED ITEMS': 'PLATOS MEJOR VALORADOS',
    'Waiter Requests': 'Peticiones al camarero',
    'WAITER REQUESTS': 'PETICIONES AL CAMARERO',
    'Total': 'Total',
    'TOTAL': 'TOTAL',
    'Order Status (30 days)': 'Estado de pedidos (30 días)',
    'ORDER STATUS (30 DAYS)': 'ESTADO DE PEDIDOS (30 DÍAS)',
    'Recent Guest Feedback': 'Opiniones recientes de clientes',
    'RECENT GUEST FEEDBACK': 'OPINIONES RECIENTES DE CLIENTES',
    'Conversion Rate': 'Tasa de conversión',
    'CONVERSION RATE': 'TASA DE CONVERSIÓN',
    'of menu visitors placed an order': 'de los visitantes de la carta hicieron un pedido',
    'Refreshed': 'Actualizado',
    'REFRESHED': 'ACTUALIZADO',
    'Last 30 days': 'Últimos 30 días',
    'LAST 30 DAYS': 'ÚLTIMOS 30 DÍAS',
    'cash': 'efectivo',
    'Cash': 'Efectivo',
    'CASH': 'EFECTIVO',
    'bill': 'la cuenta',
    'Bill': 'La cuenta',
    'BILL': 'LA CUENTA',
    'water': 'agua',
    'Water': 'Agua',
    'WATER': 'AGUA',
    'service': 'servicio',
    'Service': 'Servicio',
    'SERVICE': 'SERVICIO',
    'No interaction data yet': 'No hay datos de interacción aún',
    'No ratings collected yet': 'No se han recopilado valoraciones aún',
    'No requests in period': 'Sin peticiones en este período',
    'No feedback collected yet': 'Sin comentarios de clientes aún',
    'No menu views recorded yet': 'No se han registrado visitas a la carta aún',
    'Dish Rating Breakdown': 'Desglose de valoraciones por plato',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'El ranking de platos y satisfacción de clientes están disponibles en los planes MAX y Enterprise.',
    'Customer Sentiment & Reviews': 'Sentimiento y opiniones de clientes',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Las reseñas directas de comensales y el análisis de opiniones requieren una suscripción MAX o Enterprise.',
    'Reviews AI': 'IA de análisis de opiniones',
    'Guest Feedback': 'Opiniones de clientes',
    'Upgrade to unlock rating analytics →': 'Mejore su plan para desbloquear estadísticas de valoración →',
    'Upgrade to Max': 'Mejorar al plan Max',
    'UPGRADE TO MAX': 'MEJORAR AL PLAN MAX',
    'MAX PLAN': 'PLAN MAX',
    'No Data Yet': 'No hay datos todavía',
    'Share your QR code to start collecting real venue insights.': 'Comparta su código QR para empezar a recopilar métricas reales del establecimiento.',
    'Revenue Analytics': 'Análisis de ingresos',
    'REVENUE ANALYTICS': 'ANÁLISIS DE INGRESOS',
    'Track your earnings, orders, and average ticket values over time.': 'Supervise sus ingresos, pedidos y el valor medio del ticket a lo largo del tiempo.',
    'Operational Velocity': 'Velocidad operativa',
    'OPERATIONAL VELOCITY': 'VELOCIDAD OPERATIVA',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Supervise el flujo de comensales, los platos populares y la carga de los camareros.',
    'Upgrade to Pro': 'Mejorar a Pro',
    'UPGRADE TO PRO': 'MEJORAR AL PLAN PRO',
    'Loading Insights…': 'Cargando estadísticas…',
    'Loading Insights...': 'Cargando estadísticas...',
    'Telemetry Unavailable': 'Datos de actividad no disponibles',
    'TELEMETRY UNAVAILABLE': 'DATOS DE ACTIVIDAD NO DISPONIBLES',
    'No Data Captured': 'No hay datos registrados',
    'NO DATA CAPTURED': 'NO HAY DATOS REGISTRADOS',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Comparta su código QR para empezar a recopilar estadísticas de clientes en tiempo real.',
    'INTELLIGENCE STUDIO': 'CENTRO DE ANÁLISIS',
    'Insights Engine Locked': 'Módulo de análisis avanzado bloqueado',
    'INSIGHTS ENGINE LOCKED': 'MÓDULO DE ANÁLISIS AVANZADO BLOQUEADO',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'El análisis avanzado de tendencias, acciones masivas y exportaciones están reservados para',
    'Unlock Pro Features': 'Desbloquear funciones Pro',
    'UNLOCK PRO FEATURES': 'DESBLOQUEAR FUNCIONES PRO',
    'MENU VIEWS': 'VISTAS DE LA CARTA',
    'ITEM INTERACTIONS': 'INTERACCIONES CON PLATOS',
    'GUEST RATING': 'VALORACIÓN DE CLIENTES',
    'DELIVERY REVENUE': 'INGRESOS POR ENTREGA',
    'AVERAGE DELIVERY ORDER': 'PEDIDO MEDIO DE ENTREGA',
    'AVERAGE DELIVERY FEE': 'TARIFA MEDIA DE ENTREGA',
    'INCLUDING FEES': 'TARIFAS INCLUIDAS',
    'ZONE AVERAGE': 'MEDIA DE LA ZONA',
    'TRAFFIC PROJECTIONS': 'PROYECCIONES DE AFLUENCIA',
    'TELEMETRY SIGNAL TRACKING': 'SEGUIMIENTO DE SEÑALES DE ACTIVIDAD',
    'VEGETARIAN': 'VEGETARIANO',
    'VEGAN': 'VEGANO',
    'SPICY': 'PICANTE',
    'GLUTEN-FREE': 'SIN GLUTEN',
    'Reset: All In-Stock': 'Restablecer: Todo disponible',
    'Emergency Stop (All Out)': 'Parada de emergencia: Todo agotado',
    'No compliance issues': 'Sin problemas detectados',
    'NO COMPLIANCE ISSUES': 'SIN PROBLEMAS DETECTADOS',
    'detected in current menu': 'en la carta actual',
    'DETECTED IN CURRENT MENU': 'EN LA CARTA ACTUAL',
    'Telemetry History (24h)': 'Historial de actividad (24h)',
    'TELEMETRY HISTORY (24H)': 'HISTORIAL DE ACTIVIDAD (24H)',
    'Telemetry History (30d)': 'Historial de actividad (30d)',
    'TELEMETRY HISTORY (30D)': 'HISTORIAL DE ACTIVIDAD (30D)',
    'Save': 'Guardar',
    'Saved': 'Guardado',
    'Saving...': 'Guardando...',
    'Save Changes': 'Guardar cambios',
    'Save Settings': 'Guardar configuración',
    'Cancel': 'Cancelar',
    'Close': 'Cerrar',
    'Create': 'Crear',
    'Add': 'Añadir',
    'Edit': 'Editar',
    'Delete': 'Eliminar',
    'Remove': 'Quitar',
    'Update': 'Actualizar',
    'Refresh': 'Actualizar',
    'Search': 'Buscar',
    'Search...': 'Buscar...',
    'Search items...': 'Buscar platos...',
    'Search categories...': 'Buscar categorías...',
    'Filter': 'Filtrar',
    'All': 'Todo',
    'Export': 'Exportar',
    'Import': 'Importar',
    'Download': 'Descargar',
    'Upload': 'Subir',
    'Copy': 'Copiar',
    'Copied': 'Copiado',
    'Copied!': '¡Copiado!',
    'Preview': 'Vista previa',
    'Open': 'Abierto',
    'View': 'Ver',
    'Manage': 'Gestionar',
    'Configure': 'Configurar',
    'Enable': 'Activar',
    'Disable': 'Desactivar',
    'Enabled': 'Activado',
    'Disabled': 'Desactivado',
    'Active': 'Activo',
    'Inactive': 'Inactivo',
    'Available': 'Disponible',
    'Unavailable': 'No disponible',
    'Visible': 'Visible',
    'Hidden': 'Oculto',
    'Required': 'Obligatorio',
    'Optional': 'Opcional',
    'Confirm': 'Confirmar',
    'Back': 'Atrás',
    'Next': 'Siguiente',
    'Done': 'Hecho',
    'Upgrade': 'Mejorar plan',
    'Sign Out': 'Cerrar sesión',
    'Exit to Main Admin': 'Volver a administración',
    'Change Language': 'Cambiar idioma',
    'Add Category': 'Añadir categoría',
    'Add Menu Item': 'Añadir plato',
    'Add Item': 'Añadir artículo',
    'Add Ingredient': 'Añadir ingrediente',
    'Add Department': 'Añadir departamento',
    'Delete Item': 'Eliminar artículo',
    'Delete Category': 'Eliminar categoría',
    'Upload Image': 'Subir imagen',
    'Upload Photo': 'Subir foto',
    'Select Category': 'Seleccionar categoría',
    'Select Department': 'Seleccionar departamento',
    'Add translation...': 'Añadir traducción...',
    'Clear': 'Limpiar',
    'Reset': 'Restablecer',
    'Apply': 'Aplicar',
    'Submit': 'Enviar',
    'Core & Operations': 'Operaciones de sala',
    'Menu & Experience': 'Menú y platos',
    'Enterprise & Security': 'Gestión y equipo',
    'System & Control': 'Ajustes y ayuda',
    'Intelligence Studio': 'Centro de análisis',
    'Venue Insights': 'Informes y ventas',
    'Reservations': 'Reservas',
    'Floor Plan': 'Plano del salón',
    'Live Waitlist': 'Lista de espera',
    'Walk-Ins': 'Clientes directos',
    'Walk-ins': 'Clientes directos',
    'Departments': 'Departamentos',
    'Service Hours': 'Horario de servicio',
    'Categories': 'Categorías',
    'Menu Categories': 'Categorías del menú',
    'Menu Items': 'Platos del menú',
    'Items': 'Artículos',
    'Ingredients': 'Ingredientes',
    'Loyalty Program': 'Programa de fidelidad',
    'Campaign Studio': 'Ofertas y promociones',
    'Signage': 'Pantallas TV',
    'QR Code': 'Códigos QR',
    'Kitchen Ops': 'Pantalla de cocina',
    'Theme Engine': 'Diseño y colores',
    'Governance': 'Gobernanza',
    'Audit Log': 'Historial de actividad',
    'API & Webhooks': 'Integraciones y conexiones',
    'SSO & Security': 'Seguridad y acceso',
    'Team': 'Equipo y personal',
    'Billing': 'Suscripción y facturación',
    'SEO & Google': 'Presencia en Google',
    'Notifications': 'Notificaciones',
    'Support Center': 'Centro de ayuda',
    'Settings': 'Ajustes del local',
    'Name': 'Nombre',
    'Description': 'Descripción',
    'Price': 'Precio',
    'Status': 'Estado',
    'Actions': 'Acciones',
    'Role': 'Rol',
    'Email': 'Correo',
    'Phone': 'Teléfono',
    'Address': 'Dirección',
    'City': 'Ciudad',
    'Currency': 'Moneda',
    'Language': 'Idioma',
    'Theme': 'Tema',
    'General': 'General',
    'Total Revenue': 'Ingresos totales',
    'Total Orders': 'Total de pedidos',
    'Average Order': 'Ticket medio',
    'Active Sessions': 'Mesas activas',
    'Recent Orders': 'Últimos pedidos',
    'Live Orders': 'Pedidos en directo',
    'Table': 'Mesa',
    'Date': 'Fecha',
    'Time': 'Hora',
    'Amount': 'Importe',
    'Customer': 'Cliente',
    'In Stock': 'En stock',
    'Out of Stock': 'Agotado',
    'Sold Out': 'Agotado',
    'Dine In': 'En sala',
    'Pickup': 'Para llevar',
    'Delivery': 'Entrega',
    'Paid': 'Pagado',
    'Unpaid': 'No pagado',
    'Pending': 'Pendiente',
    'Preparing': 'En preparación',
    'Ready': 'Listo',
    'Completed': 'Completado',
    'Cancelled': 'Cancelado',
    'Loading...': 'Cargando...',
    'No data': 'Sin datos',
    'No results found': 'No se encontraron resultados',
    'No items found': 'No se encontraron artículos',
    'No translations defined yet': 'Aún no hay traducciones definidas',
    'Are you sure?': '¿Estás seguro?',
    'This action cannot be undone.': 'Esta acción no se puede deshacer.',
    'Settings saved successfully!': '¡Ajustes guardados correctamente!',
    'Administrator': 'Administrador',
    'Manager': 'Gerente',
    'Owner': 'Propietario',
    'Chef': 'Chef',
    'Waiter': 'Camarero',
    'Cashier': 'Cajero',
    'Type a command or search...': 'Escribe un comando o busca...',
    'Command Palette': 'Comandos rápidos',
    'Demand Velocity': 'Velocidad de demanda',
    'DEMAND VELOCITY': 'VELOCIDAD DE DEMANDA',
    'Top Performing Entities': 'Platos con mejor rendimiento',
    'TOP PERFORMING ENTITIES': 'PLATOS CON MEJOR RENDIMIENTO',
    'Hourly Load Analysis': 'Análisis horario de afluencia',
    'HOURLY LOAD ANALYSIS': 'ANÁLISIS HORARIO DE AFLUENCIA',
    'Bulk Command Center': 'Centro de comandos masivos',
    'BULK COMMAND CENTER': 'CENTRO DE COMANDOS MASIVOS',
    'Governance Scan': 'Escaneo de calidad del menú',
    'GOVERNANCE SCAN': 'ESCANEO DE CALIDAD DEL MENÚ',
    'No compliance issues detected in current menu': 'No se detectaron problemas en el menú actual',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'NO SE DETECTARON PROBLEMAS EN EL MENÚ ACTUAL',
    'Procedural Tagging': 'Etiquetado automático',
    'PROCEDURAL TAGGING': 'ETIQUETADO AUTOMÁTICO',
    'Name match...': 'Coincidencia de nombre...',
    '...Name match': 'Coincidencia de nombre...',
    'Healthy': 'Saludable',
    'HEALTHY': 'SALUDABLE',
    'Apply Tags': 'Aplicar etiquetas',
    'APPLY TAGS': 'APLICAR ETIQUETAS',
    'Global Price Shift': 'Ajuste global de precios',
    'GLOBAL PRICE SHIFT': 'AJUSTE GLOBAL DE PRECIOS',
    'Adjust': 'Ajustar',
    'ADJUST': 'AJUSTAR',
    'Availability Override': 'Control de disponibilidad inmediata',
    'AVAILABILITY OVERRIDE': 'CONTROL DE DISPONIBILIDAD INMEDIATA',
    'Reset: ALL IN-STOCK': 'Restablecer: TODO DISPONIBLE',
    'RESET: ALL IN-STOCK': 'RESTABLECER: TODO DISPONIBLE',
    'EMERGENCY STOP (ALL OUT)': 'Parada de emergencia (TODO AGOTADO)',
    'Integrity Engine': 'Motor de integridad del menú',
    'INTEGRITY ENGINE': 'MOTOR DE INTEGRIDAD DEL MENÚ',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Este sistema inteligente supervisa la visibilidad de los platos, anomalías de precios y la calidad de las descripciones en todo su establecimiento.',
    'Inventory Audit Export': 'Exportación para auditoría de inventario',
    'INVENTORY AUDIT EXPORT': 'EXPORTACIÓN PARA AUDITORÍA DE INVENTARIO',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Genere una instantánea completa del rendimiento del menú y metadatos para auditoría sin conexión.',
    'Enterprise Data Export': 'Exportación avanzada de datos',
    'ENTERPRISE DATA EXPORT': 'EXPORTACIÓN AVANZADA DE DATOS',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Genere y descargue auditorías completas de rendimiento en formatos estándar. Incluye flujo de clientes, demanda de platos y conversión.',
    'JSON Snapshot': 'Instantánea JSON',
    'JSON SNAPSHOT': 'INSTANTÁNEA JSON',
    'Compile CSV Report': 'Compilar informe CSV',
    'COMPILE CSV REPORT': 'COMPILAR INFORME CSV',
    'Date Segment': 'Segmento de fecha',
    'DATE SEGMENT': 'SEGMENTO DE FECHA',
    'Inbound Views': 'Visitas entrantes',
    'INBOUND VIEWS': 'VISITAS ENTRANTES',
    'Asset Interactions': 'Interacciones con platos',
    'ASSET INTERACTIONS': 'INTERACCIONES CON PLATOS',
    'Telemetry History': 'Historial de actividad',
    'TELEMETRY HISTORY': 'HISTORIAL DE ACTIVIDAD',
    'Telemetry History (7d)': 'Historial de actividad (7d)',
    'TELEMETRY HISTORY (7D)': 'HISTORIAL DE ACTIVIDAD (7D)',
    'Growth Trajectory': 'Trayectoria de crecimiento',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'La frecuencia de interacción con el menú muestra una tendencia al alza en los últimos 7 días.',
    'Deep Analysis Engine': 'Motor de análisis profundo',
    'DEEP ANALYSIS ENGINE': 'MOTOR DE ANÁLISIS PROFUNDO',
    'Asset Velocity': 'Rendimiento de platos',
    'ASSET VELOCITY': 'RENDIMIENTO DE PLATOS',
    'Traffic Projections': 'Proyecciones de tráfico',
    'Telemetry Signal Tracking': 'Seguimiento de señales de actividad',
    'Service Hour Update': 'Actualización de horarios de servicio',
    'Late Night Lounge Hours': 'Horario nocturno',
    'Bar & Lounge Hours': 'Horario de bar y lounge',
    'Breakfast & Brunch Hours': 'Horario de desayuno y brunch',
    'Switch Location': 'Cambiar ubicación',
    'SWITCH LOCATION': 'CAMBIAR UBICACIÓN',
    'Global Admin': 'Administrador global',
    'GLOBAL ADMIN': 'ADMINISTRADOR GLOBAL',
    'Staff Access': 'Acceso del personal',
    'STAFF ACCESS': 'ACCESO DEL PERSONAL',
    'Venue Overview': 'Resumen del establecimiento',
    'VENUE OVERVIEW': 'RESUMEN DEL ESTABLECIMIENTO',
    'Unified Telemetry Engine': 'Motor unificado de actividad',
    'UNIFIED TELEMETRY ENGINE': 'MOTOR UNIFICADO DE ACTIVIDAD',
    'Pulse': 'En vivo',
    'PULSE': 'EN VIVO',
    'Insights': 'Tendencias',
    'INSIGHTS': 'TENDENCIAS',
    'Reports': 'Informes',
    'REPORTS': 'INFORMES',
    'GOVERNANCE': 'GOBERNANZA',
    'Multi-Venue': 'Multisede',
    'MULTI-VENUE': 'MULTISEDE',
    'Menu Views': 'Visitas al menú',
    'Item Interactions': 'Interacciones con platos',
    'Guest Rating': 'Calificación de clientes',
    'Revenue (7d)': 'Ingresos (7d)',
    'Revenue (24h)': 'Ingresos (24h)',
    'Revenue (30d)': 'Ingresos (30d)',
    'Delivery orders (7d)': 'Pedidos a domicilio (7d)',
    'Delivery orders (24h)': 'Pedidos a domicilio (24h)',
    'Delivery orders (30d)': 'Pedidos a domicilio (30d)',
    'Delivery revenue': 'Ingresos por delivery',
    'Average delivery order': 'Ticket medio de delivery',
    'Average delivery fee': 'Tarifa media de entrega',
    'Zone average': 'Promedio de zona',
    'Including fees': 'Tarifas incluidas',
    'No deliveries': 'Sin entregas',
    'No orders': 'Sin pedidos',
    'No reviews': 'Sin opiniones',
    'Live': 'En vivo',
    'LIVE': 'EN VIVO',
    'reviews': 'opiniones',
    'orders': 'pedidos',
    'deliveries': 'entregas',
    'of orders': 'de los pedidos',
    'rate': 'tasa',
    'Recent Activity': 'Actividad reciente',
    'Real-time Venue Telemetry': 'Actividad del local en tiempo real',
    'REAL-TIME VENUE TELEMETRY': 'ACTIVIDAD DEL LOCAL EN TIEMPO REAL',
    'Rapid Pulse View': 'Vista rápida en vivo',
    'Anonymous Guest': 'Cliente anónimo',
    'scanned menu': 'escaneó el menú',
    'viewed item': 'vio el plato',
    'interacted with': 'interactuó con',
    'placed order': 'realizó un pedido',
    'searched menu': 'buscó en el menú',
    'Main Landing': 'Página principal',
    'Trace': 'Rastreo',
    'TRACE': 'RASTREO',
    'Gaining Telemetry...': 'Obteniendo telemetría...',
    'No recent traffic detected': 'No se detectó actividad reciente',
    'Activities will appear here as guests scan your QR code': 'Las actividades aparecerán aquí cuando los clientes escaneen su código QR',
    'Analyze All Logs': 'Analizar todos los registros',
    'Just now': 'Ahora mismo',
    'less than a minute ago': 'hace menos de un minuto',
    '1 minute ago': 'hace 1 minuto',
    '1 hour ago': 'hace 1 hora',
    'about 1 hour ago': 'hace aproximadamente 1 hora',
    '1 day ago': 'hace 1 día',
    'DAY AGO 1': 'hace 1 día',
    '2 days ago': 'hace 2 días',
    'DAY AGO 2': 'hace 2 días',
    '3 days ago': 'hace 3 días',
    'DAY AGO 3': 'hace 3 días',
    '4 days ago': 'hace 4 días',
    'DAY AGO 4': 'hace 4 días',
    '5 days ago': 'hace 5 días',
    'DAY AGO 5': 'hace 5 días',
    '6 days ago': 'hace 6 días',
    'DAY AGO 6': 'hace 6 días',
    '7 days ago': 'hace 7 días',
    'DAY AGO 7': 'hace 7 días',
    'about 1 month ago': 'hace aproximadamente 1 mes',
    'Monday': 'Lunes',
    'Tuesday': 'Martes',
    'Wednesday': 'Miércoles',
    'Thursday': 'Jueves',
    'Friday': 'Viernes',
    'Saturday': 'Sábado',
    'Sunday': 'Domingo',
    'Mon': 'Lun',
    'Tue': 'Mar',
    'Wed': 'Mié',
    'Thu': 'Jue',
    'Fri': 'Vie',
    'Sat': 'Sáb',
    'Sun': 'Dom',
    'Opening Hours': 'Horario de apertura',
    'Start Time': 'Hora de inicio',
    'End Time': 'Hora de fin',
    'Opening Time': 'Hora de apertura',
    'Closing Time': 'Hora de cierre',
    'All Day': 'Todo el día',
    'Closed': 'Cerrado',
    'Active Days': 'Días activos',
    'Active Schedule': 'Horario activo',
    'Always Available': 'Siempre disponible',
    'Always Visible': 'Siempre visible',
    'New Category': 'Nueva categoría',
    'Category': 'Categoría',
    'Category Name': 'Nombre de la categoría',
    'Category Description': 'Descripción de la categoría',
    'Display Order': 'Orden de visualización',
    'Item Name': 'Nombre del plato',
    'Item Description': 'Descripción del plato',
    'Base Price': 'Precio base',
    'Preparation Time': 'Tiempo de preparación',
    'Calories': 'Calorías',
    'Featured Item': 'Plato destacado',
    'Chef\'s Recommendation': 'Recomendación del chef',
    'Spicy': 'Picante',
    'Vegetarian': 'Vegetariano',
    'Vegan': 'Vegano',
    'Gluten-Free': 'Sin gluten',
    'Halal': 'Halal',
    'Kosher': 'Kósher',
    'Contains Nuts': 'Contiene frutos secos',
    'Dairy-Free': 'Sin lácteos',
    'Allergen Exclusions': 'Alérgenos',
    'Table Number': 'Número de mesa',
    'Assigned Table': 'Mesa asignada',
    'Seats': 'Asientos',
    'Capacity': 'Capacidad',
    'Indoor': 'Interior',
    'Outdoor': 'Exterior',
    'Terrace': 'Terraza',
    'Bar': 'Bar',
    'Add Table': 'Añadir mesa',
    'Edit Table': 'Editar mesa',
    'Delete Table': 'Eliminar mesa',
    'Bookings': 'Reservas',
    'Add to Waitlist': 'Añadir a lista de espera',
    'Guest Name': 'Nombre del cliente',
    'Party Size': 'Número de comensales',
    'Arrival Time': 'Hora de llegada',
    'Special Requests': 'Peticiones especiales',
    'Confirmed': 'Confirmado',
    'No Show': 'No presentado',
    'Add Staff Member': 'Añadir miembro del equipo',
    'Invite Staff': 'Invitar personal',
    'Email Address': 'Dirección de correo',
    'Phone Number': 'Número de teléfono',
    'Download QR Code': 'Descargar código QR',
    'Print Table Stands': 'Imprimir soportes de mesa',
    'Today': 'Hoy',
    'Yesterday': 'Ayer',
    'This Week': 'Esta semana',
    'This Month': 'Este mes',
    'Last 30 Days': 'Últimos 30 días',
    'Access Denied': 'Acceso denegado',
    'Advanced Settings': 'Configuración avanzada',
    'Change Image': 'Cambiar imagen',
    'Remove Image': 'Eliminar imagen',
    'Announcement Banner': 'Banner de anuncios',
    'Please fill in all required fields.': 'Por favor, complete todos los campos obligatorios.',
    'Changes saved successfully': 'Cambios guardados con éxito',
    '(You)': '(Tú)',
    'AI Auto-Tag': 'Autoetiquetado con IA',
    'Analyzing:': 'Analizando:',
    'Apple (Fruit/Vegan)': 'Manzana (Fruta/Vegano)',
    'Cancel editing': 'Cancelar edición',
    'Capacity:': 'Capacidad:',
    'Carrot (Veggie)': 'Zanahoria (Vegetal)',
    'CircleDot (Organic)': 'Punto circular (Orgánico)',
    'Coffee (Caffeine)': 'Café (Cafeína)',
    'Configuration': 'Configuración',
    'Copy verification record name': 'Copiar nombre del registro',
    'Copy verification record value': 'Copiar valor del registro',
    'Critical exception intercepted': 'Error crítico interceptado',
    'Kernel Panic': 'Error del Sistema',
    'DNA (Organic/Bio)': 'ADN (Orgánico/Bío)',
    'Egg (Ovo/Allergen)': 'Huevo (Ovo/Alérgeno)',
    'Empty': 'Vacío',
    'Engraving...': 'Generando...',
    'Flame (Spicy)': 'Llama (Picante)',
    'Free': 'Gratis',
    'From': 'Desde',
    'Generating': 'Generando',
    'Governance & Traceability Studio': 'Centro de gestión y trazabilidad',
    'Greet': 'Bienvenida',
    'Heart (Healthy)': 'Corazón (Saludable)',
    'In-App': 'En la app',
    'Infinity SVG': 'SVG Vectorial (Ilimitado)',
    'Leaf (Default)': 'Hoja (Por defecto)',
    'MAX Feature': 'Función Enterprise',
    'Milk (Dairy)': 'Leche (Lácteo)',
    'Network': 'Red',
    'Notify SMS': 'Notificación SMS',
    'Order': 'Pedido',
    'Original': 'Original',
    'Overview': 'Resumen general',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Roles Estándar',
    'Shield (Safety)': 'Escudo (Seguridad)',
    'Soup (Warm/Soup)': 'Sopa (Caliente/Sopa)',
    'Standard Pro View': 'Vista Estándar',
    'Star': 'Estrella',
    'Synchronized with Venue Identity': 'Adaptado al estilo de tu local',
    'Synchronizing Security Context...': 'Conectando de forma segura...',
    'Targeted Timing': 'Horario de servicio',
    'Targeting': 'Aplica a',
    'To': 'Hasta',
    'Toggle Navigation': 'Alternar navegación',
    'Venue': 'Local',
    'Venue Governance Suite': 'Operaciones y ajustes del local',
    'Volume:': 'Volumen:',
    'WHITEBOARD NOTE...': 'Nota para el equipo...',
    'Warning (Alert)': 'Advertencia (Alerta)',
    'Wheat (Flour/Gluten)': 'Trigo (Harina/Gluten)',
    'Wine (Alcohol)': 'Vino (Alcohol)',
    'YouTube/Vimeo/Direct Link': 'Enlace de video (YouTube, Vimeo o MP4)',
    'Your name': 'Tu nombre',
    '2. Table Number': '2. Número de mesa',
    'for Morocco or': 'para Marruecos, o',
    'in': 'en',
    'to': 'a',
    'e.g. 12 or Patio-1': 'ej. 12 o Terraza-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'ej. Fettuccine artesanal, trufa negra de invierno, parmesano 36 meses...',
    'e.g. Head Sommelier & Wine Lead': 'ej. Sumiller jefe y encargado de bodega',
    'e.g. London': 'ej. Madrid',
    'e.g. SOMMELIER, HOST, BARISTA': 'ej. SUMILLER, ANFITRIÓN, BARISTA',
    'e.g. Skyline Lounge': 'ej. Terraza Panorámica',
    'e.g. TRUFFLE OIL': 'ej. ACEITE DE TRUFA',
    'e.g. The Grand Cafe': 'ej. El Gran Café',
    'e.g. Window preference, high chair': 'ej. Cerca de la ventana, trona para bebé',
    'Copied to clipboard!': '¡Copiado al portapapeles!',
    'Copy failed.': 'Error al copiar.',
    'Link copied!': '¡Enlace copiado!',
    'Logo uploaded': 'Logo subido',
    'Cover image uploaded': 'Imagen de portada subida',
    'Profile saved and synced successfully': 'Perfil guardado y sincronizado con éxito',
    'Category Created': 'Categoría creada',
    'Category Updated': 'Categoría actualizada',
    'Category Deleted': 'Categoría eliminada',
    'Item Created': 'Plato creado',
    'Item Updated': 'Plato actualizado',
    'Item Deleted': 'Plato eliminado',
    'Menu Published': 'Menú publicado',
    'Delete Category?': '¿Eliminar categoría?',
    'Delete Menu Item?': '¿Eliminar este plato?',
    'Delete Ingredient?': '¿Eliminar este ingrediente?',
    'Delete Service Period?': '¿Eliminar este horario de servicio?',
    'Remove team member?': '¿Eliminar a este miembro del equipo?',
    'Cancel invitation?': '¿Cancelar invitación?',
    'Revoke API key?': '¿Revocar clave API?',
    'Delete webhook endpoint?': '¿Eliminar este webhook?',
    'Clear all notifications?': '¿Borrar todas las notificaciones?',
    'Disconnect Stripe account?': '¿Desconectar cuenta de Stripe?',
    'Disconnect Google Business Profile?': '¿Desconectar Perfil de Negocio de Google?',
    'Campaigns published successfully!': '¡Campañas publicadas con éxito!',
    'Changes successfully synchronized with Google Business Profile!': '¡Cambios sincronizados con éxito con Google Business Profile!',
    'Google Business Profile successfully connected!': '¡Perfil de Google Business conectado con éxito!',
    'Google Business Profile disconnected.': 'Perfil de Google Business desconectado.',
    'Google Post draft saved successfully!': '¡Borrador de publicación en Google guardado con éxito!',
    'Post draft deleted.': 'Borrador de publicación eliminado.',
    'Review response published successfully!': '¡Respuesta a la reseña publicada con éxito!',
    'SEO configuration saved successfully!': '¡Configuración SEO guardada con éxito!',
    'SEO metadata translated across all languages!': '¡Metadatos SEO traducidos a todos los idiomas!',
    'Venue profile & bio translated across all languages!': '¡Perfil y descripción del local traducidos a todos los idiomas!',
    'Reservation configuration saved successfully': 'Ajustes de reservas guardados con éxito',
    'Stripe settings saved successfully.': 'Ajustes de Stripe guardados con éxito.',
    'Print-Ready PDF downloaded successfully': 'PDF listo para impresión descargado con éxito',
    'Vector QR PDF downloaded successfully': 'PDF de QR vectorial descargado con éxito',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Paquete de preimpresión 300 DPI generado con éxito',
    'At least one language must be enabled.': 'Debe haber al menos un idioma activado.',
    'Enter a non-zero percentage before adjusting prices.': 'Introduce un porcentaje distinto de cero antes de ajustar los precios.',
    'Please enter a Venue Name before translating.': 'Introduce el nombre del local antes de traducir.',
    'Please enter an item name before running dietary and allergen analysis.': 'Introduce el nombre del plato antes de analizar alérgenos y dietas.',
    'Please write a reply before submitting.': 'Escribe una respuesta antes de enviar.',
    'Table 3 Called Waiter': 'Mesa 3 llamó al camarero',
    'Table Call Bell': 'Timbre de llamada de mesa',
    'Waiter Calls': 'Llamadas al camarero',
    'Waiter Dispatch & Calls': 'Gestión y avisos de camareros',
    'Kitchen Display & Whiteboard': 'Pantalla de cocina y pizarra',
    'Customer Orders': 'Pedidos de clientes',
    'Bakery': 'Panadería / Pastelería',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Cafetería / Café',
    'Fast Food': 'Comida rápida',
    'Gourmet / Fine Dining': 'Alta cocina / Gastronómico',
    'Hotel': 'Hotel',
    'General Local Business': 'Negocio local general',
    'Classic': 'Clásico',
    'Clean': 'Limpio',
    'Modern': 'Moderno',
    'Elegant': 'Elegante',
    'Energetic': 'Dinámico',
    'Glass': 'Efecto cristal',
    'Playful & Casual': 'Divertido y casual',
    'Sophisticated & Gourmet': 'Sofisticado y gourmet',
    'Warm & Welcoming': 'Cálido y acogedor',
    'Executive': 'Ejecutivo',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Actualización requerida: Los menús en varios idiomas están disponibles en el plan Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Actualización requerida: Los pedidos y avisos al camarero no están disponibles en el plan Starter.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'La exportación de vectores SVG es una función Pro y Max. Actualiza para descargar vectores escalables.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'La exportación de PDF para imprenta es una función Pro y Max. Actualiza para exportar archivos PDF.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'El plan Pro incluye un máximo de 3 campañas activas. Pausa otra campaña o sube a MAX para tener campañas ilimitadas.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'El plan Starter incluye hasta 20 mesas (Mesas 1–20). Actualiza a PRO para mesas ilimitadas.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'El plan Starter admite mesas numéricas (1–20). Los nombres personalizados (ej. Terraza, VIP) requieren PRO.',
    'Add Menu Categories & Items': 'Añadir categorías y platos',
    'Add Menu Items': 'Añadir platos al menú',
    'Organize your menu sections and food items.': 'Organiza las secciones y los platos de tu carta.',
    'Add and manage your food & beverage offerings.': 'Añade y gestiona tu oferta de comidas y bebidas.',
    'Customize Venue Branding': 'Personalizar la marca del local',
    'Set your brand colors, logo, and presentation.': 'Define tus colores, logotipo y estilo de presentación.',
    'Get Your QR Code & Go Live': 'Obtén tu código QR y empieza a recibir clientes',
    'Download table QR codes and preview your live menu.': 'Descarga los códigos QR de mesa y previsualiza tu menú en directo.',
    'Account & Venue Created Successfully!': '¡Cuenta y local creados con éxito!',
    '⚡ Off-Peak Hours': '⚡ Horas valle',
    '🍽️ Lunch Crowd': '🍽️ Comidas de mediodía',
    '🚀 Weekend Rush': '🚀 Fin de semana animado',
    '🌟 Seasonal Special': '🌟 Especial de temporada',
    '📣 Engagement Buzz': '📣 Notoriedad y fidelización',
    'Festive & Energetic': 'Festivo y enérgico',
    'Urgent & High-Conversion': 'Urgente y de alta conversión',
    'Generated 3 tailored campaign strategies': 'Se han generado 3 estrategias de campaña a medida',
    'Could not generate campaign ideas. Please try again.': 'No se pudieron generar ideas de campaña. Inténtalo de nuevo.',
    'Venue Themes': 'Temas del local',
    'Choose the character of your public menu': 'Elige la personalidad visual de tu menú público',
    'Glassmorphism & Depth': 'Efecto cristal y profundidad',
    'Independent XML Sitemap': 'Mapa del sitio XML independiente',
    'Structured Schema & Sync': 'Datos estructurados y sincronización',
    'Improve how your venue appears on Google and in local search results.': 'Mejora la visibilidad de tu local en Google y en los resultados de búsqueda local.',
    'Terms of Service': 'Términos del servicio',
    'Confirm your email': 'Confirma tu correo electrónico',
    'Check your spam / junk folder': 'Revisa tu carpeta de correo no deseado o spam',
    'Commercial pre-press print production package': 'Paquete de impresión profesional de preimpresión',
    'Over-bleed safety margin': 'Margen de seguridad de sangrado',
    'Crop Marks': 'Marcas de corte',
    'Guillotine cutting guides': 'Guías de corte de guillotina',
    'Global Banners': 'Banners globales',
    'Featured Items': 'Platos destacados',
    'AI Assistant': 'Asistente de IA',
    'Custom Role Schemas': 'Esquemas de roles personalizados',
    'Notification Preferences': 'Preferencias de notificaciones',
    'Preferences saved automatically': 'Preferencias guardadas automáticamente',
    'Menu Extractor (PDF & Images)': 'Extractor de menús (PDF e imágenes)',
    'Multi-Language Translation': 'Traducción multilingüe',
    'Start Menu Translation': 'Iniciar traducción del menú',
    'Menu Items Hub': 'Centro de platos del menú',
    'Open Menu Items': 'Abrir platos del menú',
    'Enterprise Traceability': 'Trazabilidad empresarial',
    'Full History': 'Historial completo',
    'Billing & Plans': 'Facturación y planes',
    'Subscription Governance': 'Gestión de suscripciones',
    'Platform Usage': 'Uso de la plataforma',
    'Menu Page Views': 'Visualizaciones del menú',
    'Enterprise Identity': 'Identidad empresarial',
    'Access Governance': 'Gobernanza de accesos',
    'Required Fields *': 'Campos obligatorios *',
    'Enabled Languages': 'Idiomas activados',
    'Venue Info & Bio Localizations': 'Traducciones de la información y biografía del local',
    'Public Address': 'Dirección pública',
    'Public URL Slug': 'Identificador de URL pública',
    'Contact Phone': 'Teléfono de contacto',
    'Business Address': 'Dirección del negocio',
    'No Categories Yet': 'Aún no hay categorías',
    'No Custom Roles Configured': 'No hay roles personalizados configurados',
    'No Google reviews retrieved yet': 'Aún no se han obtenido reseñas de Google',
    'No Guests Found': 'No se encontraron clientes',
    'No Menu Items Yet': 'Aún no hay platos en el menú',
    'No Promotions Configured': 'No hay promociones configuradas',
    'No Service Periods Yet': 'Aún no hay horarios de servicio configurados',
    'No Support Tickets': 'No hay solicitudes de asistencia',
    'No Tables on Floor Plan': 'No hay mesas en el plano de sala',
    'No Team Members Found': 'No se encontraron miembros del equipo',
    'No Upcoming Events': 'No hay eventos próximos',
    'No Walk-Ins Recorded': 'No hay clientes sin reserva registrados',
    'No items are available right now': 'No hay platos disponibles en este momento',
    'No notifications found': 'No hay notificaciones',
    'No reservations matching your filters': 'No hay reservas que coincidan con los filtros',
    'No tables currently occupied': 'No hay mesas ocupadas en este momento',
    'No venue selected': 'No hay ningún local seleccionado',
    'Waitlist': 'Lista de espera',
    'Waitlist is Empty': 'La lista de espera está vacía',
    'Waves (Gluten)': 'Espigas (Gluten)',
    'We sent a temporary verification code to': 'Hemos enviado un código de verificación temporal a',
    'Website URL': 'URL del sitio web',
    'What the customer receives': 'Lo que recibe el cliente',
    'White-label control is available on eligible plans.': 'La personalización de marca blanca está disponible en los planes compatibles.',
    'Why this works:': 'Por qué funciona esta propuesta:',
    'WiFi Broadcast': 'Conexión WiFi para clientes',
    'WiFi Password': 'Contraseña de WiFi',
    'Window seat, dietary request, birthday celebration...': 'Mesa cerca de la ventana, alergias, celebración de cumpleaños...',
    'Your Published Response': 'Tu respuesta publicada',
    'Your Venues': 'Tus locales',
    '🎉 Ready to publish!': '🎉 ¡Listo para publicar!',
    'e.g. Grill, Salad, Pastry': 'ej. Parrilla, Ensaladas, Pastelería',
    'e.g. Guest called, emergency': 'ej. Llamada del cliente, urgencia',
    'e.g. Italian, Moroccan, Burgers': 'ej. Italiana, Marroquí, Hamburguesas',
    'e.g. Mains, Pasta, Starters': 'ej. Principales, Pastas, Entrantes',
    'e.g. Signature Truffle Pasta': 'ej. Pasta exclusiva de trufa',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'ej. Especial café de la tarde, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'ej. Desayuno, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'ej. ¡Disfruta de jazz en directo todos los viernes a las 20:00!',
  },
  de: {
    '123 Main St, City': 'Hauptstraße 123, Stadt',
    'Balance': 'Punktestand',
    'Lifetime': 'Gesamtverlauf',
    'Source': 'Quelle',
    'Menu': 'Speisekarte',
    'Advanced': 'Erweitert',
    'Core Suite': 'Basis-Funktionen',
    'Privacy': 'Datenschutz',
    'Support': 'Hilfe & Support',
    'Save Layout': 'Raumplan speichern',
    'Shape:': 'Form:',
    'SEARCH LIBRARY...': 'BIBLIOTHEK DURCHSUCHEN...',
    'Search library...': 'Bibliothek durchsuchen...',
    'None': 'Keine',
    'Type': 'Typ',
    'Target': 'Ziel',
    'Value / Target': 'Wert / Ziel',
    'London (GMT/BST)': 'London (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tokio (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Startfarbe',
    'Depth Shade': 'Tiefenschattierung',
    'Matrix Geometry': 'Punktmuster des QR-Codes',
    'Eye Architecture': 'Eckpunkte des QR-Codes',
    'Brand Centerpiece': 'Zentrales Logo im QR-Code',
    'Scannability Shield': 'Scanbarkeits-Schutz',
    'Environment Blur': 'Hintergrund-Unschärfe',
    'Color Target:': 'Farbprofil:',
    'DPI:': 'DPI (Auflösung):',
    '3mm Bleed Box': '3 mm Beschnittzugabe',
    '300 DPI': '300 DPI (Hochauflösend)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'SAML 2.0 Protokoll',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'IdP-Entitäts-ID (Entity ID)',
    'SSO URL': 'SSO-Anmelde-URL',
    'X.509 Certificate': 'X.509 Sicherheitszertifikat',
    'ACS URL': 'ACS-URL (Assertion Consumer Service)',
    'Entity ID': 'Entitäts-ID',
    'Navigate': 'Navigieren',
    'Select': 'Auswählen',
    'OmniSearch v1.0': 'Universalsuche OmniSearch',
    'Back to order review': 'Zurück zur Bestellübersicht',
    'Return to Intelligence Studio': 'Zurück zum Analyse-Studio',
    'View Full Notification Center': 'Vollständiges Benachrichtigungszentrum öffnen',
    'Read all': 'Alle als gelesen markieren',
    'Cancel department': 'Abteilung abbrechen',
    'Save department': 'Abteilung speichern',
    'Venue Segmentation': 'Bereichssegmentierung',
    'Collaborate on your menu together.': 'Arbeiten Sie gemeinsam an Ihrer Speisekarte.',
    'Timeline': 'Zeitplan',
    'Calendar': 'Reservierungskalender',
    'Book': 'Tisch reservieren',
    'Duration': 'Aufenthaltsdauer',
    'Time & Duration': 'Uhrzeit & Dauer',
    'Seating Preference': 'Sitzplatz-Präferenz',
    'Section': 'Saalbereich',
    'Min Capacity': 'Mindestbelegung',
    'Max Capacity': 'Maximalbelegung',
    'Phone (Optional)': 'Telefonnummer (Optional)',
    'Name or Walk-in': 'Name oder Laufkunde',
    'Next day': 'Nächster Tag',
    'Next month': 'Nächster Monat',
    'Live Connection': 'Live-Verbindung',
    'Live Inventory': 'Echtzeit-Lagerbestand',
    'Main Venue (Full Menu)': 'Hauptlokal (Vollständige Karte)',
    'Preview Menu': 'Speisekarte vorschauen',
    'Manual Setup Guide': 'Manuelle Einrichtungsanleitung',
    'Connected & Active': 'Verbunden und aktiv',
    'CTA Action': 'Call-to-Action-Schaltfläche',
    'Phone Contact': 'Kontakt-Telefonnummer',
    'Menu URL': 'Speisekarten-Webadresse',
    'Google Location': 'Google-Standort',
    'Field': 'Feld',
    'Linked': 'Verknüpft',
    'Synchronizing Hub...': 'Daten werden synchronisiert...',
    'Data Integrity': 'Datenintegrität',
    'Station Governance Suite': 'Arbeitsstationen-Verwaltung',
    'Open audit log': 'Audit-Protokoll öffnen',
    'Signal / Time': 'Ereignis / Zeit',
    'Identity': 'Benutzer / Identität',
    'Invitation Invalid': 'Einladung ungültig oder abgelaufen',
    'Invited To': 'Eingeladen zu',
    'Team Limit Reached': 'Limit für Teammitglieder erreicht',
    'Venue Limit Reached': 'Limit für Lokale erreicht',
    'Venue Setup Required': 'Ersteinrichtung erforderlich',
    'Create Venue': 'Neues Lokal erstellen',
    'TableGreet Onboarding': 'TableGreet Einführung',
    'No payment required': 'Keine Zahlung erforderlich',
    'Selected Plan': 'Ausgewählter Tarif',
    'Payment Method': 'Zahlungsart',
    'Invoice ID': 'Rechnungsnummer',
    'Verified': 'Verifiziert',
    'Accent color': 'Akzentfarbe',
    'Set the voice and overall light level.': 'Legen Sie den Stil und das Helligkeitsniveau fest.',
    'Layout architecture': 'Layout-Architektur',
    'Navigation & motion': 'Navigation & Animationen',
    'Loading preview engine': 'Vorschau-Engine wird geladen',
    'Real Live Preview': 'Echtzeit-Live-Vorschau',
    'Included with Pro & Max': 'Enthalten in Pro & Max',
    'Daypart Scheduling': 'Tageszeiten-Planung',
    'Operational Daypart Architecture': 'Architektur der Betriebszeiten',
    'Service Protocol': 'Service-Protokoll',
    'Configuration Engine': 'Konfigurations-Engine',
    'Linked Department': 'Verknüpfte Abteilung',
    'Team Hub': 'Team-Zentrale',
    'Team Size': 'Teamgröße',
    'Joined': 'Beigetreten am',
    'Cancel Invitation': 'Einladung stornieren',
    'Initialize New Organization': 'Neue Organisation initialisieren',
    'Operational': 'Betriebsbereit',
    'Pro Feature': 'Pro-Funktion',
    'Current State': 'Aktueller Zustand',
    'Open on the big TV': 'Auf dem großen TV-Bildschirm öffnen',
    'Menu Board': 'Digitale Menütafel',
    'Reward': 'Belohnung',
    'Reward name': 'Prämienname',
    'Cancel & refund': 'Stornieren & erstatten',
    'Code': 'Code',
    'Find': 'Finden',
    'Complete': 'Abgeschlossen',
    'Seated': 'Platziert',
    'No-Show': 'Nicht erschienen',
    'Walk-in': 'Laufkundschaft',
    'Party': 'Personenzahl',
    'Turnover': 'Tischdrehung',
    'We couldn\'t load the menu': 'Die Speisekarte konnte nicht geladen werden',
    'Venue Not Found': 'Lokal nicht gefunden',
    'Live Availability': 'Live-Verfügbarkeit',
    'Multi-Language Suite': 'Mehrsprachigkeits-Suite',
    'Key name': 'Schlüssel-Bezeichnung',
    'Revoke API key': 'API-Schlüssel widerrufen',
    'Delete webhook': 'Webhook löschen',
    'Identity Provider': 'Identitätsanbieter (IdP)',
    'Provider': 'Anbieter',
    'Mandatory MFA': 'Verpflichtende Zwei-Faktor-Authentifizierung (MFA)',
    'Account Identity': 'Konto-Identität',
    'Login Email': 'Anmelde-E-Mail',
    'New Login Email': 'Neue Anmelde-E-Mail',
    'Phone Login': 'Telefon-Anmeldung',
    'SMS Verification Code': 'SMS-Bestätigungscode',
    'Integration Method': 'Integrationsmethode',
    'Stripe Connect': 'Stripe Connect Zahlungen',
    'Stripe Secret Key': 'Geheimer Stripe-Schlüssel',
    'Stripe Webhook Secret': 'Stripe-Webhook-Geheimnis',
    'Client-Owned': 'Kunden-Eigentum',
    'Verify connection': 'Verbindung überprüfen',
    'Setup Health': 'Einrichtungsstatus',
    'Privacy Protected': 'Datenschutz gesichert',
    'Privacy Policy': 'Datenschutzerklärung',
    'Continue': 'Weiter',
    'Sign In': 'Anmelden',
    'Didn\'t get it?': 'Nicht erhalten?',
    'Logged In Account': 'Angemeldetes Konto',
    'Annual': 'Jährlich',
    'Unread': 'Ungelesen',
    'Starred': 'Markiert',
    'High / Urgent': 'Hoch / Dringend',
    'Mark Read': 'Als gelesen markieren',
    'Delete notification': 'Benachrichtigung löschen',
    'Urgent': 'Dringend',
    'High': 'Hoch',
    'Normal': 'Normal',
    'Low': 'Niedrig',
    'Select a Ticket': 'Ticket auswählen',
    'Urgent (Immediate attention)': 'Dringend (Sofortige Beachtung)',
    'Type your reply here...': 'Geben Sie hier Ihre Antwort ein...',
    'Print Card (PDF)': 'Tischkarte drucken (PDF)',
    'Advanced Printing Engine': 'Professionelle Druck-Engine',
    'Multi-Venue Governance': 'Filial- und Mehrbetriebsverwaltung',
    'Network Revenue': 'Netzwerk-Gesamtumsatz',
    'Live Network Total': 'Netzwerk-Echtzeitsumme',
    'Audit Log Hub': 'Audit-Protokollzentrale',
    'Export Log': 'Protokoll exportieren',
    'CUSTOM ROLE SCHEMAS': 'BENUTZERDEFINIERTE ROLLENSCHEMATA',
    'Unique internal role identifier.': 'Eindeutige interne Rollenkennung.',
    'Human-friendly role label.': 'Verständliche Bezeichnung für das Team.',
    'Marketing Hub': 'Marketing-Zentrale',
    'Revenue Generation Suite': 'Umsatzsteigerungs-Tools',
    'Active Campaign Limit Reached (3/3)': 'Limit für aktive Kampagnen erreicht (3/3)',
    'Banner Content': 'Banner-Inhalt',
    'Style Profile': 'Stil-Profil',
    'Action URL (Optional)': 'Aktions-Link (Optional)',
    'Promotion Label': 'Aktionsbezeichnung',
    'Target Selection': 'Ziel-Auswahl',
    'Edit Promotion': 'Aktion bearbeiten',
    'Delete Promotion': 'Aktion löschen',
    'Loyalty Engine': 'Kundenbindungsprogramm',
    'Reward catalog': 'Prämien-Katalog',
    'Add reward': 'Prämie hinzufügen',
    'Loyalty ID': 'Kunden-ID',
    'No point activity yet': 'Noch keine Punkte-Aktivität',
    'Scan or enter QR value': 'QR-Code scannen oder Code eingeben',
    'TableGreet Admin': 'TableGreet Verwaltung',
    'Capacity Pacing': 'Kapazitätssteuerung',
    'Cancellation Policy': 'Stornierungsbedingungen',
    'Booking Experience': 'Reservierungserlebnis',
    'Floor Occupancy': 'Saal-Auslastung',
    'Currently Seated': 'Derzeit am Tisch',
    'Seat Party': 'Gäste platzieren',
    'Seat Table': 'Tisch zuweisen',
    'Select Table': 'Tisch auswählen',
    'Table / Capacity': 'Tisch / Kapazität',
    'Table Name/Number': 'Tisch-Name oder -Nummer',
    'Mark Arrived': 'Als anwesend markieren',
    'Mark No-Show': 'Als nicht erschienen markieren',
    'Complete Dining': 'Essen beenden',
    'Standard Dining (Default)': 'Standard-Gastbereich (Standard)',
    'Walk-In Dining Log': 'Protokoll für Laufkundschaft',
    'Notification Center': 'Benachrichtigungszentrale',
    'Single Sign-On': 'Einmalanmeldung (SSO)',
    'Welcome Aboard!': 'Herzlich willkommen bei TableGreet!',
    'Select your TableGreet plan': 'Wählen Sie Ihren TableGreet-Tarif',
    'Free Plan Activation': 'Aktivierung des kostenlosen Tarifs',
    'Venue Created!': 'Lokal erfolgreich erstellt!',
    'Upgrade for Unlimited': 'Auf unbegrenzt upgraden',
    'Auto-Tag Menu': 'Menü automatisch taggen',
    'Start Auto-Tagging': 'Automatisches Taggen starten',
    'Launch Auto-Tagger': 'Auto-Tagger starten',
    'Launch PDF Importer': 'PDF-Menü importieren',
    'Analyzing Menu...': 'Speisekarte wird analysiert...',
    'Analyzing Recipe...': 'Rezept wird analysiert...',
    'Manual Availability': 'Manuelle Verfügbarkeit',
    'Technical Breakdown': 'Technische Aufschlüsselung',
    'Dietary Taxonomy': 'Ernährungs- und Allergenangaben',
    'Dietary & Allergen AI': 'KI für Ernährung und Allergene',
    'Inherit from Category': 'Aus Kategorie übernehmen',
    'Ingredient Library': 'Zutaten-Bibliothek',
    'Ingredient Name': 'Name der Zutat',
    'Menu Organization': 'Menü-Strukturierung',
    'Inventory Taxonomy Hub': 'Inventar- und Kategorieverwaltung',
    'Category Intelligence': 'Kategorie-Erkenntnisse',
    'Service Period': 'Servicezeitraum',
    'Manual Configuration / All Day': 'Manuelle Konfiguration / Ganztägig',
    'Operational Department': 'Betriebsabteilung',
    'Global / No Department': 'Allgemein / Keine Abteilung',
    'Cancel category': 'Kategorie abbrechen',
    'New category name...': 'Neuer Kategoriename...',
    'Save category': 'Kategorie speichern',
    'QR Matrix Only': 'Nur QR-Code',
    'Vector & PDF': 'Vektor & PDF',
    'Vector QR (SVG)': 'Vektor-QR-Code (SVG)',
    'Vector QR (PDF)': 'Druckfertiger QR-Code (PDF)',
    'Save QR Style': 'QR-Stil speichern',
    'Share Link': 'Link teilen',
    'High Fidelity 300DPI': 'Hochauflösende 300 DPI Druckqualität',
    'Generating 300 DPI PDF...': '300 DPI PDF wird erstellt...',
    'Give your venue a point of view.': 'Geben Sie Ihrem Lokal eine unverwechselbare visuelle Identität.',
    'Signage Menu Not Found': 'Digitales Anzeigemenü nicht gefunden',
    'Scan Menu': 'Menü scannen',
    'Open on your phone': 'Auf dem Smartphone öffnen',
    'Full Screen': 'Vollbild',
    'Lock TV board': 'TV-Anzeige sperren',
    'Big-Screen Menu': 'Großbild-Menüanzeige',
    'TV Signage': 'TV-Signage',
    'General Information': 'Allgemeine Informationen',
    'Venue Name *': 'Name des Lokals *',
    'Public Slug *': 'Öffentliche Webadresse (Slug) *',
    'Venue Logo': 'Lokal-Logo',
    'Hero Cover Image': 'Haupt-Titelbild',
    'Venue Timezone': 'Zeitzone des Lokals',
    'Primary Brand Color': 'Haupt-Markenfarbe',
    'TableGreet footer credit': 'TableGreet-Hinweis in der Fußzeile',
    'White-Label Branding': 'White-Label-Branding',
    'Client DNS Setup': 'DNS-Konfiguration der Domain',
    'Copy CNAME target': 'CNAME-Ziel kopieren',
    'Copy TableGreet URL': 'TableGreet-URL kopieren',
    'Included TableGreet URL': 'Inkludierte TableGreet-Webadresse',
    'Open TableGreet URL': 'TableGreet-URL öffnen',
    'Public menu': 'Öffentliche Gästekarte',
    'Public venue delivery': 'Lieferservice über die Speisekarte',
    'Zone name': 'Zonenbezeichnung',
    'Coverage note': 'Hinweis zum Liefergebiet',
    'Default ETA min': 'Mindest-Lieferzeit (Minuten)',
    'Default ETA max': 'Höchst-Lieferzeit (Minuten)',
    'Packaging fee': 'Verpackungsgebühr',
    'Courier handled by': 'Lieferung durchgeführt durch',
    'Third-party courier': 'Externer Kurierdienst',
    'Unified Experience': 'Einheitliches Gästeerlebnis',
    'Save WiFi to Venue': 'WLAN für das Lokal speichern',
    'Network Name (SSID)': 'WLAN-Netzwerkname (SSID)',
    'Audio Alert Chime': 'Akustisches Tonsignal',
    'Time Format': 'Zeitformat',
    'Auto / Default': 'Automatisch / Standard',
    'Syncing SEO & Google Data...': 'SEO- und Google-Daten werden synchronisiert...',
    'Google Connection': 'Google-Verbindung',
    'Search Engine Metadata': 'Suchmaschinen-Metadaten',
    'Social card preview': 'Vorschau für soziale Netzwerke',
    'Allow Indexing': 'Indexierung erlauben',
    'Canonical URL Override': 'Kanonische URL überschreiben',
    'Price Range': 'Preiskategorie',
    'Latitude': 'Breitengrad',
    'Longitude': 'Längengrad',
    'Google Place ID / Location ID': 'Google Place-ID / Standort-ID',
    'Google Search Preview': 'Vorschau in der Google-Suche',
    'Linked Google Location': 'Verknüpfter Google-Standort',
    'SEO Documentation': 'SEO-Dokumentation',
    ', and': ', und',
    'and': 'und',
    'Delete Department?': 'Abteilung löschen?',
    'Delete Department': 'Abteilung löschen',
    'Live URL': 'Live-URL',
    'Copy full live URL': 'Vollständige Live-URL kopieren',
    'Open live department menu': 'Live-Abteilungsmenü öffnen',
    'Loading departments...': 'Abteilungen werden geladen...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Die Bereichssegmentierung mit mehreren Abteilungen ist eine Premium-Funktion ab PRO.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Gliedern Sie Ihre Speisekarte nach Räumen, Tischen, Bars oder Bereichen.',
    'Targeted Ordering': 'Gezielte Bestellungen',
    'Route guests and orders to specific preparation areas.': 'Leiten Sie Gäste und Bestellungen an bestimmte Zubereitungsbereiche weiter.',
    'Operational Departments': 'Betriebsabteilungen',
    'OPERATIONAL DEPARTMENTS': 'BETRIEBSABTEILUNGEN',
    'Venue Segmentation Hub': 'Zentrum für Bereichssegmentierung',
    'VENUE SEGMENTATION HUB': 'ZENTRUM FÜR BEREICHSSEGMENTIERUNG',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Erstellen Sie Abteilungen, um Ihre Menüs zu gliedern. Beispielsweise kann ein großes Lokal',
    'Restaurant': 'Restaurant',
    'Poolside': 'Poolbereich',
    'department, each with its own categories.': ', jeweils mit eigenen Kategorien.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Mit Abteilungen können Sie Ihr Lokal in Bereiche wie Restaurant, Bar oder Pool unterteilen – jeweils mit eigenen Kategorien.',
    'Department name': 'Abteilungsname',
    'New department name...': 'Neuer Abteilungsname...',
    'No Linked Daypart (Always Available)': 'Keine Tageszeit verknüpft (immer verfügbar)',
    'Linked Daypart': 'Verknüpfte Tageszeit',
    'Localizations': 'Übersetzungen',
    'LOCALIZATIONS': 'ÜBERSETZUNGEN',
    'AI Auto-Translate': 'Automatische KI-Übersetzung',
    'AI AUTO-TRANSLATE': 'AUTOMATISCHE KI-ÜBERSETZUNG',
    'Translated name': 'Übersetzter Name',
    'No Departments Yet': 'Noch keine Abteilungen vorhanden',
    'Add First Department': 'Erste Abteilung hinzufügen',
    'Avg Order Value': 'Durchschnittlicher Bestellwert',
    'AVG ORDER VALUE': 'DURCHSCHNITTLICHER BESTELLWERT',
    'item taps': 'Gerichtsaufrufe',
    'ITEM TAPS': 'GERICHTSAUFRUFE',
    'Avg Rating': 'Durchschnittsbewertung',
    'AVG RATING': 'DURCHSCHNITTSBEWERTUNG',
    'Revenue (Last 7 Days)': 'Umsatz (Letzte 7 Tage)',
    'REVENUE (LAST 7 DAYS)': 'UMSATZ (LETZTE 7 TAGE)',
    'Daily breakdown': 'Tägliche Übersicht',
    'DAILY BREAKDOWN': 'TÄGLICHE ÜBERSICHT',
    'Menu Views (Last 7 Days)': 'Menüaufrufe (Letzte 7 Tage)',
    'MENU VIEWS (LAST 7 DAYS)': 'MENÜAUFRUFE (LETZTE 7 TAGE)',
    'Guest traffic': 'Gästeaufkommen',
    'GUEST TRAFFIC': 'GÄSTEAUFKOMMEN',
    'Most Viewed Items': 'Meistangesehene Gerichte',
    'MOST VIEWED ITEMS': 'MEISTANGESEHENE GERICHTE',
    'Top Rated Items': 'Bestbewertete Gerichte',
    'TOP RATED ITEMS': 'BESTBEWERTETE GERICHTE',
    'Waiter Requests': 'Kellner-Anfragen',
    'WAITER REQUESTS': 'KELLNER-ANFRAGEN',
    'Total': 'Gesamt',
    'TOTAL': 'GESAMT',
    'Order Status (30 days)': 'Bestellstatus (30 Tage)',
    'ORDER STATUS (30 DAYS)': 'BESTELLSTATUS (30 TAGE)',
    'Recent Guest Feedback': 'Neueste Gästebewertungen',
    'RECENT GUEST FEEDBACK': 'NEUESTE GÄSTEBEWERTUNGEN',
    'Conversion Rate': 'Konversionsrate',
    'CONVERSION RATE': 'KONVERSIONSRATE',
    'of menu visitors placed an order': 'der Speisekartenbesucher haben bestellt',
    'Refreshed': 'Aktualisiert',
    'REFRESHED': 'AKTUALISIERT',
    'Last 30 days': 'Letzte 30 Tage',
    'LAST 30 DAYS': 'LETZTE 30 TAGE',
    'cash': 'bar',
    'Cash': 'Bar',
    'CASH': 'BAR',
    'bill': 'rechnung',
    'Bill': 'Rechnung',
    'BILL': 'RECHNUNG',
    'water': 'wasser',
    'Water': 'Wasser',
    'WATER': 'WASSER',
    'service': 'service',
    'Service': 'Service',
    'SERVICE': 'SERVICE',
    'No interaction data yet': 'Noch keine Interaktionsdaten erfasst',
    'No ratings collected yet': 'Noch keine Bewertungen erfasst',
    'No requests in period': 'Keine Anfragen im Zeitraum',
    'No feedback collected yet': 'Noch kein Feedback erhalten',
    'No menu views recorded yet': 'Noch keine Menüaufrufe erfasst',
    'Dish Rating Breakdown': 'Bewertungsübersicht der Gerichte',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Gerichte-Rankings und Zufriedenheitswerte sind exklusiv in den Tarifen MAX und Enterprise verfügbar.',
    'Customer Sentiment & Reviews': 'Gästefeedback & Bewertungen',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Direkte Gästebewertungen und Sentiment-Analysen erfordern ein MAX- oder Enterprise-Abonnement.',
    'Reviews AI': 'Bewertungs-KI',
    'Guest Feedback': 'Gästefeedback',
    'Upgrade to unlock rating analytics →': 'Upgrade durchführen, um Bewertungsanalysen freizuschalten →',
    'Upgrade to Max': 'Auf Max upgraden',
    'UPGRADE TO MAX': 'AUF MAX UPGRADEN',
    'MAX PLAN': 'MAX-TARIF',
    'No Data Yet': 'Noch keine Daten vorhanden',
    'Share your QR code to start collecting real venue insights.': 'Teilen Sie Ihren QR-Code, um echte Einblicke in den Betriebsablauf zu erhalten.',
    'Revenue Analytics': 'Umsatzanalyse',
    'REVENUE ANALYTICS': 'UMSATZANALYSE',
    'Track your earnings, orders, and average ticket values over time.': 'Verfolgen Sie Einnahmen, Bestellungen und durchschnittliche Bons über die Zeit.',
    'Operational Velocity': 'Betriebsgeschwindigkeit',
    'OPERATIONAL VELOCITY': 'BETRIEBSGESCHWINDIGKEIT',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Überwachen Sie Gästeaufkommen, beliebte Gerichte und die Auslastung der Servicekräfte.',
    'Upgrade to Pro': 'Auf Pro upgraden',
    'UPGRADE TO PRO': 'AUF PRO UPGRADEN',
    'Loading Insights…': 'Lade Einblicke…',
    'Loading Insights...': 'Lade Einblicke...',
    'Telemetry Unavailable': 'Aktivitätsdaten nicht verfügbar',
    'TELEMETRY UNAVAILABLE': 'AKTIVITÄTSDATEN NICHT VERFÜGBAR',
    'No Data Captured': 'Keine Daten erfasst',
    'NO DATA CAPTURED': 'KEINE DATEN ERFASST',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Teilen Sie Ihren QR-Code, um Besucher- und Bestelldaten in Echtzeit zu erfassen.',
    'INTELLIGENCE STUDIO': 'ANALYSEZENTRUM',
    'Insights Engine Locked': 'Erweiterte Analysen gesperrt',
    'INSIGHTS ENGINE LOCKED': 'ERWEITERTE ANALYSEN GESPERRT',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Erweiterte Trendanalysen, Sammelaktionen und Datenexporte sind reserviert für',
    'Unlock Pro Features': 'Pro-Funktionen freischalten',
    'UNLOCK PRO FEATURES': 'PRO-FUNKTIONEN FREISCHALTEN',
    'MENU VIEWS': 'MENÜAUFRUFE',
    'ITEM INTERACTIONS': 'GERICHTSINTERAKTIONEN',
    'GUEST RATING': 'GÄSTEBEWERTUNG',
    'DELIVERY REVENUE': 'LIEFERUMSATZ',
    'AVERAGE DELIVERY ORDER': 'DURCHSCHNITTLICHE LIEFERBESTELLUNG',
    'AVERAGE DELIVERY FEE': 'DURCHSCHNITTLICHE LIEFERGEBÜHR',
    'INCLUDING FEES': 'INKLUSIVE GEBÜHREN',
    'ZONE AVERAGE': 'ZONENDURCHSCHNITT',
    'TRAFFIC PROJECTIONS': 'BESUCHERPROGNOSEN',
    'TELEMETRY SIGNAL TRACKING': 'AKTIVITÄTSSIGNAL-TRACKING',
    'VEGETARIAN': 'VEGETARISCH',
    'VEGAN': 'VEGAN',
    'SPICY': 'SCHARF',
    'GLUTEN-FREE': 'GLUTENFREI',
    'Reset: All In-Stock': 'Zurücksetzen: Alles vorrätig',
    'Emergency Stop (All Out)': 'Notstopp: Alles ausverkauft',
    'No compliance issues': 'Keine Beanstandungen',
    'NO COMPLIANCE ISSUES': 'KEINE BEANSTANDUNGEN',
    'detected in current menu': 'in der aktuellen Speisekarte',
    'DETECTED IN CURRENT MENU': 'IN DER AKTUELLEN SPEISEKARTE',
    'Telemetry History (24h)': 'Aktivitätsverlauf (24h)',
    'TELEMETRY HISTORY (24H)': 'AKTIVITÄTSVERLAUF (24H)',
    'Telemetry History (30d)': 'Aktivitätsverlauf (30 T.)',
    'TELEMETRY HISTORY (30D)': 'AKTIVITÄTSVERLAUF (30 T.)',
    'Save': 'Speichern',
    'Saved': 'Gespeichert',
    'Saving...': 'Wird gespeichert...',
    'Save Changes': 'Änderungen speichern',
    'Save Settings': 'Einstellungen speichern',
    'Cancel': 'Abbrechen',
    'Close': 'Schließen',
    'Create': 'Erstellen',
    'Add': 'Hinzufügen',
    'Edit': 'Bearbeiten',
    'Delete': 'Löschen',
    'Remove': 'Entfernen',
    'Update': 'Aktualisieren',
    'Refresh': 'Neu laden',
    'Search': 'Suchen',
    'Search...': 'Suchen...',
    'Search items...': 'Gerichte suchen...',
    'Search categories...': 'Kategorien suchen...',
    'Filter': 'Filtern',
    'All': 'Alle',
    'Export': 'Exportieren',
    'Import': 'Importieren',
    'Download': 'Herunterladen',
    'Upload': 'Hochladen',
    'Copy': 'Kopieren',
    'Copied': 'Kopiert',
    'Copied!': 'Kopiert!',
    'Preview': 'Vorschau',
    'Open': 'Geöffnet',
    'View': 'Anzeigen',
    'Manage': 'Verwalten',
    'Configure': 'Konfigurieren',
    'Enable': 'Aktivieren',
    'Disable': 'Deaktivieren',
    'Enabled': 'Aktiviert',
    'Disabled': 'Deaktiviert',
    'Active': 'Aktiv',
    'Inactive': 'Inaktiv',
    'Available': 'Verfügbar',
    'Unavailable': 'Nicht verfügbar',
    'Visible': 'Sichtbar',
    'Hidden': 'Ausgeblendet',
    'Required': 'Erforderlich',
    'Optional': 'Optional',
    'Confirm': 'Bestätigen',
    'Back': 'Zurück',
    'Next': 'Weiter',
    'Done': 'Fertig',
    'Upgrade': 'Upgrade',
    'Sign Out': 'Abmelden',
    'Exit to Main Admin': 'Zurück zur Hauptverwaltung',
    'Change Language': 'Sprache ändern',
    'Add Category': 'Kategorie hinzufügen',
    'Add Menu Item': 'Gericht hinzufügen',
    'Add Item': 'Artikel hinzufügen',
    'Add Ingredient': 'Zutat hinzufügen',
    'Add Department': 'Bereich hinzufügen',
    'Delete Item': 'Artikel löschen',
    'Delete Category': 'Kategorie löschen',
    'Upload Image': 'Bild hochladen',
    'Upload Photo': 'Foto hochladen',
    'Select Category': 'Kategorie auswählen',
    'Select Department': 'Bereich auswählen',
    'Add translation...': 'Übersetzung hinzufügen...',
    'Clear': 'Löschen',
    'Reset': 'Zurücksetzen',
    'Apply': 'Anwenden',
    'Submit': 'Absenden',
    'Core & Operations': 'Betrieb & Service',
    'Menu & Experience': 'Speisekarte & Gäste',
    'Enterprise & Security': 'Verwaltung & Team',
    'System & Control': 'Einstellungen & Hilfe',
    'Intelligence Studio': 'Analysezentrum',
    'Venue Insights': 'Berichte & Umsatz',
    'Reservations': 'Reservierungen',
    'Floor Plan': 'Tischplan',
    'Live Waitlist': 'Warteliste',
    'Walk-Ins': 'Laufkundschaft',
    'Walk-ins': 'Laufkundschaft',
    'Departments': 'Bereiche',
    'Service Hours': 'Servicezeiten',
    'Categories': 'Kategorien',
    'Menu Categories': 'Menükategorien',
    'Menu Items': 'Gerichte im Menü',
    'Items': 'Artikel',
    'Ingredients': 'Zutaten',
    'Loyalty Program': 'Treueprogramm',
    'Campaign Studio': 'Aktionen & Rabatte',
    'Signage': 'TV-Anzeige',
    'QR Code': 'QR-Codes',
    'Kitchen Ops': 'Küchenmonitor',
    'Theme Engine': 'Design & Farben',
    'Governance': 'Verwaltung',
    'Audit Log': 'Aktivitätsprotokoll',
    'API & Webhooks': 'Integrationen',
    'SSO & Security': 'Sicherheit & Anmeldung',
    'Team': 'Mitarbeiter',
    'Billing': 'Abo & Abrechnung',
    'SEO & Google': 'Google-Sichtbarkeit',
    'Notifications': 'Benachrichtigungen',
    'Support Center': 'Hilfe & Kontakt',
    'Settings': 'Restaurant-Einstellungen',
    'Name': 'Name',
    'Description': 'Beschreibung',
    'Price': 'Preis',
    'Status': 'Status',
    'Actions': 'Aktionen',
    'Role': 'Rolle',
    'Email': 'E-Mail',
    'Phone': 'Telefon',
    'Address': 'Adresse',
    'City': 'Stadt',
    'Currency': 'Währung',
    'Language': 'Sprache',
    'Theme': 'Thema',
    'General': 'Allgemein',
    'Total Revenue': 'Gesamtumsatz',
    'Total Orders': 'Bestellungen gesamt',
    'Average Order': 'Durchschnittsbestellung',
    'Active Sessions': 'Aktive Tische',
    'Recent Orders': 'Aktuelle Bestellungen',
    'Live Orders': 'Live-Bestellungen',
    'Table': 'Tisch',
    'Date': 'Datum',
    'Time': 'Uhrzeit',
    'Amount': 'Betrag',
    'Customer': 'Gast',
    'In Stock': 'Vorrätig',
    'Out of Stock': 'Ausverkauft',
    'Sold Out': 'Ausverkauft',
    'Dine In': 'Vor Ort',
    'Pickup': 'Abholung',
    'Delivery': 'Lieferung',
    'Paid': 'Bezahlt',
    'Unpaid': 'Unbezahlt',
    'Pending': 'Ausstehend',
    'Preparing': 'In Zubereitung',
    'Ready': 'Bereit',
    'Completed': 'Abgeschlossen',
    'Cancelled': 'Storniert',
    'Loading...': 'Laden...',
    'No data': 'Keine Daten',
    'No results found': 'Keine Ergebnisse gefunden',
    'No items found': 'Keine Artikel gefunden',
    'No translations defined yet': 'Noch keine Übersetzungen festgelegt',
    'Are you sure?': 'Sind Sie sicher?',
    'This action cannot be undone.': 'Diese Aktion kann nicht rückgängig gemacht werden.',
    'Settings saved successfully!': 'Einstellungen erfolgreich gespeichert!',
    'Administrator': 'Administrator',
    'Manager': 'Manager',
    'Owner': 'Eigentümer',
    'Chef': 'Küchenchef',
    'Waiter': 'Kellner',
    'Cashier': 'Kassierer',
    'Type a command or search...': 'Befehl eingeben oder suchen...',
    'Command Palette': 'Befehlsübersicht',
    'Demand Velocity': 'Nachfragegeschwindigkeit',
    'DEMAND VELOCITY': 'NACHFRAGEGESCHWINDIGKEIT',
    'Top Performing Entities': 'Leistungsstärkste Gerichte',
    'TOP PERFORMING ENTITIES': 'LEISTUNGSSTÄRKSTE GERICHTE',
    'Hourly Load Analysis': 'Stündliche Auslastungsanalyse',
    'HOURLY LOAD ANALYSIS': 'STÜNDLICHE AUSLASTUNGSANALYSE',
    'Bulk Command Center': 'Massenaktionen-Zentrale',
    'BULK COMMAND CENTER': 'MASSENAKTIONEN-ZENTRALE',
    'Governance Scan': 'Menüqualitäts-Scan',
    'GOVERNANCE SCAN': 'MENÜQUALITÄTS-SCAN',
    'No compliance issues detected in current menu': 'Keine Probleme im aktuellen Menü gefunden',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'KEINE PROBLEME IM AKTUELLEN MENÜ GEFUNDEN',
    'Procedural Tagging': 'Automatisches Tagging',
    'PROCEDURAL TAGGING': 'AUTOMATISCHES TAGGING',
    'Name match...': 'Namenssuche...',
    '...Name match': 'Namenssuche...',
    'Healthy': 'Gesund',
    'HEALTHY': 'GESUND',
    'Apply Tags': 'Tags anwenden',
    'APPLY TAGS': 'TAGS ANWENDEN',
    'Global Price Shift': 'Globale Preisanpassung',
    'GLOBAL PRICE SHIFT': 'GLOBALE PREISANPASSUNG',
    'Adjust': 'Anpassen',
    'ADJUST': 'ANPASSEN',
    'Availability Override': 'Verfügbarkeit sofort steuern',
    'AVAILABILITY OVERRIDE': 'VERFÜGBARKEIT SOFORT STEUERN',
    'Reset: ALL IN-STOCK': 'Zurücksetzen: ALLES VORRÄTIG',
    'RESET: ALL IN-STOCK': 'ZURÜCKSETZEN: ALLES VORRÄTIG',
    'EMERGENCY STOP (ALL OUT)': 'Notstopp (ALLES AUSVERKAUFT)',
    'Integrity Engine': 'Menü-Integritätssystem',
    'INTEGRITY ENGINE': 'MENÜ-INTEGRITÄTSSYSTEM',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Dieses System überwacht die Verfügbarkeit der Gerichte, Preisabweichungen und die Vollständigkeit der Beschreibungen in Ihrem Betrieb.',
    'Inventory Audit Export': 'Inventur-Audit-Export',
    'INVENTORY AUDIT EXPORT': 'INVENTUR-AUDIT-EXPORT',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Erstellen Sie eine vollständige Übersicht über Menüleistung und Metadaten für die Offline-Prüfung.',
    'Enterprise Data Export': 'Erweiterter Datenexport',
    'ENTERPRISE DATA EXPORT': 'ERWEITERTER DATENEXPORT',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Erstellen und laden Sie umfassende Leistungsberichte in Standardformaten herunter. Enthält Besucherströme, Gerichtsnachfrage und Konversionsraten.',
    'JSON Snapshot': 'JSON-Momentaufnahme',
    'JSON SNAPSHOT': 'JSON-MOMENTAUFNAHME',
    'Compile CSV Report': 'CSV-Bericht erstellen',
    'COMPILE CSV REPORT': 'CSV-BERICHT ERSTELLEN',
    'Date Segment': 'Datumsabschnitt',
    'DATE SEGMENT': 'DATUMSABSCHNITT',
    'Inbound Views': 'Eingehende Aufrufe',
    'INBOUND VIEWS': 'EINGEHENDE AUFRUFE',
    'Asset Interactions': 'Interaktionen mit Gerichten',
    'ASSET INTERACTIONS': 'INTERAKTIONEN MIT GERICHTEN',
    'Telemetry History': 'Aktivitätsverlauf',
    'TELEMETRY HISTORY': 'AKTIVITÄTSVERLAUF',
    'Telemetry History (7d)': 'Aktivitätsverlauf (7T)',
    'TELEMETRY HISTORY (7D)': 'AKTIVITÄTSVERLAUF (7T)',
    'Growth Trajectory': 'Wachstumstrend',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Die Interaktionsrate Ihrer Speisekarte zeigt in den letzten 7 Tagen einen Aufwärtstrend.',
    'Deep Analysis Engine': 'Detaillierte Analyse',
    'DEEP ANALYSIS ENGINE': 'DETAILLIERTE ANALYSE',
    'Asset Velocity': 'Gerichte-Beliebtheit',
    'ASSET VELOCITY': 'GERICHTE-BELIEBTHEIT',
    'Traffic Projections': 'Besucherprognosen',
    'Telemetry Signal Tracking': 'Signal- und Aktivitätstracking',
    'Service Hour Update': 'Aktualisierung der Servicezeiten',
    'Late Night Lounge Hours': 'Spätöffnungszeiten Lounge',
    'Bar & Lounge Hours': 'Bar- & Loungezeiten',
    'Breakfast & Brunch Hours': 'Frühstücks- & Brunchzeiten',
    'Switch Location': 'Standort wechseln',
    'SWITCH LOCATION': 'STANDORT WECHSELN',
    'Global Admin': 'Hauptadministrator',
    'GLOBAL ADMIN': 'HAUPTADMINISTRATOR',
    'Staff Access': 'Mitarbeiterzugang',
    'STAFF ACCESS': 'MITARBEITERZUGANG',
    'Venue Overview': 'Betriebsübersicht',
    'VENUE OVERVIEW': 'BETRIEBSÜBERSICHT',
    'Unified Telemetry Engine': 'Zentrales Aktivitätsprotokoll',
    'UNIFIED TELEMETRY ENGINE': 'ZENTRALES AKTIVITÄTSPROTOKOLL',
    'Pulse': 'Echtzeit',
    'PULSE': 'ECHTZEIT',
    'Insights': 'Erkenntnisse',
    'INSIGHTS': 'ERKENNTNISSE',
    'Reports': 'Berichte',
    'REPORTS': 'BERICHTE',
    'GOVERNANCE': 'VERWALTUNG',
    'Multi-Venue': 'Multi-Standort',
    'MULTI-VENUE': 'MULTI-STANDORT',
    'Menu Views': 'Menüaufrufe',
    'Item Interactions': 'Gerichte-Interaktionen',
    'Guest Rating': 'Gästebewertung',
    'Revenue (7d)': 'Umsatz (7T)',
    'Revenue (24h)': 'Umsatz (24h)',
    'Revenue (30d)': 'Umsatz (30T)',
    'Delivery orders (7d)': 'Lieferbestellungen (7T)',
    'Delivery orders (24h)': 'Lieferbestellungen (24h)',
    'Delivery orders (30d)': 'Lieferbestellungen (30T)',
    'Delivery revenue': 'Lieferumsatz',
    'Average delivery order': 'Durchschnittliche Lieferbestellung',
    'Average delivery fee': 'Durchschnittliche Liefergebühr',
    'Zone average': 'Zonendurchschnitt',
    'Including fees': 'Inklusive Gebühren',
    'No deliveries': 'Keine Lieferungen',
    'No orders': 'Keine Bestellungen',
    'No reviews': 'Keine Bewertungen',
    'Live': 'Live',
    'LIVE': 'LIVE',
    'reviews': 'Bewertungen',
    'orders': 'Bestellungen',
    'deliveries': 'Lieferungen',
    'of orders': 'der Bestellungen',
    'rate': 'Rate',
    'Recent Activity': 'Neueste Aktivität',
    'Real-time Venue Telemetry': 'Echtzeit-Betriebsaktivität',
    'REAL-TIME VENUE TELEMETRY': 'ECHTZEIT-BETRIEBSAKTIVITÄT',
    'Rapid Pulse View': 'Schnellübersicht Echtzeit',
    'Anonymous Guest': 'Anonymer Gast',
    'scanned menu': 'hat die Speisekarte gescannt',
    'viewed item': 'hat das Gericht angesehen',
    'interacted with': 'hat interagiert mit',
    'placed order': 'hat eine Bestellung aufgegeben',
    'searched menu': 'hat im Menü gesucht',
    'Main Landing': 'Startseite',
    'Trace': 'Verifiziert',
    'TRACE': 'VERIFIZIERT',
    'Gaining Telemetry...': 'Verbindung zu Betriebsdaten...',
    'No recent traffic detected': 'Keine aktuelle Aktivität erkannt',
    'Activities will appear here as guests scan your QR code': 'Aktivitäten werden hier angezeigt, sobald Gäste Ihren QR-Code scannen',
    'Analyze All Logs': 'Alle Protokolle analysieren',
    'Just now': 'Gerade eben',
    'less than a minute ago': 'vor weniger als einer Minute',
    '1 minute ago': 'vor 1 Minute',
    '1 hour ago': 'vor 1 Stunde',
    'about 1 hour ago': 'vor etwa 1 Stunde',
    '1 day ago': 'vor 1 Tag',
    'DAY AGO 1': 'vor 1 Tag',
    '2 days ago': 'vor 2 Tagen',
    'DAY AGO 2': 'vor 2 Tagen',
    '3 days ago': 'vor 3 Tagen',
    'DAY AGO 3': 'vor 3 Tagen',
    '4 days ago': 'vor 4 Tagen',
    'DAY AGO 4': 'vor 4 Tagen',
    '5 days ago': 'vor 5 Tagen',
    'DAY AGO 5': 'vor 5 Tagen',
    '6 days ago': 'vor 6 Tagen',
    'DAY AGO 6': 'vor 6 Tagen',
    '7 days ago': 'vor 7 Tagen',
    'DAY AGO 7': 'vor 7 Tagen',
    'about 1 month ago': 'vor etwa 1 Monat',
    'Monday': 'Montag',
    'Tuesday': 'Dienstag',
    'Wednesday': 'Mittwoch',
    'Thursday': 'Donnerstag',
    'Friday': 'Freitag',
    'Saturday': 'Samstag',
    'Sunday': 'Sonntag',
    'Mon': 'Mo',
    'Tue': 'Di',
    'Wed': 'Mi',
    'Thu': 'Do',
    'Fri': 'Fr',
    'Sat': 'Sa',
    'Sun': 'So',
    'Opening Hours': 'Öffnungszeiten',
    'Start Time': 'Startzeit',
    'End Time': 'Endzeit',
    'Opening Time': 'Öffnungszeit',
    'Closing Time': 'Schließzeit',
    'All Day': 'Ganztägig',
    'Closed': 'Geschlossen',
    'Active Days': 'Aktive Tage',
    'Active Schedule': 'Aktiver Zeitplan',
    'Always Available': 'Immer verfügbar',
    'Always Visible': 'Immer sichtbar',
    'New Category': 'Neue Kategorie',
    'Category': 'Kategorie',
    'Category Name': 'Kategoriename',
    'Category Description': 'Kategoriebeschreibung',
    'Display Order': 'Anzeigereihenfolge',
    'Item Name': 'Artikelname',
    'Item Description': 'Artikelbeschreibung',
    'Base Price': 'Grundpreis',
    'Preparation Time': 'Zubereitungszeit',
    'Calories': 'Kalorien',
    'Featured Item': 'Empfohlenes Gericht',
    'Chef\'s Recommendation': 'Empfehlung des Küchenchefs',
    'Spicy': 'Scharf',
    'Vegetarian': 'Vegetarisch',
    'Vegan': 'Vegan',
    'Gluten-Free': 'Glutenfrei',
    'Halal': 'Halal',
    'Kosher': 'Koscher',
    'Contains Nuts': 'Enthält Nüsse',
    'Dairy-Free': 'Laktosefrei',
    'Allergen Exclusions': 'Allergene',
    'Table Number': 'Tischnummer',
    'Assigned Table': 'Zugewiesener Tisch',
    'Seats': 'Sitzplätze',
    'Capacity': 'Kapazität',
    'Indoor': 'Innenbereich',
    'Outdoor': 'Außenbereich',
    'Terrace': 'Terrasse',
    'Bar': 'Bar',
    'Add Table': 'Tisch hinzufügen',
    'Edit Table': 'Tisch bearbeiten',
    'Delete Table': 'Tisch löschen',
    'Bookings': 'Buchungen',
    'Add to Waitlist': 'Auf Warteliste setzen',
    'Guest Name': 'Gastname',
    'Party Size': 'Personenanzahl',
    'Arrival Time': 'Ankunftszeit',
    'Special Requests': 'Besondere Wünsche',
    'Confirmed': 'Bestätigt',
    'No Show': 'Nicht erschienen',
    'Add Staff Member': 'Mitarbeiter hinzufügen',
    'Invite Staff': 'Mitarbeiter einladen',
    'Email Address': 'E-Mail-Adresse',
    'Phone Number': 'Telefonnummer',
    'Download QR Code': 'QR-Code herunterladen',
    'Print Table Stands': 'Tischaufsteller drucken',
    'Today': 'Heute',
    'Yesterday': 'Gestern',
    'This Week': 'Diese Woche',
    'This Month': 'Diesen Monat',
    'Last 30 Days': 'Letzte 30 Tage',
    'Access Denied': 'Zugriff verweigert',
    'Advanced Settings': 'Erweiterte Einstellungen',
    'Change Image': 'Bild ändern',
    'Remove Image': 'Bild entfernen',
    'Announcement Banner': 'Ankündigungsbanner',
    'Please fill in all required fields.': 'Bitte füllen Sie alle Pflichtfelder aus.',
    'Changes saved successfully': 'Änderungen erfolgreich gespeichert',
    '(You)': '(Du)',
    'AI Auto-Tag': 'KI-Auto-Tagging',
    'Analyzing:': 'Analysiere:',
    'Apple (Fruit/Vegan)': 'Apfel (Obst/Vegan)',
    'Cancel editing': 'Bearbeitung abbrechen',
    'Capacity:': 'Kapazität:',
    'Carrot (Veggie)': 'Karotte (Gemüse)',
    'CircleDot (Organic)': 'Punktkreis (Bio)',
    'Coffee (Caffeine)': 'Kaffee (Koffein)',
    'Configuration': 'Konfiguration',
    'Copy verification record name': 'Verifizierungs-Eintragsnamen kopieren',
    'Copy verification record value': 'Verifizierungs-Eintragswert kopieren',
    'Critical exception intercepted': 'Kritischer Ausnahmefehler abgefangen',
    'Kernel Panic': 'Systemfehler',
    'DNA (Organic/Bio)': 'DNA (Bio/Organisch)',
    'Egg (Ovo/Allergen)': 'Ei (Ovo/Allergen)',
    'Empty': 'Leer',
    'Engraving...': 'Erstelle...',
    'Flame (Spicy)': 'Flamme (Scharf)',
    'Free': 'Kostenlos',
    'From': 'Von',
    'Generating': 'Erstellen',
    'Governance & Traceability Studio': 'Verwaltungs- und Aktivitätszentrum',
    'Greet': 'Willkommen',
    'Heart (Healthy)': 'Herz (Gesund)',
    'In-App': 'In der App',
    'Infinity SVG': 'Vektor-SVG (Unbegrenzt)',
    'Leaf (Default)': 'Blatt (Standard)',
    'MAX Feature': 'Enterprise-Funktion',
    'Milk (Dairy)': 'Milch (Laktose)',
    'Network': 'Netzwerk',
    'Notify SMS': 'SMS-Benachrichtigung',
    'Order': 'Bestellung',
    'Original': 'Original',
    'Overview': 'Übersicht',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Standardrollen',
    'Shield (Safety)': 'Schild (Sicherheit)',
    'Soup (Warm/Soup)': 'Suppe (Warm/Suppe)',
    'Standard Pro View': 'Standardansicht',
    'Star': 'Stern',
    'Synchronized with Venue Identity': 'An das Design Ihres Betriebs angepasst',
    'Synchronizing Security Context...': 'Stelle sichere Verbindung her...',
    'Targeted Timing': 'Servicezeiten',
    'Targeting': 'Gilt für',
    'To': 'Bis',
    'Toggle Navigation': 'Navigation umschalten',
    'Venue': 'Betrieb',
    'Venue Governance Suite': 'Betriebs- und Filialverwaltung',
    'Volume:': 'Lautstärke:',
    'WHITEBOARD NOTE...': 'Notiz für das Team...',
    'Warning (Alert)': 'Warnung (Hinweis)',
    'Wheat (Flour/Gluten)': 'Weizen (Mehl/Gluten)',
    'Wine (Alcohol)': 'Wein (Alkohol)',
    'YouTube/Vimeo/Direct Link': 'Video-Link (YouTube, Vimeo oder MP4)',
    'Your name': 'Ihr Name',
    '2. Table Number': '2. Tischnummer',
    'for Morocco or': 'für Marokko, oder',
    'in': 'in',
    'to': 'bis',
    'e.g. 12 or Patio-1': 'z. B. 12 oder Terrasse-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'z. B. Hausgemachte Fettuccine, schwarzer Wintertrüffel, 36 Monate gereifter Parmesan...',
    'e.g. Head Sommelier & Wine Lead': 'z. B. Chef-Sommelier & Weinexperte',
    'e.g. London': 'z. B. Berlin',
    'e.g. SOMMELIER, HOST, BARISTA': 'z. B. SOMMELIER, HOST, BARISTA',
    'e.g. Skyline Lounge': 'z. B. Skyline Lounge',
    'e.g. TRUFFLE OIL': 'z. B. TRÜFFELÖL',
    'e.g. The Grand Cafe': 'z. B. Das Grand Café',
    'e.g. Window preference, high chair': 'z. B. Fensterplatz bevorzugt, Kinderstuhl',
    'Copied to clipboard!': 'In die Zwischenablage kopiert!',
    'Copy failed.': 'Kopieren fehlgeschlagen.',
    'Link copied!': 'Link kopiert!',
    'Logo uploaded': 'Logo hochgeladen',
    'Cover image uploaded': 'Titelbild hochgeladen',
    'Profile saved and synced successfully': 'Profil erfolgreich gespeichert und synchronisiert',
    'Category Created': 'Kategorie erstellt',
    'Category Updated': 'Kategorie aktualisiert',
    'Category Deleted': 'Kategorie gelöscht',
    'Item Created': 'Gericht erstellt',
    'Item Updated': 'Gericht aktualisiert',
    'Item Deleted': 'Gericht gelöscht',
    'Menu Published': 'Speisekarte veröffentlicht',
    'Delete Category?': 'Kategorie löschen?',
    'Delete Menu Item?': 'Dieses Gericht löschen?',
    'Delete Ingredient?': 'Diese Zutat löschen?',
    'Delete Service Period?': 'Service-Zeitraum löschen?',
    'Remove team member?': 'Teammitglied entfernen?',
    'Cancel invitation?': 'Einladung zurückziehen?',
    'Revoke API key?': 'API-Schlüssel widerrufen?',
    'Delete webhook endpoint?': 'Webhook-Endpunkt löschen?',
    'Clear all notifications?': 'Alle Benachrichtigungen löschen?',
    'Disconnect Stripe account?': 'Stripe-Konto trennen?',
    'Disconnect Google Business Profile?': 'Google Unternehmensprofil trennen?',
    'Campaigns published successfully!': 'Kampagnen erfolgreich veröffentlicht!',
    'Changes successfully synchronized with Google Business Profile!': 'Änderungen erfolgreich mit Google Unternehmensprofil synchronisiert!',
    'Google Business Profile successfully connected!': 'Google Unternehmensprofil erfolgreich verbunden!',
    'Google Business Profile disconnected.': 'Google Unternehmensprofil getrennt.',
    'Google Post draft saved successfully!': 'Entwurf für Google-Beitrag erfolgreich gespeichert!',
    'Post draft deleted.': 'Beitragsentwurf gelöscht.',
    'Review response published successfully!': 'Antwort auf Bewertung erfolgreich veröffentlicht!',
    'SEO configuration saved successfully!': 'SEO-Konfiguration erfolgreich gespeichert!',
    'SEO metadata translated across all languages!': 'SEO-Metadaten in alle Sprachen übersetzt!',
    'Venue profile & bio translated across all languages!': 'Betriebsprofil & Beschreibung in alle Sprachen übersetzt!',
    'Reservation configuration saved successfully': 'Reservierungseinstellungen erfolgreich gespeichert',
    'Stripe settings saved successfully.': 'Stripe-Einstellungen erfolgreich gespeichert.',
    'Print-Ready PDF downloaded successfully': 'Druckfertiges PDF erfolgreich heruntergeladen',
    'Vector QR PDF downloaded successfully': 'Vektor-QR-PDF erfolgreich heruntergeladen',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Druckerei-Paket (300 DPI) erfolgreich erstellt',
    'At least one language must be enabled.': 'Mindestens eine Sprache muss aktiviert sein.',
    'Enter a non-zero percentage before adjusting prices.': 'Geben Sie einen Prozentsatz ungleich null ein, bevor Sie Preise anpassen.',
    'Please enter a Venue Name before translating.': 'Bitte geben Sie einen Betriebsnamen ein, bevor Sie übersetzen.',
    'Please enter an item name before running dietary and allergen analysis.': 'Bitte geben Sie einen Gerichtsnamen ein, bevor Sie Allergene analysieren.',
    'Please write a reply before submitting.': 'Bitte verfassen Sie eine Antwort, bevor Sie absenden.',
    'Table 3 Called Waiter': 'Tisch 3 hat den Kellner gerufen',
    'Table Call Bell': 'Tisch-Ruftaste',
    'Waiter Calls': 'Service-Rufe',
    'Waiter Dispatch & Calls': 'Service-Koordination & Rufe',
    'Kitchen Display & Whiteboard': 'Küchenanzeige & Notizboard',
    'Customer Orders': 'Gästebestellungen',
    'Bakery': 'Bäckerei / Konditorei',
    'Bar / Pub': 'Bar / Kneipe',
    'Café / Coffee Shop': 'Café / Kaffeebar',
    'Fast Food': 'Schnellrestaurant',
    'Gourmet / Fine Dining': 'Gehobene Gastronomie / Fine Dining',
    'Hotel': 'Hotel',
    'General Local Business': 'Allgemeiner lokaler Betrieb',
    'Classic': 'Klassisch',
    'Clean': 'Klar & Schlicht',
    'Modern': 'Modern',
    'Elegant': 'Elegant',
    'Energetic': 'Dynamisch & Lebendig',
    'Glass': 'Glas-Effekt',
    'Playful & Casual': 'Locker & Verspielt',
    'Sophisticated & Gourmet': 'Raffiniert & Gourmet',
    'Warm & Welcoming': 'Herzlich & Einladend',
    'Executive': 'Exklusiv & Repräsentativ',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Upgrade erforderlich: Mehrsprachige Speisekarten sind im Pro-Tarif verfügbar.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Upgrade erforderlich: Tischbestellungen und Kellner-Rufe sind im Starter-Tarif nicht enthalten.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Vektor-SVG-Export ist eine Pro- & Max-Funktion. Bitte führen Sie ein Upgrade durch.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Druckfertiger PDF-Export ist eine Pro- & Max-Funktion. Bitte führen Sie ein Upgrade durch.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Der Pro-Tarif ist auf 3 aktive Kampagnen begrenzt. Pausieren Sie eine Kampagne oder wechseln Sie zu MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Der Starter-Tarif umfasst bis zu 20 Tische (Tische 1–20). Wechseln Sie zu PRO für unbegrenzte Tische.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Der Starter-Tarif unterstützt nummerierte Tische (1–20). Eigene Tischnamen (z. B. Terrasse, VIP) erfordern PRO.',
    'Add Menu Categories & Items': 'Kategorien & Gerichte hinzufügen',
    'Add Menu Items': 'Menü-Gerichte hinzufügen',
    'Organize your menu sections and food items.': 'Strukturieren Sie Ihre Speisekarte und Gerichte übersichtlich.',
    'Add and manage your food & beverage offerings.': 'Fügen Sie Speisen und Getränke hinzu und verwalten Sie diese.',
    'Customize Venue Branding': 'Betriebsdesign & Branding anpassen',
    'Set your brand colors, logo, and presentation.': 'Legen Sie Ihre Farben, Ihr Logo und das Erscheinungsbild fest.',
    'Get Your QR Code & Go Live': 'QR-Code erstellen & live gehen',
    'Download table QR codes and preview your live menu.': 'Laden Sie Tisch-QR-Codes herunter und testen Sie die Live-Vorschau.',
    'Account & Venue Created Successfully!': 'Konto & Betrieb erfolgreich erstellt!',
    '⚡ Off-Peak Hours': '⚡ Randzeiten & Nebenzeiten',
    '🍽️ Lunch Crowd': '🍽️ Mittagsgeschäft',
    '🚀 Weekend Rush': '🚀 Wochenend-Ansturm',
    '🌟 Seasonal Special': '🌟 Saisonale Highlights',
    '📣 Engagement Buzz': '📣 Kundenbindung & Reichweite',
    'Festive & Energetic': 'Feierlich & Schwungvoll',
    'Urgent & High-Conversion': 'Zeitlich begrenzt & Zugkräftig',
    'Generated 3 tailored campaign strategies': '3 maßgeschneiderte Kampagnenstrategien erstellt',
    'Could not generate campaign ideas. Please try again.': 'Kampagnenideen konnten nicht erstellt werden. Bitte erneut versuchen.',
    'Venue Themes': 'Design-Themes für den Betrieb',
    'Choose the character of your public menu': 'Wählen Sie den visuellen Charakter Ihrer Speisekarte',
    'Glassmorphism & Depth': 'Glasoptik & Raumtiefe',
    'Independent XML Sitemap': 'Unabhängige XML-Sitemap',
    'Structured Schema & Sync': 'Strukturierte Daten & Synchronisierung',
    'Improve how your venue appears on Google and in local search results.': 'Verbessern Sie die Auffindbarkeit Ihres Betriebs bei Google und in lokalen Suchen.',
    'Terms of Service': 'Nutzungsbedingungen',
    'Confirm your email': 'Bestätigen Sie Ihre E-Mail-Adresse',
    'Check your spam / junk folder': 'Überprüfen Sie Ihren Spam- bzw. Junk-Ordner',
    'Commercial pre-press print production package': 'Druckerei-fertiges Pre-Press-Paket',
    'Over-bleed safety margin': 'Sicherheitsrand mit Beschnittzugabe',
    'Crop Marks': 'Schnittmarken',
    'Guillotine cutting guides': 'Schneidhilfen für Papierschneider',
    'Global Banners': 'Allgemeine Kopfzeilen-Banner',
    'Featured Items': 'Hervorgehobene Spezialitäten',
    'AI Assistant': 'KI-Assistent',
    'Custom Role Schemas': 'Benutzerdefinierte Rollenschemata',
    'Notification Preferences': 'Benachrichtigungseinstellungen',
    'Preferences saved automatically': 'Einstellungen automatisch gespeichert',
    'Menu Extractor (PDF & Images)': 'Menü-Erkennung (PDF & Bilder)',
    'Multi-Language Translation': 'Mehrsprachige Übersetzung',
    'Start Menu Translation': 'Menü-Übersetzung starten',
    'Menu Items Hub': 'Verwaltung der Speisekarte',
    'Open Menu Items': 'Menü-Gerichte öffnen',
    'Enterprise Traceability': 'Vollständige Aktivitätsrückverfolgung',
    'Full History': 'Gesamter Verlauf',
    'Billing & Plans': 'Abrechnung & Tarife',
    'Subscription Governance': 'Abonnement-Verwaltung',
    'Platform Usage': 'Plattform-Nutzung',
    'Menu Page Views': 'Seitenaufrufe der Speisekarte',
    'Enterprise Identity': 'Unternehmens-Identität',
    'Access Governance': 'Zugriffskontrolle & Richtlinien',
    'Required Fields *': 'Pflichtfelder *',
    'Enabled Languages': 'Aktivierte Sprachen',
    'Venue Info & Bio Localizations': 'Lokalisierungen von Betriebsinfos & Beschreibung',
    'Public Address': 'Öffentliche Adresse',
    'Public URL Slug': 'Öffentlicher URL-Slug',
    'Contact Phone': 'Kontakttelefon',
    'Business Address': 'Geschäftsadresse',
    'No Categories Yet': 'Noch keine Kategorien angelegt',
    'No Custom Roles Configured': 'Keine benutzerdefinierten Rollen konfiguriert',
    'No Google reviews retrieved yet': 'Noch keine Google-Bewertungen abgerufen',
    'No Guests Found': 'Keine Gäste gefunden',
    'No Menu Items Yet': 'Noch keine Gerichte in der Speisekarte',
    'No Promotions Configured': 'Keine Aktionen eingerichtet',
    'No Service Periods Yet': 'Noch keine Servicezeiten definiert',
    'No Support Tickets': 'Keine Support-Tickets vorhanden',
    'No Tables on Floor Plan': 'Keine Tische im Raumplan vorhanden',
    'No Team Members Found': 'Keine Teammitglieder gefunden',
    'No Upcoming Events': 'Keine anstehenden Veranstaltungen',
    'No Walk-Ins Recorded': 'Keine Laufkundschaft erfasst',
    'No items are available right now': 'Derzeit sind keine Gerichte verfügbar',
    'No notifications found': 'Keine Benachrichtigungen vorhanden',
    'No reservations matching your filters': 'Keine Reservierungen entsprechen den Filtern',
    'No tables currently occupied': 'Derzeit sind keine Tische belegt',
    'No venue selected': 'Kein Betrieb ausgewählt',
    'Waitlist': 'Warteliste',
    'Waitlist is Empty': 'Warteliste ist leer',
    'Waves (Gluten)': 'Ähren (Gluten)',
    'We sent a temporary verification code to': 'Wir haben einen temporären Verifizierungscode gesendet an',
    'Website URL': 'Website-Adresse',
    'What the customer receives': 'Was der Gast erhält',
    'White-label control is available on eligible plans.': 'White-Label-Branding ist in ausgewählten Tarifen verfügbar.',
    'Why this works:': 'Warum diese Strategie funktioniert:',
    'WiFi Broadcast': 'Gäste-WLAN Übertragung',
    'WiFi Password': 'WLAN-Passwort',
    'Window seat, dietary request, birthday celebration...': 'Fensterplatz, Diätwünsche, Geburtstagsfeier...',
    'Your Published Response': 'Ihre veröffentlichte Antwort',
    'Your Venues': 'Ihre Betriebe',
    '🎉 Ready to publish!': '🎉 Bereit zur Veröffentlichung!',
    'e.g. Grill, Salad, Pastry': 'z. B. Grill, Salate, Patisserie',
    'e.g. Guest called, emergency': 'z. B. Gast hat angerufen, Notfall',
    'e.g. Italian, Moroccan, Burgers': 'z. B. Italienisch, Marokkanisch, Burger',
    'e.g. Mains, Pasta, Starters': 'z. B. Hauptgerichte, Pasta, Vorspeisen',
    'e.g. Signature Truffle Pasta': 'z. B. Hausgemachte Trüffelpasta',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'z. B. Nachmittags-Kaffee-Aktion, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'z. B. Frühstück, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'z. B. Erleben Sie Live-Jazz jeden Freitagabend ab 20:00 Uhr!',
  },
  it: {
    '123 Main St, City': 'Via Roma 123, Città',
    'Balance': 'Saldo punti',
    'Lifetime': 'Totale accumulato',
    'Source': 'Origine',
    'Menu': 'Menu',
    'Advanced': 'Avanzate',
    'Core Suite': 'Funzionalità di base',
    'Privacy': 'Privacy',
    'Support': 'Assistenza',
    'Save Layout': 'Salva disposizione sala',
    'Shape:': 'Forma:',
    'SEARCH LIBRARY...': 'CERCA NELLA LIBRERIA...',
    'Search library...': 'Cerca nella libreria...',
    'None': 'Nessuno',
    'Type': 'Tipo',
    'Target': 'Obiettivo',
    'Value / Target': 'Valore / Obiettivo',
    'London (GMT/BST)': 'Londra (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tokyo (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Colore iniziale',
    'Depth Shade': 'Ombreggiatura e profondità',
    'Matrix Geometry': 'Geometria dei punti QR',
    'Eye Architecture': 'Angoli di orientamento QR',
    'Brand Centerpiece': 'Logo centrale nel QR',
    'Scannability Shield': 'Protezione della leggibilità',
    'Environment Blur': 'Sfocatura ambientale',
    'Color Target:': 'Profilo colore:',
    'DPI:': 'DPI (Risoluzione):',
    '3mm Bleed Box': 'Abbondanza di taglio da 3 mm',
    '300 DPI': '300 DPI (Alta definizione)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Protocollo SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'Entity ID del provider di identità',
    'SSO URL': 'URL di accesso SSO',
    'X.509 Certificate': 'Certificato di sicurezza X.509',
    'ACS URL': 'URL del servizio ACS',
    'Entity ID': 'ID entità',
    'Navigate': 'Naviga',
    'Select': 'Seleziona',
    'OmniSearch v1.0': 'Ricerca universale OmniSearch',
    'Back to order review': 'Torna al riepilogo ordine',
    'Return to Intelligence Studio': 'Torna allo studio analitico',
    'View Full Notification Center': 'Visualizza centro notifiche completo',
    'Read all': 'Segna tutti come letti',
    'Cancel department': 'Annulla reparto',
    'Save department': 'Salva reparto',
    'Venue Segmentation': 'Segmentazione degli spazi',
    'Collaborate on your menu together.': 'Collabora al menu insieme al tuo team.',
    'Timeline': 'Cronologia',
    'Calendar': 'Calendario prenotazioni',
    'Book': 'Prenota tavolo',
    'Duration': 'Durata della permanenza',
    'Time & Duration': 'Orario e durata',
    'Seating Preference': 'Preferenza per il tavolo',
    'Section': 'Sezione della sala',
    'Min Capacity': 'Capienza minima',
    'Max Capacity': 'Capienza massima',
    'Phone (Optional)': 'Telefono (Opzionale)',
    'Name or Walk-in': 'Nome o cliente diretto',
    'Next day': 'Giorno successivo',
    'Next month': 'Mese successivo',
    'Live Connection': 'Connessione dal vivo',
    'Live Inventory': 'Disponibilità in tempo reale',
    'Main Venue (Full Menu)': 'Locale principale (Menu completo)',
    'Preview Menu': 'Anteprima del menu',
    'Manual Setup Guide': 'Guida di configurazione manuale',
    'Connected & Active': 'Connesso e attivo',
    'CTA Action': 'Pulsante di azione (CTA)',
    'Phone Contact': 'Telefono di contatto',
    'Menu URL': 'Indirizzo web del menu',
    'Google Location': 'Posizione su Google Maps',
    'Field': 'Campo',
    'Linked': 'Collegato',
    'Synchronizing Hub...': 'Sincronizzazione in corso...',
    'Data Integrity': 'Integrità dei dati',
    'Station Governance Suite': 'Gestione delle postazioni di lavoro',
    'Open audit log': 'Apri registro di controllo',
    'Signal / Time': 'Evento / Ora',
    'Identity': 'Utente / Identità',
    'Invitation Invalid': 'Invito non valido o scaduto',
    'Invited To': 'Invitato a unirsi a',
    'Team Limit Reached': 'Limite membri del team raggiunto',
    'Venue Limit Reached': 'Limite di locali raggiunto',
    'Venue Setup Required': 'Configurazione iniziale richiesta',
    'Create Venue': 'Crea locale',
    'TableGreet Onboarding': 'Benvenuto in TableGreet',
    'No payment required': 'Nessun pagamento richiesto',
    'Selected Plan': 'Piano selezionato',
    'Payment Method': 'Metodo di pagamento',
    'Invoice ID': 'Numero fattura',
    'Verified': 'Verificato',
    'Accent color': 'Colore di accento',
    'Set the voice and overall light level.': 'Imposta lo stile e la luminosità complessiva.',
    'Layout architecture': 'Architettura del layout',
    'Navigation & motion': 'Navigazione e animazioni',
    'Loading preview engine': 'Caricamento motore di anteprima',
    'Real Live Preview': 'Anteprima dal vivo in tempo reale',
    'Included with Pro & Max': 'Incluso con Pro e Max',
    'Daypart Scheduling': 'Programmazione delle fasce orarie',
    'Operational Daypart Architecture': 'Architettura delle fasce operative',
    'Service Protocol': 'Protocollo di servizio',
    'Configuration Engine': 'Motore di configurazione',
    'Linked Department': 'Reparto collegato',
    'Team Hub': 'Gestione del team',
    'Team Size': 'Dimensioni del team',
    'Joined': 'Iscritto il',
    'Cancel Invitation': 'Annulla invito',
    'Initialize New Organization': 'Inizializza nuova organizzazione',
    'Operational': 'Operativo',
    'Pro Feature': 'Funzionalità Pro',
    'Current State': 'Stato attuale',
    'Open on the big TV': 'Apri sul grande schermo TV',
    'Menu Board': 'Tabellone digitale del menu',
    'Reward': 'Premio',
    'Reward name': 'Nome del premio',
    'Cancel & refund': 'Annulla e rimborsa',
    'Code': 'Codice',
    'Find': 'Trova',
    'Complete': 'Completato',
    'Seated': 'Al tavolo',
    'No-Show': 'Mancata presentazione',
    'Walk-in': 'Senza prenotazione',
    'Party': 'Numero persone',
    'Turnover': 'Rotazione tavoli',
    'We couldn\'t load the menu': 'Impossibile caricare il menu',
    'Venue Not Found': 'Locale non trovato',
    'Live Availability': 'Disponibilità in tempo reale',
    'Multi-Language Suite': 'Gestione multilingue',
    'Key name': 'Nome della chiave',
    'Revoke API key': 'Revoca chiave API',
    'Delete webhook': 'Elimina webhook',
    'Identity Provider': 'Provider d\'identità (IdP)',
    'Provider': 'Fornitore',
    'Mandatory MFA': 'Autenticazione a due fattori obbligatoria (MFA)',
    'Account Identity': 'Identità dell’account',
    'Login Email': 'Email di accesso',
    'New Login Email': 'Nuova email di accesso',
    'Phone Login': 'Accesso con telefono',
    'SMS Verification Code': 'Codice di verifica SMS',
    'Integration Method': 'Metodo di integrazione',
    'Stripe Connect': 'Pagamenti Stripe Connect',
    'Stripe Secret Key': 'Chiave segreta Stripe',
    'Stripe Webhook Secret': 'Chiave webhook Stripe',
    'Client-Owned': 'Di proprietà del cliente',
    'Verify connection': 'Verifica connessione',
    'Setup Health': 'Stato configurazione',
    'Privacy Protected': 'Dati protetti da privacy',
    'Privacy Policy': 'Informativa sulla privacy',
    'Continue': 'Continua',
    'Sign In': 'Accedi',
    'Didn\'t get it?': 'Non l’hai ricevuto?',
    'Logged In Account': 'Account collegato',
    'Annual': 'Annuale',
    'Unread': 'Non letto',
    'Starred': 'Speciali',
    'High / Urgent': 'Alto / Urgente',
    'Mark Read': 'Segna come letto',
    'Delete notification': 'Elimina notifica',
    'Urgent': 'Urgente',
    'High': 'Alto',
    'Normal': 'Normale',
    'Low': 'Basso',
    'Select a Ticket': 'Seleziona un ticket',
    'Urgent (Immediate attention)': 'Urgente (Attenzione immediata)',
    'Type your reply here...': 'Scrivi qui la tua risposta...',
    'Print Card (PDF)': 'Stampa segnatavolo (PDF)',
    'Advanced Printing Engine': 'Motore di stampa ad alta fedeltà',
    'Multi-Venue Governance': 'Gestione multi-locale',
    'Network Revenue': 'Fatturato complessivo rete',
    'Live Network Total': 'Totale rete in tempo reale',
    'Audit Log Hub': 'Registro di audit e conformità',
    'Export Log': 'Esporta registro',
    'CUSTOM ROLE SCHEMAS': 'SCHEMI DI RUOLO PERSONALIZZATI',
    'Unique internal role identifier.': 'Identificatore interno univoco del ruolo.',
    'Human-friendly role label.': 'Nome chiaro del ruolo per il team.',
    'Marketing Hub': 'Centro marketing',
    'Revenue Generation Suite': 'Strumenti di crescita dei ricavi',
    'Active Campaign Limit Reached (3/3)': 'Limite campagne attive raggiunto (3/3)',
    'Banner Content': 'Contenuto del banner',
    'Style Profile': 'Stile visivo',
    'Action URL (Optional)': 'Link di azione (Opzionale)',
    'Promotion Label': 'Etichetta della promozione',
    'Target Selection': 'Selezione destinatari',
    'Edit Promotion': 'Modifica promozione',
    'Delete Promotion': 'Elimina promozione',
    'Loyalty Engine': 'Programma fedeltà',
    'Reward catalog': 'Catalogo premi',
    'Add reward': 'Aggiungi premio',
    'Loyalty ID': 'ID fedeltà',
    'No point activity yet': 'Nessuna attività punti al momento',
    'Scan or enter QR value': 'Scansiona o inserisci il codice QR',
    'TableGreet Admin': 'Gestione TableGreet',
    'Capacity Pacing': 'Gestione del flusso coperti',
    'Cancellation Policy': 'Termini di cancellazione',
    'Booking Experience': 'Esperienza di prenotazione',
    'Floor Occupancy': 'Occupazione della sala',
    'Currently Seated': 'Attualmente al tavolo',
    'Seat Party': 'Accompagna al tavolo',
    'Seat Table': 'Assegna tavolo',
    'Select Table': 'Seleziona tavolo',
    'Table / Capacity': 'Tavolo / Posti',
    'Table Name/Number': 'Nome o numero del tavolo',
    'Mark Arrived': 'Segna come arrivato',
    'Mark No-Show': 'Segna mancata presentazione',
    'Complete Dining': 'Concludi pasto',
    'Standard Dining (Default)': 'Servizio standard al tavolo (Predefinito)',
    'Walk-In Dining Log': 'Registro clienti senza prenotazione',
    'Notification Center': 'Centro notifiche',
    'Single Sign-On': 'Accesso unico (SSO)',
    'Welcome Aboard!': 'Benvenuto in TableGreet!',
    'Select your TableGreet plan': 'Scegli il tuo piano TableGreet',
    'Free Plan Activation': 'Attivazione piano gratuito',
    'Venue Created!': 'Locale creato con successo!',
    'Upgrade for Unlimited': 'Passa a illimitato',
    'Auto-Tag Menu': 'Tag automatici del menu',
    'Start Auto-Tagging': 'Avvia tag automatici',
    'Launch Auto-Tagger': 'Apri strumento tag automatici',
    'Launch PDF Importer': 'Importa da menu PDF',
    'Analyzing Menu...': 'Analisi del menu in corso...',
    'Analyzing Recipe...': 'Analisi della ricetta...',
    'Manual Availability': 'Disponibilità manuale',
    'Technical Breakdown': 'Dettagli tecnici',
    'Dietary Taxonomy': 'Diete e allergeni',
    'Dietary & Allergen AI': 'IA per diete e allergeni',
    'Inherit from Category': 'Eredita dalla categoria',
    'Ingredient Library': 'Libreria degli ingredienti',
    'Ingredient Name': 'Nome dell\'ingrediente',
    'Menu Organization': 'Organizzazione del menu',
    'Inventory Taxonomy Hub': 'Centro categorie e inventario',
    'Category Intelligence': 'Analisi intelligente categoria',
    'Service Period': 'Periodo di servizio',
    'Manual Configuration / All Day': 'Configurazione manuale / Tutto il giorno',
    'Operational Department': 'Reparto operativo',
    'Global / No Department': 'Generale / Nessun reparto',
    'Cancel category': 'Annulla categoria',
    'New category name...': 'Nome nuova categoria...',
    'Save category': 'Salva categoria',
    'QR Matrix Only': 'Solo codice QR',
    'Vector & PDF': 'Vettoriale e PDF',
    'Vector QR (SVG)': 'Codice QR vettoriale (SVG)',
    'Vector QR (PDF)': 'Codice QR per stampa (PDF)',
    'Save QR Style': 'Salva stile QR',
    'Share Link': 'Condividi link',
    'High Fidelity 300DPI': 'Alta risoluzione 300 DPI per stampa',
    'Generating 300 DPI PDF...': 'Generazione PDF a 300 DPI...',
    'Give your venue a point of view.': 'Dona al tuo locale una personalità visiva unica.',
    'Signage Menu Not Found': 'Menu per display digitale non trovato',
    'Scan Menu': 'Scansiona il menu',
    'Open on your phone': 'Apri sullo smartphone',
    'Full Screen': 'Schermo intero',
    'Lock TV board': 'Blocca display TV',
    'Big-Screen Menu': 'Menu per schermi grandi',
    'TV Signage': 'Segnaletica digitale TV',
    'General Information': 'Informazioni generali',
    'Venue Name *': 'Nome del locale *',
    'Public Slug *': 'Indirizzo pubblico (Slug) *',
    'Venue Logo': 'Logo del locale',
    'Hero Cover Image': 'Immagine di copertina principale',
    'Venue Timezone': 'Fuso orario del locale',
    'Primary Brand Color': 'Colore principale del brand',
    'TableGreet footer credit': 'Dicitura TableGreet nel piè di pagina',
    'White-Label Branding': 'Personalizzazione White-Label',
    'Client DNS Setup': 'Configurazione DNS del dominio',
    'Copy CNAME target': 'Copia destinazione CNAME',
    'Copy TableGreet URL': 'Copia l\'URL TableGreet',
    'Included TableGreet URL': 'URL TableGreet incluso',
    'Open TableGreet URL': 'Apri link TableGreet',
    'Public menu': 'Menu pubblico clienti',
    'Public venue delivery': 'Consegna a domicilio dal menu online',
    'Zone name': 'Nome della zona',
    'Coverage note': 'Note sulla copertura',
    'Default ETA min': 'Tempo stimato min. (minuti)',
    'Default ETA max': 'Tempo stimato max. (minuti)',
    'Packaging fee': 'Costo di imballaggio',
    'Courier handled by': 'Consegna a cura di',
    'Third-party courier': 'Corriere esterno partner',
    'Unified Experience': 'Esperienza unificata',
    'Save WiFi to Venue': 'Salva Wi-Fi per il locale',
    'Network Name (SSID)': 'Nome rete Wi-Fi (SSID)',
    'Audio Alert Chime': 'Suoneria di notifica',
    'Time Format': 'Formato orario',
    'Auto / Default': 'Automatico / Predefinito',
    'Syncing SEO & Google Data...': 'Sincronizzazione dati SEO e Google...',
    'Google Connection': 'Integrazione con Google',
    'Search Engine Metadata': 'Metadati per i motori di ricerca',
    'Social card preview': 'Anteprima social card',
    'Allow Indexing': 'Consenti l\'indicizzazione',
    'Canonical URL Override': 'Personalizzazione URL canonico',
    'Price Range': 'Fascia di prezzo',
    'Latitude': 'Latitudine',
    'Longitude': 'Longitudine',
    'Google Place ID / Location ID': 'ID luogo Google (Place ID)',
    'Google Search Preview': 'Anteprima ricerca Google',
    'Linked Google Location': 'Sede Google collegata',
    'SEO Documentation': 'Guida all\'ottimizzazione SEO',
    ', and': ', e',
    'and': 'e',
    'Delete Department?': 'Eliminare il reparto?',
    'Delete Department': 'Elimina reparto',
    'Live URL': 'URL live',
    'Copy full live URL': 'Copia l\'URL live completo',
    'Open live department menu': 'Apri menu del reparto dal vivo',
    'Loading departments...': 'Caricamento reparti...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'La segmentazione in più reparti è una funzionalità premium disponibile da PRO in su.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Suddividi il tuo menu per sale, tavoli, bar o postazioni fisiche.',
    'Targeted Ordering': 'Ordinazioni mirate',
    'Route guests and orders to specific preparation areas.': 'Indirizza ospiti e comande ad aree di preparazione dedicate.',
    'Operational Departments': 'Reparti operativi',
    'OPERATIONAL DEPARTMENTS': 'REPARTI OPERATIVI',
    'Venue Segmentation Hub': 'Centro segmentazione del locale',
    'VENUE SEGMENTATION HUB': 'CENTRO SEGMENTAZIONE DEL LOCALE',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Crea reparti per organizzare il tuo menu. Ad esempio, un locale grande può avere un',
    'Restaurant': 'Ristorante',
    'Poolside': 'Bordo piscina',
    'department, each with its own categories.': ', ciascuno con le proprie categorie.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'I reparti ti consentono di suddivider il tuo locale in sezioni come Ristorante, Bar o Bordo piscina, ciascuna con le proprie categorie.',
    'Department name': 'Nome del reparto',
    'New department name...': 'Nome nuovo reparto...',
    'No Linked Daypart (Always Available)': 'Nessuna fascia oraria collegata (sempre disponibile)',
    'Linked Daypart': 'Fascia oraria associata',
    'Localizations': 'Traduzioni',
    'LOCALIZATIONS': 'TRADUZIONI',
    'AI Auto-Translate': 'Traduzione automatica con IA',
    'AI AUTO-TRANSLATE': 'TRADUZIONE AUTOMATICA CON IA',
    'Translated name': 'Nome tradotto',
    'No Departments Yet': 'Ancora nessun reparto',
    'Add First Department': 'Aggiungi il primo reparto',
    'Avg Order Value': 'Valore medio ordine',
    'AVG ORDER VALUE': 'VALORE MEDIO ORDINE',
    'item taps': 'tocchi sui piatti',
    'ITEM TAPS': 'TOCCHI SUI PIATTI',
    'Avg Rating': 'Valutazione media',
    'AVG RATING': 'VALUTAZIONE MEDIA',
    'Revenue (Last 7 Days)': 'Ricavi (ultimi 7 giorni)',
    'REVENUE (LAST 7 DAYS)': 'RICAVI (ULTIMI 7 GIORNI)',
    'Daily breakdown': 'Ripartizione giornaliera',
    'DAILY BREAKDOWN': 'RIPARTIZIONE GIORNALIERA',
    'Menu Views (Last 7 Days)': 'Visualizzazioni menu (ultimi 7 giorni)',
    'MENU VIEWS (LAST 7 DAYS)': 'VISUALIZZAZIONI MENU (ULTIMI 7 GIORNI)',
    'Guest traffic': 'Afflusso degli ospiti',
    'GUEST TRAFFIC': 'AFFLUSSO DEGLI OSPITI',
    'Most Viewed Items': 'Piatti più visualizzati',
    'MOST VIEWED ITEMS': 'PIATTI PIÙ VISUALIZZATI',
    'Top Rated Items': 'Piatti più votati',
    'TOP RATED ITEMS': 'PIATTI PIÙ VOTATI',
    'Waiter Requests': 'Chiamate cameriere',
    'WAITER REQUESTS': 'CHIAMATE CAMERIERE',
    'Total': 'Totale',
    'TOTAL': 'TOTALE',
    'Order Status (30 days)': 'Stato ordini (30 giorni)',
    'ORDER STATUS (30 DAYS)': 'STATO ORDINI (30 GIORNI)',
    'Recent Guest Feedback': 'Recensioni recenti dei clienti',
    'RECENT GUEST FEEDBACK': 'RECENSIONI RECENTI DEI CLIENTI',
    'Conversion Rate': 'Tasso di conversione',
    'CONVERSION RATE': 'TASSO DI CONVERSIONE',
    'of menu visitors placed an order': 'dei visitatori del menu ha effettuato un ordine',
    'Refreshed': 'Aggiornato',
    'REFRESHED': 'AGGIORNATO',
    'Last 30 days': 'Ultimi 30 giorni',
    'LAST 30 DAYS': 'ULTIMI 30 GIORNI',
    'cash': 'contanti',
    'Cash': 'Contanti',
    'CASH': 'CONTANTI',
    'bill': 'il conto',
    'Bill': 'Il conto',
    'BILL': 'IL CONTO',
    'water': 'acqua',
    'Water': 'Acqua',
    'WATER': 'ACQUA',
    'service': 'servizio',
    'Service': 'Servizio',
    'SERVICE': 'SERVIZIO',
    'No interaction data yet': 'Nessun dato di interazione disponibile',
    'No ratings collected yet': 'Nessuna valutazione registrata',
    'No requests in period': 'Nessuna richiesta nel periodo',
    'No feedback collected yet': 'Nessun commento ricevuto finora',
    'No menu views recorded yet': 'Nessuna visualizzazione menu registrata',
    'Dish Rating Breakdown': 'Dettaglio valutazioni per piatto',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Le classifiche dei piatti e le metriche di soddisfazione dei clienti sono esclusive dei piani MAX ed Enterprise.',
    'Customer Sentiment & Reviews': 'Opinioni e sentiment dei clienti',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Le recensioni dirette degli ospiti e l\'analisi del sentiment richiedono un abbonamento MAX o Enterprise.',
    'Reviews AI': 'IA analisi recensioni',
    'Guest Feedback': 'Feedback degli ospiti',
    'Upgrade to unlock rating analytics →': 'Aggiorna per sbloccare le statistiche di valutazione →',
    'Upgrade to Max': 'Passa al piano Max',
    'UPGRADE TO MAX': 'PASSA AL PIANO MAX',
    'MAX PLAN': 'PIANO MAX',
    'No Data Yet': 'Ancora nessun dato',
    'Share your QR code to start collecting real venue insights.': 'Condividi il tuo codice QR per iniziare a raccogliere informazioni reali sull\'attività.',
    'Revenue Analytics': 'Analisi dei ricavi',
    'REVENUE ANALYTICS': 'ANALISI DEI RICAVI',
    'Track your earnings, orders, and average ticket values over time.': 'Monitora guadagni, ordini e valore medio dello scontrino nel tempo.',
    'Operational Velocity': 'Efficienza operativa',
    'OPERATIONAL VELOCITY': 'EFFICIENZA OPERATIVA',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Monitora l\'afflusso dei clienti, i piatti più richiesti e il carico dei camerieri.',
    'Upgrade to Pro': 'Passa a Pro',
    'UPGRADE TO PRO': 'PASSA AL PIANO PRO',
    'Loading Insights…': 'Caricamento statistiche…',
    'Loading Insights...': 'Caricamento statistiche...',
    'Telemetry Unavailable': 'Dati di attività non disponibili',
    'TELEMETRY UNAVAILABLE': 'DATI DI ATTIVITÀ NON DISPONIBILI',
    'No Data Captured': 'Nessun dato registrato',
    'NO DATA CAPTURED': 'NESSUN DATO REGISTRATO',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Condividi il tuo codice QR per iniziare a raccogliere statistiche e ordini in tempo reale.',
    'INTELLIGENCE STUDIO': 'CENTRO STATISTICHE',
    'Insights Engine Locked': 'Modulo analisi avanzate bloccato',
    'INSIGHTS ENGINE LOCKED': 'MODULO ANALISI AVANZATE BLOCCATO',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Le analisi avanzate delle tendenze, i comandi di gestione in blocco e le esportazioni sono riservati agli utenti',
    'Unlock Pro Features': 'Sblocca le funzioni Pro',
    'UNLOCK PRO FEATURES': 'SBLOCCA LE FUNZIONI PRO',
    'MENU VIEWS': 'VISUALIZZAZIONI MENU',
    'ITEM INTERACTIONS': 'INTERAZIONI COI PIATTI',
    'GUEST RATING': 'VALUTAZIONE DEGLI OSPITI',
    'DELIVERY REVENUE': 'RICAVI DA CONSEGNE',
    'AVERAGE DELIVERY ORDER': 'ORDINE MEDIO DI CONSEGNA',
    'AVERAGE DELIVERY FEE': 'COSTO MEDIO DI CONSEGNA',
    'INCLUDING FEES': 'COMMISSIONI INCLUSE',
    'ZONE AVERAGE': 'MEDIA DELLA ZONA',
    'TRAFFIC PROJECTIONS': 'PROIEZIONI DEL FLUSSO',
    'TELEMETRY SIGNAL TRACKING': 'MONITORAGGIO SEGNALI ATTIVITÀ',
    'VEGETARIAN': 'VEGETARIANO',
    'VEGAN': 'VEGANO',
    'SPICY': 'PICCANTE',
    'GLUTEN-FREE': 'SENZA GLUTINE',
    'Reset: All In-Stock': 'Reimposta: Tutto disponibile',
    'Emergency Stop (All Out)': 'Arresto d\'emergenza: Tutto esaurito',
    'No compliance issues': 'Nessun problema rilevato',
    'NO COMPLIANCE ISSUES': 'NESSUN PROBLEMA RILEVATO',
    'detected in current menu': 'nel menu attuale',
    'DETECTED IN CURRENT MENU': 'NEL MENU ATTUALE',
    'Telemetry History (24h)': 'Cronologia attività (24h)',
    'TELEMETRY HISTORY (24H)': 'CRONOLOGIA ATTIVITÀ (24H)',
    'Telemetry History (30d)': 'Cronologia attività (30g)',
    'TELEMETRY HISTORY (30D)': 'CRONOLOGIA ATTIVITÀ (30G)',
    'Save': 'Salva',
    'Saved': 'Salvato',
    'Saving...': 'Salvataggio...',
    'Save Changes': 'Salva modifiche',
    'Save Settings': 'Salva impostazioni',
    'Cancel': 'Annulla',
    'Close': 'Chiudi',
    'Create': 'Crea',
    'Add': 'Aggiungi',
    'Edit': 'Modifica',
    'Delete': 'Elimina',
    'Remove': 'Rimuovi',
    'Update': 'Aggiorna',
    'Refresh': 'Aggiorna',
    'Search': 'Cerca',
    'Search...': 'Cerca...',
    'Search items...': 'Cerca piatti...',
    'Search categories...': 'Cerca categorie...',
    'Filter': 'Filtra',
    'All': 'Tutto',
    'Export': 'Esporta',
    'Import': 'Importa',
    'Download': 'Scarica',
    'Upload': 'Carica',
    'Copy': 'Copia',
    'Copied': 'Copiato',
    'Copied!': 'Copiato!',
    'Preview': 'Anteprima',
    'Open': 'Aperto',
    'View': 'Visualizza',
    'Manage': 'Gestisci',
    'Configure': 'Configura',
    'Enable': 'Attiva',
    'Disable': 'Disattiva',
    'Enabled': 'Attivato',
    'Disabled': 'Disattivato',
    'Active': 'Attivo',
    'Inactive': 'Inattivo',
    'Available': 'Disponibile',
    'Unavailable': 'Non disponibile',
    'Visible': 'Visibile',
    'Hidden': 'Nascosto',
    'Required': 'Obbligatorio',
    'Optional': 'Opzionale',
    'Confirm': 'Conferma',
    'Back': 'Indietro',
    'Next': 'Avanti',
    'Done': 'Fatto',
    'Upgrade': 'Passa a Pro',
    'Sign Out': 'Disconnetti',
    'Exit to Main Admin': 'Torna all’amministrazione',
    'Change Language': 'Cambia lingua',
    'Add Category': 'Aggiungi categoria',
    'Add Menu Item': 'Aggiungi piatto',
    'Add Item': 'Aggiungi articolo',
    'Add Ingredient': 'Aggiungi ingrediente',
    'Add Department': 'Aggiungi reparto',
    'Delete Item': 'Elimina articolo',
    'Delete Category': 'Elimina categoria',
    'Upload Image': 'Carica immagine',
    'Upload Photo': 'Carica foto',
    'Select Category': 'Seleziona categoria',
    'Select Department': 'Seleziona reparto',
    'Add translation...': 'Aggiungi traduzione...',
    'Clear': 'Cancella',
    'Reset': 'Ripristina',
    'Apply': 'Applica',
    'Submit': 'Invia',
    'Core & Operations': 'Operazioni e sala',
    'Menu & Experience': 'Menu ed esperienza',
    'Enterprise & Security': 'Gestione e staff',
    'System & Control': 'Impostazioni e aiuto',
    'Intelligence Studio': 'Centro statistiche',
    'Venue Insights': 'Vendite e report',
    'Reservations': 'Prenotazioni',
    'Floor Plan': 'Mappa dei tavoli',
    'Live Waitlist': 'Lista d’attesa',
    'Walk-Ins': 'Clienti di passaggio',
    'Walk-ins': 'Clienti di passaggio',
    'Departments': 'Reparti',
    'Service Hours': 'Orari di servizio',
    'Categories': 'Categorie',
    'Menu Categories': 'Categorie menu',
    'Menu Items': 'Piatti del menu',
    'Items': 'Articoli',
    'Ingredients': 'Ingredienti',
    'Loyalty Program': 'Programma fedeltà',
    'Campaign Studio': 'Offerte speciali',
    'Signage': 'Schermi TV',
    'QR Code': 'Codici QR',
    'Kitchen Ops': 'Monitor cucina',
    'Theme Engine': 'Colori e stile',
    'Governance': 'Governance',
    'Audit Log': 'Registro attività',
    'API & Webhooks': 'Integrazioni',
    'SSO & Security': 'Sicurezza e accesso',
    'Team': 'Personale',
    'Billing': 'Abbonamento e fatture',
    'SEO & Google': 'Visibilità su Google',
    'Notifications': 'Notifiche',
    'Support Center': 'Centro assistenza',
    'Settings': 'Impostazioni locale',
    'Name': 'Nome',
    'Description': 'Descrizione',
    'Price': 'Prezzo',
    'Status': 'Stato',
    'Actions': 'Azioni',
    'Role': 'Ruolo',
    'Email': 'E-mail',
    'Phone': 'Telefono',
    'Address': 'Indirizzo',
    'City': 'Città',
    'Currency': 'Valuta',
    'Language': 'Lingua',
    'Theme': 'Tema',
    'General': 'Generale',
    'Total Revenue': 'Incasso totale',
    'Total Orders': 'Ordini totali',
    'Average Order': 'Scontrino medio',
    'Active Sessions': 'Tavoli attivi',
    'Recent Orders': 'Ordini recenti',
    'Live Orders': 'Ordini in tempo reale',
    'Table': 'Tavolo',
    'Date': 'Data',
    'Time': 'Ora',
    'Amount': 'Importo',
    'Customer': 'Cliente',
    'In Stock': 'Disponibile',
    'Out of Stock': 'Esaurito',
    'Sold Out': 'Esaurito',
    'Dine In': 'Al tavolo',
    'Pickup': 'Asporto',
    'Delivery': 'Consegna',
    'Paid': 'Pagato',
    'Unpaid': 'Non pagato',
    'Pending': 'In attesa',
    'Preparing': 'In preparazione',
    'Ready': 'Pronto',
    'Completed': 'Completato',
    'Cancelled': 'Annullato',
    'Loading...': 'Caricamento...',
    'No data': 'Nessun dato',
    'No results found': 'Nessun risultato trovato',
    'No items found': 'Nessun articolo trovato',
    'No translations defined yet': 'Nessuna traduzione definita',
    'Are you sure?': 'Sei sicuro?',
    'This action cannot be undone.': 'Questa azione non può essere annullata.',
    'Settings saved successfully!': 'Impostazioni salvate con successo!',
    'Administrator': 'Amministratore',
    'Manager': 'Manager',
    'Owner': 'Proprietario',
    'Chef': 'Chef',
    'Waiter': 'Cameriere',
    'Cashier': 'Cassiere',
    'Type a command or search...': 'Digita un comando o cerca...',
    'Command Palette': 'Comandi rapidi',
    'Demand Velocity': 'Velocità della domanda',
    'DEMAND VELOCITY': 'VELOCITÀ DELLA DOMANDA',
    'Top Performing Entities': 'Piatti più performanti',
    'TOP PERFORMING ENTITIES': 'PIATTI PIÙ PERFORMATI',
    'Hourly Load Analysis': 'Analisi oraria dell\'affluenza',
    'HOURLY LOAD ANALYSIS': 'ANALISI ORARIA DELL\'AFFLUENZA',
    'Bulk Command Center': 'Centro comandi di gruppo',
    'BULK COMMAND CENTER': 'CENTRO COMANDI DI GRUPPO',
    'Governance Scan': 'Controllo qualità del menu',
    'GOVERNANCE SCAN': 'CONTROLLO QUALITÀ DEL MENU',
    'No compliance issues detected in current menu': 'Nessun problema rilevato nel menu attuale',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'NESSUN PROBLEMA RILEVATO NEL MENU ATTUALE',
    'Procedural Tagging': 'Assegnazione automatica etichette',
    'PROCEDURAL TAGGING': 'ASSEGNAZIONE AUTOMATICA ETICHETTE',
    'Name match...': 'Corrispondenza nome...',
    '...Name match': 'Corrispondenza nome...',
    'Healthy': 'Salutare',
    'HEALTHY': 'SALUTARE',
    'Apply Tags': 'Applica etichette',
    'APPLY TAGS': 'APPLICA ETICHETTE',
    'Global Price Shift': 'Variazione globale dei prezzi',
    'GLOBAL PRICE SHIFT': 'VARIAZIONE GLOBALE DEI PREZZI',
    'Adjust': 'Applica',
    'ADJUST': 'APPLICA',
    'Availability Override': 'Gestione immediata disponibilità',
    'AVAILABILITY OVERRIDE': 'GESTIONE IMMEDIATA DISPONIBILITÀ',
    'Reset: ALL IN-STOCK': 'Ripristina: TUTTO DISPONIBILE',
    'RESET: ALL IN-STOCK': 'RIPRISTINA: TUTTO DISPONIBILE',
    'EMERGENCY STOP (ALL OUT)': 'Arresto d\'emergenza (TUTTO ESAURITO)',
    'Integrity Engine': 'Motore di integrità del menu',
    'INTEGRITY ENGINE': 'MOTORE DI INTEGRITÀ DEL MENU',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Questo sistema intelligente controlla la visibilità dei piatti, le anomalie dei prezzi e la qualità delle descrizioni in tutto il locale.',
    'Inventory Audit Export': 'Esportazione inventario',
    'INVENTORY AUDIT EXPORT': 'ESPORTAZIONE INVENTARIO',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Genera un report completo sulle prestazioni del menu e sui metadati per la verifica offline.',
    'Enterprise Data Export': 'Esportazione avanzata dati',
    'ENTERPRISE DATA EXPORT': 'ESPORTAZIONE AVANZATA DATI',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Genera e scarica audit completi sulle prestazioni in formati standard. Include flusso di visitatori, popolarità dei piatti e tassi di conversione.',
    'JSON Snapshot': 'Istantanea JSON',
    'JSON SNAPSHOT': 'ISTANTANEA JSON',
    'Compile CSV Report': 'Genera report CSV',
    'COMPILE CSV REPORT': 'GENERA REPORT CSV',
    'Date Segment': 'Segmento data',
    'DATE SEGMENT': 'SEGMENTO DATA',
    'Inbound Views': 'Visualizzazioni in entrata',
    'INBOUND VIEWS': 'VISUALIZZAZIONI IN ENTRATA',
    'Asset Interactions': 'Interazioni con i piatti',
    'ASSET INTERACTIONS': 'INTERAZIONI CON I PIATTI',
    'Telemetry History': 'Cronologia delle attività',
    'TELEMETRY HISTORY': 'CRONOLOGIA DELLE ATTIVITÀ',
    'Telemetry History (7d)': 'Cronologia delle attività (7g)',
    'TELEMETRY HISTORY (7D)': 'CRONOLOGIA DELLE ATTIVITÀ (7G)',
    'Growth Trajectory': 'Trend di crescita',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'La frequenza di interazione con il menu è in crescita negli ultimi 7 giorni.',
    'Deep Analysis Engine': 'Motore di analisi avanzata',
    'DEEP ANALYSIS ENGINE': 'MOTORE DI ANALISI AVANZATA',
    'Asset Velocity': 'Rendimento dei piatti',
    'ASSET VELOCITY': 'RENDIMENTO DEI PIATTI',
    'Traffic Projections': 'Previsioni di affluenza',
    'Telemetry Signal Tracking': 'Monitoraggio segnali di attività',
    'Service Hour Update': 'Aggiornamento orari di servizio',
    'Late Night Lounge Hours': 'Orari lounge serale',
    'Bar & Lounge Hours': 'Orari bar e lounge',
    'Breakfast & Brunch Hours': 'Orari colazione e brunch',
    'Switch Location': 'Cambia sede',
    'SWITCH LOCATION': 'CAMBIA SEDE',
    'Global Admin': 'Amministratore globale',
    'GLOBAL ADMIN': 'AMMINISTRATORE GLOBALE',
    'Staff Access': 'Accesso personale',
    'STAFF ACCESS': 'ACCESSO PERSONALE',
    'Venue Overview': 'Panoramica locale',
    'VENUE OVERVIEW': 'PANORAMICA LOCALE',
    'Unified Telemetry Engine': 'Motore unificato delle attività',
    'UNIFIED TELEMETRY ENGINE': 'MOTORE UNIFICATO DELLE ATTIVITÀ',
    'Pulse': 'In tempo reale',
    'PULSE': 'IN TEMPO REALE',
    'Insights': 'Tendenze',
    'INSIGHTS': 'TENDENZE',
    'Reports': 'Report',
    'REPORTS': 'REPORT',
    'GOVERNANCE': 'GOVERNANCE',
    'Multi-Venue': 'Multisede',
    'MULTI-VENUE': 'MULTISEDE',
    'Menu Views': 'Visualizzazioni menu',
    'Item Interactions': 'Interazioni piatti',
    'Guest Rating': 'Valutazione ospiti',
    'Revenue (7d)': 'Incassi (7g)',
    'Revenue (24h)': 'Incassi (24h)',
    'Revenue (30d)': 'Incassi (30g)',
    'Delivery orders (7d)': 'Ordini a domicilio (7g)',
    'Delivery orders (24h)': 'Ordini a domicilio (24h)',
    'Delivery orders (30d)': 'Ordini a domicilio (30g)',
    'Delivery revenue': 'Ricavi consegne',
    'Average delivery order': 'Ordine medio di consegna',
    'Average delivery fee': 'Costo medio di consegna',
    'Zone average': 'Media di zona',
    'Including fees': 'Spese incluse',
    'No deliveries': 'Nessuna consegna',
    'No orders': 'Nessun ordine',
    'No reviews': 'Nessuna recensione',
    'Live': 'Dal vivo',
    'LIVE': 'DAL VIVO',
    'reviews': 'recensioni',
    'orders': 'ordini',
    'deliveries': 'consegne',
    'of orders': 'degli ordini',
    'rate': 'tasso',
    'Recent Activity': 'Attività recente',
    'Real-time Venue Telemetry': 'Attività del locale in tempo reale',
    'REAL-TIME VENUE TELEMETRY': 'ATTIVITÀ DEL LOCALE IN TEMPO REALE',
    'Rapid Pulse View': 'Panoramica rapida dal vivo',
    'Anonymous Guest': 'Ospite anonimo',
    'scanned menu': 'ha scansionato il menu',
    'viewed item': 'ha visualizzato il piatto',
    'interacted with': 'ha interagito con',
    'placed order': 'ha effettuato un ordine',
    'searched menu': 'ha cercato nel menu',
    'Main Landing': 'Pagina principale',
    'Trace': 'Traccia',
    'TRACE': 'TRACCIA',
    'Gaining Telemetry...': 'Connessione ai dati...',
    'No recent traffic detected': 'Nessuna attività recente rilevata',
    'Activities will appear here as guests scan your QR code': 'Le attività appariranno qui quando gli ospiti scansioneranno il codice QR',
    'Analyze All Logs': 'Analizza tutti i registri',
    'Just now': 'Proprio ora',
    'less than a minute ago': 'meno di un minuto fa',
    '1 minute ago': '1 minuto fa',
    '1 hour ago': '1 ora fa',
    'about 1 hour ago': 'circa 1 ora fa',
    '1 day ago': '1 giorno fa',
    'DAY AGO 1': '1 giorno fa',
    '2 days ago': '2 giorni fa',
    'DAY AGO 2': '2 giorni fa',
    '3 days ago': '3 giorni fa',
    'DAY AGO 3': '3 giorni fa',
    '4 days ago': '4 giorni fa',
    'DAY AGO 4': '4 giorni fa',
    '5 days ago': '5 giorni fa',
    'DAY AGO 5': '5 giorni fa',
    '6 days ago': '6 giorni fa',
    'DAY AGO 6': '6 giorni fa',
    '7 days ago': '7 giorni fa',
    'DAY AGO 7': '7 giorni fa',
    'about 1 month ago': 'circa 1 mese fa',
    'Monday': 'Lunedì',
    'Tuesday': 'Martedì',
    'Wednesday': 'Mercoledì',
    'Thursday': 'Giovedì',
    'Friday': 'Venerdì',
    'Saturday': 'Sabato',
    'Sunday': 'Domenica',
    'Mon': 'Lun',
    'Tue': 'Mar',
    'Wed': 'Mer',
    'Thu': 'Gio',
    'Fri': 'Ven',
    'Sat': 'Sab',
    'Sun': 'Dom',
    'Opening Hours': 'Orari di apertura',
    'Start Time': 'Ora di inizio',
    'End Time': 'Ora di fine',
    'Opening Time': 'Orario di apertura',
    'Closing Time': 'Orario di chiusura',
    'All Day': 'Tutto il giorno',
    'Closed': 'Chiuso',
    'Active Days': 'Giorni attivi',
    'Active Schedule': 'Orario attivo',
    'Always Available': 'Sempre disponibile',
    'Always Visible': 'Sempre visibile',
    'New Category': 'Nuova categoria',
    'Category': 'Categoria',
    'Category Name': 'Nome categoria',
    'Category Description': 'Descrizione categoria',
    'Display Order': 'Ordine di visualizzazione',
    'Item Name': 'Nome piatto',
    'Item Description': 'Descrizione piatto',
    'Base Price': 'Prezzo base',
    'Preparation Time': 'Tempo di preparazione',
    'Calories': 'Calorie',
    'Featured Item': 'Piatto in evidenza',
    'Chef\'s Recommendation': 'Consigliato dallo chef',
    'Spicy': 'Piccante',
    'Vegetarian': 'Vegetariano',
    'Vegan': 'Vegano',
    'Gluten-Free': 'Senza glutine',
    'Halal': 'Halal',
    'Kosher': 'Kosher',
    'Contains Nuts': 'Contiene frutta a guscio',
    'Dairy-Free': 'Senza lattosio',
    'Allergen Exclusions': 'Allergeni',
    'Table Number': 'Numero tavolo',
    'Assigned Table': 'Tavolo assegnato',
    'Seats': 'Posti',
    'Capacity': 'Capacità',
    'Indoor': 'Interno',
    'Outdoor': 'Esterno',
    'Terrace': 'Terrazza',
    'Bar': 'Bar',
    'Add Table': 'Aggiungi tavolo',
    'Edit Table': 'Modifica tavolo',
    'Delete Table': 'Elimina tavolo',
    'Bookings': 'Prenotazioni',
    'Add to Waitlist': 'Aggiungi alla lista d’attesa',
    'Guest Name': 'Nome ospite',
    'Party Size': 'Numero di persone',
    'Arrival Time': 'Ora di arrivo',
    'Special Requests': 'Richieste speciali',
    'Confirmed': 'Confermato',
    'No Show': 'Non presentato',
    'Add Staff Member': 'Aggiungi membro dello staff',
    'Invite Staff': 'Invita collaboratore',
    'Email Address': 'Indirizzo e-mail',
    'Phone Number': 'Numero di telefono',
    'Download QR Code': 'Scarica codice QR',
    'Print Table Stands': 'Stampa cavalletti da tavolo',
    'Today': 'Oggi',
    'Yesterday': 'Ieri',
    'This Week': 'Questa settimana',
    'This Month': 'Questo mese',
    'Last 30 Days': 'Ultimi 30 giorni',
    'Access Denied': 'Accesso negato',
    'Advanced Settings': 'Impostazioni avanzate',
    'Change Image': 'Cambia immagine',
    'Remove Image': 'Rimuovi immagine',
    'Announcement Banner': 'Banner annunci',
    'Please fill in all required fields.': 'Si prega di compilare tutti i campi obbligatori.',
    'Changes saved successfully': 'Modifiche salvate con successo',
    '(You)': '(Tu)',
    'AI Auto-Tag': 'Tagging automatico IA',
    'Analyzing:': 'Analisi in corso:',
    'Apple (Fruit/Vegan)': 'Mela (Frutta/Vegano)',
    'Cancel editing': 'Annulla modifica',
    'Capacity:': 'Capienza:',
    'Carrot (Veggie)': 'Carota (Verdura)',
    'CircleDot (Organic)': 'Cerchio punto (Biologico)',
    'Coffee (Caffeine)': 'Caffè (Caffeina)',
    'Configuration': 'Configurazione',
    'Copy verification record name': 'Copia nome record verifica',
    'Copy verification record value': 'Copia valore record verifica',
    'Critical exception intercepted': 'Eccezione critica intercettata',
    'Kernel Panic': 'Errore di Sistema',
    'DNA (Organic/Bio)': 'DNA (Biologico/Bio)',
    'Egg (Ovo/Allergen)': 'Uovo (Ovo/Allergene)',
    'Empty': 'Vuoto',
    'Engraving...': 'Generazione in corso...',
    'Flame (Spicy)': 'Fiamma (Piccante)',
    'Free': 'Gratuito',
    'From': 'Da',
    'Generating': 'Generazione',
    'Governance & Traceability Studio': 'Centro di gestione e tracciabilità',
    'Greet': 'Benvenuto',
    'Heart (Healthy)': 'Cuore (Salutare)',
    'In-App': 'Nell\'app',
    'Infinity SVG': 'SVG Vettoriale (Illimitato)',
    'Leaf (Default)': 'Foglia (Predefinita)',
    'MAX Feature': 'Funzionalità Enterprise',
    'Milk (Dairy)': 'Latte (Latticini)',
    'Network': 'Rete',
    'Notify SMS': 'Notifica SMS',
    'Order': 'Ordine',
    'Original': 'Originale',
    'Overview': 'Panoramica',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Ruoli Standard',
    'Shield (Safety)': 'Scudo (Sicurezza)',
    'Soup (Warm/Soup)': 'Zuppa (Calda/Zuppa)',
    'Standard Pro View': 'Visualizzazione Standard',
    'Star': 'Stella',
    'Synchronized with Venue Identity': 'Personalizzato con l\'identità del locale',
    'Synchronizing Security Context...': 'Connessione sicura in corso...',
    'Targeted Timing': 'Orario del servizio',
    'Targeting': 'Si applica a',
    'To': 'A',
    'Toggle Navigation': 'Attiva navigazione',
    'Venue': 'Locale',
    'Venue Governance Suite': 'Gestione e impostazioni del locale',
    'Volume:': 'Volume:',
    'WHITEBOARD NOTE...': 'Nota per lo staff...',
    'Warning (Alert)': 'Avviso (Attenzione)',
    'Wheat (Flour/Gluten)': 'Grano (Farina/Gluten)',
    'Wine (Alcohol)': 'Vino (Alcol)',
    'YouTube/Vimeo/Direct Link': 'Link video (YouTube, Vimeo o MP4)',
    'Your name': 'Il tuo nome',
    '2. Table Number': '2. Numero del tavolo',
    'for Morocco or': 'per il Marocco, o',
    'in': 'in',
    'to': 'a',
    'e.g. 12 or Patio-1': 'es. 12 o Terrazza-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'es. Fettuccine artigianali, tartufo nero invernale, parmigiano 36 mesi...',
    'e.g. Head Sommelier & Wine Lead': 'es. Primo sommelier',
    'e.g. London': 'es. Roma',
    'e.g. SOMMELIER, HOST, BARISTA': 'es. SOMMELIER, HOSTESS, BARISTA',
    'e.g. Skyline Lounge': 'es. Terrazza Panoramica',
    'e.g. TRUFFLE OIL': 'es. OLIO AL TARTUFO',
    'e.g. The Grand Cafe': 'es. Il Gran Caffè',
    'e.g. Window preference, high chair': 'es. Vicino alla finestra, seggiolone per bambini',
    'Copied to clipboard!': 'Copiato negli appunti!',
    'Copy failed.': 'Copia non riuscita.',
    'Link copied!': 'Link copiato!',
    'Logo uploaded': 'Logo caricato',
    'Cover image uploaded': 'Immagine di copertina caricata',
    'Profile saved and synced successfully': 'Profilo salvato e sincronizzato con successo',
    'Category Created': 'Categoria creata',
    'Category Updated': 'Categoria aggiornata',
    'Category Deleted': 'Categoria eliminata',
    'Item Created': 'Articolo creato',
    'Item Updated': 'Articolo aggiornato',
    'Item Deleted': 'Articolo eliminato',
    'Menu Published': 'Menu pubblicato',
    'Delete Category?': 'Eliminare la categoria?',
    'Delete Menu Item?': 'Eliminare questo articolo?',
    'Delete Ingredient?': 'Eliminare questo ingrediente?',
    'Delete Service Period?': 'Eliminare il periodo di servizio?',
    'Remove team member?': 'Rimuovere questo membro dello staff?',
    'Cancel invitation?': 'Annullare l\'invito?',
    'Revoke API key?': 'Revocare la chiave API?',
    'Delete webhook endpoint?': 'Eliminare l\'endpoint webhook?',
    'Clear all notifications?': 'Cancellare tutte le notifiche?',
    'Disconnect Stripe account?': 'Disconnettere l\'account Stripe?',
    'Disconnect Google Business Profile?': 'Disconnettere il profilo Google Business?',
    'Campaigns published successfully!': 'Campagne pubblicate con successo!',
    'Changes successfully synchronized with Google Business Profile!': 'Modifiche sincronizzate con successo con il profilo Google Business!',
    'Google Business Profile successfully connected!': 'Profilo Google Business connesso con successo!',
    'Google Business Profile disconnected.': 'Profilo Google Business disconnesso.',
    'Google Post draft saved successfully!': 'Bozza del post Google salvata con successo!',
    'Post draft deleted.': 'Bozza del post eliminata.',
    'Review response published successfully!': 'Risposta alla recensione pubblicata con successo!',
    'SEO configuration saved successfully!': 'Configurazione SEO salvata con successo!',
    'SEO metadata translated across all languages!': 'Metadati SEO tradotti in tutte le lingue!',
    'Venue profile & bio translated across all languages!': 'Profilo e descrizione del locale tradotti in tutte le lingue!',
    'Reservation configuration saved successfully': 'Impostazioni prenotazioni salvate con successo',
    'Stripe settings saved successfully.': 'Impostazioni Stripe salvate con successo.',
    'Print-Ready PDF downloaded successfully': 'PDF pronto per la stampa scaricato con successo',
    'Vector QR PDF downloaded successfully': 'QR vettoriale PDF scaricato con successo',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Pacchetto prestampa 300 DPI generato con successo',
    'At least one language must be enabled.': 'È necessario abilitare almeno una lingua.',
    'Enter a non-zero percentage before adjusting prices.': 'Inserisci una percentuale diversa da zero prima di modificare i prezzi.',
    'Please enter a Venue Name before translating.': 'Inserisci il nome del locale prima di avviare la traduzione.',
    'Please enter an item name before running dietary and allergen analysis.': 'Inserisci il nome del piatto prima di avviare l\'analisi degli allergeni.',
    'Please write a reply before submitting.': 'Scrivi una risposta prima di inviare.',
    'Table 3 Called Waiter': 'Il tavolo 3 chiama il cameriere',
    'Table Call Bell': 'Campanello chiamata tavolo',
    'Waiter Calls': 'Chiamate cameriere',
    'Waiter Dispatch & Calls': 'Gestione chiamate cameriere',
    'Kitchen Display & Whiteboard': 'Display cucina e lavagna',
    'Customer Orders': 'Ordini clienti',
    'Bakery': 'Panetteria / Pasticceria',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Caffetteria / Bar',
    'Fast Food': 'Fast food',
    'Gourmet / Fine Dining': 'Ristorazione gourmet / Fine dining',
    'Hotel': 'Hotel',
    'General Local Business': 'Attività locale generale',
    'Classic': 'Classico',
    'Clean': 'Pulito',
    'Modern': 'Moderno',
    'Elegant': 'Elegante',
    'Energetic': 'Dinamico',
    'Glass': 'Effetto vetro',
    'Playful & Casual': 'Informale e giocoso',
    'Sophisticated & Gourmet': 'Sofisticato e gourmet',
    'Warm & Welcoming': 'Caldo e accogliente',
    'Executive': 'Executive',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Aggiornamento richiesto: I menu multilingue sono disponibili nel piano Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Aggiornamento richiesto: Gli ordini e le chiamate al cameriere non sono disponibili nel piano Starter.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'L\'esportazione di vettoriali SVG è disponibile nei piani Pro e Max. Effettua l\'upgrade.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'L\'esportazione di PDF pronti per la stampa è disponibile nei piani Pro e Max. Effettua l\'upgrade.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Il piano Pro include fino a 3 campagne attive. Metti in pausa un\'altra campagna o passa a MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Il piano Starter include fino a 20 tavoli (Tavoli 1–20). Passa a PRO per tavoli illimitati.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Il piano Starter supporta tavoli numerici (1–20). I nomi personalizzati (es. Terrazza, VIP) richiedono PRO.',
    'Add Menu Categories & Items': 'Aggiungi categorie e articoli',
    'Add Menu Items': 'Aggiungi articoli al menu',
    'Organize your menu sections and food items.': 'Organizza le sezioni del menu e le tue portate.',
    'Add and manage your food & beverage offerings.': 'Aggiungi e gestisci le tue proposte di cibo e bevande.',
    'Customize Venue Branding': 'Personalizza il brand del locale',
    'Set your brand colors, logo, and presentation.': 'Imposta i colori del brand, il logo e l\'aspetto grafico.',
    'Get Your QR Code & Go Live': 'Ottieni il tuo codice QR e vai online',
    'Download table QR codes and preview your live menu.': 'Scarica i codici QR per i tavoli e visualizza l\'anteprima del menu.',
    'Account & Venue Created Successfully!': 'Account e locale creati con successo!',
    '⚡ Off-Peak Hours': '⚡ Orari di morbida',
    '🍽️ Lunch Crowd': '🍽️ Clientela del pranzo',
    '🚀 Weekend Rush': '🚀 Servizio del weekend',
    '🌟 Seasonal Special': '🌟 Specialità di stagione',
    '📣 Engagement Buzz': '📣 Passaparola e interazione',
    'Festive & Energetic': 'Festoso ed energico',
    'Urgent & High-Conversion': 'Offerta a tempo ad alto impatto',
    'Generated 3 tailored campaign strategies': 'Generate 3 strategie promozionali su misura',
    'Could not generate campaign ideas. Please try again.': 'Impossibile generare idee promozionali. Riprova.',
    'Venue Themes': 'Temi grafici del locale',
    'Choose the character of your public menu': 'Scegli il carattere visivo del tuo menu pubblico',
    'Glassmorphism & Depth': 'Effetto vetro e profondità',
    'Independent XML Sitemap': 'Sitemap XML indipendente',
    'Structured Schema & Sync': 'Dati strutturati e sincronizzazione',
    'Improve how your venue appears on Google and in local search results.': 'Migliora la visibilità del tuo locale su Google e nelle ricerche locali.',
    'Terms of Service': 'Termini di servizio',
    'Confirm your email': 'Conferma la tua email',
    'Check your spam / junk folder': 'Controlla la cartella spam o posta indesiderata',
    'Commercial pre-press print production package': 'Pacchetto di prestampa per tipografia',
    'Over-bleed safety margin': 'Margine di sicurezza con abbondanza',
    'Crop Marks': 'Segni di taglio',
    'Guillotine cutting guides': 'Guide per taglio a ghigliottina',
    'Global Banners': 'Banner globali in evidenza',
    'Featured Items': 'Articoli in primo piano',
    'AI Assistant': 'Assistente IA',
    'Custom Role Schemas': 'Schemi di ruolo personalizzati',
    'Notification Preferences': 'Preferenze di notifica',
    'Preferences saved automatically': 'Preferenze salvate automaticamente',
    'Menu Extractor (PDF & Images)': 'Estrattore menu (PDF e immagini)',
    'Multi-Language Translation': 'Traduzione multilingue',
    'Start Menu Translation': 'Avvia traduzione del menu',
    'Menu Items Hub': 'Gestione articoli del menu',
    'Open Menu Items': 'Apri articoli del menu',
    'Enterprise Traceability': 'Tracciabilità aziendale',
    'Full History': 'Cronologia completa',
    'Billing & Plans': 'Fatturazione e piani',
    'Subscription Governance': 'Gestione abbonamento',
    'Platform Usage': 'Utilizzo della piattaforma',
    'Menu Page Views': 'Visualizzazioni del menu',
    'Enterprise Identity': 'Identità aziendale',
    'Access Governance': 'Controllo degli accessi',
    'Required Fields *': 'Campi obbligatori *',
    'Enabled Languages': 'Lingue abilitate',
    'Venue Info & Bio Localizations': 'Traduzioni della descrizione e delle info del locale',
    'Public Address': 'Indirizzo pubblico',
    'Public URL Slug': 'Slug URL pubblico',
    'Contact Phone': 'Telefono di contatto',
    'Business Address': 'Indirizzo commerciale',
    'No Categories Yet': 'Nessuna categoria presente',
    'No Custom Roles Configured': 'Nessun ruolo personalizzato configurato',
    'No Google reviews retrieved yet': 'Nessuna recensione Google recuperata',
    'No Guests Found': 'Nessun ospite trovato',
    'No Menu Items Yet': 'Nessun articolo nel menu',
    'No Promotions Configured': 'Nessuna promozione configurata',
    'No Service Periods Yet': 'Nessun periodo di servizio definito',
    'No Support Tickets': 'Nessun ticket di supporto',
    'No Tables on Floor Plan': 'Nessun tavolo nella mappa della sala',
    'No Team Members Found': 'Nessun membro del team trovato',
    'No Upcoming Events': 'Nessun evento in programma',
    'No Walk-Ins Recorded': 'Nessun cliente senza prenotazione registrato',
    'No items are available right now': 'Nessun articolo disponibile al momento',
    'No notifications found': 'Nessuna notifica trovata',
    'No reservations matching your filters': 'Nessuna prenotazione corrisponde ai filtri',
    'No tables currently occupied': 'Nessun tavolo attualmente occupato',
    'No venue selected': 'Nessun locale selezionato',
    'Waitlist': 'Lista d\'attesa',
    'Waitlist is Empty': 'La lista d\'attesa è vuota',
    'Waves (Gluten)': 'Spighe (Glutine)',
    'We sent a temporary verification code to': 'Abbiamo inviato un codice di verifica temporaneo a',
    'Website URL': 'URL del sito web',
    'What the customer receives': 'Cosa riceve il cliente',
    'White-label control is available on eligible plans.': 'La personalizzazione white-label è disponibile nei piani compatibili.',
    'Why this works:': 'Perché questa strategia funziona:',
    'WiFi Broadcast': 'Condivisione WiFi',
    'WiFi Password': 'Password WiFi',
    'Window seat, dietary request, birthday celebration...': 'Tavolo vicino alla finestra, esigenze alimentari, compleanno...',
    'Your Published Response': 'La tua risposta pubblicata',
    'Your Venues': 'I tuoi locali',
    '🎉 Ready to publish!': '🎉 Pronto per la pubblicazione!',
    'e.g. Grill, Salad, Pastry': 'es. Griglia, Insalate, Pasticceria',
    'e.g. Guest called, emergency': 'es. Chiamata del cliente, urgenza',
    'e.g. Italian, Moroccan, Burgers': 'es. Cucina italiana, marocchina, burger',
    'e.g. Mains, Pasta, Starters': 'es. Secondi, Primi piatti, Antipasti',
    'e.g. Signature Truffle Pasta': 'es. Pasta d\'autore al tartufo',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'es. Pausa caffè pomeridiana, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'es. Colazione, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'es. Ti aspettiamo per il concerto Jazz dal vivo ogni venerdì alle 20:00!',
  },
  pt: {
    '123 Main St, City': 'Rua Principal 123, Cidade',
    'Balance': 'Saldo de pontos',
    'Lifetime': 'Total acumulado',
    'Source': 'Origem',
    'Menu': 'Cardápio',
    'Advanced': 'Avançado',
    'Core Suite': 'Recursos essenciais',
    'Privacy': 'Privacidade',
    'Support': 'Suporte',
    'Save Layout': 'Salvar layout do salão',
    'Shape:': 'Formato:',
    'SEARCH LIBRARY...': 'BUSCAR NA BIBLIOTECA...',
    'Search library...': 'Buscar na biblioteca...',
    'None': 'Nenhum',
    'Type': 'Tipo',
    'Target': 'Alvo',
    'Value / Target': 'Valor / Alvo',
    'London (GMT/BST)': 'Londres (GMT/BST)',
    'New York (EST/EDT)': 'Nova York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tóquio (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Cor inicial',
    'Depth Shade': 'Sombra e profundidade',
    'Matrix Geometry': 'Geometria do padrão QR',
    'Eye Architecture': 'Cantos de orientação do QR',
    'Brand Centerpiece': 'Logo central do QR',
    'Scannability Shield': 'Garantia de leitura do QR',
    'Environment Blur': 'Desfoque ambiental',
    'Color Target:': 'Perfil de cor:',
    'DPI:': 'DPI (Resolução):',
    '3mm Bleed Box': 'Sangria de corte de 3 mm',
    '300 DPI': '300 DPI (Alta definição)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Protocolo SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'ID de entidade do IdP (Entity ID)',
    'SSO URL': 'URL de login SSO',
    'X.509 Certificate': 'Certificado de segurança X.509',
    'ACS URL': 'URL do serviço ACS',
    'Entity ID': 'ID da entidade',
    'Navigate': 'Navegar',
    'Select': 'Selecionar',
    'OmniSearch v1.0': 'Busca universal OmniSearch',
    'Back to order review': 'Voltar para revisão do pedido',
    'Return to Intelligence Studio': 'Voltar ao estúdio de inteligência',
    'View Full Notification Center': 'Ver central de notificações completa',
    'Read all': 'Marcar tudo como lido',
    'Cancel department': 'Cancelar departamento',
    'Save department': 'Salvar departamento',
    'Venue Segmentation': 'Segmentação de espaços',
    'Collaborate on your menu together.': 'Colabore no seu cardápio em equipe.',
    'Timeline': 'Linha do tempo',
    'Calendar': 'Calendário de reservas',
    'Book': 'Reservar mesa',
    'Duration': 'Duração da permanência',
    'Time & Duration': 'Horário e duração',
    'Seating Preference': 'Preferência de lugar',
    'Section': 'Seção do salão',
    'Min Capacity': 'Capacidade mínima',
    'Max Capacity': 'Capacidade máxima',
    'Phone (Optional)': 'Telefone (Opcional)',
    'Name or Walk-in': 'Nome ou cliente direto',
    'Next day': 'Dia seguinte',
    'Next month': 'Mês seguinte',
    'Live Connection': 'Conexão ao vivo',
    'Live Inventory': 'Estoque em tempo real',
    'Main Venue (Full Menu)': 'Estabelecimento principal (Cardápio completo)',
    'Preview Menu': 'Visualizar cardápio',
    'Manual Setup Guide': 'Guia de configuração manual',
    'Connected & Active': 'Conectado e ativo',
    'CTA Action': 'Botão de ação (CTA)',
    'Phone Contact': 'Telefone para contato',
    'Menu URL': 'Endereço web do cardápio',
    'Google Location': 'Localização no Google',
    'Field': 'Campo',
    'Linked': 'Vinculado',
    'Synchronizing Hub...': 'Sincronizando a central...',
    'Data Integrity': 'Integridade dos dados',
    'Station Governance Suite': 'Gestão de estações de trabalho',
    'Open audit log': 'Abrir registro de auditoria',
    'Signal / Time': 'Evento / Horário',
    'Identity': 'Usuário / Identidade',
    'Invitation Invalid': 'Convite inválido ou expirado',
    'Invited To': 'Convidado para se juntar a',
    'Team Limit Reached': 'Limite de membros da equipe atingido',
    'Venue Limit Reached': 'Limite de estabelecimentos atingido',
    'Venue Setup Required': 'Configuração inicial necessária',
    'Create Venue': 'Criar estabelecimento',
    'TableGreet Onboarding': 'Boas-vindas ao TableGreet',
    'No payment required': 'Nenhum pagamento necessário',
    'Selected Plan': 'Plano selecionado',
    'Payment Method': 'Forma de pagamento',
    'Invoice ID': 'Número da fatura',
    'Verified': 'Verificado',
    'Accent color': 'Cor de destaque',
    'Set the voice and overall light level.': 'Defina o tom e o nível de iluminação geral.',
    'Layout architecture': 'Arquitetura do layout',
    'Navigation & motion': 'Navegação e animações',
    'Loading preview engine': 'Carregando mecanismo de pré-visualização',
    'Real Live Preview': 'Pré-visualização em tempo real',
    'Included with Pro & Max': 'Incluído nos planos Pro e Max',
    'Daypart Scheduling': 'Programação de períodos do dia',
    'Operational Daypart Architecture': 'Estrutura dos períodos operacionais',
    'Service Protocol': 'Protocolo de atendimento',
    'Configuration Engine': 'Mecanismo de configuração',
    'Linked Department': 'Departamento vinculado',
    'Team Hub': 'Gestão da equipe',
    'Team Size': 'Tamanho da equipe',
    'Joined': 'Ingressou em',
    'Cancel Invitation': 'Cancelar convite',
    'Initialize New Organization': 'Criar nova organização',
    'Operational': 'Operacional',
    'Pro Feature': 'Recurso Pro',
    'Current State': 'Estado atual',
    'Open on the big TV': 'Abrir na TV grande',
    'Menu Board': 'Quadro digital do cardápio',
    'Reward': 'Recompensa',
    'Reward name': 'Nome da recompensa',
    'Cancel & refund': 'Cancelar e reembolsar',
    'Code': 'Código',
    'Find': 'Buscar',
    'Complete': 'Concluído',
    'Seated': 'Sentados',
    'No-Show': 'Não compareceu',
    'Walk-in': 'Sem reserva',
    'Party': 'Pessoas',
    'Turnover': 'Rotatividade de mesas',
    'We couldn\'t load the menu': 'Não foi possível carregar o cardápio',
    'Venue Not Found': 'Estabelecimento não encontrado',
    'Live Availability': 'Disponibilidade ao vivo',
    'Multi-Language Suite': 'Gestão multilíngue',
    'Key name': 'Nome da chave',
    'Revoke API key': 'Revogar chave de API',
    'Delete webhook': 'Excluir webhook',
    'Identity Provider': 'Provedor de identidade (IdP)',
    'Provider': 'Provedor',
    'Mandatory MFA': 'Autenticação de dois fatores obrigatória (MFA)',
    'Account Identity': 'Identidade da conta',
    'Login Email': 'E-mail de login',
    'New Login Email': 'Novo e-mail de login',
    'Phone Login': 'Login por telefone',
    'SMS Verification Code': 'Código de verificação por SMS',
    'Integration Method': 'Método de integração',
    'Stripe Connect': 'Pagamentos Stripe Connect',
    'Stripe Secret Key': 'Chave secreta do Stripe',
    'Stripe Webhook Secret': 'Segredo de webhook do Stripe',
    'Client-Owned': 'De propriedade do cliente',
    'Verify connection': 'Verificar conexão',
    'Setup Health': 'Status da configuração',
    'Privacy Protected': 'Privacidade protegida',
    'Privacy Policy': 'Política de privacidade',
    'Continue': 'Continuar',
    'Sign In': 'Entrar',
    'Didn\'t get it?': 'Não recebeu?',
    'Logged In Account': 'Conta conectada',
    'Annual': 'Anual',
    'Unread': 'Não lido',
    'Starred': 'Favoritos',
    'High / Urgent': 'Alto / Urgente',
    'Mark Read': 'Marcar como lido',
    'Delete notification': 'Excluir notificação',
    'Urgent': 'Urgente',
    'High': 'Alto',
    'Normal': 'Normal',
    'Low': 'Baixo',
    'Select a Ticket': 'Selecionar um chamado',
    'Urgent (Immediate attention)': 'Urgente (Atenção imediata)',
    'Type your reply here...': 'Digite sua resposta aqui...',
    'Print Card (PDF)': 'Imprimir display de mesa (PDF)',
    'Advanced Printing Engine': 'Mecanismo avançado de impressão gráfica',
    'Multi-Venue Governance': 'Gestão multi-estabelecimentos',
    'Network Revenue': 'Receita total da rede',
    'Live Network Total': 'Total da rede em tempo real',
    'Audit Log Hub': 'Registro de auditoria e operações',
    'Export Log': 'Exportar registro',
    'CUSTOM ROLE SCHEMAS': 'ESQUEMAS DE FUNÇÕES PERSONALIZADAS',
    'Unique internal role identifier.': 'Identificador interno exclusivo da função.',
    'Human-friendly role label.': 'Nome amigável da função para a equipe.',
    'Marketing Hub': 'Centro de marketing',
    'Revenue Generation Suite': 'Ferramentas de aumento de receita',
    'Active Campaign Limit Reached (3/3)': 'Limite de campanhas ativas atingido (3/3)',
    'Banner Content': 'Conteúdo do banner',
    'Style Profile': 'Estilo visual',
    'Action URL (Optional)': 'Link de ação (Opcional)',
    'Promotion Label': 'Rótulo da promoção',
    'Target Selection': 'Seleção de destino',
    'Edit Promotion': 'Editar promoção',
    'Delete Promotion': 'Excluir promoção',
    'Loyalty Engine': 'Programa de fidelidade',
    'Reward catalog': 'Catálogo de recompensas',
    'Add reward': 'Adicionar recompensa',
    'Loyalty ID': 'ID de fidelidade',
    'No point activity yet': 'Nenhuma atividade de pontos ainda',
    'Scan or enter QR value': 'Escaneie ou digite o código QR',
    'TableGreet Admin': 'Administração TableGreet',
    'Capacity Pacing': 'Regulação de capacidade',
    'Cancellation Policy': 'Política de cancelamento',
    'Booking Experience': 'Experiência de reserva',
    'Floor Occupancy': 'Ocupação do salão',
    'Currently Seated': 'Atualmente no local',
    'Seat Party': 'Acomodar clientes',
    'Seat Table': 'Ocupar mesa',
    'Select Table': 'Selecionar mesa',
    'Table / Capacity': 'Mesa / Capacidade',
    'Table Name/Number': 'Nome ou número da mesa',
    'Mark Arrived': 'Marcar como presente',
    'Mark No-Show': 'Marcar como ausente',
    'Complete Dining': 'Encerrar atendimento',
    'Standard Dining (Default)': 'Salão principal (Padrão)',
    'Walk-In Dining Log': 'Registro de clientes sem reserva',
    'Notification Center': 'Central de notificações',
    'Single Sign-On': 'Login único (SSO)',
    'Welcome Aboard!': 'Bem-vindo ao TableGreet!',
    'Select your TableGreet plan': 'Escolha seu plano TableGreet',
    'Free Plan Activation': 'Ativação do plano gratuito',
    'Venue Created!': 'Estabelecimento criado com sucesso!',
    'Upgrade for Unlimited': 'Atualizar para ilimitado',
    'Auto-Tag Menu': 'Marcação automática do cardápio',
    'Start Auto-Tagging': 'Iniciar marcação automática',
    'Launch Auto-Tagger': 'Abrir assistente de marcação',
    'Launch PDF Importer': 'Importar cardápio em PDF',
    'Analyzing Menu...': 'Analisando o cardápio...',
    'Analyzing Recipe...': 'Analisando a receita...',
    'Manual Availability': 'Disponibilidade manual',
    'Technical Breakdown': 'Detalhamento técnico',
    'Dietary Taxonomy': 'Dietas e alérgenos',
    'Dietary & Allergen AI': 'IA de dietas e alérgenos',
    'Inherit from Category': 'Herdar da categoria',
    'Ingredient Library': 'Biblioteca de ingredientes',
    'Ingredient Name': 'Nome do ingrediente',
    'Menu Organization': 'Organização do cardápio',
    'Inventory Taxonomy Hub': 'Gestor de taxonomia e categorias',
    'Category Intelligence': 'Inteligência de categoria',
    'Service Period': 'Período de serviço',
    'Manual Configuration / All Day': 'Configuração manual / O dia todo',
    'Operational Department': 'Departamento operacional',
    'Global / No Department': 'Global / Sem departamento',
    'Cancel category': 'Cancelar categoria',
    'New category name...': 'Nome da nova categoria...',
    'Save category': 'Salvar categoria',
    'QR Matrix Only': 'Apenas código QR',
    'Vector & PDF': 'Vetor e PDF',
    'Vector QR (SVG)': 'Código QR vetorial (SVG)',
    'Vector QR (PDF)': 'Código QR para impressão (PDF)',
    'Save QR Style': 'Salvar estilo do QR',
    'Share Link': 'Compartilhar link',
    'High Fidelity 300DPI': 'Alta resolução 300 DPI para gráfica',
    'Generating 300 DPI PDF...': 'Gerando PDF em 300 DPI...',
    'Give your venue a point of view.': 'Dê ao seu estabelecimento uma identidade visual única.',
    'Signage Menu Not Found': 'Cardápio para tela digital não encontrado',
    'Scan Menu': 'Escanear cardápio',
    'Open on your phone': 'Abrir no celular',
    'Full Screen': 'Tela cheia',
    'Lock TV board': 'Bloquear tela de TV',
    'Big-Screen Menu': 'Cardápio para telões',
    'TV Signage': 'Sinalização digital para TV',
    'General Information': 'Informações gerais',
    'Venue Name *': 'Nome do estabelecimento *',
    'Public Slug *': 'Endereço público (Slug) *',
    'Venue Logo': 'Logo do estabelecimento',
    'Hero Cover Image': 'Imagem de capa principal',
    'Venue Timezone': 'Fuso horário do estabelecimento',
    'Primary Brand Color': 'Cor principal da marca',
    'TableGreet footer credit': 'Crédito TableGreet no rodapé',
    'White-Label Branding': 'Marca branca personalizada',
    'Client DNS Setup': 'Configuração de DNS do domínio',
    'Copy CNAME target': 'Copiar destino CNAME',
    'Copy TableGreet URL': 'Copiar URL TableGreet',
    'Included TableGreet URL': 'URL TableGreet incluso',
    'Open TableGreet URL': 'Abrir link TableGreet',
    'Public menu': 'Cardápio público para clientes',
    'Public venue delivery': 'Entrega a domicílio pelo cardápio',
    'Zone name': 'Nome da zona',
    'Coverage note': 'Observação de cobertura',
    'Default ETA min': 'Tempo estimado mín. (minutos)',
    'Default ETA max': 'Tempo estimado máx. (minutos)',
    'Packaging fee': 'Taxa de embalagem',
    'Courier handled by': 'Entrega realizada por',
    'Third-party courier': 'Entregador externo parceiro',
    'Unified Experience': 'Experiência unificada',
    'Save WiFi to Venue': 'Salvar Wi-Fi no estabelecimento',
    'Network Name (SSID)': 'Nome da rede Wi-Fi (SSID)',
    'Audio Alert Chime': 'Sinal sonoro de notificação',
    'Time Format': 'Formato de hora',
    'Auto / Default': 'Automático / Padrão',
    'Syncing SEO & Google Data...': 'Sincronizando dados de SEO e Google...',
    'Google Connection': 'Conexão com o Google',
    'Search Engine Metadata': 'Metadados para motores de busca',
    'Social card preview': 'Pré-visualização nas redes sociais',
    'Allow Indexing': 'Permitir indexação',
    'Canonical URL Override': 'Substituição de URL canônico',
    'Price Range': 'Faixa de preço',
    'Latitude': 'Latitude',
    'Longitude': 'Longitude',
    'Google Place ID / Location ID': 'ID de local do Google (Place ID)',
    'Google Search Preview': 'Prévia na pesquisa do Google',
    'Linked Google Location': 'Localização do Google vinculada',
    'SEO Documentation': 'Documentação de SEO',
    ', and': ', e',
    'and': 'e',
    'Delete Department?': 'Excluir departamento?',
    'Delete Department': 'Excluir departamento',
    'Live URL': 'URL ao vivo',
    'Copy full live URL': 'Copiar URL ao vivo completo',
    'Open live department menu': 'Abrir cardápio do departamento ao vivo',
    'Loading departments...': 'Carregando departamentos...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'A segmentação em múltiplos departamentos é um recurso premium disponível no plano PRO ou superior.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Segmente seus cardápios por salões, mesas, bares ou locais físicos.',
    'Targeted Ordering': 'Pedidos direcionados',
    'Route guests and orders to specific preparation areas.': 'Direcione clientes e pedidos para áreas específicas de preparo.',
    'Operational Departments': 'Departamentos operacionais',
    'OPERATIONAL DEPARTMENTS': 'DEPARTAMENTOS OPERACIONAIS',
    'Venue Segmentation Hub': 'Centro de segmentação do estabelecimento',
    'VENUE SEGMENTATION HUB': 'CENTRO DE SEGMENTAÇÃO DO ESTABELECIMENTO',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Crie departamentos para organizar seus cardápios. Por exemplo, um grande estabelecimento pode ter',
    'Restaurant': 'Restaurante',
    'Poolside': 'Piscina',
    'department, each with its own categories.': ', cada um com suas próprias categorias.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Os departamentos permitem dividir seu estabelecimento em seções como Restaurante, Bar ou Piscina — cada um com suas próprias categorias.',
    'Department name': 'Nome do departamento',
    'New department name...': 'Nome do novo departamento...',
    'No Linked Daypart (Always Available)': 'Sem horário vinculado (sempre disponível)',
    'Linked Daypart': 'Horário de funcionamento vinculado',
    'Localizations': 'Traduções',
    'LOCALIZATIONS': 'TRADUÇÕES',
    'AI Auto-Translate': 'Tradução automática por IA',
    'AI AUTO-TRANSLATE': 'TRADUÇÃO AUTOMÁTICA POR IA',
    'Translated name': 'Nome traduzido',
    'No Departments Yet': 'Ainda não há departamentos',
    'Add First Department': 'Adicionar primeiro departamento',
    'Avg Order Value': 'Valor médio do pedido',
    'AVG ORDER VALUE': 'VALOR MÉDIO DO PEDIDO',
    'item taps': 'toques nos pratos',
    'ITEM TAPS': 'TOQUES NOS PRATOS',
    'Avg Rating': 'Avaliação média',
    'AVG RATING': 'AVALIAÇÃO MÉDIA',
    'Revenue (Last 7 Days)': 'Receita (últimos 7 dias)',
    'REVENUE (LAST 7 DAYS)': 'RECEITA (ÚLTIMOS 7 DIAS)',
    'Daily breakdown': 'Detalhamento diário',
    'DAILY BREAKDOWN': 'DETALHAMENTO DIÁRIO',
    'Menu Views (Last 7 Days)': 'Visualizações do cardápio (últimos 7 dias)',
    'MENU VIEWS (LAST 7 DAYS)': 'VISUALIZAÇÕES DO CARDÁPIO (ÚLTIMOS 7 DIAS)',
    'Guest traffic': 'Fluxo de clientes',
    'GUEST TRAFFIC': 'FLUXO DE CLIENTES',
    'Most Viewed Items': 'Itens mais visualizados',
    'MOST VIEWED ITEMS': 'ITENS MAIS VISUALIZADOS',
    'Top Rated Items': 'Itens mais bem avaliados',
    'TOP RATED ITEMS': 'ITENS MAIS BEM AVALIADOS',
    'Waiter Requests': 'Chamadas de garçom',
    'WAITER REQUESTS': 'CHAMADAS DE GARÇOM',
    'Total': 'Total',
    'TOTAL': 'TOTAL',
    'Order Status (30 days)': 'Status dos pedidos (30 dias)',
    'ORDER STATUS (30 DAYS)': 'STATUS DOS PEDIDOS (30 DIAS)',
    'Recent Guest Feedback': 'Feedbacks recentes de clientes',
    'RECENT GUEST FEEDBACK': 'FEEDBACKS RECENTES DE CLIENTES',
    'Conversion Rate': 'Taxa de conversão',
    'CONVERSION RATE': 'TAXA DE CONVERSÃO',
    'of menu visitors placed an order': 'dos visitantes do cardápio fizeram um pedido',
    'Refreshed': 'Atualizado',
    'REFRESHED': 'ATUALIZADO',
    'Last 30 days': 'Últimos 30 dias',
    'LAST 30 DAYS': 'ÚLTIMOS 30 DIAS',
    'cash': 'dinheiro',
    'Cash': 'Dinheiro',
    'CASH': 'DINHEIRO',
    'bill': 'a conta',
    'Bill': 'A conta',
    'BILL': 'A CONTA',
    'water': 'água',
    'Water': 'Água',
    'WATER': 'ÁGUA',
    'service': 'serviço',
    'Service': 'Serviço',
    'SERVICE': 'SERVIÇO',
    'No interaction data yet': 'Nenhum dado de interação ainda',
    'No ratings collected yet': 'Nenhuma avaliação coletada ainda',
    'No requests in period': 'Nenhuma solicitação no período',
    'No feedback collected yet': 'Nenhum feedback de cliente ainda',
    'No menu views recorded yet': 'Nenhuma visualização do cardápio registrada ainda',
    'Dish Rating Breakdown': 'Detalhamento de notas por prato',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'O ranking de pratos e a satisfação dos clientes são exclusivos dos planos MAX e Enterprise.',
    'Customer Sentiment & Reviews': 'Sentimento e avaliações dos clientes',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Avaliações diretas de clientes e análise de sentimento exigem uma assinatura MAX ou Enterprise.',
    'Reviews AI': 'IA de análise de avaliações',
    'Guest Feedback': 'Feedback dos clientes',
    'Upgrade to unlock rating analytics →': 'Faça upgrade para liberar análises de avaliação →',
    'Upgrade to Max': 'Fazer upgrade para o Max',
    'UPGRADE TO MAX': 'FAZER UPGRADE PARA O MAX',
    'MAX PLAN': 'PLANO MAX',
    'No Data Yet': 'Ainda não há dados',
    'Share your QR code to start collecting real venue insights.': 'Compartilhe seu código QR para começar a coletar estatísticas reais do seu estabelecimento.',
    'Revenue Analytics': 'Análise de receita',
    'REVENUE ANALYTICS': 'ANÁLISE DE RECEITA',
    'Track your earnings, orders, and average ticket values over time.': 'Acompanhe seus ganhos, pedidos e ticket médio ao longo do tempo.',
    'Operational Velocity': 'Velocidade operacional',
    'OPERATIONAL VELOCITY': 'VELOCIDADE OPERACIONAL',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Monitore o fluxo de clientes, pratos populares e a demanda de chamadas aos garçons.',
    'Upgrade to Pro': 'Mudar para Pro',
    'UPGRADE TO PRO': 'FAZER UPGRADE PARA O PRO',
    'Loading Insights…': 'Carregando insights…',
    'Loading Insights...': 'Carregando insights...',
    'Telemetry Unavailable': 'Dados de atividade indisponíveis',
    'TELEMETRY UNAVAILABLE': 'DADOS DE ATIVIDADE INDISPONÍVEIS',
    'No Data Captured': 'Nenhum dado registrado',
    'NO DATA CAPTURED': 'NENHUM DADO REGISTRADO',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Compartilhe seu código QR para começar a coletar estatísticas de clientes em tempo real.',
    'INTELLIGENCE STUDIO': 'CENTRO DE ANÁLISE',
    'Insights Engine Locked': 'Módulo de análise avançada bloqueado',
    'INSIGHTS ENGINE LOCKED': 'MÓDULO DE ANÁLISE AVANÇADA BLOQUEADO',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'A análise avançada de tendências, ações em massa e exportações são exclusivas para',
    'Unlock Pro Features': 'Desbloquear recursos Pro',
    'UNLOCK PRO FEATURES': 'DESBLOQUEAR RECURSOS PRO',
    'MENU VIEWS': 'VISUALIZAÇÕES DO CARDÁPIO',
    'ITEM INTERACTIONS': 'INTERAÇÕES COM ITENS',
    'GUEST RATING': 'AVALIAÇÃO DOS CLIENTES',
    'DELIVERY REVENUE': 'RECEITA DE ENTREGAS',
    'AVERAGE DELIVERY ORDER': 'PEDIDO MÉDIO DE ENTREGA',
    'AVERAGE DELIVERY FEE': 'TAXA MÉDIA DE ENTREGA',
    'INCLUDING FEES': 'INCLUINDO TAXAS',
    'ZONE AVERAGE': 'MÉDIA DA ZONA',
    'TRAFFIC PROJECTIONS': 'PROJEÇÕES DE CLIENTES',
    'TELEMETRY SIGNAL TRACKING': 'RASTREAMENTO DE SINAIS DE ATIVIDADE',
    'VEGETARIAN': 'VEGETARIANO',
    'VEGAN': 'VEGANO',
    'SPICY': 'PICANTE',
    'GLUTEN-FREE': 'SEM GLÚTEN',
    'Reset: All In-Stock': 'Redefinir: Tudo em estoque',
    'Emergency Stop (All Out)': 'Parada de emergência: Tudo esgotado',
    'No compliance issues': 'Nenhum problema detectado',
    'NO COMPLIANCE ISSUES': 'NENHUM PROBLEMA DETECTADO',
    'detected in current menu': 'no cardápio atual',
    'DETECTED IN CURRENT MENU': 'NO CARDÁPIO ATUAL',
    'Telemetry History (24h)': 'Histórico de atividade (24h)',
    'TELEMETRY HISTORY (24H)': 'HISTÓRICO DE ATIVIDADE (24H)',
    'Telemetry History (30d)': 'Histórico de atividade (30d)',
    'TELEMETRY HISTORY (30D)': 'HISTÓRICO DE ATIVIDADE (30D)',
    'Save': 'Salvar',
    'Saved': 'Salvo',
    'Saving...': 'Salvando...',
    'Save Changes': 'Salvar alterações',
    'Save Settings': 'Salvar configurações',
    'Cancel': 'Cancelar',
    'Close': 'Fechar',
    'Create': 'Criar',
    'Add': 'Adicionar',
    'Edit': 'Editar',
    'Delete': 'Excluir',
    'Remove': 'Remover',
    'Update': 'Atualizar',
    'Refresh': 'Atualizar',
    'Search': 'Buscar',
    'Search...': 'Buscar...',
    'Search items...': 'Buscar itens...',
    'Search categories...': 'Buscar categorias...',
    'Filter': 'Filtrar',
    'All': 'Tudo',
    'Export': 'Exportar',
    'Import': 'Importar',
    'Download': 'Baixar',
    'Upload': 'Enviar',
    'Copy': 'Copiar',
    'Copied': 'Copiado',
    'Copied!': 'Copiado!',
    'Preview': 'Visualizar',
    'Open': 'Aberto',
    'View': 'Ver',
    'Manage': 'Gerenciar',
    'Configure': 'Configurar',
    'Enable': 'Ativar',
    'Disable': 'Desativar',
    'Enabled': 'Ativado',
    'Disabled': 'Desativado',
    'Active': 'Ativo',
    'Inactive': 'Inativo',
    'Available': 'Disponível',
    'Unavailable': 'Indisponível',
    'Visible': 'Visível',
    'Hidden': 'Oculto',
    'Required': 'Obrigatório',
    'Optional': 'Opcional',
    'Confirm': 'Confirmar',
    'Back': 'Voltar',
    'Next': 'Avançar',
    'Done': 'Concluído',
    'Upgrade': 'Fazer upgrade',
    'Sign Out': 'Sair',
    'Exit to Main Admin': 'Voltar à administração principal',
    'Change Language': 'Mudar idioma',
    'Add Category': 'Adicionar categoria',
    'Add Menu Item': 'Adicionar item',
    'Add Item': 'Adicionar item',
    'Add Ingredient': 'Adicionar ingrediente',
    'Add Department': 'Adicionar departamento',
    'Delete Item': 'Excluir item',
    'Delete Category': 'Excluir categoria',
    'Upload Image': 'Enviar imagem',
    'Upload Photo': 'Carregar foto',
    'Select Category': 'Selecionar categoria',
    'Select Department': 'Selecionar departamento',
    'Add translation...': 'Adicionar tradução...',
    'Clear': 'Limpar',
    'Reset': 'Redefinir',
    'Apply': 'Aplicar',
    'Submit': 'Enviar',
    'Core & Operations': 'Operações e salão',
    'Menu & Experience': 'Cardápio e pratos',
    'Enterprise & Security': 'Gestão e equipe',
    'System & Control': 'Configurações e ajuda',
    'Intelligence Studio': 'Centro de análise',
    'Venue Insights': 'Relatórios de vendas',
    'Reservations': 'Reservas',
    'Floor Plan': 'Planta do salão',
    'Live Waitlist': 'Lista de espera',
    'Walk-Ins': 'Clientes sem reserva',
    'Walk-ins': 'Clientes sem reserva',
    'Departments': 'Departamentos',
    'Service Hours': 'Horário de serviço',
    'Categories': 'Categorias',
    'Menu Categories': 'Categorias do cardápio',
    'Menu Items': 'Pratos do cardápio',
    'Items': 'Itens',
    'Ingredients': 'Ingredientes',
    'Loyalty Program': 'Programa de fidelidade',
    'Campaign Studio': 'Ofertas e promoções',
    'Signage': 'Telas digitais',
    'QR Code': 'Códigos QR',
    'Kitchen Ops': 'Painel da cozinha',
    'Theme Engine': 'Cores e identidade',
    'Governance': 'Governança',
    'Audit Log': 'Histórico de atividades',
    'API & Webhooks': 'Integrações',
    'SSO & Security': 'Segurança e acesso',
    'Team': 'Equipe e funcionários',
    'Billing': 'Assinatura e faturas',
    'SEO & Google': 'Presença no Google',
    'Notifications': 'Notificações',
    'Support Center': 'Ajuda e suporte',
    'Settings': 'Configurações do local',
    'Name': 'Nome',
    'Description': 'Descrição',
    'Price': 'Preço',
    'Status': 'Status',
    'Actions': 'Ações',
    'Role': 'Função',
    'Email': 'E-mail',
    'Phone': 'Telefone',
    'Address': 'Endereço',
    'City': 'Cidade',
    'Currency': 'Moeda',
    'Language': 'Idioma',
    'Theme': 'Tema',
    'General': 'Geral',
    'Total Revenue': 'Faturamento total',
    'Total Orders': 'Total de pedidos',
    'Average Order': 'Ticket médio',
    'Active Sessions': 'Mesas ativas',
    'Recent Orders': 'Pedidos recentes',
    'Live Orders': 'Pedidos em tempo real',
    'Table': 'Mesa',
    'Date': 'Data',
    'Time': 'Hora',
    'Amount': 'Valor',
    'Customer': 'Cliente',
    'In Stock': 'Em estoque',
    'Out of Stock': 'Esgotado',
    'Sold Out': 'Esgotado',
    'Dine In': 'No salão',
    'Pickup': 'Retirada',
    'Delivery': 'Entrega',
    'Paid': 'Pago',
    'Unpaid': 'Não pago',
    'Pending': 'Pendente',
    'Preparing': 'Em preparo',
    'Ready': 'Pronto',
    'Completed': 'Concluído',
    'Cancelled': 'Cancelado',
    'Loading...': 'Carregando...',
    'No data': 'Nenhum dado',
    'No results found': 'Nenhum resultado encontrado',
    'No items found': 'Nenhum item encontrado',
    'No translations defined yet': 'Nenhuma tradução definida ainda',
    'Are you sure?': 'Tem certeza?',
    'This action cannot be undone.': 'Esta ação não pode ser desfeita.',
    'Settings saved successfully!': 'Configurações salvas com sucesso!',
    'Administrator': 'Administrador',
    'Manager': 'Gerente',
    'Owner': 'Proprietário',
    'Chef': 'Chef',
    'Waiter': 'Garçom',
    'Cashier': 'Caixa',
    'Type a command or search...': 'Digite um comando ou busque...',
    'Command Palette': 'Comandos rápidos',
    'Demand Velocity': 'Velocidade da demanda',
    'DEMAND VELOCITY': 'VELOCIDADE DA DEMANDA',
    'Top Performing Entities': 'Itens com melhor desempenho',
    'TOP PERFORMING ENTITIES': 'ITENS COM MELHOR DESEMPENHO',
    'Hourly Load Analysis': 'Análise horária de movimento',
    'HOURLY LOAD ANALYSIS': 'ANÁLISE HORÁRIA DE MOVIMENTO',
    'Bulk Command Center': 'Central de ações em lote',
    'BULK COMMAND CENTER': 'CENTRAL DE AÇÕES EM LOTE',
    'Governance Scan': 'Verificação do cardápio',
    'GOVERNANCE SCAN': 'VERIFICAÇÃO DO CARDÁPIO',
    'No compliance issues detected in current menu': 'Nenhum problema detectado no cardápio atual',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'NENHUM PROBLEMA DETECTADO NO CARDÁPIO ATUAL',
    'Procedural Tagging': 'Atribuição automática de tags',
    'PROCEDURAL TAGGING': 'ATRIBUIÇÃO AUTOMÁTICA DE TAGS',
    'Name match...': 'Buscar por nome...',
    '...Name match': 'Buscar por nome...',
    'Healthy': 'Saudável',
    'HEALTHY': 'SAUDÁVEL',
    'Apply Tags': 'Aplicar tags',
    'APPLY TAGS': 'APLICAR TAGS',
    'Global Price Shift': 'Ajuste global de preços',
    'GLOBAL PRICE SHIFT': 'AJUSTE GLOBAL DE PREÇOS',
    'Adjust': 'Ajustar',
    'ADJUST': 'AJUSTAR',
    'Availability Override': 'Controle de disponibilidade imediata',
    'AVAILABILITY OVERRIDE': 'CONTROLE DE DISPONIBILIDADE IMEDIATA',
    'Reset: ALL IN-STOCK': 'Restaurar: TUDO DISPONÍVEL',
    'RESET: ALL IN-STOCK': 'RESTAURAR: TUDO DISPONÍVEL',
    'EMERGENCY STOP (ALL OUT)': 'Parada de emergência (TUDO ESGOTADO)',
    'Integrity Engine': 'Motor de integridade do cardápio',
    'INTEGRITY ENGINE': 'MOTOR DE INTEGRIDADE DO CARDÁPIO',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Este sistema inteligente monitora a visibilidade dos itens, discrepâncias de preços e a precisão das descrições em seu estabelecimento.',
    'Inventory Audit Export': 'Exportação para auditoria de estoque',
    'INVENTORY AUDIT EXPORT': 'EXPORTAÇÃO PARA AUDITORIA DE ESTOQUE',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Gere um relatório abrangente do desempenho do cardápio e metadados para auditoria offline.',
    'Enterprise Data Export': 'Exportação avançada de dados',
    'ENTERPRISE DATA EXPORT': 'EXPORTAÇÃO AVANÇADA DE DADOS',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Gere e baixe relatórios completos de desempenho em formatos padrão. Inclui fluxo de clientes, demanda de pratos e conversão.',
    'JSON Snapshot': 'Instantâneo JSON',
    'JSON SNAPSHOT': 'INSTANTÂNEO JSON',
    'Compile CSV Report': 'Gerar relatório CSV',
    'COMPILE CSV REPORT': 'GERAR RELATÓRIO CSV',
    'Date Segment': 'Período da data',
    'DATE SEGMENT': 'PERÍODO DA DATA',
    'Inbound Views': 'Visualizações recebidas',
    'INBOUND VIEWS': 'VISUALIZAÇÕES RECEBIDAS',
    'Asset Interactions': 'Interações com os itens',
    'ASSET INTERACTIONS': 'INTERAÇÕES COM OS ITENS',
    'Telemetry History': 'Histórico de atividade',
    'TELEMETRY HISTORY': 'HISTÓRICO DE ATIVIDADE',
    'Telemetry History (7d)': 'Histórico de atividade (7d)',
    'TELEMETRY HISTORY (7D)': 'HISTÓRICO DE ATIVIDADE (7D)',
    'Growth Trajectory': 'Trajetória de crescimento',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'A frequência de interação com seu cardápio está em alta nos últimos 7 dias.',
    'Deep Analysis Engine': 'Motor de análise aprofundada',
    'DEEP ANALYSIS ENGINE': 'MOTOR DE ANÁLISE APROFUNDADA',
    'Asset Velocity': 'Desempenho dos pratos',
    'ASSET VELOCITY': 'DESEMPENHO DOS PRATOS',
    'Traffic Projections': 'Projeções de tráfego',
    'Telemetry Signal Tracking': 'Monitoramento de atividade',
    'Service Hour Update': 'Atualização de horários de serviço',
    'Late Night Lounge Hours': 'Horário da lounge noturna',
    'Bar & Lounge Hours': 'Horário do bar e lounge',
    'Breakfast & Brunch Hours': 'Horário de café da manhã e brunch',
    'Switch Location': 'Trocar de local',
    'SWITCH LOCATION': 'TROCAR DE LOCAL',
    'Global Admin': 'Administrador global',
    'GLOBAL ADMIN': 'ADMINISTRADOR GLOBAL',
    'Staff Access': 'Acesso da equipe',
    'STAFF ACCESS': 'ACESSO DA EQUIPE',
    'Venue Overview': 'Visão geral do local',
    'VENUE OVERVIEW': 'VISÃO GERAL DO LOCAL',
    'Unified Telemetry Engine': 'Painel de atividades em tempo real',
    'UNIFIED TELEMETRY ENGINE': 'PAINEL DE ATIVIDADES EM TEMPO REAL',
    'Pulse': 'Em tempo real',
    'PULSE': 'EM TEMPO REAL',
    'Insights': 'Tendências',
    'INSIGHTS': 'TENDÊNCIAS',
    'Reports': 'Relatórios',
    'REPORTS': 'RELATÓRIOS',
    'GOVERNANCE': 'GOVERNANÇA',
    'Multi-Venue': 'Multilocal',
    'MULTI-VENUE': 'MULTILOCAL',
    'Menu Views': 'Visualizações do cardápio',
    'Item Interactions': 'Interações com pratos',
    'Guest Rating': 'Avaliação dos clientes',
    'Revenue (7d)': 'Receita (7d)',
    'Revenue (24h)': 'Receita (24h)',
    'Revenue (30d)': 'Receita (30d)',
    'Delivery orders (7d)': 'Pedidos para entrega (7d)',
    'Delivery orders (24h)': 'Pedidos para entrega (24h)',
    'Delivery orders (30d)': 'Pedidos para entrega (30d)',
    'Delivery revenue': 'Receita de entregas',
    'Average delivery order': 'Ticket médio de entrega',
    'Average delivery fee': 'Taxa média de entrega',
    'Zone average': 'Média da região',
    'Including fees': 'Taxas inclusas',
    'No deliveries': 'Sem entregas',
    'No orders': 'Sem pedidos',
    'No reviews': 'Sem avaliações',
    'Live': 'Ao vivo',
    'LIVE': 'AO VIVO',
    'reviews': 'avaliações',
    'orders': 'pedidos',
    'deliveries': 'entregas',
    'of orders': 'dos pedidos',
    'rate': 'taxa',
    'Recent Activity': 'Atividade recente',
    'Real-time Venue Telemetry': 'Atividade do local em tempo real',
    'REAL-TIME VENUE TELEMETRY': 'ATIVIDADE DO LOCAL EM TEMPO REAL',
    'Rapid Pulse View': 'Visão rápida em tempo real',
    'Anonymous Guest': 'Cliente anônimo',
    'scanned menu': 'escaneou o cardápio',
    'viewed item': 'visualizou o item',
    'interacted with': 'interagiu com',
    'placed order': 'fez um pedido',
    'searched menu': 'pesquisou no cardápio',
    'Main Landing': 'Página inicial',
    'Trace': 'Rastreio',
    'TRACE': 'RASTREIO',
    'Gaining Telemetry...': 'Obtendo telemetria...',
    'No recent traffic detected': 'Nenhuma atividade recente detectada',
    'Activities will appear here as guests scan your QR code': 'As atividades aparecerão aqui assim que os clientes escanearem seu código QR',
    'Analyze All Logs': 'Analisar todos os registros',
    'Just now': 'Agora mesmo',
    'less than a minute ago': 'há menos de um minuto',
    '1 minute ago': 'há 1 minuto',
    '1 hour ago': 'há 1 hora',
    'about 1 hour ago': 'há cerca de 1 hora',
    '1 day ago': 'há 1 dia',
    'DAY AGO 1': 'há 1 dia',
    '2 days ago': 'há 2 dias',
    'DAY AGO 2': 'há 2 dias',
    '3 days ago': 'há 3 dias',
    'DAY AGO 3': 'há 3 dias',
    '4 days ago': 'há 4 dias',
    'DAY AGO 4': 'há 4 dias',
    '5 days ago': 'há 5 dias',
    'DAY AGO 5': 'há 5 dias',
    '6 days ago': 'há 6 dias',
    'DAY AGO 6': 'há 6 dias',
    '7 days ago': 'há 7 dias',
    'DAY AGO 7': 'há 7 dias',
    'about 1 month ago': 'há cerca de 1 mês',
    'Monday': 'Segunda-feira',
    'Tuesday': 'Terça-feira',
    'Wednesday': 'Quarta-feira',
    'Thursday': 'Quinta-feira',
    'Friday': 'Sexta-feira',
    'Saturday': 'Sábado',
    'Sunday': 'Domingo',
    'Mon': 'Seg',
    'Tue': 'Ter',
    'Wed': 'Qua',
    'Thu': 'Qui',
    'Fri': 'Sex',
    'Sat': 'Sáb',
    'Sun': 'Dom',
    'Opening Hours': 'Horário de funcionamento',
    'Start Time': 'Hora de início',
    'End Time': 'Hora de término',
    'Opening Time': 'Hora de abertura',
    'Closing Time': 'Hora de fechamento',
    'All Day': 'O dia todo',
    'Closed': 'Fechado',
    'Active Days': 'Dias ativos',
    'Active Schedule': 'Cronograma ativo',
    'Always Available': 'Sempre disponível',
    'Always Visible': 'Sempre visível',
    'New Category': 'Nova categoria',
    'Category': 'Categoria',
    'Category Name': 'Nome da categoria',
    'Category Description': 'Descrição da categoria',
    'Display Order': 'Ordem de exibição',
    'Item Name': 'Nome do item',
    'Item Description': 'Descrição do item',
    'Base Price': 'Preço base',
    'Preparation Time': 'Tempo de preparo',
    'Calories': 'Calorias',
    'Featured Item': 'Item em destaque',
    'Chef\'s Recommendation': 'Recomendação do chef',
    'Spicy': 'Picante',
    'Vegetarian': 'Vegetariano',
    'Vegan': 'Vegano',
    'Gluten-Free': 'Sem glúten',
    'Halal': 'Halal',
    'Kosher': 'Kasher',
    'Contains Nuts': 'Contém nozes',
    'Dairy-Free': 'Sem lactose',
    'Allergen Exclusions': 'Alérgenos',
    'Table Number': 'Número da mesa',
    'Assigned Table': 'Mesa atribuída',
    'Seats': 'Assentos',
    'Capacity': 'Capacidade',
    'Indoor': 'Interior',
    'Outdoor': 'Exterior',
    'Terrace': 'Terraço',
    'Bar': 'Bar',
    'Add Table': 'Adicionar mesa',
    'Edit Table': 'Editar mesa',
    'Delete Table': 'Excluir mesa',
    'Bookings': 'Reservas',
    'Add to Waitlist': 'Adicionar à lista de espera',
    'Guest Name': 'Nome do cliente',
    'Party Size': 'Número de pessoas',
    'Arrival Time': 'Hora de chegada',
    'Special Requests': 'Pedidos especiais',
    'Confirmed': 'Confirmado',
    'No Show': 'Não compareceu',
    'Add Staff Member': 'Adicionar funcionário',
    'Invite Staff': 'Convidar funcionário',
    'Email Address': 'Endereço de e-mail',
    'Phone Number': 'Número de telefone',
    'Download QR Code': 'Baixar código QR',
    'Print Table Stands': 'Imprimir displays de mesa',
    'Today': 'Hoje',
    'Yesterday': 'Ontem',
    'This Week': 'Esta semana',
    'This Month': 'Este mês',
    'Last 30 Days': 'Últimos 30 dias',
    'Access Denied': 'Acesso negado',
    'Advanced Settings': 'Configurações avançadas',
    'Change Image': 'Alterar imagem',
    'Remove Image': 'Remover imagem',
    'Announcement Banner': 'Banner de avisos',
    'Please fill in all required fields.': 'Preencha todos os campos obrigatórios.',
    'Changes saved successfully': 'Alterações salvas com sucesso',
    '(You)': '(Você)',
    'AI Auto-Tag': 'Auto-etiquetagem por IA',
    'Analyzing:': 'A analisar:',
    'Apple (Fruit/Vegan)': 'Maçã (Fruta/Vegano)',
    'Cancel editing': 'Cancelar edição',
    'Capacity:': 'Capacidade:',
    'Carrot (Veggie)': 'Cenoura (Legume)',
    'CircleDot (Organic)': 'Ponto circular (Biológico)',
    'Coffee (Caffeine)': 'Café (Cafeína)',
    'Configuration': 'Configuração',
    'Copy verification record name': 'Copiar nome do registo de verificação',
    'Copy verification record value': 'Copiar valor do registo de verificação',
    'Critical exception intercepted': 'Exceção crítica intercetada',
    'Kernel Panic': 'Erro de Sistema',
    'DNA (Organic/Bio)': 'ADN (Biológico/Bio)',
    'Egg (Ovo/Allergen)': 'Ovo (Ovo/Alérgeno)',
    'Empty': 'Vazio',
    'Engraving...': 'A gerar...',
    'Flame (Spicy)': 'Chama (Picante)',
    'Free': 'Grátis',
    'From': 'De',
    'Generating': 'A gerar',
    'Governance & Traceability Studio': 'Centro de gestão e rastreabilidade',
    'Greet': 'Boas-vindas',
    'Heart (Healthy)': 'Coração (Saudável)',
    'In-App': 'Na aplicação',
    'Infinity SVG': 'SVG Vetorial (Ilimitado)',
    'Leaf (Default)': 'Folha (Padrão)',
    'MAX Feature': 'Funcionalidade Enterprise',
    'Milk (Dairy)': 'Leite (Laticínios)',
    'Network': 'Rede',
    'Notify SMS': 'Notificação por SMS',
    'Order': 'Pedido',
    'Original': 'Original',
    'Overview': 'Visão geral',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Funções Padrão',
    'Shield (Safety)': 'Escudo (Segurança)',
    'Soup (Warm/Soup)': 'Sopa (Quente/Sopa)',
    'Standard Pro View': 'Vista Padrão',
    'Star': 'Estrela',
    'Synchronized with Venue Identity': 'Personalizado com a identidade do espaço',
    'Synchronizing Security Context...': 'A estabelecer ligação segura...',
    'Targeted Timing': 'Horário do serviço',
    'Targeting': 'Aplica-se a',
    'To': 'Para',
    'Toggle Navigation': 'Alternar navegação',
    'Venue': 'Espaço',
    'Venue Governance Suite': 'Gestão e configurações do espaço',
    'Volume:': 'Volume:',
    'WHITEBOARD NOTE...': 'Nota para a equipa...',
    'Warning (Alert)': 'Aviso (Alerta)',
    'Wheat (Flour/Gluten)': 'Trigo (Farinha/Glúten)',
    'Wine (Alcohol)': 'Vinho (Álcool)',
    'YouTube/Vimeo/Direct Link': 'Ligação de vídeo (YouTube, Vimeo ou MP4)',
    'Your name': 'O seu nome',
    '2. Table Number': '2. Número da mesa',
    'for Morocco or': 'para Marrocos, ou',
    'in': 'em',
    'to': 'para',
    'e.g. 12 or Patio-1': 'ex. 12 ou Esplanada-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'ex. Fettuccine artesanal, trufa preta de inverno, parmesão 36 meses...',
    'e.g. Head Sommelier & Wine Lead': 'ex. Sommelier principal',
    'e.g. London': 'ex. Lisboa',
    'e.g. SOMMELIER, HOST, BARISTA': 'ex. ESCANÇÃO, ANFITRIÃO, BARISTA',
    'e.g. Skyline Lounge': 'ex. Lounge Panorâmico',
    'e.g. TRUFFLE OIL': 'ex. AZEITE DE TRUFA',
    'e.g. The Grand Cafe': 'ex. O Grande Café',
    'e.g. Window preference, high chair': 'ex. Perto da janela, cadeira de bebé',
    'Copied to clipboard!': 'Copiado para a área de transferência!',
    'Copy failed.': 'Falha ao copiar.',
    'Link copied!': 'Ligação copiada!',
    'Logo uploaded': 'Logótipo carregado',
    'Cover image uploaded': 'Imagem de capa carregada',
    'Profile saved and synced successfully': 'Perfil guardado e sincronizado com sucesso',
    'Category Created': 'Categoria criada',
    'Category Updated': 'Categoria atualizada',
    'Category Deleted': 'Categoria eliminada',
    'Item Created': 'Artigo criado',
    'Item Updated': 'Artigo atualizado',
    'Item Deleted': 'Artigo eliminado',
    'Menu Published': 'Menu publicado',
    'Delete Category?': 'Eliminar categoria?',
    'Delete Menu Item?': 'Eliminar este artigo?',
    'Delete Ingredient?': 'Eliminar este ingrediente?',
    'Delete Service Period?': 'Eliminar período de serviço?',
    'Remove team member?': 'Remover este membro da equipa?',
    'Cancel invitation?': 'Cancelar convite?',
    'Revoke API key?': 'Revogar chave de API?',
    'Delete webhook endpoint?': 'Eliminar ponto de webhook?',
    'Clear all notifications?': 'Limpar todas as notificações?',
    'Disconnect Stripe account?': 'Desconectar conta Stripe?',
    'Disconnect Google Business Profile?': 'Desconectar perfil do Google Business?',
    'Campaigns published successfully!': 'Campanhas publicadas com sucesso!',
    'Changes successfully synchronized with Google Business Profile!': 'Alterações sincronizadas com sucesso com o Google Business Profile!',
    'Google Business Profile successfully connected!': 'Google Business Profile conectado com sucesso!',
    'Google Business Profile disconnected.': 'Google Business Profile desconectado.',
    'Google Post draft saved successfully!': 'Rascunho de publicação do Google guardado com sucesso!',
    'Post draft deleted.': 'Rascunho de publicação eliminado.',
    'Review response published successfully!': 'Resposta à avaliação publicada com sucesso!',
    'SEO configuration saved successfully!': 'Configuração de SEO guardada com sucesso!',
    'SEO metadata translated across all languages!': 'Metadados de SEO traduzidos para todos os idiomas!',
    'Venue profile & bio translated across all languages!': 'Perfil e apresentação do espaço traduzidos para todos os idiomas!',
    'Reservation configuration saved successfully': 'Configurações de reservas guardadas com sucesso',
    'Stripe settings saved successfully.': 'Configurações do Stripe guardadas com sucesso.',
    'Print-Ready PDF downloaded successfully': 'PDF para impressão transferido com sucesso',
    'Vector QR PDF downloaded successfully': 'PDF de QR vetorial transferido com sucesso',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Pacote pré-impressão 300 DPI gerado com sucesso',
    'At least one language must be enabled.': 'Pelo menos um idioma deve estar ativado.',
    'Enter a non-zero percentage before adjusting prices.': 'Introduza uma percentagem diferente de zero antes de ajustar os preços.',
    'Please enter a Venue Name before translating.': 'Introduza o nome do espaço antes de traduzir.',
    'Please enter an item name before running dietary and allergen analysis.': 'Introduza o nome do prato antes de analisar alergénios e dietas.',
    'Please write a reply before submitting.': 'Escreva uma resposta antes de submeter.',
    'Table 3 Called Waiter': 'Mesa 3 chamou o funcionário',
    'Table Call Bell': 'Campainha de mesa',
    'Waiter Calls': 'Chamadas de funcionários',
    'Waiter Dispatch & Calls': 'Gestão e chamadas de serviço',
    'Kitchen Display & Whiteboard': 'Ecrã de cozinha e quadro',
    'Customer Orders': 'Pedidos dos clientes',
    'Bakery': 'Padaria / Pastelaria',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Café / Cafetaria',
    'Fast Food': 'Fast food / Refeições rápidas',
    'Gourmet / Fine Dining': 'Alta gastronomia / Fine dining',
    'Hotel': 'Hotel',
    'General Local Business': 'Comércio local geral',
    'Classic': 'Clássico',
    'Clean': 'Elegante & Limpo',
    'Modern': 'Moderno',
    'Elegant': 'Elegante',
    'Energetic': 'Enérgico',
    'Glass': 'Efeito vidro',
    'Playful & Casual': 'Descontraído e alegre',
    'Sophisticated & Gourmet': 'Sofisticado e gourmet',
    'Warm & Welcoming': 'Acolhedor e caloroso',
    'Executive': 'Executivo',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Atualização necessária: Menus em vários idiomas estão disponíveis no plano Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Atualização necessária: Pedidos e chamadas de serviço não estão disponíveis no plano Starter.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'A exportação de vetores SVG é um recurso Pro e Max. Atualize o seu plano.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'A exportação de PDF pronto para impressão é um recurso Pro e Max. Atualize o seu plano.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'O plano Pro está limitado a 3 campanhas ativas. Pause outra campanha ou atualize para MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'O plano Starter inclui até 20 mesas (Mesas 1–20). Atualize para PRO para mesas ilimitadas.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'O plano Starter suporta mesas numéricas (1–20). Nomes personalizados (ex. Esplanada, VIP) requerem PRO.',
    'Add Menu Categories & Items': 'Adicionar categorias e artigos',
    'Add Menu Items': 'Adicionar artigos ao menu',
    'Organize your menu sections and food items.': 'Organize as secções da ementa e os seus pratos.',
    'Add and manage your food & beverage offerings.': 'Adicione e faça a gestão da sua oferta de comida e bebidas.',
    'Customize Venue Branding': 'Personalizar a identidade do espaço',
    'Set your brand colors, logo, and presentation.': 'Defina as cores da marca, logótipo e apresentação.',
    'Get Your QR Code & Go Live': 'Obtenha o seu código QR e comece a operar',
    'Download table QR codes and preview your live menu.': 'Transfira os códigos QR de mesa e pré-visualize a sua ementa online.',
    'Account & Venue Created Successfully!': 'Conta e espaço criados com sucesso!',
    '⚡ Off-Peak Hours': '⚡ Horas de menor afluência',
    '🍽️ Lunch Crowd': '🍽️ Refeições de almoço',
    '🚀 Weekend Rush': '🚀 Pico de fim de semana',
    '🌟 Seasonal Special': '🌟 Especial da estação',
    '📣 Engagement Buzz': '📣 Notoriedade e partilhas',
    'Festive & Energetic': 'Festivo e dinâmico',
    'Urgent & High-Conversion': 'Urgente e de elevada conversão',
    'Generated 3 tailored campaign strategies': 'Geradas 3 estratégias de campanha personalizadas',
    'Could not generate campaign ideas. Please try again.': 'Não foi possível gerar ideias de campanha. Tente novamente.',
    'Venue Themes': 'Temas do espaço',
    'Choose the character of your public menu': 'Escolha o estilo e caráter da sua ementa pública',
    'Glassmorphism & Depth': 'Efeito vidro e profundidade',
    'Independent XML Sitemap': 'Sitemap XML independente',
    'Structured Schema & Sync': 'Dados estruturados e sincronização',
    'Improve how your venue appears on Google and in local search results.': 'Melhore a presença do seu espaço no Google e nas pesquisas locais.',
    'Terms of Service': 'Termos de serviço',
    'Confirm your email': 'Confirme o seu e-mail',
    'Check your spam / junk folder': 'Verifique a sua pasta de spam ou lixo eletrónico',
    'Commercial pre-press print production package': 'Pacote de produção gráfica pré-impressão',
    'Over-bleed safety margin': 'Margem de segurança de sangria',
    'Crop Marks': 'Marcas de corte',
    'Guillotine cutting guides': 'Guias de corte por guilhotina',
    'Global Banners': 'Banners globais',
    'Featured Items': 'Pratos em destaque',
    'AI Assistant': 'Assistente de IA',
    'Custom Role Schemas': 'Esquemas de funções personalizados',
    'Notification Preferences': 'Preferências de notificação',
    'Preferences saved automatically': 'Preferências guardadas automaticamente',
    'Menu Extractor (PDF & Images)': 'Extrator de ementas (PDF e imagens)',
    'Multi-Language Translation': 'Tradução multilíngue',
    'Start Menu Translation': 'Iniciar tradução da ementa',
    'Menu Items Hub': 'Centro de artigos da ementa',
    'Open Menu Items': 'Abrir artigos da ementa',
    'Enterprise Traceability': 'Rastreabilidade empresarial',
    'Full History': 'Histórico completo',
    'Billing & Plans': 'Faturação e planos',
    'Subscription Governance': 'Gestão de subscrições',
    'Platform Usage': 'Utilização da plataforma',
    'Menu Page Views': 'Visualizações da ementa',
    'Enterprise Identity': 'Identidade empresarial',
    'Access Governance': 'Políticas de acesso',
    'Required Fields *': 'Campos obrigatórios *',
    'Enabled Languages': 'Idiomas ativados',
    'Venue Info & Bio Localizations': 'Traduções da descrição e informações do espaço',
    'Public Address': 'Endereço público',
    'Public URL Slug': 'Slug de URL pública',
    'Contact Phone': 'Telefone de contacto',
    'Business Address': 'Morada comercial',
    'No Categories Yet': 'Ainda sem categorias',
    'No Custom Roles Configured': 'Nenhuma função personalizada configurada',
    'No Google reviews retrieved yet': 'Ainda não foram recolhidas avaliações do Google',
    'No Guests Found': 'Nenhum cliente encontrado',
    'No Menu Items Yet': 'Ainda sem artigos na ementa',
    'No Promotions Configured': 'Nenhuma promoção configurada',
    'No Service Periods Yet': 'Ainda sem períodos de serviço',
    'No Support Tickets': 'Nenhum ticket de suporte',
    'No Tables on Floor Plan': 'Nenhuma mesa no plano da sala',
    'No Team Members Found': 'Nenhum membro da equipa encontrado',
    'No Upcoming Events': 'Nenhum evento agendado',
    'No Walk-Ins Recorded': 'Nenhum cliente sem reserva registado',
    'No items are available right now': 'Nenhum artigo disponível no momento',
    'No notifications found': 'Nenhuma notificação encontrada',
    'No reservations matching your filters': 'Nenhuma reserva corresponde aos filtros aplicados',
    'No tables currently occupied': 'Nenhuma mesa ocupada no momento',
    'No venue selected': 'Nenhum espaço selecionado',
    'Waitlist': 'Lista de espera',
    'Waitlist is Empty': 'A lista de espera está vazia',
    'Waves (Gluten)': 'Espigas (Glúten)',
    'We sent a temporary verification code to': 'Enviámos um código de verificação temporário para',
    'Website URL': 'URL do website',
    'What the customer receives': 'O que o cliente recebe',
    'White-label control is available on eligible plans.': 'A personalização de marca própria está disponível nos planos elegíveis.',
    'Why this works:': 'Porque funciona esta abordagem:',
    'WiFi Broadcast': 'Partilha de WiFi',
    'WiFi Password': 'Palavra-passe do WiFi',
    'Window seat, dietary request, birthday celebration...': 'Mesa junto à janela, preferências alimentares, aniversário...',
    'Your Published Response': 'A sua resposta publicada',
    'Your Venues': 'Os seus espaços',
    '🎉 Ready to publish!': '🎉 Pronto para publicar!',
    'e.g. Grill, Salad, Pastry': 'ex. Grelhados, Saladas, Pastelaria',
    'e.g. Guest called, emergency': 'ex. Cliente ligou, imprevisto',
    'e.g. Italian, Moroccan, Burgers': 'ex. Italiana, Marroquina, Hambúrgueres',
    'e.g. Mains, Pasta, Starters': 'ex. Pratos principais, Massas, Entradas',
    'e.g. Signature Truffle Pasta': 'ex. Massa exclusiva de trufa',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'ex. Especial de café da tarde, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'ex. Pequeno-almoço, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'ex. Junte-se a nós para Jazz ao vivo todas as sextas-feiras às 20:00!',
  },
  ru: {
    '123 Main St, City': 'ул. Центральная 123, Город',
    'Balance': 'Баланс баллов',
    'Lifetime': 'Всего накоплено',
    'Source': 'Источник',
    'Menu': 'Меню',
    'Advanced': 'Дополнительно',
    'Core Suite': 'Базовый набор',
    'Privacy': 'Конфиденциальность',
    'Support': 'Поддержка',
    'Save Layout': 'Сохранить схему зала',
    'Shape:': 'Форма стола:',
    'SEARCH LIBRARY...': 'ПОИСК ПО БАЗЕ...',
    'Search library...': 'Поиск по базе...',
    'None': 'Нет',
    'Type': 'Тип',
    'Target': 'Цель',
    'Value / Target': 'Значение / Цель',
    'London (GMT/BST)': 'Лондон (GMT/BST)',
    'New York (EST/EDT)': 'Нью-Йорк (EST/EDT)',
    'Chicago (CST/CDT)': 'Чикаго (CST/CDT)',
    'Denver (MST/MDT)': 'Денвер (MST/MDT)',
    'Dubai (GST)': 'Дубай (GST)',
    'Tokyo (JST)': 'Токио (JST)',
    'Sydney (AEST/AEDT)': 'Сидней (AEST/AEDT)',
    'Start Color': 'Начальный цвет',
    'Depth Shade': 'Глубина тени',
    'Matrix Geometry': 'Геометрия точек QR-кода',
    'Eye Architecture': 'Угловые маркеры QR-кода',
    'Brand Centerpiece': 'Логотип в центре QR-кода',
    'Scannability Shield': 'Защита читаемости QR-кода',
    'Environment Blur': 'Размытие фона',
    'Color Target:': 'Цветовой профиль:',
    'DPI:': 'DPI (Разрешение):',
    '3mm Bleed Box': 'Поле под обрез 3 мм',
    '300 DPI': '300 DPI (Высокое разрешение)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Протокол SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'Идентификатор сущности IdP (Entity ID)',
    'SSO URL': 'URL-адрес входа через SSO',
    'X.509 Certificate': 'Сертификат безопасности X.509',
    'ACS URL': 'URL-адрес сервиса ACS',
    'Entity ID': 'Идентификатор сущности',
    'Navigate': 'Перейти',
    'Select': 'Выбрать',
    'OmniSearch v1.0': 'Универсальный поиск OmniSearch',
    'Back to order review': 'Назад к проверке заказа',
    'Return to Intelligence Studio': 'Вернуться в центр аналитики',
    'View Full Notification Center': 'Открыть все уведомления',
    'Read all': 'Прочитать все',
    'Cancel department': 'Отменить отдел',
    'Save department': 'Сохранить отдел',
    'Venue Segmentation': 'Зонирование заведения',
    'Collaborate on your menu together.': 'Работайте над меню вместе со своей командой.',
    'Timeline': 'Хронология бронирований',
    'Calendar': 'Календарь броней',
    'Book': 'Забронировать',
    'Duration': 'Длительность посадки',
    'Time & Duration': 'Время и длительность',
    'Seating Preference': 'Пожелания по посадке',
    'Section': 'Секция зала',
    'Min Capacity': 'Мин. количество гостей',
    'Max Capacity': 'Макс. количество гостей',
    'Phone (Optional)': 'Телефон (Необязательно)',
    'Name or Walk-in': 'Имя гостя или без брони',
    'Next day': 'Следующий день',
    'Next month': 'Следующий месяц',
    'Live Connection': 'Прямое соединение',
    'Live Inventory': 'Остатки в реальном времени',
    'Main Venue (Full Menu)': 'Основное заведение (Полное меню)',
    'Preview Menu': 'Предпросмотр меню',
    'Manual Setup Guide': 'Пошаговое руководство по настройке',
    'Connected & Active': 'Подключено и активно',
    'CTA Action': 'Кнопка действия (CTA)',
    'Phone Contact': 'Контактный телефон',
    'Menu URL': 'Веб-ссылка на меню',
    'Google Location': 'Точка на карте Google',
    'Field': 'Поле',
    'Linked': 'Связано',
    'Synchronizing Hub...': 'Синхронизация данных...',
    'Data Integrity': 'Целостность данных',
    'Station Governance Suite': 'Управление рабочими станциями кухни и бара',
    'Open audit log': 'Открыть журнал аудита',
    'Signal / Time': 'Событие / Время',
    'Identity': 'Пользователь / Субъект',
    'Invitation Invalid': 'Приглашение недействительно или истекло',
    'Invited To': 'Вас пригласили в',
    'Team Limit Reached': 'Лимит участников команды исчерпан',
    'Venue Limit Reached': 'Лимит заведений исчерпан',
    'Venue Setup Required': 'Требуется завершить настройку заведения',
    'Create Venue': 'Создать заведение',
    'TableGreet Onboarding': 'Начало работы с TableGreet',
    'No payment required': 'Оплата не требуется',
    'Selected Plan': 'Выбранный тариф',
    'Payment Method': 'Способ оплаты',
    'Invoice ID': 'Номер счета/фактуры',
    'Verified': 'Подтверждено',
    'Accent color': 'Акцентный цвет',
    'Set the voice and overall light level.': 'Задайте тон и уровень освещения темы.',
    'Layout architecture': 'Архитектура макета меню',
    'Navigation & motion': 'Навигация и анимация',
    'Loading preview engine': 'Загрузка движка предпросмотра',
    'Real Live Preview': 'Реальный предпросмотр вживую',
    'Included with Pro & Max': 'Входит в тарифы Pro и Max',
    'Daypart Scheduling': 'Расписание по времени суток',
    'Operational Daypart Architecture': 'Архитектура операционных смен',
    'Service Protocol': 'Протокол обслуживания',
    'Configuration Engine': 'Движок настроек',
    'Linked Department': 'Связанный отдел',
    'Team Hub': 'Управление командой',
    'Team Size': 'Численность команды',
    'Joined': 'Присоединился',
    'Cancel Invitation': 'Отозвать приглашение',
    'Initialize New Organization': 'Создать новую организацию',
    'Operational': 'В рабочем режиме',
    'Pro Feature': 'Функция тарифа Pro',
    'Current State': 'Текущее состояние',
    'Open on the big TV': 'Открыть на большом экране ТВ',
    'Menu Board': 'Цифровое меню-борд',
    'Reward': 'Награда',
    'Reward name': 'Название награды',
    'Cancel & refund': 'Отменить и оформить возврат',
    'Code': 'Код',
    'Find': 'Найти',
    'Complete': 'Завершено',
    'Seated': 'Сидят за столом',
    'No-Show': 'Неявка (No-Show)',
    'Walk-in': 'Без бронирования',
    'Party': 'Гостей в группе',
    'Turnover': 'Оборачиваемость столов',
    'We couldn\'t load the menu': 'Не удалось загрузить меню',
    'Venue Not Found': 'Заведение не найдено',
    'Live Availability': 'Доступность в реальном времени',
    'Multi-Language Suite': 'Многоязычный модуль',
    'Key name': 'Название ключа',
    'Revoke API key': 'Отозвать API-ключ',
    'Delete webhook': 'Удалить вебхук',
    'Identity Provider': 'Провайдер идентификации (IdP)',
    'Provider': 'Провайдер',
    'Mandatory MFA': 'Обязательная двухфакторная аутентификация (MFA)',
    'Account Identity': 'Данные учетной записи',
    'Login Email': 'Email для входа',
    'New Login Email': 'Новый email для входа',
    'Phone Login': 'Вход по номеру телефона',
    'SMS Verification Code': 'Код подтверждения из SMS',
    'Integration Method': 'Способ интеграции',
    'Stripe Connect': 'Платежи через Stripe Connect',
    'Stripe Secret Key': 'Секретный ключ Stripe',
    'Stripe Webhook Secret': 'Секрет вебхука Stripe',
    'Client-Owned': 'Управляется клиентом',
    'Verify connection': 'Проверить подключение',
    'Setup Health': 'Состояние настроек',
    'Privacy Protected': 'Конфиденциальность защищена',
    'Privacy Policy': 'Политика конфиденциальности',
    'Continue': 'Продолжить',
    'Sign In': 'Войти',
    'Didn\'t get it?': 'Не получили код?',
    'Logged In Account': 'Активная учетная запись',
    'Annual': 'Годовой тариф',
    'Unread': 'Непрочитанные',
    'Starred': 'Избранные',
    'High / Urgent': 'Высокий / Срочный',
    'Mark Read': 'Отметить прочитанным',
    'Delete notification': 'Удалить уведомление',
    'Urgent': 'Срочно',
    'High': 'Высокий',
    'Normal': 'Обычный',
    'Low': 'Низкий',
    'Select a Ticket': 'Выберите обращение в поддержку',
    'Urgent (Immediate attention)': 'Срочно (Требует немедленного внимания)',
    'Type your reply here...': 'Введите ваш ответ здесь...',
    'Print Card (PDF)': 'Печать тейбл-тента (PDF)',
    'Advanced Printing Engine': 'Профессиональная печатная подготовка',
    'Multi-Venue Governance': 'Управление сетью заведений',
    'Network Revenue': 'Выручка всей сети заведений',
    'Live Network Total': 'Текущий итог по сети в реальном времени',
    'Audit Log Hub': 'Журнал аудита действий',
    'Export Log': 'Экспортировать журнал',
    'CUSTOM ROLE SCHEMAS': 'ПОЛЬЗОВАТЕЛЬСКИЕ РОЛИ И ПРАВА',
    'Unique internal role identifier.': 'Уникальный внутренний идентификатор роли.',
    'Human-friendly role label.': 'Понятное название должности для сотрудников.',
    'Marketing Hub': 'Центр маркетинга',
    'Revenue Generation Suite': 'Инструменты увеличения выручки',
    'Active Campaign Limit Reached (3/3)': 'Лимит активных акций исчерпан (3/3)',
    'Banner Content': 'Текст баннера',
    'Style Profile': 'Визуальный стиль',
    'Action URL (Optional)': 'Ссылка для перехода (Необязательно)',
    'Promotion Label': 'Название промо-акции',
    'Target Selection': 'Выбор целевых блюд',
    'Edit Promotion': 'Редактировать акцию',
    'Delete Promotion': 'Удалить акцию',
    'Loyalty Engine': 'Программа лояльности',
    'Reward catalog': 'Каталог наград',
    'Add reward': 'Добавить награду',
    'Loyalty ID': 'Номер карты лояльности',
    'No point activity yet': 'Движения баллов пока нет',
    'Scan or enter QR value': 'Отсканируйте или введите код вручную',
    'TableGreet Admin': 'Панель управления TableGreet',
    'Capacity Pacing': 'Управление загрузкой зала',
    'Cancellation Policy': 'Правила отмены бронирования',
    'Booking Experience': 'Процесс бронирования столов',
    'Floor Occupancy': 'Заполняемость зала',
    'Currently Seated': 'Гости за столами',
    'Seat Party': 'Посадить за стол',
    'Seat Table': 'Занять стол',
    'Select Table': 'Выбрать стол',
    'Table / Capacity': 'Стол / Вместимость',
    'Table Name/Number': 'Название или номер стола',
    'Mark Arrived': 'Отметить прибытие',
    'Mark No-Show': 'Отметить неявку',
    'Complete Dining': 'Завершить обслуживание стола',
    'Standard Dining (Default)': 'Обычная посадка в зале (По умолчанию)',
    'Walk-In Dining Log': 'Журнал гостей без брони',
    'Notification Center': 'Центр уведомлений',
    'Single Sign-On': 'Единый вход (SSO)',
    'Welcome Aboard!': 'Добро пожаловать в TableGreet!',
    'Select your TableGreet plan': 'Выберите подходящий тариф TableGreet',
    'Free Plan Activation': 'Активация бесплатного тарифа',
    'Venue Created!': 'Заведение успешно создано!',
    'Upgrade for Unlimited': 'Перейти на безлимитный тариф',
    'Auto-Tag Menu': 'Авторазметка меню тегами',
    'Start Auto-Tagging': 'Начать авторазметку',
    'Launch Auto-Tagger': 'Запустить авторазметчик',
    'Launch PDF Importer': 'Импорт меню из PDF',
    'Analyzing Menu...': 'Анализ меню...',
    'Analyzing Recipe...': 'Анализ рецепта и ингредиентов...',
    'Manual Availability': 'Ручное управление доступностью',
    'Technical Breakdown': 'Подробные характеристики',
    'Dietary Taxonomy': 'Аллергены и типы питания',
    'Dietary & Allergen AI': 'ИИ-анализ аллергенов и диет',
    'Inherit from Category': 'Наследовать из категории',
    'Ingredient Library': 'Библиотека ингредиентов',
    'Ingredient Name': 'Название ингредиента',
    'Menu Organization': 'Структура и организация меню',
    'Inventory Taxonomy Hub': 'Центр классификации меню',
    'Category Intelligence': 'Умная аналитика категории',
    'Service Period': 'Период обслуживания',
    'Manual Configuration / All Day': 'Вручную / Доступно весь день',
    'Operational Department': 'Операционный отдел',
    'Global / No Department': 'Общий / Без привязки к отделу',
    'Cancel category': 'Отменить категорию',
    'New category name...': 'Название новой категории...',
    'Save category': 'Сохранить категорию',
    'QR Matrix Only': 'Только QR-код',
    'Vector & PDF': 'Векторные форматы и PDF',
    'Vector QR (SVG)': 'Векторный QR-код (SVG)',
    'Vector QR (PDF)': 'QR-код для печати (PDF)',
    'Save QR Style': 'Сохранить стиль QR-кода',
    'Share Link': 'Поделиться ссылкой',
    'High Fidelity 300DPI': 'Высокое качество печати (300 DPI)',
    'Generating 300 DPI PDF...': 'Создание PDF в качестве 300 DPI...',
    'Give your venue a point of view.': 'Придайте вашему заведению неповторимый визуальный стиль.',
    'Signage Menu Not Found': 'Меню для цифровых экранов не найдено',
    'Scan Menu': 'Сканировать меню',
    'Open on your phone': 'Открыть на телефоне',
    'Full Screen': 'Во весь экран',
    'Lock TV board': 'Зафиксировать ТВ-экран',
    'Big-Screen Menu': 'Меню для больших экранов',
    'TV Signage': 'Цифровые вывески для ТВ',
    'General Information': 'Общая информация',
    'Venue Name *': 'Название заведения *',
    'Public Slug *': 'Публичная веб-ссылка *',
    'Venue Logo': 'Логотип заведения',
    'Hero Cover Image': 'Главная обложка меню',
    'Venue Timezone': 'Часовой пояс заведения',
    'Primary Brand Color': 'Основной фирменный цвет',
    'TableGreet footer credit': 'Подпись TableGreet в подвале',
    'White-Label Branding': 'White-Label брендинг',
    'Client DNS Setup': 'Настройка DNS домена',
    'Copy CNAME target': 'Скопировать цель CNAME',
    'Copy TableGreet URL': 'Скопировать ссылку TableGreet',
    'Included TableGreet URL': 'Стандартная веб-ссылка TableGreet',
    'Open TableGreet URL': 'Открыть ссылку TableGreet',
    'Public menu': 'Публичное меню для гостей',
    'Public venue delivery': 'Доставка заказов из публичного меню',
    'Zone name': 'Название зоны доставки',
    'Coverage note': 'Примечание к зоне охвата',
    'Default ETA min': 'Минимальное время доставки (мин)',
    'Default ETA max': 'Максимальное время доставки (мин)',
    'Packaging fee': 'Плата за упаковку',
    'Courier handled by': 'Служба доставки',
    'Third-party courier': 'Сторонняя курьерская служба',
    'Unified Experience': 'Единый интерфейс для гостей',
    'Save WiFi to Venue': 'Сохранить Wi-Fi заведения',
    'Network Name (SSID)': 'Имя сети Wi-Fi (SSID)',
    'Audio Alert Chime': 'Звуковой сигнал уведомлений',
    'Time Format': 'Формат времени',
    'Auto / Default': 'Автоматически / По умолчанию',
    'Syncing SEO & Google Data...': 'Синхронизация данных SEO и Google...',
    'Google Connection': 'Подключение к Google',
    'Search Engine Metadata': 'Метаданные для поисковых систем',
    'Social card preview': 'Предпросмотр в соцсетях',
    'Allow Indexing': 'Разрешить индексацию',
    'Canonical URL Override': 'Основной канонический URL',
    'Price Range': 'Ценовой диапазон',
    'Latitude': 'Широта',
    'Longitude': 'Долгота',
    'Google Place ID / Location ID': 'Идентификатор места Google (Place ID)',
    'Google Search Preview': 'Предпросмотр в поиске Google',
    'Linked Google Location': 'Связанная локация в Google',
    'SEO Documentation': 'Документация по SEO',
    ', and': ', и',
    'and': 'и',
    'Delete Department?': 'Удалить отдел?',
    'Delete Department': 'Удалить отдел',
    'Live URL': 'Прямая ссылка',
    'Copy full live URL': 'Скопировать полную ссылку',
    'Open live department menu': 'Открыть меню отдела онлайн',
    'Loading departments...': 'Загрузка отделов...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Сегментация заведения по отделам доступна на тарифе PRO и выше.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Разделите меню по залам, столам, барам или отдельным зонам.',
    'Targeted Ordering': 'Маршрутизация заказов',
    'Route guests and orders to specific preparation areas.': 'Направляйте гостей и заказы в конкретные зоны кухни или бара.',
    'Operational Departments': 'Операционные отделы',
    'OPERATIONAL DEPARTMENTS': 'ОПЕРАЦИОННЫЕ ОТДЕЛЫ',
    'Venue Segmentation Hub': 'Центр зонирования заведения',
    'VENUE SEGMENTATION HUB': 'ЦЕНТР ЗОНИРОВАНИЯ ЗАВЕДЕНИЯ',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Создавайте подразделения для группировки меню. Например, в большом заведении могут быть',
    'Restaurant': 'Ресторан',
    'Poolside': 'Бассейн',
    'department, each with its own categories.': ', каждое со своими категориями блюд.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Подразделения позволяют разделить заведение на зоны (ресторан, бар, бассейн) со своими категориями блюд.',
    'Department name': 'Название отдела',
    'New department name...': 'Новое название отдела...',
    'No Linked Daypart (Always Available)': 'Без привязки к часам (доступно всегда)',
    'Linked Daypart': 'Связанные часы работы',
    'Localizations': 'Локализация',
    'LOCALIZATIONS': 'ЛОКАЛИЗАЦИЯ',
    'AI Auto-Translate': 'Автоперевод с помощью ИИ',
    'AI AUTO-TRANSLATE': 'АВТОПЕРЕВОД С ПОМОЩЬЮ ИИ',
    'Translated name': 'Переведенное название',
    'No Departments Yet': 'Отделы еще не созданы',
    'Add First Department': 'Добавить первый отдел',
    'Avg Order Value': 'Средний чек',
    'AVG ORDER VALUE': 'СРЕДНИЙ ЧЕК',
    'item taps': 'нажатий на блюда',
    'ITEM TAPS': 'НАЖАТИЙ НА БЛЮДА',
    'Avg Rating': 'Средняя оценка',
    'AVG RATING': 'СРЕДНЯЯ ОЦЕНКА',
    'Revenue (Last 7 Days)': 'Выручка (последние 7 дней)',
    'REVENUE (LAST 7 DAYS)': 'ВЫРУЧКА (ПОСЛЕДНИЕ 7 ДНЕЙ)',
    'Daily breakdown': 'По дням',
    'DAILY BREAKDOWN': 'ПО ДНЯМ',
    'Menu Views (Last 7 Days)': 'Просмотры меню (последние 7 дней)',
    'MENU VIEWS (LAST 7 DAYS)': 'ПРОСМОТРЫ МЕНЮ (ПОСЛЕДНИЕ 7 ДНЕЙ)',
    'Guest traffic': 'Поток гостей',
    'GUEST TRAFFIC': 'ПОТОК ГОСТЕЙ',
    'Most Viewed Items': 'Самые просматриваемые блюда',
    'MOST VIEWED ITEMS': 'САМЫЕ ПРОСМАТРИВАЕМЫЕ БЛЮДА',
    'Top Rated Items': 'Самые высокооцененные блюда',
    'TOP RATED ITEMS': 'САМЫЕ ВЫСОКООЦЕНЕННЫЕ БЛЮДА',
    'Waiter Requests': 'Вызовы официанта',
    'WAITER REQUESTS': 'ВЫЗОВЫ ОФИЦИАНТА',
    'Total': 'Всего',
    'TOTAL': 'ВСЕГО',
    'Order Status (30 days)': 'Статус заказов (30 дней)',
    'ORDER STATUS (30 DAYS)': 'СТАТУС ЗАКАЗОВ (30 ДНЕЙ)',
    'Recent Guest Feedback': 'Свежие отзывы гостей',
    'RECENT GUEST FEEDBACK': 'СВЕЖИЕ ОТЗЫВЫ ГОСТЕЙ',
    'Conversion Rate': 'Коэффициент конверсии',
    'CONVERSION RATE': 'КОЭФФИЦИЕНТ КОНВЕРСИИ',
    'of menu visitors placed an order': 'посетителей меню оформили заказ',
    'Refreshed': 'Обновлено',
    'REFRESHED': 'ОБНОВЛЕНО',
    'Last 30 days': 'Последние 30 дней',
    'LAST 30 DAYS': 'ПОСЛЕДНИЕ 30 ДНЕЙ',
    'cash': 'наличные',
    'Cash': 'Наличные',
    'CASH': 'НАЛИЧНЫЕ',
    'bill': 'счет',
    'Bill': 'Счет',
    'BILL': 'СЧЕТ',
    'water': 'вода',
    'Water': 'Вода',
    'WATER': 'ВОДА',
    'service': 'обслуживание',
    'Service': 'Обслуживание',
    'SERVICE': 'ОБСЛУЖИВАНИЕ',
    'No interaction data yet': 'Данные о взаимодействиях пока отсутствуют',
    'No ratings collected yet': 'Оценки пока не собраны',
    'No requests in period': 'Запросов за период не поступало',
    'No feedback collected yet': 'Отзывы гостей пока отсутствуют',
    'No menu views recorded yet': 'Просмотры меню еще не зафиксированы',
    'Dish Rating Breakdown': 'Анализ оценок блюд',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Рейтинг блюд и показатели удовлетворенности клиентов доступны на тарифах MAX и Enterprise.',
    'Customer Sentiment & Reviews': 'Тональность и отзывы гостей',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Прямые отзывы гостей и интеллектуальный анализ мнений требуют подписки MAX или Enterprise.',
    'Reviews AI': 'ИИ анализа отзывов',
    'Guest Feedback': 'Отзывы гостей',
    'Upgrade to unlock rating analytics →': 'Перейдите на более высокий тариф для аналитики оценок →',
    'Upgrade to Max': 'Перейти на тариф Max',
    'UPGRADE TO MAX': 'ПЕРЕЙТИ НА ТАРИФ MAX',
    'MAX PLAN': 'ТАРИФ MAX',
    'No Data Yet': 'Данных пока нет',
    'Share your QR code to start collecting real venue insights.': 'Поделитесь QR-кодом, чтобы начать собирать реальную аналитику посещаемости.',
    'Revenue Analytics': 'Аналитика выручки',
    'REVENUE ANALYTICS': 'АНАЛИТИКА ВЫРУЧКИ',
    'Track your earnings, orders, and average ticket values over time.': 'Отслеживайте выручку, количество заказов и средний чек во времени.',
    'Operational Velocity': 'Операционная скорость',
    'OPERATIONAL VELOCITY': 'ОПЕРАЦИОННАЯ СКОРОСТЬ',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Контролируйте поток гостей, популярность блюд и нагрузку на официантов.',
    'Upgrade to Pro': 'Перейти на Pro',
    'UPGRADE TO PRO': 'ПЕРЕЙТИ НА ТАРИФ PRO',
    'Loading Insights…': 'Загрузка аналитики…',
    'Loading Insights...': 'Загрузка аналитики...',
    'Telemetry Unavailable': 'Данные активности недоступны',
    'TELEMETRY UNAVAILABLE': 'ДАННЫЕ АКТИВНОСТИ НЕДОСТУПНЫ',
    'No Data Captured': 'Данные еще не собраны',
    'NO DATA CAPTURED': 'ДАННЫЕ ЕЩЕ НЕ СОБРАНЫ',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Поделитесь своим QR-кодом, чтобы отслеживать активность гостей и заказы в реальном времени.',
    'INTELLIGENCE STUDIO': 'ЦЕНТР АНАЛИТИКИ',
    'Insights Engine Locked': 'Расширенная аналитика заблокирована',
    'INSIGHTS ENGINE LOCKED': 'РАСШИРЕННАЯ АНАЛИТИКА ЗАБЛОКИРОВАНА',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Расширенный анализ трендов, массовые операции и выгрузка отчетов доступны для тарифа',
    'Unlock Pro Features': 'Разблокировать функции Pro',
    'UNLOCK PRO FEATURES': 'РАЗБЛОКИРОВАТЬ ФУНКЦИИ PRO',
    'MENU VIEWS': 'ПРОСМОТРЫ МЕНЮ',
    'ITEM INTERACTIONS': 'ВЗАИМОДЕЙСТВИЯ С БЛЮДАМИ',
    'GUEST RATING': 'ОЦЕНКА ГОСТЕЙ',
    'DELIVERY REVENUE': 'ВЫРУЧКА ОТ ДОСТАВКИ',
    'AVERAGE DELIVERY ORDER': 'СРЕДНИЙ ЧЕК ДОСТАВКИ',
    'AVERAGE DELIVERY FEE': 'СРЕДНЯЯ СТОИМОСТЬ ДОСТАВКИ',
    'INCLUDING FEES': 'ВКЛЮЧАЯ СБОРЫ',
    'ZONE AVERAGE': 'СРЕДНЕЕ ПО ЗОНЕ',
    'TRAFFIC PROJECTIONS': 'ПРОГНОЗЫ ПОСЕЩАЕМОСТИ',
    'TELEMETRY SIGNAL TRACKING': 'ОТСЛЕЖИВАНИЕ СИГНАЛОВ АКТИВНОСТИ',
    'VEGETARIAN': 'ВЕГЕТАРИАНСКОЕ',
    'VEGAN': 'ВЕГАНСКОЕ',
    'SPICY': 'ОСТРОЕ',
    'GLUTEN-FREE': 'БЕЗ ГЛЮТЕНА',
    'Reset: All In-Stock': 'Сброс: все блюда в наличии',
    'Emergency Stop (All Out)': 'Экстренная остановка: все блюда на стопе',
    'No compliance issues': 'Замечаний не обнаружено',
    'NO COMPLIANCE ISSUES': 'ЗАМЕЧАНИЙ НЕ ОБНАРУЖЕНО',
    'detected in current menu': 'в текущем меню',
    'DETECTED IN CURRENT MENU': 'В ТЕКУЩЕМ МЕНЮ',
    'Telemetry History (24h)': 'История активности (24ч)',
    'TELEMETRY HISTORY (24H)': 'ИСТОРИЯ АКТИВНОСТИ (24Ч)',
    'Telemetry History (30d)': 'История активности (30д)',
    'TELEMETRY HISTORY (30D)': 'ИСТОРИЯ АКТИВНОСТИ (30Д)',
    'Save': 'Сохранить',
    'Saved': 'Сохранено',
    'Saving...': 'Сохранение...',
    'Save Changes': 'Сохранить изменения',
    'Save Settings': 'Сохранить настройки',
    'Cancel': 'Отмена',
    'Close': 'Закрыть',
    'Create': 'Создать',
    'Add': 'Добавить',
    'Edit': 'Редактировать',
    'Delete': 'Удалить',
    'Remove': 'Убрать',
    'Update': 'Обновить',
    'Refresh': 'Обновить',
    'Search': 'Поиск',
    'Search...': 'Поиск...',
    'Search items...': 'Поиск блюд...',
    'Search categories...': 'Поиск категорий...',
    'Filter': 'Фильтр',
    'All': 'Все',
    'Export': 'Экспорт',
    'Import': 'Импорт',
    'Download': 'Скачать',
    'Upload': 'Загрузить',
    'Copy': 'Копировать',
    'Copied': 'Скопировано',
    'Copied!': 'Скопировано!',
    'Preview': 'Предпросмотр',
    'Open': 'Открыто',
    'View': 'Просмотр',
    'Manage': 'Управление',
    'Configure': 'Настроить',
    'Enable': 'Включить',
    'Disable': 'Отключить',
    'Enabled': 'Включено',
    'Disabled': 'Отключено',
    'Active': 'Активно',
    'Inactive': 'Неактивно',
    'Available': 'Доступно',
    'Unavailable': 'Недоступно',
    'Visible': 'Видимый',
    'Hidden': 'Скрыто',
    'Required': 'Обязательно',
    'Optional': 'Необязательно',
    'Confirm': 'Подтвердить',
    'Back': 'Назад',
    'Next': 'Далее',
    'Done': 'Готово',
    'Upgrade': 'Улучшить тариф',
    'Sign Out': 'Выйти',
    'Exit to Main Admin': 'Назад в панель управления',
    'Change Language': 'Сменить язык',
    'Add Category': 'Добавить категорию',
    'Add Menu Item': 'Добавить блюдо',
    'Add Item': 'Добавить',
    'Add Ingredient': 'Добавить ингредиент',
    'Add Department': 'Добавить отдел',
    'Delete Item': 'Удалить блюдо',
    'Delete Category': 'Удалить категорию',
    'Upload Image': 'Загрузить фото',
    'Upload Photo': 'Загрузить фото',
    'Select Category': 'Выберите категорию',
    'Select Department': 'Выберите отдел',
    'Add translation...': 'Добавить перевод...',
    'Clear': 'Очистить',
    'Reset': 'Сбросить',
    'Apply': 'Применить',
    'Submit': 'Отправить',
    'Core & Operations': 'Обслуживание и зал',
    'Menu & Experience': 'Меню и блюда',
    'Enterprise & Security': 'Управление и команда',
    'System & Control': 'Настройки и помощь',
    'Intelligence Studio': 'Центр аналитики',
    'Venue Insights': 'Отчеты о продажах',
    'Reservations': 'Бронирования',
    'Floor Plan': 'План зала',
    'Live Waitlist': 'Лист ожидания',
    'Walk-Ins': 'Гости без брони',
    'Walk-ins': 'Гости без брони',
    'Departments': 'Отделы',
    'Service Hours': 'Время обслуживания',
    'Categories': 'Категории',
    'Menu Categories': 'Категории меню',
    'Menu Items': 'Блюда меню',
    'Items': 'Блюда',
    'Ingredients': 'Ингредиенты',
    'Loyalty Program': 'Программа лояльности',
    'Campaign Studio': 'Акции и скидки',
    'Signage': 'Экраны в зале',
    'QR Code': 'QR-коды',
    'Kitchen Ops': 'Экран кухни',
    'Theme Engine': 'Оформление меню',
    'Governance': 'Управление филиалами',
    'Audit Log': 'История действий',
    'API & Webhooks': 'Интеграции',
    'SSO & Security': 'Безопасность входа',
    'Team': 'Сотрудники',
    'Billing': 'Подписка и счета',
    'SEO & Google': 'Поиск Google',
    'Notifications': 'Уведомления',
    'Support Center': 'Помощь и поддержка',
    'Settings': 'Настройки заведения',
    'Name': 'Название',
    'Description': 'Описание',
    'Price': 'Цена',
    'Status': 'Статус',
    'Actions': 'Действия',
    'Role': 'Роль',
    'Email': 'Эл. почта',
    'Phone': 'Телефон',
    'Address': 'Адрес',
    'City': 'Город',
    'Currency': 'Валюта',
    'Language': 'Язык',
    'Theme': 'Тема',
    'General': 'Общие',
    'Total Revenue': 'Общая выручка',
    'Total Orders': 'Всего заказов',
    'Average Order': 'Средний чек',
    'Active Sessions': 'Активные столы',
    'Recent Orders': 'Недавние заказы',
    'Live Orders': 'Текущие заказы',
    'Table': 'Стол',
    'Date': 'Дата',
    'Time': 'Время',
    'Amount': 'Сумма',
    'Customer': 'Гость',
    'In Stock': 'В наличии',
    'Out of Stock': 'Нет в наличии',
    'Sold Out': 'Распродано',
    'Dine In': 'В зале',
    'Pickup': 'Самовывоз',
    'Delivery': 'Доставка',
    'Paid': 'Оплачено',
    'Unpaid': 'Не оплачено',
    'Pending': 'Ожидает',
    'Preparing': 'Готовится',
    'Ready': 'Готово',
    'Completed': 'Завершено',
    'Cancelled': 'Отменено',
    'Loading...': 'Загрузка...',
    'No data': 'Нет данных',
    'No results found': 'Ничего не найдено',
    'No items found': 'Блюда не найдены',
    'No translations defined yet': 'Переводы еще не добавлены',
    'Are you sure?': 'Вы уверены?',
    'This action cannot be undone.': 'Это действие нельзя отменить.',
    'Settings saved successfully!': 'Настройки успешно сохранены!',
    'Administrator': 'Администратор',
    'Manager': 'Менеджер',
    'Owner': 'Владелец',
    'Chef': 'Шеф-повар',
    'Waiter': 'Официант',
    'Cashier': 'Кассир',
    'Type a command or search...': 'Введите команду или запрос...',
    'Command Palette': 'Палитра команд',
    'Demand Velocity': 'Скорость спроса',
    'DEMAND VELOCITY': 'СКОРОСТЬ СПРОСА',
    'Top Performing Entities': 'Самые популярные позиции',
    'TOP PERFORMING ENTITIES': 'САМЫЕ ПОПУЛЯРНЫЕ ПОЗИЦИИ',
    'Hourly Load Analysis': 'Почасовой анализ посещаемости',
    'HOURLY LOAD ANALYSIS': 'ПОЧАСОВОЙ АНАЛИЗ ПОСЕЩАЕМОСТИ',
    'Bulk Command Center': 'Центр массовых операций',
    'BULK COMMAND CENTER': 'ЦЕНТР МАССОВЫХ ОПЕРАЦИЙ',
    'Governance Scan': 'Проверка качества меню',
    'GOVERNANCE SCAN': 'ПРОВЕРКА КАЧЕСТВА МЕНЮ',
    'No compliance issues detected in current menu': 'В текущем меню проблем не обнаружено',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'В ТЕКУЩЕМ МЕНЮ ПРОБЛЕМ НЕ ОБНАРУЖЕНО',
    'Procedural Tagging': 'Автоматическая расстановка меток',
    'PROCEDURAL TAGGING': 'АВТОМАТИЧЕСКАЯ РАССТАНОВКА МЕТОК',
    'Name match...': 'Поиск по названию...',
    '...Name match': 'Поиск по названию...',
    'Healthy': 'Полезное',
    'HEALTHY': 'ПОЛЕЗНОЕ',
    'Apply Tags': 'Применить метки',
    'APPLY TAGS': 'ПРИМЕНИТЬ МЕТКИ',
    'Global Price Shift': 'Общее изменение цен',
    'GLOBAL PRICE SHIFT': 'ОБЩЕЕ ИЗМЕНЕНИЕ ЦЕН',
    'Adjust': 'Применить',
    'ADJUST': 'ПРИМЕНИТЬ',
    'Availability Override': 'Срочное управление наличием',
    'AVAILABILITY OVERRIDE': 'СРОЧНОЕ УПРАВЛЕНИЕ НАЛИЧИЕМ',
    'Reset: ALL IN-STOCK': 'Сброс: ВСЁ В НАЛИЧИИ',
    'RESET: ALL IN-STOCK': 'СБРОС: ВСЁ В НАЛИЧИИ',
    'EMERGENCY STOP (ALL OUT)': 'Экстренная остановка (ВСЁ РАСПРОДАНО)',
    'Integrity Engine': 'Система контроля меню',
    'INTEGRITY ENGINE': 'СИСТЕМА КОНТРОЛЯ МЕНЮ',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Автоматизированная система отслеживает видимость блюд, ошибки в ценах и полноту описаний во всех заведениях сети.',
    'Inventory Audit Export': 'Экспорт для инвентаризации',
    'INVENTORY AUDIT EXPORT': 'ЭКСПОРТ ДЛЯ ИНВЕНТАРИЗАЦИИ',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Создание подробного отчета о показателях меню и ценах для аудита без подключения к сети.',
    'Enterprise Data Export': 'Корпоративный экспорт данных',
    'ENTERPRISE DATA EXPORT': 'КОРПОРАТИВНЫЙ ЭКСПОРТ ДАННЫХ',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Скачивайте подробные аудиторские отчеты в стандартных форматах, включая посещаемость, спрос на блюда и конверсию.',
    'JSON Snapshot': 'Снимок JSON',
    'JSON SNAPSHOT': 'СНИМОК JSON',
    'Compile CSV Report': 'Сформировать отчет CSV',
    'COMPILE CSV REPORT': 'СФОРМИРОВАТЬ ОТЧЕТ CSV',
    'Date Segment': 'Временной отрезок',
    'DATE SEGMENT': 'ВРЕМЕННОЙ ОТРЕЗОК',
    'Inbound Views': 'Входящие просмотры',
    'INBOUND VIEWS': 'ВХОДЯЩИЕ ПРОСМОТРЫ',
    'Asset Interactions': 'Действия с блюдами',
    'ASSET INTERACTIONS': 'ДЕЙСТВИЯ С БЛЮДАМИ',
    'Telemetry History': 'История активности',
    'TELEMETRY HISTORY': 'ИСТОРИЯ АКТИВНОСТИ',
    'Telemetry History (7d)': 'История активности (7д)',
    'TELEMETRY HISTORY (7D)': 'ИСТОРИЯ АКТИВНОСТИ (7Д)',
    'Growth Trajectory': 'Траектория роста',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Активность взаимодействия гостей с меню растет за последние 7 дней.',
    'Deep Analysis Engine': 'Глубокий анализ данных',
    'DEEP ANALYSIS ENGINE': 'ГЛУБОКИЙ АНАЛИЗ ДАННЫХ',
    'Asset Velocity': 'Популярность блюд',
    'ASSET VELOCITY': 'ПОПУЛЯРНОСТЬ БЛЮД',
    'Traffic Projections': 'Прогнозы посещаемости',
    'Telemetry Signal Tracking': 'Отслеживание сигналов активности',
    'Service Hour Update': 'Обновление времени обслуживания',
    'Late Night Lounge Hours': 'Ночные часы лаунжа',
    'Bar & Lounge Hours': 'Часы работы бара и лаунжа',
    'Breakfast & Brunch Hours': 'Часы завтрака и бранча',
    'Switch Location': 'Сменить заведение',
    'SWITCH LOCATION': 'СМЕНИТЬ ЗАВЕДЕНИЕ',
    'Global Admin': 'Главный администратор',
    'GLOBAL ADMIN': 'ГЛАВНЫЙ АДМИНИСТРАТОР',
    'Staff Access': 'Доступ для персонала',
    'STAFF ACCESS': 'ДОСТУП ДЛЯ ПЕРСОНАЛА',
    'Venue Overview': 'Обзор заведения',
    'VENUE OVERVIEW': 'ОБЗОР ЗАВЕДЕНИЯ',
    'Unified Telemetry Engine': 'Единый мониторинг активности',
    'UNIFIED TELEMETRY ENGINE': 'ЕДИНЫЙ МОНИТОРИНГ АКТИВНОСТИ',
    'Pulse': 'Пульс',
    'PULSE': 'ПУЛЬС',
    'Insights': 'Инсайты',
    'INSIGHTS': 'ИНСАЙТЫ',
    'Reports': 'Отчёты',
    'REPORTS': 'ОТЧЁТЫ',
    'GOVERNANCE': 'УПРАВЛЕНИЕ ФИЛИАЛАМИ',
    'Multi-Venue': 'Сеть заведений',
    'MULTI-VENUE': 'СЕТЬ ЗАВЕДЕНИЙ',
    'Menu Views': 'Просмотры меню',
    'Item Interactions': 'Взаимодействия с блюдами',
    'Guest Rating': 'Оценка гостей',
    'Revenue (7d)': 'Выручка (7д)',
    'Revenue (24h)': 'Выручка (24ч)',
    'Revenue (30d)': 'Выручка (30д)',
    'Delivery orders (7d)': 'Заказы на доставку (7д)',
    'Delivery orders (24h)': 'Заказы на доставку (24ч)',
    'Delivery orders (30d)': 'Заказы на доставку (30д)',
    'Delivery revenue': 'Выручка с доставки',
    'Average delivery order': 'Средний чек доставки',
    'Average delivery fee': 'Средняя стоимость доставки',
    'Zone average': 'Среднее по зоне',
    'Including fees': 'Включая сборы',
    'No deliveries': 'Нет доставок',
    'No orders': 'Нет заказов',
    'No reviews': 'Нет отзывов',
    'Live': 'Прямой эфир',
    'LIVE': 'ПРЯМОЙ ЭФИР',
    'reviews': 'отзывов',
    'orders': 'заказов',
    'deliveries': 'доставок',
    'of orders': 'от заказов',
    'rate': 'показатель',
    'Recent Activity': 'Последняя активность',
    'Real-time Venue Telemetry': 'Активность заведения в реальном времени',
    'REAL-TIME VENUE TELEMETRY': 'АКТИВНОСТЬ ЗАВЕДЕНИЯ В РЕАЛЬНОМ ВРЕМЕНИ',
    'Rapid Pulse View': 'Быстрый обзор в реальном времени',
    'Anonymous Guest': 'Анонимный гость',
    'scanned menu': 'отсканировал меню',
    'viewed item': 'просмотрел блюдо',
    'interacted with': 'взаимодействовал с',
    'placed order': 'оформил заказ',
    'searched menu': 'искал в меню',
    'Main Landing': 'Главная страница',
    'Trace': 'Отслежено',
    'TRACE': 'ОТСЛЕЖЕНО',
    'Gaining Telemetry...': 'Подключение к данным...',
    'No recent traffic detected': 'Недавней активности не обнаружено',
    'Activities will appear here as guests scan your QR code': 'Действия появятся здесь, как только гости отсканируют ваш QR-код',
    'Analyze All Logs': 'Анализ всех записей',
    'Just now': 'Только что',
    'less than a minute ago': 'меньше минуты назад',
    '1 minute ago': '1 минуту назад',
    '1 hour ago': '1 час назад',
    'about 1 hour ago': 'около 1 часа назад',
    '1 day ago': '1 день назад',
    'DAY AGO 1': '1 день назад',
    '2 days ago': '2 дня назад',
    'DAY AGO 2': '2 дня назад',
    '3 days ago': '3 дня назад',
    'DAY AGO 3': '3 дня назад',
    '4 days ago': '4 дня назад',
    'DAY AGO 4': '4 дня назад',
    '5 days ago': '5 дней назад',
    'DAY AGO 5': '5 дней назад',
    '6 days ago': '6 дней назад',
    'DAY AGO 6': '6 дней назад',
    '7 days ago': '7 дней назад',
    'DAY AGO 7': '7 дней назад',
    'about 1 month ago': 'около 1 месяца назад',
    'Monday': 'Понедельник',
    'Tuesday': 'Вторник',
    'Wednesday': 'Среда',
    'Thursday': 'Четверг',
    'Friday': 'Пятница',
    'Saturday': 'Суббота',
    'Sunday': 'Воскресенье',
    'Mon': 'Пн',
    'Tue': 'Вт',
    'Wed': 'Ср',
    'Thu': 'Чт',
    'Fri': 'Пт',
    'Sat': 'Сб',
    'Sun': 'Вс',
    'Opening Hours': 'Часы работы',
    'Start Time': 'Время начала',
    'End Time': 'Время окончания',
    'Opening Time': 'Время открытия',
    'Closing Time': 'Время закрытия',
    'All Day': 'Весь день',
    'Closed': 'Закрыто',
    'Active Days': 'Рабочие дни',
    'Active Schedule': 'Активное расписание',
    'Always Available': 'Всегда доступно',
    'Always Visible': 'Всегда отображается',
    'New Category': 'Новая категория',
    'Category': 'Категория',
    'Category Name': 'Название категории',
    'Category Description': 'Описание категории',
    'Display Order': 'Порядок отображения',
    'Item Name': 'Название блюда',
    'Item Description': 'Описание блюда',
    'Base Price': 'Базовая цена',
    'Preparation Time': 'Время приготовления',
    'Calories': 'Калории',
    'Featured Item': 'Рекомендуемое блюдо',
    'Chef\'s Recommendation': 'Выбор шеф-повара',
    'Spicy': 'Острое',
    'Vegetarian': 'Вегетарианское',
    'Vegan': 'Веганское',
    'Gluten-Free': 'Без глютена',
    'Halal': 'Халяль',
    'Kosher': 'Кошерное',
    'Contains Nuts': 'Содержит орехи',
    'Dairy-Free': 'Без молочных продуктов',
    'Allergen Exclusions': 'Аллергены',
    'Table Number': 'Номер стола',
    'Assigned Table': 'Назначенный стол',
    'Seats': 'Места',
    'Capacity': 'Вместимость',
    'Indoor': 'Внутри',
    'Outdoor': 'На улице',
    'Terrace': 'Терраса',
    'Bar': 'Бар',
    'Add Table': 'Добавить стол',
    'Edit Table': 'Изменить стол',
    'Delete Table': 'Удалить стол',
    'Bookings': 'Брони',
    'Add to Waitlist': 'Добавить в лист ожидания',
    'Guest Name': 'Имя гостя',
    'Party Size': 'Количество гостей',
    'Arrival Time': 'Время прибытия',
    'Special Requests': 'Особые пожелания',
    'Confirmed': 'Подтверждено',
    'No Show': 'Не явился',
    'Add Staff Member': 'Добавить сотрудника',
    'Invite Staff': 'Пригласить сотрудника',
    'Email Address': 'Адрес эл. почты',
    'Phone Number': 'Номер телефона',
    'Download QR Code': 'Скачать QR-код',
    'Print Table Stands': 'Печать настольных подставок',
    'Today': 'Сегодня',
    'Yesterday': 'Вчера',
    'This Week': 'На этой неделе',
    'This Month': 'В этом месяце',
    'Last 30 Days': 'За последние 30 дней',
    'Access Denied': 'Доступ запрещен',
    'Advanced Settings': 'Дополнительные настройки',
    'Change Image': 'Сменить фото',
    'Remove Image': 'Удалить фото',
    'Announcement Banner': 'Баннер объявлений',
    'Please fill in all required fields.': 'Пожалуйста, заполните все обязательные поля.',
    'Changes saved successfully': 'Изменения успешно сохранены',
    '(You)': '(Вы)',
    'AI Auto-Tag': 'Автотегирование ИИ',
    'Analyzing:': 'Анализ:',
    'Apple (Fruit/Vegan)': 'Яблоко (Фрукты/Веган)',
    'Cancel editing': 'Отменить редактирование',
    'Capacity:': 'Вместимость:',
    'Carrot (Veggie)': 'Морковь (Овощи)',
    'CircleDot (Organic)': 'Точка в круге (Органический)',
    'Coffee (Caffeine)': 'Кофе (Кофеин)',
    'Configuration': 'Конфигурация',
    'Copy verification record name': 'Скопировать имя записи проверки',
    'Copy verification record value': 'Скопировать значение записи',
    'Critical exception intercepted': 'Перехвачена критическая ошибка',
    'Kernel Panic': 'Системная ошибка',
    'DNA (Organic/Bio)': 'ДНК (Органический/Био)',
    'Egg (Ovo/Allergen)': 'Яйцо (Ово/Аллерген)',
    'Empty': 'Пусто',
    'Engraving...': 'Создание...',
    'Flame (Spicy)': 'Пламя (Острый)',
    'Free': 'Бесплатно',
    'From': 'От',
    'Generating': 'Создание',
    'Governance & Traceability Studio': 'Центр управления и аудита',
    'Greet': 'Приветствие',
    'Heart (Healthy)': 'Сердце (Здоровое)',
    'In-App': 'В приложении',
    'Infinity SVG': 'Векторный SVG (Без потери качества)',
    'Leaf (Default)': 'Лист (По умолчанию)',
    'MAX Feature': 'Функция Enterprise',
    'Milk (Dairy)': 'Молоко (Молочные продукты)',
    'Network': 'Сеть',
    'Notify SMS': 'SMS-оповещение',
    'Order': 'Заказ',
    'Original': 'Оригинал',
    'Overview': 'Обзор',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 стандартные роли',
    'Shield (Safety)': 'Щит (Безопасность)',
    'Soup (Warm/Soup)': 'Суп (Горячее/Суп)',
    'Standard Pro View': 'Стандартный вид',
    'Star': 'Звезда',
    'Synchronized with Venue Identity': 'Оформлено в стиле вашего заведения',
    'Synchronizing Security Context...': 'Установка безопасного соединения...',
    'Targeted Timing': 'Расписание обслуживания',
    'Targeting': 'Применяется к',
    'To': 'До',
    'Toggle Navigation': 'Переключить меню',
    'Venue': 'Заведение',
    'Venue Governance Suite': 'Управление и настройки заведения',
    'Volume:': 'Громкость:',
    'WHITEBOARD NOTE...': 'Заметка для команды...',
    'Warning (Alert)': 'Внимание (Предупреждение)',
    'Wheat (Flour/Gluten)': 'Пшеница (Мука/Глютен)',
    'Wine (Alcohol)': 'Вино (Алкоголь)',
    'YouTube/Vimeo/Direct Link': 'Ссылка на видео (YouTube, Vimeo или MP4)',
    'Your name': 'Ваше имя',
    '2. Table Number': '2. Номер стола',
    'for Morocco or': 'для Марокко или',
    'in': 'в',
    'to': 'до',
    'e.g. 12 or Patio-1': 'напр. 12 или Веранда-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'напр. Домашние феттуччине, черный трюфель, выдержанный пармезан 36 месяцев...',
    'e.g. Head Sommelier & Wine Lead': 'напр. Шеф-сомелье',
    'e.g. London': 'напр. Лондон',
    'e.g. SOMMELIER, HOST, BARISTA': 'напр. СОМЕЛЬЕ, ХОСТЕС, БАРИСТА',
    'e.g. Skyline Lounge': 'напр. Панорамный лаундж',
    'e.g. TRUFFLE OIL': 'напр. ТРЮФЕЛЬНОЕ МАСЛО',
    'e.g. The Grand Cafe': 'напр. Гранд Кафе',
    'e.g. Window preference, high chair': 'напр. У окна, детский стульчик',
    'Copied to clipboard!': 'Скопировано в буфер обмена!',
    'Copy failed.': 'Не удалось скопировать.',
    'Link copied!': 'Ссылка скопирована!',
    'Logo uploaded': 'Логотип загружен',
    'Cover image uploaded': 'Обложка загружена',
    'Profile saved and synced successfully': 'Профиль успешно сохранен и синхронизирован',
    'Category Created': 'Категория создана',
    'Category Updated': 'Категория обновлена',
    'Category Deleted': 'Категория удалена',
    'Item Created': 'Блюдо создано',
    'Item Updated': 'Блюдо обновлено',
    'Item Deleted': 'Блюдо удалено',
    'Menu Published': 'Меню опубликовано',
    'Delete Category?': 'Удалить категорию?',
    'Delete Menu Item?': 'Удалить это блюдо?',
    'Delete Ingredient?': 'Удалить этот ингредиент?',
    'Delete Service Period?': 'Удалить период обслуживания?',
    'Remove team member?': 'Удалить этого сотрудника?',
    'Cancel invitation?': 'Отозвать приглашение?',
    'Revoke API key?': 'Отозвать ключ API?',
    'Delete webhook endpoint?': 'Удалить этот вебхук?',
    'Clear all notifications?': 'Очистить все уведомления?',
    'Disconnect Stripe account?': 'Отключить аккаунт Stripe?',
    'Disconnect Google Business Profile?': 'Отключить профиль Google для бизнеса?',
    'Campaigns published successfully!': 'Кампании успешно опубликованы!',
    'Changes successfully synchronized with Google Business Profile!': 'Изменения успешно синхронизированы с профилем Google для бизнеса!',
    'Google Business Profile successfully connected!': 'Профиль Google для бизнеса успешно подключен!',
    'Google Business Profile disconnected.': 'Профиль Google для бизнеса отключен.',
    'Google Post draft saved successfully!': 'Черновик публикации Google успешно сохранен!',
    'Post draft deleted.': 'Черновик публикации удален.',
    'Review response published successfully!': 'Ответ на отзыв успешно опубликован!',
    'SEO configuration saved successfully!': 'Настройки SEO успешно сохранены!',
    'SEO metadata translated across all languages!': 'Метаданные SEO переведены на все языки!',
    'Venue profile & bio translated across all languages!': 'Профиль и описание заведения переведены на все языки!',
    'Reservation configuration saved successfully': 'Настройки бронирования успешно сохранены',
    'Stripe settings saved successfully.': 'Настройки Stripe успешно сохранены.',
    'Print-Ready PDF downloaded successfully': 'Готовый к печати PDF успешно загружен',
    'Vector QR PDF downloaded successfully': 'Векторный QR в PDF успешно загружен',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Профессиональный пакет для печати 300 DPI успешно создан',
    'At least one language must be enabled.': 'Должен быть включен как минимум один язык.',
    'Enter a non-zero percentage before adjusting prices.': 'Введите процент отличный от нуля перед изменением цен.',
    'Please enter a Venue Name before translating.': 'Пожалуйста, введите название заведения перед переводом.',
    'Please enter an item name before running dietary and allergen analysis.': 'Введите название блюда перед анализом аллергенов и диетических особенностей.',
    'Please write a reply before submitting.': 'Пожалуйста, напишите ответ перед отправкой.',
    'Table 3 Called Waiter': 'Стол 3 вызвал официанта',
    'Table Call Bell': 'Кнопка вызова со стола',
    'Waiter Calls': 'Вызовы официанта',
    'Waiter Dispatch & Calls': 'Диспетчер вызовов официантов',
    'Kitchen Display & Whiteboard': 'Кухонный экран и доска заметок',
    'Customer Orders': 'Заказы клиентов',
    'Bakery': 'Пекарня / Кондитерская',
    'Bar / Pub': 'Бар / Паб',
    'Café / Coffee Shop': 'Кафе / Кофейня',
    'Fast Food': 'Быстрое питание / Фастфуд',
    'Gourmet / Fine Dining': 'Изысканная кухня / Ресторан премиум',
    'Hotel': 'Отель / Гостиница',
    'General Local Business': 'Местное заведение / Бизнес',
    'Classic': 'Классический',
    'Clean': 'Минималистичный',
    'Modern': 'Современный',
    'Elegant': 'Элегантный',
    'Energetic': 'Энергичный',
    'Glass': 'Стеклянный эффект',
    'Playful & Casual': 'Непринужденный и яркий',
    'Sophisticated & Gourmet': 'Изысканный и гастрономический',
    'Warm & Welcoming': 'Теплый и радушный',
    'Executive': 'Премиальный бизнес',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Требуется обновление: Многоязычные меню доступны на плане Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Требуется обновление: Заказы и вызовы официанта недоступны на плане Starter.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Экспорт в векторный SVG доступен на планах Pro и Max. Пожалуйста, обновите план.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Экспорт готовых к печати PDF доступен на планах Pro и Max. Пожалуйста, обновите план.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'План Pro ограничен 3 активными кампаниями. Приостановите другую кампанию или перейдите на MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'План Starter включает до 20 столов (Столы 1–20). Перейдите на PRO для неограниченного числа столов.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'План Starter поддерживает нумерованные столы (1–20). Пользовательские имена (например, Веранда, VIP) требуют PRO.',
    'Add Menu Categories & Items': 'Добавить категории и блюда',
    'Add Menu Items': 'Добавить блюда в меню',
    'Organize your menu sections and food items.': 'Организуйте разделы меню и блюда для гостей.',
    'Add and manage your food & beverage offerings.': 'Добавляйте и редактируйте блюда и напитки вашего заведения.',
    'Customize Venue Branding': 'Настроить фирменный стиль заведения',
    'Set your brand colors, logo, and presentation.': 'Задайте цвета бренда, логотип и оформление витрины.',
    'Get Your QR Code & Go Live': 'Получите QR-код и начните работу',
    'Download table QR codes and preview your live menu.': 'Скачайте QR-коды для столов и посмотрите меню глазами гостя.',
    'Account & Venue Created Successfully!': 'Аккаунт и заведение успешно созданы!',
    '⚡ Off-Peak Hours': '⚡ Спокойные часы',
    '🍽️ Lunch Crowd': '🍽️ Обеденное время',
    '🚀 Weekend Rush': '🚀 Выходной ажиотаж',
    '🌟 Seasonal Special': '🌟 Сезонное предложение',
    '📣 Engagement Buzz': '📣 Активность и отзывы',
    'Festive & Energetic': 'Праздничный и бодрый',
    'Urgent & High-Conversion': 'Срочное и привлекательное',
    'Generated 3 tailored campaign strategies': 'Создано 3 индивидуальные стратегии кампаний',
    'Could not generate campaign ideas. Please try again.': 'Не удалось создать идеи для кампании. Пожалуйста, попробуйте еще раз.',
    'Venue Themes': 'Темы оформления заведения',
    'Choose the character of your public menu': 'Выберите визуальный стиль вашего публичного меню',
    'Glassmorphism & Depth': 'Стеклянный эффект и глубина',
    'Independent XML Sitemap': 'Автономная карта сайта XML',
    'Structured Schema & Sync': 'Структурированные данные и синхронизация',
    'Improve how your venue appears on Google and in local search results.': 'Улучшите видимость заведения в Google и в локальном поиске гостей.',
    'Terms of Service': 'Условия обслуживания',
    'Confirm your email': 'Подтвердите вашу почту',
    'Check your spam / junk folder': 'Проверьте папку «Спам» или нежелательной почты',
    'Commercial pre-press print production package': 'Профессиональный допечатный пакет для полиграфии',
    'Over-bleed safety margin': 'Поля выпуска за обрез',
    'Crop Marks': 'Метки обреза',
    'Guillotine cutting guides': 'Направляющие для резака',
    'Global Banners': 'Главные баннеры',
    'Featured Items': 'Рекомендуемые блюда',
    'AI Assistant': 'ИИ-помощник',
    'Custom Role Schemas': 'Пользовательские схемы ролей',
    'Notification Preferences': 'Настройки уведомлений',
    'Preferences saved automatically': 'Настройки сохраняются автоматически',
    'Menu Extractor (PDF & Images)': 'Импорт меню из PDF и фото',
    'Multi-Language Translation': 'Многоязычный перевод',
    'Start Menu Translation': 'Начать перевод меню',
    'Menu Items Hub': 'Центр управления блюдами',
    'Open Menu Items': 'Открыть список блюд',
    'Enterprise Traceability': 'Корпоративный журнал аудита',
    'Full History': 'Полная история',
    'Billing & Plans': 'Оплата и тарифы',
    'Subscription Governance': 'Управление подпиской',
    'Platform Usage': 'Использование ресурсов',
    'Menu Page Views': 'Просмотры страниц меню',
    'Enterprise Identity': 'Корпоративная идентификация',
    'Access Governance': 'Управление доступом',
    'Required Fields *': 'Обязательные поля *',
    'Enabled Languages': 'Включенные языки',
    'Venue Info & Bio Localizations': 'Переводы описания и информации о заведении',
    'Public Address': 'Публичный адрес',
    'Public URL Slug': 'Пользовательский URL-адрес',
    'Contact Phone': 'Контактный телефон',
    'Business Address': 'Фактический адрес заведения',
    'No Categories Yet': 'Пока нет категорий',
    'No Custom Roles Configured': 'Пользовательские роли не настроены',
    'No Google reviews retrieved yet': 'Отзывы Google пока не получены',
    'No Guests Found': 'Клиенты не найдены',
    'No Menu Items Yet': 'В меню пока нет блюд',
    'No Promotions Configured': 'Акции пока не настроены',
    'No Service Periods Yet': 'Периоды обслуживания пока не настроены',
    'No Support Tickets': 'Обращений в поддержку нет',
    'No Tables on Floor Plan': 'На схеме зала пока нет столов',
    'No Team Members Found': 'Сотрудники не найдены',
    'No Upcoming Events': 'Предстоящих событий нет',
    'No Walk-Ins Recorded': 'Гостей без брони не зарегистрировано',
    'No items are available right now': 'Сейчас нет доступных блюд',
    'No notifications found': 'Уведомлений нет',
    'No reservations matching your filters': 'Нет бронирований, соответствующих фильтрам',
    'No tables currently occupied': 'В данный момент занятых столов нет',
    'No venue selected': 'Заведение не выбрано',
    'Waitlist': 'Лист ожидания',
    'Waitlist is Empty': 'Лист ожидания пуст',
    'Waves (Gluten)': 'Колосья (Глютен)',
    'We sent a temporary verification code to': 'Мы отправили временный проверочный код на',
    'Website URL': 'Адрес веб-сайта',
    'What the customer receives': 'Что получает гость',
    'White-label control is available on eligible plans.': 'White-label оформление доступно на соответствующих тарифных планах.',
    'Why this works:': 'Почему это работает:',
    'WiFi Broadcast': 'Гостевой Wi-Fi',
    'WiFi Password': 'Пароль от Wi-Fi',
    'Window seat, dietary request, birthday celebration...': 'Столик у окна, предпочтения в еде, день рождения...',
    'Your Published Response': 'Ваш опубликованный ответ',
    'Your Venues': 'Ваши заведения',
    '🎉 Ready to publish!': '🎉 Готово к публикации!',
    'e.g. Grill, Salad, Pastry': 'напр. Гриль, Салаты, Десерты',
    'e.g. Guest called, emergency': 'напр. Звонок от гостя, форс-мажор',
    'e.g. Italian, Moroccan, Burgers': 'напр. Итальянская, Марокканская, Бургеры',
    'e.g. Mains, Pasta, Starters': 'напр. Основные блюда, Паста, Закуски',
    'e.g. Signature Truffle Pasta': 'напр. Фирменная паста с трюфелем',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'напр. Дневной кофе-брейк, Счастливые часы 15%',
    'e.g., Breakfast, Happy Hour': 'напр. Завтрак, Счастливые часы',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'напр. Приглашаем на живой джаз каждую пятницу в 20:00!',
  },
  zh: {
    '123 Main St, City': '某市主要街道 123 号',
    'Balance': '积分余额',
    'Lifetime': '历史累计积分',
    'Source': '来源渠道',
    'Menu': '菜单',
    'Advanced': '高级设置',
    'Core Suite': '核心功能套件',
    'Privacy': '隐私保护',
    'Support': '客服支持',
    'Save Layout': '保存桌位布局',
    'Shape:': '餐桌形状:',
    'SEARCH LIBRARY...': '搜索食材与菜品库...',
    'Search library...': '搜索食材与菜品库...',
    'None': '无',
    'Type': '类型',
    'Target': '目标对象',
    'Value / Target': '设定值 / 目标',
    'London (GMT/BST)': '伦敦 (GMT/BST)',
    'New York (EST/EDT)': '纽约 (EST/EDT)',
    'Chicago (CST/CDT)': '芝加哥 (CST/CDT)',
    'Denver (MST/MDT)': '丹佛 (MST/MDT)',
    'Dubai (GST)': '迪拜 (GST)',
    'Tokyo (JST)': '东京 (JST)',
    'Sydney (AEST/AEDT)': '悉尼 (AEST/AEDT)',
    'Start Color': '渐变起始色',
    'Depth Shade': '立体阴影深度',
    'Matrix Geometry': '二维码点阵形态',
    'Eye Architecture': '定位角眼设计',
    'Brand Centerpiece': '二维码中心品牌 Logo',
    'Scannability Shield': '扫码可读性高对比度保障',
    'Environment Blur': '环境背景模糊效果',
    'Color Target:': '色彩输出格式:',
    'DPI:': 'DPI (印刷分辨率):',
    '3mm Bleed Box': '3毫米专业裁切出血位',
    '300 DPI': '300 DPI (印刷级高清)',
    'PRO+': 'PRO+ 专业进阶版',
    'SAML 2.0': 'SAML 2.0 企业认证协议',
    'OpenID Connect': 'OpenID Connect (OIDC) 协议',
    'IdP Entity ID': '身份提供商实体 ID (Entity ID)',
    'SSO URL': '单点登录访问地址 (SSO URL)',
    'X.509 Certificate': 'X.509 安全数字证书',
    'ACS URL': '断言消费者服务地址 (ACS URL)',
    'Entity ID': '实体标识 (Entity ID)',
    'Navigate': '导航',
    'Select': '选择',
    'OmniSearch v1.0': '全域智能全局搜索',
    'Back to order review': '返回订单确认明细',
    'Return to Intelligence Studio': '返回数据智能分析中心',
    'View Full Notification Center': '查看所有消息通知',
    'Read all': '全部标为已读',
    'Cancel department': '取消部门编辑',
    'Save department': '保存部门',
    'Venue Segmentation': '营业区域划分',
    'Collaborate on your menu together.': '与团队成员共同协作编辑和完善菜单。',
    'Timeline': '预约时间轴',
    'Calendar': '预约日历视图',
    'Book': '立即订座',
    'Duration': '就餐时长',
    'Time & Duration': '到店时间与时长',
    'Seating Preference': '座位偏好 (窗边/包厢等)',
    'Section': '区域分区',
    'Min Capacity': '最少容纳人数',
    'Max Capacity': '最多容纳人数',
    'Phone (Optional)': '联系电话 (选填)',
    'Name or Walk-in': '客人姓名或直接到店',
    'Next day': '下一天',
    'Next month': '下个月',
    'Live Connection': '实时在线连接',
    'Live Inventory': '实时库存与在售管理',
    'Main Venue (Full Menu)': '总店全功能完整菜单',
    'Preview Menu': '预览顾客端菜单',
    'Manual Setup Guide': '手动配置指引与操作教程',
    'Connected & Active': '已连接并正常生效',
    'CTA Action': '行动号召按钮 (CTA)',
    'Phone Contact': '联系电话',
    'Menu URL': '在线菜单网址',
    'Google Location': 'Google 地图商户位置',
    'Field': '字段',
    'Linked': '已关联',
    'Synchronizing Hub...': '正在同步数据中枢...',
    'Data Integrity': '数据完整性与合规验证',
    'Station Governance Suite': '出餐与出品工作站管理',
    'Open audit log': '查看操作审计明细',
    'Signal / Time': '事件类型 / 记录时间',
    'Identity': '执行者 / 身份',
    'Invitation Invalid': '团队邀请链接已失效或过期',
    'Invited To': '受邀加入',
    'Team Limit Reached': '员工账号数量已达套餐上限',
    'Venue Limit Reached': '门店数量已达套餐配额上限',
    'Venue Setup Required': '需先完成餐厅基础设置',
    'Create Venue': '创建新门店',
    'TableGreet Onboarding': 'TableGreet 新手指引与设置',
    'No payment required': '无需付费，永久免费',
    'Selected Plan': '已选订阅套餐',
    'Payment Method': '支付方式',
    'Invoice ID': '发票单号',
    'Verified': '已通过验证',
    'Accent color': '辅助强调色',
    'Set the voice and overall light level.': '设定整体品牌语调与明暗视觉模式。',
    'Layout architecture': '菜单排版与版式架构',
    'Navigation & motion': '导航动效与页面过渡',
    'Loading preview engine': '正在加载实时预览引擎',
    'Real Live Preview': '真实实时预览',
    'Included with Pro & Max': 'Pro 与 Max 套餐专享包含',
    'Daypart Scheduling': '分时段菜单供应调度',
    'Operational Daypart Architecture': '营运供餐时段架构',
    'Service Protocol': '服务标准与规程',
    'Configuration Engine': '高级配置引擎',
    'Linked Department': '关联营运部门',
    'Team Hub': '员工团队协作中心',
    'Team Size': '团队人员规模',
    'Joined': '加入时间',
    'Cancel Invitation': '撤回邀请',
    'Initialize New Organization': '创建并初始化新组织/集团',
    'Operational': '正常营运中',
    'Pro Feature': 'Pro 专业版专属功能',
    'Current State': '当前运行状态',
    'Open on the big TV': '在大屏电视上全屏展示',
    'Menu Board': '电子菜单水牌看板',
    'Reward': '积分奖品',
    'Reward name': '奖品名称',
    'Cancel & refund': '取消订单并退款',
    'Code': '兑换码',
    'Find': '查找',
    'Complete': '已完成',
    'Seated': '在座用餐',
    'No-Show': '失约未到 (No-Show)',
    'Walk-in': '现场散客',
    'Party': '就餐人数',
    'Turnover': '翻台率 / 翻台周期',
    'We couldn\'t load the menu': '无法加载当前菜单',
    'Venue Not Found': '未找到该餐厅信息',
    'Live Availability': '实时在售状态',
    'Multi-Language Suite': '多语言国际化管理套件',
    'Key name': '接口密钥名称',
    'Revoke API key': '注销并撤销该 API 密钥',
    'Delete webhook': '删除 Webhook',
    'Identity Provider': '身份认证提供方 (IdP)',
    'Provider': '服务提供商',
    'Mandatory MFA': '强制双重身份验证 (MFA)',
    'Account Identity': '账户身份与安全',
    'Login Email': '登录邮箱',
    'New Login Email': '新登录邮箱',
    'Phone Login': '手机号码验证登录',
    'SMS Verification Code': '短信验证码',
    'Integration Method': '对接与集成方式',
    'Stripe Connect': 'Stripe Connect 在线支付网关',
    'Stripe Secret Key': 'Stripe 私有密钥 (Secret Key)',
    'Stripe Webhook Secret': 'Stripe Webhook 签名密钥',
    'Client-Owned': '商户自主拥有与管理',
    'Verify connection': '测试并验证连接',
    'Setup Health': '系统设置健康度',
    'Privacy Protected': '数据隐私全程安全受护',
    'Privacy Policy': '隐私保护政策',
    'Continue': '继续',
    'Sign In': '登录',
    'Didn\'t get it?': '未收到验证码？',
    'Logged In Account': '当前登录账号',
    'Annual': '按年结算 (优惠2个月)',
    'Unread': '未读消息',
    'Starred': '星标重要',
    'High / Urgent': '高优先级 / 紧急',
    'Mark Read': '标为已读',
    'Delete notification': '删除通知',
    'Urgent': '特急',
    'High': '高',
    'Normal': '普通',
    'Low': '低',
    'Select a Ticket': '选择工单以查看详情',
    'Urgent (Immediate attention)': '特急 (需立即处理)',
    'Type your reply here...': '在此输入您的回复内容...',
    'Print Card (PDF)': '打印桌牌立卡 (PDF)',
    'Advanced Printing Engine': '专业高保真印刷渲染引擎',
    'Multi-Venue Governance': '多门店与连锁分支统一管理',
    'Network Revenue': '全门店网络总营收',
    'Live Network Total': '全网实时汇总数据',
    'Audit Log Hub': '操作审计与合规日志中心',
    'Export Log': '导出审计日志',
    'CUSTOM ROLE SCHEMAS': '自定义员工角色与权限体系',
    'Unique internal role identifier.': '角色内部唯一英文标识。',
    'Human-friendly role label.': '团队中直观显示的职位名称。',
    'Marketing Hub': '营销与促销中心',
    'Revenue Generation Suite': '营收增长与促销套件',
    'Active Campaign Limit Reached (3/3)': '已达进行中营销活动上限 (3/3)',
    'Banner Content': '横幅促销内容',
    'Style Profile': '视觉风格预设',
    'Action URL (Optional)': '点击跳转链接 (选填)',
    'Promotion Label': '优惠活动标签',
    'Target Selection': '活动适用菜品/分类',
    'Edit Promotion': '编辑优惠活动',
    'Delete Promotion': '删除优惠活动',
    'Loyalty Engine': '会员忠诚度与积分系统',
    'Reward catalog': '可兑换奖品列表',
    'Add reward': '添加新奖品',
    'Loyalty ID': '会员卡号 / 会员 ID',
    'No point activity yet': '暂无积分变动记录',
    'Scan or enter QR value': '扫码或手动输入会员码',
    'TableGreet Admin': 'TableGreet 门店管理后台',
    'Capacity Pacing': '客流负荷与预约速率控制',
    'Cancellation Policy': '预订取消条款与规则',
    'Booking Experience': '在线订座预订体验',
    'Floor Occupancy': '餐厅座位就座率',
    'Currently Seated': '当前在座客人',
    'Seat Party': '安排入座',
    'Seat Table': '开台入座',
    'Select Table': '选择餐桌',
    'Table / Capacity': '餐桌号 / 可容纳人数',
    'Table Name/Number': '桌号 / 桌名',
    'Mark Arrived': '标记为已到店',
    'Mark No-Show': '标记为未到店 (No-Show)',
    'Complete Dining': '结账离店并清理餐桌',
    'Standard Dining (Default)': '堂食大厅就餐 (默认)',
    'Walk-In Dining Log': '无预约散客接待记录',
    'Notification Center': '消息通知中心',
    'Single Sign-On': '企业级单点登录 (SSO)',
    'Welcome Aboard!': '欢迎开启 TableGreet 智慧餐饮之旅！',
    'Select your TableGreet plan': '选择适合您餐厅的 TableGreet 套餐',
    'Free Plan Activation': '激活永久免费套餐',
    'Venue Created!': '餐厅已成功创建！',
    'Upgrade for Unlimited': '升级以解锁无限制额度',
    'Auto-Tag Menu': '智能一键生成菜品标签',
    'Start Auto-Tagging': '开始智能打标',
    'Launch Auto-Tagger': '启动智能标签生成器',
    'Launch PDF Importer': '启动 PDF 菜单智能导入',
    'Analyzing Menu...': '正在深度分析菜单结构...',
    'Analyzing Recipe...': '正在分析食材与配方...',
    'Manual Availability': '手动控制在售状态',
    'Technical Breakdown': '规格与技术明细',
    'Dietary Taxonomy': '饮食偏好与过敏原规范',
    'Dietary & Allergen AI': '智能过敏原与膳食分析',
    'Inherit from Category': '继承所属分类的默认设置',
    'Ingredient Library': '食材与配料知识库',
    'Ingredient Name': '食材原料名称',
    'Menu Organization': '菜单分类规划与层级',
    'Inventory Taxonomy Hub': '菜单分类与品类中枢',
    'Category Intelligence': '分类数据智能分析',
    'Service Period': '供餐时段 (早/午/晚餐)',
    'Manual Configuration / All Day': '手动设定 / 全天供应',
    'Operational Department': '归属营运部门',
    'Global / No Department': '通用全区 / 不限定特定部门',
    'Cancel category': '取消分类',
    'New category name...': '输入新分类名称...',
    'Save category': '保存分类',
    'QR Matrix Only': '纯二维码图案 (无边框)',
    'Vector & PDF': '矢量高清与印刷 PDF',
    'Vector QR (SVG)': '矢量二维码 (SVG 格式)',
    'Vector QR (PDF)': '高清印刷版二维码 (PDF)',
    'Save QR Style': '保存当前二维码设计样式',
    'Share Link': '分享点餐链接',
    'High Fidelity 300DPI': '专业印刷级 300 DPI 超清解析度',
    'Generating 300 DPI PDF...': '正在生成 300 DPI 印刷级 PDF...',
    'Give your venue a point of view.': '定制专属视觉风格，彰显品牌独特餐饮品味。',
    'Signage Menu Not Found': '未找到电子看板菜单',
    'Scan Menu': '扫码查看菜单',
    'Open on your phone': '在手机上浏览点餐',
    'Full Screen': '全屏模式',
    'Lock TV board': '锁定电视大屏展示',
    'Big-Screen Menu': '电视电子水牌菜单',
    'TV Signage': '电视电子水牌与大屏看板',
    'General Information': '基本信息',
    'Venue Name *': '场所名称 *',
    'Public Slug *': '公开网址标识 *',
    'Venue Logo': '品牌标识 / Logo',
    'Hero Cover Image': '首页封面大图',
    'Venue Timezone': '经营所在地时区',
    'Primary Brand Color': '品牌主色调',
    'TableGreet footer credit': '页脚 TableGreet 品牌标识',
    'White-Label Branding': '全专属品牌定制 (White-Label)',
    'Client DNS Setup': '自定义域名 DNS 解析设置',
    'Copy CNAME target': '复制 CNAME 目标记录',
    'Copy TableGreet URL': '复制 TableGreet 菜单链接',
    'Included TableGreet URL': '系统默认赠送 TableGreet 链接',
    'Open TableGreet URL': '在新窗口打开菜单',
    'Public menu': '顾客公开点餐菜单',
    'Public venue delivery': '线上点餐外卖配送服务',
    'Zone name': '配送区域名称',
    'Coverage note': '配送范围及覆盖说明',
    'Default ETA min': '预计送达最短耗时 (分钟)',
    'Default ETA max': '预计送达最长耗时 (分钟)',
    'Packaging fee': '打包打包费',
    'Courier handled by': '配送承运方',
    'Third-party courier': '第三方合作外卖骑手',
    'Unified Experience': '全渠道一体化就餐体验',
    'Save WiFi to Venue': '保存顾客专属无线网络 (Wi-Fi)',
    'Network Name (SSID)': '无线网络名称 (SSID)',
    'Audio Alert Chime': '订单及服务呼叫提示音',
    'Time Format': '时间显示格式',
    'Auto / Default': '自动 / 默认设置',
    'Syncing SEO & Google Data...': '正在同步搜索引擎与 Google 数据...',
    'Google Connection': 'Google 商业资料连接',
    'Search Engine Metadata': '搜索引擎收录元数据',
    'Social card preview': '社交媒体分享卡片预览',
    'Allow Indexing': '允许搜索引擎索引',
    'Canonical URL Override': '规范网址重写 (Canonical URL)',
    'Price Range': '人均消费价格带',
    'Latitude': '纬度',
    'Longitude': '经度',
    'Google Place ID / Location ID': 'Google 地图地点标识 (Place ID)',
    'Google Search Preview': 'Google 搜索结果展示预览',
    'Linked Google Location': '已关联的 Google 地图商家位置',
    'SEO Documentation': 'SEO 搜索优化使用指南',
    ', and': '、以及',
    'and': '及',
    'Delete Department?': '确定删除该部门吗？',
    'Delete Department': '删除部门',
    'Live URL': '公开访问链接',
    'Copy full live URL': '复制完整公开访问链接',
    'Open live department menu': '在新窗口打开该部门菜单',
    'Loading departments...': '正在加载营运部门...',
    'Multi-department venue segmentations are premium features available on PRO and above.': '多营运部门区域划分属于专业版（PRO）及以上套餐的高级功能。',
    'Segment your menu by rooms, tables, bars, or physical locations.': '根据包厢、用餐区、吧台或独立物理空间分别规划菜单。',
    'Targeted Ordering': '定向订单分流',
    'Route guests and orders to specific preparation areas.': '将顾客与订单精准路由至指定出品与出餐工作站。',
    'Operational Departments': '营运部门架构',
    'OPERATIONAL DEPARTMENTS': '营运部门架构',
    'Venue Segmentation Hub': '营业区域细分管理中心',
    'VENUE SEGMENTATION HUB': '营业区域细分管理中心',
    'Create departments to group your menu sections. For example, a large venue might have a': '创建经营分区以归类不同菜单板块。例如大型综合场所可设立',
    'Restaurant': '餐厅',
    'Poolside': '泳池休闲吧',
    'department, each with its own categories.': '等独立部门，各自分配专属菜品分类。',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': '通过营运部门可将场所划分为餐厅、酒吧或泳池吧等独立区域，各自分配专属菜单分类。',
    'Department name': '部门名称',
    'New department name...': '输入新部门名称...',
    'No Linked Daypart (Always Available)': '不绑定特定营业时段（全天供应）',
    'Linked Daypart': '关联营业时段',
    'Localizations': '多语言翻译',
    'LOCALIZATIONS': '多语言翻译',
    'AI Auto-Translate': 'AI 智能一键翻译',
    'AI AUTO-TRANSLATE': 'AI 智能一键翻译',
    'Translated name': '翻译后名称',
    'No Departments Yet': '暂未创建任何营运部门',
    'Add First Department': '添加首个营运部门',
    'Avg Order Value': '平均客单价',
    'AVG ORDER VALUE': '平均客单价',
    'item taps': '次菜品点击',
    'ITEM TAPS': '次菜品点击',
    'Avg Rating': '平均评分',
    'AVG RATING': '平均评分',
    'Revenue (Last 7 Days)': '营业额（近7天）',
    'REVENUE (LAST 7 DAYS)': '营业额（近7天）',
    'Daily breakdown': '每日细分',
    'DAILY BREAKDOWN': '每日细分',
    'Menu Views (Last 7 Days)': '菜单浏览量（近7天）',
    'MENU VIEWS (LAST 7 DAYS)': '菜单浏览量（近7天）',
    'Guest traffic': '到店客流',
    'GUEST TRAFFIC': '到店客流',
    'Most Viewed Items': '最常浏览菜品',
    'MOST VIEWED ITEMS': '最常浏览菜品',
    'Top Rated Items': '好评最高菜品',
    'TOP RATED ITEMS': '好评最高菜品',
    'Waiter Requests': '呼叫服务员记录',
    'WAITER REQUESTS': '呼叫服务员记录',
    'Total': '总计',
    'TOTAL': '总计',
    'Order Status (30 days)': '订单状态统计（30天）',
    'ORDER STATUS (30 DAYS)': '订单状态统计（30天）',
    'Recent Guest Feedback': '顾客最新评价',
    'RECENT GUEST FEEDBACK': '顾客最新评价',
    'Conversion Rate': '订单转化率',
    'CONVERSION RATE': '订单转化率',
    'of menu visitors placed an order': '的浏览访客成功下了订单',
    'Refreshed': '已更新',
    'REFRESHED': '已更新',
    'Last 30 days': '近30天',
    'LAST 30 DAYS': '近30天',
    'cash': '现金',
    'Cash': '现金',
    'CASH': '现金',
    'bill': '账单',
    'Bill': '账单',
    'BILL': '账单',
    'water': '水',
    'Water': '水',
    'WATER': '水',
    'service': '呼叫协助',
    'Service': '呼叫协助',
    'SERVICE': '呼叫协助',
    'No interaction data yet': '暂无互动点击数据',
    'No ratings collected yet': '尚未收集到评分',
    'No requests in period': '此周期内无服务呼叫',
    'No feedback collected yet': '尚未收到顾客文字评价',
    'No menu views recorded yet': '尚未记录到菜单浏览量',
    'Dish Rating Breakdown': '菜品满意度详细分析',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': '菜品质量排行榜和顾客满意度分析是 MAX 与 Enterprise 套餐专属功能。',
    'Customer Sentiment & Reviews': '顾客情感倾向与文字评价',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': '直接查看食客评价与智能情绪分析需开通 MAX 或 Enterprise 订阅。',
    'Reviews AI': 'AI 智能评价分析',
    'Guest Feedback': '顾客就餐反馈',
    'Upgrade to unlock rating analytics →': '升级以解锁详细评分分析 →',
    'Upgrade to Max': '升级至 Max 套餐',
    'UPGRADE TO MAX': '升级至 MAX 套餐',
    'MAX PLAN': 'MAX 套餐',
    'No Data Yet': '暂无统计数据',
    'Share your QR code to start collecting real venue insights.': '放置并分享您的桌码，即可开始收集餐厅真实的运营与顾客互动数据。',
    'Revenue Analytics': '营收深度分析',
    'REVENUE ANALYTICS': '营收深度分析',
    'Track your earnings, orders, and average ticket values over time.': '持续追踪餐厅营业收入、订单总量以及平均客单价的变化趋势。',
    'Operational Velocity': '日常运营流转效率',
    'OPERATIONAL VELOCITY': '日常运营流转效率',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': '实时监控店内客流密度、热销菜品排名以及服务员呼叫负载情况。',
    'Upgrade to Pro': '升级到 Pro',
    'UPGRADE TO PRO': '升级至 PRO 套餐',
    'Loading Insights…': '正在载入经营分析…',
    'Loading Insights...': '正在载入经营分析...',
    'Telemetry Unavailable': '营业活动数据暂不可用',
    'TELEMETRY UNAVAILABLE': '营业活动数据暂不可用',
    'No Data Captured': '暂无记录数据',
    'NO DATA CAPTURED': '暂无记录数据',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': '分享您的桌号码即可开始实时收集顾客浏览与下单数据。',
    'INTELLIGENCE STUDIO': '数据分析中心',
    'Insights Engine Locked': '进阶分析功能未解锁',
    'INSIGHTS ENGINE LOCKED': '进阶分析功能未解锁',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': '深度趋势分析、菜单批量操作和数据导出报表专属于',
    'Unlock Pro Features': '升级解锁专业版功能',
    'UNLOCK PRO FEATURES': '升级解锁专业版功能',
    'MENU VIEWS': '菜单浏览量',
    'ITEM INTERACTIONS': '菜品互动次数',
    'GUEST RATING': '顾客评分',
    'DELIVERY REVENUE': '外卖配送营收',
    'AVERAGE DELIVERY ORDER': '平均配送订单金额',
    'AVERAGE DELIVERY FEE': '平均配送费',
    'INCLUDING FEES': '包含服务费',
    'ZONE AVERAGE': '区域均值',
    'TRAFFIC PROJECTIONS': '客流趋势预测',
    'TELEMETRY SIGNAL TRACKING': '实时客流信号追踪',
    'VEGETARIAN': '素食',
    'VEGAN': '纯素',
    'SPICY': '香辣',
    'GLUTEN-FREE': '无麸质',
    'Reset: All In-Stock': '重置：全部恢复供应',
    'Emergency Stop (All Out)': '紧急停止：全单沽清',
    'No compliance issues': '未发现任何合规问题',
    'NO COMPLIANCE ISSUES': '未发现任何合规问题',
    'detected in current menu': '在当前菜单中',
    'DETECTED IN CURRENT MENU': '在当前菜单中',
    'Telemetry History (24h)': '营业活动历史 (24小时)',
    'TELEMETRY HISTORY (24H)': '营业活动历史 (24小时)',
    'Telemetry History (30d)': '营业活动历史 (30天)',
    'TELEMETRY HISTORY (30D)': '营业活动历史 (30天)',
    'Save': '保存',
    'Saved': '已保存',
    'Saving...': '保存中...',
    'Save Changes': '保存更改',
    'Save Settings': '保存设置',
    'Cancel': '取消',
    'Close': '关闭',
    'Create': '创建',
    'Add': '添加',
    'Edit': '编辑',
    'Delete': '删除',
    'Remove': '移除',
    'Update': '更新',
    'Refresh': '刷新',
    'Search': '搜索',
    'Search...': '搜索...',
    'Search items...': '搜索菜品...',
    'Search categories...': '搜索分类...',
    'Filter': '筛选',
    'All': '全部',
    'Export': '导出',
    'Import': '导入',
    'Download': '下载',
    'Upload': '上传',
    'Copy': '复制',
    'Copied': '已复制',
    'Copied!': '已复制！',
    'Preview': '预览',
    'Open': '营业中',
    'View': '查看',
    'Manage': '管理',
    'Configure': '配置',
    'Enable': '启用',
    'Disable': '禁用',
    'Enabled': '已启用',
    'Disabled': '已禁用',
    'Active': '活跃',
    'Inactive': '未激活',
    'Available': '可用',
    'Unavailable': '不可用',
    'Visible': '显示',
    'Hidden': '隐藏',
    'Required': '必填',
    'Optional': '可选',
    'Confirm': '确认',
    'Back': '返回',
    'Next': '下一步',
    'Done': '完成',
    'Upgrade': '升级套餐',
    'Sign Out': '退出登录',
    'Exit to Main Admin': '返回主管理台',
    'Change Language': '切换语言',
    'Add Category': '添加分类',
    'Add Menu Item': '添加菜品',
    'Add Item': '添加项目',
    'Add Ingredient': '添加配料',
    'Add Department': '添加部门',
    'Delete Item': '删除菜品',
    'Delete Category': '删除分类',
    'Upload Image': '上传图片',
    'Upload Photo': '上传照片',
    'Select Category': '选择分类',
    'Select Department': '选择部门',
    'Add translation...': '添加翻译...',
    'Clear': '清除',
    'Reset': '重置',
    'Apply': '应用',
    'Submit': '提交',
    'Core & Operations': '日常运营与堂食',
    'Menu & Experience': '菜单与菜品',
    'Enterprise & Security': '门店与员工管理',
    'System & Control': '系统设置与帮助',
    'Intelligence Studio': '数据分析中心',
    'Venue Insights': '销售与报表',
    'Reservations': '订座预订',
    'Floor Plan': '餐桌布局图',
    'Live Waitlist': '排队名单',
    'Walk-Ins': '到店散客',
    'Walk-ins': '到店散客',
    'Departments': '部门区域',
    'Service Hours': '服务时间',
    'Categories': '菜品分类',
    'Menu Categories': '菜单分类',
    'Menu Items': '菜品列表',
    'Items': '菜品',
    'Ingredients': '食材配料',
    'Loyalty Program': '会员积分',
    'Campaign Studio': '促销活动',
    'Signage': '电子大屏幕',
    'QR Code': '餐桌二维码',
    'Kitchen Ops': '后厨出餐屏',
    'Theme Engine': '菜单样式与配色',
    'Governance': '连锁治理',
    'Audit Log': '操作记录',
    'API & Webhooks': '系统对接与集成',
    'SSO & Security': '安全与账号登录',
    'Team': '员工与账号',
    'Billing': '套餐与账单',
    'SEO & Google': '谷歌搜索与展现',
    'Notifications': '消息通知',
    'Support Center': '帮助与客服',
    'Settings': '门店设置',
    'Name': '名称',
    'Description': '介绍',
    'Price': '价格',
    'Status': '状态',
    'Actions': '操作',
    'Role': '权限角色',
    'Email': '邮箱',
    'Phone': '联系电话',
    'Address': '地址',
    'City': '城市',
    'Currency': '货币',
    'Language': '语言',
    'Theme': '主题',
    'General': '常规',
    'Total Revenue': '总营业额',
    'Total Orders': '总订单数',
    'Average Order': '平均客单价',
    'Active Sessions': '活跃餐桌',
    'Recent Orders': '最新订单',
    'Live Orders': '实时订单',
    'Table': '桌号',
    'Date': '日期',
    'Time': '时间',
    'Amount': '金额',
    'Customer': '顾客',
    'In Stock': '有货',
    'Out of Stock': '售罄',
    'Sold Out': '已售完',
    'Dine In': '堂食',
    'Pickup': '自取',
    'Delivery': '外卖配送',
    'Paid': '已支付',
    'Unpaid': '未支付',
    'Pending': '等待处理',
    'Preparing': '制作中',
    'Ready': '已备好',
    'Completed': '已完成',
    'Cancelled': '已取消',
    'Loading...': '加载中...',
    'No data': '暂无数据',
    'No results found': '未找到相关结果',
    'No items found': '未找到菜品',
    'No translations defined yet': '暂无多语言翻译',
    'Are you sure?': '您确定吗？',
    'This action cannot be undone.': '此操作无法撤销。',
    'Settings saved successfully!': '设置保存成功！',
    'Administrator': '管理员',
    'Manager': '经理',
    'Owner': '店主',
    'Chef': '主厨',
    'Waiter': '服务生',
    'Cashier': '收银员',
    'Type a command or search...': '输入指令或搜索...',
    'Command Palette': '快捷命令',
    'Demand Velocity': '菜品需求热度',
    'DEMAND VELOCITY': '菜品需求热度',
    'Top Performing Entities': '销量最佳菜品',
    'TOP PERFORMING ENTITIES': '销量最佳菜品',
    'Hourly Load Analysis': '分时客流峰值分析',
    'HOURLY LOAD ANALYSIS': '分时客流峰值分析',
    'Bulk Command Center': '批量快捷管理中心',
    'BULK COMMAND CENTER': '批量快捷管理中心',
    'Governance Scan': '菜单健康巡检',
    'GOVERNANCE SCAN': '菜单健康巡检',
    'No compliance issues detected in current menu': '当前菜单全部合规，未发现任何异常',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': '当前菜单全部合规，未发现任何异常',
    'Procedural Tagging': '批量添加菜品标签',
    'PROCEDURAL TAGGING': '批量添加菜品标签',
    'Name match...': '按名称匹配...',
    '...Name match': '按名称匹配...',
    'Healthy': '健康轻食',
    'HEALTHY': '健康轻食',
    'Apply Tags': '应用所选标签',
    'APPLY TAGS': '应用所选标签',
    'Global Price Shift': '全菜单价格统一调整',
    'GLOBAL PRICE SHIFT': '全菜单价格统一调整',
    'Adjust': '调整价格',
    'ADJUST': '调整价格',
    'Availability Override': '菜品供应状态一键控制',
    'AVAILABILITY OVERRIDE': '菜品供应状态一键控制',
    'Reset: ALL IN-STOCK': '重置：全部恢复供应',
    'RESET: ALL IN-STOCK': '重置：全部恢复供应',
    'EMERGENCY STOP (ALL OUT)': '紧急停售：全部标记售罄',
    'Integrity Engine': '菜单数据完整性引擎',
    'INTEGRITY ENGINE': '菜单数据完整性引擎',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': '智能检测系统全天候监测菜品展示状态、价格异常波动以及菜品描述的完整性，确保顾客体验始终如一。',
    'Inventory Audit Export': '菜品与库存数据导出',
    'INVENTORY AUDIT EXPORT': '菜品与库存数据导出',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': '生成并下载包含菜品销量与属性的完整报表，方便离线审计与归档。',
    'Enterprise Data Export': '企业级数据批量导出',
    'ENTERPRISE DATA EXPORT': '企业级数据批量导出',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': '生成并下载行业标准的经营审计报表，涵盖客流动线、菜品热度与订单转化全景数据。',
    'JSON Snapshot': '导出 JSON 快照',
    'JSON SNAPSHOT': '导出 JSON 快照',
    'Compile CSV Report': '生成 CSV 电子表格',
    'COMPILE CSV REPORT': '生成 CSV 电子表格',
    'Date Segment': '日期周期',
    'DATE SEGMENT': '日期周期',
    'Inbound Views': '菜单访问量',
    'INBOUND VIEWS': '菜单访问量',
    'Asset Interactions': '菜品点击与互动',
    'ASSET INTERACTIONS': '菜品点击与互动',
    'Telemetry History': '运营历史记录',
    'TELEMETRY HISTORY': '运营历史记录',
    'Telemetry History (7d)': '运营历史记录 (近7天)',
    'TELEMETRY HISTORY (7D)': '运营历史记录 (近7天)',
    'Growth Trajectory': '客流增长趋势',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': '在过去的7天内，顾客浏览和点击菜单的频次呈现持续增长趋势。',
    'Deep Analysis Engine': '深度数据分析',
    'DEEP ANALYSIS ENGINE': '深度数据分析',
    'Asset Velocity': '菜品热销榜',
    'ASSET VELOCITY': '菜品热销榜',
    'Traffic Projections': '客流趋势预测',
    'Telemetry Signal Tracking': '实时客流信号追踪',
    'Service Hour Update': '服务时间更新',
    'Late Night Lounge Hours': '深夜酒廊时段',
    'Bar & Lounge Hours': '酒吧与休闲区时段',
    'Breakfast & Brunch Hours': '早餐与早午餐时段',
    'Switch Location': '切换门店',
    'SWITCH LOCATION': '切换门店',
    'Global Admin': '全局管理员',
    'GLOBAL ADMIN': '全局管理员',
    'Staff Access': '员工权限',
    'STAFF ACCESS': '员工权限',
    'Venue Overview': '门店总览',
    'VENUE OVERVIEW': '门店总览',
    'Unified Telemetry Engine': '实时运营监控中心',
    'UNIFIED TELEMETRY ENGINE': '实时运营监控中心',
    'Pulse': '实时脉搏',
    'PULSE': '实时脉搏',
    'Insights': '运营洞察',
    'INSIGHTS': '运营洞察',
    'Reports': '财务与销售报表',
    'REPORTS': '财务与销售报表',
    'GOVERNANCE': '连锁治理',
    'Multi-Venue': '多门店管理',
    'MULTI-VENUE': '多门店管理',
    'Menu Views': '菜单浏览量',
    'Item Interactions': '菜品点击互动',
    'Guest Rating': '顾客评分',
    'Revenue (7d)': '营收 (近7天)',
    'Revenue (24h)': '营收 (24小时)',
    'Revenue (30d)': '营收 (近30天)',
    'Delivery orders (7d)': '外卖订单 (近7天)',
    'Delivery orders (24h)': '外卖订单 (24小时)',
    'Delivery orders (30d)': '外卖订单 (近30天)',
    'Delivery revenue': '外卖营业额',
    'Average delivery order': '平均外卖客单价',
    'Average delivery fee': '平均配送费',
    'Zone average': '区域平均',
    'Including fees': '含配送附加费',
    'No deliveries': '暂无外卖订单',
    'No orders': '暂无订单',
    'No reviews': '暂无评价',
    'Live': '实时',
    'LIVE': '实时',
    'reviews': '条评价',
    'orders': '单',
    'deliveries': '次配送',
    'of orders': '订单占比',
    'rate': '转化率',
    'Recent Activity': '最新动态',
    'Real-time Venue Telemetry': '餐厅实时运营监测',
    'REAL-TIME VENUE TELEMETRY': '餐厅实时运营监测',
    'Rapid Pulse View': '实时速览',
    'Anonymous Guest': '匿名顾客',
    'scanned menu': '扫码查看了菜单',
    'viewed item': '查看了菜品详情',
    'interacted with': '点击了',
    'placed order': '提交了新订单',
    'searched menu': '在菜单中搜索了',
    'Main Landing': '主页展示',
    'Trace': '已追踪',
    'TRACE': '已追踪',
    'Gaining Telemetry...': '正在同步实时动态...',
    'No recent traffic detected': '暂无访客动态',
    'Activities will appear here as guests scan your QR code': '当顾客扫描餐桌二维码时，活动记录将实时显示在此处',
    'Analyze All Logs': '查看完整日志分析',
    'Just now': '刚刚',
    'less than a minute ago': '不到一分钟前',
    '1 minute ago': '1分钟前',
    '1 hour ago': '1小时前',
    'about 1 hour ago': '约1小时前',
    '1 day ago': '1天前',
    'DAY AGO 1': '1天前',
    '2 days ago': '2天前',
    'DAY AGO 2': '2天前',
    '3 days ago': '3天前',
    'DAY AGO 3': '3天前',
    '4 days ago': '4天前',
    'DAY AGO 4': '4天前',
    '5 days ago': '5天前',
    'DAY AGO 5': '5天前',
    '6 days ago': '6天前',
    'DAY AGO 6': '6天前',
    '7 days ago': '7天前',
    'DAY AGO 7': '7天前',
    'about 1 month ago': '约1个月前',
    'Monday': '星期一',
    'Tuesday': '星期二',
    'Wednesday': '星期三',
    'Thursday': '星期四',
    'Friday': '星期五',
    'Saturday': '星期六',
    'Sunday': '星期日',
    'Mon': '周一',
    'Tue': '周二',
    'Wed': '周三',
    'Thu': '周四',
    'Fri': '周五',
    'Sat': '周六',
    'Sun': '周日',
    'Opening Hours': '营业时间',
    'Start Time': '开始时间',
    'End Time': '结束时间',
    'Opening Time': '开门时间',
    'Closing Time': '打烊时间',
    'All Day': '全天',
    'Closed': '休息中',
    'Active Days': '营业日',
    'Active Schedule': '生效时段',
    'Always Available': '全时段供应',
    'Always Visible': '始终显示',
    'New Category': '新建分类',
    'Category': '菜品分类',
    'Category Name': '分类名称',
    'Category Description': '分类介绍',
    'Display Order': '显示顺序',
    'Item Name': '菜品名称',
    'Item Description': '菜品描述',
    'Base Price': '基础售价',
    'Preparation Time': '制作耗时',
    'Calories': '热量 (卡路里)',
    'Featured Item': '招牌推荐',
    'Chef\'s Recommendation': '主厨推荐',
    'Spicy': '辣',
    'Vegetarian': '蛋奶素',
    'Vegan': '纯素',
    'Gluten-Free': '无麸质',
    'Halal': '清真',
    'Kosher': '犹太洁食',
    'Contains Nuts': '含坚果',
    'Dairy-Free': '不含乳制品',
    'Allergen Exclusions': '过敏原提示',
    'Table Number': '餐桌号',
    'Assigned Table': '分配桌号',
    'Seats': '座位数',
    'Capacity': '容纳人数',
    'Indoor': '室内',
    'Outdoor': '室外',
    'Terrace': '露台',
    'Bar': '吧台',
    'Add Table': '新增餐桌',
    'Edit Table': '编辑餐桌',
    'Delete Table': '删除餐桌',
    'Bookings': '预订记录',
    'Add to Waitlist': '加入排队等位',
    'Guest Name': '客人姓名',
    'Party Size': '就餐人数',
    'Arrival Time': '到达时间',
    'Special Requests': '特殊要求备注',
    'Confirmed': '已确认',
    'No Show': '未到店',
    'Add Staff Member': '添加员工',
    'Invite Staff': '邀请员工加入',
    'Email Address': '电子邮箱地址',
    'Phone Number': '电话号码',
    'Download QR Code': '下载餐桌二维码',
    'Print Table Stands': '打印桌牌立卡',
    'Today': '今日',
    'Yesterday': '昨日',
    'This Week': '本周',
    'This Month': '本月',
    'Last 30 Days': '近30天',
    'Access Denied': '无权访问',
    'Advanced Settings': '高级设置',
    'Change Image': '更换图片',
    'Remove Image': '移除图片',
    'Announcement Banner': '公告横幅',
    'Please fill in all required fields.': '请填写所有必填项。',
    'Changes saved successfully': '修改已成功保存',
    '(You)': '(您)',
    'AI Auto-Tag': 'AI 自动标签',
    'Analyzing:': '正在分析：',
    'Apple (Fruit/Vegan)': '苹果 (水果/纯素)',
    'Cancel editing': '取消编辑',
    'Capacity:': '容量：',
    'Carrot (Veggie)': '胡萝卜 (蔬菜)',
    'CircleDot (Organic)': '圆点 (有机)',
    'Coffee (Caffeine)': '咖啡 (咖啡因)',
    'Configuration': '配置',
    'Copy verification record name': '复制验证记录名称',
    'Copy verification record value': '复制验证记录值',
    'Critical exception intercepted': '拦截到严重异常',
    'Kernel Panic': '系统错误',
    'DNA (Organic/Bio)': 'DNA (有机/生物)',
    'Egg (Ovo/Allergen)': '鸡蛋 (蛋类/过敏原)',
    'Empty': '空',
    'Engraving...': '正在生成...',
    'Flame (Spicy)': '火焰 (辛辣)',
    'Free': '免费',
    'From': '从',
    'Generating': '生成中',
    'Governance & Traceability Studio': '门店管理与活动中心',
    'Greet': '欢迎',
    'Heart (Healthy)': '爱心 (健康)',
    'In-App': '应用内',
    'Infinity SVG': '矢量 SVG (无损高清)',
    'Leaf (Default)': '树叶 (默认)',
    'MAX Feature': '企业版专属功能',
    'Milk (Dairy)': '牛奶 (乳制品)',
    'Network': '网络',
    'Notify SMS': '短信通知',
    'Order': '订单',
    'Original': '原始',
    'Overview': '概览',
    'PRO: 4 PREDEFINED ROLES': 'Pro：4 种预设角色',
    'Shield (Safety)': '护盾 (安全)',
    'Soup (Warm/Soup)': '热汤 (暖胃/汤品)',
    'Standard Pro View': '标准视图',
    'Star': '星标',
    'Synchronized with Venue Identity': '与您的门店品牌风格同步',
    'Synchronizing Security Context...': '正在建立安全连接...',
    'Targeted Timing': '适用时段',
    'Targeting': '适用对象',
    'To': '至',
    'Toggle Navigation': '切换导航',
    'Venue': '门店',
    'Venue Governance Suite': '门店管理与运营设置',
    'Volume:': '音量：',
    'WHITEBOARD NOTE...': '员工留言或备忘录...',
    'Warning (Alert)': '警告 (提示)',
    'Wheat (Flour/Gluten)': '小麦 (面粉/麸质)',
    'Wine (Alcohol)': '葡萄酒 (含酒精)',
    'YouTube/Vimeo/Direct Link': '视频链接 (YouTube、Vimeo 或 MP4)',
    'Your name': '您的姓名',
    '2. Table Number': '2. 桌号',
    'for Morocco or': '适用摩洛哥，或',
    'in': '在',
    'to': '至',
    'e.g. 12 or Patio-1': '例如：12 或 露台-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': '例如：手作意大利宽面、黑松露、36个月熟成帕玛森干酪...',
    'e.g. Head Sommelier & Wine Lead': '例如：首席侍酒师兼酒水主管',
    'e.g. London': '例如：伦敦',
    'e.g. SOMMELIER, HOST, BARISTA': '例如：侍酒师、迎宾领位、咖啡师',
    'e.g. Skyline Lounge': '例如：天际休闲吧',
    'e.g. TRUFFLE OIL': '例如：松露油',
    'e.g. The Grand Cafe': '例如：大都会咖啡馆',
    'e.g. Window preference, high chair': '例如：靠窗位置、儿童高脚椅',
    'Copied to clipboard!': '已复制到剪贴板！',
    'Copy failed.': '复制失败。',
    'Link copied!': '链接已复制！',
    'Logo uploaded': '标志已上传',
    'Cover image uploaded': '封面图片已上传',
    'Profile saved and synced successfully': '门店资料已保存并同步成功',
    'Category Created': '分类已创建',
    'Category Updated': '分类已更新',
    'Category Deleted': '分类已删除',
    'Item Created': '菜品已添加',
    'Item Updated': '菜品已更新',
    'Item Deleted': '菜品已删除',
    'Menu Published': '菜单已发布',
    'Delete Category?': '确认删除该分类？',
    'Delete Menu Item?': '确认删除该菜品？',
    'Delete Ingredient?': '确认删除该配料？',
    'Delete Service Period?': '确认删除该服务时段？',
    'Remove team member?': '确认移除该团队成员？',
    'Cancel invitation?': '确认撤回邀请？',
    'Revoke API key?': '确认吊销该 API 密钥？',
    'Delete webhook endpoint?': '确认删除该 Webhook 节点？',
    'Clear all notifications?': '清除所有通知？',
    'Disconnect Stripe account?': '确认断开 Stripe 账户？',
    'Disconnect Google Business Profile?': '确认断开 Google 商家资料连接？',
    'Campaigns published successfully!': '营销活动已成功发布！',
    'Changes successfully synchronized with Google Business Profile!': '更改已成功同步至 Google 商家资料！',
    'Google Business Profile successfully connected!': 'Google 商家资料连接成功！',
    'Google Business Profile disconnected.': 'Google 商家资料已断开连接。',
    'Google Post draft saved successfully!': 'Google 帖子草稿已保存成功！',
    'Post draft deleted.': '帖子草稿已删除。',
    'Review response published successfully!': '评价回复已发布成功！',
    'SEO configuration saved successfully!': 'SEO 设置已成功保存！',
    'SEO metadata translated across all languages!': 'SEO 元数据已翻译为所有支持的语言！',
    'Venue profile & bio translated across all languages!': '门店资料与简介已翻译为所有语言！',
    'Reservation configuration saved successfully': '预订设置已成功保存',
    'Stripe settings saved successfully.': 'Stripe 设置保存成功。',
    'Print-Ready PDF downloaded successfully': '高精度打印 PDF 下载成功',
    'Vector QR PDF downloaded successfully': '矢量 QR 码 PDF 下载成功',
    'Enterprise 300 DPI Pre-Press Package generated successfully': '300 DPI 印刷级文件包生成成功',
    'At least one language must be enabled.': '必须至少启用一种语言。',
    'Enter a non-zero percentage before adjusting prices.': '调整价格前请输入非零的百分比值。',
    'Please enter a Venue Name before translating.': '在开始翻译之前，请先输入门店名称。',
    'Please enter an item name before running dietary and allergen analysis.': '在进行饮食与过敏原分析之前，请先输入菜品名称。',
    'Please write a reply before submitting.': '提交前请先编写回复内容。',
    'Table 3 Called Waiter': '3号桌呼叫服务员',
    'Table Call Bell': '餐桌呼叫铃',
    'Waiter Calls': '服务呼叫',
    'Waiter Dispatch & Calls': '服务呼叫与调度',
    'Kitchen Display & Whiteboard': '厨房显示屏与白板',
    'Customer Orders': '客户订单',
    'Bakery': '烘焙面包房',
    'Bar / Pub': '酒吧 / 酒馆',
    'Café / Coffee Shop': '咖啡厅 / 饮品店',
    'Fast Food': '快餐简餐',
    'Gourmet / Fine Dining': '高端精致料理',
    'Hotel': '酒店 / 宾馆',
    'General Local Business': '综合本地门店',
    'Classic': '经典',
    'Clean': '极简纯净',
    'Modern': '现代',
    'Elegant': '典雅高贵',
    'Energetic': '活力四射',
    'Glass': '玻璃磨砂',
    'Playful & Casual': '轻松休闲',
    'Sophisticated & Gourmet': '考究美馔',
    'Warm & Welcoming': '温馨热情',
    'Executive': '尊享商务',
    'Upgrade required: Multi-language menus are available on the Pro plan.': '需升级计划：多语言数字菜单仅在专业版 (Pro) 中提供。',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': '需升级计划：线上点单与服务呼叫功能在入门免费版中不可用。',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': '矢量 SVG 导出是专业版与旗舰版专属功能。请升级计划以导出无损高清矢量图。',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': '印刷级 PDF 导出是专业版与旗舰版专属功能。请升级计划以导出高保真印刷文件。',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': '专业版限制最多 3 个活跃营销活动。请暂停其他活动或升级至旗舰版 (MAX) 享受无限制活动。',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': '入门版包含最多 20 张餐桌 (1–20号)。升级至专业版 (PRO) 可享无限餐桌。',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': '入门版支持数字编号餐桌 (1–20)。自定义餐桌名称 (如露台、VIP) 需要专业版 (PRO)。',
    'Add Menu Categories & Items': '添加菜单分类与菜品',
    'Add Menu Items': '添加菜单菜品',
    'Organize your menu sections and food items.': '轻松规划和管理您的菜单分栏与菜品。',
    'Add and manage your food & beverage offerings.': '添加并管理您的美食和饮品组合。',
    'Customize Venue Branding': '自定义门店品牌形象',
    'Set your brand colors, logo, and presentation.': '设置品牌主色调、图标 Logo 以及菜单展示风格。',
    'Get Your QR Code & Go Live': '获取专属 QR 码并即刻上线',
    'Download table QR codes and preview your live menu.': '下载餐桌专属二维码，并在手机上实时预览菜单效果。',
    'Account & Venue Created Successfully!': '账户与门店均已创建成功！',
    '⚡ Off-Peak Hours': '⚡ 非高峰闲时',
    '🍽️ Lunch Crowd': '🍽️ 午市商务客流',
    '🚀 Weekend Rush': '🚀 周末黄金客流',
    '🌟 Seasonal Special': '🌟 季节限定特惠',
    '📣 Engagement Buzz': '📣 社交口碑互动',
    'Festive & Energetic': '欢聚庆典',
    'Urgent & High-Conversion': '限时强推',
    'Generated 3 tailored campaign strategies': '已成功生成 3 套专属定制营销策略',
    'Could not generate campaign ideas. Please try again.': '未能生成活动灵感，请稍后重试。',
    'Venue Themes': '门店视觉主题',
    'Choose the character of your public menu': '为您面向顾客的数字菜单挑选最契合的视觉格调',
    'Glassmorphism & Depth': '磨砂玻璃质感与立体景深',
    'Independent XML Sitemap': '独立 XML 网站地图',
    'Structured Schema & Sync': '结构化数据与搜索同步',
    'Improve how your venue appears on Google and in local search results.': '全面优化您的门店在 Google 以及本地周边搜索结果中的展示效果。',
    'Terms of Service': '服务条款',
    'Confirm your email': '验证您的电子邮箱',
    'Check your spam / junk folder': '请检查您的垃圾邮件文件夹',
    'Commercial pre-press print production package': '商业印刷级出版文件包',
    'Over-bleed safety margin': '出血安全裁切边距',
    'Crop Marks': '裁切标记',
    'Guillotine cutting guides': '切纸裁切导线',
    'Global Banners': '全局置顶通告横幅',
    'Featured Items': '招牌主推特色菜',
    'AI Assistant': 'AI 智能助手',
    'Custom Role Schemas': '自定义岗位权限体系',
    'Notification Preferences': '通知接收偏好设置',
    'Preferences saved automatically': '偏好设置已自动保存',
    'Menu Extractor (PDF & Images)': '菜单智能识别提取 (PDF 与图片)',
    'Multi-Language Translation': '多语言智能翻译',
    'Start Menu Translation': '开始翻译菜单',
    'Menu Items Hub': '菜品管理中心',
    'Open Menu Items': '打开菜品列表',
    'Enterprise Traceability': '企业级操作审计与追溯',
    'Full History': '完整操作历史',
    'Billing & Plans': '账单与订阅套餐',
    'Subscription Governance': '订阅状态与方案管理',
    'Platform Usage': '功能与配额用量',
    'Menu Page Views': '数字菜单访问量',
    'Enterprise Identity': '企业身份认证',
    'Access Governance': '访问控制与安全策略',
    'Required Fields *': '必填项 *',
    'Enabled Languages': '已启用的语言',
    'Venue Info & Bio Localizations': '门店基本信息与简介的多语言本地化',
    'Public Address': '公开展示地址',
    'Public URL Slug': '公开访问链接后缀',
    'Contact Phone': '联系电话',
    'Business Address': '营业地址',
    'No Categories Yet': '暂无菜单分类',
    'No Custom Roles Configured': '未配置任何自定义岗位角色',
    'No Google reviews retrieved yet': '暂未获取到 Google 客户评价',
    'No Guests Found': '未找到符合条件的顾客',
    'No Menu Items Yet': '菜单中暂未添加任何菜品',
    'No Promotions Configured': '暂无配置任何营销促销活动',
    'No Service Periods Yet': '暂未设置任何服务时段',
    'No Support Tickets': '暂无支持工单',
    'No Tables on Floor Plan': '平面图上暂未布置餐桌',
    'No Team Members Found': '未找到团队成员',
    'No Upcoming Events': '暂无即将举行的活动',
    'No Walk-Ins Recorded': '暂无现场直接到店客流记录',
    'No items are available right now': '当前时段暂无在售菜品',
    'No notifications found': '暂无通知消息',
    'No reservations matching your filters': '未找到符合筛选条件的预订',
    'No tables currently occupied': '当前暂无在座就餐桌台',
    'No venue selected': '未选择任何门店',
    'Waitlist': '等候排队名单',
    'Waitlist is Empty': '当前排队等待列表为空',
    'Waves (Gluten)': '麦穗 (含麸质)',
    'We sent a temporary verification code to': '我们已向以下地址发送了临时验证码：',
    'Website URL': '官网网址',
    'What the customer receives': '顾客将获得的权益',
    'White-label control is available on eligible plans.': '白标去水印自主品牌功能仅在高级方案中提供。',
    'Why this works:': '方案优势与成效解析：',
    'WiFi Broadcast': '顾客 WiFi 广播连接',
    'WiFi Password': 'WiFi 密码',
    'Window seat, dietary request, birthday celebration...': '靠窗座位、特定饮食需求、生日庆祝等...',
    'Your Published Response': '您已正式发布的回复',
    'Your Venues': '您旗下的门店',
    '🎉 Ready to publish!': '🎉 准备就绪，可以发布！',
    'e.g. Grill, Salad, Pastry': '例如：烧烤扒房、生鲜沙拉、精致烘焙',
    'e.g. Guest called, emergency': '例如：客人致电取消、紧急状况',
    'e.g. Italian, Moroccan, Burgers': '例如：意式风味、摩洛哥特色、汉堡简餐',
    'e.g. Mains, Pasta, Starters': '例如：精选主菜、意面披萨、开胃前菜',
    'e.g. Signature Truffle Pasta': '例如：招牌黑松露手作意面',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': '例如：午后咖啡悠享特惠、畅饮时光 85折',
    'e.g., Breakfast, Happy Hour': '例如：晨光早市、晚间欢乐时光',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': '例如：每周五晚 8 点，与我们一起沉浸于浪漫爵士现场音乐！',
  },
  ja: {
    '123 Main St, City': '〇〇市中央通り 123',
    'Balance': 'ポイント残高',
    'Lifetime': '累計獲得ポイント',
    'Source': '注文元 / 獲得元',
    'Menu': 'メニュー',
    'Advanced': '詳細設定',
    'Core Suite': '基本機能',
    'Privacy': 'プライバシー',
    'Support': 'カスタマーサポート',
    'Save Layout': 'フロアレイアウトを保存',
    'Shape:': 'テーブル形状:',
    'SEARCH LIBRARY...': 'ライブラリを検索...',
    'Search library...': 'ライブラリを検索...',
    'None': 'なし',
    'Type': '種類',
    'Target': '対象',
    'Value / Target': '設定値 / 対象',
    'London (GMT/BST)': 'ロンドン (GMT/BST)',
    'New York (EST/EDT)': 'ニューヨーク (EST/EDT)',
    'Chicago (CST/CDT)': 'シカゴ (CST/CDT)',
    'Denver (MST/MDT)': 'デンバー (MST/MDT)',
    'Dubai (GST)': 'ドバイ (GST)',
    'Tokyo (JST)': '東京 (JST)',
    'Sydney (AEST/AEDT)': 'シドニー (AEST/AEDT)',
    'Start Color': '開始色',
    'Depth Shade': 'シャドウ・陰影深度',
    'Matrix Geometry': 'QRコードのドット形状',
    'Eye Architecture': 'QRコードの角枠デザイン',
    'Brand Centerpiece': 'QRコード中央のロゴ',
    'Scannability Shield': 'スキャン認識性の自動補正',
    'Environment Blur': '背景ぼかし効果',
    'Color Target:': 'カラー出力プロファイル:',
    'DPI:': 'DPI (解像度):',
    '3mm Bleed Box': '3mm 印刷裁断トンボ (塗り足し)',
    '300 DPI': '300 DPI (高解像度印刷)',
    'PRO+': 'PRO+ プラン',
    'SAML 2.0': 'SAML 2.0 認証プロトコル',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'IdP エンティティID (Entity ID)',
    'SSO URL': 'SSO ログインURL',
    'X.509 Certificate': 'X.509 セキュリティ証明書',
    'ACS URL': 'ACS URL (Assertion Consumer Service)',
    'Entity ID': 'エンティティID',
    'Navigate': '移動',
    'Select': '選択',
    'OmniSearch v1.0': '全機能・統合検索 (OmniSearch)',
    'Back to order review': '注文確認画面に戻る',
    'Return to Intelligence Studio': 'アナリティクス・ハブに戻る',
    'View Full Notification Center': 'すべての通知を確認',
    'Read all': 'すべて既読にする',
    'Cancel department': '部門設定をキャンセル',
    'Save department': '部門を保存',
    'Venue Segmentation': 'フロア・エリア区分',
    'Collaborate on your menu together.': 'チーム全員でメニューを共有・編集。',
    'Timeline': 'タイムライン表示',
    'Calendar': '予約カレンダー',
    'Book': '予約する',
    'Duration': 'ご利用時間',
    'Time & Duration': '時間と利用枠',
    'Seating Preference': '座席の希望 (窓際/テラス等)',
    'Section': 'フロアエリア',
    'Min Capacity': '最小着席人数',
    'Max Capacity': '最大定員人数',
    'Phone (Optional)': '電話番号 (任意)',
    'Name or Walk-in': 'お客様のお名前またはウォークイン',
    'Next day': '翌日',
    'Next month': '翌月',
    'Live Connection': 'リアルタイム接続',
    'Live Inventory': 'リアルタイム在庫管理',
    'Main Venue (Full Menu)': 'メイン店舗 (フルメニュー)',
    'Preview Menu': 'メニューをプレビュー',
    'Manual Setup Guide': '手動セットアップ手順ガイド',
    'Connected & Active': '接続完了・稼働中',
    'CTA Action': 'アクションボタン (CTA)',
    'Phone Contact': '店舗お問い合わせ電話番号',
    'Menu URL': 'オンラインメニューURL',
    'Google Location': 'Googleマップ掲載位置',
    'Field': '入力項目',
    'Linked': '連携中',
    'Synchronizing Hub...': 'データを同期中...',
    'Data Integrity': 'データ整合性・信頼性',
    'Station Governance Suite': 'キッチン・フロア端末管理',
    'Open audit log': '監査ログを表示',
    'Signal / Time': 'イベント / 記録日時',
    'Identity': '担当者 / ユーザー情報',
    'Invitation Invalid': '招待リンクが無効または有効期限切れです',
    'Invited To': '参加招待先:',
    'Team Limit Reached': 'スタッフ登録上限に達しました',
    'Venue Limit Reached': '登録可能な店舗数の上限に達しました',
    'Venue Setup Required': '店舗の初期設定が必要です',
    'Create Venue': '新規店舗を追加',
    'TableGreet Onboarding': 'TableGreet かんたん初期設定',
    'No payment required': 'お支払い不要・完全無料',
    'Selected Plan': '選択中のプラン',
    'Payment Method': 'お支払い方法',
    'Invoice ID': '請求書番号',
    'Verified': '認証済み',
    'Accent color': 'アクセントカラー',
    'Set the voice and overall light level.': '店舗のトーンと全体の明るさ（ライト/ダーク）を設定。',
    'Layout architecture': 'レイアウト構成',
    'Navigation & motion': 'ナビゲーション & モーション',
    'Loading preview engine': 'プレビューエンジンを読み込み中',
    'Real Live Preview': 'リアルタイム実機プレビュー',
    'Included with Pro & Max': 'Pro & Max プランで利用可能',
    'Daypart Scheduling': '時間帯別 (デイパート) スケジュール',
    'Operational Daypart Architecture': '営業シフト・提供時間体系',
    'Service Protocol': 'サービス運用規程',
    'Configuration Engine': '設定管理エンジン',
    'Linked Department': '関連付けられた部門',
    'Team Hub': 'スタッフ・チーム管理',
    'Team Size': 'スタッフ人数',
    'Joined': '参加日時',
    'Cancel Invitation': '招待を取り消す',
    'Initialize New Organization': '新しい組織・法人を開設',
    'Operational': '正常稼働中',
    'Pro Feature': 'Proプラン専用機能',
    'Current State': '現在の稼働状態',
    'Open on the big TV': '大型テレビ画面で表示',
    'Menu Board': 'デジタルメニューボード',
    'Reward': 'リワード・特典',
    'Reward name': '特典名',
    'Cancel & refund': 'キャンセルして返金',
    'Code': 'コード',
    'Find': '検索',
    'Complete': '完了',
    'Seated': '着席中',
    'No-Show': '来店なし (No-Show)',
    'Walk-in': '予約なし来店',
    'Party': '来店人数',
    'Turnover': 'テーブル回転率',
    'We couldn\'t load the menu': 'メニューを読み込めませんでした',
    'Venue Not Found': '店舗が見つかりません',
    'Live Availability': 'リアルタイム提供状況',
    'Multi-Language Suite': '多言語翻訳管理',
    'Key name': 'APIキー名',
    'Revoke API key': 'APIキーを失効',
    'Delete webhook': 'Webhookを削除',
    'Identity Provider': 'IDプロバイダー (IdP)',
    'Provider': 'プロバイダー',
    'Mandatory MFA': '多要素認証の必須化 (MFA)',
    'Account Identity': 'アカウント情報・認証',
    'Login Email': 'ログイン用メールアドレス',
    'New Login Email': '新しいログイン用メールアドレス',
    'Phone Login': '電話番号ログイン',
    'SMS Verification Code': 'SMS認証コード',
    'Integration Method': 'システム連携方式',
    'Stripe Connect': 'Stripe Connect オンライン決済',
    'Stripe Secret Key': 'Stripe シークレットキー',
    'Stripe Webhook Secret': 'Stripe Webhook シークレット',
    'Client-Owned': '自店舗保有・直接管理',
    'Verify connection': '接続状態を確認',
    'Setup Health': '初期設定の完了状況',
    'Privacy Protected': 'プライバシー厳格保護',
    'Privacy Policy': 'プライバシーポリシー',
    'Continue': '次へ進む',
    'Sign In': 'ログイン',
    'Didn\'t get it?': '届かない場合',
    'Logged In Account': 'ログイン中のアカウント',
    'Annual': '年払い (お得な年間契約)',
    'Unread': '未読',
    'Starred': 'スター付き',
    'High / Urgent': '高 / 緊急',
    'Mark Read': '既読にする',
    'Delete notification': '通知を削除',
    'Urgent': '至急',
    'High': '高',
    'Normal': '通常',
    'Low': '低',
    'Select a Ticket': 'サポートチケットを選択',
    'Urgent (Immediate attention)': '至急 (即時対応が必要)',
    'Type your reply here...': '返信メッセージを入力...',
    'Print Card (PDF)': '卓上POP・テーブルカード印刷 (PDF)',
    'Advanced Printing Engine': '高解像度・印刷出力エンジン',
    'Multi-Venue Governance': '複数店舗・チェーン統括管理',
    'Network Revenue': '全店舗ネットワーク合計売上',
    'Live Network Total': 'リアルタイム店舗ネットワーク合計',
    'Audit Log Hub': '操作履歴・監査ログハブ',
    'Export Log': 'ログを書き出し',
    'CUSTOM ROLE SCHEMAS': 'カスタムスタッフ権限・役割設定',
    'Unique internal role identifier.': 'システム内部用の一意な役割識別子。',
    'Human-friendly role label.': 'チームにわかりやすい職種・役職名。',
    'Marketing Hub': 'マーケティング管理',
    'Revenue Generation Suite': '売上向上・プロモーション機能',
    'Active Campaign Limit Reached (3/3)': '有効なキャンペーン上限に達しました (3/3)',
    'Banner Content': 'バナー表示内容',
    'Style Profile': 'スタイル設定',
    'Action URL (Optional)': 'リンク先URL (任意)',
    'Promotion Label': 'プロモーション名',
    'Target Selection': '対象商品の選択',
    'Edit Promotion': 'プロモーションを編集',
    'Delete Promotion': 'プロモーションを削除',
    'Loyalty Engine': 'ロイヤルティ・ポイント管理',
    'Reward catalog': '特典・リワード一覧',
    'Add reward': '特典を追加',
    'Loyalty ID': '会員番号 / 会員ID',
    'No point activity yet': 'ポイント履歴はまだありません',
    'Scan or enter QR value': 'QRをスキャンまたは番号を入力',
    'TableGreet Admin': 'TableGreet 店舗管理画面',
    'Capacity Pacing': '混雑緩和・予約ペース管理',
    'Cancellation Policy': '予約キャンセル規定',
    'Booking Experience': 'テーブル予約体験',
    'Floor Occupancy': 'フロア・客席稼働率',
    'Currently Seated': '現在ご着席中',
    'Seat Party': 'お席へご案内',
    'Seat Table': 'テーブルに着席',
    'Select Table': 'テーブルを選択',
    'Table / Capacity': 'テーブル / 定員数',
    'Table Name/Number': '卓番号 / テーブル名',
    'Mark Arrived': '来店済みに変更',
    'Mark No-Show': '無断キャンセルとして記録',
    'Complete Dining': 'お会計・退店完了',
    'Standard Dining (Default)': '通常フロア席 (標準)',
    'Walk-In Dining Log': 'ウォークイン (予約なし来店) 記録',
    'Notification Center': '通知センター',
    'Single Sign-On': 'シングルサインオン (SSO)',
    'Welcome Aboard!': 'TableGreet へようこそ！',
    'Select your TableGreet plan': '店舗に合わせた TableGreet プランを選択',
    'Free Plan Activation': '無料プランの利用開始',
    'Venue Created!': '店舗の登録が完了しました！',
    'Upgrade for Unlimited': '無制限プランにアップグレード',
    'Auto-Tag Menu': 'メニューの自動タグ付け',
    'Start Auto-Tagging': '自動タグ付けを開始',
    'Launch Auto-Tagger': '自動タグ機能を起動',
    'Launch PDF Importer': 'PDFメニュー取り込みを開始',
    'Analyzing Menu...': 'メニューを分析中...',
    'Analyzing Recipe...': 'レシピと食材を分析中...',
    'Manual Availability': '手動在庫・提供管理',
    'Technical Breakdown': '技術・スペック詳細',
    'Dietary Taxonomy': '食事制限・アレルギー区分',
    'Dietary & Allergen AI': 'アレルギー・食事AI判定',
    'Inherit from Category': '所属カテゴリーの設定を継承',
    'Ingredient Library': '食材・原材料ライブラリ',
    'Ingredient Name': '原材料・食材名',
    'Menu Organization': 'メニュー構成・整理',
    'Inventory Taxonomy Hub': 'メニュー分類・品目管理ハブ',
    'Category Intelligence': 'カテゴリー分析インサイト',
    'Service Period': '提供時間帯 (モーニング/ランチ/ディナー)',
    'Manual Configuration / All Day': '手動設定 / 終日提供',
    'Operational Department': '担当部門',
    'Global / No Department': '全店舗共通 / 特定部門なし',
    'Cancel category': 'カテゴリー編集をキャンセル',
    'New category name...': '新規カテゴリー名...',
    'Save category': 'カテゴリーを保存',
    'QR Matrix Only': 'QRコード本体のみ',
    'Vector & PDF': 'ベクター & 印刷用PDF',
    'Vector QR (SVG)': 'ベクターQRコード (SVG)',
    'Vector QR (PDF)': '印刷用QRコード (PDF)',
    'Save QR Style': 'QRコードデザインを保存',
    'Share Link': 'リンクを共有',
    'High Fidelity 300DPI': '高解像度 300 DPI 印刷品質',
    'Generating 300 DPI PDF...': '300 DPI 印刷用PDFを生成中...',
    'Give your venue a point of view.': '店舗の世界観を伝える、洗練されたデザインを設定。',
    'Signage Menu Not Found': 'サイネージ用メニューが見つかりません',
    'Scan Menu': 'QRコードをスキャン',
    'Open on your phone': 'スマートフォンで開く',
    'Full Screen': '全画面表示',
    'Lock TV board': 'テレビ画面を固定',
    'Big-Screen Menu': '大型モニター用メニュー',
    'TV Signage': 'TVデジタルサイネージ',
    'General Information': '基本情報',
    'Venue Name *': '店舗名 *',
    'Public Slug *': '公開URL識別名 *',
    'Venue Logo': '店舗ロゴ',
    'Hero Cover Image': 'メインカバー画像',
    'Venue Timezone': '店舗のタイムゾーン',
    'Primary Brand Color': 'メインブランドカラー',
    'TableGreet footer credit': 'フッターのTableGreetクレジット',
    'White-Label Branding': 'ホワイトレーベル・独自ブランド化',
    'Client DNS Setup': '独自ドメイン DNS 設定',
    'Copy CNAME target': 'CNAMEターゲットをコピー',
    'Copy TableGreet URL': 'TableGreet URLをコピー',
    'Included TableGreet URL': '標準提供 TableGreet URL',
    'Open TableGreet URL': 'TableGreet URLを開く',
    'Public menu': 'お客様向け公開メニュー',
    'Public venue delivery': '公開メニューからのデリバリー注文',
    'Zone name': '配送エリア名',
    'Coverage note': '配送対象エリアの案内',
    'Default ETA min': '目安最短お届け時間 (分)',
    'Default ETA max': '目安最長お届け時間 (分)',
    'Packaging fee': '包装・容器代',
    'Courier handled by': '配達担当',
    'Third-party courier': '外部提携デリバリー業者',
    'Unified Experience': '統合型ゲスト体験',
    'Save WiFi to Venue': '店舗Wi-Fi情報を保存',
    'Network Name (SSID)': 'ネットワーク名 (SSID)',
    'Audio Alert Chime': '注文・呼び出し通知音',
    'Time Format': '時間表示形式',
    'Auto / Default': '自動 / デフォルト',
    'Syncing SEO & Google Data...': 'SEOおよびGoogleデータを同期中...',
    'Google Connection': 'Google連携設定',
    'Search Engine Metadata': '検索エンジン用メタデータ',
    'Social card preview': 'SNSシェアカードプレビュー',
    'Allow Indexing': '検索インデックスを許可',
    'Canonical URL Override': '正規URLの個別指定 (Canonical)',
    'Price Range': '価格帯目安',
    'Latitude': '緯度',
    'Longitude': '経度',
    'Google Place ID / Location ID': 'Google プレイスID (Place ID)',
    'Google Search Preview': 'Google 検索結果プレビュー',
    'Linked Google Location': '連携済み Google ビジネス所在地',
    'SEO Documentation': 'SEO最適化ガイド',
    ', and': '、',
    'and': 'および',
    'Delete Department?': '部門を削除しますか？',
    'Delete Department': '部門を削除',
    'Live URL': '公開URL',
    'Copy full live URL': '公開URLをコピー',
    'Open live department menu': '部門の公開メニューを開く',
    'Loading departments...': '部門を読み込み中...',
    'Multi-department venue segmentations are premium features available on PRO and above.': '複数部門による店舗区分管理は、PROプラン以上でご利用いただけます。',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'フロア、個室、バー、テラスなどのエリアごとにメニューを整理します。',
    'Targeted Ordering': 'エリア別注文ルーティング',
    'Route guests and orders to specific preparation areas.': 'お客様や注文を指定の調理・提供エリアへ直接ルーティングします。',
    'Operational Departments': '営業部門管理',
    'OPERATIONAL DEPARTMENTS': '営業部門管理',
    'Venue Segmentation Hub': 'フロア・エリア区分管理',
    'VENUE SEGMENTATION HUB': 'フロア・エリア区分管理',
    'Create departments to group your menu sections. For example, a large venue might have a': 'メニューの各エリアをまとめる部門を作成します。例えば大型店舗では、',
    'Restaurant': 'レストラン',
    'Poolside': 'プールサイド',
    'department, each with its own categories.': 'を設け、それぞれに独自のカテゴリーを持たせることができます。',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': '部門機能を使うと、レストラン、バー、プールサイドなどのエリアに店舗を分割し、それぞれにカテゴリーを設定できます。',
    'Department name': '部門名',
    'New department name...': '新しい部門名を入力...',
    'No Linked Daypart (Always Available)': '営業時間帯の連動なし（常時利用可能）',
    'Linked Daypart': '連動する営業時間帯',
    'Localizations': '多言語設定',
    'LOCALIZATIONS': '多言語設定',
    'AI Auto-Translate': 'AI自動翻訳',
    'AI AUTO-TRANSLATE': 'AI自動翻訳',
    'Translated name': '翻訳された名称',
    'No Departments Yet': '部門はまだ登録されていません',
    'Add First Department': '最初の部門を追加',
    'Avg Order Value': '平均注文額',
    'AVG ORDER VALUE': '平均注文額',
    'item taps': '回の料理タップ',
    'ITEM TAPS': '回の料理タップ',
    'Avg Rating': '平均評価',
    'AVG RATING': '平均評価',
    'Revenue (Last 7 Days)': '売上（過去7日間）',
    'REVENUE (LAST 7 DAYS)': '売上（過去7日間）',
    'Daily breakdown': '日別内訳',
    'DAILY BREAKDOWN': '日別内訳',
    'Menu Views (Last 7 Days)': 'メニュー閲覧数（過去7日間）',
    'MENU VIEWS (LAST 7 DAYS)': 'メニュー閲覧数（過去7日間）',
    'Guest traffic': '来店客の動向',
    'GUEST TRAFFIC': '来店客の動向',
    'Most Viewed Items': '最も見られている料理',
    'MOST VIEWED ITEMS': '最も見られている料理',
    'Top Rated Items': '最高評価の料理',
    'TOP RATED ITEMS': '最高評価の料理',
    'Waiter Requests': '店員呼び出し履歴',
    'WAITER REQUESTS': '店員呼び出し履歴',
    'Total': '合計',
    'TOTAL': '合計',
    'Order Status (30 days)': '注文ステータス（30日間）',
    'ORDER STATUS (30 DAYS)': '注文ステータス（30日間）',
    'Recent Guest Feedback': '最新のお客様レビュー',
    'RECENT GUEST FEEDBACK': '最新のお客様レビュー',
    'Conversion Rate': '注文転換率',
    'CONVERSION RATE': '注文転換率',
    'of menu visitors placed an order': 'の閲覧客が実際に注文しました',
    'Refreshed': '更新済み',
    'REFRESHED': '更新済み',
    'Last 30 days': '過去30日間',
    'LAST 30 DAYS': '過去30日間',
    'cash': '現金',
    'Cash': '現金',
    'CASH': '現金',
    'bill': 'お会計',
    'Bill': 'お会計',
    'BILL': 'お会計',
    'water': 'お冷・水',
    'Water': 'お冷・水',
    'WATER': 'お冷・水',
    'service': 'サービス',
    'Service': 'サービス',
    'SERVICE': 'サービス',
    'No interaction data yet': 'インタラクションデータはまだありません',
    'No ratings collected yet': '収集された評価はまだありません',
    'No requests in period': '指定期間内に呼び出しはありません',
    'No feedback collected yet': 'お客様からのレビューはまだありません',
    'No menu views recorded yet': 'メニューの閲覧はまだ記録されていません',
    'Dish Rating Breakdown': '料理別評価内訳',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': '料理のランキングと顧客満足度スコアは、MAXおよびEnterpriseプラン限定です。',
    'Customer Sentiment & Reviews': 'お客様の声と感情分析',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'お客様からの直接レビューや感情分析の閲覧には、MAXまたはEnterpriseプランが必要です。',
    'Reviews AI': 'レビュー分析AI',
    'Guest Feedback': 'お客様のフィードバック',
    'Upgrade to unlock rating analytics →': 'アップグレードして評価分析をアンロック →',
    'Upgrade to Max': 'Maxプランにアップグレード',
    'UPGRADE TO MAX': 'MAXプランにアップグレード',
    'MAX PLAN': 'MAXプラン',
    'No Data Yet': 'データはまだありません',
    'Share your QR code to start collecting real venue insights.': 'QRコードを共有して、店舗の実際のデータ分析を始めましょう。',
    'Revenue Analytics': '売上分析',
    'REVENUE ANALYTICS': '売上分析',
    'Track your earnings, orders, and average ticket values over time.': '売上、注文数、客単価の推移を時系列で把握できます。',
    'Operational Velocity': 'オペレーション速度',
    'OPERATIONAL VELOCITY': 'オペレーション速度',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': '来客数、人気メニュー、ホールスタッフの呼び出し負荷を可視化します。',
    'Upgrade to Pro': 'Proにアップグレード',
    'UPGRADE TO PRO': 'PROプランにアップグレード',
    'Loading Insights…': '分析データを読み込み中…',
    'Loading Insights...': '分析データを読み込み中...',
    'Telemetry Unavailable': 'アクティビティデータは利用できません',
    'TELEMETRY UNAVAILABLE': 'アクティビティデータは利用できません',
    'No Data Captured': '記録されたデータはありません',
    'NO DATA CAPTURED': '記録されたデータはありません',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'QRコードを共有して、来客と注文のデータをリアルタイムで収集しましょう。',
    'INTELLIGENCE STUDIO': '分析スタジオ',
    'Insights Engine Locked': '高度な分析機能はロックされています',
    'INSIGHTS ENGINE LOCKED': '高度な分析機能はロックされています',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': '高度なトレンド分析、一括管理、データ出力は次のユーザー限定です：',
    'Unlock Pro Features': 'Pro機能をアンロック',
    'UNLOCK PRO FEATURES': 'PRO機能をアンロック',
    'MENU VIEWS': 'メニュー閲覧数',
    'ITEM INTERACTIONS': '料理のクリック数',
    'GUEST RATING': 'お客様の評価',
    'DELIVERY REVENUE': 'デリバリー売上',
    'AVERAGE DELIVERY ORDER': 'デリバリー平均注文額',
    'AVERAGE DELIVERY FEE': '平均配送料',
    'INCLUDING FEES': '手数料込み',
    'ZONE AVERAGE': 'エリア平均',
    'TRAFFIC PROJECTIONS': '来店客数の予測推移',
    'TELEMETRY SIGNAL TRACKING': 'アクティビティ信号のトラッキング',
    'VEGETARIAN': 'ベジタリアン',
    'VEGAN': 'ヴィーガン',
    'SPICY': 'スパイシー',
    'GLUTEN-FREE': 'グルテンフリー',
    'Reset: All In-Stock': 'リセット：全品在庫あり',
    'Emergency Stop (All Out)': '緊急停止：全品品切れ',
    'No compliance issues': '規約上の問題はありません',
    'NO COMPLIANCE ISSUES': '規約上の問題はありません',
    'detected in current menu': '現在のメニュー内',
    'DETECTED IN CURRENT MENU': '現在のメニュー内',
    'Telemetry History (24h)': 'アクティビティ履歴 (24時間)',
    'TELEMETRY HISTORY (24H)': 'アクティビティ履歴 (24時間)',
    'Telemetry History (30d)': 'アクティビティ履歴 (30日間)',
    'TELEMETRY HISTORY (30D)': 'アクティビティ履歴 (30日間)',
    'Save': '保存',
    'Saved': '保存済み',
    'Saving...': '保存中...',
    'Save Changes': '変更を保存',
    'Save Settings': '設定を保存',
    'Cancel': 'キャンセル',
    'Close': '閉じる',
    'Create': '作成',
    'Add': '追加',
    'Edit': '編集',
    'Delete': '削除',
    'Remove': '削除',
    'Update': '更新',
    'Refresh': '再読み込み',
    'Search': '検索',
    'Search...': '検索...',
    'Search items...': 'メニューを検索...',
    'Search categories...': 'カテゴリーを検索...',
    'Filter': 'フィルター',
    'All': 'すべて',
    'Export': 'エクスポート',
    'Import': 'インポート',
    'Download': 'ダウンロード',
    'Upload': 'アップロード',
    'Copy': 'コピー',
    'Copied': 'コピー済み',
    'Copied!': 'コピーしました！',
    'Preview': 'プレビュー',
    'Open': '営業中',
    'View': '表示',
    'Manage': '管理',
    'Configure': '設定',
    'Enable': '有効化',
    'Disable': '無効化',
    'Enabled': '有効',
    'Disabled': '無効',
    'Active': '有効',
    'Inactive': '無効',
    'Available': '注文可能',
    'Unavailable': '売り切れ',
    'Visible': '表示',
    'Hidden': '非表示',
    'Required': '必須',
    'Optional': '任意',
    'Confirm': '確認',
    'Back': '戻る',
    'Next': '次へ',
    'Done': '完了',
    'Upgrade': 'アップグレード',
    'Sign Out': 'ログアウト',
    'Exit to Main Admin': 'メイン管理に戻る',
    'Change Language': '言語を変更',
    'Add Category': 'カテゴリーを追加',
    'Add Menu Item': 'メニュー品目を追加',
    'Add Item': '品目を追加',
    'Add Ingredient': '食材を追加',
    'Add Department': '部門を追加',
    'Delete Item': '品目を削除',
    'Delete Category': 'カテゴリーを削除',
    'Upload Image': '画像をアップロード',
    'Upload Photo': '写真をアップロード',
    'Select Category': 'カテゴリーを選択',
    'Select Department': '部門を選択',
    'Add translation...': '翻訳を追加...',
    'Clear': 'クリア',
    'Reset': 'リセット',
    'Apply': '適用',
    'Submit': '送信',
    'Core & Operations': 'フロア業務と運営',
    'Menu & Experience': 'メニューと体験',
    'Enterprise & Security': '店舗とスタッフ管理',
    'System & Control': 'システム設定とサポート',
    'Intelligence Studio': '分析スタジオ',
    'Venue Insights': '売上とレポート',
    'Reservations': '予約管理',
    'Floor Plan': 'フロアマップ',
    'Live Waitlist': '順番待ちリスト',
    'Walk-Ins': '直接来店ゲスト',
    'Walk-ins': '直接来店ゲスト',
    'Departments': '部門・エリア',
    'Service Hours': 'サービス提供時間',
    'Categories': 'カテゴリー',
    'Menu Categories': 'メニューカテゴリー',
    'Menu Items': 'メニュー品目',
    'Items': 'メニュー',
    'Ingredients': '食材一覧',
    'Loyalty Program': 'ポイント会員',
    'Campaign Studio': '特別プロモーション',
    'Signage': '店内モニター表示',
    'QR Code': '卓上QRコード',
    'Kitchen Ops': '厨房ディスプレイ',
    'Theme Engine': 'メニューデザイン',
    'Governance': 'ガバナンス',
    'Audit Log': '操作履歴',
    'API & Webhooks': 'システム連携',
    'SSO & Security': 'セキュリティとログイン',
    'Team': 'スタッフ管理',
    'Billing': 'プランとお支払い',
    'SEO & Google': 'Google検索対策',
    'Notifications': '通知一覧',
    'Support Center': 'ヘルプとサポート',
    'Settings': '店舗設定',
    'Name': '名称',
    'Description': '説明',
    'Price': '価格',
    'Status': '状態',
    'Actions': '操作',
    'Role': '権限',
    'Email': 'メールアドレス',
    'Phone': '電話番号',
    'Address': '住所',
    'City': '市区町村',
    'Currency': '通貨',
    'Language': '言語',
    'Theme': 'テーマ',
    'General': '一般',
    'Total Revenue': '総売上',
    'Total Orders': '総注文数',
    'Average Order': '平均客単価',
    'Active Sessions': '利用中テーブル',
    'Recent Orders': '最近の注文',
    'Live Orders': '受付中オーダー',
    'Table': 'テーブル',
    'Date': '日付',
    'Time': '時間',
    'Amount': '金額',
    'Customer': 'お客様',
    'In Stock': '在庫あり',
    'Out of Stock': '売り切れ',
    'Sold Out': '売り切れ',
    'Dine In': '店内飲食',
    'Pickup': 'テイクアウト',
    'Delivery': '配達',
    'Paid': '支払済み',
    'Unpaid': '未払い',
    'Pending': '保留中',
    'Preparing': '調理中',
    'Ready': '準備完了',
    'Completed': '完了',
    'Cancelled': 'キャンセル済み',
    'Loading...': '読み込み中...',
    'No data': 'データがありません',
    'No results found': '一致する結果がありません',
    'No items found': 'メニューが見つかりません',
    'No translations defined yet': '翻訳はまだ登録されていません',
    'Are you sure?': '本当によろしいですか？',
    'This action cannot be undone.': 'この操作は取り消せません。',
    'Settings saved successfully!': '設定を保存しました！',
    'Administrator': '管理者',
    'Manager': 'マネージャー',
    'Owner': 'オーナー',
    'Chef': 'シェフ',
    'Waiter': 'ウェイター',
    'Cashier': 'レジ担当',
    'Type a command or search...': 'コマンドを入力または検索...',
    'Command Palette': 'コマンドパレット',
    'Demand Velocity': '需要の勢い',
    'DEMAND VELOCITY': '需要の勢い',
    'Top Performing Entities': 'トップ人気メニュー',
    'TOP PERFORMING ENTITIES': 'トップ人気メニュー',
    'Hourly Load Analysis': '時間帯別混雑分析',
    'HOURLY LOAD ANALYSIS': '時間帯別混雑分析',
    'Bulk Command Center': '一括操作センター',
    'BULK COMMAND CENTER': '一括操作センター',
    'Governance Scan': 'メニュー健全性チェック',
    'GOVERNANCE SCAN': 'メニュー健全性チェック',
    'No compliance issues detected in current menu': '現在のメニューに問題は検出されませんでした',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': '現在のメニューに問題は検出されませんでした',
    'Procedural Tagging': '一括バッジ付与',
    'PROCEDURAL TAGGING': '一括バッジ付与',
    'Name match...': '料理名で一致...',
    '...Name match': '料理名で一致...',
    'Healthy': 'ヘルシー',
    'HEALTHY': 'ヘルシー',
    'Apply Tags': 'タグを適用',
    'APPLY TAGS': 'タグを適用',
    'Global Price Shift': '全品一括価格変更',
    'GLOBAL PRICE SHIFT': '全品一括価格変更',
    'Adjust': '変更を適用',
    'ADJUST': '変更を適用',
    'Availability Override': '在庫・品切れ緊急切り替え',
    'AVAILABILITY OVERRIDE': '在庫・品切れ緊急切り替え',
    'Reset: ALL IN-STOCK': '全品を「提供中」に戻す',
    'RESET: ALL IN-STOCK': '全品を「提供中」に戻す',
    'EMERGENCY STOP (ALL OUT)': '緊急停止（全品売り切れ）',
    'Integrity Engine': 'メニュー完全性エンジン',
    'INTEGRITY ENGINE': 'メニュー完全性エンジン',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': '自動管理機能がメニューの表示状況、価格設定の異常、料理説明の記載漏れを店舗全体で常時チェックします。',
    'Inventory Audit Export': 'メニュー台帳データのエクスポート',
    'INVENTORY AUDIT EXPORT': 'メニュー台帳データのエクスポート',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'オフラインでの点検・確認用に、メニューの実績と詳細情報を含む包括的なデータを生成します。',
    'Enterprise Data Export': '詳細データエクスポート',
    'ENTERPRISE DATA EXPORT': '詳細データエクスポート',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': '来店客の動線、料理の注文傾向、コンバージョン率を含む詳細な業績監査レポートを標準形式で出力します。',
    'JSON Snapshot': 'JSONスナップショット',
    'JSON SNAPSHOT': 'JSONスナップショット',
    'Compile CSV Report': 'CSVレポートを作成',
    'COMPILE CSV REPORT': 'CSVレポートを作成',
    'Date Segment': '対象日付',
    'DATE SEGMENT': '対象日付',
    'Inbound Views': '閲覧アクセス数',
    'INBOUND VIEWS': '閲覧アクセス数',
    'Asset Interactions': '料理の操作・注文数',
    'ASSET INTERACTIONS': '料理の操作・注文数',
    'Telemetry History': 'アクティビティ履歴',
    'TELEMETRY HISTORY': 'アクティビティ履歴',
    'Telemetry History (7d)': 'アクティビティ履歴 (7日間)',
    'TELEMETRY HISTORY (7D)': 'アクティビティ履歴 (7日間)',
    'Growth Trajectory': '成長トレンド',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': '過去7日間のメニュー閲覧および操作頻度は上昇傾向にあります。',
    'Deep Analysis Engine': '詳細データ分析',
    'DEEP ANALYSIS ENGINE': '詳細データ分析',
    'Asset Velocity': '料理の人気推移',
    'ASSET VELOCITY': '料理の人気推移',
    'Traffic Projections': '来店者数予測',
    'Telemetry Signal Tracking': 'アクティビティ信号の追跡',
    'Service Hour Update': 'サービス提供時間の更新',
    'Late Night Lounge Hours': '深夜ラウンジ営業時間',
    'Bar & Lounge Hours': 'バー＆ラウンジ営業時間',
    'Breakfast & Brunch Hours': '朝食＆ブランチ営業時間',
    'Switch Location': '店舗を切り替え',
    'SWITCH LOCATION': '店舗を切り替え',
    'Global Admin': '全体管理者',
    'GLOBAL ADMIN': '全体管理者',
    'Staff Access': 'スタッフアクセス',
    'STAFF ACCESS': 'スタッフアクセス',
    'Venue Overview': '店舗概要',
    'VENUE OVERVIEW': '店舗概要',
    'Unified Telemetry Engine': '総合運営モニタリング',
    'UNIFIED TELEMETRY ENGINE': '総合運営モニタリング',
    'Pulse': 'ライブ速報',
    'PULSE': 'ライブ速報',
    'Insights': 'インサイト',
    'INSIGHTS': 'インサイト',
    'Reports': 'レポート',
    'REPORTS': 'レポート',
    'GOVERNANCE': 'ガバナンス',
    'Multi-Venue': '複数店舗管理',
    'MULTI-VENUE': '複数店舗管理',
    'Menu Views': 'メニュー閲覧数',
    'Item Interactions': '料理のタップ数',
    'Guest Rating': 'お客様の評価',
    'Revenue (7d)': '売上 (7日間)',
    'Revenue (24h)': '売上 (24時間)',
    'Revenue (30d)': '売上 (30日間)',
    'Delivery orders (7d)': 'デリバリー注文 (7日間)',
    'Delivery orders (24h)': 'デリバリー注文 (24時間)',
    'Delivery orders (30d)': 'デリバリー注文 (30日間)',
    'Delivery revenue': 'デリバリー売上',
    'Average delivery order': '平均デリバリー客単価',
    'Average delivery fee': '平均配送料',
    'Zone average': 'エリア平均',
    'Including fees': '手数料込み',
    'No deliveries': '配達注文なし',
    'No orders': '注文なし',
    'No reviews': 'レビューなし',
    'Live': 'ライブ',
    'LIVE': 'ライブ',
    'reviews': '件のレビュー',
    'orders': '件の注文',
    'deliveries': '件の配達',
    'of orders': '注文の割合',
    'rate': '率',
    'Recent Activity': '最近のアクティビティ',
    'Real-time Venue Telemetry': '店舗のリアルタイム動向',
    'REAL-TIME VENUE TELEMETRY': '店舗のリアルタイム動向',
    'Rapid Pulse View': 'クイック速報ビュー',
    'Anonymous Guest': 'ゲスト様',
    'scanned menu': 'メニューをスキャンしました',
    'viewed item': '料理を閲覧しました',
    'interacted with': '操作しました:',
    'placed order': '注文を送信しました',
    'searched menu': 'メニュー内を検索しました',
    'Main Landing': 'トップページ',
    'Trace': '追跡済み',
    'TRACE': '追跡済み',
    'Gaining Telemetry...': 'アクティビティを受信中...',
    'No recent traffic detected': '最近のアクティビティはありません',
    'Activities will appear here as guests scan your QR code': 'お客様がQRコードをスキャンすると、ここに履歴が表示されます',
    'Analyze All Logs': 'すべての履歴を分析',
    'Just now': 'たった今',
    'less than a minute ago': '1分未満前',
    '1 minute ago': '1分前',
    '1 hour ago': '1時間前',
    'about 1 hour ago': '約1時間前',
    '1 day ago': '1日前',
    'DAY AGO 1': '1日前',
    '2 days ago': '2日前',
    'DAY AGO 2': '2日前',
    '3 days ago': '3日前',
    'DAY AGO 3': '3日前',
    '4 days ago': '4日前',
    'DAY AGO 4': '4日前',
    '5 days ago': '5日前',
    'DAY AGO 5': '5日前',
    '6 days ago': '6日前',
    'DAY AGO 6': '6日前',
    '7 days ago': '7日前',
    'DAY AGO 7': '7日前',
    'about 1 month ago': '約1か月前',
    'Monday': '月曜日',
    'Tuesday': '火曜日',
    'Wednesday': '水曜日',
    'Thursday': '木曜日',
    'Friday': '金曜日',
    'Saturday': '土曜日',
    'Sunday': '日曜日',
    'Mon': '月',
    'Tue': '火',
    'Wed': '水',
    'Thu': '木',
    'Fri': '金',
    'Sat': '土',
    'Sun': '日',
    'Opening Hours': '営業時間',
    'Start Time': '開始時間',
    'End Time': '終了時間',
    'Opening Time': 'オープン時間',
    'Closing Time': 'クローズ時間',
    'All Day': '終日',
    'Closed': '準備中',
    'Active Days': '営業日',
    'Active Schedule': '有効スケジュール',
    'Always Available': '常時提供',
    'Always Visible': '常に表示',
    'New Category': '新規カテゴリー',
    'Category': 'カテゴリー',
    'Category Name': 'カテゴリー名',
    'Category Description': 'カテゴリーの説明',
    'Display Order': '表示順',
    'Item Name': '料理名',
    'Item Description': '料理の説明',
    'Base Price': '基本価格',
    'Preparation Time': '調理時間',
    'Calories': 'カロリー',
    'Featured Item': 'おすすめ料理',
    'Chef\'s Recommendation': 'シェフのおすすめ',
    'Spicy': '辛い',
    'Vegetarian': 'ベジタリアン',
    'Vegan': 'ヴィーガン',
    'Gluten-Free': 'グルテンフリー',
    'Halal': 'ハラール',
    'Kosher': 'コーシャ',
    'Contains Nuts': 'ナッツ類を含む',
    'Dairy-Free': '乳製品不使用',
    'Allergen Exclusions': 'アレルゲン情報',
    'Table Number': 'テーブル番号',
    'Assigned Table': '指定テーブル',
    'Seats': '座席数',
    'Capacity': '収容人数',
    'Indoor': '店内席',
    'Outdoor': 'テラス席',
    'Terrace': 'テラス',
    'Bar': 'カウンター席',
    'Add Table': 'テーブルを追加',
    'Edit Table': 'テーブルを編集',
    'Delete Table': 'テーブルを削除',
    'Bookings': '予約',
    'Add to Waitlist': 'ウェイティングに追加',
    'Guest Name': 'お客様のお名前',
    'Party Size': '人数',
    'Arrival Time': '到着時間',
    'Special Requests': '特別なご要望',
    'Confirmed': '確定済み',
    'No Show': '来店なし',
    'Add Staff Member': 'スタッフを追加',
    'Invite Staff': 'スタッフを招待',
    'Email Address': 'メールアドレス',
    'Phone Number': '電話番号',
    'Download QR Code': 'QRコードを保存',
    'Print Table Stands': '卓上POPを印刷',
    'Today': '今日',
    'Yesterday': '昨日',
    'This Week': '今週',
    'This Month': '今月',
    'Last 30 Days': '過去30日間',
    'Access Denied': 'アクセスが拒否されました',
    'Advanced Settings': '詳細設定',
    'Change Image': '画像を変更',
    'Remove Image': '画像を削除',
    'Announcement Banner': 'お知らせバナー',
    'Please fill in all required fields.': '必須項目をすべて入力してください。',
    'Changes saved successfully': '変更を保存しました',
    '(You)': '(あなた)',
    'AI Auto-Tag': 'AI自動タグ付け',
    'Analyzing:': '分析中:',
    'Apple (Fruit/Vegan)': 'リンゴ (フルーツ/ヴィーガン)',
    'Cancel editing': '編集をキャンセル',
    'Capacity:': '収容人数:',
    'Carrot (Veggie)': '人参 (野菜)',
    'CircleDot (Organic)': '丸点 (オーガニック)',
    'Coffee (Caffeine)': 'コーヒー (カフェイン)',
    'Configuration': '設定',
    'Copy verification record name': '検証レコード名をコピー',
    'Copy verification record value': '検証レコード値をコピー',
    'Critical exception intercepted': '致命的なエラーが検出されました',
    'Kernel Panic': 'システムエラー',
    'DNA (Organic/Bio)': 'DNA (オーガニック/バイオ)',
    'Egg (Ovo/Allergen)': '卵 (アレルゲン)',
    'Empty': '空',
    'Engraving...': '生成中...',
    'Flame (Spicy)': '炎 (辛口)',
    'Free': '無料',
    'From': 'から',
    'Generating': '生成',
    'Governance & Traceability Studio': '店舗管理＆履歴ハブ',
    'Greet': 'ようこそ',
    'Heart (Healthy)': 'ハート (ヘルシー)',
    'In-App': 'アプリ内',
    'Infinity SVG': 'ベクターSVG (高品質)',
    'Leaf (Default)': '葉 (デフォルト)',
    'MAX Feature': 'エンタープライズ機能',
    'Milk (Dairy)': 'ミルク (乳製品)',
    'Network': 'ネットワーク',
    'Notify SMS': 'SMS通知',
    'Order': '注文',
    'Original': 'オリジナル',
    'Overview': '概要',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4つの標準ロール',
    'Shield (Safety)': 'シールド (安心・安全)',
    'Soup (Warm/Soup)': 'スープ (温かい/スープ)',
    'Standard Pro View': '標準表示',
    'Star': '星',
    'Synchronized with Venue Identity': '店舗のブランドスタイルに連動',
    'Synchronizing Security Context...': 'セキュアな接続を確立中...',
    'Targeted Timing': '提供時間帯',
    'Targeting': '対象',
    'To': 'まで',
    'Toggle Navigation': 'ナビゲーション切り替え',
    'Venue': '店舗',
    'Venue Governance Suite': '店舗運営・設定ハブ',
    'Volume:': '音量:',
    'WHITEBOARD NOTE...': 'スタッフ向けメモ...',
    'Warning (Alert)': '警告 (通知)',
    'Wheat (Flour/Gluten)': '小麦 (グルテン)',
    'Wine (Alcohol)': 'ワイン (アルコール)',
    'YouTube/Vimeo/Direct Link': '動画リンク (YouTube、Vimeo、MP4)',
    'Your name': 'お名前',
    '2. Table Number': '2. テーブル番号',
    'for Morocco or': 'モロッコ向け、または',
    'in': '内',
    'to': 'まで',
    'e.g. 12 or Patio-1': '例: 12 または テラス-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': '例: 手打ちフェットチーネ、黒トリュフ、36ヶ月熟成パルミジャーノ...',
    'e.g. Head Sommelier & Wine Lead': '例: ヘッドソムリエ＆ワイン担当',
    'e.g. London': '例: ロンドン',
    'e.g. SOMMELIER, HOST, BARISTA': '例: ソムリエ、レセプション、バリスタ',
    'e.g. Skyline Lounge': '例: スカイラインラウンジ',
    'e.g. TRUFFLE OIL': '例: トリュフオイル',
    'e.g. The Grand Cafe': '例: グランドカフェ',
    'e.g. Window preference, high chair': '例: 窓側の席希望、ベビーチェア',
    'Copied to clipboard!': 'クリップボードにコピーしました！',
    'Copy failed.': 'コピーに失敗しました。',
    'Link copied!': 'リンクをコピーしました！',
    'Logo uploaded': 'ロゴをアップロードしました',
    'Cover image uploaded': 'カバー画像をアップロードしました',
    'Profile saved and synced successfully': 'プロフィールを正常に保存・同期しました',
    'Category Created': 'カテゴリーを作成しました',
    'Category Updated': 'カテゴリーを更新しました',
    'Category Deleted': 'カテゴリーを削除しました',
    'Item Created': 'メニュー商品を追加しました',
    'Item Updated': 'メニュー商品を更新しました',
    'Item Deleted': 'メニュー商品を削除しました',
    'Menu Published': 'メニューを公開しました',
    'Delete Category?': 'カテゴリーを削除しますか？',
    'Delete Menu Item?': 'このメニュー商品を削除しますか？',
    'Delete Ingredient?': 'この原材料を削除しますか？',
    'Delete Service Period?': '提供時間帯を削除しますか？',
    'Remove team member?': 'このチームメンバーを削除しますか？',
    'Cancel invitation?': '招待をキャンセルしますか？',
    'Revoke API key?': 'APIキーを失効させますか？',
    'Delete webhook endpoint?': 'Webhookエンドポイントを削除しますか？',
    'Clear all notifications?': 'すべての通知を消去しますか？',
    'Disconnect Stripe account?': 'Stripeアカウントを切断しますか？',
    'Disconnect Google Business Profile?': 'Googleビジネスプロフィールを切断しますか？',
    'Campaigns published successfully!': 'キャンペーンを公開しました！',
    'Changes successfully synchronized with Google Business Profile!': 'Googleビジネスプロフィールと正常に同期しました！',
    'Google Business Profile successfully connected!': 'Googleビジネスプロフィールが正常に接続されました！',
    'Google Business Profile disconnected.': 'Googleビジネスプロフィールの接続を解除しました。',
    'Google Post draft saved successfully!': 'Google投稿の下書きを保存しました！',
    'Post draft deleted.': '投稿の下書きを削除しました。',
    'Review response published successfully!': 'クチコミへの返信を公開しました！',
    'SEO configuration saved successfully!': 'SEO設定を保存しました！',
    'SEO metadata translated across all languages!': 'SEOメタデータをすべての言語に翻訳しました！',
    'Venue profile & bio translated across all languages!': '店舗情報と紹介文をすべての言語に翻訳しました！',
    'Reservation configuration saved successfully': '予約設定を保存しました',
    'Stripe settings saved successfully.': 'Stripeの設定を保存しました。',
    'Print-Ready PDF downloaded successfully': '印刷用高解像度PDFをダウンロードしました',
    'Vector QR PDF downloaded successfully': 'ベクターQR PDFをダウンロードしました',
    'Enterprise 300 DPI Pre-Press Package generated successfully': '300 DPI 印刷用データパッケージを作成しました',
    'At least one language must be enabled.': '少なくとも1つの言語を有効にする必要があります。',
    'Enter a non-zero percentage before adjusting prices.': '価格を調整する前に、0以外のパーセンテージを入力してください。',
    'Please enter a Venue Name before translating.': '翻訳を開始する前に店舗名を入力してください。',
    'Please enter an item name before running dietary and allergen analysis.': 'アレルギー・食事制限の分析を行う前にメニュー名を入力してください。',
    'Please write a reply before submitting.': '送信する前に返信内容を入力してください。',
    'Table 3 Called Waiter': 'テーブル3からスタッフ呼び出しがありました',
    'Table Call Bell': 'テーブル呼び出しベル',
    'Waiter Calls': 'スタッフ呼び出し',
    'Waiter Dispatch & Calls': 'フロアスタッフ呼び出し＆対応',
    'Kitchen Display & Whiteboard': 'キッチンスクリーン＆連絡ボード',
    'Customer Orders': 'お客様のご注文',
    'Bakery': 'ベーカリー / パン屋',
    'Bar / Pub': 'バー / パブ',
    'Café / Coffee Shop': 'カフェ / 喫茶店',
    'Fast Food': 'ファストフード',
    'Gourmet / Fine Dining': '高級レストラン / ファインダイニング',
    'Hotel': 'ホテル',
    'General Local Business': '一般ローカル店舗',
    'Classic': 'クラシック',
    'Clean': 'クリーン',
    'Modern': 'モダン',
    'Elegant': 'エレガント',
    'Energetic': 'エネルギッシュ',
    'Glass': 'フロストガラス',
    'Playful & Casual': 'カジュアル＆フレンドリー',
    'Sophisticated & Gourmet': '洗練されたグルメ',
    'Warm & Welcoming': '温かみのあるおもてなし',
    'Executive': 'エグゼクティブ',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'アップグレードが必要です: 多言語メニューはProプランでご利用いただけます。',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'アップグレードが必要です: 注文およびスタッフ呼び出し機能はStarterプランでは利用できません。',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'ベクターSVGエクスポートはProおよびMaxプランの機能です。アップグレードしてご利用ください。',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': '印刷用PDFエクスポートはProおよびMaxプランの機能です。アップグレードしてご利用ください。',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Proプランのアクティブキャンペーンは最大3件までです。他のキャンペーンを一時停止するか、MAXへアップグレードしてください。',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Starterプランでは最大20席（テーブル1〜20）が含まれます。無制限に利用するにはPROにアップグレードしてください。',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Starterプランは数字のテーブル（1〜20）に対応しています。カスタム名（テラス、VIPなど）にはPROが必要です。',
    'Add Menu Categories & Items': 'カテゴリーとメニュー商品を追加',
    'Add Menu Items': 'メニュー商品を追加',
    'Organize your menu sections and food items.': 'メニューのカテゴリーと料理を整理・登録します。',
    'Add and manage your food & beverage offerings.': 'フードやドリンクのメニューを登録・管理します。',
    'Customize Venue Branding': '店舗ブランディングのカスタマイズ',
    'Set your brand colors, logo, and presentation.': 'ブランドカラー、ロゴ、表示デザインを設定します。',
    'Get Your QR Code & Go Live': 'QRコードを取得して運用を開始',
    'Download table QR codes and preview your live menu.': '卓上用QRコードをダウンロードし、実際のメニュー画面を確認します。',
    'Account & Venue Created Successfully!': 'アカウントと店舗の登録が完了しました！',
    '⚡ Off-Peak Hours': '⚡ アイドルタイム（閑散時）',
    '🍽️ Lunch Crowd': '🍽️ ランチタイム集客',
    '🚀 Weekend Rush': '🚀 週末のピークタイム',
    '🌟 Seasonal Special': '🌟 季節限定スペシャル',
    '📣 Engagement Buzz': '📣 クチコミ＆SNS拡散',
    'Festive & Energetic': 'フェスティブ＆にぎやか',
    'Urgent & High-Conversion': 'タイムセール＆高反応',
    'Generated 3 tailored campaign strategies': '店舗に合わせた3つのプロモーション戦略を作成しました',
    'Could not generate campaign ideas. Please try again.': 'プロモーション案を作成できませんでした。もう一度お試しください。',
    'Venue Themes': '店舗デザインテーマ',
    'Choose the character of your public menu': 'お客様向けデジタルメニューのビジュアルスタイルを選択',
    'Glassmorphism & Depth': 'グラスモーフィズム＆立体感',
    'Independent XML Sitemap': '独立したXMLサイトマップ',
    'Structured Schema & Sync': '構造化データ＆検索同期',
    'Improve how your venue appears on Google and in local search results.': 'Google検索やマップでの店舗の露出と掲載順位を向上させます。',
    'Terms of Service': '利用規約',
    'Confirm your email': 'メールアドレスを確認してください',
    'Check your spam / junk folder': '迷惑メールフォルダをご確認ください',
    'Commercial pre-press print production package': '業務用・印刷所入稿向け高画質パッケージ',
    'Over-bleed safety margin': '塗り足し・裁ち落とし安全マージン',
    'Crop Marks': 'トンボ（トリムマーク）',
    'Guillotine cutting guides': '断裁用ガイドライン',
    'Global Banners': '全画面共通告知バナー',
    'Featured Items': 'おすすめ・人気メニュー',
    'AI Assistant': 'AIアシスタント',
    'Custom Role Schemas': 'カスタム役職・権限設計',
    'Notification Preferences': '通知受信設定',
    'Preferences saved automatically': '設定は自動的に保存されました',
    'Menu Extractor (PDF & Images)': 'メニューAI読み取り抽出 (PDF・画像対応)',
    'Multi-Language Translation': '多言語自動翻訳',
    'Start Menu Translation': 'メニューの翻訳を開始',
    'Menu Items Hub': 'メニュー商品管理ハブ',
    'Open Menu Items': 'メニュー商品一覧を開く',
    'Enterprise Traceability': 'オペレーション履歴・トレーサビリティ',
    'Full History': '完全な操作履歴',
    'Billing & Plans': 'ご請求とプラン管理',
    'Subscription Governance': 'サブスクリプション管理',
    'Platform Usage': '利用状況・リソース',
    'Menu Page Views': 'メニューページの閲覧数',
    'Enterprise Identity': 'エンタープライズ認証管理',
    'Access Governance': 'アクセス制御とセキュリティ方針',
    'Required Fields *': '必須項目 *',
    'Enabled Languages': '有効な言語',
    'Venue Info & Bio Localizations': '店舗情報と紹介文の言語別設定',
    'Public Address': '公開用住所',
    'Public URL Slug': '公開用URLスラッグ',
    'Contact Phone': '連絡先電話番号',
    'Business Address': '事業所住所',
    'No Categories Yet': 'カテゴリーがまだありません',
    'No Custom Roles Configured': 'カスタム役職はまだ設定されていません',
    'No Google reviews retrieved yet': 'Googleクチコミはまだ取得されていません',
    'No Guests Found': '該当するお客様が見つかりません',
    'No Menu Items Yet': 'メニュー商品がまだありません',
    'No Promotions Configured': '設定済みのプロモーションはありません',
    'No Service Periods Yet': '提供時間帯がまだ設定されていません',
    'No Support Tickets': 'サポートチケットはありません',
    'No Tables on Floor Plan': 'フロアマップにテーブルが配置されていません',
    'No Team Members Found': 'チームメンバーが見つかりません',
    'No Upcoming Events': '予定されているイベントはありません',
    'No Walk-Ins Recorded': '直接来店されたお客様の記録はありません',
    'No items are available right now': '現在提供可能なメニュー商品はありません',
    'No notifications found': '通知はありません',
    'No reservations matching your filters': '条件に一致する予約は見つかりませんでした',
    'No tables currently occupied': '現在使用中のテーブルはありません',
    'No venue selected': '店舗が選択されていません',
    'Waitlist': 'ウェイティングリスト',
    'Waitlist is Empty': '現在ウェイティング中のお客様はいません',
    'Waves (Gluten)': '麦 (グルテン含有)',
    'We sent a temporary verification code to': '以下の連絡先に確認コードを送信しました:',
    'Website URL': 'ウェブサイトURL',
    'What the customer receives': 'お客様への提供内容・特典',
    'White-label control is available on eligible plans.': 'ホワイトレーベル（独自ブランド表示）機能は対象プランでご利用いただけます。',
    'Why this works:': 'この戦略が効果的な理由:',
    'WiFi Broadcast': 'ゲスト用Wi-Fi接続表示',
    'WiFi Password': 'Wi-Fiパスワード',
    'Window seat, dietary request, birthday celebration...': '窓際の席希望、アレルギー対応、誕生日のお祝いなど...',
    'Your Published Response': '公開済みの返信内容',
    'Your Venues': '登録店舗一覧',
    '🎉 Ready to publish!': '🎉 準備完了！今すぐ公開できます',
    'e.g. Grill, Salad, Pastry': '例: グリル料理、サラダ、デザート',
    'e.g. Guest called, emergency': '例: お客様からの事前連絡、緊急の都合',
    'e.g. Italian, Moroccan, Burgers': '例: イタリアン、モロッコ料理、バーガー',
    'e.g. Mains, Pasta, Starters': '例: メイン料理、パスタ、前菜',
    'e.g. Signature Truffle Pasta': '例: 特製トリュフパスタ',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': '例: アフタヌーンコーヒースペシャル、ハッピーアワー15%OFF',
    'e.g., Breakfast, Happy Hour': '例: モーニング、ハッピーアワー',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': '例: 毎週金曜日20時より、生演奏ジャズライブを開催！',
  },
  nl: {
    '123 Main St, City': 'Hoofdstraat 123, Stad',
    'Balance': 'Puntenaldo',
    'Lifetime': 'Cumulatief totaal',
    'Source': 'Bron',
    'Menu': 'Menukaart',
    'Advanced': 'Geavanceerd',
    'Core Suite': 'Basisfuncties',
    'Privacy': 'Privacy',
    'Support': 'Ondersteuning',
    'Save Layout': 'Zaalindeling opslaan',
    'Shape:': 'Vorm:',
    'SEARCH LIBRARY...': 'ZOEKEN IN BIBLIOTHEEK...',
    'Search library...': 'Zoeken in bibliotheek...',
    'None': 'Geen',
    'Type': 'Type',
    'Target': 'Doel',
    'Value / Target': 'Waarde / Doel',
    'London (GMT/BST)': 'Londen (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tokio (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Startkleur',
    'Depth Shade': 'Diepteschaduw',
    'Matrix Geometry': 'QR-puntpatroon',
    'Eye Architecture': 'Hoekmarkeringen QR-code',
    'Brand Centerpiece': 'Centraal logo in QR-code',
    'Scannability Shield': 'Scanbaarheidsbescherming',
    'Environment Blur': 'Omgevingsonscherpte',
    'Color Target:': 'Kleurdoel:',
    'DPI:': 'DPI (Resolutie):',
    '3mm Bleed Box': '3 mm afloopmarge',
    '300 DPI': '300 DPI (Hoge resolutie)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'SAML 2.0-protocol',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'IdP Entity-ID (Entity ID)',
    'SSO URL': 'SSO-aanmeldings-URL',
    'X.509 Certificate': 'X.509-beveiligingscertificaat',
    'ACS URL': 'ACS-service-URL',
    'Entity ID': 'Entiteit-ID',
    'Navigate': 'Navigeren',
    'Select': 'Selecteren',
    'OmniSearch v1.0': 'Universele zoekfunctie OmniSearch',
    'Back to order review': 'Terug naar besteloverzicht',
    'Return to Intelligence Studio': 'Terug naar intelligence-studio',
    'View Full Notification Center': 'Bekijk alle meldingen',
    'Read all': 'Alles als gelezen markeren',
    'Cancel department': 'Afdeling annuleren',
    'Save department': 'Afdeling opslaan',
    'Venue Segmentation': 'Locatiesegmentatie',
    'Collaborate on your menu together.': 'Werk samen aan uw menukaart.',
    'Timeline': 'Tijdlijn',
    'Calendar': 'Reserveringskalender',
    'Book': 'Reserveren',
    'Duration': 'Verblijfsduur',
    'Time & Duration': 'Tijd en duur',
    'Seating Preference': 'Zitplaatsvoorkeur',
    'Section': 'Zaalzone',
    'Min Capacity': 'Minimale capaciteit',
    'Max Capacity': 'Maximale capaciteit',
    'Phone (Optional)': 'Telefoon (Optioneel)',
    'Name or Walk-in': 'Naam of passant',
    'Next day': 'Volgende dag',
    'Next month': 'Volgende maand',
    'Live Connection': 'Live-verbinding',
    'Live Inventory': 'Actuele voorraad',
    'Main Venue (Full Menu)': 'Hoofdlocatie (Volledige menukaart)',
    'Preview Menu': 'Menukaart bekijken',
    'Manual Setup Guide': 'Handleiding voor handmatige installatie',
    'Connected & Active': 'Verbonden en actief',
    'CTA Action': 'Actieknop (CTA)',
    'Phone Contact': 'Contacttelefoonnummer',
    'Menu URL': 'Menukaart-link',
    'Google Location': 'Google-locatie',
    'Field': 'Veld',
    'Linked': 'Gekoppeld',
    'Synchronizing Hub...': 'Gegevens synchroniseren...',
    'Data Integrity': 'Gegevensintegriteit',
    'Station Governance Suite': 'Werkstationsbeheer',
    'Open audit log': 'Open auditlogboek',
    'Signal / Time': 'Gebeurtenis / Tijd',
    'Identity': 'Gebruiker / Identiteit',
    'Invitation Invalid': 'Uitnodiging ongeldig of verlopen',
    'Invited To': 'Uitgenodigd voor',
    'Team Limit Reached': 'Limiet voor teamleden bereikt',
    'Venue Limit Reached': 'Limiet voor locaties bereikt',
    'Venue Setup Required': 'Eerste configuratie vereist',
    'Create Venue': 'Horecazaak aanmaken',
    'TableGreet Onboarding': 'TableGreet Kennismaking',
    'No payment required': 'Geen betaling vereist',
    'Selected Plan': 'Gekozen abonnement',
    'Payment Method': 'Betaalmethode',
    'Invoice ID': 'Factuurnummer',
    'Verified': 'Geverifieerd',
    'Accent color': 'Accentkleur',
    'Set the voice and overall light level.': 'Stel de stijl en het helderheidsniveau in.',
    'Layout architecture': 'Indelingsarchitectuur',
    'Navigation & motion': 'Navigatie en animaties',
    'Loading preview engine': 'Voorbeeldengine laden',
    'Real Live Preview': 'Echte live voorvertoning',
    'Included with Pro & Max': 'Inbegrepen bij Pro & Max',
    'Daypart Scheduling': 'Dagdeelschema',
    'Operational Daypart Architecture': 'Operationele dagdeelstructuur',
    'Service Protocol': 'Serviceprotocol',
    'Configuration Engine': 'Configuratie-engine',
    'Linked Department': 'Gekoppelde afdeling',
    'Team Hub': 'Teamhub',
    'Team Size': 'Teamgrootte',
    'Joined': 'Lid sinds',
    'Cancel Invitation': 'Uitnodiging annuleren',
    'Initialize New Organization': 'Nieuwe organisatie initialiseren',
    'Operational': 'Operationeel',
    'Pro Feature': 'Pro-functie',
    'Current State': 'Huidige staat',
    'Open on the big TV': 'Open op het grote tv-scherm',
    'Menu Board': 'Digitaal menubord',
    'Reward': 'Beloning',
    'Reward name': 'Naam beloning',
    'Cancel & refund': 'Annuleren & terugbetalen',
    'Code': 'Code',
    'Find': 'Zoeken',
    'Complete': 'Voltooid',
    'Seated': 'Aan tafel',
    'No-Show': 'Niet opgedaagd',
    'Walk-in': 'Zonder reservering',
    'Party': 'Aantal gasten',
    'Turnover': 'Tafelomloop',
    'We couldn\'t load the menu': 'Het menu kon niet worden geladen',
    'Venue Not Found': 'Horecazaak niet gevonden',
    'Live Availability': 'Actuele beschikbaarheid',
    'Multi-Language Suite': 'Meertalige module',
    'Key name': 'Sleutelnaam',
    'Revoke API key': 'API-sleutel intrekken',
    'Delete webhook': 'Webhook verwijderen',
    'Identity Provider': 'Identiteitsprovider (IdP)',
    'Provider': 'Provider',
    'Mandatory MFA': 'Verplichte tweestapsverificatie (MFA)',
    'Account Identity': 'Accountidentiteit',
    'Login Email': 'Inlog e-mailadres',
    'New Login Email': 'Nieuw inlog e-mailadres',
    'Phone Login': 'Inloggen met telefoon',
    'SMS Verification Code': 'SMS-verificatiecode',
    'Integration Method': 'Integratiemethode',
    'Stripe Connect': 'Stripe Connect Betalingen',
    'Stripe Secret Key': 'Geheime Stripe-sleutel',
    'Stripe Webhook Secret': 'Stripe Webhook-geheim',
    'Client-Owned': 'In eigen beheer',
    'Verify connection': 'Verbinding controleren',
    'Setup Health': 'Status van instellingen',
    'Privacy Protected': 'Privacy beschermd',
    'Privacy Policy': 'Privacybeleid',
    'Continue': 'Doorgaan',
    'Sign In': 'Inloggen',
    'Didn\'t get it?': 'Niet ontvangen?',
    'Logged In Account': 'Ingelogd account',
    'Annual': 'Jaarlijks',
    'Unread': 'Ongelezen',
    'Starred': 'Met ster',
    'High / Urgent': 'Hoog / Urgent',
    'Mark Read': 'Als gelezen markeren',
    'Delete notification': 'Melding verwijderen',
    'Urgent': 'Urgent',
    'High': 'Hoog',
    'Normal': 'Normaal',
    'Low': 'Laag',
    'Select a Ticket': 'Selecteer een ticket',
    'Urgent (Immediate attention)': 'Urgent (Onmiddellijke aandacht vereist)',
    'Type your reply here...': 'Typ hier uw reactie...',
    'Print Card (PDF)': 'Tafelkaart afdrukken (PDF)',
    'Advanced Printing Engine': 'Professionele printengine',
    'Multi-Venue Governance': 'Beheer van meerdere vestigingen',
    'Network Revenue': 'Totale netwerkomzet',
    'Live Network Total': 'Live netwerktotaal',
    'Audit Log Hub': 'Auditlogboek en overzicht',
    'Export Log': 'Logboek exporteren',
    'CUSTOM ROLE SCHEMAS': 'AANGEPASTE ROLSCHEMA\'S',
    'Unique internal role identifier.': 'Unieke interne rolidentificatie.',
    'Human-friendly role label.': 'Duidelijke functienaam voor het team.',
    'Marketing Hub': 'Marketinghub',
    'Revenue Generation Suite': 'Omzetgroei-suite',
    'Active Campaign Limit Reached (3/3)': 'Limiet voor actieve campagnes bereikt (3/3)',
    'Banner Content': 'Bannerinhoud',
    'Style Profile': 'Stijlprofiel',
    'Action URL (Optional)': 'Actielink (Optioneel)',
    'Promotion Label': 'Promotielabel',
    'Target Selection': 'Doelselectie',
    'Edit Promotion': 'Promotie bewerken',
    'Delete Promotion': 'Promotie verwijderen',
    'Loyalty Engine': 'Loyaltyprogramma',
    'Reward catalog': 'Beloningscatalogus',
    'Add reward': 'Beloning toevoegen',
    'Loyalty ID': 'Klant-ID',
    'No point activity yet': 'Nog geen puntenactiviteit',
    'Scan or enter QR value': 'Scan of voer QR-code in',
    'TableGreet Admin': 'TableGreet Beheer',
    'Capacity Pacing': 'Capaciteitsspreiding',
    'Cancellation Policy': 'Annuleringsvoorwaarden',
    'Booking Experience': 'Reserveringservaring',
    'Floor Occupancy': 'Zaalbezetting',
    'Currently Seated': 'Nu aan tafel',
    'Seat Party': 'Gasten plaatsen',
    'Seat Table': 'Tafel toewijzen',
    'Select Table': 'Selecteer tafel',
    'Table / Capacity': 'Tafel / Capaciteit',
    'Table Name/Number': 'Tafelnaam of -nummer',
    'Mark Arrived': 'Als gearriveerd markeren',
    'Mark No-Show': 'Als niet verschenen markeren',
    'Complete Dining': 'Tafel afronden',
    'Standard Dining (Default)': 'Standaard dineren (Standaard)',
    'Walk-In Dining Log': 'Overzicht passanten zonder reservering',
    'Notification Center': 'Meldingencentrum',
    'Single Sign-On': 'Single Sign-On (SSO)',
    'Welcome Aboard!': 'Welkom bij TableGreet!',
    'Select your TableGreet plan': 'Kies uw TableGreet-abonnement',
    'Free Plan Activation': 'Gratis abonnement activeren',
    'Venue Created!': 'Horecazaak succesvol aangemaakt!',
    'Upgrade for Unlimited': 'Upgrade naar onbeperkt',
    'Auto-Tag Menu': 'Menukaart automatisch labelen',
    'Start Auto-Tagging': 'Start automatisch labelen',
    'Launch Auto-Tagger': 'Start automatisch labelhulpmiddel',
    'Launch PDF Importer': 'PDF-menukaart importeren',
    'Analyzing Menu...': 'Menukaart analyseren...',
    'Analyzing Recipe...': 'Recept analyseren...',
    'Manual Availability': 'Handmatige beschikbaarheid',
    'Technical Breakdown': 'Technische specificaties',
    'Dietary Taxonomy': 'Dieet- en allergenencategorieën',
    'Dietary & Allergen AI': 'AI voor diëten en allergenen',
    'Inherit from Category': 'Overnemen van categorie',
    'Ingredient Library': 'Ingrediëntenbibliotheek',
    'Ingredient Name': 'Naam ingrediënt',
    'Menu Organization': 'Menustructuur',
    'Inventory Taxonomy Hub': 'Categorieën en classificatie',
    'Category Intelligence': 'Categorie-inzichten',
    'Service Period': 'Serviceshift / Dagdeel',
    'Manual Configuration / All Day': 'Handmatige instelling / Hele dag',
    'Operational Department': 'Operationele afdeling',
    'Global / No Department': 'Algemeen / Geen afdeling',
    'Cancel category': 'Categorie annuleren',
    'New category name...': 'Nieuwe categorienaam...',
    'Save category': 'Categorie opslaan',
    'QR Matrix Only': 'Alleen QR-code',
    'Vector & PDF': 'Vector & PDF',
    'Vector QR (SVG)': 'Vector QR-code (SVG)',
    'Vector QR (PDF)': 'Drukklare QR-code (PDF)',
    'Save QR Style': 'QR-stijl opslaan',
    'Share Link': 'Link delen',
    'High Fidelity 300DPI': '300 DPI drukwerkkwaliteit',
    'Generating 300 DPI PDF...': '300 DPI PDF aanmaken...',
    'Give your venue a point of view.': 'Geef uw horecazaak een unieke visuele identiteit.',
    'Signage Menu Not Found': 'Digitaal schermmenu niet gevonden',
    'Scan Menu': 'Scan de menukaart',
    'Open on your phone': 'Open op uw telefoon',
    'Full Screen': 'Volledig scherm',
    'Lock TV board': 'TV-scherm vergrendelen',
    'Big-Screen Menu': 'Groot scherm menukaart',
    'TV Signage': 'Digitale TV-schermen',
    'General Information': 'Algemene informatie',
    'Venue Name *': 'Naam horecazaak *',
    'Public Slug *': 'Publieke weblink *',
    'Venue Logo': 'Huisstijl / Logo',
    'Hero Cover Image': 'Hoofdomslagfoto',
    'Venue Timezone': 'Tijdzone van de locatie',
    'Primary Brand Color': 'Primaire merkkleur',
    'TableGreet footer credit': 'TableGreet-vermelding in de voettekst',
    'White-Label Branding': 'White-label huisstijl',
    'Client DNS Setup': 'DNS-domeininstellingen',
    'Copy CNAME target': 'CNAME-doel kopiëren',
    'Copy TableGreet URL': 'TableGreet-URL kopiëren',
    'Included TableGreet URL': 'Inbegrepen TableGreet-link',
    'Open TableGreet URL': 'TableGreet-link openen',
    'Public menu': 'Publieke menukaart',
    'Public venue delivery': 'Bezorging via online menukaart',
    'Zone name': 'Zonenaam',
    'Coverage note': 'Dekkingsnotitie',
    'Default ETA min': 'Geschatte minimumtijd (minuten)',
    'Default ETA max': 'Geschatte maximumtijd (minuten)',
    'Packaging fee': 'Verpakkingskosten',
    'Courier handled by': 'Bezorging uitgevoerd door',
    'Third-party courier': 'Externe bezorgdienst',
    'Unified Experience': 'Geïntegreerde ervaring',
    'Save WiFi to Venue': 'Wifi voor gasten opslaan',
    'Network Name (SSID)': 'Netwerknaam (SSID)',
    'Audio Alert Chime': 'Geluidssignaal bij bestelling',
    'Time Format': 'Tijdnotatie',
    'Auto / Default': 'Automatisch / Standaard',
    'Syncing SEO & Google Data...': 'SEO- en Google-gegevens synchroniseren...',
    'Google Connection': 'Google-koppeling',
    'Search Engine Metadata': 'Zoekmachine-metadata',
    'Social card preview': 'Voorbeeld op sociale media',
    'Allow Indexing': 'Indexering toestaan',
    'Canonical URL Override': 'Canonieke URL aanpassen',
    'Price Range': 'Prijsklasse',
    'Latitude': 'Breedtegraad',
    'Longitude': 'Lengtegraad',
    'Google Place ID / Location ID': 'Google Locatie-ID (Place ID)',
    'Google Search Preview': 'Voorbeeld in Google Zoeken',
    'Linked Google Location': 'Gekoppelde Google-locatie',
    'SEO Documentation': 'SEO-documentatie',
    ', and': ', en',
    'and': 'en',
    'Delete Department?': 'Afdeling verwijderen?',
    'Delete Department': 'Afdeling verwijderen',
    'Live URL': 'Live-URL',
    'Copy full live URL': 'Volledige live-URL kopiëren',
    'Open live department menu': 'Live afdelingsmenu openen',
    'Loading departments...': 'Afdelingen laden...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Multi-afdelingssegmentatie is een premium functie beschikbaar vanaf het PRO-abonnement.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Verdeel uw menukaart over zalen, tafels, bars of fysieke locaties.',
    'Targeted Ordering': 'Gerichte bestellingen',
    'Route guests and orders to specific preparation areas.': 'Leid gasten en bestellingen naar specifieke bereidingszones.',
    'Operational Departments': 'Operationele afdelingen',
    'OPERATIONAL DEPARTMENTS': 'OPERATIONELE AFDELINGEN',
    'Venue Segmentation Hub': 'Locatiesegmentatiehub',
    'VENUE SEGMENTATION HUB': 'LOCATIESEGMENTATIEHUB',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Maak afdelingen aan om uw menusecties te groeperen. Een grote horecazaak kan bijvoorbeeld een',
    'Restaurant': 'Restaurant',
    'Poolside': 'Zwembadterras',
    'department, each with its own categories.': ', elk met eigen categorieën.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Met afdelingen kunt u uw locatie opsplitsen in secties zoals Restaurant, Bar of Zwembad — elk met eigen categorieën.',
    'Department name': 'Afdelingsnaam',
    'New department name...': 'Nieuwe afdelingsnaam...',
    'No Linked Daypart (Always Available)': 'Geen tijdslot gekoppeld (altijd beschikbaar)',
    'Linked Daypart': 'Gekoppeld tijdslot',
    'Localizations': 'Vertalingen',
    'LOCALIZATIONS': 'VERTALINGEN',
    'AI Auto-Translate': 'Automatisch vertalen met AI',
    'AI AUTO-TRANSLATE': 'AUTOMATISCH VERTALEN MET AI',
    'Translated name': 'Vertaalde naam',
    'No Departments Yet': 'Nog geen afdelingen aangemaakt',
    'Add First Department': 'Eerste afdeling toevoegen',
    'Avg Order Value': 'Gemiddelde bestelwaarde',
    'AVG ORDER VALUE': 'GEMIDDELDE BESTELWAARDE',
    'item taps': 'gerechttiks',
    'ITEM TAPS': 'GERECHTTIKS',
    'Avg Rating': 'Gemiddelde score',
    'AVG RATING': 'GEMIDDELDE SCORE',
    'Revenue (Last 7 Days)': 'Omzet (laatste 7 dagen)',
    'REVENUE (LAST 7 DAYS)': 'OMZET (LAATSTE 7 DAGEN)',
    'Daily breakdown': 'Dagelijkse uitsplitsing',
    'DAILY BREAKDOWN': 'DAGELIJKSE UITSPLITSING',
    'Menu Views (Last 7 Days)': 'Menubekijkingen (laatste 7 dagen)',
    'MENU VIEWS (LAST 7 DAYS)': 'MENUBEKIJKINGEN (LAATSTE 7 DAGEN)',
    'Guest traffic': 'Gastenverkeer',
    'GUEST TRAFFIC': 'GASTENVERKEER',
    'Most Viewed Items': 'Meest bekeken gerechten',
    'MOST VIEWED ITEMS': 'MEEST BEKEKEN GERECHTEN',
    'Top Rated Items': 'Best beoordeelde gerechten',
    'TOP RATED ITEMS': 'BEST BEOORDEELDE GERECHTEN',
    'Waiter Requests': 'Oberoproepen',
    'WAITER REQUESTS': 'OBEROPROEPEN',
    'Total': 'Totaal',
    'TOTAL': 'TOTAAL',
    'Order Status (30 days)': 'Bestelstatus (30 dagen)',
    'ORDER STATUS (30 DAYS)': 'BESTELSTATUS (30 DAGEN)',
    'Recent Guest Feedback': 'Recente gastenbeoordelingen',
    'RECENT GUEST FEEDBACK': 'RECENTE GASTENBEOORDELINGEN',
    'Conversion Rate': 'Conversieratio',
    'CONVERSION RATE': 'CONVERSIERATIO',
    'of menu visitors placed an order': 'van de menubezoekers heeft een bestelling geplaatst',
    'Refreshed': 'Bijgewerkt',
    'REFRESHED': 'BIJGEWERKT',
    'Last 30 days': 'Laatste 30 dagen',
    'LAST 30 DAYS': 'LAATSTE 30 DAGEN',
    'cash': 'contant',
    'Cash': 'Contant',
    'CASH': 'CONTANT',
    'bill': 'rekening',
    'Bill': 'Rekening',
    'BILL': 'REKENING',
    'water': 'water',
    'Water': 'Water',
    'WATER': 'WATER',
    'service': 'service',
    'Service': 'Service',
    'SERVICE': 'SERVICE',
    'No interaction data yet': 'Nog geen interactiegegevens',
    'No ratings collected yet': 'Nog geen beoordelingen verzameld',
    'No requests in period': 'Geen oproepen in deze periode',
    'No feedback collected yet': 'Nog geen feedback ontvangen',
    'No menu views recorded yet': 'Nog geen menubekijkingen vastgelegd',
    'Dish Rating Breakdown': 'Beoordelingsoverzicht per gerecht',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Gerechtranglijsten en klanttevredenheidsscores zijn exclusief beschikbaar in MAX en Enterprise.',
    'Customer Sentiment & Reviews': 'Klantbeleving & beoordelingen',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Directe gastenbeoordelingen en sentimentsanalyses vereisen een MAX- of Enterprise-abonnement.',
    'Reviews AI': 'Beoordelings-AI',
    'Guest Feedback': 'Gastenfeedback',
    'Upgrade to unlock rating analytics →': 'Upgrade om beoordelingsstatistieken te ontgrendelen →',
    'Upgrade to Max': 'Upgraden naar Max',
    'UPGRADE TO MAX': 'UPGRADEN NAAR MAX',
    'MAX PLAN': 'MAX-PAKKET',
    'No Data Yet': 'Nog geen gegevens beschikbaar',
    'Share your QR code to start collecting real venue insights.': 'Deel uw QR-code om echte bedrijfsinzichten te verzamelen.',
    'Revenue Analytics': 'Omzetanalyse',
    'REVENUE ANALYTICS': 'OMZETANALYSE',
    'Track your earnings, orders, and average ticket values over time.': 'Volg uw inkomsten, bestellingen en gemiddelde bonwaarde in de loop van de tijd.',
    'Operational Velocity': 'Operationele snelheid',
    'OPERATIONAL VELOCITY': 'OPERATIONELE SNELHEID',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Houd gastenverkeer, populaire gerechten en de werkdruk van het personeel in de gaten.',
    'Upgrade to Pro': 'Upgrade naar Pro',
    'UPGRADE TO PRO': 'UPGRADEN NAAR PRO',
    'Loading Insights…': 'Inzichten laden…',
    'Loading Insights...': 'Inzichten laden...',
    'Telemetry Unavailable': 'Activiteitsgegevens niet beschikbaar',
    'TELEMETRY UNAVAILABLE': 'ACTIVITEITSGEGEVENS NIET BESCHIKBAAR',
    'No Data Captured': 'Geen gegevens vastgelegd',
    'NO DATA CAPTURED': 'GEEN GEGEVENS VASTGELEGD',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Deel uw QR-code om realtime bezoekers- en bestelstatistieken te verzamelen.',
    'INTELLIGENCE STUDIO': 'ANALYSESTUDIO',
    'Insights Engine Locked': 'Geavanceerde analyse vergrendeld',
    'INSIGHTS ENGINE LOCKED': 'GEAVANCEERDE ANALYSE VERGRENDELD',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Geavanceerde trendanalyses, bulkbeheeropdrachten en gegevensexporten zijn gereserveerd voor',
    'Unlock Pro Features': 'Ontgrendel Pro-functies',
    'UNLOCK PRO FEATURES': 'ONTGRENDEL PRO-FUNCTIES',
    'MENU VIEWS': 'MENUBEKIKKINGEN',
    'ITEM INTERACTIONS': 'GERECHTINTERACTIES',
    'GUEST RATING': 'GASTENBEOORDELING',
    'DELIVERY REVENUE': 'BEZORGOMZET',
    'AVERAGE DELIVERY ORDER': 'GEMIDDELDE BEZORGBESTELLING',
    'AVERAGE DELIVERY FEE': 'GEMIDDELDE BEZORGKOSTEN',
    'INCLUDING FEES': 'INCLUSIEF KOSTEN',
    'ZONE AVERAGE': 'ZONEGEMIDDELDE',
    'TRAFFIC PROJECTIONS': 'BEZOEKERSPROGNOSE',
    'TELEMETRY SIGNAL TRACKING': 'ACTIVITEITSSIGNAAL-TRACKING',
    'VEGETARIAN': 'VEGETARISCH',
    'VEGAN': 'VEGAN',
    'SPICY': 'PITTIG',
    'GLUTEN-FREE': 'GLUTENVRIJ',
    'Reset: All In-Stock': 'Herstellen: Alles op voorraad',
    'Emergency Stop (All Out)': 'Noodstop: Alles uitverkocht',
    'No compliance issues': 'Geen problemen gedetecteerd',
    'NO COMPLIANCE ISSUES': 'GEEN PROBLEMEN GEDETECTEERD',
    'detected in current menu': 'in het huidige menu',
    'DETECTED IN CURRENT MENU': 'IN HET HUIDIGE MENU',
    'Telemetry History (24h)': 'Activiteitengeschiedenis (24u)',
    'TELEMETRY HISTORY (24H)': 'ACTIVITEITENGESCHIEDENIS (24U)',
    'Telemetry History (30d)': 'Activiteitengeschiedenis (30d)',
    'TELEMETRY HISTORY (30D)': 'ACTIVITEITENGESCHIEDENIS (30D)',
    'Save': 'Opslaan',
    'Saved': 'Opgeslagen',
    'Saving...': 'Opslaan...',
    'Save Changes': 'Wijzigingen opslaan',
    'Save Settings': 'Instellingen opslaan',
    'Cancel': 'Annuleren',
    'Close': 'Sluiten',
    'Create': 'Maken',
    'Add': 'Toevoegen',
    'Edit': 'Bewerken',
    'Delete': 'Verwijderen',
    'Remove': 'Verwijderen',
    'Update': 'Bijwerken',
    'Refresh': 'Vernieuwen',
    'Search': 'Zoeken',
    'Search...': 'Zoeken...',
    'Search items...': 'Items zoeken...',
    'Search categories...': 'Categorieën zoeken...',
    'Filter': 'Filteren',
    'All': 'Alles',
    'Export': 'Exporteren',
    'Import': 'Importeren',
    'Download': 'Downloaden',
    'Upload': 'Uploaden',
    'Copy': 'Kopiëren',
    'Copied': 'Gekopieerd',
    'Copied!': 'Gekopieerd!',
    'Preview': 'Voorbeeld',
    'Open': 'Open',
    'View': 'Bekijken',
    'Manage': 'Beheren',
    'Configure': 'Configureren',
    'Enable': 'Inschakelen',
    'Disable': 'Uitschakelen',
    'Enabled': 'Ingeschakeld',
    'Disabled': 'Uitgeschakeld',
    'Active': 'Actief',
    'Inactive': 'Inactief',
    'Available': 'Beschikbaar',
    'Unavailable': 'Niet beschikbaar',
    'Visible': 'Zichtbaar',
    'Hidden': 'Verborgen',
    'Required': 'Verplicht',
    'Optional': 'Optioneel',
    'Confirm': 'Bevestigen',
    'Back': 'Terug',
    'Next': 'Volgende',
    'Done': 'Klaar',
    'Upgrade': 'Upgraden',
    'Sign Out': 'Uitloggen',
    'Exit to Main Admin': 'Terug naar beheer',
    'Change Language': 'Taal wijzigen',
    'Add Category': 'Categorie toevoegen',
    'Add Menu Item': 'Menu-item toevoegen',
    'Add Item': 'Item toevoegen',
    'Add Ingredient': 'Ingrediënt toevoegen',
    'Add Department': 'Afdeling toevoegen',
    'Delete Item': 'Item verwijderen',
    'Delete Category': 'Categorie verwijderen',
    'Upload Image': 'Afbeelding uploaden',
    'Upload Photo': 'Foto uploaden',
    'Select Category': 'Selecteer categorie',
    'Select Department': 'Selecteer afdeling',
    'Add translation...': 'Vertaling toevoegen...',
    'Clear': 'Wissen',
    'Reset': 'Herstellen',
    'Apply': 'Toepassen',
    'Submit': 'Versturen',
    'Core & Operations': 'Bedrijfsvoering',
    'Menu & Experience': 'Menu en ervaring',
    'Enterprise & Security': 'Beheer & beveiliging',
    'System & Control': 'Systeem & instellingen',
    'Intelligence Studio': 'Analysestudio',
    'Venue Insights': 'Verkooprapporten',
    'Reservations': 'Reserveringen',
    'Floor Plan': 'Tafelplattegrond',
    'Live Waitlist': 'Wachtlijst',
    'Walk-Ins': 'Binnenlopers',
    'Walk-ins': 'Binnenlopers',
    'Departments': 'Afdelingen',
    'Service Hours': 'Servicetijden',
    'Categories': 'Categorieën',
    'Menu Categories': 'Menucategorieën',
    'Menu Items': 'Menu-items',
    'Items': 'Items',
    'Ingredients': 'Ingrediënten',
    'Loyalty Program': 'Loyaliteitsprogramma',
    'Campaign Studio': 'Aanbiedingen',
    'Signage': 'Digitale schermen',
    'QR Code': 'QR-codes',
    'Kitchen Ops': 'Keukenscherm',
    'Theme Engine': 'Vormgeving & kleuren',
    'Governance': 'Beheer',
    'Audit Log': 'Activiteitenlogboek',
    'API & Webhooks': 'Koppelingen',
    'SSO & Security': 'Beveiliging & inloggen',
    'Team': 'Personeel',
    'Billing': 'Facturering en abonnement',
    'SEO & Google': 'Vindbaarheid in Google',
    'Notifications': 'Meldingen',
    'Support Center': 'Hulp en ondersteuning',
    'Settings': 'Instellingen',
    'Name': 'Naam',
    'Description': 'Beschrijving',
    'Price': 'Prijs',
    'Status': 'Status',
    'Actions': 'Acties',
    'Role': 'Rol',
    'Email': 'E-mail',
    'Phone': 'Telefoon',
    'Address': 'Adres',
    'City': 'Stad',
    'Currency': 'Valuta',
    'Language': 'Taal',
    'Theme': 'Thema',
    'General': 'Algemeen',
    'Total Revenue': 'Totale omzet',
    'Total Orders': 'Totaal bestellingen',
    'Average Order': 'Gemiddelde bestelling',
    'Active Sessions': 'Actieve tafels',
    'Recent Orders': 'Recente bestellingen',
    'Live Orders': 'Live bestellingen',
    'Table': 'Tafel',
    'Date': 'Datum',
    'Time': 'Tijd',
    'Amount': 'Bedrag',
    'Customer': 'Klant',
    'In Stock': 'Op voorraad',
    'Out of Stock': 'Niet op voorraad',
    'Sold Out': 'Uitverkocht',
    'Dine In': 'Ter plaatse',
    'Pickup': 'Afhalen',
    'Delivery': 'Bezorging',
    'Paid': 'Betaald',
    'Unpaid': 'Niet betaald',
    'Pending': 'In afwachting',
    'Preparing': 'In bereiding',
    'Ready': 'Gereed',
    'Completed': 'Voltooid',
    'Cancelled': 'Geannuleerd',
    'Loading...': 'Laden...',
    'No data': 'Geen gegevens',
    'No results found': 'Geen resultaten gevonden',
    'No items found': 'Geen items gevonden',
    'No translations defined yet': 'Nog geen vertalingen ingesteld',
    'Are you sure?': 'Weet u het zeker?',
    'This action cannot be undone.': 'Deze actie kan niet ongedaan worden gemaakt.',
    'Settings saved successfully!': 'Instellingen succesvol opgeslagen!',
    'Administrator': 'Beheerder',
    'Manager': 'Manager',
    'Owner': 'Eigenaar',
    'Chef': 'Chef-kok',
    'Waiter': 'Kelner',
    'Cashier': 'Kassier',
    'Type a command or search...': 'Typ een opdracht of zoek...',
    'Command Palette': 'Opdrachtenpaneel',
    'Demand Velocity': 'Vraagsnelheid',
    'DEMAND VELOCITY': 'VRAAGSNELHEID',
    'Top Performing Entities': 'Best presterende gerechten',
    'TOP PERFORMING ENTITIES': 'BEST PRESTERENDE GERECHTEN',
    'Hourly Load Analysis': 'Uurlijkse drukte-analyse',
    'HOURLY LOAD ANALYSIS': 'UURLIJKSE DRUKTE-ANALYSE',
    'Bulk Command Center': 'Centrum voor bulkacties',
    'BULK COMMAND CENTER': 'CENTRUM VOOR BULKACTIES',
    'Governance Scan': 'Menukwaliteitsscan',
    'GOVERNANCE SCAN': 'MENUKWALITEITSSCAN',
    'No compliance issues detected in current menu': 'Geen problemen gevonden in het huidige menu',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'GEEN PROBLEMEN GEVONDEN IN HET HUIDIGE MENU',
    'Procedural Tagging': 'Automatisch labels toewijzen',
    'PROCEDURAL TAGGING': 'AUTOMATISCH LABELS TOEWIJZEN',
    'Name match...': 'Zoeken op naam...',
    '...Name match': 'Zoeken op naam...',
    'Healthy': 'Gezond',
    'HEALTHY': 'GEZOND',
    'Apply Tags': 'Labels toepassen',
    'APPLY TAGS': 'LABELS TOEPASSEN',
    'Global Price Shift': 'Algemene prijsaanpassing',
    'GLOBAL PRICE SHIFT': 'ALGEMENE PRIJSAANPASSING',
    'Adjust': 'Aanpassen',
    'ADJUST': 'AANPASSEN',
    'Availability Override': 'Beschikbaarheid direct overschrijven',
    'AVAILABILITY OVERRIDE': 'BESCHIKBAARHEID DIRECT OVERSCHRIJVEN',
    'Reset: ALL IN-STOCK': 'Herstellen: ALLES OP VOORRAAD',
    'RESET: ALL IN-STOCK': 'HERSTELLEN: ALLES OP VOORRAAD',
    'EMERGENCY STOP (ALL OUT)': 'Noodstop (ALLES UITVERKOCHT)',
    'Integrity Engine': 'Integriteitssysteem voor menu\'s',
    'INTEGRITY ENGINE': 'INTEGRITEITSSYSTEEM VOOR MENU\'S',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Dit systeem bewaakt automatisch de zichtbaarheid van gerechten, prijsafwijkingen en de volledigheid van beschrijvingen binnen uw zaak.',
    'Inventory Audit Export': 'Voorraadaudit exporteren',
    'INVENTORY AUDIT EXPORT': 'VOORRAADAUDIT EXPORTEREN',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Genereer een volledig overzicht van menuprestaties en gegevens voor offline controle.',
    'Enterprise Data Export': 'Geavanceerde gegevensexport',
    'ENTERPRISE DATA EXPORT': 'GEAVANCEERDE GEGEVENSEXPORT',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Genereer en download uitgebreide prestatie-audits in standaardformaten. Bevat bezoekersstromen, gerechtvraag en conversie.',
    'JSON Snapshot': 'JSON-momentopname',
    'JSON SNAPSHOT': 'JSON-MOMENTOPNAME',
    'Compile CSV Report': 'CSV-rapport samenstellen',
    'COMPILE CSV REPORT': 'CSV-RAPPORT SAMENSTELLEN',
    'Date Segment': 'Datumperiode',
    'DATE SEGMENT': 'DATUMPERIODE',
    'Inbound Views': 'Binnenkomende weergaven',
    'INBOUND VIEWS': 'BINNENKOMENDE WEERGAVEN',
    'Asset Interactions': 'Interacties met gerechten',
    'ASSET INTERACTIONS': 'INTERACTIES MET GERECHTEN',
    'Telemetry History': 'Activiteitsgeschiedenis',
    'TELEMETRY HISTORY': 'ACTIVITEITSGESCHIEDENIS',
    'Telemetry History (7d)': 'Activiteitsgeschiedenis (7d)',
    'TELEMETRY HISTORY (7D)': 'ACTIVITEITSGESCHIEDENIS (7D)',
    'Growth Trajectory': 'Groeitraject',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'De interactiefrequentie met uw menu vertoont een stijgende lijn over de afgelopen 7 dagen.',
    'Deep Analysis Engine': 'Diepgaande analyse',
    'DEEP ANALYSIS ENGINE': 'DIEPGAANDE ANALYSE',
    'Asset Velocity': 'Gerechtenprestaties',
    'ASSET VELOCITY': 'GERECHTENPRESTATIES',
    'Traffic Projections': 'Bezoekersprognoses',
    'Telemetry Signal Tracking': 'Signaal- en activiteitstracking',
    'Service Hour Update': 'Servicetijden bijgewerkt',
    'Late Night Lounge Hours': 'Nachtelijke lounge-uren',
    'Bar & Lounge Hours': 'Bar- en lounge-uren',
    'Breakfast & Brunch Hours': 'Ontbijt- en brunch-uren',
    'Switch Location': 'Locatie wisselen',
    'SWITCH LOCATION': 'LOCATIE WISSELEN',
    'Global Admin': 'Hoofdbeheerder',
    'GLOBAL ADMIN': 'HOOFDBEHEERDER',
    'Staff Access': 'Personeelstoegang',
    'STAFF ACCESS': 'PERSONEELSTOEGANG',
    'Venue Overview': 'Locatie-overzicht',
    'VENUE OVERVIEW': 'LOCATIE-OVERZICHT',
    'Unified Telemetry Engine': 'Centraal activiteitsoverzicht',
    'UNIFIED TELEMETRY ENGINE': 'CENTRAAL ACTIVITEITSOVERZICHT',
    'Pulse': 'Live puls',
    'PULSE': 'LIVE PULS',
    'Insights': 'Inzichten',
    'INSIGHTS': 'INZICHTEN',
    'Reports': 'Rapporten',
    'REPORTS': 'RAPPORTEN',
    'GOVERNANCE': 'BEHEER',
    'Multi-Venue': 'Meerdere vestigingen',
    'MULTI-VENUE': 'MEERDERE VESTIGINGEN',
    'Menu Views': 'Menubekeken',
    'Item Interactions': 'Gerecht-interacties',
    'Guest Rating': 'Gastbeoordeling',
    'Revenue (7d)': 'Omzet (7d)',
    'Revenue (24h)': 'Omzet (24u)',
    'Revenue (30d)': 'Omzet (30d)',
    'Delivery orders (7d)': 'Bezorgbestellingen (7d)',
    'Delivery orders (24h)': 'Bezorgbestellingen (24u)',
    'Delivery orders (30d)': 'Bezorgbestellingen (30d)',
    'Delivery revenue': 'Bezorgomzet',
    'Average delivery order': 'Gemiddelde bezorgbestelling',
    'Average delivery fee': 'Gemiddelde bezorgkosten',
    'Zone average': 'Zonegemiddelde',
    'Including fees': 'Inclusief kosten',
    'No deliveries': 'Geen bezorgingen',
    'No orders': 'Geen bestellingen',
    'No reviews': 'Geen beoordelingen',
    'Live': 'Live',
    'LIVE': 'LIVE',
    'reviews': 'beoordelingen',
    'orders': 'bestellingen',
    'deliveries': 'bezorgingen',
    'of orders': 'van bestellingen',
    'rate': 'percentage',
    'Recent Activity': 'Recente activiteit',
    'Real-time Venue Telemetry': 'Realtime locatieactiviteit',
    'REAL-TIME VENUE TELEMETRY': 'REALTIME LOCATIEACTIVITEIT',
    'Rapid Pulse View': 'Snelle live weergave',
    'Anonymous Guest': 'Anonieme gast',
    'scanned menu': 'heeft het menu gescand',
    'viewed item': 'heeft gerecht bekeken',
    'interacted with': 'had interactie met',
    'placed order': 'heeft bestelling geplaatst',
    'searched menu': 'heeft in menu gezocht',
    'Main Landing': 'Hoofdpagina',
    'Trace': 'Geverifieerd',
    'TRACE': 'GEVERIFIEERD',
    'Gaining Telemetry...': 'Activiteitsgegevens laden...',
    'No recent traffic detected': 'Geen recente activiteit gedetecteerd',
    'Activities will appear here as guests scan your QR code': 'Activiteiten verschijnen hier zodra gasten uw QR-code scannen',
    'Analyze All Logs': 'Alle logboeken analyseren',
    'Just now': 'Zojuist',
    'less than a minute ago': 'minder dan een minuut geleden',
    '1 minute ago': '1 minuut geleden',
    '1 hour ago': '1 uur geleden',
    'about 1 hour ago': 'ongeveer 1 uur geleden',
    '1 day ago': '1 dag geleden',
    'DAY AGO 1': '1 dag geleden',
    '2 days ago': '2 dagen geleden',
    'DAY AGO 2': '2 dagen geleden',
    '3 days ago': '3 dagen geleden',
    'DAY AGO 3': '3 dagen geleden',
    '4 days ago': '4 dagen geleden',
    'DAY AGO 4': '4 dagen geleden',
    '5 days ago': '5 dagen geleden',
    'DAY AGO 5': '5 dagen geleden',
    '6 days ago': '6 dagen geleden',
    'DAY AGO 6': '6 dagen geleden',
    '7 days ago': '7 dagen geleden',
    'DAY AGO 7': '7 dagen geleden',
    'about 1 month ago': 'ongeveer 1 maand geleden',
    'Monday': 'Maandag',
    'Tuesday': 'Dinsdag',
    'Wednesday': 'Woensdag',
    'Thursday': 'Donderdag',
    'Friday': 'Vrijdag',
    'Saturday': 'Zaterdag',
    'Sunday': 'Zondag',
    'Mon': 'Ma',
    'Tue': 'Di',
    'Wed': 'Wo',
    'Thu': 'Do',
    'Fri': 'Vr',
    'Sat': 'Za',
    'Sun': 'Zo',
    'Opening Hours': 'Openingstijden',
    'Start Time': 'Starttijd',
    'End Time': 'Eindtijd',
    'Opening Time': 'Openingstijd',
    'Closing Time': 'Sluitingstijd',
    'All Day': 'Hele dag',
    'Closed': 'Gesloten',
    'Active Days': 'Actieve dagen',
    'Active Schedule': 'Actief schema',
    'Always Available': 'Altijd beschikbaar',
    'Always Visible': 'Altijd zichtbaar',
    'New Category': 'Nieuwe categorie',
    'Category': 'Categorie',
    'Category Name': 'Categorienaam',
    'Category Description': 'Categorieomschrijving',
    'Display Order': 'Weergavevolgorde',
    'Item Name': 'Gerechtnaam',
    'Item Description': 'Omschrijving',
    'Base Price': 'Basisprijs',
    'Preparation Time': 'Bereidingstijd',
    'Calories': 'Calorieën',
    'Featured Item': 'Uitgelicht gerecht',
    'Chef\'s Recommendation': 'Aanbeveling van de chef',
    'Spicy': 'Pittig',
    'Vegetarian': 'Vegetarisch',
    'Vegan': 'Veganistisch',
    'Gluten-Free': 'Glutenvrij',
    'Halal': 'Halal',
    'Kosher': 'Kosher',
    'Contains Nuts': 'Bevat noten',
    'Dairy-Free': 'Zuivelvrij',
    'Allergen Exclusions': 'Allergenen',
    'Table Number': 'Tafelnummer',
    'Assigned Table': 'Toegewezen tafel',
    'Seats': 'Zitplaatsen',
    'Capacity': 'Capaciteit',
    'Indoor': 'Binnen',
    'Outdoor': 'Buiten',
    'Terrace': 'Terras',
    'Bar': 'Bar',
    'Add Table': 'Tafel toevoegen',
    'Edit Table': 'Tafel bewerken',
    'Delete Table': 'Tafel verwijderen',
    'Bookings': 'Boekingen',
    'Add to Waitlist': 'Aan wachtlijst toevoegen',
    'Guest Name': 'Naam gast',
    'Party Size': 'Aantal personen',
    'Arrival Time': 'Aankomsttijd',
    'Special Requests': 'Speciale verzoeken',
    'Confirmed': 'Bevestigd',
    'No Show': 'Niet opgekomen',
    'Add Staff Member': 'Medewerker toevoegen',
    'Invite Staff': 'Medewerker uitnodigen',
    'Email Address': 'E-mailadres',
    'Phone Number': 'Telefoonnummer',
    'Download QR Code': 'QR-code downloaden',
    'Print Table Stands': 'Tafelkaarten afdrukken',
    'Today': 'Vandaag',
    'Yesterday': 'Gisteren',
    'This Week': 'Deze week',
    'This Month': 'Deze maand',
    'Last 30 Days': 'Laatste 30 dagen',
    'Access Denied': 'Toegang geweigerd',
    'Advanced Settings': 'Geavanceerde instellingen',
    'Change Image': 'Afbeelding wijzigen',
    'Remove Image': 'Afbeelding verwijderen',
    'Announcement Banner': 'Aankondigingsbanner',
    'Please fill in all required fields.': 'Vul alle verplichte velden in.',
    'Changes saved successfully': 'Wijzigingen succesvol opgeslagen',
    '(You)': '(Jij)',
    'AI Auto-Tag': 'AI automatisch taggen',
    'Analyzing:': 'Analyseren:',
    'Apple (Fruit/Vegan)': 'Appel (Fruit/Vegan)',
    'Cancel editing': 'Bewerken annuleren',
    'Capacity:': 'Capaciteit:',
    'Carrot (Veggie)': 'Wortel (Groente)',
    'CircleDot (Organic)': 'Cirkelpunt (Biologisch)',
    'Coffee (Caffeine)': 'Koffie (Cafeïne)',
    'Configuration': 'Configuratie',
    'Copy verification record name': 'Naam verificatierecord kopiëren',
    'Copy verification record value': 'Waarde verificatierecord kopiëren',
    'Critical exception intercepted': 'Kritieke uitzondering opgevangen',
    'Kernel Panic': 'Systeemfout',
    'DNA (Organic/Bio)': 'DNA (Biologisch/Bio)',
    'Egg (Ovo/Allergen)': 'Ei (Ovo/Allergeen)',
    'Empty': 'Leeg',
    'Engraving...': 'Genereren...',
    'Flame (Spicy)': 'Vlam (Pittig)',
    'Free': 'Gratis',
    'From': 'Van',
    'Generating': 'Genereren',
    'Governance & Traceability Studio': 'Beheer- en traceerbaarheidshub',
    'Greet': 'Welkom',
    'Heart (Healthy)': 'Hart (Gezond)',
    'In-App': 'In de app',
    'Infinity SVG': 'Vector SVG (Onbeperkt)',
    'Leaf (Default)': 'Blad (Standaard)',
    'MAX Feature': 'Enterprise-functie',
    'Milk (Dairy)': 'Melk (Zuivel)',
    'Network': 'Netwerk',
    'Notify SMS': 'Sms-melding',
    'Order': 'Bestelling',
    'Original': 'Origineel',
    'Overview': 'Overzicht',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 standaardrollen',
    'Shield (Safety)': 'Schild (Veiligheid)',
    'Soup (Warm/Soup)': 'Soep (Warm/Soep)',
    'Standard Pro View': 'Standaardweergave',
    'Star': 'Ster',
    'Synchronized with Venue Identity': 'Aangepast aan uw huisstijl',
    'Synchronizing Security Context...': 'Beveiligde verbinding maken...',
    'Targeted Timing': 'Servicerooster',
    'Targeting': 'Van toepassing op',
    'To': 'Tot',
    'Toggle Navigation': 'Navigatie wisselen',
    'Venue': 'Locatie',
    'Venue Governance Suite': 'Locatiebeheer & instellingen',
    'Volume:': 'Volume:',
    'WHITEBOARD NOTE...': 'Notitie voor het team...',
    'Warning (Alert)': 'Waarschuwing (Melding)',
    'Wheat (Flour/Gluten)': 'Tarwe (Bloem/Gluten)',
    'Wine (Alcohol)': 'Wijn (Alcohol)',
    'YouTube/Vimeo/Direct Link': 'Videolink (YouTube, Vimeo of MP4)',
    'Your name': 'Uw naam',
    '2. Table Number': '2. Tafelnummer',
    'for Morocco or': 'voor Marokko, of',
    'in': 'in',
    'to': 'naar',
    'e.g. 12 or Patio-1': 'bijv. 12 of Terras-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'bijv. Huisgemaakte fettuccine, zwarte wintertruffel, 36 maanden gerijpte parmezaan...',
    'e.g. Head Sommelier & Wine Lead': 'bijv. Hoofdsommelier',
    'e.g. London': 'bijv. Amsterdam',
    'e.g. SOMMELIER, HOST, BARISTA': 'bijv. SOMMELIER, GASTHEER, BARISTA',
    'e.g. Skyline Lounge': 'bijv. Skyline Lounge',
    'e.g. TRUFFLE OIL': 'bijv. TRUFFELOLIE',
    'e.g. The Grand Cafe': 'bijv. Het Grand Café',
    'e.g. Window preference, high chair': 'bijv. Bij het raam, kinderstoel',
    'Copied to clipboard!': 'Gekopieerd naar klembord!',
    'Copy failed.': 'Kopiëren mislukt.',
    'Link copied!': 'Link gekopieerd!',
    'Logo uploaded': 'Logo geüpload',
    'Cover image uploaded': 'Omslagafbeelding geüpload',
    'Profile saved and synced successfully': 'Profiel succesvol opgeslagen en gesynchroniseerd',
    'Category Created': 'Categorie aangemaakt',
    'Category Updated': 'Categorie bijgewerkt',
    'Category Deleted': 'Categorie verwijderd',
    'Item Created': 'Item aangemaakt',
    'Item Updated': 'Item bijgewerkt',
    'Item Deleted': 'Item verwijderd',
    'Menu Published': 'Menu gepubliceerd',
    'Delete Category?': 'Categorie verwijderen?',
    'Delete Menu Item?': 'Dit item verwijderen?',
    'Delete Ingredient?': 'Dit ingrediënt verwijderen?',
    'Delete Service Period?': 'Serviceperiode verwijderen?',
    'Remove team member?': 'Teamlid verwijderen?',
    'Cancel invitation?': 'Uitnodiging annuleren?',
    'Revoke API key?': 'API-sleutel intrekken?',
    'Delete webhook endpoint?': 'Webhook-eindpunt verwijderen?',
    'Clear all notifications?': 'Alle meldingen wissen?',
    'Disconnect Stripe account?': 'Stripe-account loskoppelen?',
    'Disconnect Google Business Profile?': 'Google Bedrijfsprofiel loskoppelen?',
    'Campaigns published successfully!': 'Campagnes succesvol gepubliceerd!',
    'Changes successfully synchronized with Google Business Profile!': 'Wijzigingen succesvol gesynchroniseerd met Google Bedrijfsprofiel!',
    'Google Business Profile successfully connected!': 'Google Bedrijfsprofiel succesvol gekoppeld!',
    'Google Business Profile disconnected.': 'Google Bedrijfsprofiel losgekoppeld.',
    'Google Post draft saved successfully!': 'Concept Google-bericht succesvol opgeslagen!',
    'Post draft deleted.': 'Conceptbericht verwijderd.',
    'Review response published successfully!': 'Reactie op review succesvol gepubliceerd!',
    'SEO configuration saved successfully!': 'SEO-configuratie succesvol opgeslagen!',
    'SEO metadata translated across all languages!': 'SEO-metadata vertaald naar alle talen!',
    'Venue profile & bio translated across all languages!': 'Locatieprofiel en beschrijving vertaald naar alle talen!',
    'Reservation configuration saved successfully': 'Reserveringsinstellingen succesvol opgeslagen',
    'Stripe settings saved successfully.': 'Stripe-instellingen succesvol opgeslagen.',
    'Print-Ready PDF downloaded successfully': 'Afdrukklaar PDF succesvol gedownload',
    'Vector QR PDF downloaded successfully': 'Vector QR PDF succesvol gedownload',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Pre-presspakket (300 DPI) succesvol gegenereerd',
    'At least one language must be enabled.': 'Ten minste één taal moet ingeschakeld zijn.',
    'Enter a non-zero percentage before adjusting prices.': 'Voer een percentage groter dan nul in voordat u prijzen aanpast.',
    'Please enter a Venue Name before translating.': 'Voer eerst een locatienaam in voordat u vertaalt.',
    'Please enter an item name before running dietary and allergen analysis.': 'Voer eerst een itemnaam in voordat u allergenen analyseert.',
    'Please write a reply before submitting.': 'Schrijf eerst een reactie voordat u verzendt.',
    'Table 3 Called Waiter': 'Tafel 3 roept bediening',
    'Table Call Bell': 'Tafelbel',
    'Waiter Calls': 'Oproepen bediening',
    'Waiter Dispatch & Calls': 'Bediening & Oproepbeheer',
    'Kitchen Display & Whiteboard': 'Keukenscherm & Notitiebord',
    'Customer Orders': 'Klantbestellingen',
    'Bakery': 'Bakkerij',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Café / Koffiebar',
    'Fast Food': 'Fastfood',
    'Gourmet / Fine Dining': 'Gastronomisch / Fine dining',
    'Hotel': 'Hotel',
    'General Local Business': 'Algemeen lokaal bedrijf',
    'Classic': 'Klassiek',
    'Clean': 'Strak',
    'Modern': 'Modern',
    'Elegant': 'Elegant',
    'Energetic': 'Dynamisch',
    'Glass': 'Glaseffect',
    'Playful & Casual': 'Speels & Ongedwongen',
    'Sophisticated & Gourmet': 'Verfijnd & Gastronomisch',
    'Warm & Welcoming': 'Warm & Gastvrij',
    'Executive': 'Exclusief',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Upgrade vereist: Meertalige menu\'s zijn beschikbaar in het Pro-abonnement.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Upgrade vereist: Bestellingen en bedieningsoproepen zijn niet beschikbaar in het Starter-abonnement.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Vector SVG-export is een Pro- en Max-functie. Upgrade om schaalbare vectoren te downloaden.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Afdrukklaar PDF-export is een Pro- en Max-functie. Upgrade om PDF-bestanden te downloaden.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Het Pro-abonnement is beperkt tot 3 actieve campagnes. Pauzeer een andere campagne of upgrade naar MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Het Starter-abonnement bevat tot 20 tafels (Tafels 1–20). Upgrade naar PRO voor onbeperkte tafels.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Het Starter-abonnement ondersteunt genummerde tafels (1–20). Aangepaste namen (bijv. Terras, VIP) vereisen PRO.',
    'Add Menu Categories & Items': 'Categorieën & items toevoegen',
    'Add Menu Items': 'Menu-items toevoegen',
    'Organize your menu sections and food items.': 'Organiseer uw menusecties en gerechten overzichtelijk.',
    'Add and manage your food & beverage offerings.': 'Voeg uw eten en drinken toe en beheer deze eenvoudig.',
    'Customize Venue Branding': 'Huisstijl en branding aanpassen',
    'Set your brand colors, logo, and presentation.': 'Kies uw merkkleuren, logo en presentatiestijl.',
    'Get Your QR Code & Go Live': 'Download uw QR-code & ga live',
    'Download table QR codes and preview your live menu.': 'Download tafel-QR-codes en bekijk de live preview van uw menu.',
    'Account & Venue Created Successfully!': 'Account & locatie succesvol aangemaakt!',
    '⚡ Off-Peak Hours': '⚡ Daluurtijden',
    '🍽️ Lunch Crowd': '🍽️ Lunchdrukte',
    '🚀 Weekend Rush': '🚀 Weekenddrukte',
    '🌟 Seasonal Special': '🌟 Seizoensspecial',
    '📣 Engagement Buzz': '📣 Klantbetrokkenheid',
    'Festive & Energetic': 'Feestelijk & Dynamisch',
    'Urgent & High-Conversion': 'Tijdelijk & Doeltreffend',
    'Generated 3 tailored campaign strategies': '3 op maat gemaakte campagnestrategieën gegenereerd',
    'Could not generate campaign ideas. Please try again.': 'Kon geen campagne-ideeën genereren. Probeer het opnieuw.',
    'Venue Themes': 'Locatiethema\'s',
    'Choose the character of your public menu': 'Kies het visuele karakter van uw publieke menu',
    'Glassmorphism & Depth': 'Glaseffect & diepte',
    'Independent XML Sitemap': 'Onafhankelijke XML-sitemap',
    'Structured Schema & Sync': 'Gestructureerde gegevens & synchronisatie',
    'Improve how your venue appears on Google and in local search results.': 'Verbeter hoe uw locatie wordt weergegeven op Google en in lokale zoekresultaten.',
    'Terms of Service': 'Servicevoorwaarden',
    'Confirm your email': 'Bevestig uw e-mailadres',
    'Check your spam / junk folder': 'Controleer uw map met ongewenste e-mail of spam',
    'Commercial pre-press print production package': 'Professioneel pre-press drukwerkpakket',
    'Over-bleed safety margin': 'Afloopveiligheidsmarge',
    'Crop Marks': 'Snijtekens',
    'Guillotine cutting guides': 'Snijhulplijnen',
    'Global Banners': 'Globale banners',
    'Featured Items': 'Aanbevolen items',
    'AI Assistant': 'AI-assistent',
    'Custom Role Schemas': 'Aangepaste roldefinities',
    'Notification Preferences': 'Meldingsvoorkeuren',
    'Preferences saved automatically': 'Voorkeuren automatisch opgeslagen',
    'Menu Extractor (PDF & Images)': 'Menu-importeur (PDF & afbeeldingen)',
    'Multi-Language Translation': 'Meertalige vertaling',
    'Start Menu Translation': 'Start menuvertaling',
    'Menu Items Hub': 'Menu-itemshub',
    'Open Menu Items': 'Open menu-items',
    'Enterprise Traceability': 'Traceerbaarheid & audit',
    'Full History': 'Volledige geschiedenis',
    'Billing & Plans': 'Facturatie & abonnementen',
    'Subscription Governance': 'Abonnementsbeheer',
    'Platform Usage': 'Platformgebruik',
    'Menu Page Views': 'Paginaweergaven van menu',
    'Enterprise Identity': 'Bedrijfsidentiteit',
    'Access Governance': 'Toegangsbeheer',
    'Required Fields *': 'Verplichte velden *',
    'Enabled Languages': 'Ingeschakelde talen',
    'Venue Info & Bio Localizations': 'Vertalingen van locatie-informatie en bio',
    'Public Address': 'Publiek adres',
    'Public URL Slug': 'Publieke URL-slug',
    'Contact Phone': 'Contacttelefoon',
    'Business Address': 'Zakelijk adres',
    'No Categories Yet': 'Nog geen categorieën',
    'No Custom Roles Configured': 'Geen aangepaste rollen geconfigureerd',
    'No Google reviews retrieved yet': 'Nog geen Google-reviews opgehaald',
    'No Guests Found': 'Geen gasten gevonden',
    'No Menu Items Yet': 'Nog geen gerechten in het menu',
    'No Promotions Configured': 'Geen promoties geconfigureerd',
    'No Service Periods Yet': 'Nog geen serviceperioden ingesteld',
    'No Support Tickets': 'Geen ondersteuningstickets',
    'No Tables on Floor Plan': 'Geen tafels op de plattegrond',
    'No Team Members Found': 'Geen teamleden gevonden',
    'No Upcoming Events': 'Geen aankomende evenementen',
    'No Walk-Ins Recorded': 'Geen inloopgasten geregistreerd',
    'No items are available right now': 'Momenteel zijn er geen items beschikbaar',
    'No notifications found': 'Geen meldingen gevonden',
    'No reservations matching your filters': 'Geen reserveringen gevonden voor deze filters',
    'No tables currently occupied': 'Momenteel zijn er geen tafels bezet',
    'No venue selected': 'Geen locatie geselecteerd',
    'Waitlist': 'Wachtlijst',
    'Waitlist is Empty': 'De wachtlijst is leeg',
    'Waves (Gluten)': 'Korenhalmen (Gluten)',
    'We sent a temporary verification code to': 'We hebben een tijdelijke verificatiecode gestuurd naar',
    'Website URL': 'Website-URL',
    'What the customer receives': 'Wat de klant ontvangt',
    'White-label control is available on eligible plans.': 'White-label branding is beschikbaar op geselecteerde abonnementen.',
    'Why this works:': 'Waarom dit werkt:',
    'WiFi Broadcast': 'WiFi-uitzending',
    'WiFi Password': 'WiFi-wachtwoord',
    'Window seat, dietary request, birthday celebration...': 'Tafel bij het raam, dieetwensen, verjaardagsviering...',
    'Your Published Response': 'Uw gepubliceerde reactie',
    'Your Venues': 'Uw locaties',
    '🎉 Ready to publish!': '🎉 Klaar om te publiceren!',
    'e.g. Grill, Salad, Pastry': 'bijv. Grill, Salades, Patisserie',
    'e.g. Guest called, emergency': 'bijv. Gast heeft gebeld, noodgeval',
    'e.g. Italian, Moroccan, Burgers': 'bijv. Italiaans, Marokkaans, Burgers',
    'e.g. Mains, Pasta, Starters': 'bijv. Hoofdgerechten, Pasta, Voorgerechten',
    'e.g. Signature Truffle Pasta': 'bijv. Huisgemaakte truffelpasta',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'bijv. Middagkoffie-actie, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'bijv. Ontbijt, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'bijv. Kom genieten van live jazz elke vrijdagavond om 20:00 uur!',
  },
  tr: {
    '123 Main St, City': 'Ana Cadde No: 123, Şehir',
    'Balance': 'Puan Bakiyesi',
    'Lifetime': 'Toplam Kazanılan',
    'Source': 'Kaynak',
    'Menu': 'Menü',
    'Advanced': 'Gelişmiş',
    'Core Suite': 'Temel Araçlar',
    'Privacy': 'Gizlilik',
    'Support': 'Destek',
    'Save Layout': 'Masa Düzenini Kaydet',
    'Shape:': 'Masa Şekli:',
    'SEARCH LIBRARY...': 'KÜTÜPHANEDE ARA...',
    'Search library...': 'Kütüphanede ara...',
    'None': 'Yok',
    'Type': 'Tür',
    'Target': 'Hedef',
    'Value / Target': 'Değer / Hedef',
    'London (GMT/BST)': 'Londra (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tokyo (JST)',
    'Sydney (AEST/AEDT)': 'Sidney (AEST/AEDT)',
    'Start Color': 'Başlangıç Rengi',
    'Depth Shade': 'Derinlik Gölgesi',
    'Matrix Geometry': 'QR Nokta Geometrisi',
    'Eye Architecture': 'QR Köşe Tasarımı',
    'Brand Centerpiece': 'QR Merkezindeki Logo',
    'Scannability Shield': 'Taranabilirlik Koruması',
    'Environment Blur': 'Arka Plan Bulanıklığı',
    'Color Target:': 'Renk Profili:',
    'DPI:': 'DPI (Baskı Çözünürlüğü):',
    '3mm Bleed Box': '3 mm Kesim Payı',
    '300 DPI': '300 DPI (Yüksek Çözünürlük)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'SAML 2.0 Protokolü',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'IdP Varlık Kimliği (Entity ID)',
    'SSO URL': 'SSO Giriş URL’si',
    'X.509 Certificate': 'X.509 Güvenlik Sertifikası',
    'ACS URL': 'ACS Servis URL’si',
    'Entity ID': 'Varlık Kimliği',
    'Navigate': 'Gezin',
    'Select': 'Seç',
    'OmniSearch v1.0': 'Evrensel Arama OmniSearch',
    'Back to order review': 'Sipariş İncelemesine Geri Dön',
    'Return to Intelligence Studio': 'Analitik Stüdyosuna Geri Dön',
    'View Full Notification Center': 'Tüm Bildirimleri Görüntüle',
    'Read all': 'Tümünü Okundu İşaretle',
    'Cancel department': 'Departmanı İptal Et',
    'Save department': 'Departmanı Kaydet',
    'Venue Segmentation': 'İşletme Alanı Bölümlendirmesi',
    'Collaborate on your menu together.': 'Ekibinizle birlikte menünüz üzerinde çalışın.',
    'Timeline': 'Zaman Çizelgesi',
    'Calendar': 'Rezervasyon Takvimi',
    'Book': 'Masa Ayırt',
    'Duration': 'Kalış Süresi',
    'Time & Duration': 'Saat ve Süre',
    'Seating Preference': 'Oturma Tercihi',
    'Section': 'Bölüm',
    'Min Capacity': 'En Az Kişi',
    'Max Capacity': 'En Çok Kişi',
    'Phone (Optional)': 'Telefon (İsteğe Bağlı)',
    'Name or Walk-in': 'Misafir Adı veya Randevusuz',
    'Next day': 'Ertesi Gün',
    'Next month': 'Gelecek Ay',
    'Live Connection': 'Canlı Bağlantı',
    'Live Inventory': 'Canlı Stok Takibi',
    'Main Venue (Full Menu)': 'Ana İşletme (Tam Menü)',
    'Preview Menu': 'Menüyü Önizle',
    'Manual Setup Guide': 'Manuel Kurulum Kılavuzu',
    'Connected & Active': 'Bağlı ve Etkin',
    'CTA Action': 'Eylem Çağrısı Butonu (CTA)',
    'Phone Contact': 'İletişim Numarası',
    'Menu URL': 'Menü Web Adresi',
    'Google Location': 'Google Haritalar Konumu',
    'Field': 'Alan',
    'Linked': 'Bağlandı',
    'Synchronizing Hub...': 'Veriler Eşitleniyor...',
    'Data Integrity': 'Veri Bütünlüğü ve Güvenilirliği',
    'Station Governance Suite': 'İstasyon ve Hazırlık Yönetimi',
    'Open audit log': 'Denetim Günlüğünü Aç',
    'Signal / Time': 'Olay / Zaman',
    'Identity': 'Kullanıcı / Kimlik',
    'Invitation Invalid': 'Davet bağlantısı geçersiz veya süresi dolmuş',
    'Invited To': 'Katılmaya davet edildiniz:',
    'Team Limit Reached': 'Ekip Üyesi Sınırına Ulaşıldı',
    'Venue Limit Reached': 'İşletme Sınırına Ulaşıldı',
    'Venue Setup Required': 'İşletme Kurulumu Gereklidir',
    'Create Venue': 'Yeni İşletme Ekle',
    'TableGreet Onboarding': 'TableGreet Başlangıç Rehberi',
    'No payment required': 'Ödeme Gerekmez',
    'Selected Plan': 'Seçilen Paket',
    'Payment Method': 'Ödeme Yöntemi',
    'Invoice ID': 'Fatura Numarası',
    'Verified': 'Doğrulandı',
    'Accent color': 'Vurgu Rengi',
    'Set the voice and overall light level.': 'Genel tonu ve aydınlık seviyesini belirleyin.',
    'Layout architecture': 'Yerleşim Mimarisi',
    'Navigation & motion': 'Gezinme ve Hareket Efektleri',
    'Loading preview engine': 'Önizleme motoru yükleniyor',
    'Real Live Preview': 'Birebir Canlı Önizleme',
    'Included with Pro & Max': 'Pro ve Max Paketlerine Dahildir',
    'Daypart Scheduling': 'Öğün ve Zaman Dilimi Planlaması',
    'Operational Daypart Architecture': 'Operasyonel Zaman Dilimi Mimarisi',
    'Service Protocol': 'Servis Protokolü',
    'Configuration Engine': 'Yapılandırma Motoru',
    'Linked Department': 'Bağlı Departman',
    'Team Hub': 'Ekip Yönetim Merkezi',
    'Team Size': 'Ekip Büyüklüğü',
    'Joined': 'Katılım Tarihi',
    'Cancel Invitation': 'Daveti İptal Et',
    'Initialize New Organization': 'Yeni Kuruluş Oluştur',
    'Operational': 'Faal ve Çalışıyor',
    'Pro Feature': 'Pro Özelliği',
    'Current State': 'Mevcut Durum',
    'Open on the big TV': 'Büyük TV Ekranında Açın',
    'Menu Board': 'Dijital Menü Panosu',
    'Reward': 'Ödül',
    'Reward name': 'Ödül Adı',
    'Cancel & refund': 'İptal Et ve İade Yap',
    'Code': 'Kod',
    'Find': 'Bul',
    'Complete': 'Tamamlandı',
    'Seated': 'Masada',
    'No-Show': 'Gelmedi (No-Show)',
    'Walk-in': 'Randevusuz Giriş',
    'Party': 'Kişi Sayısı',
    'Turnover': 'Masa Devir Hızı',
    'We couldn\'t load the menu': 'Menü şu anda yüklenemedi',
    'Venue Not Found': 'İşletme Bulunamadı',
    'Live Availability': 'Canlı Bulunabilirlik',
    'Multi-Language Suite': 'Çok Dilli Yönetim Modülü',
    'Key name': 'Anahtar Adı',
    'Revoke API key': 'API Anahtarını İptal Et',
    'Delete webhook': 'Webhook’u Sil',
    'Identity Provider': 'Kimlik Sağlayıcı (IdP)',
    'Provider': 'Sağlayıcı',
    'Mandatory MFA': 'Zorunlu İki Aşamalı Doğrulama (MFA)',
    'Account Identity': 'Hesap Kimliği',
    'Login Email': 'Giriş E-postası',
    'New Login Email': 'Yeni Giriş E-postası',
    'Phone Login': 'Telefon ile Giriş',
    'SMS Verification Code': 'SMS Doğrulama Kodu',
    'Integration Method': 'Entegrasyon Yöntemi',
    'Stripe Connect': 'Stripe Connect Çevrimiçi Ödeme',
    'Stripe Secret Key': 'Stripe Gizli Anahtarı',
    'Stripe Webhook Secret': 'Stripe Webhook Gizli Kodu',
    'Client-Owned': 'İşletmeye Ait',
    'Verify connection': 'Bağlantıyı Doğrula',
    'Setup Health': 'Kurulum Durumu',
    'Privacy Protected': 'Gizlilik Korumalı',
    'Privacy Policy': 'Gizlilik Politikası',
    'Continue': 'Devam Et',
    'Sign In': 'Giriş Yap',
    'Didn\'t get it?': 'Kodu almadınız mı?',
    'Logged In Account': 'Giriş Yapılmış Hesap',
    'Annual': 'Yıllık',
    'Unread': 'Okunmamış',
    'Starred': 'Yıldızlı',
    'High / Urgent': 'Yüksek / Acil',
    'Mark Read': 'Okundu Olarak İşaretle',
    'Delete notification': 'Bildirimi Sil',
    'Urgent': 'Acil',
    'High': 'Yüksek',
    'Normal': 'Normal',
    'Low': 'Düşük',
    'Select a Ticket': 'Destek Talebi Seçin',
    'Urgent (Immediate attention)': 'Acil (Derhal Müdahale Gerektirir)',
    'Type your reply here...': 'Yanıtınızı buraya yazın...',
    'Print Card (PDF)': 'Masa Kartını Yazdır (PDF)',
    'Advanced Printing Engine': 'Gelişmiş Profesyonel Baskı Motoru',
    'Multi-Venue Governance': 'Çoklu Şube ve İşletme Yönetimi',
    'Network Revenue': 'Tüm Şubeler Toplam Geliri',
    'Live Network Total': 'Canlı Ağ Toplamı',
    'Audit Log Hub': 'İşlem Denetim Günlüğü Merkezi',
    'Export Log': 'Günlüğü Dışa Aktar',
    'CUSTOM ROLE SCHEMAS': 'ÖZEL ROL VE YETKİ ŞEMALARI',
    'Unique internal role identifier.': 'Rol için benzersiz dahili tanımlayıcı.',
    'Human-friendly role label.': 'Ekip için anlaşılır görev unvanı.',
    'Marketing Hub': 'Pazarlama Merkezi',
    'Revenue Generation Suite': 'Gelir Artırma Araçları',
    'Active Campaign Limit Reached (3/3)': 'Aktif Kampanya Sınırına Ulaşıldı (3/3)',
    'Banner Content': 'Afiş İçeriği',
    'Style Profile': 'Stil Profili',
    'Action URL (Optional)': 'Eylem Bağlantısı (İsteğe Bağlı)',
    'Promotion Label': 'Promosyon Başlığı',
    'Target Selection': 'Hedef Ürün Seçimi',
    'Edit Promotion': 'Promosyonu Düzenle',
    'Delete Promotion': 'Promosyonu Sil',
    'Loyalty Engine': 'Sadakat ve Ödül Programı',
    'Reward catalog': 'Ödül Kataloğu',
    'Add reward': 'Ödül Ekle',
    'Loyalty ID': 'Üyelik Numarası',
    'No point activity yet': 'Henüz puan hareketi bulunmuyor',
    'Scan or enter QR value': 'QR kodu tarayın veya kodu girin',
    'TableGreet Admin': 'TableGreet Yönetim Paneli',
    'Capacity Pacing': 'Kapasite ve Rezervasyon Akışı',
    'Cancellation Policy': 'İptal Koşulları',
    'Booking Experience': 'Masa Rezervasyon Deneyimi',
    'Floor Occupancy': 'Salon Doluluk Oranı',
    'Currently Seated': 'Şu Anda Masada',
    'Seat Party': 'Masaya Yerleştir',
    'Seat Table': 'Masa Aç',
    'Select Table': 'Masa Seçin',
    'Table / Capacity': 'Masa / Kişi Kapasitesi',
    'Table Name/Number': 'Masa Adı veya Numarası',
    'Mark Arrived': 'Geldi Olarak İşaretle',
    'Mark No-Show': 'Gelmedi Olarak İşaretle',
    'Complete Dining': 'Servisi Tamamla ve Masayı Boşalt',
    'Standard Dining (Default)': 'Standart Yemek Alanı (Varsayılan)',
    'Walk-In Dining Log': 'Randevusuz Misafir Kayıt Defteri',
    'Notification Center': 'Bildirim Merkezi',
    'Single Sign-On': 'Tek Noktadan Giriş (SSO)',
    'Welcome Aboard!': 'TableGreet Ailesine Hoş Geldiniz!',
    'Select your TableGreet plan': 'TableGreet Planınızı Seçin',
    'Free Plan Activation': 'Ücretsiz Planı Etkinleştir',
    'Venue Created!': 'İşletme Başarıyla Oluşturuldu!',
    'Upgrade for Unlimited': 'Sınırsız Kullanıma Yükselt',
    'Auto-Tag Menu': 'Menüyü Otomatik Etiketle',
    'Start Auto-Tagging': 'Otomatik Etiketlemeyi Başlat',
    'Launch Auto-Tagger': 'Otomatik Etiketleyiciyi Başlat',
    'Launch PDF Importer': 'PDF Menü İçe Aktarmayı Başlat',
    'Analyzing Menu...': 'Menü Analiz Ediliyor...',
    'Analyzing Recipe...': 'Tarif Analiz Ediliyor...',
    'Manual Availability': 'Manuel Bulunabilirlik',
    'Technical Breakdown': 'Teknik Detaylar',
    'Dietary Taxonomy': 'Diyet ve Alerjen Sınıflandırması',
    'Dietary & Allergen AI': 'Diyet ve Alerjen Yapay Zekası',
    'Inherit from Category': 'Kategoriden Devral',
    'Ingredient Library': 'Malzeme Kütüphanesi',
    'Ingredient Name': 'Malzeme Adı',
    'Menu Organization': 'Menü Düzeni ve Hiyerarşisi',
    'Inventory Taxonomy Hub': 'Kategori ve Sınıflandırma Merkezi',
    'Category Intelligence': 'Kategori Analitiği',
    'Service Period': 'Servis Dönemi',
    'Manual Configuration / All Day': 'Manuel Ayar / Tüm Gün',
    'Operational Department': 'Operasyonel Departman',
    'Global / No Department': 'Genel / Departmansız',
    'Cancel category': 'Kategoriyi İptal Et',
    'New category name...': 'Yeni kategori adı...',
    'Save category': 'Kategoriyi Kaydet',
    'QR Matrix Only': 'Yalnızca QR Kodu',
    'Vector & PDF': 'Vektör ve PDF',
    'Vector QR (SVG)': 'Vektörel QR Kodu (SVG)',
    'Vector QR (PDF)': 'Baskıya Hazır QR Kodu (PDF)',
    'Save QR Style': 'QR Tasarımını Kaydet',
    'Share Link': 'Bağlantıyı Paylaş',
    'High Fidelity 300DPI': '300 DPI Yüksek Baskı Kalitesi',
    'Generating 300 DPI PDF...': '300 DPI PDF Oluşturuluyor...',
    'Give your venue a point of view.': 'İşletmenize kendine has görsel bir kimlik kazandırın.',
    'Signage Menu Not Found': 'Dijital Pano Menüsü Bulunamadı',
    'Scan Menu': 'Menüyü Tara',
    'Open on your phone': 'Telefonunuzda Açın',
    'Full Screen': 'Tam Ekran',
    'Lock TV board': 'TV Ekranını Kilitle',
    'Big-Screen Menu': 'Büyük Ekran Menü Panosu',
    'TV Signage': 'TV Dijital Menü Panosu',
    'General Information': 'Genel Bilgiler',
    'Venue Name *': 'İşletme Adı *',
    'Public Slug *': 'Genel Bağlantı Tanımı *',
    'Venue Logo': 'İşletme Logosu',
    'Hero Cover Image': 'Ana Kapak Görseli',
    'Venue Timezone': 'İşletme Saat Dilimi',
    'Primary Brand Color': 'Ana Marka Rengi',
    'TableGreet footer credit': 'Altbilgide TableGreet imzası',
    'White-Label Branding': 'Özel Beyaz Etiket Markalama',
    'Client DNS Setup': 'Özel Alan Adı DNS Kurulumu',
    'Copy CNAME target': 'CNAME hedefini kopyala',
    'Copy TableGreet URL': 'TableGreet URL\'sini kopyala',
    'Included TableGreet URL': 'Dahil Olan TableGreet Bağlantısı',
    'Open TableGreet URL': 'TableGreet bağlantısını aç',
    'Public menu': 'Müşteriye Açık Menü',
    'Public venue delivery': 'Menüden Adrese Teslimat',
    'Zone name': 'Teslimat Bölgesi Adı',
    'Coverage note': 'Hizmet Alanı Notu',
    'Default ETA min': 'Tahmini En Kısa Süre (dakika)',
    'Default ETA max': 'Tahmini En Uzun Süre (dakika)',
    'Packaging fee': 'Paketleme Ücreti',
    'Courier handled by': 'Kurye Sorumlusu',
    'Third-party courier': 'Üçüncü Taraf Kurye',
    'Unified Experience': 'Bütünleşik Misafir Deneyimi',
    'Save WiFi to Venue': 'İşletme Wi-Fi Bilgisini Kaydet',
    'Network Name (SSID)': 'Ağ Adı (SSID)',
    'Audio Alert Chime': 'Sesli Uyarı Sinyali',
    'Time Format': 'Saat Biçimi',
    'Auto / Default': 'Otomatik / Varsayılan',
    'Syncing SEO & Google Data...': 'SEO ve Google Verileri Eşitleniyor...',
    'Google Connection': 'Google Bağlantısı',
    'Search Engine Metadata': 'Arama Motoru Meta Verileri',
    'Social card preview': 'Sosyal Medya Paylaşım Önizlemesi',
    'Allow Indexing': 'İndekslenmeye İzin Ver',
    'Canonical URL Override': 'Kanonik URL Geçersiz Kılma',
    'Price Range': 'Fiyat Aralığı',
    'Latitude': 'Enlem',
    'Longitude': 'Boylam',
    'Google Place ID / Location ID': 'Google Yer Kimliği (Place ID)',
    'Google Search Preview': 'Google Arama Sonucu Önizlemesi',
    'Linked Google Location': 'Bağlı Google Konumu',
    'SEO Documentation': 'SEO Kullanım Rehberi',
    ', and': ', ve',
    'and': 've',
    'Delete Department?': 'Departmanı Sil?',
    'Delete Department': 'Departmanı Sil',
    'Live URL': 'Canlı URL',
    'Copy full live URL': 'Tam canlı URL\'yi kopyala',
    'Open live department menu': 'Canlı departman menüsünü aç',
    'Loading departments...': 'Departmanlar yükleniyor...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Çok departmanlı işletme bölümlendirmesi PRO ve üzeri paketlerde sunulan bir özelliktir.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Menünüzü salonlar, masalar, barlar veya fiziksel alanlara göre bölün.',
    'Targeted Ordering': 'Hedefli Sipariş Yönlendirme',
    'Route guests and orders to specific preparation areas.': 'Misafirleri ve siparişleri belirli hazırlık istasyonlarına yönlendirin.',
    'Operational Departments': 'Operasyonel Departmanlar',
    'OPERATIONAL DEPARTMENTS': 'OPERASYONEL DEPARTMANLAR',
    'Venue Segmentation Hub': 'İşletme Bölümlendirme Merkezi',
    'VENUE SEGMENTATION HUB': 'İŞLETME BÖLÜMLENDİRME MERKEZİ',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Menü bölümlerinizi gruplamak için departmanlar oluşturun. Örneğin büyük bir işletmede',
    'Restaurant': 'Restoran',
    'Poolside': 'Havuz Başı',
    'department, each with its own categories.': 'gibi bölümler olup her birinin kendi kategorileri bulunabilir.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Departmanlar işletmenizi Restoran, Bar veya Havuz Başı gibi bölümlere ayırmanızı ve her biri için özel kategoriler oluşturmanızı sağlar.',
    'Department name': 'Departman Adı',
    'New department name...': 'Yeni departman adı...',
    'No Linked Daypart (Always Available)': 'Bağlı Çalışma Saati Yok (Her Zaman Müsait)',
    'Linked Daypart': 'Bağlı Çalışma Saati',
    'Localizations': 'Yerelleştirmeler',
    'LOCALIZATIONS': 'YERELLEŞTİRMELER',
    'AI Auto-Translate': 'Yapay Zeka ile Otomatik Çevir',
    'AI AUTO-TRANSLATE': 'YAPAY ZEKA İLE OTOMATİK ÇEVİR',
    'Translated name': 'Çevrilmiş İsim',
    'No Departments Yet': 'Henüz Departman Yok',
    'Add First Department': 'İlk Departmanı Ekle',
    'Avg Order Value': 'Ortalama Sipariş Tutarı',
    'AVG ORDER VALUE': 'ORTALAMA SİPARİŞ TUTARI',
    'item taps': 'ürün tıklaması',
    'ITEM TAPS': 'ÜRÜN TIKLAMASI',
    'Avg Rating': 'Ortalama Puan',
    'AVG RATING': 'ORTALAMA PUAN',
    'Revenue (Last 7 Days)': 'Gelir (Son 7 Gün)',
    'REVENUE (LAST 7 DAYS)': 'GELİR (SON 7 GÜN)',
    'Daily breakdown': 'Günlük Dağılım',
    'DAILY BREAKDOWN': 'GÜNLÜK DAĞILIM',
    'Menu Views (Last 7 Days)': 'Menü Görüntülemeleri (Son 7 Gün)',
    'MENU VIEWS (LAST 7 DAYS)': 'MENÜ GÖRÜNTÜLEMELERİ (SON 7 GÜN)',
    'Guest traffic': 'Müşteri Trafiği',
    'GUEST TRAFFIC': 'MÜŞTERİ TRAFİĞİ',
    'Most Viewed Items': 'En Çok Görüntülenen Ürünler',
    'MOST VIEWED ITEMS': 'EN ÇOK GÖRÜNTÜLENEN ÜRÜNLER',
    'Top Rated Items': 'En Yüksek Puanlı Ürünler',
    'TOP RATED ITEMS': 'EN YÜKSEK PUANLI ÜRÜNLER',
    'Waiter Requests': 'Garson Çağrıları',
    'WAITER REQUESTS': 'GARSON ÇAĞRILARI',
    'Total': 'Toplam',
    'TOTAL': 'TOPLAM',
    'Order Status (30 days)': 'Sipariş Durumları (30 Gün)',
    'ORDER STATUS (30 DAYS)': 'SİPARİŞ DURUMLARI (30 GÜN)',
    'Recent Guest Feedback': 'Son Müşteri Yorumları',
    'RECENT GUEST FEEDBACK': 'SON MÜŞTERİ YORUMLARI',
    'Conversion Rate': 'Dönüşüm Oranı',
    'CONVERSION RATE': 'DÖNÜŞÜM ORANI',
    'of menu visitors placed an order': 'menü ziyaretçisi sipariş verdi',
    'Refreshed': 'Güncellendi',
    'REFRESHED': 'GÜNCELLENDİ',
    'Last 30 days': 'Son 30 Gün',
    'LAST 30 DAYS': 'SON 30 GÜN',
    'cash': 'nakit',
    'Cash': 'Nakit',
    'CASH': 'NAKİT',
    'bill': 'hesap',
    'Bill': 'Hesap',
    'BILL': 'HESAP',
    'water': 'su',
    'Water': 'Su',
    'WATER': 'SU',
    'service': 'servis',
    'Service': 'Servis',
    'SERVICE': 'SERVİS',
    'No interaction data yet': 'Henüz etkileşim verisi yok',
    'No ratings collected yet': 'Henüz değerlendirme toplanmadı',
    'No requests in period': 'Bu dönemde çağrı bulunmuyor',
    'No feedback collected yet': 'Henüz müşteri yorumu alınmadı',
    'No menu views recorded yet': 'Henüz menü görüntülenmesi kaydedilmedi',
    'Dish Rating Breakdown': 'Yemek Puanı Dağılımı',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Yemek puan sıralamaları ve memnuniyet verileri yalnızca MAX ve Enterprise paketlerinde mevcuttur.',
    'Customer Sentiment & Reviews': 'Müşteri Memnuniyeti ve Yorumlar',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Doğrudan müşteri değerlendirmeleri ve memnuniyet analizleri için MAX veya Enterprise aboneliği gerekir.',
    'Reviews AI': 'Yorum Analizi Yapay Zekası',
    'Guest Feedback': 'Müşteri Geri Bildirimleri',
    'Upgrade to unlock rating analytics →': 'Puan analizlerini açmak için paketinizi yükseltin →',
    'Upgrade to Max': 'Max Paketine Yükselt',
    'UPGRADE TO MAX': 'MAX PAKETİNE YÜKSELT',
    'MAX PLAN': 'MAX PAKETİ',
    'No Data Yet': 'Henüz Veri Yok',
    'Share your QR code to start collecting real venue insights.': 'Gerçek işletme analizlerini toplamaya başlamak için QR kodunuzu paylaşın.',
    'Revenue Analytics': 'Gelir Analizleri',
    'REVENUE ANALYTICS': 'GELİR ANALİZLERİ',
    'Track your earnings, orders, and average ticket values over time.': 'Zaman içindeki kazançlarınızı, siparişlerinizi ve ortalama adisyon tutarınızı takip edin.',
    'Operational Velocity': 'Operasyonel Hız',
    'OPERATIONAL VELOCITY': 'OPERASYONEL HIZ',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Müşteri trafiğini, popüler yemekleri ve garson çağrı yoğunluğunu izleyin.',
    'Upgrade to Pro': 'Pro\'ya Yükselt',
    'UPGRADE TO PRO': 'PRO PAKETİNE YÜKSELT',
    'Loading Insights…': 'Analizler yükleniyor…',
    'Loading Insights...': 'Analizler yükleniyor...',
    'Telemetry Unavailable': 'Aktivite verileri şu anda kullanılamıyor',
    'TELEMETRY UNAVAILABLE': 'AKTİVİTE VERİLERİ ŞU ANDA KULLANILAMIYOR',
    'No Data Captured': 'Henüz veri kaydedilmedi',
    'NO DATA CAPTURED': 'HENÜZ VERİ KAYDEDİLMEDİ',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Gerçek zamanlı ziyaretçi ve sipariş verilerini toplamak için QR kodunuzu paylaşın.',
    'INTELLIGENCE STUDIO': 'ANALİZ MERKEZİ',
    'Insights Engine Locked': 'Gelişmiş Analizler Kilitli',
    'INSIGHTS ENGINE LOCKED': 'GELİŞMİŞ ANALİZLER KİLİTLİ',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Gelişmiş trend analizleri, toplu menü işlemleri ve veri dışa aktarımları şu kullanıcılar içindir:',
    'Unlock Pro Features': 'Pro Özelliklerin Kilidini Aç',
    'UNLOCK PRO FEATURES': 'PRO ÖZELLİKLERİN KİLİDİNİ AÇ',
    'MENU VIEWS': 'MENÜ GÖRÜNTÜLENMELERİ',
    'ITEM INTERACTIONS': 'ÜRÜN ETKİLEŞİMLERİ',
    'GUEST RATING': 'MÜŞTERİ PUANI',
    'DELIVERY REVENUE': 'TESLİMAT GELİRİ',
    'AVERAGE DELIVERY ORDER': 'ORTALAMA TESLİMAT SİPARİŞİ',
    'AVERAGE DELIVERY FEE': 'ORTALAMA TESLİMAT ÜCRETİ',
    'INCLUDING FEES': 'ÜCRETLER DÂHİL',
    'ZONE AVERAGE': 'BÖLGE ORTALAMASI',
    'TRAFFIC PROJECTIONS': 'MÜŞTERİ TRAFİĞİ TAHMİNLERİ',
    'TELEMETRY SIGNAL TRACKING': 'AKTİVİTE SİNYALİ TAKİBİ',
    'VEGETARIAN': 'VEJETARYEN',
    'VEGAN': 'VEGAN',
    'SPICY': 'ACILI',
    'GLUTEN-FREE': 'GLÜTENSİZ',
    'Reset: All In-Stock': 'Sıfırla: Tümü Stokta',
    'Emergency Stop (All Out)': 'Acil Durdurma: Tümü Tükendi',
    'No compliance issues': 'Herhangi bir uyumsuzluk yok',
    'NO COMPLIANCE ISSUES': 'HERHANGİ BİR UYUMSUZLUK YOK',
    'detected in current menu': 'mevcut menüde',
    'DETECTED IN CURRENT MENU': 'MEVCUT MENÜDE',
    'Telemetry History (24h)': 'Aktivite Geçmişi (24s)',
    'TELEMETRY HISTORY (24H)': 'AKTİVİTE GEÇMİŞİ (24S)',
    'Telemetry History (30d)': 'Aktivite Geçmişi (30g)',
    'TELEMETRY HISTORY (30D)': 'AKTİVİTE GEÇMİŞİ (30G)',
    'Save': 'Kaydet',
    'Saved': 'Kaydedildi',
    'Saving...': 'Kaydediliyor...',
    'Save Changes': 'Değişiklikleri kaydet',
    'Save Settings': 'Ayarları kaydet',
    'Cancel': 'İptal',
    'Close': 'Kapat',
    'Create': 'Oluştur',
    'Add': 'Ekle',
    'Edit': 'Düzenle',
    'Delete': 'Sil',
    'Remove': 'Kaldır',
    'Update': 'Güncelle',
    'Refresh': 'Yenile',
    'Search': 'Ara',
    'Search...': 'Ara...',
    'Search items...': 'Öğeleri ara...',
    'Search categories...': 'Kategorileri ara...',
    'Filter': 'Filtrele',
    'All': 'Tümü',
    'Export': 'Dışa aktar',
    'Import': 'İçe aktar',
    'Download': 'İndir',
    'Upload': 'Yükle',
    'Copy': 'Kopyala',
    'Copied': 'Kopyalandı',
    'Copied!': 'Kopyalandı!',
    'Preview': 'Önizleme',
    'Open': 'Açık',
    'View': 'Görüntüle',
    'Manage': 'Yönet',
    'Configure': 'Yapılandır',
    'Enable': 'Etkinleştir',
    'Disable': 'Devre dışı bırak',
    'Enabled': 'Etkin',
    'Disabled': 'Devre dışı',
    'Active': 'Aktif',
    'Inactive': 'Pasif',
    'Available': 'Mevcut',
    'Unavailable': 'Mevcut değil',
    'Visible': 'Görünür',
    'Hidden': 'Gizli',
    'Required': 'Zorunlu',
    'Optional': 'İsteğe bağlı',
    'Confirm': 'Onayla',
    'Back': 'Geri',
    'Next': 'İleri',
    'Done': 'Tamam',
    'Upgrade': 'Yükselt',
    'Sign Out': 'Çıkış yap',
    'Exit to Main Admin': 'Ana Panele Dön',
    'Change Language': 'Dili Değiştir',
    'Add Category': 'Kategori Ekle',
    'Add Menu Item': 'Menü Öğesi Ekle',
    'Add Item': 'Öğe Ekle',
    'Add Ingredient': 'Malzeme Ekle',
    'Add Department': 'Bölüm Ekle',
    'Delete Item': 'Öğeyi Sil',
    'Delete Category': 'Kategoriyi Sil',
    'Upload Image': 'Görsel Yükle',
    'Upload Photo': 'Fotoğraf Yükle',
    'Select Category': 'Kategori Seç',
    'Select Department': 'Bölüm Seç',
    'Add translation...': 'Çeviri ekle...',
    'Clear': 'Temizle',
    'Reset': 'Sıfırla',
    'Apply': 'Uygula',
    'Submit': 'Gönder',
    'Core & Operations': 'Operasyonlar',
    'Menu & Experience': 'Menü ve Sunum',
    'Enterprise & Security': 'Yönetim ve Güvenlik',
    'System & Control': 'Ayarlar ve Destek',
    'Intelligence Studio': 'Analiz Merkezi',
    'Venue Insights': 'Satış Raporları',
    'Reservations': 'Rezervasyonlar',
    'Floor Plan': 'Masa Düzeni',
    'Live Waitlist': 'Bekleme Listesi',
    'Walk-Ins': 'Rezervasyonsuz Misafirler',
    'Walk-ins': 'Rezervasyonsuz Misafirler',
    'Departments': 'Bölümler',
    'Service Hours': 'Servis Saatleri',
    'Categories': 'Kategoriler',
    'Menu Categories': 'Menü Kategorileri',
    'Menu Items': 'Menü Öğeleri',
    'Items': 'Öğeler',
    'Ingredients': 'Malzemeler',
    'Loyalty Program': 'Sadakat Programı',
    'Campaign Studio': 'Kampanyalar ve İndirimler',
    'Signage': 'Ekran Menüsü',
    'QR Code': 'Masa QR Kodları',
    'Kitchen Ops': 'Mutfak Ekranı',
    'Theme Engine': 'Tasarım ve Renkler',
    'Governance': 'Yönetişim',
    'Audit Log': 'İşlem Geçmişi',
    'API & Webhooks': 'Entegrasyonlar',
    'SSO & Security': 'Güvenlik ve Giriş',
    'Team': 'Personel',
    'Billing': 'Abonelik ve Faturalar',
    'SEO & Google': 'Google Görünürlüğü',
    'Notifications': 'Bildirimler',
    'Support Center': 'Yardım Merkezi',
    'Settings': 'İşletme Ayarları',
    'Name': 'Ad',
    'Description': 'Açıklama',
    'Price': 'Fiyat',
    'Status': 'Durum',
    'Actions': 'İşlemler',
    'Role': 'Rol',
    'Email': 'E-posta',
    'Phone': 'Telefon',
    'Address': 'Adres',
    'City': 'Şehir',
    'Currency': 'Para Birimi',
    'Language': 'Dil',
    'Theme': 'Tema',
    'General': 'Genel',
    'Total Revenue': 'Toplam Ciro',
    'Total Orders': 'Toplam Sipariş',
    'Average Order': 'Ortalama Hesap',
    'Active Sessions': 'Aktif Masalar',
    'Recent Orders': 'Son Siparişler',
    'Live Orders': 'Canlı Siparişler',
    'Table': 'Masa',
    'Date': 'Tarih',
    'Time': 'Saat',
    'Amount': 'Tutar',
    'Customer': 'Müşteri',
    'In Stock': 'Stokta',
    'Out of Stock': 'Tükendi',
    'Sold Out': 'Tükendi',
    'Dine In': 'Restoranda',
    'Pickup': 'Gel-Al',
    'Delivery': 'Teslimat',
    'Paid': 'Ödendi',
    'Unpaid': 'Ödenmedi',
    'Pending': 'Beklemede',
    'Preparing': 'Hazırlanıyor',
    'Ready': 'Hazır',
    'Completed': 'Tamamlandı',
    'Cancelled': 'İptal edildi',
    'Loading...': 'Yükleniyor...',
    'No data': 'Veri yok',
    'No results found': 'Sonuç bulunamadı',
    'No items found': 'Öğe bulunamadı',
    'No translations defined yet': 'Henüz çeviri eklenmedi',
    'Are you sure?': 'Emin misiniz?',
    'This action cannot be undone.': 'Bu işlem geri alınamaz.',
    'Settings saved successfully!': 'Ayarlar başarıyla kaydedildi!',
    'Administrator': 'Yönetici',
    'Manager': 'Müdür',
    'Owner': 'İşletme Sahibi',
    'Chef': 'Şef',
    'Waiter': 'Garson',
    'Cashier': 'Kasiyer',
    'Type a command or search...': 'Bir komut yazın veya arayın...',
    'Command Palette': 'Hızlı İşlemler',
    'Demand Velocity': 'Talep Hızı',
    'DEMAND VELOCITY': 'TALEP HIZI',
    'Top Performing Entities': 'En Çok Satan Ürünler',
    'TOP PERFORMING ENTITIES': 'EN ÇOK SATAN ÜRÜNLER',
    'Hourly Load Analysis': 'Saatlik Yoğunluk Analizi',
    'HOURLY LOAD ANALYSIS': 'SAATLİK YOĞUNLUK ANALİZİ',
    'Bulk Command Center': 'Toplu İşlem Merkezi',
    'BULK COMMAND CENTER': 'TOPLU İŞLEM MERKEZİ',
    'Governance Scan': 'Menü Uyumluluk Taraması',
    'GOVERNANCE SCAN': 'MENÜ UYUMLULUK TARAMASI',
    'No compliance issues detected in current menu': 'Mevcut menüde herhangi bir sorun bulunamadı',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'MEVCUT MENÜDE HERHANGİ BİR SORUN BULUNAMADI',
    'Procedural Tagging': 'Otomatik Etiketleme',
    'PROCEDURAL TAGGING': 'OTOMATİK ETİKETLEME',
    'Name match...': 'Ada göre eşleştir...',
    '...Name match': 'Ada göre eşleştir...',
    'Healthy': 'Sağlıklı',
    'HEALTHY': 'SAĞLIKLI',
    'Apply Tags': 'Etiketleri Uygula',
    'APPLY TAGS': 'ETİKETLERİ UYGULA',
    'Global Price Shift': 'Genel Fiyat Güncelleme',
    'GLOBAL PRICE SHIFT': 'GENEL FİYAT GÜNCELLEME',
    'Adjust': 'Uygula',
    'ADJUST': 'UYGULA',
    'Availability Override': 'Acil Stok ve Bulunurluk Kontrolü',
    'AVAILABILITY OVERRIDE': 'ACİL STOK VE BULUNURLUK KONTROLÜ',
    'Reset: ALL IN-STOCK': 'Sıfırla: TÜMÜ MEVCUT',
    'RESET: ALL IN-STOCK': 'SIFIRLA: TÜMÜ MEVCUT',
    'EMERGENCY STOP (ALL OUT)': 'Acil Durdurma (TÜMÜ TÜKENDİ)',
    'Integrity Engine': 'Menü Bütünlük ve Güvence Sistemi',
    'INTEGRITY ENGINE': 'MENÜ BÜTÜNLÜK VE GÜVENCE SİSTEMİ',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Akıllı denetim sistemi, işletmenizdeki ürün görünürlüğünü, fiyat anomalilerini ve menü açıklamalarının doğruluğunu otomatik olarak izler.',
    'Inventory Audit Export': 'Menü ve Stok Denetim Verisini Dışa Aktar',
    'INVENTORY AUDIT EXPORT': 'MENÜ VE STOK DENETİM VERİSİNİ DIŞA AKTAR',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Çevrimdışı denetim için menü performansı ve detaylarını içeren kapsamlı bir özet oluşturun.',
    'Enterprise Data Export': 'Kurumsal Veri Dışa Aktarma',
    'ENTERPRISE DATA EXPORT': 'KURUMSAL VERİ DIŞA AKTARMA',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Ziyaretçi trafiği, ürün talep hızı ve sipariş dönüşüm oranlarını içeren kapsamlı performans raporlarını standart formatlarda indirin.',
    'JSON Snapshot': 'JSON Dosyası İndir',
    'JSON SNAPSHOT': 'JSON DOSYASI İNDİR',
    'Compile CSV Report': 'CSV Raporu Oluştur',
    'COMPILE CSV REPORT': 'CSV RAPORU OLUŞTUR',
    'Date Segment': 'Tarih Aralığı',
    'DATE SEGMENT': 'TARİH ARALIĞI',
    'Inbound Views': 'Gelen Görüntülemeler',
    'INBOUND VIEWS': 'GELEN GÖRÜNTÜLEMELER',
    'Asset Interactions': 'Ürün Etkileşimleri',
    'ASSET INTERACTIONS': 'ÜRÜN ETKİLEŞİMLERİ',
    'Telemetry History': 'Canlı Hareket Geçmişi',
    'TELEMETRY HISTORY': 'CANLI HAREKET GEÇMİŞİ',
    'Telemetry History (7d)': 'Canlı Hareket Geçmişi (7g)',
    'TELEMETRY HISTORY (7D)': 'CANLI HAREKET GEÇMİŞİ (7G)',
    'Growth Trajectory': 'Büyüme Eğilimi',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Son 7 günlük dönemde menü etkileşim sıklığınız artış eğilimindedir.',
    'Deep Analysis Engine': 'Detaylı Analiz Sistemi',
    'DEEP ANALYSIS ENGINE': 'DETAYLI ANALİZ SİSTEMİ',
    'Asset Velocity': 'Ürün Popülerliği',
    'ASSET VELOCITY': 'ÜRÜN POPÜLERLİĞİ',
    'Traffic Projections': 'Ziyaretçi Tahminleri',
    'Telemetry Signal Tracking': 'Hareket Sinyali Takibi',
    'Service Hour Update': 'Servis Saatleri Güncellemesi',
    'Late Night Lounge Hours': 'Gece Kulübü Saatleri',
    'Bar & Lounge Hours': 'Bar ve Lounge Saatleri',
    'Breakfast & Brunch Hours': 'Kahvaltı ve Brunch Saatleri',
    'Switch Location': 'Şube Değiştir',
    'SWITCH LOCATION': 'ŞUBE DEĞİŞTİR',
    'Global Admin': 'Genel Yönetici',
    'GLOBAL ADMIN': 'GENEL YÖNETİCİ',
    'Staff Access': 'Personel Erişimi',
    'STAFF ACCESS': 'PERSONEL ERİŞİMİ',
    'Venue Overview': 'Şube Özeti',
    'VENUE OVERVIEW': 'ŞUBE ÖZETİ',
    'Unified Telemetry Engine': 'Birleşik Canlı Takip Sistemi',
    'UNIFIED TELEMETRY ENGINE': 'BİRLEŞİK CANLI TAKİP SİSTEMİ',
    'Pulse': 'Canlı Nabız',
    'PULSE': 'CANLI NABIZ',
    'Insights': 'İçgörüler',
    'INSIGHTS': 'İÇGÖRÜLER',
    'Reports': 'Raporlar',
    'REPORTS': 'RAPORLAR',
    'GOVERNANCE': 'YÖNETİŞİM',
    'Multi-Venue': 'Çoklu Şube',
    'MULTI-VENUE': 'ÇOKLU ŞUBE',
    'Menu Views': 'Menü Görüntülemeleri',
    'Item Interactions': 'Ürün Etkileşimleri',
    'Guest Rating': 'Müşteri Puanı',
    'Revenue (7d)': 'Ciro (7g)',
    'Revenue (24h)': 'Ciro (24s)',
    'Revenue (30d)': 'Ciro (30g)',
    'Delivery orders (7d)': 'Paket Servis Siparişleri (7g)',
    'Delivery orders (24h)': 'Paket Servis Siparişleri (24s)',
    'Delivery orders (30d)': 'Paket Servis Siparişleri (30g)',
    'Delivery revenue': 'Paket Servis Geliri',
    'Average delivery order': 'Ortalama Paket Siparişi',
    'Average delivery fee': 'Ortalama Teslimat Ücreti',
    'Zone average': 'Bölge Ortalaması',
    'Including fees': 'Ücretler Dahil',
    'No deliveries': 'Teslimat Yok',
    'No orders': 'Sipariş Yok',
    'No reviews': 'Değerlendirme Yok',
    'Live': 'Canlı',
    'LIVE': 'CANLI',
    'reviews': 'değerlendirme',
    'orders': 'sipariş',
    'deliveries': 'teslimat',
    'of orders': 'sipariş oranı',
    'rate': 'oran',
    'Recent Activity': 'Son Hareketler',
    'Real-time Venue Telemetry': 'Gerçek Zamanlı Şube Takibi',
    'REAL-TIME VENUE TELEMETRY': 'GERÇEK ZAMANLI ŞUBE TAKİBİ',
    'Rapid Pulse View': 'Hızlı Canlı Görünüm',
    'Anonymous Guest': 'Misafir Müşteri',
    'scanned menu': 'menüyü taradı',
    'viewed item': 'ürünü görüntüledi',
    'interacted with': 'ile etkileşime girdi:',
    'placed order': 'sipariş verdi',
    'searched menu': 'menüde arama yaptı',
    'Main Landing': 'Ana Sayfa',
    'Trace': 'Kayıtlı',
    'TRACE': 'KAYITLI',
    'Gaining Telemetry...': 'Canlı Veriler Alınıyor...',
    'No recent traffic detected': 'Son zamanlarda hareket algılanmadı',
    'Activities will appear here as guests scan your QR code': 'Misafirler QR kodunuzu taradıkça hareketler burada görünecektir',
    'Analyze All Logs': 'Tüm Kayıtları İncele',
    'Just now': 'Az önce',
    'less than a minute ago': 'bir dakikadan az önce',
    '1 minute ago': '1 dakika önce',
    '1 hour ago': '1 saat önce',
    'about 1 hour ago': 'yaklaşık 1 saat önce',
    '1 day ago': '1 gün önce',
    'DAY AGO 1': '1 gün önce',
    '2 days ago': '2 gün önce',
    'DAY AGO 2': '2 gün önce',
    '3 days ago': '3 gün önce',
    'DAY AGO 3': '3 gün önce',
    '4 days ago': '4 gün önce',
    'DAY AGO 4': '4 gün önce',
    '5 days ago': '5 gün önce',
    'DAY AGO 5': '5 gün önce',
    '6 days ago': '6 gün önce',
    'DAY AGO 6': '6 gün önce',
    '7 days ago': '7 gün önce',
    'DAY AGO 7': '7 gün önce',
    'about 1 month ago': 'yaklaşık 1 ay önce',
    'Monday': 'Pazartesi',
    'Tuesday': 'Salı',
    'Wednesday': 'Çarşamba',
    'Thursday': 'Perşembe',
    'Friday': 'Cuma',
    'Saturday': 'Cumartesi',
    'Sunday': 'Pazar',
    'Mon': 'Pzt',
    'Tue': 'Sal',
    'Wed': 'Çar',
    'Thu': 'Per',
    'Fri': 'Cum',
    'Sat': 'Cmt',
    'Sun': 'Paz',
    'Opening Hours': 'Çalışma Saatleri',
    'Start Time': 'Başlangıç Saati',
    'End Time': 'Bitiş Saati',
    'Opening Time': 'Açılış Saati',
    'Closing Time': 'Kapanış Saati',
    'All Day': 'Tüm Gün',
    'Closed': 'Kapalı',
    'Active Days': 'Aktif Günler',
    'Active Schedule': 'Aktif Çalışma Takvimi',
    'Always Available': 'Her Zaman Mevcut',
    'Always Visible': 'Her Zaman Görünür',
    'New Category': 'Yeni Kategori',
    'Category': 'Kategori',
    'Category Name': 'Kategori Adı',
    'Category Description': 'Kategori Açıklaması',
    'Display Order': 'Sıralama',
    'Item Name': 'Ürün Adı',
    'Item Description': 'Ürün Açıklaması',
    'Base Price': 'Taban Fiyat',
    'Preparation Time': 'Hazırlık Süresi',
    'Calories': 'Kalori',
    'Featured Item': 'Öne Çıkan Ürün',
    'Chef\'s Recommendation': 'Şefin Önerisi',
    'Spicy': 'Acılı',
    'Vegetarian': 'Vejetaryen',
    'Vegan': 'Vegan',
    'Gluten-Free': 'Glütensiz',
    'Halal': 'Helal',
    'Kosher': 'Koşer',
    'Contains Nuts': 'Kuruyemiş İçerir',
    'Dairy-Free': 'Süt Ürünü İçermez',
    'Allergen Exclusions': 'Alerjen Bilgisi',
    'Table Number': 'Masa Numarası',
    'Assigned Table': 'Atanan Masa',
    'Seats': 'Koltuk Sayısı',
    'Capacity': 'Kapasite',
    'Indoor': 'İç Mekan',
    'Outdoor': 'Dış Mekan',
    'Terrace': 'Teras',
    'Bar': 'Bar',
    'Add Table': 'Masa Ekle',
    'Edit Table': 'Masayı Düzenle',
    'Delete Table': 'Masayı Sil',
    'Bookings': 'Rezervasyonlar',
    'Add to Waitlist': 'Bekleme Listesine Ekle',
    'Guest Name': 'Müşteri Adı',
    'Party Size': 'Kişi Sayısı',
    'Arrival Time': 'Varış Saati',
    'Special Requests': 'Özel İstekler',
    'Confirmed': 'Onaylandı',
    'No Show': 'Gelmedi',
    'Add Staff Member': 'Personel Ekle',
    'Invite Staff': 'Personel Davet Et',
    'Email Address': 'E-posta Adresi',
    'Phone Number': 'Telefon Numarası',
    'Download QR Code': 'QR Kodunu İndir',
    'Print Table Stands': 'Masa Kartlarını Yazdır',
    'Today': 'Bugün',
    'Yesterday': 'Dün',
    'This Week': 'Bu Hafta',
    'This Month': 'Bu Ay',
    'Last 30 Days': 'Son 30 Gün',
    'Access Denied': 'Erişim Reddedildi',
    'Advanced Settings': 'Gelişmiş Ayarlar',
    'Change Image': 'Görseli Değiştir',
    'Remove Image': 'Görseli Kaldır',
    'Announcement Banner': 'Duyuru Bandı',
    'Please fill in all required fields.': 'Lütfen zorunlu tüm alanları doldurun.',
    'Changes saved successfully': 'Değişiklikler başarıyla kaydedildi',
    '(You)': '(Sen)',
    'AI Auto-Tag': 'Yapay Zekâ ile Otomatik Etiketleme',
    'Analyzing:': 'Analiz ediliyor:',
    'Apple (Fruit/Vegan)': 'Elma (Meyve/Vegan)',
    'Cancel editing': 'Düzenlemeyi iptal et',
    'Capacity:': 'Kapasite:',
    'Carrot (Veggie)': 'Havuç (Sebze)',
    'CircleDot (Organic)': 'Halka Nokta (Organik)',
    'Coffee (Caffeine)': 'Kahve (Kafein)',
    'Configuration': 'Yapılandırma',
    'Copy verification record name': 'Doğrulama kayıt adını kopyala',
    'Copy verification record value': 'Doğrulama kayıt değerini kopyala',
    'Critical exception intercepted': 'Kritik sistem hatası yakalandı',
    'Kernel Panic': 'Sistem Hatası',
    'DNA (Organic/Bio)': 'DNA (Organik/Biyo)',
    'Egg (Ovo/Allergen)': 'Yumurta (Ovo/Alerjen)',
    'Empty': 'Boş',
    'Engraving...': 'Oluşturuluyor...',
    'Flame (Spicy)': 'Alev (Acı)',
    'Free': 'Ücretsiz',
    'From': 'Başlangıç',
    'Generating': 'Oluşturma',
    'Governance & Traceability Studio': 'Mekân Yönetimi ve Denetim Merkezi',
    'Greet': 'Hoş Geldiniz',
    'Heart (Healthy)': 'Kalp (Sağlıklı)',
    'In-App': 'Uygulama İçi',
    'Infinity SVG': 'Vektörel SVG (Sınırsız)',
    'Leaf (Default)': 'Yaprak (Varsayılan)',
    'MAX Feature': 'Kurumsal Özellik',
    'Milk (Dairy)': 'Süt (Süt Ürünleri)',
    'Network': 'Ağ',
    'Notify SMS': 'SMS Bildirimi',
    'Order': 'Sipariş',
    'Original': 'Orijinal',
    'Overview': 'Genel Bakış',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Standart Rol',
    'Shield (Safety)': 'Kalkan (Güvenlik)',
    'Soup (Warm/Soup)': 'Çorba (Sıcak/Çorba)',
    'Standard Pro View': 'Standart Görünüm',
    'Star': 'Yıldız',
    'Synchronized with Venue Identity': 'Mekânınızın kurumsal kimliğiyle uyumlu',
    'Synchronizing Security Context...': 'Güvenli bağlantı kuruluyor...',
    'Targeted Timing': 'Servis Saatleri',
    'Targeting': 'Kapsam',
    'To': 'Bitiş',
    'Toggle Navigation': 'Gezinmeyi Değiştir',
    'Venue': 'Mekân',
    'Venue Governance Suite': 'Mekân Yönetimi ve Ayarları',
    'Volume:': 'Ses Seviyesi:',
    'WHITEBOARD NOTE...': 'Ekip için not veya hatırlatma...',
    'Warning (Alert)': 'Uyarı (Bildirim)',
    'Wheat (Flour/Gluten)': 'Buğday (Un/Glüten)',
    'Wine (Alcohol)': 'Şarap (Alkol)',
    'YouTube/Vimeo/Direct Link': 'Video Bağlantısı (YouTube, Vimeo veya MP4)',
    'Your name': 'Adınız',
    '2. Table Number': '2. Masa Numarası',
    'for Morocco or': 'Fas için veya',
    'in': 'içinde',
    'to': '-e',
    'e.g. 12 or Patio-1': 'örn. 12 veya Teras-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'örn. El yapımı fettuccine, siyah kış trüfü, 36 ay dinlendirilmiş parmesan...',
    'e.g. Head Sommelier & Wine Lead': 'örn. Baş Sommelier ve Şarap Sorumlusu',
    'e.g. London': 'örn. Londra',
    'e.g. SOMMELIER, HOST, BARISTA': 'örn. SOMMELIER, HOST, BARİSTA',
    'e.g. Skyline Lounge': 'örn. Manzara Lounge',
    'e.g. TRUFFLE OIL': 'örn. TRÜF YAĞI',
    'e.g. The Grand Cafe': 'örn. Büyük Kafe',
    'e.g. Window preference, high chair': 'örn. Cam kenarı, bebek mama sandalyesi',
    'Copied to clipboard!': 'Panoya kopyalandı!',
    'Copy failed.': 'Kopyalama başarısız.',
    'Link copied!': 'Bağlantı kopyalandı!',
    'Logo uploaded': 'Logo yüklendi',
    'Cover image uploaded': 'Kapak görseli yüklendi',
    'Profile saved and synced successfully': 'Profil başarıyla kaydedildi ve senkronize edildi',
    'Category Created': 'Kategori Oluşturuldu',
    'Category Updated': 'Kategori Güncellendi',
    'Category Deleted': 'Kategori Silindi',
    'Item Created': 'Ürün Oluşturuldu',
    'Item Updated': 'Ürün Güncellendi',
    'Item Deleted': 'Ürün Silindi',
    'Menu Published': 'Menü Yayınlandı',
    'Delete Category?': 'Kategori Silinsin mi?',
    'Delete Menu Item?': 'Bu Ürün Silinsin mi?',
    'Delete Ingredient?': 'Bu Malzeme Silinsin mi?',
    'Delete Service Period?': 'Servis Dönemi Silinsin mi?',
    'Remove team member?': 'Ekip üyesi çıkarılsın mı?',
    'Cancel invitation?': 'Davetiye iptal edilsin mi?',
    'Revoke API key?': 'API anahtarı iptal edilsin mi?',
    'Delete webhook endpoint?': 'Webhook uç noktası silinsin mi?',
    'Clear all notifications?': 'Tüm bildirimler temizlensin mi?',
    'Disconnect Stripe account?': 'Stripe hesabının bağlantısı kesilsin mi?',
    'Disconnect Google Business Profile?': 'Google İşletme Profili bağlantısı kesilsin mi?',
    'Campaigns published successfully!': 'Kampanyalar başarıyla yayınlandı!',
    'Changes successfully synchronized with Google Business Profile!': 'Değişiklikler Google İşletme Profili ile başarıyla senkronize edildi!',
    'Google Business Profile successfully connected!': 'Google İşletme Profili başarıyla bağlandı!',
    'Google Business Profile disconnected.': 'Google İşletme Profili bağlantısı kesildi.',
    'Google Post draft saved successfully!': 'Google Gönderi taslağı başarıyla kaydedildi!',
    'Post draft deleted.': 'Gönderi taslağı silindi.',
    'Review response published successfully!': 'Yorum yanıtı başarıyla yayınlandı!',
    'SEO configuration saved successfully!': 'SEO yapılandırması başarıyla kaydedildi!',
    'SEO metadata translated across all languages!': 'SEO meta verileri tüm dillere çevrildi!',
    'Venue profile & bio translated across all languages!': 'Mekân profili ve tanıtımı tüm dillere çevrildi!',
    'Reservation configuration saved successfully': 'Rezervasyon ayarları başarıyla kaydedildi',
    'Stripe settings saved successfully.': 'Stripe ayarları başarıyla kaydedildi.',
    'Print-Ready PDF downloaded successfully': 'Baskıya hazır PDF başarıyla indirildi',
    'Vector QR PDF downloaded successfully': 'Vektörel QR PDF başarıyla indirildi',
    'Enterprise 300 DPI Pre-Press Package generated successfully': '300 DPI baskı paketi başarıyla oluşturuldu',
    'At least one language must be enabled.': 'En az bir dil etkinleştirilmelidir.',
    'Enter a non-zero percentage before adjusting prices.': 'Fiyatları ayarlamadan önce sıfırdan farklı bir yüzde girin.',
    'Please enter a Venue Name before translating.': 'Lütfen çeviri yapmadan önce bir Mekân Adı girin.',
    'Please enter an item name before running dietary and allergen analysis.': 'Alerjen ve beslenme analizi yapmadan önce lütfen bir ürün adı girin.',
    'Please write a reply before submitting.': 'Lütfen göndermeden önce bir yanıt yazın.',
    'Table 3 Called Waiter': 'Masa 3 Garson Çağırdı',
    'Table Call Bell': 'Masa Çağrı Zili',
    'Waiter Calls': 'Garson Çağrıları',
    'Waiter Dispatch & Calls': 'Garson Yönlendirme ve Çağrılar',
    'Kitchen Display & Whiteboard': 'Mutfak Ekranı ve Duyuru Panosu',
    'Customer Orders': 'Müşteri Siparişleri',
    'Bakery': 'Fırın / Pastane',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Kafe / Kahve Dükkanı',
    'Fast Food': 'Fast Food / Hızlı Yemek',
    'Gourmet / Fine Dining': 'Gurme / Seçkin Restoran',
    'Hotel': 'Otel',
    'General Local Business': 'Genel Yerel İşletme',
    'Classic': 'Klasik',
    'Clean': 'Sade & Temiz',
    'Modern': 'Modern',
    'Elegant': 'Zarif',
    'Energetic': 'Dinamik & Enerjik',
    'Glass': 'Buzlu Cam',
    'Playful & Casual': 'Samimi ve Neşeli',
    'Sophisticated & Gourmet': 'Seçkin ve Gurme',
    'Warm & Welcoming': 'Sıcak ve Samimi',
    'Executive': 'Yönetici & Prestij',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Yükseltme gerekli: Çok dilli menüler Pro planda mevcuttur.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Yükseltme gerekli: Sipariş ve Garson Çağırma özellikleri Başlangıç planında mevcut değildir.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Vektörel SVG dışa aktarma Pro ve Max özelliğidir. Ölçeklenebilir vektörler için lütfen yükseltin.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Baskıya hazır PDF dışa aktarma Pro ve Max özelliğidir. Lütfen yükseltin.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Pro plan en fazla 3 aktif kampanya ile sınırlıdır. Başka bir kampanyayı duraklatın veya MAX plana yükseltin.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Başlangıç planı en fazla 20 masa içerir (Masa 1–20). Sınırsız masa için PRO plana yükseltin.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Başlangıç planı numaralı masaları destekler (1–20). Özel masa adları (örn. Teras, VIP) PRO plan gerektirir.',
    'Add Menu Categories & Items': 'Menü Kategorileri ve Ürünler Ekle',
    'Add Menu Items': 'Menü Ürünleri Ekle',
    'Organize your menu sections and food items.': 'Menü bölümlerinizi ve yemeklerinizi düzenleyin.',
    'Add and manage your food & beverage offerings.': 'Yiyecek ve içecek menünüzü ekleyin ve yönetin.',
    'Customize Venue Branding': 'Mekân Markasını Özelleştirin',
    'Set your brand colors, logo, and presentation.': 'Marka renklerinizi, logonuzu ve sunum tarzınızı belirleyin.',
    'Get Your QR Code & Go Live': 'QR Kodunuzu Alın ve Yayına Başlayın',
    'Download table QR codes and preview your live menu.': 'Masa QR kodlarını indirin ve canlı menünüzü önizleyin.',
    'Account & Venue Created Successfully!': 'Hesap ve Mekân Başarıyla Oluşturuldu!',
    '⚡ Off-Peak Hours': '⚡ Yoğun Olmayan Saatler',
    '🍽️ Lunch Crowd': '🍽️ Öğle Yemeği Yoğunluğu',
    '🚀 Weekend Rush': '🚀 Hafta Sonu Yoğunluğu',
    '🌟 Seasonal Special': '🌟 Mevsimsel Özel Menü',
    '📣 Engagement Buzz': '📣 Etkileşim ve Tanıtım',
    'Festive & Energetic': 'Coşkulu ve Canlı',
    'Urgent & High-Conversion': 'Zaman Sınırlı ve Etkili',
    'Generated 3 tailored campaign strategies': '3 özel kampanya stratejisi oluşturuldu',
    'Could not generate campaign ideas. Please try again.': 'Kampanya fikirleri oluşturulamadı. Lütfen tekrar deneyin.',
    'Venue Themes': 'Mekân Temaları',
    'Choose the character of your public menu': 'Halka açık menünüzün görsel karakterini seçin',
    'Glassmorphism & Depth': 'Buzlu Cam ve Derinlik',
    'Independent XML Sitemap': 'Bağımsız XML Site Haritası',
    'Structured Schema & Sync': 'Yapılandırılmış Veri ve Senkronizasyon',
    'Improve how your venue appears on Google and in local search results.': 'Mekânınızın Google\'da ve yerel arama sonuçlarındaki görünürlüğünü artırın.',
    'Terms of Service': 'Hizmet Şartları',
    'Confirm your email': 'E-postanızı onaylayın',
    'Check your spam / junk folder': 'Spam veya gereksiz e-posta klasörünüzü kontrol edin',
    'Commercial pre-press print production package': 'Ticari matbaa baskı öncesi üretim paketi',
    'Over-bleed safety margin': 'Taşma payı güvenlik kenar boşluğu',
    'Crop Marks': 'Kırpma İşaretleri',
    'Guillotine cutting guides': 'Giyotin kesim kılavuzları',
    'Global Banners': 'Genel Duyuru Başlıkları',
    'Featured Items': 'Öne Çıkan Ürünler',
    'AI Assistant': 'Yapay Zekâ Asistanı',
    'Custom Role Schemas': 'Özel Rol Şemaları',
    'Notification Preferences': 'Bildirim Tercihleri',
    'Preferences saved automatically': 'Tercihler otomatik olarak kaydedildi',
    'Menu Extractor (PDF & Images)': 'Menü Çıkarıcı (PDF ve Görseller)',
    'Multi-Language Translation': 'Çok Dilli Çeviri',
    'Start Menu Translation': 'Menü Çevirisini Başlat',
    'Menu Items Hub': 'Menü Ürünleri Merkezi',
    'Open Menu Items': 'Menü Ürünlerini Aç',
    'Enterprise Traceability': 'Kurumsal İzlenebilirlik',
    'Full History': 'Tam Geçmiş',
    'Billing & Plans': 'Faturalandırma ve Planlar',
    'Subscription Governance': 'Abonelik Yönetimi',
    'Platform Usage': 'Platform Kullanımı',
    'Menu Page Views': 'Menü Sayfa Görüntülemeleri',
    'Enterprise Identity': 'Kurumsal Kimlik Doğrulama',
    'Access Governance': 'Erişim Denetimi',
    'Required Fields *': 'Zorunlu Alanlar *',
    'Enabled Languages': 'Etkin Diller',
    'Venue Info & Bio Localizations': 'Mekân Bilgisi ve Tanıtım Çevirileri',
    'Public Address': 'Halka Açık Adres',
    'Public URL Slug': 'Halka Açık URL Bağlantısı',
    'Contact Phone': 'İletişim Telefonu',
    'Business Address': 'İşletme Adresi',
    'No Categories Yet': 'Henüz Kategori Yok',
    'No Custom Roles Configured': 'Yapılandırılmış Özel Rol Yok',
    'No Google reviews retrieved yet': 'Henüz Google yorumu alınmadı',
    'No Guests Found': 'Misafir Bulunamadı',
    'No Menu Items Yet': 'Henüz Menü Ürünü Yok',
    'No Promotions Configured': 'Yapılandırılmış Kampanya Yok',
    'No Service Periods Yet': 'Henüz Servis Dönemi Yok',
    'No Support Tickets': 'Destek Talebi Yok',
    'No Tables on Floor Plan': 'Kat Planında Masa Yok',
    'No Team Members Found': 'Ekip Üyesi Bulunamadı',
    'No Upcoming Events': 'Yaklaşan Etkinlik Yok',
    'No Walk-Ins Recorded': 'Rezervasyonsuz Müşteri Kaydı Yok',
    'No items are available right now': 'Şu anda mevcut ürün bulunmuyor',
    'No notifications found': 'Bildirim bulunamadı',
    'No reservations matching your filters': 'Filtrelerinize uygun rezervasyon bulunamadı',
    'No tables currently occupied': 'Şu anda dolu masa bulunmuyor',
    'No venue selected': 'Mekân seçilmedi',
    'Waitlist': 'Bekleme Listesi',
    'Waitlist is Empty': 'Bekleme Listesi Boş',
    'Waves (Gluten)': 'Başak (Glüten)',
    'We sent a temporary verification code to': 'Geçici bir doğrulama kodu gönderildi:',
    'Website URL': 'Web Sitesi Adresi',
    'What the customer receives': 'Müşterinin alacağı avantaj',
    'White-label control is available on eligible plans.': 'Beyaz etiket (white-label) yönetimi uygun planlarda mevcuttur.',
    'Why this works:': 'Bu stratejinin etkili olma nedeni:',
    'WiFi Broadcast': 'WiFi Yayını',
    'WiFi Password': 'WiFi Şifresi',
    'Window seat, dietary request, birthday celebration...': 'Cam kenarı masa, özel diyet isteği, doğum günü kutlaması...',
    'Your Published Response': 'Yayınlanan Yanıtınız',
    'Your Venues': 'Mekânlarınız',
    '🎉 Ready to publish!': '🎉 Yayınlanmaya hazır!',
    'e.g. Grill, Salad, Pastry': 'örn. Izgara, Salata, Pastane',
    'e.g. Guest called, emergency': 'örn. Misafir aradı, acil durum',
    'e.g. Italian, Moroccan, Burgers': 'örn. İtalyan, Fas Mutfağı, Burger',
    'e.g. Mains, Pasta, Starters': 'örn. Ana Yemekler, Makarna, Başlangıçlar',
    'e.g. Signature Truffle Pasta': 'örn. Özel Trüflü Makarna',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'örn. İkindi Kahvesi Fırsatı, Happy Hour %15',
    'e.g., Breakfast, Happy Hour': 'örn. Kahvaltı, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'örn. Her Cuma saat 20:00\'de canlı caz müzik dinletimize davetlisiniz!',
  },
  ko: {
    '123 Main St, City': '서울시 중앙로 123',
    'Balance': '포인트 잔액',
    'Lifetime': '누적 적립 포인트',
    'Source': '출처',
    'Menu': '메뉴',
    'Advanced': '고급 설정',
    'Core Suite': '핵심 기능',
    'Privacy': '개인정보',
    'Support': '고객 지원',
    'Save Layout': '테이블 배치도 저장',
    'Shape:': '테이블 형태:',
    'SEARCH LIBRARY...': '라이브러리 검색...',
    'Search library...': '라이브러리 검색...',
    'None': '없음',
    'Type': '유형',
    'Target': '대상',
    'Value / Target': '설정값 / 대상',
    'London (GMT/BST)': '런던 (GMT/BST)',
    'New York (EST/EDT)': '뉴욕 (EST/EDT)',
    'Chicago (CST/CDT)': '시카고 (CST/CDT)',
    'Denver (MST/MDT)': '덴버 (MST/MDT)',
    'Dubai (GST)': '두바이 (GST)',
    'Tokyo (JST)': '도쿄 (JST)',
    'Sydney (AEST/AEDT)': '시드니 (AEST/AEDT)',
    'Start Color': '시작 색상',
    'Depth Shade': '입체 그림자 깊이',
    'Matrix Geometry': 'QR 코드 도트 패턴 형태',
    'Eye Architecture': 'QR 모서리 인식점 디자인',
    'Brand Centerpiece': 'QR 중앙 브랜드 로고',
    'Scannability Shield': '스캔 인식률 보호 모드',
    'Environment Blur': '배경 흐림(블러) 효과',
    'Color Target:': '색상 출력 모드:',
    'DPI:': 'DPI (인쇄 해상도):',
    '3mm Bleed Box': '3mm 재단 여백 (블리드)',
    '300 DPI': '300 DPI (초고화질 인쇄)',
    'PRO+': 'PRO+ 플랜',
    'SAML 2.0': 'SAML 2.0 표준 프로토콜',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'IdP 엔터티 ID (Entity ID)',
    'SSO URL': 'SSO 로그인 URL',
    'X.509 Certificate': 'X.509 보안 인증서',
    'ACS URL': 'ACS URL (Assertion Consumer Service)',
    'Entity ID': '엔터티 ID',
    'Navigate': '이동',
    'Select': '선택',
    'OmniSearch v1.0': '통합 검색 OmniSearch',
    'Back to order review': '주문 검토로 돌아가기',
    'Return to Intelligence Studio': '인텔리전스 스튜디오로 돌아가기',
    'View Full Notification Center': '전체 알림 센터 열기',
    'Read all': '모두 읽음 처리',
    'Cancel department': '부서 취소',
    'Save department': '부서 저장',
    'Venue Segmentation': '매장 구역 분할',
    'Collaborate on your menu together.': '팀원들과 함께 메뉴를 공동 관리하세요.',
    'Timeline': '예약 타임라인',
    'Calendar': '예약 달력',
    'Book': '테이블 예약',
    'Duration': '이용 시간',
    'Time & Duration': '방문 시간 및 이용 시간',
    'Seating Preference': '선호 좌석 (창가/홀 등)',
    'Section': '구역',
    'Min Capacity': '최소 수용 인원',
    'Max Capacity': '최대 수용 인원',
    'Phone (Optional)': '전화번호 (선택)',
    'Name or Walk-in': '고객명 또는 현장 손님',
    'Next day': '다음 날',
    'Next month': '다음 달',
    'Live Connection': '실시간 연결',
    'Live Inventory': '실시간 재고 현황',
    'Main Venue (Full Menu)': '메인 매장 (전체 메뉴)',
    'Preview Menu': '메뉴 미리보기',
    'Manual Setup Guide': '수동 설정 단계별 가이드',
    'Connected & Active': '연결됨 및 정상 작동 중',
    'CTA Action': '주요 행동 유도 버튼 (CTA)',
    'Phone Contact': '대표 연락처',
    'Menu URL': '온라인 메뉴 주소 (URL)',
    'Google Location': 'Google 지도 위치',
    'Field': '필드',
    'Linked': '연동됨',
    'Synchronizing Hub...': '데이터 허브 동기화 중...',
    'Data Integrity': '데이터 무결성 검증',
    'Station Governance Suite': '조리 및 준비 스테이션 관리',
    'Open audit log': '감사 로그 열기',
    'Signal / Time': '이벤트 / 발생 시각',
    'Identity': '작업자 / 계정',
    'Invitation Invalid': '초대 링크가 유효하지 않거나 만료되었습니다',
    'Invited To': '초대받은 매장:',
    'Team Limit Reached': '팀원 등록 한도에 도달했습니다',
    'Venue Limit Reached': '등록 매장 수 한도에 도달했습니다',
    'Venue Setup Required': '매장 초기 설정이 필요합니다',
    'Create Venue': '새 매장 등록',
    'TableGreet Onboarding': 'TableGreet 온보딩 가이드',
    'No payment required': '결제 불필요 (완전 무료)',
    'Selected Plan': '선택한 요금제',
    'Payment Method': '결제 수단',
    'Invoice ID': '청구서(영수증) 번호',
    'Verified': '인증 완료',
    'Accent color': '강조 색상',
    'Set the voice and overall light level.': '매장 분위기와 전반적인 밝기(라이트/다크)를 설정하세요.',
    'Layout architecture': '레이아웃 구조 및 배열',
    'Navigation & motion': '내비게이션 및 화면 전환 모션',
    'Loading preview engine': '미리보기 엔진을 불러오는 중',
    'Real Live Preview': '실시간 라이브 미리보기',
    'Included with Pro & Max': 'Pro 및 Max 플랜 기본 포함',
    'Daypart Scheduling': '시간대별(조/중/석식) 메뉴 스케줄링',
    'Operational Daypart Architecture': '매장 운영 시간대 구조 체계',
    'Service Protocol': '서비스 운영 프로토콜',
    'Configuration Engine': '환경 설정 엔진',
    'Linked Department': '연동된 운영 부서',
    'Team Hub': '직원 팀 관리 센터',
    'Team Size': '팀 인원수',
    'Joined': '가입일',
    'Cancel Invitation': '초대 취소',
    'Initialize New Organization': '새 조직(기업) 생성',
    'Operational': '정상 운영 중',
    'Pro Feature': 'Pro 전용 기능',
    'Current State': '현재 상태',
    'Open on the big TV': '대형 TV 화면에서 열기',
    'Menu Board': '디지털 메뉴 보드',
    'Reward': '리워드',
    'Reward name': '리워드 명칭',
    'Cancel & refund': '주문 취소 및 환불',
    'Code': '코드',
    'Find': '찾기',
    'Complete': '완료됨',
    'Seated': '착석 중',
    'No-Show': '미방문 (노쇼)',
    'Walk-in': '워크인(현장 입장)',
    'Party': '방문 인원',
    'Turnover': '테이블 회전율',
    'We couldn\'t load the menu': '메뉴를 불러올 수 없습니다',
    'Venue Not Found': '매장을 찾을 수 없습니다',
    'Live Availability': '실시간 판매 가능 여부',
    'Multi-Language Suite': '다국어 번역 관리 스위트',
    'Key name': 'API 키 이름',
    'Revoke API key': 'API 키 폐기',
    'Delete webhook': '웹훅 삭제',
    'Identity Provider': '통합 인증 제공업체 (IdP)',
    'Provider': '제공업체',
    'Mandatory MFA': '2단계 인증 필수 적용 (MFA)',
    'Account Identity': '계정 정보 및 신원',
    'Login Email': '로그인 이메일',
    'New Login Email': '새 로그인 이메일',
    'Phone Login': '휴대폰 번호 로그인',
    'SMS Verification Code': 'SMS 인증 번호',
    'Integration Method': '연동 방식',
    'Stripe Connect': 'Stripe Connect 전자결제 연동',
    'Stripe Secret Key': 'Stripe 비밀 키 (Secret Key)',
    'Stripe Webhook Secret': 'Stripe 웹훅 비밀 키',
    'Client-Owned': '자체 계정 보유/운영',
    'Verify connection': '연결 상태 확인',
    'Setup Health': '설정 완성도 및 상태',
    'Privacy Protected': '개인정보 완벽 보호',
    'Privacy Policy': '개인정보 처리방침',
    'Continue': '계속하기',
    'Sign In': '로그인',
    'Didn\'t get it?': '코드를 받지 못하셨나요?',
    'Logged In Account': '현재 로그인된 계정',
    'Annual': '연간 결제 (할인 혜택)',
    'Unread': '읽지 않음',
    'Starred': '중요 표시',
    'High / Urgent': '높음 / 긴급',
    'Mark Read': '읽음 처리',
    'Delete notification': '알림 삭제',
    'Urgent': '긴급',
    'High': '높음',
    'Normal': '보통',
    'Low': '낮음',
    'Select a Ticket': '문의 티켓 선택',
    'Urgent (Immediate attention)': '긴급 (즉시 조치 필요)',
    'Type your reply here...': '답변을 입력하세요...',
    'Print Card (PDF)': '테이블 POP 카드 인쇄 (PDF)',
    'Advanced Printing Engine': '고화질 인쇄 렌더링 엔진',
    'Multi-Venue Governance': '다점포 및 프랜차이즈 통합 관리',
    'Network Revenue': '전체 매장 합산 매출',
    'Live Network Total': '실시간 매장 네트워크 총계',
    'Audit Log Hub': '감사 및 작업 로그 허브',
    'Export Log': '로그 내보내기',
    'CUSTOM ROLE SCHEMAS': '사용자 정의 역할 및 권한 체계',
    'Unique internal role identifier.': '역할의 내부 고유 식별 코드입니다.',
    'Human-friendly role label.': '직원들에게 표시될 친숙한 직책명입니다.',
    'Marketing Hub': '마케팅 관리 센터',
    'Revenue Generation Suite': '매출 증대 프로모션 스위트',
    'Active Campaign Limit Reached (3/3)': '활성 캠페인 수 한도 도달 (3/3)',
    'Banner Content': '배너 내용',
    'Style Profile': '스타일 프로필',
    'Action URL (Optional)': '이동 링크 (선택)',
    'Promotion Label': '프로모션 라벨',
    'Target Selection': '적용 대상 선택',
    'Edit Promotion': '프로모션 수정',
    'Delete Promotion': '프로모션 삭제',
    'Loyalty Engine': '고객 로열티 프로그램',
    'Reward catalog': '리워드 교환 목록',
    'Add reward': '리워드 추가',
    'Loyalty ID': '회원 번호 (ID)',
    'No point activity yet': '포인트 적립/사용 내역이 없습니다',
    'Scan or enter QR value': 'QR을 스캔하거나 번호를 입력하세요',
    'TableGreet Admin': 'TableGreet 매장 관리 시스템',
    'Capacity Pacing': '예약 유입 속도 조절',
    'Cancellation Policy': '예약 취소 정책',
    'Booking Experience': '테이블 예약 경험',
    'Floor Occupancy': '홀 좌석 점유율',
    'Currently Seated': '현재 착석 중',
    'Seat Party': '손님 좌석 배정',
    'Seat Table': '테이블 배정',
    'Select Table': '테이블 선택',
    'Table / Capacity': '테이블 / 수용 인원',
    'Table Name/Number': '테이블 번호/이름',
    'Mark Arrived': '도착 완료 체크',
    'Mark No-Show': '노쇼(미방문) 기록',
    'Complete Dining': '식사 및 정산 완료',
    'Standard Dining (Default)': '일반 홀 식사 (기본)',
    'Walk-In Dining Log': '워크인(현장 방문) 대장',
    'Notification Center': '알림 센터',
    'Single Sign-On': '통합 로그인 (SSO)',
    'Welcome Aboard!': 'TableGreet에 오신 것을 환영합니다!',
    'Select your TableGreet plan': '매장에 적합한 TableGreet 요금제를 선택하세요',
    'Free Plan Activation': '무료 플랜 즉시 시작',
    'Venue Created!': '매장이 성공적으로 등록되었습니다!',
    'Upgrade for Unlimited': '무제한 플랜으로 업그레이드',
    'Auto-Tag Menu': '메뉴 자동 태그 지정',
    'Start Auto-Tagging': '자동 태깅 시작',
    'Launch Auto-Tagger': '자동 태거 실행',
    'Launch PDF Importer': 'PDF 메뉴 가져오기 실행',
    'Analyzing Menu...': '메뉴 분석 중...',
    'Analyzing Recipe...': '조리법 및 재료 분석 중...',
    'Manual Availability': '수동 품절/판매 설정',
    'Technical Breakdown': '세부 사양 분석',
    'Dietary Taxonomy': '식단 선호 및 알레르기 구분',
    'Dietary & Allergen AI': 'AI 알레르기 및 맞춤 식단 분석',
    'Inherit from Category': '상위 카테고리 설정 따름',
    'Ingredient Library': '식재료 라이브러리',
    'Ingredient Name': '식재료명',
    'Menu Organization': '메뉴 구조화 및 정렬',
    'Inventory Taxonomy Hub': '카테고리 및 분류 관리 센터',
    'Category Intelligence': '카테고리 성과 인사이트',
    'Service Period': '운영 시간대 (조/중/석식)',
    'Manual Configuration / All Day': '수동 설정 / 종일 제공',
    'Operational Department': '소속 운영 부서',
    'Global / No Department': '공통 / 지정 부서 없음',
    'Cancel category': '카테고리 취소',
    'New category name...': '새 카테고리 이름...',
    'Save category': '카테고리 저장',
    'QR Matrix Only': '순수 QR 코드만',
    'Vector & PDF': '벡터 및 인쇄용 PDF',
    'Vector QR (SVG)': '벡터 QR 코드 (SVG)',
    'Vector QR (PDF)': '인쇄용 QR 코드 (PDF)',
    'Save QR Style': 'QR 코드 스타일 저장',
    'Share Link': '링크 공유',
    'High Fidelity 300DPI': '초고화질 300 DPI 인쇄 해상도',
    'Generating 300 DPI PDF...': '300 DPI 인쇄용 PDF 생성 중...',
    'Give your venue a point of view.': '매장만의 독창적인 분위기와 시각적 브랜딩을 완성하세요.',
    'Signage Menu Not Found': '디지털 사이니지 메뉴를 찾을 수 없습니다',
    'Scan Menu': '메뉴 스캔하기',
    'Open on your phone': '스마트폰으로 열기',
    'Full Screen': '전체 화면',
    'Lock TV board': 'TV 화면 잠금',
    'Big-Screen Menu': '대형 화면용 메뉴판',
    'TV Signage': 'TV 디지털 사이니지',
    'General Information': '기본 정보',
    'Venue Name *': '매장명 *',
    'Public Slug *': '공개 웹 링크 식별자 *',
    'Venue Logo': '매장 로고',
    'Hero Cover Image': '메인 커버 이미지',
    'Venue Timezone': '매장 시간대',
    'Primary Brand Color': '브랜드 대표 색상',
    'TableGreet footer credit': '하단 TableGreet 표기',
    'White-Label Branding': '화이트라벨 맞춤 브랜딩',
    'Client DNS Setup': '고객 도메인 DNS 설정',
    'Copy CNAME target': 'CNAME 대상 복사',
    'Copy TableGreet URL': 'TableGreet 링크 복사',
    'Included TableGreet URL': '기본 제공 TableGreet 링크',
    'Open TableGreet URL': 'TableGreet 링크 열기',
    'Public menu': '고객용 공개 메뉴',
    'Public venue delivery': '온라인 메뉴 배달 주문',
    'Zone name': '배달 구역 이름',
    'Coverage note': '배달 가능 지역 안내',
    'Default ETA min': '최소 예상 소요 시간 (분)',
    'Default ETA max': '최대 예상 소요 시간 (분)',
    'Packaging fee': '포장비',
    'Courier handled by': '배달 수행 주체',
    'Third-party courier': '외부 제휴 배달 대행',
    'Unified Experience': '통합 게스트 경험',
    'Save WiFi to Venue': '매장 와이파이 정보 저장',
    'Network Name (SSID)': '와이파이 네트워크 이름 (SSID)',
    'Audio Alert Chime': '주문 및 호출 알림음',
    'Time Format': '시간 표시 형식',
    'Auto / Default': '자동 / 기본값',
    'Syncing SEO & Google Data...': 'SEO 및 Google 데이터 동기화 중...',
    'Google Connection': 'Google 비즈니스 연동',
    'Search Engine Metadata': '검색 엔진 메타데이터',
    'Social card preview': '소셜 미디어 공유 미리보기',
    'Allow Indexing': '검색 노출 허용 (인덱싱)',
    'Canonical URL Override': '대표 URL 직접 지정 (Canonical)',
    'Price Range': '가격대',
    'Latitude': '위도',
    'Longitude': '경도',
    'Google Place ID / Location ID': 'Google 장소 식별자 (Place ID)',
    'Google Search Preview': 'Google 검색 결과 미리보기',
    'Linked Google Location': '연동된 Google 매장 위치',
    'SEO Documentation': 'SEO 검색 최적화 가이드',
    ', and': ' 및 ',
    'and': '및',
    'Delete Department?': '부서를 삭제하시겠습니까?',
    'Delete Department': '부서 삭제',
    'Live URL': '실시간 링크',
    'Copy full live URL': '전체 실시간 링크 복사',
    'Open live department menu': '부서 실시간 메뉴 열기',
    'Loading departments...': '부서 목록을 불러오는 중...',
    'Multi-department venue segmentations are premium features available on PRO and above.': '다중 부서 구역 분할은 PRO 이상 플랜에서 제공되는 프리미엄 기능입니다.',
    'Segment your menu by rooms, tables, bars, or physical locations.': '홀, 테이블, 바 또는 물리적 위치별로 메뉴를 분할 관리하세요.',
    'Targeted Ordering': '타깃 주문 라우팅',
    'Route guests and orders to specific preparation areas.': '고객과 주문을 지정된 조리 및 준비 구역으로 자동 전달합니다.',
    'Operational Departments': '매장 운영 부서',
    'OPERATIONAL DEPARTMENTS': '매장 운영 부서',
    'Venue Segmentation Hub': '매장 구역 분할 관리 허브',
    'VENUE SEGMENTATION HUB': '매장 구역 분할 관리 허브',
    'Create departments to group your menu sections. For example, a large venue might have a': '메뉴 영역을 구분할 운영 부서를 생성하세요. 예를 들어 대형 매장의 경우',
    'Restaurant': '레스토랑',
    'Poolside': '풀사이드 (수영장)',
    'department, each with its own categories.': '등의 부서를 두고 각각 독립된 카테고리를 운영할 수 있습니다.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': '부서 기능을 통해 매장을 레스토랑, 바, 풀사이드 등의 구역으로 나누고 각각 독립된 카테고리를 구성할 수 있습니다.',
    'Department name': '부서 이름',
    'New department name...': '새 부서 이름 입력...',
    'No Linked Daypart (Always Available)': '연결된 영업 시간대 없음 (항상 이용 가능)',
    'Linked Daypart': '연결된 운영 시간대',
    'Localizations': '다국어 설정',
    'LOCALIZATIONS': '다국어 설정',
    'AI Auto-Translate': 'AI 자동 번역',
    'AI AUTO-TRANSLATE': 'AI 자동 번역',
    'Translated name': '번역된 이름',
    'No Departments Yet': '등록된 부서가 없습니다',
    'Add First Department': '첫 번째 부서 추가',
    'Avg Order Value': '평균 주문 금액',
    'AVG ORDER VALUE': '평균 주문 금액',
    'item taps': '회 메뉴 탭',
    'ITEM TAPS': '회 메뉴 탭',
    'Avg Rating': '평균 평점',
    'AVG RATING': '평균 평점',
    'Revenue (Last 7 Days)': '매출 (최근 7일)',
    'REVENUE (LAST 7 DAYS)': '매출 (최근 7일)',
    'Daily breakdown': '일별 상세',
    'DAILY BREAKDOWN': '일별 상세',
    'Menu Views (Last 7 Days)': '메뉴 조회수 (최근 7일)',
    'MENU VIEWS (LAST 7 DAYS)': '메뉴 조회수 (최근 7일)',
    'Guest traffic': '방문객 유입 추이',
    'GUEST TRAFFIC': '방문객 유입 추이',
    'Most Viewed Items': '가장 많이 본 메뉴',
    'MOST VIEWED ITEMS': '가장 많이 본 메뉴',
    'Top Rated Items': '최고 평점 메뉴',
    'TOP RATED ITEMS': '최고 평점 메뉴',
    'Waiter Requests': '직원 호출 요청',
    'WAITER REQUESTS': '직원 호출 요청',
    'Total': '합계',
    'TOTAL': '합계',
    'Order Status (30 days)': '주문 처리 상태 (30일)',
    'ORDER STATUS (30 DAYS)': '주문 처리 상태 (30일)',
    'Recent Guest Feedback': '최근 고객 피드백',
    'RECENT GUEST FEEDBACK': '최근 고객 피드백',
    'Conversion Rate': '주문 전환율',
    'CONVERSION RATE': '주문 전환율',
    'of menu visitors placed an order': '의 메뉴 방문자가 실제로 주문했습니다',
    'Refreshed': '업데이트됨',
    'REFRESHED': '업데이트됨',
    'Last 30 days': '최근 30일',
    'LAST 30 DAYS': '최근 30일',
    'cash': '현금',
    'Cash': '현금',
    'CASH': '현금',
    'bill': '계산서',
    'Bill': '계산서',
    'BILL': '계산서',
    'water': '물',
    'Water': '물',
    'WATER': '물',
    'service': '서비스',
    'Service': '서비스',
    'SERVICE': '서비스',
    'No interaction data yet': '수집된 상호작용 데이터가 없습니다',
    'No ratings collected yet': '수집된 평점이 없습니다',
    'No requests in period': '해당 기간 내 요청이 없습니다',
    'No feedback collected yet': '접수된 고객 피드백이 없습니다',
    'No menu views recorded yet': '메뉴 조회 기록이 아직 없습니다',
    'Dish Rating Breakdown': '메뉴별 평점 상세 분석',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': '메뉴별 만족도 점수와 순위 지표는 MAX 및 Enterprise 플랜 전용 기능입니다.',
    'Customer Sentiment & Reviews': '고객 만족도 및 리뷰 피드',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': '고객의 생생한 리뷰 피드와 감성 분석 기능은 MAX 또는 Enterprise 구독이 필요합니다.',
    'Reviews AI': '리뷰 분석 AI',
    'Guest Feedback': '고객 피드백',
    'Upgrade to unlock rating analytics →': '플랜을 업그레이드하여 평점 분석 잠금 해제 →',
    'Upgrade to Max': 'Max 플랜으로 업그레이드',
    'UPGRADE TO MAX': 'MAX 플랜으로 업그레이드',
    'MAX PLAN': 'MAX 플랜',
    'No Data Yet': '아직 데이터가 없습니다',
    'Share your QR code to start collecting real venue insights.': 'QR 코드를 비치하여 매장의 실제 운영 분석 데이터를 수집해 보세요.',
    'Revenue Analytics': '매출 분석',
    'REVENUE ANALYTICS': '매출 분석',
    'Track your earnings, orders, and average ticket values over time.': '시간 경과에 따른 매출, 주문 수, 평균 주문 단가의 변화를 추적하세요.',
    'Operational Velocity': '매장 운영 속도',
    'OPERATIONAL VELOCITY': '매장 운영 속도',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': '방문객 유입량, 인기 메뉴 순위 및 홀 직원 호출 빈도를 한눈에 파악하세요.',
    'Upgrade to Pro': 'Pro로 업그레이드',
    'UPGRADE TO PRO': 'PRO 플랜으로 업그레이드',
    'Loading Insights…': '분석 데이터 불러오는 중…',
    'Loading Insights...': '분석 데이터 불러오는 중...',
    'Telemetry Unavailable': '매장 활동 데이터를 불러올 수 없습니다',
    'TELEMETRY UNAVAILABLE': '매장 활동 데이터를 불러올 수 없습니다',
    'No Data Captured': '수집된 데이터가 없습니다',
    'NO DATA CAPTURED': '수집된 데이터가 없습니다',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'QR 코드를 비치하여 실시간 방문자 및 주문 분석 데이터를 수집해 보세요.',
    'INTELLIGENCE STUDIO': '데이터 분석실',
    'Insights Engine Locked': '고급 분석 엔진 잠김',
    'INSIGHTS ENGINE LOCKED': '고급 분석 엔진 잠김',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': '심층 트렌드 분석, 대량 관리 기능 및 데이터 내보내기는 다음 플랜 전용입니다:',
    'Unlock Pro Features': 'Pro 기능 잠금 해제',
    'UNLOCK PRO FEATURES': 'PRO 기능 잠금 해제',
    'MENU VIEWS': '메뉴 조회수',
    'ITEM INTERACTIONS': '메뉴 상호작용 수',
    'GUEST RATING': '고객 평점',
    'DELIVERY REVENUE': '배달 주문 매출',
    'AVERAGE DELIVERY ORDER': '평균 배달 주문액',
    'AVERAGE DELIVERY FEE': '평균 배달비',
    'INCLUDING FEES': '수수료 포함',
    'ZONE AVERAGE': '배달 권역 평균',
    'TRAFFIC PROJECTIONS': '방문객 유입 예측',
    'TELEMETRY SIGNAL TRACKING': '실시간 활동 신호 추적',
    'VEGETARIAN': '채식 (베지테리언)',
    'VEGAN': '완전 채식 (비건)',
    'SPICY': '매운맛',
    'GLUTEN-FREE': '글루텐 프리',
    'Reset: All In-Stock': '초기화: 전 메뉴 재고 있음',
    'Emergency Stop (All Out)': '긴급 품절: 전 메뉴 마감',
    'No compliance issues': '이상 항목이 없습니다',
    'NO COMPLIANCE ISSUES': '이상 항목이 없습니다',
    'detected in current menu': '현재 메뉴에서',
    'DETECTED IN CURRENT MENU': '현재 메뉴에서',
    'Telemetry History (24h)': '활동 내역 (24시간)',
    'TELEMETRY HISTORY (24H)': '활동 내역 (24시간)',
    'Telemetry History (30d)': '활동 내역 (30일)',
    'TELEMETRY HISTORY (30D)': '활동 내역 (30일)',
    'Save': '저장',
    'Saved': '저장됨',
    'Saving...': '저장 중...',
    'Save Changes': '변경사항 저장',
    'Save Settings': '설정 저장',
    'Cancel': '취소',
    'Close': '닫기',
    'Create': '생성',
    'Add': '추가',
    'Edit': '편집',
    'Delete': '삭제',
    'Remove': '제거',
    'Update': '업데이트',
    'Refresh': '새로고침',
    'Search': '검색',
    'Search...': '검색...',
    'Search items...': '메뉴 검색...',
    'Search categories...': '카테고리 검색...',
    'Filter': '필터',
    'All': '전체',
    'Export': '내보내기',
    'Import': '가져오기',
    'Download': '다운로드',
    'Upload': '업로드',
    'Copy': '복사',
    'Copied': '복사됨',
    'Copied!': '복사 완료!',
    'Preview': '미리보기',
    'Open': '영업 중',
    'View': '보기',
    'Manage': '관리',
    'Configure': '구성',
    'Enable': '활성화',
    'Disable': '비활성화',
    'Enabled': '활성화됨',
    'Disabled': '비활성화됨',
    'Active': '활성',
    'Inactive': '비활성',
    'Available': '주문 가능',
    'Unavailable': '품절',
    'Visible': '표시됨',
    'Hidden': '숨김',
    'Required': '필수',
    'Optional': '선택사항',
    'Confirm': '확인',
    'Back': '뒤로',
    'Next': '다음',
    'Done': '완료',
    'Upgrade': '업그레이드',
    'Sign Out': '로그아웃',
    'Exit to Main Admin': '기본 관리로 돌아가기',
    'Change Language': '언어 변경',
    'Add Category': '카테고리 추가',
    'Add Menu Item': '메뉴 항목 추가',
    'Add Item': '항목 추가',
    'Add Ingredient': '재료 추가',
    'Add Department': '부서 추가',
    'Delete Item': '항목 삭제',
    'Delete Category': '카테고리 삭제',
    'Upload Image': '이미지 업로드',
    'Upload Photo': '사진 업로드',
    'Select Category': '카테고리 선택',
    'Select Department': '부서 선택',
    'Add translation...': '번역 추가...',
    'Clear': '지우기',
    'Reset': '재설정',
    'Apply': '적용',
    'Submit': '제출',
    'Core & Operations': '운영 및 서비스',
    'Menu & Experience': '메뉴 및 주문',
    'Enterprise & Security': '매장 관리 및 보안',
    'System & Control': '설정 및 지원',
    'Intelligence Studio': '데이터 분석실',
    'Venue Insights': '매출 및 분석',
    'Reservations': '예약 목록',
    'Floor Plan': '좌석 배치도',
    'Live Waitlist': '대기 명단',
    'Walk-Ins': '워크인 손님',
    'Walk-ins': '워크인 손님',
    'Departments': '부서 및 구역',
    'Service Hours': '서비스 시간',
    'Categories': '카테고리',
    'Menu Categories': '메뉴 카테고리',
    'Menu Items': '메뉴 목록',
    'Items': '항목',
    'Ingredients': '재료 관리',
    'Loyalty Program': '고객 리워드',
    'Campaign Studio': '프로모션 및 할인',
    'Signage': '디지털 메뉴판',
    'QR Code': '테이블 QR 코드',
    'Kitchen Ops': '주방 화면',
    'Theme Engine': '메뉴 디자인 및 색상',
    'Governance': '운영 관리',
    'Audit Log': '활동 내역',
    'API & Webhooks': '외부 연동',
    'SSO & Security': '계정 보안 및 로그인',
    'Team': '직원 관리',
    'Billing': '구독 및 결제',
    'SEO & Google': 'Google 검색 노출',
    'Notifications': '알림',
    'Support Center': '고객 지원',
    'Settings': '매장 설정',
    'Name': '이름',
    'Description': '설명',
    'Price': '가격',
    'Status': '상태',
    'Actions': '작업',
    'Role': '역할',
    'Email': '이메일',
    'Phone': '전화번호',
    'Address': '주소',
    'City': '도시',
    'Currency': '통화',
    'Language': '언어',
    'Theme': '테마',
    'General': '일반',
    'Total Revenue': '총 매출',
    'Total Orders': '총 주문 수',
    'Average Order': '평균 주문 금액',
    'Active Sessions': '이용 중인 테이블',
    'Recent Orders': '최근 주문',
    'Live Orders': '실시간 주문',
    'Table': '테이블',
    'Date': '날짜',
    'Time': '시간',
    'Amount': '금액',
    'Customer': '손님',
    'In Stock': '재고 있음',
    'Out of Stock': '품절',
    'Sold Out': '매진',
    'Dine In': '매장 식사',
    'Pickup': '포장',
    'Delivery': '배달',
    'Paid': '결제 완료',
    'Unpaid': '미결제',
    'Pending': '대기 중',
    'Preparing': '조리 중',
    'Ready': '준비 완료',
    'Completed': '완료됨',
    'Cancelled': '취소됨',
    'Loading...': '불러오는 중...',
    'No data': '데이터 없음',
    'No results found': '검색 결과가 없습니다',
    'No items found': '항목을 찾을 수 없습니다',
    'No translations defined yet': '아직 등록된 번역이 없습니다',
    'Are you sure?': '정말 진행하시겠습니까?',
    'This action cannot be undone.': '이 작업은 취소할 수 없습니다.',
    'Settings saved successfully!': '설정이 성공적으로 저장되었습니다!',
    'Administrator': '관리자',
    'Manager': '매니저',
    'Owner': '대표자',
    'Chef': '주방장',
    'Waiter': '직원',
    'Cashier': '계산원',
    'Type a command or search...': '명령어를 입력하거나 검색하세요...',
    'Command Palette': '빠른 실행',
    'Demand Velocity': '수요 반응 속도',
    'DEMAND VELOCITY': '수요 반응 속도',
    'Top Performing Entities': '최고 인기 메뉴',
    'TOP PERFORMING ENTITIES': '최고 인기 메뉴',
    'Hourly Load Analysis': '시간대별 방문 분석',
    'HOURLY LOAD ANALYSIS': '시간대별 방문 분석',
    'Bulk Command Center': '일괄 관리 센터',
    'BULK COMMAND CENTER': '일괄 관리 센터',
    'Governance Scan': '메뉴 점검 검사',
    'GOVERNANCE SCAN': '메뉴 점검 검사',
    'No compliance issues detected in current menu': '현재 메뉴에서 발견된 문제가 없습니다',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': '현재 메뉴에서 발견된 문제가 없습니다',
    'Procedural Tagging': '자동 태그 적용',
    'PROCEDURAL TAGGING': '자동 태그 적용',
    'Name match...': '이름 검색...',
    '...Name match': '이름 검색...',
    'Healthy': '건강식',
    'HEALTHY': '건강식',
    'Apply Tags': '태그 적용하기',
    'APPLY TAGS': '태그 적용하기',
    'Global Price Shift': '전체 메뉴 가격 일괄 변경',
    'GLOBAL PRICE SHIFT': '전체 메뉴 가격 일괄 변경',
    'Adjust': '변경 적용',
    'ADJUST': '변경 적용',
    'Availability Override': '품절 및 판매 재개 일괄 제어',
    'AVAILABILITY OVERRIDE': '품절 및 판매 재개 일괄 제어',
    'Reset: ALL IN-STOCK': '초기화: 전 메뉴 판매 재개',
    'RESET: ALL IN-STOCK': '초기화: 전 메뉴 판매 재개',
    'EMERGENCY STOP (ALL OUT)': '긴급 정지 (전 메뉴 품절 처리)',
    'Integrity Engine': '메뉴 품질 보증 엔진',
    'INTEGRITY ENGINE': '메뉴 품질 보증 엔진',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': '자동 관리 시스템이 매장 전체의 메뉴 노출 여부, 가격 오류, 설명 누락 등을 실시간으로 모니터링합니다.',
    'Inventory Audit Export': '메뉴 및 재고 점검 파일 다운로드',
    'INVENTORY AUDIT EXPORT': '메뉴 및 재고 점검 파일 다운로드',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': '오프라인 검토 및 감사를 위해 메뉴 판매 실적과 상세 정보를 담은 종합 파일을 생성합니다.',
    'Enterprise Data Export': '상세 데이터 내보내기',
    'ENTERPRISE DATA EXPORT': '상세 데이터 내보내기',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': '방문객 유입, 메뉴별 주문 속도, 주문 전환율을 포함한 종합 성과 감사 보고서를 표준 규격으로 내려받을 수 있습니다.',
    'JSON Snapshot': 'JSON 파일 다운로드',
    'JSON SNAPSHOT': 'JSON 파일 다운로드',
    'Compile CSV Report': 'CSV 보고서 생성',
    'COMPILE CSV REPORT': 'CSV 보고서 생성',
    'Date Segment': '날짜 구간',
    'DATE SEGMENT': '날짜 구간',
    'Inbound Views': '방문 조회수',
    'INBOUND VIEWS': '방문 조회수',
    'Asset Interactions': '메뉴 상호작용',
    'ASSET INTERACTIONS': '메뉴 상호작용',
    'Telemetry History': '활동 기록 내역',
    'TELEMETRY HISTORY': '활동 기록 내역',
    'Telemetry History (7d)': '활동 기록 내역 (7일)',
    'TELEMETRY HISTORY (7D)': '활동 기록 내역 (7일)',
    'Growth Trajectory': '성장 추세',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': '최근 7일간 메뉴 확인 및 반응 빈도가 지속적으로 상승하고 있습니다.',
    'Deep Analysis Engine': '심층 분석 엔진',
    'DEEP ANALYSIS ENGINE': '심층 분석 엔진',
    'Asset Velocity': '메뉴 인기도 순위',
    'ASSET VELOCITY': '메뉴 인기도 순위',
    'Traffic Projections': '방문객 추이 예측',
    'Telemetry Signal Tracking': '실시간 활동 신호 추적',
    'Service Hour Update': '서비스 시간 변경',
    'Late Night Lounge Hours': '심야 라운지 운영 시간',
    'Bar & Lounge Hours': '바 및 라운지 운영 시간',
    'Breakfast & Brunch Hours': '조식 및 브런치 운영 시간',
    'Switch Location': '매장 전환',
    'SWITCH LOCATION': '매장 전환',
    'Global Admin': '총괄 관리자',
    'GLOBAL ADMIN': '총괄 관리자',
    'Staff Access': '직원 전용',
    'STAFF ACCESS': '직원 전용',
    'Venue Overview': '매장 현황',
    'VENUE OVERVIEW': '매장 현황',
    'Unified Telemetry Engine': '실시간 운영 모니터링',
    'UNIFIED TELEMETRY ENGINE': '실시간 운영 모니터링',
    'Pulse': '실시간 펄스',
    'PULSE': '실시간 펄스',
    'Insights': '운영 인사이트',
    'INSIGHTS': '운영 인사이트',
    'Reports': '보고서',
    'REPORTS': '보고서',
    'GOVERNANCE': '운영 관리',
    'Multi-Venue': '다중 매장 관리',
    'MULTI-VENUE': '다중 매장 관리',
    'Menu Views': '메뉴 조회수',
    'Item Interactions': '메뉴 클릭 및 반응',
    'Guest Rating': '고객 평점',
    'Revenue (7d)': '매출 (7일)',
    'Revenue (24h)': '매출 (24시간)',
    'Revenue (30d)': '매출 (30일)',
    'Delivery orders (7d)': '배달 주문 (7일)',
    'Delivery orders (24h)': '배달 주문 (24시간)',
    'Delivery orders (30d)': '배달 주문 (30일)',
    'Delivery revenue': '배달 매출',
    'Average delivery order': '평균 배달 주문액',
    'Average delivery fee': '평균 배달비',
    'Zone average': '지역 평균',
    'Including fees': '수수료 포함',
    'No deliveries': '배달 내역 없음',
    'No orders': '주문 내역 없음',
    'No reviews': '리뷰 없음',
    'Live': '실시간',
    'LIVE': '실시간',
    'reviews': '개의 리뷰',
    'orders': '건의 주문',
    'deliveries': '건의 배달',
    'of orders': '주문 비율',
    'rate': '비율',
    'Recent Activity': '최근 활동 내역',
    'Real-time Venue Telemetry': '실시간 매장 운영 현황',
    'REAL-TIME VENUE TELEMETRY': '실시간 매장 운영 현황',
    'Rapid Pulse View': '실시간 빠른 보기',
    'Anonymous Guest': '익명 손님',
    'scanned menu': '메뉴를 스캔했습니다',
    'viewed item': '메뉴를 확인했습니다',
    'interacted with': '반응을 보였습니다:',
    'placed order': '주문을 접수했습니다',
    'searched menu': '메뉴에서 검색했습니다',
    'Main Landing': '메인 화면',
    'Trace': '추적됨',
    'TRACE': '추적됨',
    'Gaining Telemetry...': '실시간 활동 수신 중...',
    'No recent traffic detected': '최근 활동이 없습니다',
    'Activities will appear here as guests scan your QR code': '손님이 QR 코드를 스캔하면 여기에 활동 내역이 표시됩니다',
    'Analyze All Logs': '전체 활동 기록 분석',
    'Just now': '방금 전',
    'less than a minute ago': '1분 미만 전',
    '1 minute ago': '1분 전',
    '1 hour ago': '1시간 전',
    'about 1 hour ago': '약 1시간 전',
    '1 day ago': '1일 전',
    'DAY AGO 1': '1일 전',
    '2 days ago': '2일 전',
    'DAY AGO 2': '2일 전',
    '3 days ago': '3일 전',
    'DAY AGO 3': '3일 전',
    '4 days ago': '4일 전',
    'DAY AGO 4': '4일 전',
    '5 days ago': '5일 전',
    'DAY AGO 5': '5일 전',
    '6 days ago': '6일 전',
    'DAY AGO 6': '6일 전',
    '7 days ago': '7일 전',
    'DAY AGO 7': '7일 전',
    'about 1 month ago': '약 1달 전',
    'Monday': '월요일',
    'Tuesday': '화요일',
    'Wednesday': '수요일',
    'Thursday': '목요일',
    'Friday': '금요일',
    'Saturday': '토요일',
    'Sunday': '일요일',
    'Mon': '월',
    'Tue': '화',
    'Wed': '수',
    'Thu': '목',
    'Fri': '금',
    'Sat': '토',
    'Sun': '일',
    'Opening Hours': '영업 시간',
    'Start Time': '시작 시간',
    'End Time': '종료 시간',
    'Opening Time': '오픈 시간',
    'Closing Time': '마감 시간',
    'All Day': '하루 종일',
    'Closed': '영업 종료',
    'Active Days': '영업 요일',
    'Active Schedule': '활성 일정',
    'Always Available': '상시 주문 가능',
    'Always Visible': '항상 표시됨',
    'New Category': '새 카테고리',
    'Category': '카테고리',
    'Category Name': '카테고리 이름',
    'Category Description': '카테고리 설명',
    'Display Order': '표시 순서',
    'Item Name': '메뉴 이름',
    'Item Description': '메뉴 설명',
    'Base Price': '기본 가격',
    'Preparation Time': '조리 시간',
    'Calories': '칼로리',
    'Featured Item': '대표 메뉴',
    'Chef\'s Recommendation': '셰프 추천',
    'Spicy': '매움',
    'Vegetarian': '채식',
    'Vegan': '비건',
    'Gluten-Free': '글루텐 프리',
    'Halal': '할랄',
    'Kosher': '코셔',
    'Contains Nuts': '견과류 함유',
    'Dairy-Free': '유제품 미함유',
    'Allergen Exclusions': '알레르기 유발 물질',
    'Table Number': '테이블 번호',
    'Assigned Table': '배정된 테이블',
    'Seats': '좌석 수',
    'Capacity': '수용 인원',
    'Indoor': '실내',
    'Outdoor': '야외',
    'Terrace': '테라스',
    'Bar': '바 / 카운터',
    'Add Table': '테이블 추가',
    'Edit Table': '테이블 편집',
    'Delete Table': '테이블 삭제',
    'Bookings': '예약',
    'Add to Waitlist': '대기 명단에 추가',
    'Guest Name': '고객명',
    'Party Size': '인원 수',
    'Arrival Time': '도착 시간',
    'Special Requests': '요청 사항',
    'Confirmed': '예약 확정',
    'No Show': '노쇼 (미방문)',
    'Add Staff Member': '직원 추가',
    'Invite Staff': '직원 초대',
    'Email Address': '이메일 주소',
    'Phone Number': '전화번호',
    'Download QR Code': 'QR 코드 다운로드',
    'Print Table Stands': '테이블 스탠드 인쇄',
    'Today': '오늘',
    'Yesterday': '어제',
    'This Week': '이번 주',
    'This Month': '이번 달',
    'Last 30 Days': '지난 30일',
    'Access Denied': '접근 권한이 없습니다',
    'Advanced Settings': '고급 설정',
    'Change Image': '이미지 변경',
    'Remove Image': '이미지 삭제',
    'Announcement Banner': '공지 배너',
    'Please fill in all required fields.': '모든 필수 항목을 입력해 주세요.',
    'Changes saved successfully': '변경사항이 성공적으로 저장되었습니다',
    '(You)': '(본인)',
    'AI Auto-Tag': 'AI 자동 태깅',
    'Analyzing:': '분석 중:',
    'Apple (Fruit/Vegan)': '사과 (과일/비건)',
    'Cancel editing': '편집 취소',
    'Capacity:': '수용 인원:',
    'Carrot (Veggie)': '당근 (야채)',
    'CircleDot (Organic)': '원형 점 (유기농)',
    'Coffee (Caffeine)': '커피 (카페인)',
    'Configuration': '구성',
    'Copy verification record name': '인증 레코드 이름 복사',
    'Copy verification record value': '인증 레코드 값 복사',
    'Critical exception intercepted': '심각한 시스템 오류 발생',
    'Kernel Panic': '시스템 오류',
    'DNA (Organic/Bio)': 'DNA (유기농/바이오)',
    'Egg (Ovo/Allergen)': '계란 (알레르기 유발)',
    'Empty': '비어 있음',
    'Engraving...': '생성 중...',
    'Flame (Spicy)': '불꽃 (매운맛)',
    'Free': '무료',
    'From': '시작',
    'Generating': '생성 중',
    'Governance & Traceability Studio': '매장 관리 및 활동 허브',
    'Greet': '환영',
    'Heart (Healthy)': '하트 (건강식)',
    'In-App': '인앱',
    'Infinity SVG': '벡터 SVG (무제한)',
    'Leaf (Default)': '나뭇잎 (기본값)',
    'MAX Feature': '엔터프라이즈 기능',
    'Milk (Dairy)': '우유 (유제품)',
    'Network': '네트워크',
    'Notify SMS': 'SMS 알림',
    'Order': '주문',
    'Original': '원본',
    'Overview': '개요',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4가지 기본 역할',
    'Shield (Safety)': '방패 (안전)',
    'Soup (Warm/Soup)': '수프 (따뜻한 음식)',
    'Standard Pro View': '기본 보기',
    'Star': '별',
    'Synchronized with Venue Identity': '매장 브랜드 스타일에 맞춤 적용',
    'Synchronizing Security Context...': '보안 연결 수립 중...',
    'Targeted Timing': '제공 시간대',
    'Targeting': '적용 대상',
    'To': '종료',
    'Toggle Navigation': '내비게이션 전환',
    'Venue': '매장',
    'Venue Governance Suite': '매장 운영 및 설정 허브',
    'Volume:': '음량:',
    'WHITEBOARD NOTE...': '직원 메모 또는 안내...',
    'Warning (Alert)': '경고 (알림)',
    'Wheat (Flour/Gluten)': '밀 (밀가루/글루텐)',
    'Wine (Alcohol)': '와인 (알코올)',
    'YouTube/Vimeo/Direct Link': '동영상 링크 (YouTube, Vimeo 또는 MP4)',
    'Your name': '이름',
    '2. Table Number': '2. 테이블 번호',
    'for Morocco or': '모로코의 경우 또는',
    'in': '내',
    'to': '~',
    'e.g. 12 or Patio-1': '예: 12 또는 테라스-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': '예: 수제 페투치니, 블랙 윈터 트러플, 36개월 숙성 파르미지아노...',
    'e.g. Head Sommelier & Wine Lead': '예: 수석 소믈리에',
    'e.g. London': '예: 런던',
    'e.g. SOMMELIER, HOST, BARISTA': '예: 소믈리에, 호스트, 바리스타',
    'e.g. Skyline Lounge': '예: 스카이라인 라운지',
    'e.g. TRUFFLE OIL': '예: 트러플 오일',
    'e.g. The Grand Cafe': '예: 그랜드 카페',
    'e.g. Window preference, high chair': '예: 창가 자리 선호, 아기 의자',
    'Copied to clipboard!': '클립보드에 복사되었습니다!',
    'Copy failed.': '복사하지 못했습니다.',
    'Link copied!': '링크가 복사되었습니다!',
    'Logo uploaded': '로고 업로드 완료',
    'Cover image uploaded': '커버 이미지 업로드 완료',
    'Profile saved and synced successfully': '프로필이 성공적으로 저장 및 동기화되었습니다',
    'Category Created': '카테고리 생성 완료',
    'Category Updated': '카테고리 수정 완료',
    'Category Deleted': '카테고리 삭제 완료',
    'Item Created': '메뉴 항목 생성 완료',
    'Item Updated': '메뉴 항목 수정 완료',
    'Item Deleted': '메뉴 항목 삭제 완료',
    'Menu Published': '메뉴 공개 완료',
    'Delete Category?': '카테고리를 삭제하시겠습니까?',
    'Delete Menu Item?': '이 메뉴 항목을 삭제하시겠습니까?',
    'Delete Ingredient?': '이 재료를 삭제하시겠습니까?',
    'Delete Service Period?': '제공 시간대를 삭제하시겠습니까?',
    'Remove team member?': '이 팀원을 제외하시겠습니까?',
    'Cancel invitation?': '초대를 취소하시겠습니까?',
    'Revoke API key?': 'API 키를 해지하시겠습니까?',
    'Delete webhook endpoint?': '웹훅 엔드포인트를 삭제하시겠습니까?',
    'Clear all notifications?': '모든 알림을 삭제하시겠습니까?',
    'Disconnect Stripe account?': 'Stripe 계정 연결을 해제하시겠습니까?',
    'Disconnect Google Business Profile?': 'Google 비즈니스 프로필 연결을 해제하시겠습니까?',
    'Campaigns published successfully!': '프로모션이 성공적으로 게시되었습니다!',
    'Changes successfully synchronized with Google Business Profile!': 'Google 비즈니스 프로필과 성공적으로 동기화되었습니다!',
    'Google Business Profile successfully connected!': 'Google 비즈니스 프로필이 성공적으로 연결되었습니다!',
    'Google Business Profile disconnected.': 'Google 비즈니스 프로필 연결이 해제되었습니다.',
    'Google Post draft saved successfully!': 'Google 게시물 임시 저장이 완료되었습니다!',
    'Post draft deleted.': '게시물 임시 저장이 삭제되었습니다.',
    'Review response published successfully!': '고객 리뷰에 대한 답글이 등록되었습니다!',
    'SEO configuration saved successfully!': 'SEO 설정이 성공적으로 저장되었습니다!',
    'SEO metadata translated across all languages!': '모든 지원 언어로 SEO 메타데이터 번역 완료!',
    'Venue profile & bio translated across all languages!': '매장 프로필 및 소개글이 모든 언어로 번역되었습니다!',
    'Reservation configuration saved successfully': '예약 설정이 성공적으로 저장되었습니다',
    'Stripe settings saved successfully.': 'Stripe 설정이 저장되었습니다.',
    'Print-Ready PDF downloaded successfully': '인쇄용 고해상도 PDF 다운로드 완료',
    'Vector QR PDF downloaded successfully': '벡터 QR PDF 다운로드 완료',
    'Enterprise 300 DPI Pre-Press Package generated successfully': '300 DPI 인쇄용 패키지가 성공적으로 생성되었습니다',
    'At least one language must be enabled.': '최소 하나의 언어를 활성화해야 합니다.',
    'Enter a non-zero percentage before adjusting prices.': '가격을 조정하기 전에 0이 아닌 비율을 입력해 주세요.',
    'Please enter a Venue Name before translating.': '번역을 시작하기 전에 매장 이름을 입력해 주세요.',
    'Please enter an item name before running dietary and allergen analysis.': '알레르기 및 식단 분석을 실행하기 전에 메뉴 이름을 입력해 주세요.',
    'Please write a reply before submitting.': '제출하기 전에 답글을 작성해 주세요.',
    'Table 3 Called Waiter': '3번 테이블에서 직원을 호출했습니다',
    'Table Call Bell': '테이블 호출 벨',
    'Waiter Calls': '직원 호출 내역',
    'Waiter Dispatch & Calls': '홀 직원 호출 및 배정',
    'Kitchen Display & Whiteboard': '주방 디스플레이 및 메모 보드',
    'Customer Orders': '고객 주문 내역',
    'Bakery': '베이커리 / 제과점',
    'Bar / Pub': '바 / 펍',
    'Café / Coffee Shop': '카페 / 커피숍',
    'Fast Food': '패스트푸드',
    'Gourmet / Fine Dining': '파인 다이닝 / 고급 레스토랑',
    'Hotel': '호텔',
    'General Local Business': '일반 로컬 매장',
    'Classic': '클래식',
    'Clean': '깔끔한',
    'Modern': '모던',
    'Elegant': '우아한',
    'Energetic': '활기찬',
    'Glass': '글래스모피즘',
    'Playful & Casual': '캐주얼 & 친근한',
    'Sophisticated & Gourmet': '세련된 미식',
    'Warm & Welcoming': '따뜻하고 아늑한',
    'Executive': '이그제큐티브',
    'Upgrade required: Multi-language menus are available on the Pro plan.': '플랜 업그레이드 필요: 다국어 메뉴는 Pro 플랜에서 지원됩니다.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': '플랜 업그레이드 필요: 주문 및 직원 호출 기능은 Starter 플랜에서 지원되지 않습니다.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': '벡터 SVG 내보내기는 Pro 및 Max 플랜 기능입니다. 고해상도 벡터 파일을 위해 플랜을 업그레이드하세요.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': '인쇄용 PDF 내보내기는 Pro 및 Max 플랜 기능입니다. 고품질 PDF를 위해 플랜을 업그레이드하세요.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Pro 플랜은 활성 프로모션이 최대 3개로 제한됩니다. 다른 캠페인을 일시 중지하거나 MAX로 업그레이드하세요.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Starter 플랜은 최대 20개 테이블(테이블 1–20)을 지원합니다. 무제한 테이블을 위해 PRO로 업그레이드하세요.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Starter 플랜은 숫자 번호 테이블(1–20)을 지원합니다. 맞춤 테이블 이름(예: 테라스, VIP)은 PRO가 필요합니다.',
    'Add Menu Categories & Items': '메뉴 카테고리 및 항목 추가',
    'Add Menu Items': '메뉴 항목 추가',
    'Organize your menu sections and food items.': '메뉴 분류와 요리 항목을 체계적으로 구성하세요.',
    'Add and manage your food & beverage offerings.': '제공하는 음식과 음료를 등록하고 관리하세요.',
    'Customize Venue Branding': '매장 브랜드 맞춤 설정',
    'Set your brand colors, logo, and presentation.': '브랜드 색상, 로고, 메뉴 프레젠테이션을 설정하세요.',
    'Get Your QR Code & Go Live': 'QR 코드를 발급받고 바로 시작하세요',
    'Download table QR codes and preview your live menu.': '테이블별 QR 코드를 다운로드하고 실제 모바일 메뉴를 미리 확인하세요.',
    'Account & Venue Created Successfully!': '계정 및 매장이 성공적으로 생성되었습니다!',
    '⚡ Off-Peak Hours': '⚡ 한산한 시간대',
    '🍽️ Lunch Crowd': '🍽️ 점심시간 직장인',
    '🚀 Weekend Rush': '🚀 주말 골든 타임',
    '🌟 Seasonal Special': '🌟 시즌 한정 스페셜',
    '📣 Engagement Buzz': '📣 입소문 및 고객 참여',
    'Festive & Energetic': '축제 분위기 & 활기찬',
    'Urgent & High-Conversion': '타임 세일 & 높은 전환율',
    'Generated 3 tailored campaign strategies': '3가지 맞춤형 프로모션 전략이 생성되었습니다',
    'Could not generate campaign ideas. Please try again.': '프로모션 아이디어를 생성하지 못했습니다. 다시 시도해 주세요.',
    'Venue Themes': '매장 디자인 테마',
    'Choose the character of your public menu': '고객에게 공개되는 디지털 메뉴의 비주얼 스타일을 선택하세요',
    'Glassmorphism & Depth': '글래스모피즘 및 입체감',
    'Independent XML Sitemap': '독립형 XML 사이트맵',
    'Structured Schema & Sync': '구조화된 데이터 및 검색 동기화',
    'Improve how your venue appears on Google and in local search results.': 'Google 및 로컬 검색 결과에서 매장의 검색 노출을 극대화하세요.',
    'Terms of Service': '이용약관',
    'Confirm your email': '이메일을 인증해 주세요',
    'Check your spam / junk folder': '스팸 메일함을 확인해 주세요',
    'Commercial pre-press print production package': '상업용 인쇄소 납품용 고품질 패키지',
    'Over-bleed safety margin': '재단 여백 (블리드 안전 마진)',
    'Crop Marks': '재단선 (크롭 마크)',
    'Guillotine cutting guides': '재단기 가이드라인',
    'Global Banners': '상단 공지 배너',
    'Featured Items': '추천 시그니처 메뉴',
    'AI Assistant': 'AI 어시스턴트',
    'Custom Role Schemas': '맞춤형 직책 및 권한 스키마',
    'Notification Preferences': '알림 수신 설정',
    'Preferences saved automatically': '설정이 자동으로 저장되었습니다',
    'Menu Extractor (PDF & Images)': '메뉴 스마트 추출기 (PDF 및 이미지)',
    'Multi-Language Translation': '다국어 자동 번역',
    'Start Menu Translation': '메뉴 번역 시작',
    'Menu Items Hub': '메뉴 항목 관리 허브',
    'Open Menu Items': '메뉴 항목 열기',
    'Enterprise Traceability': '운영 이력 및 활동 감사',
    'Full History': '전체 변경 내역',
    'Billing & Plans': '결제 및 구독 플랜',
    'Subscription Governance': '구독 상태 관리',
    'Platform Usage': '플랫폼 사용량 현황',
    'Menu Page Views': '디지털 메뉴 조회수',
    'Enterprise Identity': '엔터프라이즈 계정 및 인증',
    'Access Governance': '접근 제어 및 보안 정책',
    'Required Fields *': '필수 입력란 *',
    'Enabled Languages': '활성화된 언어 목록',
    'Venue Info & Bio Localizations': '매장 안내 및 소개글 다국어 설정',
    'Public Address': '공개 매장 주소',
    'Public URL Slug': '공개 웹주소 슬러그',
    'Contact Phone': '대표 전화번호',
    'Business Address': '사업장 주소',
    'No Categories Yet': '등록된 카테고리가 없습니다',
    'No Custom Roles Configured': '설정된 맞춤 직책이 없습니다',
    'No Google reviews retrieved yet': '아직 연동된 Google 리뷰가 없습니다',
    'No Guests Found': '고객 정보가 없습니다',
    'No Menu Items Yet': '등록된 메뉴 항목이 없습니다',
    'No Promotions Configured': '설정된 프로모션이 없습니다',
    'No Service Periods Yet': '설정된 영업 시간대가 없습니다',
    'No Support Tickets': '문의 내역이 없습니다',
    'No Tables on Floor Plan': '배치도에 등록된 테이블이 없습니다',
    'No Team Members Found': '등록된 팀원이 없습니다',
    'No Upcoming Events': '예정된 특별 이벤트가 없습니다',
    'No Walk-Ins Recorded': '등록된 워크인 손님이 없습니다',
    'No items are available right now': '현재 이용 가능한 메뉴 항목이 없습니다',
    'No notifications found': '도착한 알림이 없습니다',
    'No reservations matching your filters': '필터 조건과 일치하는 예약이 없습니다',
    'No tables currently occupied': '현재 식사 중인 테이블이 없습니다',
    'No venue selected': '선택된 매장이 없습니다',
    'Waitlist': '대기자 명단',
    'Waitlist is Empty': '현재 대기 중인 고객이 없습니다',
    'Waves (Gluten)': '이삭 (글루텐 함유)',
    'We sent a temporary verification code to': '다음 연락처로 인증 코드를 전송했습니다:',
    'Website URL': '웹사이트 주소',
    'What the customer receives': '고객에게 제공되는 혜택',
    'White-label control is available on eligible plans.': '화이트 라벨 (자체 브랜드 맞춤 적용)은 해당 플랜에서 지원됩니다.',
    'Why this works:': '이 전략이 효과적인 이유:',
    'WiFi Broadcast': '고객용 Wi-Fi 안내',
    'WiFi Password': 'Wi-Fi 비밀번호',
    'Window seat, dietary request, birthday celebration...': '창가 자리 선호, 식이요법 요청, 생일 축하 등...',
    'Your Published Response': '등록된 매장 측 답변',
    'Your Venues': '내 매장 목록',
    '🎉 Ready to publish!': '🎉 게시 준비가 완료되었습니다!',
    'e.g. Grill, Salad, Pastry': '예: 그릴 요리, 샐러드, 디저트',
    'e.g. Guest called, emergency': '예: 고객 유선 연락, 긴급 사정',
    'e.g. Italian, Moroccan, Burgers': '예: 이탈리안, 모로칸, 수제버거',
    'e.g. Mains, Pasta, Starters': '예: 메인 요리, 파스타, 에피타이저',
    'e.g. Signature Truffle Pasta': '예: 시그니처 트러플 파스타',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': '예: 애프터눈 커피 스페셜, 해피아워 15% 할인',
    'e.g., Breakfast, Happy Hour': '예: 조식 뷔페, 해피아워',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': '예: 매주 금요일 저녁 8시, 감미로운 라이브 재즈 공연과 함께하세요!',
  },
  el: {
    '123 Main St, City': 'Κεντρική Οδός 123, Πόλη',
    'Balance': 'Υπόλοιπο πόντων',
    'Lifetime': 'Συνολικά κερδισμένοι',
    'Source': 'Πηγή',
    'Menu': 'Μενού',
    'Advanced': 'Για προχωρημένους',
    'Core Suite': 'Βασικές λειτουργίες',
    'Privacy': 'Απόρρητο',
    'Support': 'Υποστήριξη',
    'Save Layout': 'Αποθήκευση διάταξης αίθουσας',
    'Shape:': 'Σχήμα:',
    'SEARCH LIBRARY...': 'ΑΝΑΖΗΤΗΣΗ ΣΤΗ ΒΙΒΛΙΟΘΗΚΗ...',
    'Search library...': 'Αναζήτηση στη βιβλιοθήκη...',
    'None': 'Κανένα',
    'Type': 'Τύπος',
    'Target': 'Στόχος',
    'Value / Target': 'Τιμή / Στόχος',
    'London (GMT/BST)': 'Λονδίνο (GMT/BST)',
    'New York (EST/EDT)': 'Νέα Υόρκη (EST/EDT)',
    'Chicago (CST/CDT)': 'Σικάγο (CST/CDT)',
    'Denver (MST/MDT)': 'Ντένβερ (MST/MDT)',
    'Dubai (GST)': 'Ντουμπάι (GST)',
    'Tokyo (JST)': 'Τόκιο (JST)',
    'Sydney (AEST/AEDT)': 'Σίδνεϊ (AEST/AEDT)',
    'Start Color': 'Χρώμα εκκίνησης',
    'Depth Shade': 'Βάθος σκίασης',
    'Matrix Geometry': 'Γεωμετρία μοτίβου QR',
    'Eye Architecture': 'Γωνίες στόχευσης QR',
    'Brand Centerpiece': 'Κεντρικό λογότυπο στο QR',
    'Scannability Shield': 'Προστασία αναγνωσιμότητας σάρωσης',
    'Environment Blur': 'Θόλωμα περιβάλλοντος',
    'Color Target:': 'Προφίλ χρώματος:',
    'DPI:': 'DPI (Ανάλυση):',
    '3mm Bleed Box': 'Περιθώριο ξακρίσματος 3mm',
    '300 DPI': '300 DPI (Υψηλή ανάλυση)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Πρωτόκολλο SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'Entity ID του IdP',
    'SSO URL': 'Διεύθυνση URL σύνδεσης SSO',
    'X.509 Certificate': 'Πιστοποιητικό ασφαλείας X.509',
    'ACS URL': 'Διεύθυνση URL της υπηρεσίας ACS',
    'Entity ID': 'Αναγνωριστικό οντότητας',
    'Navigate': 'Πλοήγηση',
    'Select': 'Επιλογή',
    'OmniSearch v1.0': 'Καθολική αναζήτηση OmniSearch',
    'Back to order review': 'Επιστροφή στον έλεγχο παραγγελίας',
    'Return to Intelligence Studio': 'Επιστροφή στο στούντιο αναλύσεων',
    'View Full Notification Center': 'Προβολή πλήρους κέντρου ειδοποιήσεων',
    'Read all': 'Ανάγνωση όλων',
    'Cancel department': 'Ακύρωση τμήματος',
    'Save department': 'Αποθήκευση τμήματος',
    'Venue Segmentation': 'Διαχωρισμός χώρων',
    'Collaborate on your menu together.': 'Συνεργαστείτε στο μενού σας μαζί.',
    'Timeline': 'Χρονολόγιο',
    'Calendar': 'Ημερολόγιο κρατήσεων',
    'Book': 'Κράτηση',
    'Duration': 'Διάρκεια παραμονής',
    'Time & Duration': 'Ώρα και διάρκεια',
    'Seating Preference': 'Προτίμηση θέσης',
    'Section': 'Τομέας',
    'Min Capacity': 'Ελάχιστη χωρητικότητα',
    'Max Capacity': 'Μέγιστη χωρητικότητα',
    'Phone (Optional)': 'Τηλέφωνο (Προαιρετικό)',
    'Name or Walk-in': 'Όνομα ή απευθείας προσέλευση',
    'Next day': 'Επόμενη ημέρα',
    'Next month': 'Επόμενος μήνας',
    'Live Connection': 'Ζωντανή σύνδεση',
    'Live Inventory': 'Ζωντανό απόθεμα',
    'Main Venue (Full Menu)': 'Κύριο κατάστημα (Πλήρες μενού)',
    'Preview Menu': 'Προεπισκόπηση μενού',
    'Manual Setup Guide': 'Οδηγός χειροκίνητης ρύθμισης',
    'Connected & Active': 'Συνδεδεμένο & ενεργό',
    'CTA Action': 'Κουμπί προτροπής ενέργειας (CTA)',
    'Phone Contact': 'Τηλέφωνο επικοινωνίας',
    'Menu URL': 'Διεύθυνση διαδικτυακού μενού',
    'Google Location': 'Τοποθεσία Google Maps',
    'Field': 'Πεδίο',
    'Linked': 'Συνδεδεμένο',
    'Synchronizing Hub...': 'Συγχρονισμός δεδομένων...',
    'Data Integrity': 'Ακεραιότητα δεδομένων',
    'Station Governance Suite': 'Διαχείριση σταθμών εργασίας',
    'Open audit log': 'Άνοιγμα αρχείου καταγραφής',
    'Signal / Time': 'Συμβάν / Ώρα',
    'Identity': 'Χρήστης / Ταυτότητα',
    'Invitation Invalid': 'Η πρόσκληση δεν είναι έγκυρη ή έχει λήξει',
    'Invited To': 'Προσκεκλημένος σε',
    'Team Limit Reached': 'Το όριο μελών της ομάδας εξαντλήθηκε',
    'Venue Limit Reached': 'Το όριο καταστημάτων εξαντλήθηκε',
    'Venue Setup Required': 'Απαιτείται αρχική ρύθμιση καταστήματος',
    'Create Venue': 'Δημιουργία καταστήματος',
    'TableGreet Onboarding': 'Οδηγός έναρξης TableGreet',
    'No payment required': 'Δεν απαιτείται πληρωμή',
    'Selected Plan': 'Επιλεγμένο πρόγραμμα',
    'Payment Method': 'Τρόπος πληρωμής',
    'Invoice ID': 'Αριθμός τιμολογίου',
    'Verified': 'Επαληθεύτηκε',
    'Accent color': 'Χρώμα έμφασης',
    'Set the voice and overall light level.': 'Ορίστε το στυλ και το συνολικό επίπεδο φωτεινότητας.',
    'Layout architecture': 'Αρχιτεκτονική διάταξης',
    'Navigation & motion': 'Πλοήγηση & κινούμενα σχέδια',
    'Loading preview engine': 'Φόρτωση μηχανής προεπισκόπησης',
    'Real Live Preview': 'Πραγματική ζωντανή προεπισκόπηση',
    'Included with Pro & Max': 'Περιλαμβάνεται στα Pro & Max',
    'Daypart Scheduling': 'Προγραμματισμός χρονικών ζωνών ημέρας',
    'Operational Daypart Architecture': 'Αρχιτεκτονική επιχειρησιακών ζωνών',
    'Service Protocol': 'Πρωτόκολλο εξυπηρέτησης',
    'Configuration Engine': 'Μηχανή ρυθμίσεων',
    'Linked Department': 'Συνδεδεμένο τμήμα',
    'Team Hub': 'Κέντρο ομάδας',
    'Team Size': 'Μέγεθος ομάδας',
    'Joined': 'Εντάχθηκε στις',
    'Cancel Invitation': 'Ακύρωση πρόσκλησης',
    'Initialize New Organization': 'Δημιουργία νέου οργανισμού',
    'Operational': 'Σε λειτουργία',
    'Pro Feature': 'Λειτουργία Pro',
    'Current State': 'Τρέχουσα κατάσταση',
    'Open on the big TV': 'Άνοιγμα στη μεγάλη οθόνη τηλεόρασης',
    'Menu Board': 'Ψηφιακός πίνακας μενού',
    'Reward': 'Ανταμοιβή',
    'Reward name': 'Όνομα ανταμοιβής',
    'Cancel & refund': 'Ακύρωση & επιστροφή χρημάτων',
    'Code': 'Κωδικός',
    'Find': 'Εύρεση',
    'Complete': 'Ολοκληρώθηκε',
    'Seated': 'Στο τραπέζι',
    'No-Show': 'Δεν προσήλθε',
    'Walk-in': 'Χωρίς κράτηση',
    'Party': 'Άτομα',
    'Turnover': 'Εναλλαγή τραπεζιών',
    'We couldn\'t load the menu': 'Δεν ήταν δυνατή η φόρτωση του μενού',
    'Venue Not Found': 'Το κατάστημα δεν βρέθηκε',
    'Live Availability': 'Ζωντανή διαθεσιμότητα',
    'Multi-Language Suite': 'Πολυγλωσσική διαχείριση',
    'Key name': 'Όνομα κλειδιού',
    'Revoke API key': 'Ανάκληση κλειδιού API',
    'Delete webhook': 'Διαγραφή webhook',
    'Identity Provider': 'Πάροχος ταυτότητας (IdP)',
    'Provider': 'Πάροχος',
    'Mandatory MFA': 'Υποχρεωτικός έλεγχος ταυτότητας πολλαπλών παραγόντων (MFA)',
    'Account Identity': 'Ταυτότητα λογαριασμού',
    'Login Email': 'Email σύνδεσης',
    'New Login Email': 'Νέο email σύνδεσης',
    'Phone Login': 'Σύνδεση με τηλέφωνο',
    'SMS Verification Code': 'Κωδικός επαλήθευσης SMS',
    'Integration Method': 'Μέθοδος διασύνδεσης',
    'Stripe Connect': 'Πληρωμές Stripe Connect',
    'Stripe Secret Key': 'Μυστικό κλειδί Stripe',
    'Stripe Webhook Secret': 'Μυστικό webhook Stripe',
    'Client-Owned': 'Υπό την κατοχή του πελάτη',
    'Verify connection': 'Επαλήθευση σύνδεσης',
    'Setup Health': 'Κατάσταση ρύθμισης',
    'Privacy Protected': 'Προστασία απορρήτου',
    'Privacy Policy': 'Πολιτική απορρήτου',
    'Continue': 'Συνέχεια',
    'Sign In': 'Είσοδος',
    'Didn\'t get it?': 'Δεν το λάβατε;',
    'Logged In Account': 'Συνδεδεμένος λογαριασμός',
    'Annual': 'Ετήσιο',
    'Unread': 'Μη αναγνωσμένα',
    'Starred': 'Με αστέρι',
    'High / Urgent': 'Υψηλή / Επείγουσα',
    'Mark Read': 'Σήμανση ως αναγνωσμένο',
    'Delete notification': 'Διαγραφή ειδοποίησης',
    'Urgent': 'Επείγον',
    'High': 'Υψηλή',
    'Normal': 'Κανονική',
    'Low': 'Χαμηλή',
    'Select a Ticket': 'Επιλέξτε ένα αίτημα',
    'Urgent (Immediate attention)': 'Επείγον (Άμεση προσοχή)',
    'Type your reply here...': 'Πληκτρολογήστε την απάντησή σας εδώ...',
    'Print Card (PDF)': 'Εκτύπωση κάρτας τραπεζιού (PDF)',
    'Advanced Printing Engine': 'Προηγμένη μηχανή εκτύπωσης',
    'Multi-Venue Governance': 'Διαχείριση πολλαπλών καταστημάτων',
    'Network Revenue': 'Συνολικά έσοδα δικτύου',
    'Live Network Total': 'Ζωντανό σύνολο δικτύου',
    'Audit Log Hub': 'Κέντρο καταγραφής ελέγχου',
    'Export Log': 'Εξαγωγή αρχείου καταγραφής',
    'CUSTOM ROLE SCHEMAS': 'ΠΡΟΣΑΡΜΟΣΜΕΝΑ ΣΧΗΜΑΤΑ ΡΟΛΩΝ',
    'Unique internal role identifier.': 'Μοναδικό εσωτερικό αναγνωριστικό ρόλου.',
    'Human-friendly role label.': 'Ευνόητος τίτλος ρόλου για την ομάδα.',
    'Marketing Hub': 'Κέντρο μάρκετινγκ',
    'Revenue Generation Suite': 'Εργαλεία αύξησης εσόδων',
    'Active Campaign Limit Reached (3/3)': 'Όριο ενεργών εκστρατειών επιτεύχθηκε (3/3)',
    'Banner Content': 'Περιεχόμενο διαφημιστικού πλαισίου',
    'Style Profile': 'Οπτικό στυλ',
    'Action URL (Optional)': 'Σύνδεσμος ενέργειας (Προαιρετικό)',
    'Promotion Label': 'Ετικέτα προσφοράς',
    'Target Selection': 'Επιλογή στόχου',
    'Edit Promotion': 'Επεξεργασία προσφοράς',
    'Delete Promotion': 'Διαγραφή προσφοράς',
    'Loyalty Engine': 'Πρόγραμμα επιβράβευσης πελατών',
    'Reward catalog': 'Κατάλογος ανταμοιβών',
    'Add reward': 'Προσθήκη ανταμοιβής',
    'Loyalty ID': 'Αριθμός μέλους',
    'No point activity yet': 'Δεν υπάρχει ακόμη κίνηση πόντων',
    'Scan or enter QR value': 'Σαρώστε ή πληκτρολογήστε τον κωδικό QR',
    'TableGreet Admin': 'Διαχείριση TableGreet',
    'Capacity Pacing': 'Ρύθμιση ρυθμού κρατήσεων',
    'Cancellation Policy': 'Πολιτική ακύρωσης',
    'Booking Experience': 'Εμπειρία κράτησης τραπεζιών',
    'Floor Occupancy': 'Πληρότητα αίθουσας',
    'Currently Seated': 'Καθισμένοι τώρα',
    'Seat Party': 'Τοποθέτηση πελατών',
    'Seat Table': 'Ανάθεση τραπεζιού',
    'Select Table': 'Επιλογή τραπεζιού',
    'Table / Capacity': 'Τραπέζι / Χωρητικότητα',
    'Table Name/Number': 'Όνομα ή αριθμός τραπεζιού',
    'Mark Arrived': 'Σήμανση ως αφιχθείς',
    'Mark No-Show': 'Σήμανση ως μη προσέλθων',
    'Complete Dining': 'Ολοκλήρωση γεύματος',
    'Standard Dining (Default)': 'Κανονική εξυπηρέτηση (Προεπιλογή)',
    'Walk-In Dining Log': 'Καταγραφή πελατών χωρίς κράτηση',
    'Notification Center': 'Κέντρο ειδοποιήσεων',
    'Single Sign-On': 'Ενιαία είσοδος (SSO)',
    'Welcome Aboard!': 'Καλώς ήρθατε στο TableGreet!',
    'Select your TableGreet plan': 'Επιλέξτε το πρόγραμμα TableGreet που σας ταιριάζει',
    'Free Plan Activation': 'Ενεργοποίηση δωρεάν προγράμματος',
    'Venue Created!': 'Το κατάστημα δημιουργήθηκε με επιτυχία!',
    'Upgrade for Unlimited': 'Αναβάθμιση για απεριόριστη χρήση',
    'Auto-Tag Menu': 'Αυτόματη προσθήκη ετικετών μενού',
    'Start Auto-Tagging': 'Έναρξη αυτόματης προσθήκης ετικετών',
    'Launch Auto-Tagger': 'Εκκίνηση αυτόματου βοηθού ετικετών',
    'Launch PDF Importer': 'Εισαγωγή μενού από PDF',
    'Analyzing Menu...': 'Ανάλυση μενού...',
    'Analyzing Recipe...': 'Ανάλυση συνταγής...',
    'Manual Availability': 'Χειροκίνητη διαθεσιμότητα',
    'Technical Breakdown': 'Τεχνική ανάλυση',
    'Dietary Taxonomy': 'Διατροφικές κατηγορίες & αλλεργιογόνα',
    'Dietary & Allergen AI': 'Τεχνητή νοημοσύνη διατροφής & αλλεργιογόνων',
    'Inherit from Category': 'Κληρονόμηση από την κατηγορία',
    'Ingredient Library': 'Βιβλιοθήκη συστατικών',
    'Ingredient Name': 'Όνομα συστατικού',
    'Menu Organization': 'Οργάνωση μενού',
    'Inventory Taxonomy Hub': 'Κέντρο κατηγοριοποίησης αποθέματος',
    'Category Intelligence': 'Έξυπνη ανάλυση κατηγορίας',
    'Service Period': 'Περίοδος εξυπηρέτησης',
    'Manual Configuration / All Day': 'Χειροκίνητη ρύθμιση / Όλη μέρα',
    'Operational Department': 'Επιχειρησιακό τμήμα',
    'Global / No Department': 'Γενικό / Χωρίς τμήμα',
    'Cancel category': 'Ακύρωση κατηγορίας',
    'New category name...': 'Όνομα νέας κατηγορίας...',
    'Save category': 'Αποθήκευση κατηγορίας',
    'QR Matrix Only': 'Μόνο κωδικός QR',
    'Vector & PDF': 'Διανυσματικά & PDF',
    'Vector QR (SVG)': 'Διανυσματικός κωδικός QR (SVG)',
    'Vector QR (PDF)': 'Εκτυπώσιμος κωδικός QR (PDF)',
    'Save QR Style': 'Αποθήκευση στυλ QR',
    'Share Link': 'Κοινοποίηση συνδέσμου',
    'High Fidelity 300DPI': 'Υψηλή ανάλυση εκτύπωσης 300 DPI',
    'Generating 300 DPI PDF...': 'Δημιουργία PDF 300 DPI...',
    'Give your venue a point of view.': 'Δώστε στο κατάστημά σας μια μοναδική οπτική ταυτότητα.',
    'Signage Menu Not Found': 'Το μενού ψηφιακής οθόνης δεν βρέθηκε',
    'Scan Menu': 'Σάρωση μενού',
    'Open on your phone': 'Άνοιγμα στο κινητό σας',
    'Full Screen': 'Πλήρης οθόνη',
    'Lock TV board': 'Κλείδωμα οθόνης τηλεόρασης',
    'Big-Screen Menu': 'Μενού για μεγάλη οθόνη',
    'TV Signage': 'Ψηφιακή σήμανση τηλεόρασης',
    'General Information': 'Γενικές πληροφορίες',
    'Venue Name *': 'Όνομα καταστήματος *',
    'Public Slug *': 'Δημόσιος σύνδεσμος *',
    'Venue Logo': 'Λογότυπο καταστήματος',
    'Hero Cover Image': 'Κύρια εικόνα εξωφύλλου',
    'Venue Timezone': 'Ζώνη ώρας καταστήματος',
    'Primary Brand Color': 'Κύριο χρώμα επωνυμίας',
    'TableGreet footer credit': 'Αναφορά TableGreet στο υποσέλιδο',
    'White-Label Branding': 'Πλήρης εξατομίκευση επωνυμίας',
    'Client DNS Setup': 'Ρύθμιση DNS τομέα',
    'Copy CNAME target': 'Αντιγραφή προορισμού CNAME',
    'Copy TableGreet URL': 'Αντιγραφή συνδέσμου TableGreet',
    'Included TableGreet URL': 'Συμπεριλαμβανόμενος σύνδεσμος TableGreet',
    'Open TableGreet URL': 'Άνοιγμα συνδέσμου TableGreet',
    'Public menu': 'Δημόσιο μενού πελατών',
    'Public venue delivery': 'Διανομή από το διαδικτυακό μενού',
    'Zone name': 'Όνομα ζώνης',
    'Coverage note': 'Σημείωση κάλυψης',
    'Default ETA min': 'Ελάχιστος εκτιμώμενος χρόνος (λεπτά)',
    'Default ETA max': 'Μέγιστος εκτιμώμενος χρόνος (λεπτά)',
    'Packaging fee': 'Κόστος συσκευασίας',
    'Courier handled by': 'Διαχείριση διανομής από',
    'Third-party courier': 'Εξωτερική υπηρεσία ταχυμεταφορών',
    'Unified Experience': 'Ενοποιημένη εμπειρία',
    'Save WiFi to Venue': 'Αποθήκευση Wi-Fi στο κατάστημα',
    'Network Name (SSID)': 'Όνομα δικτύου (SSID)',
    'Audio Alert Chime': 'Ηχητικό σήμα ειδοποίησης',
    'Time Format': 'Μορφή ώρας',
    'Auto / Default': 'Αυτόματο / Προεπιλογή',
    'Syncing SEO & Google Data...': 'Συγχρονισμός δεδομένων SEO & Google...',
    'Google Connection': 'Σύνδεση με την Google',
    'Search Engine Metadata': 'Μεταδεδομένα μηχανών αναζήτησης',
    'Social card preview': 'Προεπισκόπηση κοινοποίησης',
    'Allow Indexing': 'Να επιτρέπεται η ευρετηρίαση',
    'Canonical URL Override': 'Παράκαμψη κανονικού συνδέσμου',
    'Price Range': 'Εύρος τιμών',
    'Latitude': 'Γεωγραφικό πλάτος',
    'Longitude': 'Γεωγραφικό μήκος',
    'Google Place ID / Location ID': 'Αναγνωριστικό τοποθεσίας Google',
    'Google Search Preview': 'Προεπισκόπηση αναζήτησης Google',
    'Linked Google Location': 'Συνδεδεμένη τοποθεσία Google',
    'SEO Documentation': 'Οδηγός βελτιστοποίησης SEO',
    ', and': ', και',
    'and': 'και',
    'Delete Department?': 'Διαγραφή τμήματος;',
    'Delete Department': 'Διαγραφή τμήματος',
    'Live URL': 'Ζωντανός σύνδεσμος',
    'Copy full live URL': 'Αντιγραφή πλήρους συνδέσμου',
    'Open live department menu': 'Άνοιγμα ζωντανού μενού τμήματος',
    'Loading departments...': 'Φόρτωση τμημάτων...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Ο διαχωρισμός σε πολλαπλά τμήματα είναι προηγμένη λειτουργία διαθέσιμη στο πακέτο PRO και άνω.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Διαχωρίστε το μενού σας κατά αίθουσες, τραπέζια, μπαρ ή φυσικούς χώρους.',
    'Targeted Ordering': 'Στοχευμένες παραγγελίες',
    'Route guests and orders to specific preparation areas.': 'Δρομολογήστε επισκέπτες και παραγγελίες σε συγκεκριμένους χώρους προετοιμασίας.',
    'Operational Departments': 'Επιχειρησιακά τμήματα',
    'OPERATIONAL DEPARTMENTS': 'ΕΠΙΧΕΙΡΗΣΙΑΚΑ ΤΜΗΜΑΤΑ',
    'Venue Segmentation Hub': 'Κέντρο διαχωρισμού χώρων',
    'VENUE SEGMENTATION HUB': 'ΚΕΝΤΡΟ ΔΙΑΧΩΡΙΣΜΟΥ ΧΩΡΩΝ',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Δημιουργήστε τμήματα για να ομαδοποιήσετε το μενού σας. Για παράδειγμα, ένα μεγάλο κατάστημα μπορεί να έχει',
    'Restaurant': 'Εστιατόριο',
    'Poolside': 'Πισίνα',
    'department, each with its own categories.': ', το καθένα με τις δικές του κατηγορίες.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Τα τμήματα σάς επιτρέπουν να χωρίσετε τον χώρο σας σε ενότητες όπως Εστιατόριο, Μπαρ ή Πισίνα — το καθένα με τις δικές του κατηγορίες.',
    'Department name': 'Όνομα τμήματος',
    'New department name...': 'Όνομα νέου τμήματος...',
    'No Linked Daypart (Always Available)': 'Χωρίς σύνδεση με ωράριο (Πάντα διαθέσιμο)',
    'Linked Daypart': 'Συνδεδεμένο ωράριο λειτουργίας',
    'Localizations': 'Μεταφράσεις',
    'LOCALIZATIONS': 'ΜΕΤΑΦΡΑΣΕΙΣ',
    'AI Auto-Translate': 'Αυτόματη μετάφραση AI',
    'AI AUTO-TRANSLATE': 'ΑΥΤΟΜΑΤΗ ΜΕΤΑΦΡΑΣΗ AI',
    'Translated name': 'Μεταφρασμένο όνομα',
    'No Departments Yet': 'Δεν υπάρχουν ακόμη τμήματα',
    'Add First Department': 'Προσθήκη πρώτου τμήματος',
    'Avg Order Value': 'Μέση αξία παραγγελίας',
    'AVG ORDER VALUE': 'ΜΕΣΗ ΑΞΙΑ ΠΑΡΑΓΓΕΛΙΑΣ',
    'item taps': 'πατήματα πιάτων',
    'ITEM TAPS': 'ΠΑΤΗΜΑΤΑ ΠΙΑΤΩΝ',
    'Avg Rating': 'Μέση βαθμολογία',
    'AVG RATING': 'ΜΕΣΗ ΒΑΘΜΟΛΟΓΙΑ',
    'Revenue (Last 7 Days)': 'Έσοδα (Τελευταίες 7 ημέρες)',
    'REVENUE (LAST 7 DAYS)': 'ΕΣΟΔΑ (ΤΕΛΕΥΤΑΙΕΣ 7 ΗΜΕΡΕΣ)',
    'Daily breakdown': 'Ημερήσια κατανομή',
    'DAILY BREAKDOWN': 'ΗΜΕΡΗΣΙΑ ΚΑΤΑΝΟΜΗ',
    'Menu Views (Last 7 Days)': 'Προβολές μενού (Τελευταίες 7 ημέρες)',
    'MENU VIEWS (LAST 7 DAYS)': 'ΠΡΟΒΟΛΕΣ ΜΕΝΟΥ (ΤΕΛΕΥΤΑΙΕΣ 7 ΗΜΕΡΕΣ)',
    'Guest traffic': 'Κίνηση επισκεπτών',
    'GUEST TRAFFIC': 'ΚΙΝΗΣΗ ΕΠΙΣΚΕΠΤΩΝ',
    'Most Viewed Items': 'Τα πιο προβεβλημένα πιάτα',
    'MOST VIEWED ITEMS': 'ΤΑ ΠΙΟ ΠΡΟΒΕΒΛΗΜΕΝΑ ΠΙΑΤΑ',
    'Top Rated Items': 'Κορυφαία σε βαθμολογία πιάτα',
    'TOP RATED ITEMS': 'ΚΟΡΥΦΑΙΑ ΣΕ ΒΑΘΜΟΛΟΓΙΑ ΠΙΑΤΑ',
    'Waiter Requests': 'Κλήσεις σερβιτόρου',
    'WAITER REQUESTS': 'ΚΛΗΣΕΙΣ ΣΕΡΒΙΤΟΡΟΥ',
    'Total': 'Σύνολο',
    'TOTAL': 'ΣΥΝΟΛΟ',
    'Order Status (30 days)': 'Κατάσταση παραγγελιών (30 ημέρες)',
    'ORDER STATUS (30 DAYS)': 'ΚΑΤΑΣΤΑΣΗ ΠΑΡΑΓΓΕΛΙΩΝ (30 ΗΜΕΡΕΣ)',
    'Recent Guest Feedback': 'Πρόσφατα σχόλια επισκεπτών',
    'RECENT GUEST FEEDBACK': 'ΠΡΟΣΦΑΤΑ ΣΧΟΛΙΑ ΕΠΙΣΚΕΠΤΩΝ',
    'Conversion Rate': 'Ποσοστό μετατροπής',
    'CONVERSION RATE': 'ΠΟΣΟΣΤΟ ΜΕΤΑΤΡΟΠΗΣ',
    'of menu visitors placed an order': 'των επισκεπτών του μενού έκαναν παραγγελία',
    'Refreshed': 'Ενημερώθηκε',
    'REFRESHED': 'ΕΝΗΜΕΡΩΘΗΚΕ',
    'Last 30 days': 'Τελευταίες 30 ημέρες',
    'LAST 30 DAYS': 'ΤΕΛΕΥΤΑΙΕΣ 30 ΗΜΕΡΕΣ',
    'cash': 'μετρητά',
    'Cash': 'Μετρητά',
    'CASH': 'ΜΕΤΡΗΤΑ',
    'bill': 'λογαριασμός',
    'Bill': 'Λογαριασμός',
    'BILL': 'ΛΟΓΑΡΙΑΣΜΟΣ',
    'water': 'νερό',
    'Water': 'Νερό',
    'WATER': 'ΝΕΡΟ',
    'service': 'εξυπηρέτηση',
    'Service': 'Εξυπηρέτηση',
    'SERVICE': 'ΕΞΥΠΗΡΕΤΗΣΗ',
    'No interaction data yet': 'Δεν υπάρχουν ακόμη δεδομένα αλληλεπίδρασης',
    'No ratings collected yet': 'Δεν έχουν καταγραφεί ακόμη αξιολογήσεις',
    'No requests in period': 'Δεν υπάρχουν αιτήματα στην περίοδο',
    'No feedback collected yet': 'Δεν έχουν ληφθεί ακόμη σχόλια',
    'No menu views recorded yet': 'Δεν έχουν καταγραφεί ακόμη προβολές μενού',
    'Dish Rating Breakdown': 'Ανάλυση αξιολόγησης πιάτων',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Η κατάταξη πιάτων και η ικανοποίηση πελατών είναι διαθέσιμα στα πακέτα MAX και Enterprise.',
    'Customer Sentiment & Reviews': 'Απόψεις και κριτικές πελατών',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Οι απευθείας κριτικές επισκεπτών και η ανάλυση συναισθήματος απαιτούν συνδρομή MAX ή Enterprise.',
    'Reviews AI': 'AI ανάλυσης κριτικών',
    'Guest Feedback': 'Σχόλια επισκεπτών',
    'Upgrade to unlock rating analytics →': 'Αναβαθμίστε για να ξεκλειδώσετε τα στατιστικά βαθμολογιών →',
    'Upgrade to Max': 'Αναβάθμιση σε Max',
    'UPGRADE TO MAX': 'ΑΝΑΒΑΘΜΙΣΗ ΣΕ MAX',
    'MAX PLAN': 'ΠΑΚΕΤΟ MAX',
    'No Data Yet': 'Δεν υπάρχουν δεδομένα ακόμα',
    'Share your QR code to start collecting real venue insights.': 'Μοιραστείτε τον κωδικό QR για να συλλέγετε πραγματικές πληροφορίες καταστήματος.',
    'Revenue Analytics': 'Ανάλυση εσόδων',
    'REVENUE ANALYTICS': 'ΑΝΑΛΥΣΗ ΕΣΟΔΩΝ',
    'Track your earnings, orders, and average ticket values over time.': 'Παρακολουθήστε τα κέρδη, τις παραγγελίες και τη μέση απόδειξη διαχρονικά.',
    'Operational Velocity': 'Επιχειρησιακή ταχύτητα',
    'OPERATIONAL VELOCITY': 'ΕΠΙΧΕΙΡΗΣΙΑΚΗ ΤΑΧΥΤΗΤΑ',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Παρακολουθήστε την κίνηση των επισκεπτών, τα δημοφιλή πιάτα και τον φόρτο εργασίας των σερβιτόρων.',
    'Upgrade to Pro': 'Αναβάθμιση σε Pro',
    'UPGRADE TO PRO': 'ΑΝΑΒΑΘΜΙΣΗ ΣΕ PRO',
    'Loading Insights…': 'Φόρτωση αναλύσεων…',
    'Loading Insights...': 'Φόρτωση αναλύσεων...',
    'Telemetry Unavailable': 'Δεδομένα δραστηριότητας μη διαθέσιμα',
    'TELEMETRY UNAVAILABLE': 'ΔΕΔΟΜΕΝΑ ΔΡΑΣΤΗΡΙΟΤΗΤΑΣ ΜΗ ΔΙΑΘΕΣΙΜΑ',
    'No Data Captured': 'Δεν έχουν καταγραφεί δεδομένα',
    'NO DATA CAPTURED': 'ΔΕΝ ΕΧΟΥΝ ΚΑΤΑΓΡΑΦΕΙ ΔΕΔΟΜΕΝΑ',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Μοιραστείτε τον κωδικό QR για να συλλέγετε στατιστικά επισκεπτών σε πραγματικό χρόνο.',
    'INTELLIGENCE STUDIO': 'ΚΕΝΤΡΟ ΑΝΑΛΥΣΕΩΝ',
    'Insights Engine Locked': 'Προηγμένη ανάλυση κλειδωμένη',
    'INSIGHTS ENGINE LOCKED': 'ΠΡΟΗΓΜΕΝΗ ΑΝΑΛΥΣΗ ΚΛΕΙΔΩΜΕΝΗ',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Η προηγμένη ανάλυση τάσεων, οι μαζικές λειτουργίες και οι εξαγωγές δεδομένων προορίζονται για χρήστες',
    'Unlock Pro Features': 'Ξεκλειδώστε τις δυνατότητες Pro',
    'UNLOCK PRO FEATURES': 'ΞΕΚΛΕΙΔΩΣΤΕ ΤΙΣ ΔΥΝΑΤΟΤΗΤΕΣ PRO',
    'MENU VIEWS': 'ΠΡΟΒΟΛΕΣ ΜΕΝΟΥ',
    'ITEM INTERACTIONS': 'ΑΛΛΗΛΕΠΙΔΡΑΣΕΙΣ ΠΙΑΤΩΝ',
    'GUEST RATING': 'ΑΞΙΟΛΟΓΗΣΗ ΕΠΙΣΚΕΠΤΩΝ',
    'DELIVERY REVENUE': 'ΕΣΟΔΑ ΔΙΑΝΟΜΗΣ',
    'AVERAGE DELIVERY ORDER': 'ΜΕΣΗ ΠΑΡΑΓΓΕΛΙΑ ΔΙΑΝΟΜΗΣ',
    'AVERAGE DELIVERY FEE': 'ΜΕΣΗ ΧΡΕΩΣΗ ΔΙΑΝΟΜΗΣ',
    'INCLUDING FEES': 'ΣΥΜΠΕΡΙΛΑΜΒΑΝΟΜΕΝΩΝ ΤΕΛΩΝ',
    'ZONE AVERAGE': 'ΜΕΣΟΣ ΟΡΟΣ ΖΩΝΗΣ',
    'TRAFFIC PROJECTIONS': 'ΠΡΟΒΛΕΨΕΙΣ ΚΙΝΗΣΗΣ',
    'TELEMETRY SIGNAL TRACKING': 'ΠΑΡΑΚΟΛΟΥΘΗΣΗ ΣΗΜΑΤΩΝ ΔΡΑΣΤΗΡΙΟΤΗΤΑΣ',
    'VEGETARIAN': 'ΧΟΡΤΟΦΑΓΙΚΟ',
    'VEGAN': 'VEGAN',
    'SPICY': 'ΚΑΥΤΕΡΟ',
    'GLUTEN-FREE': 'ΧΩΡΙΣ ΓΛΟΥΤΕΝΗ',
    'Reset: All In-Stock': 'Επαναφορά: Όλα σε απόθεμα',
    'Emergency Stop (All Out)': 'Διακοπή έκτακτης ανάγκης: Όλα εξαντλήθηκαν',
    'No compliance issues': 'Δεν εντοπίστηκαν προβλήματα',
    'NO COMPLIANCE ISSUES': 'ΔΕΝ ΕΝΤΟΠΙΣΤΗΚΑΝ ΠΡΟΒΛΗΜΑΤΑ',
    'detected in current menu': 'στο τρέχον μενού',
    'DETECTED IN CURRENT MENU': 'ΣΤΟ ΤΡΕΧΟΝ ΜΕΝΟΥ',
    'Telemetry History (24h)': 'Ιστορικό δραστηριότητας (24ω)',
    'TELEMETRY HISTORY (24H)': 'ΙΣΤΟΡΙΚΟ ΔΡΑΣΤΗΡΙΟΤΗΤΑΣ (24Ω)',
    'Telemetry History (30d)': 'Ιστορικό δραστηριότητας (30ημ)',
    'TELEMETRY HISTORY (30D)': 'ΙΣΤΟΡΙΚΟ ΔΡΑΣΤΗΡΙΟΤΗΤΑΣ (30ΗΜ)',
    'Save': 'Αποθήκευση',
    'Saved': 'Αποθηκεύτηκε',
    'Saving...': 'Αποθήκευση...',
    'Save Changes': 'Αποθήκευση αλλαγών',
    'Save Settings': 'Αποθήκευση ρυθμίσεων',
    'Cancel': 'Ακύρωση',
    'Close': 'Κλείσιμο',
    'Create': 'Δημιουργία',
    'Add': 'Προσθήκη',
    'Edit': 'Επεξεργασία',
    'Delete': 'Διαγραφή',
    'Remove': 'Αφαίρεση',
    'Update': 'Ενημέρωση',
    'Refresh': 'Ανανέωση',
    'Search': 'Αναζήτηση',
    'Search...': 'Αναζήτηση...',
    'Search items...': 'Αναζήτηση στοιχείων...',
    'Search categories...': 'Αναζήτηση κατηγοριών...',
    'Filter': 'Φίλτρο',
    'All': 'Όλα',
    'Export': 'Εξαγωγή',
    'Import': 'Εισαγωγή',
    'Download': 'Λήψη',
    'Upload': 'Μεταφόρτωση',
    'Copy': 'Αντιγραφή',
    'Copied': 'Αντιγράφηκε',
    'Copied!': 'Αντιγράφηκε!',
    'Preview': 'Προεπισκόπηση',
    'Open': 'Ανοιχτά',
    'View': 'Προβολή',
    'Manage': 'Διαχείριση',
    'Configure': 'Διαμόρφωση',
    'Enable': 'Ενεργοποίηση',
    'Disable': 'Απενεργοποίηση',
    'Enabled': 'Ενεργοποιημένο',
    'Disabled': 'Απενεργοποιημένο',
    'Active': 'Ενεργό',
    'Inactive': 'Ανενεργό',
    'Available': 'Διαθέσιμο',
    'Unavailable': 'Μη διαθέσιμο',
    'Visible': 'Ορατό',
    'Hidden': 'Κρυφό',
    'Required': 'Απαραίτητο',
    'Optional': 'Προαιρετικό',
    'Confirm': 'Επιβεβαίωση',
    'Back': 'Πίσω',
    'Next': 'Επόμενο',
    'Done': 'Τέλος',
    'Upgrade': 'Αναβάθμιση',
    'Sign Out': 'Αποσύνδεση',
    'Exit to Main Admin': 'Επιστροφή στη Διαχείριση',
    'Change Language': 'Αλλαγή γλώσσας',
    'Add Category': 'Προσθήκη κατηγορίας',
    'Add Menu Item': 'Προσθήκη στοιχείου μενού',
    'Add Item': 'Προσθήκη στοιχείου',
    'Add Ingredient': 'Προσθήκη συστατικού',
    'Add Department': 'Προσθήκη τμήματος',
    'Delete Item': 'Διαγραφή στοιχείου',
    'Delete Category': 'Διαγραφή κατηγορίας',
    'Upload Image': 'Μεταφόρτωση εικόνας',
    'Upload Photo': 'Μεταφόρτωση φωτογραφίας',
    'Select Category': 'Επιλέξτε κατηγορία',
    'Select Department': 'Επιλέξτε τμήμα',
    'Add translation...': 'Προσθήκη μετάφρασης...',
    'Clear': 'Εκκαθάριση',
    'Reset': 'Επαναφορά',
    'Apply': 'Εφαρμογή',
    'Submit': 'Υποβολή',
    'Core & Operations': 'Λειτουργίες & Σέρβις',
    'Menu & Experience': 'Μενού & Εμπειρία',
    'Enterprise & Security': 'Διοίκηση & Ασφάλεια',
    'System & Control': 'Ρυθμίσεις & Βοήθεια',
    'Intelligence Studio': 'Κέντρο αναλύσεων',
    'Venue Insights': 'Πωλήσεις & Αναφορές',
    'Reservations': 'Κρατήσεις',
    'Floor Plan': 'Κάτοψη σάλας',
    'Live Waitlist': 'Λίστα αναμονής',
    'Walk-Ins': 'Πελάτες χωρίς κράτηση',
    'Walk-ins': 'Πελάτες χωρίς κράτηση',
    'Departments': 'Τμήματα',
    'Service Hours': 'Ώρες εξυπηρέτησης',
    'Categories': 'Κατηγορίες',
    'Menu Categories': 'Κατηγορίες μενού',
    'Menu Items': 'Στοιχεία μενού',
    'Items': 'Στοιχεία',
    'Ingredients': 'Συστατικά',
    'Loyalty Program': 'Πρόγραμμα επιβράβευσης',
    'Campaign Studio': 'Προσφορές',
    'Signage': 'Ψηφιακές οθόνες',
    'QR Code': 'Κωδικοί QR',
    'Kitchen Ops': 'Οθόνη κουζίνας',
    'Theme Engine': 'Χρώματα & Στιλ',
    'Governance': 'Διακυβέρνηση',
    'Audit Log': 'Ιστορικό δραστηριότητας',
    'API & Webhooks': 'Συνδέσεις',
    'SSO & Security': 'Ασφάλεια & Σύνδεση',
    'Team': 'Προσωπικό',
    'Billing': 'Συνδρομή & Χρέωση',
    'SEO & Google': 'Παρουσία στο Google',
    'Notifications': 'Ειδοποιήσεις',
    'Support Center': 'Κέντρο βοήθειας',
    'Settings': 'Ρυθμίσεις καταστήματος',
    'Name': 'Όνομα',
    'Description': 'Περιγραφή',
    'Price': 'Τιμή',
    'Status': 'Κατάσταση',
    'Actions': 'Ενέργειες',
    'Role': 'Ρόλος',
    'Email': 'E-mail',
    'Phone': 'Τηλέφωνο',
    'Address': 'Διεύθυνση',
    'City': 'Πόλη',
    'Currency': 'Νόμισμα',
    'Language': 'Γλώσσα',
    'Theme': 'Θέμα',
    'General': 'Γενικά',
    'Total Revenue': 'Συνολικά έσοδα',
    'Total Orders': 'Σύνολο παραγγελιών',
    'Average Order': 'Μέση παραγγελία',
    'Active Sessions': 'Ενεργά τραπέζια',
    'Recent Orders': 'Πρόσφατες παραγγελίες',
    'Live Orders': 'Ζωντανές παραγγελίες',
    'Table': 'Τραπέζι',
    'Date': 'Ημερομηνία',
    'Time': 'Ώρα',
    'Amount': 'Ποσό',
    'Customer': 'Πελάτης',
    'In Stock': 'Σε απόθεμα',
    'Out of Stock': 'Εξαντλήθηκε',
    'Sold Out': 'Εξαντλημένο',
    'Dine In': 'Στο κατάστημα',
    'Pickup': 'Παραλαβή',
    'Delivery': 'Διανομή',
    'Paid': 'Πληρώθηκε',
    'Unpaid': 'Απλήρωτο',
    'Pending': 'Εκκρεμεί',
    'Preparing': 'Ετοιμάζεται',
    'Ready': 'Έτοιμο',
    'Completed': 'Ολοκληρώθηκε',
    'Cancelled': 'Ακυρώθηκε',
    'Loading...': 'Φόρτωση...',
    'No data': 'Δεν υπάρχουν δεδομένα',
    'No results found': 'Δεν βρέθηκαν αποτελέσματα',
    'No items found': 'Δεν βρέθηκαν στοιχεία',
    'No translations defined yet': 'Δεν έχουν οριστεί μεταφράσεις ακόμα',
    'Are you sure?': 'Είστε σίγουροι;',
    'This action cannot be undone.': 'Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    'Settings saved successfully!': 'Οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς!',
    'Administrator': 'Διαχειριστής',
    'Manager': 'Διευθυντής',
    'Owner': 'Ιδιοκτήτης',
    'Chef': 'Σεφ',
    'Waiter': 'Σερβιτόρος',
    'Cashier': 'Ταμίας',
    'Type a command or search...': 'Πληκτρολογήστε εντολή ή αναζήτηση...',
    'Command Palette': 'Πίνακας εντολών',
    'Demand Velocity': 'Ταχύτητα ζήτησης',
    'DEMAND VELOCITY': 'ΤΑΧΥΤΗΤΑ ΖΗΤΗΣΗΣ',
    'Top Performing Entities': 'Κορυφαία σε απόδοση πιάτα',
    'TOP PERFORMING ENTITIES': 'ΚΟΡΥΦΑΙΑ ΣΕ ΑΠΟΔΟΣΗ ΠΙΑΤΑ',
    'Hourly Load Analysis': 'Ωριαία ανάλυση κίνησης',
    'HOURLY LOAD ANALYSIS': 'ΩΡΙΑΙΑ ΑΝΑΛΥΣΗ ΚΙΝΗΣΗΣ',
    'Bulk Command Center': 'Κέντρο μαζικών ενεργειών',
    'BULK COMMAND CENTER': 'ΚΕΝΤΡΟ ΜΑΖΙΚΩΝ ΕΝΕΡΓΕΙΩΝ',
    'Governance Scan': 'Έλεγχος συμμόρφωσης μενού',
    'GOVERNANCE SCAN': 'ΕΛΕΓΧΟΣ ΣΥΜΜΟΡΦΩΣΗΣ ΜΕΝΟΥ',
    'No compliance issues detected in current menu': 'Δεν εντοπίστηκαν προβλήματα στο τρέχον μενού',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'ΔΕΝ ΕΝΤΟΠΙΣΤΗΚΑΝ ΠΡΟΒΛΗΜΑΤΑ ΣΤΟ ΤΡΕΧΟΝ ΜΕΝΟΥ',
    'Procedural Tagging': 'Αυτόματη προσθήκη ετικετών',
    'PROCEDURAL TAGGING': 'ΑΥΤΟΜΑΤΗ ΠΡΟΣΘΗΚΗ ΕΤΙΚΕΤΩΝ',
    'Name match...': 'Αναζήτηση με όνομα...',
    '...Name match': 'Αναζήτηση με όνομα...',
    'Healthy': 'Υγιεινό',
    'HEALTHY': 'ΥΓΙΕΙΝΟ',
    'Apply Tags': 'Εφαρμογή ετικετών',
    'APPLY TAGS': 'ΕΦΑΡΜΟΓΗ ΕΤΙΚΕΤΩΝ',
    'Global Price Shift': 'Γενική αναπροσαρμογή τιμών',
    'GLOBAL PRICE SHIFT': 'ΓΕΝΙΚΗ ΑΝΑΠΡΟΣΑΡΜΟΓΗ ΤΙΜΩΝ',
    'Adjust': 'Προσαρμογή',
    'ADJUST': 'ΠΡΟΣΑΡΜΟΓΗ',
    'Availability Override': 'Άμεσος έλεγχος διαθεσιμότητας',
    'AVAILABILITY OVERRIDE': 'ΑΜΕΣΟΣ ΕΛΕΓΧΟΣ ΔΙΑΘΕΣΙΜΟΤΗΤΑΣ',
    'Reset: ALL IN-STOCK': 'Επαναφορά: ΟΛΑ ΔΙΑΘΕΣΙΜΑ',
    'RESET: ALL IN-STOCK': 'ΕΠΑΝΑΦΟΡΑ: ΟΛΑ ΔΙΑΘΕΣΙΜΑ',
    'EMERGENCY STOP (ALL OUT)': 'Διακοπή έκτακτης ανάγκης (ΟΛΑ ΕΞΑΝΤΛΗΘΗΚΑΝ)',
    'Integrity Engine': 'Μηχανή ακεραιότητας μενού',
    'INTEGRITY ENGINE': 'ΜΗΧΑΝΗ ΑΚΕΡΑΙΟΤΗΤΑΣ ΜΕΝΟΥ',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Το αυτόματο σύστημα ελέγχει τη διαθεσιμότητα των πιάτων, τυχόν ασυνήθιστες τιμές και την πληρότητα των περιγραφών στο κατάστημά σας.',
    'Inventory Audit Export': 'Εξαγωγή ελέγχου αποθεμάτων',
    'INVENTORY AUDIT EXPORT': 'ΕΞΑΓΩΓΗ ΕΛΕΓΧΟΥ ΑΠΟΘΕΜΑΤΩΝ',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Δημιουργήστε μια πλήρη αναφορά απόδοσης μενού και στοιχείων για έλεγχο εκτός σύνδεσης.',
    'Enterprise Data Export': 'Εξαγωγή επιχειρηματικών δεδομένων',
    'ENTERPRISE DATA EXPORT': 'ΕΞΑΓΩΓΗ ΕΠΙΧΕΙΡΗΜΑΤΙΚΩΝ ΔΕΔΟΜΕΝΩΝ',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Δημιουργήστε και κατεβάστε πλήρεις αναφορές απόδοσης. Περιλαμβάνει τη ροή επισκεπτών, τη ζήτηση πιάτων και τις μετατροπές.',
    'JSON Snapshot': 'Στιγμιότυπο JSON',
    'JSON SNAPSHOT': 'ΣΤΙΓΜΙΟΤΥΠΟ JSON',
    'Compile CSV Report': 'Δημιουργία αναφοράς CSV',
    'COMPILE CSV REPORT': 'ΔΗΜΙΟΥΡΓΙΑ ΑΝΑΦΟΡΑΣ CSV',
    'Date Segment': 'Χρονικό διάστημα',
    'DATE SEGMENT': 'ΧΡΟΝΙΚΟ ΔΙΑΣΤΗΜΑ',
    'Inbound Views': 'Εισερχόμενες προβολές',
    'INBOUND VIEWS': 'ΕΙΣΕΡΧΟΜΕΝΕΣ ΠΡΟΒΟΛΕΣ',
    'Asset Interactions': 'Αλληλεπιδράσεις με τα πιάτα',
    'ASSET INTERACTIONS': 'ΑΛΛΗΛΕΠΙΔΡΑΣΕΙΣ ΜΕ ΤΑ ΠΙΑΤΑ',
    'Telemetry History': 'Ιστορικό δραστηριότητας',
    'TELEMETRY HISTORY': 'ΙΣΤΟΡΙΚΟ ΔΡΑΣΤΗΡΙΟΤΗΤΑΣ',
    'Telemetry History (7d)': 'Ιστορικό δραστηριότητας (7ημ)',
    'TELEMETRY HISTORY (7D)': 'ΙΣΤΟΡΙΚΟ ΔΡΑΣΤΗΡΙΟΤΗΤΑΣ (7ΗΜ)',
    'Growth Trajectory': 'Τροχιά ανάπτυξης',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Η συχνότητα αλληλεπίδρασης με το μενού σας παρουσιάζει ανοδική τάση τις τελευταίες 7 ημέρες.',
    'Deep Analysis Engine': 'Μηχανή λεπτομερούς ανάλυσης',
    'DEEP ANALYSIS ENGINE': 'ΜΗΧΑΝΗ ΛΕΠΤΟΜΕΡΟΥΣ ΑΝΑΛΥΣΗΣ',
    'Asset Velocity': 'Δημοτικότητα πιάτων',
    'ASSET VELOCITY': 'ΔΗΜΟΤΙΚΟΤΗΤΑ ΠΙΑΤΩΝ',
    'Traffic Projections': 'Προβλέψεις επισκεψιμότητας',
    'Telemetry Signal Tracking': 'Παρακολούθηση σημάτων δραστηριότητας',
    'Service Hour Update': 'Ενημέρωση ωρών εξυπηρέτησης',
    'Late Night Lounge Hours': 'Νυχτερινές ώρες lounge',
    'Bar & Lounge Hours': 'Ώρες bar & lounge',
    'Breakfast & Brunch Hours': 'Ώρες πρωινού και brunch',
    'Switch Location': 'Αλλαγή καταστήματος',
    'SWITCH LOCATION': 'ΑΛΛΑΓΗ ΚΑΤΑΣΤΗΜΑΤΟΣ',
    'Global Admin': 'Γενικός διαχειριστής',
    'GLOBAL ADMIN': 'ΓΕΝΙΚΟΣ ΔΙΑΧΕΙΡΙΣΤΗΣ',
    'Staff Access': 'Πρόσβαση προσωπικού',
    'STAFF ACCESS': 'ΠΡΟΣΒΑΣΗ ΠΡΟΣΩΠΙΚΟΥ',
    'Venue Overview': 'Επισκόπηση καταστήματος',
    'VENUE OVERVIEW': 'ΕΠΙΣΚΟΠΗΣΗ ΚΑΤΑΣΤΗΜΑΤΟΣ',
    'Unified Telemetry Engine': 'Ενιαία παρακολούθηση λειτουργίας',
    'UNIFIED TELEMETRY ENGINE': 'ΕΝΙΑΙΑ ΠΑΡΑΚΟΛΟΥΘΗΣΗ ΛΕΙΤΟΥΡΓΙΑΣ',
    'Pulse': 'Ζωντανά',
    'PULSE': 'ΖΩΝΤΑΝΑ',
    'Insights': 'Στατιστικά στοιχεία',
    'INSIGHTS': 'ΣΤΑΤΙΣΤΙΚΑ ΣΤΟΙΧΕΙΑ',
    'Reports': 'Αναφορές',
    'REPORTS': 'ΑΝΑΦΟΡΕΣ',
    'GOVERNANCE': 'ΔΙΑΚΥΒΕΡΝΗΣΗ',
    'Multi-Venue': 'Πολλαπλά καταστήματα',
    'MULTI-VENUE': 'ΠΟΛΛΑΠΛΑ ΚΑΤΑΣΤΗΜΑΤΑ',
    'Menu Views': 'Προβολές καταλόγου',
    'Item Interactions': 'Αλληλεπιδράσεις πιάτων',
    'Guest Rating': 'Αξιολόγηση επισκεπτών',
    'Revenue (7d)': 'Έσοδα (7ημ)',
    'Revenue (24h)': 'Έσοδα (24ω)',
    'Revenue (30d)': 'Έσοδα (30ημ)',
    'Delivery orders (7d)': 'Παραγγελίες διανομής (7ημ)',
    'Delivery orders (24h)': 'Παραγγελίες διανομής (24ω)',
    'Delivery orders (30d)': 'Παραγγελίες διανομής (30ημ)',
    'Delivery revenue': 'Έσοδα διανομής',
    'Average delivery order': 'Μέση παραγγελία διανομής',
    'Average delivery fee': 'Μέσο κόστος διανομής',
    'Zone average': 'Μέσος όρος περιοχής',
    'Including fees': 'Συμπεριλαμβανομένων των τελών',
    'No deliveries': 'Χωρίς διανομές',
    'No orders': 'Χωρίς παραγγελίες',
    'No reviews': 'Χωρίς κριτικές',
    'Live': 'Ζωντανά',
    'LIVE': 'ΖΩΝΤΑΝΑ',
    'reviews': 'κριτικές',
    'orders': 'παραγγελίες',
    'deliveries': 'διανομές',
    'of orders': 'των παραγγελιών',
    'rate': 'ποσοστό',
    'Recent Activity': 'Πρόσφατη δραστηριότητα',
    'Real-time Venue Telemetry': 'Παρακολούθηση καταστήματος σε πραγματικό χρόνο',
    'REAL-TIME VENUE TELEMETRY': 'ΠΑΡΑΚΟΛΟΥΘΗΣΗ ΚΑΤΑΣΤΗΜΑΤΟΣ ΣΕ ΠΡΑΓΜΑΤΙΚΟ ΧΡΟΝΟ',
    'Rapid Pulse View': 'Γρήγορη ζωντανή προβολή',
    'Anonymous Guest': 'Ανώνυμος επισκέπτης',
    'scanned menu': 'σκάναρε το μενού',
    'viewed item': 'είδε το πιάτο',
    'interacted with': 'αλληλεπίδρασε με',
    'placed order': 'έκανε παραγγελία',
    'searched menu': 'αναζήτησε στο μενού',
    'Main Landing': 'Αρχική σελίδα',
    'Trace': 'Ιχνηλάτηση',
    'TRACE': 'ΙΧΝΗΛΑΤΗΣΗ',
    'Gaining Telemetry...': 'Λήψη δεδομένων δραστηριότητας...',
    'No recent traffic detected': 'Δεν εντοπίστηκε πρόσφατη δραστηριότητα',
    'Activities will appear here as guests scan your QR code': 'Οι δραστηριότητες θα εμφανίζονται εδώ καθώς οι επισκέπτες σαρώνουν τον κωδικό QR σας',
    'Analyze All Logs': 'Ανάλυση όλων των αρχείων',
    'Just now': 'Μόλις τώρα',
    'less than a minute ago': 'λιγότερο από ένα λεπτό πριν',
    '1 minute ago': 'πριν από 1 λεπτό',
    '1 hour ago': 'πριν από 1 ώρα',
    'about 1 hour ago': 'περίπου πριν από 1 ώρα',
    '1 day ago': 'πριν από 1 ημέρα',
    'DAY AGO 1': 'πριν από 1 ημέρα',
    '2 days ago': 'πριν από 2 ημέρες',
    'DAY AGO 2': 'πριν από 2 ημέρες',
    '3 days ago': 'πριν από 3 ημέρες',
    'DAY AGO 3': 'πριν από 3 ημέρες',
    '4 days ago': 'πριν από 4 ημέρες',
    'DAY AGO 4': 'πριν από 4 ημέρες',
    '5 days ago': 'πριν από 5 ημέρες',
    'DAY AGO 5': 'πριν από 5 ημέρες',
    '6 days ago': 'πριν από 6 ημέρες',
    'DAY AGO 6': 'πριν από 6 ημέρες',
    '7 days ago': 'πριν από 7 ημέρες',
    'DAY AGO 7': 'πριν από 7 ημέρες',
    'about 1 month ago': 'περίπου πριν από 1 μήνα',
    'Monday': 'Δευτέρα',
    'Tuesday': 'Τρίτη',
    'Wednesday': 'Τετάρτη',
    'Thursday': 'Πέμπτη',
    'Friday': 'Παρασκευή',
    'Saturday': 'Σάββατο',
    'Sunday': 'Κυριακή',
    'Mon': 'Δευ',
    'Tue': 'Τρί',
    'Wed': 'Τετ',
    'Thu': 'Πέμ',
    'Fri': 'Παρ',
    'Sat': 'Σάβ',
    'Sun': 'Κυρ',
    'Opening Hours': 'Ώρες λειτουργίας',
    'Start Time': 'Ώρα έναρξης',
    'End Time': 'Ώρα λήξης',
    'Opening Time': 'Ώρα ανοίγματος',
    'Closing Time': 'Ώρα κλεισίματος',
    'All Day': 'Όλη μέρα',
    'Closed': 'Κλειστά',
    'Active Days': 'Ενεργές ημέρες',
    'Active Schedule': 'Ενεργό πρόγραμμα',
    'Always Available': 'Πάντα διαθέσιμο',
    'Always Visible': 'Πάντα ορατό',
    'New Category': 'Νέα κατηγορία',
    'Category': 'Κατηγορία',
    'Category Name': 'Όνομα κατηγορίας',
    'Category Description': 'Περιγραφή κατηγορίας',
    'Display Order': 'Σειρά εμφάνισης',
    'Item Name': 'Όνομα πιάτου',
    'Item Description': 'Περιγραφή πιάτου',
    'Base Price': 'Βασική τιμή',
    'Preparation Time': 'Χρόνος προετοιμασίας',
    'Calories': 'Θερμίδες',
    'Featured Item': 'Προτεινόμενο πιάτο',
    'Chef\'s Recommendation': 'Πρόταση του σεφ',
    'Spicy': 'Καυτερό',
    'Vegetarian': 'Χορτοφαγικό',
    'Vegan': 'Vegan',
    'Gluten-Free': 'Χωρίς γλουτένη',
    'Halal': 'Χαλάλ',
    'Kosher': 'Κοσέρ',
    'Contains Nuts': 'Περιέχει ξηρούς καρπούς',
    'Dairy-Free': 'Χωρίς γαλακτοκομικά',
    'Allergen Exclusions': 'Αλλεργιογόνα',
    'Table Number': 'Αριθμός τραπεζιού',
    'Assigned Table': 'Εκχωρημένο τραπέζι',
    'Seats': 'Θέσεις',
    'Capacity': 'Χωρητικότητα',
    'Indoor': 'Εσωτερικός χώρος',
    'Outdoor': 'Εξωτερικός χώρος',
    'Terrace': 'Ταράτσα',
    'Bar': 'Μπαρ',
    'Add Table': 'Προσθήκη τραπεζιού',
    'Edit Table': 'Επεξεργασία τραπεζιού',
    'Delete Table': 'Διαγραφή τραπεζιού',
    'Bookings': 'Κρατήσεις',
    'Add to Waitlist': 'Προσθήκη στη λίστα αναμονής',
    'Guest Name': 'Όνομα πελάτη',
    'Party Size': 'Αριθμός ατόμων',
    'Arrival Time': 'Ώρα άφιξης',
    'Special Requests': 'Ειδικά αιτήματα',
    'Confirmed': 'Επιβεβαιώθηκε',
    'No Show': 'Δεν προσήλθε',
    'Add Staff Member': 'Προσθήκη μέλους ομάδας',
    'Invite Staff': 'Πρόσκληση μέλους',
    'Email Address': 'Διεύθυνση email',
    'Phone Number': 'Αριθμός τηλεφώνου',
    'Download QR Code': 'Λήψη κωδικού QR',
    'Print Table Stands': 'Εκτύπωση σταντ τραπεζιού',
    'Today': 'Σήμερα',
    'Yesterday': 'Χθες',
    'This Week': 'Αυτή την εβδομάδα',
    'This Month': 'Αυτόν τον μήνα',
    'Last 30 Days': 'Τελευταίες 30 ημέρες',
    'Access Denied': 'Δεν επιτρέπεται η πρόσβαση',
    'Advanced Settings': 'Σύνθετες ρυθμίσεις',
    'Change Image': 'Αλλαγή εικόνας',
    'Remove Image': 'Αφαίρεση εικόνας',
    'Announcement Banner': 'Banner ανακοινώσεων',
    'Please fill in all required fields.': 'Συμπληρώστε όλα τα απαιτούμενα πεδία.',
    'Changes saved successfully': 'Οι αλλαγές αποθηκεύτηκαν με επιτυχία',
    '(You)': '(Εσείς)',
    'AI Auto-Tag': 'Αυτόματη ετικετοποίηση AI',
    'Analyzing:': 'Ανάλυση:',
    'Apple (Fruit/Vegan)': 'Μήλο (Φρούτα/Vegan)',
    'Cancel editing': 'Ακύρωση επεξεργασίας',
    'Capacity:': 'Χωρητικότητα:',
    'Carrot (Veggie)': 'Καρότο (Λαχανικό)',
    'CircleDot (Organic)': 'Κυκλικό σημείο (Βιολογικό)',
    'Coffee (Caffeine)': 'Καφές (Καφεΐνη)',
    'Configuration': 'Ρύθμιση',
    'Copy verification record name': 'Αντιγραφή ονόματος εγγραφής',
    'Copy verification record value': 'Αντιγραφή τιμής εγγραφής',
    'Critical exception intercepted': 'Εντοπίστηκε κρίσιμο σφάλμα',
    'Kernel Panic': 'Σφάλμα Συστήματος',
    'DNA (Organic/Bio)': 'DNA (Βιολογικό)',
    'Egg (Ovo/Allergen)': 'Αυγό (Αλλεργιογόνο)',
    'Empty': 'Άδειο',
    'Engraving...': 'Δημιουργία...',
    'Flame (Spicy)': 'Φλόγα (Καυτερό)',
    'Free': 'Δωρεάν',
    'From': 'Από',
    'Generating': 'Δημιουργία',
    'Governance & Traceability Studio': 'Κέντρο Διαχείρισης & Δραστηριότητας',
    'Greet': 'Καλωσόρισμα',
    'Heart (Healthy)': 'Καρδιά (Υγιεινό)',
    'In-App': 'Εντός εφαρμογής',
    'Infinity SVG': 'Διάνυσμα SVG',
    'Leaf (Default)': 'Φύλλο (Προεπιλογή)',
    'MAX Feature': 'Λειτουργία Enterprise',
    'Milk (Dairy)': 'Γάλα (Γαλακτοκομικά)',
    'Network': 'Δίκτυο',
    'Notify SMS': 'Ειδοποίηση SMS',
    'Order': 'Παραγγελία',
    'Original': 'Αρχικό',
    'Overview': 'Επισκόπηση',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Βασικοί Ρόλοι',
    'Shield (Safety)': 'Ασπίδα (Ασφάλεια)',
    'Soup (Warm/Soup)': 'Σούπα (Ζεστό)',
    'Standard Pro View': 'Βασική προβολή',
    'Star': 'Αστέρι',
    'Synchronized with Venue Identity': 'Προσαρμοσμένο στην ταυτότητα του καταστήματος',
    'Synchronizing Security Context...': 'Ασφαλής σύνδεση σε εξέλιξη...',
    'Targeted Timing': 'Ωράριο σερβιρίσματος',
    'Targeting': 'Εφαρμογή σε',
    'To': 'Έως',
    'Toggle Navigation': 'Εναλλαγή πλοήγησης',
    'Venue': 'Κατάστημα',
    'Venue Governance Suite': 'Ρυθμίσεις & λειτουργίες καταστήματος',
    'Volume:': 'Ένταση:',
    'WHITEBOARD NOTE...': 'Σημείωση προσωπικού...',
    'Warning (Alert)': 'Προειδοποίηση',
    'Wheat (Flour/Gluten)': 'Σιτάρι (Αλεύρι/Γλουτένη)',
    'Wine (Alcohol)': 'Κρασί (Αλκοόλ)',
    'YouTube/Vimeo/Direct Link': 'Σύνδεσμος βίντεο (YouTube, Vimeo ή MP4)',
    'Your name': 'Το όνομά σας',
    '2. Table Number': '2. Αριθμός τραπεζιού',
    'for Morocco or': 'για Μαρόκο ή',
    'in': 'σε',
    'to': 'έως',
    'e.g. 12 or Patio-1': 'π.χ. 12 ή Βεράντα-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'π.χ. Χειροποίητα φετουτσίνι, μαύρη χειμερινή τρούφα, παρμεζάνα 36 μηνών...',
    'e.g. Head Sommelier & Wine Lead': 'π.χ. Αρχισομελιέ',
    'e.g. London': 'π.χ. Λονδίνο',
    'e.g. SOMMELIER, HOST, BARISTA': 'π.χ. ΣΟΜΕΛΙΕ, ΥΠΟΔΟΧΗ, ΜΠΑΡΙΣΤΑ',
    'e.g. Skyline Lounge': 'π.χ. Skyline Lounge',
    'e.g. TRUFFLE OIL': 'π.χ. ΛΑΔΙ ΤΡΟΥΦΑΣ',
    'e.g. The Grand Cafe': 'π.χ. Το Μεγάλο Καφέ',
    'e.g. Window preference, high chair': 'π.χ. Κοντά στο παράθυρο, παιδικό κάθισμα',
    'Copied to clipboard!': 'Αντιγράφηκε στο πρόχειρο!',
    'Copy failed.': 'Η αντιγραφή απέτυχε.',
    'Link copied!': 'Ο σύνδεσμος αντιγράφηκε!',
    'Logo uploaded': 'Το λογότυπο μεταφορτώθηκε',
    'Cover image uploaded': 'Η εικόνα εξωφύλλου μεταφορτώθηκε',
    'Profile saved and synced successfully': 'Το προφίλ αποθηκεύτηκε και συγχρονίστηκε επιτυχώς',
    'Category Created': 'Η κατηγορία δημιουργήθηκε',
    'Category Updated': 'Η κατηγορία ενημερώθηκε',
    'Category Deleted': 'Η κατηγορία διαγράφηκε',
    'Item Created': 'Το προϊόν δημιουργήθηκε',
    'Item Updated': 'Το προϊόν ενημερώθηκε',
    'Item Deleted': 'Το προϊόν διαγράφηκε',
    'Menu Published': 'Το μενού δημοσιεύτηκε',
    'Delete Category?': 'Διαγραφή κατηγορίας;',
    'Delete Menu Item?': 'Διαγραφή αυτού του προϊόντος;',
    'Delete Ingredient?': 'Διαγραφή αυτού του συστατικού;',
    'Delete Service Period?': 'Διαγραφή περιόδου σερβιρίσματος;',
    'Remove team member?': 'Αφαίρεση μέλους της ομάδας;',
    'Cancel invitation?': 'Ακύρωση πρόσκλησης;',
    'Revoke API key?': 'Ανάκληση κλειδιού API;',
    'Delete webhook endpoint?': 'Διαγραφή σημείου webhook;',
    'Clear all notifications?': 'Εκκαθάριση όλων των ειδοποιήσεων;',
    'Disconnect Stripe account?': 'Αποσύνδεση λογαριασμού Stripe;',
    'Disconnect Google Business Profile?': 'Αποσύνδεση προφίλ Google Business;',
    'Campaigns published successfully!': 'Οι καμπάνιες δημοσιεύτηκαν επιτυχώς!',
    'Changes successfully synchronized with Google Business Profile!': 'Οι αλλαγές συγχρονίστηκαν επιτυχώς με το Google Business Profile!',
    'Google Business Profile successfully connected!': 'Το Google Business Profile συνδέθηκε επιτυχώς!',
    'Google Business Profile disconnected.': 'Το Google Business Profile αποσυνδέθηκε.',
    'Google Post draft saved successfully!': 'Το προσχέδιο δημοσίευσης Google αποθηκεύτηκε επιτυχώς!',
    'Post draft deleted.': 'Το προσχέδιο δημοσίευσης διαγράφηκε.',
    'Review response published successfully!': 'Η απάντηση στην κριτική δημοσιεύτηκε επιτυχώς!',
    'SEO configuration saved successfully!': 'Οι ρυθμίσεις SEO αποθηκεύτηκαν επιτυχώς!',
    'SEO metadata translated across all languages!': 'Τα μεταδεδομένα SEO μεταφράστηκαν σε όλες τις γλώσσες!',
    'Venue profile & bio translated across all languages!': 'Το προφίλ & η περιγραφή του καταστήματος μεταφράστηκαν σε όλες τις γλώσσες!',
    'Reservation configuration saved successfully': 'Οι ρυθμίσεις κρατήσεων αποθηκεύτηκαν επιτυχώς',
    'Stripe settings saved successfully.': 'Οι ρυθμίσεις Stripe αποθηκεύτηκαν επιτυχώς.',
    'Print-Ready PDF downloaded successfully': 'Το PDF για εκτύπωση κατέβηκε επιτυχώς',
    'Vector QR PDF downloaded successfully': 'Το διανυσματικό QR PDF κατέβηκε επιτυχώς',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Το πακέτο εκτύπωσης 300 DPI δημιουργήθηκε επιτυχώς',
    'At least one language must be enabled.': 'Πρέπει να είναι ενεργοποιημένη τουλάχιστον μία γλώσσα.',
    'Enter a non-zero percentage before adjusting prices.': 'Εισαγάγετε ένα ποσοστό διάφορο του μηδενός πριν προσαρμόσετε τις τιμές.',
    'Please enter a Venue Name before translating.': 'Εισαγάγετε το όνομα του καταστήματος πριν από τη μετάφραση.',
    'Please enter an item name before running dietary and allergen analysis.': 'Εισαγάγετε το όνομα του προϊόντος πριν από την ανάλυση αλλεργιογόνων.',
    'Please write a reply before submitting.': 'Γράψτε μια απάντηση πριν από την υποβολή.',
    'Table 3 Called Waiter': 'Το τραπέζι 3 κάλεσε σερβιτόρο',
    'Table Call Bell': 'Κουδούνι κλήσης τραπεζιού',
    'Waiter Calls': 'Κλήσεις σερβιτόρου',
    'Waiter Dispatch & Calls': 'Διαχείριση & κλήσεις σερβιτόρων',
    'Kitchen Display & Whiteboard': 'Οθόνη κουζίνας & πίνακας σημειώσεων',
    'Customer Orders': 'Παραγγελίες πελατών',
    'Bakery': 'Αρτοποιείο / Ζαχαροπλαστείο',
    'Bar / Pub': 'Μπαρ / Παμπ',
    'Café / Coffee Shop': 'Καφετέρια / Cafe',
    'Fast Food': 'Γρήγορο φαγητό',
    'Gourmet / Fine Dining': 'Υψηλή γαστρονομία',
    'Hotel': 'Ξενοδοχείο',
    'General Local Business': 'Γενική τοπική επιχείρηση',
    'Classic': 'Κλασικό',
    'Clean': 'Λιτό',
    'Modern': 'Μοντέρνο',
    'Elegant': 'Κομψό',
    'Energetic': 'Δυναμικό',
    'Glass': 'Εφέ γυαλιού',
    'Playful & Casual': 'Χαλαρό & ευχάριστο',
    'Sophisticated & Gourmet': 'Εκλεπτυσμένο & γκουρμέ',
    'Warm & Welcoming': 'Ζεστό & φιλόξενο',
    'Executive': 'Executive',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Απαιτείται αναβάθμιση: Τα πολύγλωσσα μενού είναι διαθέσιμα στο πλάνο Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Απαιτείται αναβάθμιση: Οι παραγγελίες και οι κλήσεις σερβιτόρου δεν υποστηρίζονται στο πλάνο Starter.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Η εξαγωγή διανυσματικών SVG είναι δυνατότητα Pro & Max. Αναβαθμίστε για να συνεχίσετε.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Η εξαγωγή PDF έτοιμων για εκτύπωση είναι δυνατότητα Pro & Max. Αναβαθμίστε για εξαγωγή.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Το πλάνο Pro περιορίζεται σε 3 ενεργές καμπάνιες. Παύστε μια άλλη καμπάνια ή αναβαθμίστε σε MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Το πλάνο Starter περιλαμβάνει έως 20 τραπέζια (Τραπέζια 1–20). Αναβαθμίστε σε PRO για απεριόριστα τραπέζια.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Το πλάνο Starter υποστηρίζει αριθμημένα τραπέζια (1–20). Τα προσαρμοσμένα ονόματα (π.χ. Βεράντα, VIP) απαιτούν PRO.',
    'Add Menu Categories & Items': 'Προσθήκη κατηγοριών & προϊόντων',
    'Add Menu Items': 'Προσθήκη προϊόντων στο μενού',
    'Organize your menu sections and food items.': 'Οργανώστε τις ενότητες του μενού και τα πιάτα σας.',
    'Add and manage your food & beverage offerings.': 'Προσθέστε και διαχειριστείτε τα φαγητά και τα ποτά σας.',
    'Customize Venue Branding': 'Προσαρμογή ταυτότητας καταστήματος',
    'Set your brand colors, logo, and presentation.': 'Ορίστε τα χρώματα της μάρκας, το λογότυπο και την εμφάνιση.',
    'Get Your QR Code & Go Live': 'Αποκτήστε το QR Code σας και ξεκινήστε',
    'Download table QR codes and preview your live menu.': 'Κατεβάστε τα QR codes τραπεζιών και δείτε ζωντανά το μενού σας.',
    'Account & Venue Created Successfully!': 'Ο λογαριασμός και το κατάστημα δημιουργήθηκαν επιτυχώς!',
    '⚡ Off-Peak Hours': '⚡ Ώρες χαμηλής κίνησης',
    '🍽️ Lunch Crowd': '🍽️ Μεσημεριανό κοινό',
    '🚀 Weekend Rush': '🚀 Σαββατοκύριακο αιχμής',
    '🌟 Seasonal Special': '🌟 Εποχιακή προσφορά',
    '📣 Engagement Buzz': '📣 Διάδοση & αλληλεπίδραση',
    'Festive & Energetic': 'Γιορτινό & δυναμικό',
    'Urgent & High-Conversion': 'Περιορισμένου χρόνου & άμεσης ανταπόκρισης',
    'Generated 3 tailored campaign strategies': 'Δημιουργήθηκαν 3 εξατομικευμένες στρατηγικές καμπάνιας',
    'Could not generate campaign ideas. Please try again.': 'Δεν ήταν δυνατή η δημιουργία ιδεών καμπάνιας. Δοκιμάστε ξανά.',
    'Venue Themes': 'Θέματα καταστήματος',
    'Choose the character of your public menu': 'Επιλέξτε τον χαρακτήρα του δημόσιου μενού σας',
    'Glassmorphism & Depth': 'Εφέ γυαλιού & βάθος',
    'Independent XML Sitemap': 'Ανεξάρτητο XML Sitemap',
    'Structured Schema & Sync': 'Δομημένα δεδομένα & συγχρονισμός',
    'Improve how your venue appears on Google and in local search results.': 'Βελτιώστε την εμφάνιση του καταστήματός σας στο Google και στα τοπικά αποτελέσματα.',
    'Terms of Service': 'Όροι χρήσης',
    'Confirm your email': 'Επιβεβαιώστε το email σας',
    'Check your spam / junk folder': 'Ελέγξτε τον φάκελο ανεπιθύμητων (spam)',
    'Commercial pre-press print production package': 'Επαγγελματικό πακέτο προεκτύπωσης',
    'Over-bleed safety margin': 'Περιθώριο ασφαλείας ξακρίσματος',
    'Crop Marks': 'Σημάδια κοπής',
    'Guillotine cutting guides': 'Οδηγοί κοπής γκιλοτίνας',
    'Global Banners': 'Γενικά banner',
    'Featured Items': 'Προτεινόμενα πιάτα',
    'AI Assistant': 'Βοηθός AI',
    'Custom Role Schemas': 'Προσαρμοσμένα σχήματα ρόλων',
    'Notification Preferences': 'Προτιμήσεις ειδοποιήσεων',
    'Preferences saved automatically': 'Οι προτιμήσεις αποθηκεύτηκαν αυτόματα',
    'Menu Extractor (PDF & Images)': 'Εξαγωγέας μενού (PDF & Εικόνες)',
    'Multi-Language Translation': 'Πολύγλωσση μετάφραση',
    'Start Menu Translation': 'Έναρξη μετάφρασης μενού',
    'Menu Items Hub': 'Κέντρο διαχείρισης προϊόντων',
    'Open Menu Items': 'Άνοιγμα προϊόντων μενού',
    'Enterprise Traceability': 'Εταιρική ιχνηλασιμότητα',
    'Full History': 'Πλήρες ιστορικό',
    'Billing & Plans': 'Χρέωση & πλάνα',
    'Subscription Governance': 'Διαχείριση συνδρομής',
    'Platform Usage': 'Χρήση πλατφόρμας',
    'Menu Page Views': 'Προβολές σελίδων μενού',
    'Enterprise Identity': 'Εταιρική ταυτότητα',
    'Access Governance': 'Έλεγχος πρόσβασης',
    'Required Fields *': 'Υποχρεωτικά πεδία *',
    'Enabled Languages': 'Ενεργοποιημένες γλώσσες',
    'Venue Info & Bio Localizations': 'Μεταφράσεις πληροφοριών & περιγραφής καταστήματος',
    'Public Address': 'Δημόσια διεύθυνση',
    'Public URL Slug': 'Δημόσιο αναγνωριστικό URL',
    'Contact Phone': 'Τηλέφωνο επικοινωνίας',
    'Business Address': 'Διεύθυνση επιχείρησης',
    'No Categories Yet': 'Δεν υπάρχουν ακόμη κατηγορίες',
    'No Custom Roles Configured': 'Δεν έχουν ρυθμιστεί προσαρμοσμένοι ρόλοι',
    'No Google reviews retrieved yet': 'Δεν έχουν ληφθεί ακόμη κριτικές Google',
    'No Guests Found': 'Δεν βρέθηκαν επισκέπτες',
    'No Menu Items Yet': 'Δεν υπάρχουν ακόμη προϊόντα στο μενού',
    'No Promotions Configured': 'Δεν έχουν διαμορφωθεί προφορές',
    'No Service Periods Yet': 'Δεν έχουν οριστεί ακόμη περίοδοι σερβιρίσματος',
    'No Support Tickets': 'Δεν υπάρχουν αιτήματα υποστήριξης',
    'No Tables on Floor Plan': 'Δεν υπάρχουν τραπέζια στην κάτοψη',
    'No Team Members Found': 'Δεν βρέθηκαν μέλη της ομάδας',
    'No Upcoming Events': 'Δεν υπάρχουν επερχόμενες εκδηλώσεις',
    'No Walk-Ins Recorded': 'Δεν έχουν καταγραφεί διερχόμενοι επισκέπτες',
    'No items are available right now': 'Δεν υπάρχουν διαθέσιμα προϊόντα αυτή τη στιγμή',
    'No notifications found': 'Δεν βρέθηκαν ειδοποιήσεις',
    'No reservations matching your filters': 'Δεν βρέθηκαν κρατήσεις που να ταιριάζουν με τα φίλτρα',
    'No tables currently occupied': 'Κανένα τραπέζι δεν είναι κατειλημμένο αυτή τη στιγμή',
    'No venue selected': 'Δεν έχει επιλεγεί κατάστημα',
    'Waitlist': 'Λίστα αναμονής',
    'Waitlist is Empty': 'Η λίστα αναμονής είναι άδεια',
    'Waves (Gluten)': 'Στάχυα (Γλουτένη)',
    'We sent a temporary verification code to': 'Στείλαμε έναν προσωρινό κωδικό επαλήθευσης στο',
    'Website URL': 'Διεύθυνση ιστοσελίδας',
    'What the customer receives': 'Τι λαμβάνει ο πελάτης',
    'White-label control is available on eligible plans.': 'Η προσαρμογή white-label είναι διαθέσιμη στα επιλέξιμα πλάνα.',
    'Why this works:': 'Γιατί λειτουργεί αυτή η προσέγγιση:',
    'WiFi Broadcast': 'Κοινή χρήση WiFi',
    'WiFi Password': 'Κωδικός WiFi',
    'Window seat, dietary request, birthday celebration...': 'Τραπέζι κοντά στο παράθυρο, διατροφικές προτιμήσεις, γενέθλια...',
    'Your Published Response': 'Η δημοσιευμένη απάντησή σας',
    'Your Venues': 'Τα καταστήματά σας',
    '🎉 Ready to publish!': '🎉 Έτοιμο για δημοσίευση!',
    'e.g. Grill, Salad, Pastry': 'π.χ. Σχάρα, Σαλάτες, Ζαχαροπλαστική',
    'e.g. Guest called, emergency': 'π.χ. Κλήση από πελάτη, έκτακτη ανάγκη',
    'e.g. Italian, Moroccan, Burgers': 'π.χ. Ιταλική, Μαροκινή, Burgers',
    'e.g. Mains, Pasta, Starters': 'π.χ. Κυρίως πιάτα, Ζυμαρικά, Ορεκτικά',
    'e.g. Signature Truffle Pasta': 'π.χ. Χαρακτηριστικά ζυμαρικά με τρούφα',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'π.χ. Απογευματινός καφές, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'π.χ. Πρωινό, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'π.χ. Ελάτε μαζί μας για ζωντανή τζαζ κάθε Παρασκευή στις 20:00!',
  },
  hi: {
    '123 Main St, City': '१२३ मेन स्ट्रीट, शहर',
    'Balance': 'पॉइंट शेष',
    'Lifetime': 'आजीवन कुल',
    'Source': 'स्रोत',
    'Menu': 'मेनू',
    'Advanced': 'उन्नत',
    'Core Suite': 'कोर सूट',
    'Privacy': 'गोपनीयता',
    'Support': 'सहायता',
    'Save Layout': 'लेआउट सहेजें',
    'Shape:': 'आकार:',
    'SEARCH LIBRARY...': 'लाइब्रेरी में खोजें...',
    'Search library...': 'लाइब्रेरी में खोजें...',
    'None': 'कोई नहीं',
    'Type': 'प्रकार',
    'Target': 'लक्ष्य',
    'Value / Target': 'मान / लक्ष्य',
    'London (GMT/BST)': 'लंदन (GMT/BST)',
    'New York (EST/EDT)': 'न्यूयॉर्क (EST/EDT)',
    'Chicago (CST/CDT)': 'शिकागो (CST/CDT)',
    'Denver (MST/MDT)': 'डेनवर (MST/MDT)',
    'Dubai (GST)': 'दुबई (GST)',
    'Tokyo (JST)': 'टोक्यो (JST)',
    'Sydney (AEST/AEDT)': 'सिडनी (AEST/AEDT)',
    'Start Color': 'प्रारंभिक रंग',
    'Depth Shade': 'गहराई छाया',
    'Matrix Geometry': 'QR मैट्रिक्स ज्यामिति',
    'Eye Architecture': 'QR कोने का डिज़ाइन',
    'Brand Centerpiece': 'QR के केंद्र में लोगो',
    'Scannability Shield': 'स्कैन सुरक्षा शील्ड',
    'Environment Blur': 'वातावरण धुंधलापन',
    'Color Target:': 'रंग लक्ष्य:',
    'DPI:': 'DPI (रिज़ॉल्यूशन):',
    '3mm Bleed Box': '3mm ब्लीड बॉक्स',
    '300 DPI': '300 DPI (उच्च रिज़ॉल्यूशन)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'SAML 2.0 प्रोटोकॉल',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'IdP एंटिटी आईडी',
    'SSO URL': 'SSO लॉगिन URL',
    'X.509 Certificate': 'X.509 सुरक्षा प्रमाणपत्र',
    'ACS URL': 'ACS सेवा URL',
    'Entity ID': 'एंटिटी आईडी',
    'Navigate': 'नेविगेट करें',
    'Select': 'चुनें',
    'OmniSearch v1.0': 'ओमनीसर्च वैश्विक खोज',
    'Back to order review': 'ऑर्डर समीक्षा पर वापस जाएं',
    'Return to Intelligence Studio': 'इंटेलिजेंस स्टूडियो पर वापस जाएं',
    'View Full Notification Center': 'पूरा अधिसूचना केंद्र देखें',
    'Read all': 'सभी पढ़ें',
    'Cancel department': 'विभाग रद्द करें',
    'Save department': 'विभाग सहेजें',
    'Venue Segmentation': 'स्थान विभाजन',
    'Collaborate on your menu together.': 'अपनी टीम के साथ मिलकर अपने मेनू पर काम करें।',
    'Timeline': 'समयरेखा',
    'Calendar': 'कैलेंडर',
    'Book': 'बुक करें',
    'Duration': 'अवधि',
    'Time & Duration': 'समय और अवधि',
    'Seating Preference': 'बैठने की प्राथमिकता',
    'Section': 'अनुभाग',
    'Min Capacity': 'न्यूनतम क्षमता',
    'Max Capacity': 'अधिकतम क्षमता',
    'Phone (Optional)': 'फ़ोन (वैकल्पिक)',
    'Name or Walk-in': 'नाम या सीधे आने वाले मेहमान',
    'Next day': 'अगले दिन',
    'Next month': 'अगले महीने',
    'Live Connection': 'लाइव कनेक्शन',
    'Live Inventory': 'लाइव इन्वेंटरी',
    'Main Venue (Full Menu)': 'मुख्य स्थल (पूर्ण मेनू)',
    'Preview Menu': 'मेनू का पूर्वावलोकन करें',
    'Manual Setup Guide': 'मैन्युअल सेटअप गाइड',
    'Connected & Active': 'कनेक्टेड और सक्रिय',
    'CTA Action': 'कॉल-टू-एक्शन बटन (CTA)',
    'Phone Contact': 'संपर्क फ़ोन',
    'Menu URL': 'मेनू वेब पता',
    'Google Location': 'Google स्थान',
    'Field': 'फ़ील्ड',
    'Linked': 'लिंक किया गया',
    'Synchronizing Hub...': 'डेटा सिंक हो रहा है...',
    'Data Integrity': 'डेटा अखंडता',
    'Station Governance Suite': 'कार्य केंद्र प्रबंधन सूट',
    'Open audit log': 'ऑडिट लॉग खोलें',
    'Signal / Time': 'संकेत / समय',
    'Identity': 'पहचान',
    'Invitation Invalid': 'निमंत्रण अमान्य या समाप्त हो गया है',
    'Invited To': 'में शामिल होने के लिए आमंत्रित:',
    'Team Limit Reached': 'टीम सदस्य सीमा समाप्त हो गई है',
    'Venue Limit Reached': 'स्थान सीमा समाप्त हो गई है',
    'Venue Setup Required': 'स्थान सेटअप आवश्यक है',
    'Create Venue': 'नया स्थान बनाएं',
    'TableGreet Onboarding': 'TableGreet ऑनबोर्डिंग',
    'No payment required': 'कोई भुगतान आवश्यक नहीं है',
    'Selected Plan': 'चयनित प्लान',
    'Payment Method': 'भुगतान विधि',
    'Invoice ID': 'चालान संख्या',
    'Verified': 'सत्यापित',
    'Accent color': 'एक्सेंट रंग',
    'Set the voice and overall light level.': 'आवाज़ और समग्र प्रकाश स्तर सेट करें।',
    'Layout architecture': 'लेआउट वास्तुकला',
    'Navigation & motion': 'नेविगेशन और गति',
    'Loading preview engine': 'पूर्वावलोकन इंजन लोड हो रहा है',
    'Real Live Preview': 'वास्तविक लाइव पूर्वावलोकन',
    'Included with Pro & Max': 'Pro और Max के साथ शामिल',
    'Daypart Scheduling': 'दिन के समय का समय निर्धारण',
    'Operational Daypart Architecture': 'परिचालन समय वास्तुकला',
    'Service Protocol': 'सेवा प्रोटोकॉल',
    'Configuration Engine': 'कॉन्फ़िगरेशन इंजन',
    'Linked Department': 'जुड़ा हुआ विभाग',
    'Team Hub': 'टीम हब',
    'Team Size': 'टीम का आकार',
    'Joined': 'शामिल हुए',
    'Cancel Invitation': 'निमंत्रण रद्द करें',
    'Initialize New Organization': 'नया संगठन प्रारंभ करें',
    'Operational': 'परिचालन में',
    'Pro Feature': 'Pro सुविधा',
    'Current State': 'वर्तमान स्थिति',
    'Open on the big TV': 'बड़ी TV स्क्रीन पर खोलें',
    'Menu Board': 'डिजिटल मेनू बोर्ड',
    'Reward': 'इनाम',
    'Reward name': 'इनाम का नाम',
    'Cancel & refund': 'रद्द करें और धनवापसी करें',
    'Code': 'कोड',
    'Find': 'खोजें',
    'Complete': 'पूर्ण',
    'Seated': 'बैठे हुए',
    'No-Show': 'अनुपस्थित',
    'Walk-in': 'सीधे प्रवेश',
    'Party': 'मेहमान संख्या',
    'Turnover': 'टेबल टर्नओवर',
    'We couldn\'t load the menu': 'मेनू लोड नहीं किया जा सका',
    'Venue Not Found': 'स्थान नहीं मिला',
    'Live Availability': 'लाइव उपलब्धता',
    'Multi-Language Suite': 'बहु-भाषा सूट',
    'Key name': 'कुंजी का नाम',
    'Revoke API key': 'API कुंजी निरस्त करें',
    'Delete webhook': 'वेबहुक हटाएं',
    'Identity Provider': 'पहचान प्रदाता (IdP)',
    'Provider': 'प्रदाता',
    'Mandatory MFA': 'अनिवार्य बहु-कारक प्रमाणीकरण (MFA)',
    'Account Identity': 'खाता पहचान',
    'Login Email': 'लॉगिन ईमेल',
    'New Login Email': 'नया लॉगिन ईमेल',
    'Phone Login': 'फ़ोन लॉगिन',
    'SMS Verification Code': 'SMS सत्यापन कोड',
    'Integration Method': 'एकीकरण विधि',
    'Stripe Connect': 'Stripe Connect भुगतान',
    'Stripe Secret Key': 'Stripe सीक्रेट कुंजी',
    'Stripe Webhook Secret': 'Stripe वेबहुक सीक्रेट',
    'Client-Owned': 'ग्राहक के स्वामित्व में',
    'Verify connection': 'कनेक्शन सत्यापित करें',
    'Setup Health': 'सेटअप स्थिति',
    'Privacy Protected': 'गोपनीयता सुरक्षित',
    'Privacy Policy': 'गोपनीयता नीति',
    'Continue': 'जारी रखें',
    'Sign In': 'साइन इन करें',
    'Didn\'t get it?': 'क्या आपको कोड नहीं मिला?',
    'Logged In Account': 'लॉग-इन खाता',
    'Annual': 'वार्षिक',
    'Unread': 'अपठित',
    'Starred': 'तारांकित',
    'High / Urgent': 'उच्च / तत्काल',
    'Mark Read': 'पढ़ा हुआ चिह्नित करें',
    'Delete notification': 'अधिसूचना हटाएं',
    'Urgent': 'तत्काल',
    'High': 'उच्च',
    'Normal': 'सामान्य',
    'Low': 'निम्न',
    'Select a Ticket': 'एक टिकट चुनें',
    'Urgent (Immediate attention)': 'तत्काल (तत्काल ध्यान आवश्यक)',
    'Type your reply here...': 'अपना उत्तर यहाँ लिखें...',
    'Print Card (PDF)': 'टेबल कार्ड प्रिंट करें (PDF)',
    'Advanced Printing Engine': 'उन्नत प्रिंटिंग इंजन',
    'Multi-Venue Governance': 'बहु-स्थान प्रबंधन',
    'Network Revenue': 'नेटवर्क राजस्व',
    'Live Network Total': 'लाइव नेटवर्क कुल',
    'Audit Log Hub': 'ऑडिट लॉग हब',
    'Export Log': 'लॉग निर्यात करें',
    'CUSTOM ROLE SCHEMAS': 'कस्टम भूमिका योजनाएं',
    'Unique internal role identifier.': 'अद्वितीय आंतरिक भूमिका पहचानकर्ता।',
    'Human-friendly role label.': 'टीम के लिए स्पष्ट भूमिका शीर्षक।',
    'Marketing Hub': 'मार्केटिंग हब',
    'Revenue Generation Suite': 'राजस्व वृद्धि सूट',
    'Active Campaign Limit Reached (3/3)': 'सक्रिय अभियान सीमा समाप्त (3/3)',
    'Banner Content': 'बैनर सामग्री',
    'Style Profile': 'शैली प्रोफ़ाइल',
    'Action URL (Optional)': 'कार्रवाई लिंक (वैकल्पिक)',
    'Promotion Label': 'प्रचार लेबल',
    'Target Selection': 'लक्ष्य चयन',
    'Edit Promotion': 'प्रचार संपादित करें',
    'Delete Promotion': 'प्रचार हटाएं',
    'Loyalty Engine': 'ग्राहक वफादारी कार्यक्रम',
    'Reward catalog': 'इनाम सूची',
    'Add reward': 'इनाम जोड़ें',
    'Loyalty ID': 'सदस्यता आईडी',
    'No point activity yet': 'अभी तक कोई पॉइंट गतिविधि नहीं है',
    'Scan or enter QR value': 'QR स्कैन करें या मान दर्ज करें',
    'TableGreet Admin': 'TableGreet व्यवस्थापक',
    'Capacity Pacing': 'क्षमता गति नियंत्रण',
    'Cancellation Policy': 'रद्दीकरण नीति',
    'Booking Experience': 'बुकिंग अनुभव',
    'Floor Occupancy': 'हॉल अधिभोग',
    'Currently Seated': 'वर्तमान में बैठे हुए',
    'Seat Party': 'मेहमानों को बैठाएं',
    'Seat Table': 'टेबल असाइन करें',
    'Select Table': 'टेबल चुनें',
    'Table / Capacity': 'टेबल / क्षमता',
    'Table Name/Number': 'टेबल का नाम/नंबर',
    'Mark Arrived': 'आगमन दर्ज करें',
    'Mark No-Show': 'अनुपस्थित चिह्नित करें',
    'Complete Dining': 'भोजन संपन्न करें',
    'Standard Dining (Default)': 'मानक भोजन (डिफ़ॉल्ट)',
    'Walk-In Dining Log': 'बिना आरक्षण वाले मेहमानों का लॉग',
    'Notification Center': 'अधिसूचना केंद्र',
    'Single Sign-On': 'सिंगल साइन-ऑन (SSO)',
    'Welcome Aboard!': 'TableGreet में आपका स्वागत है!',
    'Select your TableGreet plan': 'अपना TableGreet प्लान चुनें',
    'Free Plan Activation': 'मुफ्त प्लान सक्रियण',
    'Venue Created!': 'स्थान सफलतापूर्वक बनाया गया!',
    'Upgrade for Unlimited': 'असीमित के लिए अपग्रेड करें',
    'Auto-Tag Menu': 'मेनू को स्वचालित रूप से टैग करें',
    'Start Auto-Tagging': 'स्वचालित टैगिंग शुरू करें',
    'Launch Auto-Tagger': 'ऑटो-टैगर लॉन्च करें',
    'Launch PDF Importer': 'PDF मेनू आयातक लॉन्च करें',
    'Analyzing Menu...': 'मेनू का विश्लेषण किया जा रहा है...',
    'Analyzing Recipe...': 'रेसिपी का विश्लेषण किया जा रहा है...',
    'Manual Availability': 'मैन्युअल उपलब्धता',
    'Technical Breakdown': 'तकनीकी विवरण',
    'Dietary Taxonomy': 'आहार और एलर्जेन वर्गीकरण',
    'Dietary & Allergen AI': 'आहार और एलर्जेन AI',
    'Inherit from Category': 'श्रेणी से प्राप्त करें',
    'Ingredient Library': 'सामग्री लाइब्रेरी',
    'Ingredient Name': 'सामग्री का नाम',
    'Menu Organization': 'मेनू संगठन',
    'Inventory Taxonomy Hub': 'इन्वेंट्री वर्गीकरण केंद्र',
    'Category Intelligence': 'श्रेणी विश्लेषण',
    'Service Period': 'सेवा अवधि',
    'Manual Configuration / All Day': 'मैन्युअल कॉन्फ़िगरेशन / पूरे दिन',
    'Operational Department': 'परिचालन विभाग',
    'Global / No Department': 'वैश्विक / कोई विभाग नहीं',
    'Cancel category': 'श्रेणी रद्द करें',
    'New category name...': 'नया श्रेणी नाम...',
    'Save category': 'श्रेणी सहेजें',
    'QR Matrix Only': 'केवल QR कोड',
    'Vector & PDF': 'वेक्टर और PDF',
    'Vector QR (SVG)': 'वेक्टर QR कोड (SVG)',
    'Vector QR (PDF)': 'प्रिंट-रेडी QR कोड (PDF)',
    'Save QR Style': 'QR शैली सहेजें',
    'Share Link': 'लिंक साझा करें',
    'High Fidelity 300DPI': 'उच्च गुणवत्ता 300 DPI प्रिंट',
    'Generating 300 DPI PDF...': '300 DPI PDF तैयार किया जा रहा है...',
    'Give your venue a point of view.': 'अपने स्थल को एक विशिष्ट दृश्य पहचान दें।',
    'Signage Menu Not Found': 'डिजिटल साइनेज मेनू नहीं मिला',
    'Scan Menu': 'मेनू स्कैन करें',
    'Open on your phone': 'अपने फ़ोन पर खोलें',
    'Full Screen': 'फुल स्क्रीन',
    'Lock TV board': 'TV स्क्रीन लॉक करें',
    'Big-Screen Menu': 'बड़ी स्क्रीन वाला मेनू',
    'TV Signage': 'TV साइनेज',
    'General Information': 'सामान्य जानकारी',
    'Venue Name *': 'स्थान का नाम *',
    'Public Slug *': 'सार्वजनिक वेब पहचान *',
    'Venue Logo': 'स्थान का लोगो',
    'Hero Cover Image': 'मुख्य कवर फोटो',
    'Venue Timezone': 'स्थान का समय क्षेत्र',
    'Primary Brand Color': 'ब्रांड का मुख्य रंग',
    'TableGreet footer credit': 'फुटर में TableGreet क्रेडिट',
    'White-Label Branding': 'व्हाइट-लेबल ब्रांडिंग',
    'Client DNS Setup': 'डोमेन DNS सेटअप',
    'Copy CNAME target': 'CNAME लक्ष्य कॉपी करें',
    'Copy TableGreet URL': 'TableGreet लिंक कॉपी करें',
    'Included TableGreet URL': 'शामिल TableGreet वेब लिंक',
    'Open TableGreet URL': 'TableGreet लिंक खोलें',
    'Public menu': 'ग्राहकों के लिए सार्वजनिक मेनू',
    'Public venue delivery': 'ऑनलाइन मेनू से होम डिलीवरी',
    'Zone name': 'क्षेत्र का नाम',
    'Coverage note': 'वितरण क्षेत्र नोट',
    'Default ETA min': 'न्यूनतम अनुमानित समय (मिनट)',
    'Default ETA max': 'अधिकतम अनुमानित समय (मिनट)',
    'Packaging fee': 'पैकेजिंग शुल्क',
    'Courier handled by': 'डिलीवरी का माध्यम',
    'Third-party courier': 'तृतीय-पक्ष कूरियर',
    'Unified Experience': 'एकीकृत अनुभव',
    'Save WiFi to Venue': 'स्थान के लिए वाई-फाई सहेजें',
    'Network Name (SSID)': 'नेटवर्क का नाम (SSID)',
    'Audio Alert Chime': 'ध्वनि चेतावनी संकेत',
    'Time Format': 'समय प्रारूप',
    'Auto / Default': 'स्वचालित / डिफ़ॉल्ट',
    'Syncing SEO & Google Data...': 'SEO और Google डेटा सिंक हो रहा है...',
    'Google Connection': 'Google कनेक्शन',
    'Search Engine Metadata': 'सर्च इंजन मेटाडेटा',
    'Social card preview': 'सोशल मीडिया शेयर पूर्वावलोकन',
    'Allow Indexing': 'इंडेक्सिंग की अनुमति दें',
    'Canonical URL Override': 'कैनोनिकल URL ओवरराइड',
    'Price Range': 'मूल्य सीमा',
    'Latitude': 'अक्षांश',
    'Longitude': 'देशांतर',
    'Google Place ID / Location ID': 'Google प्लेस आईडी / स्थान आईडी',
    'Google Search Preview': 'Google खोज पूर्वावलोकन',
    'Linked Google Location': 'जुड़ा हुआ Google स्थान',
    'SEO Documentation': 'SEO दस्तावेज़',
    ', and': ', और',
    'and': 'और',
    'Delete Department?': 'विभाग हटाएं?',
    'Delete Department': 'विभाग हटाएं',
    'Live URL': 'लाइव लिंक',
    'Copy full live URL': 'पूरा लाइव लिंक कॉपी करें',
    'Open live department menu': 'लाइव विभाग मेनू खोलें',
    'Loading departments...': 'विभाग लोड हो रहे हैं...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'बहु-विभाग स्थल विभाजन PRO और उससे ऊपर के प्लान में उपलब्ध प्रीमियम सुविधाएँ हैं।',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'कमरों, मेजों, बार या भौतिक स्थानों के आधार पर अपने मेनू को विभाजित करें।',
    'Targeted Ordering': 'लक्षित ऑर्डरिंग',
    'Route guests and orders to specific preparation areas.': 'मेहमानों और ऑर्डर को विशिष्ट तैयारी क्षेत्रों में रूट करें।',
    'Operational Departments': 'परिचालन विभाग',
    'OPERATIONAL DEPARTMENTS': 'परिचालन विभाग',
    'Venue Segmentation Hub': 'स्थान विभाजन केंद्र',
    'VENUE SEGMENTATION HUB': 'स्थान विभाजन केंद्र',
    'Create departments to group your menu sections. For example, a large venue might have a': 'अपने मेनू अनुभागों को व्यवस्थित करने के लिए विभाग बनाएं। उदाहरण के लिए, एक बड़े स्थल में',
    'Restaurant': 'रेस्तरां',
    'Poolside': 'पूलसाइड',
    'department, each with its own categories.': ', प्रत्येक की अपनी श्रेणियां हो सकती हैं।',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'विभाग आपको अपने स्थान को रेस्तरां, बार या पूलसाइड जैसे अनुभागों में विभाजित करने की अनुमति देते हैं - प्रत्येक की अपनी श्रेणियां होती हैं।',
    'Department name': 'विभाग का नाम',
    'New department name...': 'नया विभाग का नाम...',
    'No Linked Daypart (Always Available)': 'कोई लिंक्ड समय नहीं (हमेशा उपलब्ध)',
    'Linked Daypart': 'जुड़ा हुआ सेवा समय',
    'Localizations': 'स्थानीयकरण',
    'LOCALIZATIONS': 'स्थानीयकरण',
    'AI Auto-Translate': 'एआई ऑटो-अनुवाद',
    'AI AUTO-TRANSLATE': 'एआई ऑटो-अनुवाद',
    'Translated name': 'अनुवादित नाम',
    'No Departments Yet': 'अभी कोई विभाग नहीं है',
    'Add First Department': 'पहला विभाग जोड़ें',
    'Avg Order Value': 'औसत ऑर्डर मूल्य',
    'AVG ORDER VALUE': 'औसत ऑर्डर मूल्य',
    'item taps': 'व्यंजन टैप',
    'ITEM TAPS': 'व्यंजन टैप',
    'Avg Rating': 'औसत रेटिंग',
    'AVG RATING': 'औसत रेटिंग',
    'Revenue (Last 7 Days)': 'राजस्व (पिछले 7 दिन)',
    'REVENUE (LAST 7 DAYS)': 'राजस्व (पिछले 7 दिन)',
    'Daily breakdown': 'दैनिक विवरण',
    'DAILY BREAKDOWN': 'दैनिक विवरण',
    'Menu Views (Last 7 Days)': 'मेनू दृश्य (पिछले 7 दिन)',
    'MENU VIEWS (LAST 7 DAYS)': 'मेनू दृश्य (पिछले 7 दिन)',
    'Guest traffic': 'अतिथि आवागमन',
    'GUEST TRAFFIC': 'अतिथि आवागमन',
    'Most Viewed Items': 'सबसे अधिक देखे गए व्यंजन',
    'MOST VIEWED ITEMS': 'सबसे अधिक देखे गए व्यंजन',
    'Top Rated Items': 'शीर्ष रेटेड व्यंजन',
    'TOP RATED ITEMS': 'शीर्ष रेटेड व्यंजन',
    'Waiter Requests': 'वेटर अनुरोध',
    'WAITER REQUESTS': 'वेटर अनुरोध',
    'Total': 'कुल',
    'TOTAL': 'कुल',
    'Order Status (30 days)': 'ऑर्डर स्थिति (30 दिन)',
    'ORDER STATUS (30 DAYS)': 'ऑर्डर स्थिति (30 दिन)',
    'Recent Guest Feedback': 'हालिया अतिथि प्रतिक्रिया',
    'RECENT GUEST FEEDBACK': 'हालिया अतिथि प्रतिक्रिया',
    'Conversion Rate': 'रूपांतरण दर',
    'CONVERSION RATE': 'रूपांतरण दर',
    'of menu visitors placed an order': 'मेनू दर्शकों ने ऑर्डर दिया',
    'Refreshed': 'अपडेट किया गया',
    'REFRESHED': 'अपडेट किया गया',
    'Last 30 days': 'पिछले 30 दिन',
    'LAST 30 DAYS': 'पिछले 30 दिन',
    'cash': 'नकद',
    'Cash': 'नकद',
    'CASH': 'नकद',
    'bill': 'बिल',
    'Bill': 'बिल',
    'BILL': 'बिल',
    'water': 'पानी',
    'Water': 'पानी',
    'WATER': 'पानी',
    'service': 'सेवा',
    'Service': 'सेवा',
    'SERVICE': 'सेवा',
    'No interaction data yet': 'अभी तक कोई इंटरैक्शन डेटा नहीं है',
    'No ratings collected yet': 'अभी तक कोई रेटिंग एकत्र नहीं की गई है',
    'No requests in period': 'इस अवधि में कोई अनुरोध नहीं है',
    'No feedback collected yet': 'अभी तक कोई प्रतिक्रिया प्राप्त नहीं हुई है',
    'No menu views recorded yet': 'अभी तक कोई मेनू दृश्य दर्ज नहीं किया गया है',
    'Dish Rating Breakdown': 'व्यंजन रेटिंग विश्लेषण',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'व्यंजन रैंकिंग और संतुष्टि मेट्रिक्स विशेष रूप से MAX और Enterprise योजनाओं पर उपलब्ध हैं।',
    'Customer Sentiment & Reviews': 'ग्राहक भावनाएं और समीक्षाएं',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'प्रत्यक्ष अतिथि समीक्षाओं और भावना विश्लेषण के लिए MAX या Enterprise सदस्यता आवश्यक है।',
    'Reviews AI': 'समीक्षा एआई',
    'Guest Feedback': 'अतिथि प्रतिक्रिया',
    'Upgrade to unlock rating analytics →': 'रेटिंग विश्लेषण अनलॉक करने के लिए अपग्रेड करें →',
    'Upgrade to Max': 'मैक्स में अपग्रेड करें',
    'UPGRADE TO MAX': 'मैक्स में अपग्रेड करें',
    'MAX PLAN': 'मैक्स प्लान',
    'No Data Yet': 'अभी कोई डेटा नहीं है',
    'Share your QR code to start collecting real venue insights.': 'वास्तविक इनसाइट एकत्र करने के लिए अपना क्यूआर कोड साझा करें।',
    'Revenue Analytics': 'राजस्व विश्लेषण',
    'REVENUE ANALYTICS': 'राजस्व विश्लेषण',
    'Track your earnings, orders, and average ticket values over time.': 'समय के साथ अपनी कमाई, ऑर्डर और औसत टिकट मूल्य को ट्रैक करें।',
    'Operational Velocity': 'परिचालन गति',
    'OPERATIONAL VELOCITY': 'परिचालन गति',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'अतिथि आवागमन, लोकप्रिय व्यंजन और वेटर सेवा भार की निगरानी करें।',
    'Upgrade to Pro': 'प्रो में अपग्रेड करें',
    'UPGRADE TO PRO': 'प्रो में अपग्रेड करें',
    'Loading Insights…': 'इनसाइट लोड हो रहा है…',
    'Loading Insights...': 'इनसाइट लोड हो रहा है...',
    'Telemetry Unavailable': 'गतिविधि डेटा उपलब्ध नहीं है',
    'TELEMETRY UNAVAILABLE': 'गतिविधि डेटा उपलब्ध नहीं है',
    'No Data Captured': 'कोई डेटा दर्ज नहीं किया गया',
    'NO DATA CAPTURED': 'कोई डेटा दर्ज नहीं किया गया',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'रीयल-टाइम में अतिथि आंकड़े और ऑर्डर एकत्र करने के लिए अपना क्यूआर कोड साझा करें।',
    'INTELLIGENCE STUDIO': 'एनालिटिक्स स्टूडियो',
    'Insights Engine Locked': 'उन्नत विश्लेषण बंद है',
    'INSIGHTS ENGINE LOCKED': 'उन्नत विश्लेषण बंद है',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'उन्नत रुझान विश्लेषण, बल्क प्रबंधन और डेटा निर्यात विशेष रूप से उपलब्ध हैं',
    'Unlock Pro Features': 'प्रो सुविधाएं अनलॉक करें',
    'UNLOCK PRO FEATURES': 'प्रो सुविधाएं अनलॉक करें',
    'MENU VIEWS': 'मेनू दृश्य',
    'ITEM INTERACTIONS': 'व्यंजन इंटरैक्शन',
    'GUEST RATING': 'अतिथि रेटिंग',
    'DELIVERY REVENUE': 'वितरण राजस्व',
    'AVERAGE DELIVERY ORDER': 'औसत वितरण ऑर्डर',
    'AVERAGE DELIVERY FEE': 'औसत वितरण शुल्क',
    'INCLUDING FEES': 'शुल्क सहित',
    'ZONE AVERAGE': 'क्षेत्र का औसत',
    'TRAFFIC PROJECTIONS': 'अतिथि यातायात अनुमान',
    'TELEMETRY SIGNAL TRACKING': 'गतिविधि सिग्नल ट्रैकिंग',
    'VEGETARIAN': 'शाकाहारी',
    'VEGAN': 'वीगन',
    'SPICY': 'मसालेदार',
    'GLUTEN-FREE': 'ग्लूटेन-मुक्त',
    'Reset: All In-Stock': 'रीसेट: सभी उपलब्ध हैं',
    'Emergency Stop (All Out)': 'आपातकालीन रोक: सभी समाप्त',
    'No compliance issues': 'कोई समस्या नहीं पाई गई',
    'NO COMPLIANCE ISSUES': 'कोई समस्या नहीं पाई गई',
    'detected in current menu': 'वर्तमान मेनू में',
    'DETECTED IN CURRENT MENU': 'वर्तमान मेनू में',
    'Telemetry History (24h)': 'गतिविधि इतिहास (24 घंटे)',
    'TELEMETRY HISTORY (24H)': 'गतिविधि इतिहास (24 घंटे)',
    'Telemetry History (30d)': 'गतिविधि इतिहास (30 दिन)',
    'TELEMETRY HISTORY (30D)': 'गतिविधि इतिहास (30 दिन)',
    'Save': 'सहेजें',
    'Saved': 'सहेजा गया',
    'Saving...': 'सहेजा जा रहा है...',
    'Save Changes': 'बदलाव सहेजें',
    'Save Settings': 'सेटिंग्स सहेजें',
    'Cancel': 'रद्द करें',
    'Close': 'बंद करें',
    'Create': 'बनाएं',
    'Add': 'जोड़ें',
    'Edit': 'संपादित करें',
    'Delete': 'हटाएं',
    'Remove': 'हटाएं',
    'Update': 'अपडेट करें',
    'Refresh': 'ताज़ा करें',
    'Search': 'खोजें',
    'Search...': 'खोजें...',
    'Search items...': 'आइटम खोजें...',
    'Search categories...': 'श्रेणियां खोजें...',
    'Filter': 'फ़िल्टर करें',
    'All': 'सभी',
    'Export': 'निर्यात करें',
    'Import': 'आयात करें',
    'Download': 'डाउनलोड करें',
    'Upload': 'अपलोड करें',
    'Copy': 'कॉपी करें',
    'Copied': 'कॉपी हो गया',
    'Copied!': 'कॉपी हो गया!',
    'Preview': 'पूर्वावलोकन',
    'Open': 'खुला है',
    'View': 'देखें',
    'Manage': 'प्रबंधित करें',
    'Configure': 'कॉन्फ़िगर करें',
    'Enable': 'सक्षम करें',
    'Disable': 'अक्षम करें',
    'Enabled': 'सक्षम',
    'Disabled': 'अक्षम',
    'Active': 'सक्रिय',
    'Inactive': 'निष्क्रिय',
    'Available': 'उपलब्ध',
    'Unavailable': 'अनुपलब्ध',
    'Visible': 'दृश्यमान',
    'Hidden': 'छिपा हुआ',
    'Required': 'आवश्यक',
    'Optional': 'वैकल्पिक',
    'Confirm': 'पुष्टि करें',
    'Back': 'वापस',
    'Next': 'अगला',
    'Done': 'हो गया',
    'Upgrade': 'अपग्रेड करें',
    'Sign Out': 'लॉग आउट',
    'Exit to Main Admin': 'मुख्य एडमिन पर वापस जाएं',
    'Change Language': 'भाषा बदलें',
    'Add Category': 'श्रेणी जोड़ें',
    'Add Menu Item': 'मेनू आइटम जोड़ें',
    'Add Item': 'आइटम जोड़ें',
    'Add Ingredient': 'सामग्री जोड़ें',
    'Add Department': 'विभाग जोड़ें',
    'Delete Item': 'आइटम हटाएं',
    'Delete Category': 'श्रेणी हटाएं',
    'Upload Image': 'चित्र अपलोड करें',
    'Upload Photo': 'फ़ोटो अपलोड करें',
    'Select Category': 'श्रेणी चुनें',
    'Select Department': 'विभाग चुनें',
    'Add translation...': 'अनुवाद जोड़ें...',
    'Clear': 'साफ़ करें',
    'Reset': 'रीसेट करें',
    'Apply': 'लागू करें',
    'Submit': 'जमा करें',
    'Core & Operations': 'दैनिक संचालन',
    'Menu & Experience': 'मेनू और अनुभव',
    'Enterprise & Security': 'प्रबंधन और सुरक्षा',
    'System & Control': 'सिस्टम और सहायता',
    'Intelligence Studio': 'एनालिटिक्स स्टूडियो',
    'Venue Insights': 'बिक्री रिपोर्ट',
    'Reservations': 'बुकिंग सूची',
    'Floor Plan': 'हॉल का लेआउट',
    'Live Waitlist': 'प्रतीक्षा सूची',
    'Walk-Ins': 'सीधे आए ग्राहक',
    'Walk-ins': 'सीधे आए ग्राहक',
    'Departments': 'विभाग',
    'Service Hours': 'सेवा का समय',
    'Categories': 'श्रेणियां',
    'Menu Categories': 'मेनू श्रेणियां',
    'Menu Items': 'मेनू आइटम',
    'Items': 'आइटम',
    'Ingredients': 'सामग्री',
    'Loyalty Program': 'लॉयल्टी प्रोग्राम',
    'Campaign Studio': 'प्रमोशन और ऑफ़र',
    'Signage': 'टीवी डिस्प्ले',
    'QR Code': 'क्यूआर कोड',
    'Kitchen Ops': 'रसोई स्क्रीन',
    'Theme Engine': 'रंग और डिज़ाइन',
    'Governance': 'प्रशासन',
    'Audit Log': 'गतिविधि लॉग',
    'API & Webhooks': 'कनेक्शन',
    'SSO & Security': 'सुरक्षा और लॉगिन',
    'Team': 'स्टाफ टीम',
    'Billing': 'बिलिंग और प्लान',
    'SEO & Google': 'गूगल सर्च',
    'Notifications': 'सूचनाएं',
    'Support Center': 'सहायता केंद्र',
    'Settings': 'रेस्तरां सेटिंग्स',
    'Name': 'नाम',
    'Description': 'विवरण',
    'Price': 'कीमत',
    'Status': 'स्थिति',
    'Actions': 'कार्रवाई',
    'Role': 'भूमिका',
    'Email': 'ईमेल',
    'Phone': 'फोन',
    'Address': 'पता',
    'City': 'शहर',
    'Currency': 'मुद्रा',
    'Language': 'भाषा',
    'Theme': 'थीम',
    'General': 'सामान्य',
    'Total Revenue': 'कुल राजस्व',
    'Total Orders': 'कुल ऑर्डर',
    'Average Order': 'औसत ऑर्डर',
    'Active Sessions': 'सक्रिय सत्र',
    'Recent Orders': 'हाल के ऑर्डर',
    'Live Orders': 'लाइव ऑर्डर',
    'Table': 'टेबल',
    'Date': 'तारीख',
    'Time': 'समय',
    'Amount': 'राशि',
    'Customer': 'ग्राहक',
    'In Stock': 'स्टॉक में',
    'Out of Stock': 'स्टॉक ख़त्म',
    'Sold Out': 'बिक चुका है',
    'Dine In': 'रेस्तरां में',
    'Pickup': 'पिकअप',
    'Delivery': 'डिलीवरी',
    'Paid': 'भुगतान किया गया',
    'Unpaid': 'भुगतान नहीं हुआ',
    'Pending': 'लंबित',
    'Preparing': 'तैयार हो रहा है',
    'Ready': 'तैयार',
    'Completed': 'पूर्ण हुआ',
    'Cancelled': 'रद्द',
    'Loading...': 'लोड हो रहा है...',
    'No data': 'कोई डेटा नहीं',
    'No results found': 'कोई परिणाम नहीं मिला',
    'No items found': 'कोई आइटम नहीं मिला',
    'No translations defined yet': 'अभी तक कोई अनुवाद नहीं जोड़ा गया',
    'Are you sure?': 'क्या आप निश्चित हैं?',
    'This action cannot be undone.': 'इस क्रिया को पूर्ववत नहीं किया जा सकता है।',
    'Settings saved successfully!': 'सेटिंग्स सफलतापूर्वक सहेजी गईं!',
    'Administrator': 'प्रशासक',
    'Manager': 'प्रबंधक',
    'Owner': 'मालिक',
    'Chef': 'शेफ',
    'Waiter': 'वेटर',
    'Cashier': 'कैशियर',
    'Type a command or search...': 'कमांड टाइप करें या खोजें...',
    'Command Palette': 'कमांड पैलेट',
    'Demand Velocity': 'मांग की गति',
    'DEMAND VELOCITY': 'मांग की गति',
    'Top Performing Entities': 'शीर्ष प्रदर्शनकारी व्यंजन',
    'TOP PERFORMING ENTITIES': 'शीर्ष प्रदर्शनकारी व्यंजन',
    'Hourly Load Analysis': 'प्रति घंटा लोड विश्लेषण',
    'HOURLY LOAD ANALYSIS': 'प्रति घंटा लोड विश्लेषण',
    'Bulk Command Center': 'थोक कमांड केंद्र',
    'BULK COMMAND CENTER': 'थोक कमांड केंद्र',
    'Governance Scan': 'मेनू गुणवत्ता स्कैन',
    'GOVERNANCE SCAN': 'मेनू गुणवत्ता स्कैन',
    'No compliance issues detected in current menu': 'वर्तमान मेनू में कोई समस्या नहीं पाई गई',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'वर्तमान मेनू में कोई समस्या नहीं पाई गई',
    'Procedural Tagging': 'स्वचालित टैगिंग',
    'PROCEDURAL TAGGING': 'स्वचालित टैगिंग',
    'Name match...': 'नाम से खोजें...',
    '...Name match': 'नाम से खोजें...',
    'Healthy': 'स्वास्थ्यवर्धक',
    'HEALTHY': 'स्वास्थ्यवर्धक',
    'Apply Tags': 'टैग लागू करें',
    'APPLY TAGS': 'टैग लागू करें',
    'Global Price Shift': 'वैश्विक मूल्य परिवर्तन',
    'GLOBAL PRICE SHIFT': 'वैश्विक मूल्य परिवर्तन',
    'Adjust': 'समायोजित करें',
    'ADJUST': 'समायोजित करें',
    'Availability Override': 'उपलब्धता नियंत्रण',
    'AVAILABILITY OVERRIDE': 'उपलब्धता नियंत्रण',
    'Reset: ALL IN-STOCK': 'रीसेट: सभी उपलब्ध हैं',
    'RESET: ALL IN-STOCK': 'रीसेट: सभी उपलब्ध हैं',
    'EMERGENCY STOP (ALL OUT)': 'इमरजेंसी स्टॉप (सभी समाप्त)',
    'Integrity Engine': 'मेनू अखंडता इंजन',
    'INTEGRITY ENGINE': 'मेनू अखंडता इंजन',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'स्वचालित प्रणाली आपके पूरे प्रतिष्ठान में व्यंजनों की दृश्यता, मूल्य विसंगतियों और विवरण की गुणवत्ता की निगरानी करती है।',
    'Inventory Audit Export': 'इन्वेंटरी ऑडिट निर्यात',
    'INVENTORY AUDIT EXPORT': 'इन्वेंटरी ऑडिट निर्यात',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'ऑफ़लाइन ऑडिटिंग के लिए व्यापक मेनू प्रदर्शन और मेटाडेटा स्नैपशॉट बनाएं।',
    'Enterprise Data Export': 'उन्नत डेटा निर्यात',
    'ENTERPRISE DATA EXPORT': 'उन्नत डेटा निर्यात',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'मानक प्रारूपों में व्यापक प्रदर्शन ऑडिट बनाएं और डाउनलोड करें। इसमें ट्रैफ़िक प्रवाह, व्यंजन मांग और रूपांतरण शामिल हैं।',
    'JSON Snapshot': 'JSON स्नैपशॉट',
    'JSON SNAPSHOT': 'JSON स्नैपशॉट',
    'Compile CSV Report': 'CSV रिपोर्ट संकलित करें',
    'COMPILE CSV REPORT': 'CSV रिपोर्ट संकलित करें',
    'Date Segment': 'दिनांक खंड',
    'DATE SEGMENT': 'दिनांक खंड',
    'Inbound Views': 'आने वाले दृश्य',
    'INBOUND VIEWS': 'आने वाले दृश्य',
    'Asset Interactions': 'व्यंजन सहभागिता',
    'ASSET INTERACTIONS': 'व्यंजन सहभागिता',
    'Telemetry History': 'गतिविधि इतिहास',
    'TELEMETRY HISTORY': 'गतिविधि इतिहास',
    'Telemetry History (7d)': 'गतिविधि इतिहास (7 दिन)',
    'TELEMETRY HISTORY (7D)': 'गतिविधि इतिहास (7 दिन)',
    'Growth Trajectory': 'विकास प्रक्षेपवक्र',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'पिछले 7 दिनों में आपके मेनू पर बातचीत की आवृत्ति में वृद्धि का रुझान है।',
    'Deep Analysis Engine': 'गहन विश्लेषण इंजन',
    'DEEP ANALYSIS ENGINE': 'गहन विश्लेषण इंजन',
    'Asset Velocity': 'व्यंजनों का प्रदर्शन',
    'ASSET VELOCITY': 'व्यंजनों का प्रदर्शन',
    'Traffic Projections': 'आवागमन अनुमान',
    'Telemetry Signal Tracking': 'गतिविधि संकेत ट्रैकिंग',
    'Service Hour Update': 'सेवा समय अपडेट',
    'Late Night Lounge Hours': 'देर रात लाउंज समय',
    'Bar & Lounge Hours': 'बार और लाउंज समय',
    'Breakfast & Brunch Hours': 'नाश्ता और ब्रंच समय',
    'Switch Location': 'स्थान बदलें',
    'SWITCH LOCATION': 'स्थान बदलें',
    'Global Admin': 'ग्लोबल एडमिन',
    'GLOBAL ADMIN': 'ग्लोबल एडमिन',
    'Staff Access': 'कर्मचारी पहुंच',
    'STAFF ACCESS': 'कर्मचारी पहुंच',
    'Venue Overview': 'स्थान का अवलोकन',
    'VENUE OVERVIEW': 'स्थान का अवलोकन',
    'Unified Telemetry Engine': 'सक्रियता निगरानी केंद्र',
    'UNIFIED TELEMETRY ENGINE': 'सक्रियता निगरानी केंद्र',
    'Pulse': 'लाइव पल्स',
    'PULSE': 'लाइव पल्स',
    'Insights': 'व्यापार अंतर्दृष्टि',
    'INSIGHTS': 'व्यापार अंतर्दृष्टि',
    'Reports': 'रिपोर्ट',
    'REPORTS': 'रिपोर्ट',
    'GOVERNANCE': 'प्रशासन',
    'Multi-Venue': 'मल्टी-वेन्यू',
    'MULTI-VENUE': 'मल्टी-वेन्यू',
    'Menu Views': 'मेनू देखे जाने की संख्या',
    'Item Interactions': 'व्यंजनों पर प्रतिक्रिया',
    'Guest Rating': 'अतिथि रेटिंग',
    'Revenue (7d)': 'राजस्व (7 दिन)',
    'Revenue (24h)': 'राजस्व (24 घंटे)',
    'Revenue (30d)': 'राजस्व (30 दिन)',
    'Delivery orders (7d)': 'डिलीवरी ऑर्डर (7 दिन)',
    'Delivery orders (24h)': 'डिलीवरी ऑर्डर (24 घंटे)',
    'Delivery orders (30d)': 'डिलीवरी ऑर्डर (30 दिन)',
    'Delivery revenue': 'डिलीवरी राजस्व',
    'Average delivery order': 'औसत डिलीवरी ऑर्डर',
    'Average delivery fee': 'औसत डिलीवरी शुल्क',
    'Zone average': 'क्षेत्रीय औसत',
    'Including fees': 'शुल्क सहित',
    'No deliveries': 'कोई डिलीवरी नहीं',
    'No orders': 'कोई ऑर्डर नहीं',
    'No reviews': 'कोई समीक्षा नहीं',
    'Live': 'लाइव',
    'LIVE': 'लाइव',
    'reviews': 'समीक्षाएं',
    'orders': 'ऑर्डर',
    'deliveries': 'डिलीवरी',
    'of orders': 'ऑर्डर में से',
    'rate': 'दर',
    'Recent Activity': 'हाल की गतिविधि',
    'Real-time Venue Telemetry': 'स्थान की रियल-टाइम गतिविधि',
    'REAL-TIME VENUE TELEMETRY': 'स्थान की रियल-टाइम गतिविधि',
    'Rapid Pulse View': 'रैपिड पल्स व्यू',
    'Anonymous Guest': 'अनाम अतिथि',
    'scanned menu': 'मेनू स्कैन किया',
    'viewed item': 'व्यंजन देखा',
    'interacted with': 'के साथ इंटरैक्ट किया',
    'placed order': 'ऑर्डर दिया',
    'searched menu': 'मेनू में खोजा',
    'Main Landing': 'मुख्य पृष्ठ',
    'Trace': 'ट्रेस',
    'TRACE': 'ट्रेस',
    'Gaining Telemetry...': 'गतिविधि डेटा प्राप्त हो रहा है...',
    'No recent traffic detected': 'कोई हाल की गतिविधि नहीं मिली',
    'Activities will appear here as guests scan your QR code': 'जब मेहमान आपके क्यूआर कोड को स्कैन करेंगे तो गतिविधियां यहां दिखाई देंगी',
    'Analyze All Logs': 'सभी लॉग का विश्लेषण करें',
    'Just now': 'अभी-अभी',
    'less than a minute ago': 'एक मिनट से भी कम समय पहले',
    '1 minute ago': '1 मिनट पहले',
    '1 hour ago': '1 घंटा पहले',
    'about 1 hour ago': 'लगभग 1 घंटा पहले',
    '1 day ago': '1 दिन पहले',
    'DAY AGO 1': '1 दिन पहले',
    '2 days ago': '2 दिन पहले',
    'DAY AGO 2': '2 दिन पहले',
    '3 days ago': '3 दिन पहले',
    'DAY AGO 3': '3 दिन पहले',
    '4 days ago': '4 दिन पहले',
    'DAY AGO 4': '4 दिन पहले',
    '5 days ago': '5 दिन पहले',
    'DAY AGO 5': '5 दिन पहले',
    '6 days ago': '6 दिन पहले',
    'DAY AGO 6': '6 दिन पहले',
    '7 days ago': '7 दिन पहले',
    'DAY AGO 7': '7 दिन पहले',
    'about 1 month ago': 'लगभग 1 महीना पहले',
    'Monday': 'सोमवार',
    'Tuesday': 'मंगलवार',
    'Wednesday': 'बुधवार',
    'Thursday': 'गुरुवार',
    'Friday': 'शुक्रवार',
    'Saturday': 'शनिवार',
    'Sunday': 'रविवार',
    'Mon': 'सोम',
    'Tue': 'मंगल',
    'Wed': 'बुध',
    'Thu': 'गुरु',
    'Fri': 'शुक्र',
    'Sat': 'शनि',
    'Sun': 'रवि',
    'Opening Hours': 'खुलने का समय',
    'Start Time': 'शुरू होने का समय',
    'End Time': 'समाप्त होने का समय',
    'Opening Time': 'खुलने का समय',
    'Closing Time': 'बंद होने का समय',
    'All Day': 'पूरे दिन',
    'Closed': 'बंद है',
    'Active Days': 'सक्रिय दिन',
    'Active Schedule': 'सक्रिय अनुसूची',
    'Always Available': 'हमेशा उपलब्ध',
    'Always Visible': 'हमेशा दृश्यमान',
    'New Category': 'नई श्रेणी',
    'Category': 'श्रेणी',
    'Category Name': 'श्रेणी का नाम',
    'Category Description': 'श्रेणी विवरण',
    'Display Order': 'प्रदर्शन क्रम',
    'Item Name': 'व्यंजन का नाम',
    'Item Description': 'व्यंजन का विवरण',
    'Base Price': 'मूल मूल्य',
    'Preparation Time': 'तैयारी का समय',
    'Calories': 'कैलोरी',
    'Featured Item': 'खास व्यंजन',
    'Chef\'s Recommendation': 'शेफ की सिफारिश',
    'Spicy': 'तीखा',
    'Vegetarian': 'शाकाहारी',
    'Vegan': 'शुद्ध शाकाहारी',
    'Gluten-Free': 'ग्लूटेन-मुक्त',
    'Halal': 'हलाल',
    'Kosher': 'कोशर',
    'Contains Nuts': 'मेवे शामिल हैं',
    'Dairy-Free': 'डेयरी-मुक्त',
    'Allergen Exclusions': 'एलर्जी संबंधी जानकारी',
    'Table Number': 'टेबल नंबर',
    'Assigned Table': 'सौंपा गया टेबल',
    'Seats': 'सीटें',
    'Capacity': 'क्षमता',
    'Indoor': 'अंदर का हॉल',
    'Outdoor': 'बाहर का परिसर',
    'Terrace': 'टेरेस',
    'Bar': 'बार',
    'Add Table': 'टेबल जोड़ें',
    'Edit Table': 'टेबल संपादित करें',
    'Delete Table': 'टेबल हटाएं',
    'Bookings': 'बुकिंग',
    'Add to Waitlist': 'प्रतीक्षा सूची में जोड़ें',
    'Guest Name': 'अतिथि का नाम',
    'Party Size': 'व्यक्तियों की संख्या',
    'Arrival Time': 'आगमन का समय',
    'Special Requests': 'विशेष अनुरोध',
    'Confirmed': 'पुष्टि हो गई',
    'No Show': 'उपस्थित नहीं हुए',
    'Add Staff Member': 'कर्मचारी जोड़ें',
    'Invite Staff': 'कर्मचारी को आमंत्रित करें',
    'Email Address': 'ईमेल पता',
    'Phone Number': 'फ़ोन नंबर',
    'Download QR Code': 'क्यूआर कोड डाउनलोड करें',
    'Print Table Stands': 'टेबल स्टैंड प्रिंट करें',
    'Today': 'आज',
    'Yesterday': 'कल (बीता हुआ)',
    'This Week': 'इस सप्ताह',
    'This Month': 'इस महीने',
    'Last 30 Days': 'पिछले 30 दिन',
    'Access Denied': 'पहुंच अस्वीकृत',
    'Advanced Settings': 'उन्नत सेटिंग्स',
    'Change Image': 'चित्र बदलें',
    'Remove Image': 'चित्र हटाएं',
    'Announcement Banner': 'सूचना बैनर',
    'Please fill in all required fields.': 'कृपया सभी आवश्यक फ़ील्ड भरें।',
    'Changes saved successfully': 'परिवर्तन सफलतापूर्वक सहेजे गए',
    '(You)': '(आप)',
    'AI Auto-Tag': 'AI ऑटो-टैग',
    'Analyzing:': 'विश्लेषण हो रहा है:',
    'Apple (Fruit/Vegan)': 'सेब (फल/शाकाहारी)',
    'Cancel editing': 'संपादन रद्द करें',
    'Capacity:': 'क्षमता:',
    'Carrot (Veggie)': 'गाजर (सब्जी)',
    'CircleDot (Organic)': 'वृत्त बिंदु (जैविक)',
    'Coffee (Caffeine)': 'कॉफ़ी (कैफीन)',
    'Configuration': 'विन्यास',
    'Copy verification record name': 'सत्यापन रिकॉर्ड का नाम कॉपी करें',
    'Copy verification record value': 'सत्यापन रिकॉर्ड का मान कॉपी करें',
    'Critical exception intercepted': 'गंभीर त्रुटि पकड़ी गई',
    'Kernel Panic': 'सिस्टम त्रुटि',
    'DNA (Organic/Bio)': 'डीएनए (जैविक)',
    'Egg (Ovo/Allergen)': 'अंडा (एलर्जी)',
    'Empty': 'खाली',
    'Engraving...': 'निर्माण हो रहा है...',
    'Flame (Spicy)': 'ज्वाला (मसालेदार)',
    'Free': 'मुफ्त',
    'From': 'से',
    'Generating': 'उत्पादन',
    'Governance & Traceability Studio': 'प्रबंधन और गतिविधि केंद्र',
    'Greet': 'स्वागत',
    'Heart (Healthy)': 'दिल (स्वस्थ)',
    'In-App': 'ऐप में',
    'Infinity SVG': 'वेक्टर SVG (असीमित)',
    'Leaf (Default)': 'पत्ती (डिफ़ॉल्ट)',
    'MAX Feature': 'एंटरप्राइज़ सुविधा',
    'Milk (Dairy)': 'दूध (डेयरी)',
    'Network': 'नेटवर्क',
    'Notify SMS': 'एसएमएस सूचना',
    'Order': 'ऑर्डर',
    'Original': 'मूल',
    'Overview': 'अवलोकन',
    'PRO: 4 PREDEFINED ROLES': 'प्रो: 4 मानक भूमिकाएं',
    'Shield (Safety)': 'ढाल (सुरक्षा)',
    'Soup (Warm/Soup)': 'सूप (गरम/सूप)',
    'Standard Pro View': 'मानक दृश्य',
    'Star': 'तारा',
    'Synchronized with Venue Identity': 'आपके प्रतिष्ठान की ब्रांड शैली के अनुरूप',
    'Synchronizing Security Context...': 'सुरक्षित कनेक्शन स्थापित हो रहा है...',
    'Targeted Timing': 'सेवा समय सारिणी',
    'Targeting': 'लक्षित श्रेणियां',
    'To': 'तक',
    'Toggle Navigation': 'नेविगेशन बदलें',
    'Venue': 'प्रतिष्ठान',
    'Venue Governance Suite': 'प्रतिष्ठान प्रबंधन और सेटिंग्स',
    'Volume:': 'ध्वनि:',
    'WHITEBOARD NOTE...': 'टीम के लिए नोट...',
    'Warning (Alert)': 'चेतावनी (अलर्ट)',
    'Wheat (Flour/Gluten)': 'गेहूं (आटा/ग्लूटेन)',
    'Wine (Alcohol)': 'वाइन (अल्कोहल)',
    'YouTube/Vimeo/Direct Link': 'वीडियो लिंक (YouTube, Vimeo या MP4)',
    'Your name': 'आपका नाम',
    '2. Table Number': '2. मेज़ नंबर',
    'for Morocco or': 'मोरक्को के लिए, या',
    'in': 'में',
    'to': 'से',
    'e.g. 12 or Patio-1': 'उदा. 12 या आँगन-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'उदा. हस्तनिर्मित फेटुचिनी, ब्लैक विंटर ट्रफल, 36 महीने पुरानी परमेसन...',
    'e.g. Head Sommelier & Wine Lead': 'उदा. मुख्य सोम्मेलियर',
    'e.g. London': 'उदा. लंदन',
    'e.g. SOMMELIER, HOST, BARISTA': 'उदा. सोम्मेलियर, होस्ट, बरिस्ता',
    'e.g. Skyline Lounge': 'उदा. स्काईलाइन लाउंज',
    'e.g. TRUFFLE OIL': 'उदा. ट्रफल तेल',
    'e.g. The Grand Cafe': 'उदा. द ग्रैंड कैफे',
    'e.g. Window preference, high chair': 'उदा. खिड़की के पास, बच्चों की कुर्सी',
    'Copied to clipboard!': 'क्लिपबोर्ड पर कॉपी किया गया!',
    'Copy failed.': 'कॉपी विफल रही।',
    'Link copied!': 'लिंक कॉपी किया गया!',
    'Logo uploaded': 'लोगो अपलोड हो गया',
    'Cover image uploaded': 'कवर छवि अपलोड हो गई',
    'Profile saved and synced successfully': 'प्रोफ़ाइल सफलतापूर्वक सहेजी और सिंक की गई',
    'Category Created': 'श्रेणी बनाई गई',
    'Category Updated': 'श्रेणी अपडेट की गई',
    'Category Deleted': 'श्रेणी हटा दी गई',
    'Item Created': 'आइटम बनाया गया',
    'Item Updated': 'आइटम अपडेट किया गया',
    'Item Deleted': 'आइटम हटा दिया गया',
    'Menu Published': 'मेनू प्रकाशित हुआ',
    'Delete Category?': 'क्या श्रेणी हटाना चाहते हैं?',
    'Delete Menu Item?': 'क्या यह आइटम हटाना चाहते हैं?',
    'Delete Ingredient?': 'क्या यह सामग्री हटाना चाहते हैं?',
    'Delete Service Period?': 'क्या सेवा अवधि हटाना चाहते हैं?',
    'Remove team member?': 'क्या टीम के इस सदस्य को हटाना चाहते हैं?',
    'Cancel invitation?': 'क्या निमंत्रण रद्द करना चाहते हैं?',
    'Revoke API key?': 'क्या API कुंजी रद्द करना चाहते हैं?',
    'Delete webhook endpoint?': 'क्या वेबहुक एंडपॉइंट हटाना चाहते हैं?',
    'Clear all notifications?': 'क्या सभी सूचनाएं साफ़ करना चाहते हैं?',
    'Disconnect Stripe account?': 'क्या स्ट्राइप खाता डिस्कनेक्ट करना चाहते हैं?',
    'Disconnect Google Business Profile?': 'क्या Google Business प्रोफ़ाइल डिस्कनेक्ट करना चाहते हैं?',
    'Campaigns published successfully!': 'अभियान सफलतापूर्वक प्रकाशित हुए!',
    'Changes successfully synchronized with Google Business Profile!': 'बदलाव Google Business प्रोफ़ाइल के साथ सफलतापूर्वक सिंक हो गए!',
    'Google Business Profile successfully connected!': 'Google Business प्रोफ़ाइल सफलतापूर्वक कनेक्ट हो गई!',
    'Google Business Profile disconnected.': 'Google Business प्रोफ़ाइल डिस्कनेक्ट हो गई।',
    'Google Post draft saved successfully!': 'Google पोस्ट ड्राफ्ट सफलतापूर्वक सहेजा गया!',
    'Post draft deleted.': 'पोस्ट ड्राफ्ट हटा दिया गया।',
    'Review response published successfully!': 'समीक्षा का उत्तर सफलतापूर्वक प्रकाशित हुआ!',
    'SEO configuration saved successfully!': 'SEO विन्यास सफलतापूर्वक सहेजा गया!',
    'SEO metadata translated across all languages!': 'सभी भाषाओं में SEO मेटाडेटा अनुवादित हुआ!',
    'Venue profile & bio translated across all languages!': 'प्रतिष्ठान प्रोफ़ाइल और परिचय सभी भाषाओं में अनुवादित हुआ!',
    'Reservation configuration saved successfully': 'आरक्षण विन्यास सफलतापूर्वक सहेजा गया',
    'Stripe settings saved successfully.': 'स्ट्राइप सेटिंग्स सफलतापूर्वक सहेजी गईं।',
    'Print-Ready PDF downloaded successfully': 'प्रिंट-रेडी पीडीएफ सफलतापूर्वक डाउनलोड हुआ',
    'Vector QR PDF downloaded successfully': 'वेक्टर क्यूआर पीडीएफ सफलतापूर्वक डाउनलोड हुआ',
    'Enterprise 300 DPI Pre-Press Package generated successfully': '300 DPI प्री-प्रेस पैकेज सफलतापूर्वक तैयार हुआ',
    'At least one language must be enabled.': 'कम से कम एक भाषा सक्षम होनी चाहिए।',
    'Enter a non-zero percentage before adjusting prices.': 'कीमतें समायोजित करने से पहले शून्य से भिन्न प्रतिशत दर्ज करें।',
    'Please enter a Venue Name before translating.': 'अनुवाद करने से पहले कृपया प्रतिष्ठान का नाम दर्ज करें।',
    'Please enter an item name before running dietary and allergen analysis.': 'एलर्जी और आहार विश्लेषण चलाने से पहले कृपया आइटम का नाम दर्ज करें।',
    'Please write a reply before submitting.': 'जमा करने से पहले कृपया एक उत्तर लिखें।',
    'Table 3 Called Waiter': 'टेबल 3 ने वेटर को बुलाया',
    'Table Call Bell': 'टेबल कॉल बेल',
    'Waiter Calls': 'वेटर कॉल्स',
    'Waiter Dispatch & Calls': 'वेटर डिस्पैच और कॉल',
    'Kitchen Display & Whiteboard': 'रसोई डिस्प्ले और व्हाइटबोर्ड',
    'Customer Orders': 'ग्राहक आदेश',
    'Bakery': 'बेकरी',
    'Bar / Pub': 'बार / पब',
    'Café / Coffee Shop': 'कैफे / कॉफी शॉप',
    'Fast Food': 'फास्ट फूड',
    'Gourmet / Fine Dining': 'गورमे / फाइन डाइनिंग',
    'Hotel': 'होटल',
    'General Local Business': 'सामान्य स्थानीय व्यवसाय',
    'Classic': 'क्लासिक',
    'Clean': 'स्वच्छ',
    'Modern': 'आधुनिक',
    'Elegant': 'सुरुचिपूर्ण',
    'Energetic': 'ऊर्जावान',
    'Glass': 'ग्लास',
    'Playful & Casual': 'आरामदेह और चंचल',
    'Sophisticated & Gourmet': 'परिष्कृत और लजीज',
    'Warm & Welcoming': 'गर्मजोशी भरा और स्वागतकारी',
    'Executive': 'कार्यकारी',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'अपग्रेड आवश्यक: बहुभाषी मेनू प्रो प्लान पर उपलब्ध हैं।',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'अपग्रेड आवश्यक: स्टार्टर प्लान पर ऑर्डरिंग और वेटर कॉल उपलब्ध नहीं हैं।',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'वेक्टर एसवीजी निर्यात एक प्रो और मैक्स सुविधा है। कृपया अपग्रेड करें।',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'प्रिंट-रेडी पीडीएफ निर्यात एक प्रो और मैक्स सुविधा है। कृपया अपग्रेड करें।',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'प्रो प्लान 3 सक्रिय अभियानों तक सीमित है। किसी अन्य अभियान को रोकें या असीमित के लिए MAX में अपग्रेड करें।',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'स्टार्टर प्लान में 20 टेबल तक शामिल हैं (टेबल 1-20)। असीमित टेबल के लिए PRO में अपग्रेड करें।',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'स्टार्टर प्लान संख्यात्मक टेबल (1-20) का समर्थन करता है। कस्टम नाम (उदा. आँगन, वीआईपी) के लिए PRO आवश्यक है।',
    'Add Menu Categories & Items': 'मेनू श्रेणियां और आइटम जोड़ें',
    'Add Menu Items': 'मेनू आइटम जोड़ें',
    'Organize your menu sections and food items.': 'अपने मेनू अनुभागों और खाद्य पदार्थों को व्यवस्थित करें।',
    'Add and manage your food & beverage offerings.': 'अपने भोजन और पेय पदार्थों को जोड़ें और प्रबंधित करें।',
    'Customize Venue Branding': 'प्रतिष्ठान की ब्रांडिंग अनुकूलित करें',
    'Set your brand colors, logo, and presentation.': 'अपने ब्रांड के रंग, लोगो और प्रस्तुति शैली सेट करें।',
    'Get Your QR Code & Go Live': 'अपना क्यूआर कोड प्राप्त करें और लाइव हों',
    'Download table QR codes and preview your live menu.': 'टेबल क्यूआर कोड डाउनलोड करें और अपने लाइव मेनू का पूर्वावलोकन करें।',
    'Account & Venue Created Successfully!': 'खाता और प्रतिष्ठान सफलतापूर्वक बनाए गए!',
    '⚡ Off-Peak Hours': '⚡ गैर-पीक घंटे',
    '🍽️ Lunch Crowd': '🍽️ दोपहर का भोजन भीड़',
    '🚀 Weekend Rush': '🚀 सप्ताहांत की भीड़',
    '🌟 Seasonal Special': '🌟 मौसमी विशेष',
    '📣 Engagement Buzz': '📣 जुड़ाव और चर्चा',
    'Festive & Energetic': 'उत्सवपूर्ण और ऊर्जावान',
    'Urgent & High-Conversion': 'अति आवश्यक और उच्च-रूपांतरण',
    'Generated 3 tailored campaign strategies': '3 अनुकूलित अभियान रणनीतियाँ तैयार की गईं',
    'Could not generate campaign ideas. Please try again.': 'अभियान के विचार उत्पन्न नहीं हो सके। कृपया पुन: प्रयास करें।',
    'Venue Themes': 'प्रतिष्ठान थीम',
    'Choose the character of your public menu': 'अपने सार्वजनिक मेनू का स्वरूप और शैली चुनें',
    'Glassmorphism & Depth': 'ग्लासमॉर्फ़िज्म और गहराई',
    'Independent XML Sitemap': 'स्वतंत्र XML साइटमैप',
    'Structured Schema & Sync': 'संरचित डेटा और सिंक',
    'Improve how your venue appears on Google and in local search results.': 'Google और स्थानीय खोज परिणामों में अपने प्रतिष्ठान की उपस्थिति में सुधार करें।',
    'Terms of Service': 'सेवा की शर्तें',
    'Confirm your email': 'अपने ईमेल की पुष्टि करें',
    'Check your spam / junk folder': 'अपना स्पैम / जंक फ़ोल्डर जांचें',
    'Commercial pre-press print production package': 'व्यावसायिक प्री-प्रेस प्रिंट पैकेज',
    'Over-bleed safety margin': 'सुरक्षा ब्लीड मार्जिन',
    'Crop Marks': 'कट मार्क',
    'Guillotine cutting guides': 'कागज़ कटिंग गाइड',
    'Global Banners': 'वैश्विक बैनर',
    'Featured Items': 'विशेष रुप से प्रदर्शित आइटम',
    'AI Assistant': 'AI सहायक',
    'Custom Role Schemas': 'कस्टम भूमिका स्कीमा',
    'Notification Preferences': 'सूचना प्राथमिकताएं',
    'Preferences saved automatically': 'प्राथमिकताएं स्वचालित रूप से सहेजी गईं',
    'Menu Extractor (PDF & Images)': 'मेनू निष्कर्षण (पीडीएफ और छवियां)',
    'Multi-Language Translation': 'बहु-भाषा अनुवाद',
    'Start Menu Translation': 'मेनू अनुवाद शुरू करें',
    'Menu Items Hub': 'मेनू आइटम केंद्र',
    'Open Menu Items': 'मेनू आइटम खोलें',
    'Enterprise Traceability': 'उद्यम पता लगाने की क्षमता',
    'Full History': 'पूरा इतिहास',
    'Billing & Plans': 'बिलिंग और प्लान',
    'Subscription Governance': 'सदस्यता प्रबंधन',
    'Platform Usage': 'प्लेटफ़ॉर्म उपयोग',
    'Menu Page Views': 'मेनू पेज व्यू',
    'Enterprise Identity': 'उद्यम पहचान',
    'Access Governance': 'पहुँच नियंत्रण',
    'Required Fields *': 'आवश्यक फ़ील्ड *',
    'Enabled Languages': 'सक्षम भाषाएं',
    'Venue Info & Bio Localizations': 'प्रतिष्ठान जानकारी और परिचय स्थानीयकरण',
    'Public Address': 'सार्वजनिक पता',
    'Public URL Slug': 'सार्वजनिक URL स्लग',
    'Contact Phone': 'संपर्क फोन',
    'Business Address': 'व्यावसायिक पता',
    'No Categories Yet': 'अभी तक कोई श्रेणी नहीं',
    'No Custom Roles Configured': 'कोई कस्टम भूमिका कॉन्फ़िगर नहीं की गई',
    'No Google reviews retrieved yet': 'अभी तक कोई Google समीक्षाएं प्राप्त नहीं हुईं',
    'No Guests Found': 'कोई अतिथि नहीं मिला',
    'No Menu Items Yet': 'मेनू में अभी तक कोई आइटम नहीं',
    'No Promotions Configured': 'कोई प्रचार कॉन्फ़िगर नहीं किया गया',
    'No Service Periods Yet': 'अभी तक कोई सेवा अवधि नहीं',
    'No Support Tickets': 'कोई सहायता टिकट नहीं',
    'No Tables on Floor Plan': 'फ़्लोर प्लान पर कोई टेबल नहीं',
    'No Team Members Found': 'कोई टीम सदस्य नहीं मिला',
    'No Upcoming Events': 'कोई आगामी कार्यक्रम नहीं',
    'No Walk-Ins Recorded': 'कोई वॉक-इन दर्ज नहीं',
    'No items are available right now': 'अभी कोई आइटम उपलब्ध नहीं है',
    'No notifications found': 'कोई सूचना नहीं मिली',
    'No reservations matching your filters': 'आपके फ़िल्टर से मेल खाने वाला कोई आरक्षण नहीं',
    'No tables currently occupied': 'वर्तमान में कोई टेबल व्यस्त नहीं है',
    'No venue selected': 'कोई प्रतिष्ठान नहीं चुना गया',
    'Waitlist': 'प्रतीक्षा सूची',
    'Waitlist is Empty': 'प्रतीक्षा सूची खाली है',
    'Waves (Gluten)': 'अनाज (ग्लूटेन)',
    'We sent a temporary verification code to': 'हमने एक अस्थायी सत्यापन कोड भेजा है',
    'Website URL': 'वेबसाइट का पता',
    'What the customer receives': 'ग्राहक को क्या प्राप्त होता है',
    'White-label control is available on eligible plans.': 'व्हाइट-लेबल नियंत्रण पात्र योजनाओं पर उपलब्ध है।',
    'Why this works:': 'यह रणनीति क्यों काम करती है:',
    'WiFi Broadcast': 'वाईफाई प्रसारण',
    'WiFi Password': 'वाईफाई पासवर्ड',
    'Window seat, dietary request, birthday celebration...': 'खिड़की के पास टेबल, आहार संबंधी अनुरोध, जन्मदिन समारोह...',
    'Your Published Response': 'आपका प्रकाशित उत्तर',
    'Your Venues': 'आपके प्रतिष्ठान',
    '🎉 Ready to publish!': '🎉 प्रकाशित करने के लिए तैयार!',
    'e.g. Grill, Salad, Pastry': 'उदा. ग्रिल, सलाद, पेस्ट्री',
    'e.g. Guest called, emergency': 'उदा. अतिथि का फोन आया, आपातकाल',
    'e.g. Italian, Moroccan, Burgers': 'उदा. इटैलियन, मोरक्कन, बर्गर',
    'e.g. Mains, Pasta, Starters': 'उदा. मुख्य व्यंजन, पास्ता, स्टार्टर्स',
    'e.g. Signature Truffle Pasta': 'उदा. सिग्नेचर ट्रफल पास्ता',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'उदा. दोपहर की कॉफी विशेष, हैप्पी आवर 15%',
    'e.g., Breakfast, Happy Hour': 'उदा. नाश्ता, हैप्पी आवर',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'उदा. हर शुक्रवार रात 8 बजे हमारे साथ लाइव जैज़ का आनंद लें!',
  },
  th: {
    '123 Main St, City': '123 ถนนสายหลัก, เมือง',
    'Balance': 'คะแนนคงเหลือ',
    'Lifetime': 'คะแนนสะสมตลอดชีพ',
    'Source': 'แหล่งที่มา',
    'Menu': 'เมนู',
    'Advanced': 'ขั้นสูง',
    'Core Suite': 'ชุดเครื่องมือหลัก',
    'Privacy': 'ความเป็นส่วนตัว',
    'Support': 'ฝ่ายสนับสนุน',
    'Save Layout': 'บันทึกผังโต๊ะ',
    'Shape:': 'รูปทรง:',
    'SEARCH LIBRARY...': 'ค้นหาในคลัง...',
    'Search library...': 'ค้นหาในคลัง...',
    'None': 'ไม่มี',
    'Type': 'ประเภท',
    'Target': 'เป้าหมาย',
    'Value / Target': 'ค่า / เป้าหมาย',
    'London (GMT/BST)': 'ลอนดอน (GMT/BST)',
    'New York (EST/EDT)': 'นิวยอร์ก (EST/EDT)',
    'Chicago (CST/CDT)': 'ชิคาโก (CST/CDT)',
    'Denver (MST/MDT)': 'เดนเวอร์ (MST/MDT)',
    'Dubai (GST)': 'ดูไบ (GST)',
    'Tokyo (JST)': 'โตเกียว (JST)',
    'Sydney (AEST/AEDT)': 'ซิดนีย์ (AEST/AEDT)',
    'Start Color': 'สีเริ่มต้น',
    'Depth Shade': 'มิติเงาและความลึก',
    'Matrix Geometry': 'รูปแบบจุดรหัส QR',
    'Eye Architecture': 'ดีไซน์มุมกำหนดตำแหน่งรหัส QR',
    'Brand Centerpiece': 'โลโก้ตรงกลางรหัส QR',
    'Scannability Shield': 'ระบบรับประกันความคมชัดในการสแกน',
    'Environment Blur': 'เอฟเฟกต์เบลอพื้นหลัง',
    'Color Target:': 'รูปแบบสี:',
    'DPI:': 'DPI (ความละเอียด):',
    '3mm Bleed Box': 'ระยะตัดตก 3 มม. สำหรับงานพิมพ์',
    '300 DPI': '300 DPI (ความละเอียดสูง)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'โปรโตคอล SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'รหัสเอนทิตีของ IdP (Entity ID)',
    'SSO URL': 'URL เข้าสู่ระบบ SSO',
    'X.509 Certificate': 'ใบรับรองความปลอดภัย X.509',
    'ACS URL': 'URL บริการ ACS (Assertion Consumer)',
    'Entity ID': 'รหัสเอนทิตี (Entity ID)',
    'Navigate': 'นำทาง',
    'Select': 'เลือก',
    'OmniSearch v1.0': 'ระบบค้นหาแบบครอบคลุม OmniSearch',
    'Back to order review': 'กลับไปตรวจสอบรายการสั่งซื้อ',
    'Return to Intelligence Studio': 'กลับไปที่ศูนย์วิเคราะห์ข้อมูล',
    'View Full Notification Center': 'ดูศูนย์การแจ้งเตือนทั้งหมด',
    'Read all': 'อ่านทั้งหมด',
    'Cancel department': 'ยกเลิกแผนก',
    'Save department': 'บันทึกแผนก',
    'Venue Segmentation': 'การแบ่งพื้นที่ร้าน',
    'Collaborate on your menu together.': 'ร่วมมือกับทีมงานเพื่อจัดการเมนูอาหารไปด้วยกัน',
    'Timeline': 'ไทม์ไลน์การจอง',
    'Calendar': 'ปฏิทินการจอง',
    'Book': 'จองโต๊ะ',
    'Duration': 'ระยะเวลาที่ใช้บริการ',
    'Time & Duration': 'เวลาและระยะเวลา',
    'Seating Preference': 'ความต้องการที่นั่งเป็นพิเศษ',
    'Section': 'โซนที่นั่ง',
    'Min Capacity': 'จำนวนที่นั่งขั้นต่ำ',
    'Max Capacity': 'จำนวนที่นั่งสูงสุด',
    'Phone (Optional)': 'เบอร์โทรศัพท์ (ไม่บังคับ)',
    'Name or Walk-in': 'ชื่อลูกค้าหรือลูกค้ารับประทานหน้าร้าน',
    'Next day': 'วันถัดไป',
    'Next month': 'เดือนถัดไป',
    'Live Connection': 'การเชื่อมต่อแบบสด',
    'Live Inventory': 'สถานะสต็อกแบบเรียลไทม์',
    'Main Venue (Full Menu)': 'ร้านหลัก (เมนูเต็ม)',
    'Preview Menu': 'ดูตัวอย่างเมนู',
    'Manual Setup Guide': 'คู่มือการตั้งค่าด้วยตนเอง',
    'Connected & Active': 'เชื่อมต่อและเปิดใช้งานอยู่',
    'CTA Action': 'ปุ่มกระตุ้นการดำเนินการ (CTA)',
    'Phone Contact': 'หมายเลขติดต่อ',
    'Menu URL': 'ที่อยู่เว็บเมนู',
    'Google Location': 'ตำแหน่งบน Google Maps',
    'Field': 'ช่องกรอกข้อมูล',
    'Linked': 'เชื่อมโยงแล้ว',
    'Synchronizing Hub...': 'กำลังซิงค์ข้อมูล...',
    'Data Integrity': 'ความถูกต้องและสมบูรณ์ของข้อมูล',
    'Station Governance Suite': 'การจัดการสเตชันครัวและบาร์',
    'Open audit log': 'เปิดบันทึกประวัติการตรวจสอบ',
    'Signal / Time': 'เหตุการณ์ / เวลา',
    'Identity': 'ผู้ดำเนินการ / ตัวตน',
    'Invitation Invalid': 'ลิงก์คำเชิญไม่ถูกต้องหรือหมดอายุแล้ว',
    'Invited To': 'ได้รับเชิญให้เข้าร่วม',
    'Team Limit Reached': 'ถึงจำนวนสมาชิกในทีมสูงสุดแล้ว',
    'Venue Limit Reached': 'ถึงจำนวนร้านสูงสุดแล้ว',
    'Venue Setup Required': 'จำเป็นต้องตั้งค่าร้านอาหารก่อน',
    'Create Venue': 'สร้างร้านใหม่',
    'TableGreet Onboarding': 'การเริ่มต้นใช้งาน TableGreet',
    'No payment required': 'ไม่ต้องชำระเงิน ฟรีถาวร',
    'Selected Plan': 'แพ็กเกจที่เลือก',
    'Payment Method': 'วิธีการชำระเงิน',
    'Invoice ID': 'หมายเลขใบแจ้งหนี้',
    'Verified': 'ยืนยันแล้ว',
    'Accent color': 'สีเน้น',
    'Set the voice and overall light level.': 'กำหนดโทนเสียงและระดับความสว่างโดยรวม',
    'Layout architecture': 'โครงสร้างการจัดวางเมนู',
    'Navigation & motion': 'การนำทางและการเคลื่อนไหว',
    'Loading preview engine': 'กำลังโหลดเครื่องมือแสดงตัวอย่าง',
    'Real Live Preview': 'ตัวอย่างเสมือนจริงแบบเรียลไทม์',
    'Included with Pro & Max': 'รวมอยู่ในแพ็กเกจ Pro & Max',
    'Daypart Scheduling': 'การจัดตารางเวลาตามช่วงเวลาของวัน',
    'Operational Daypart Architecture': 'โครงสร้างช่วงเวลาการดำเนินงาน',
    'Service Protocol': 'ระเบียบการให้บริการ',
    'Configuration Engine': 'ระบบจัดการการกำหนดค่า',
    'Linked Department': 'แผนกที่เชื่อมโยง',
    'Team Hub': 'ศูนย์จัดการทีมงาน',
    'Team Size': 'ขนาดของทีมงาน',
    'Joined': 'เข้าร่วมเมื่อ',
    'Cancel Invitation': 'ยกเลิกคำเชิญ',
    'Initialize New Organization': 'เริ่มต้นสร้างองค์กรใหม่',
    'Operational': 'เปิดดำเนินการตามปกติ',
    'Pro Feature': 'ฟีเจอร์สำหรับ Pro',
    'Current State': 'สถานะปัจจุบัน',
    'Open on the big TV': 'เปิดบนจอทีวีขนาดใหญ่',
    'Menu Board': 'บอร์ดแสดงเมนูดิจิทัล',
    'Reward': 'ของรางวัล',
    'Reward name': 'ชื่อของรางวัล',
    'Cancel & refund': 'ยกเลิกและคืนเงิน',
    'Code': 'รหัส',
    'Find': 'ค้นหา',
    'Complete': 'เสร็จสมบูรณ์',
    'Seated': 'นั่งที่โต๊ะ',
    'No-Show': 'ไม่มาตามนัด (No-Show)',
    'Walk-in': 'ลูกค้าหน้าร้าน',
    'Party': 'จำนวนแขก',
    'Turnover': 'อัตราการหมุนเวียนโต๊ะ',
    'We couldn\'t load the menu': 'ไม่สามารถโหลดเมนูได้ในขณะนี้',
    'Venue Not Found': 'ไม่พบข้อมูลร้านอาหาร',
    'Live Availability': 'ความพร้อมให้บริการแบบสด',
    'Multi-Language Suite': 'ชุดจัดการหลายภาษา',
    'Key name': 'ชื่อคีย์',
    'Revoke API key': 'ยกเลิกคีย์ API',
    'Delete webhook': 'ลบ Webhook',
    'Identity Provider': 'ผู้ให้บริการข้อมูลประจำตัว (IdP)',
    'Provider': 'ผู้ให้บริการ',
    'Mandatory MFA': 'บังคับใช้การยืนยันตัวตนแบบหลายปัจจัย (MFA)',
    'Account Identity': 'ข้อมูลประจำตัวของบัญชี',
    'Login Email': 'อีเมลเข้าสู่ระบบ',
    'New Login Email': 'อีเมลเข้าสู่ระบบใหม่',
    'Phone Login': 'เข้าสู่ระบบด้วยเบอร์โทรศัพท์',
    'SMS Verification Code': 'รหัสยืนยันทาง SMS',
    'Integration Method': 'วิธีการเชื่อมต่อระบบ',
    'Stripe Connect': 'การชำระเงินผ่าน Stripe Connect',
    'Stripe Secret Key': 'คีย์ลับ Stripe (Secret Key)',
    'Stripe Webhook Secret': 'คีย์ลับ Webhook ของ Stripe',
    'Client-Owned': 'บริหารจัดการโดยร้านค้าเอง',
    'Verify connection': 'ตรวจสอบการเชื่อมต่อ',
    'Setup Health': 'ความสมบูรณ์ของการตั้งค่า',
    'Privacy Protected': 'ข้อมูลได้รับการปกป้องความเป็นส่วนตัว',
    'Privacy Policy': 'นโยบายความเป็นส่วนตัว',
    'Continue': 'ดำเนินการต่อ',
    'Sign In': 'เข้าสู่ระบบ',
    'Didn\'t get it?': 'ไม่ได้รับรหัสใช่หรือไม่?',
    'Logged In Account': 'บัญชีที่เข้าสู่ระบบอยู่ในขณะนี้',
    'Annual': 'รายปี (ประหยัดกว่า)',
    'Unread': 'ยังไม่ได้อ่าน',
    'Starred': 'ติดดาว',
    'High / Urgent': 'สูง / เร่งด่วน',
    'Mark Read': 'ทำเครื่องหมายว่าอ่านแล้ว',
    'Delete notification': 'ลบการแจ้งเตือน',
    'Urgent': 'ด่วนที่สุด',
    'High': 'สูง',
    'Normal': 'ปกติ',
    'Low': 'ต่ำ',
    'Select a Ticket': 'เลือกคำร้องขอความช่วยเหลือ',
    'Urgent (Immediate attention)': 'เร่งด่วน (ต้องการการดูแลทันที)',
    'Type your reply here...': 'พิมพ์ข้อความตอบกลับของคุณที่นี่...',
    'Print Card (PDF)': 'พิมพ์การ์ดตั้งโต๊ะ (PDF)',
    'Advanced Printing Engine': 'ระบบเรนเดอร์งานพิมพ์ความละเอียดสูง',
    'Multi-Venue Governance': 'การบริหารจัดการร้านอาหารหลายสาขา',
    'Network Revenue': 'รายได้รวมของทุกสาขา',
    'Live Network Total': 'ยอดรวมสดของเครือข่ายร้าน',
    'Audit Log Hub': 'ศูนย์บันทึกประวัติการตรวจสอบ',
    'Export Log': 'ส่งออกประวัติการทำงาน',
    'CUSTOM ROLE SCHEMAS': 'โครงสร้างบทบาทและสิทธิ์ที่กำหนดเอง',
    'Unique internal role identifier.': 'รหัสอ้างอิงภายในเฉพาะของบทบาท',
    'Human-friendly role label.': 'ชื่อตำแหน่งที่เข้าใจง่ายสำหรับทีมงาน',
    'Marketing Hub': 'ศูนย์การตลาด',
    'Revenue Generation Suite': 'ชุดเครื่องมือเพิ่มรายได้',
    'Active Campaign Limit Reached (3/3)': 'ถึงขีดจำกัดแคมเปญที่เปิดใช้งานแล้ว (3/3)',
    'Banner Content': 'เนื้อหาแบนเนอร์',
    'Style Profile': 'รูปแบบสไตล์',
    'Action URL (Optional)': 'ลิงก์เป้าหมาย (ไม่บังคับ)',
    'Promotion Label': 'ป้ายชื่อโปรโมชัน',
    'Target Selection': 'เลือกรายการเป้าหมาย',
    'Edit Promotion': 'แก้ไขโปรโมชัน',
    'Delete Promotion': 'ลบโปรโมชัน',
    'Loyalty Engine': 'โปรแกรมสะสมคะแนนลูกค้า',
    'Reward catalog': 'แคตตาล็อกของรางวัล',
    'Add reward': 'เพิ่มของรางวัล',
    'Loyalty ID': 'รหัสสมาชิก',
    'No point activity yet': 'ยังไม่มีประวัติคะแนนสะสม',
    'Scan or enter QR value': 'สแกนหรือระบุรหัส QR',
    'TableGreet Admin': 'ระบบจัดการ TableGreet',
    'Capacity Pacing': 'การจัดสรรและจำกัดจำนวนแขกต่อช่วงเวลา',
    'Cancellation Policy': 'นโยบายการยกเลิกการจอง',
    'Booking Experience': 'ประสบการณ์การจองโต๊ะ',
    'Floor Occupancy': 'อัตราการนั่งของโต๊ะในร้าน',
    'Currently Seated': 'นั่งรับประทานอยู่ขณะนี้',
    'Seat Party': 'เชิญแขกนั่งโต๊ะ',
    'Seat Table': 'กำหนดโต๊ะ',
    'Select Table': 'เลือกโต๊ะ',
    'Table / Capacity': 'โต๊ะ / จำนวนที่นั่ง',
    'Table Name/Number': 'ชื่อหรือหมายเลขโต๊ะ',
    'Mark Arrived': 'บันทึกว่ามาถึงแล้ว',
    'Mark No-Show': 'บันทึกว่าไม่มาตามนัด (No-Show)',
    'Complete Dining': 'ปิดโต๊ะ/สิ้นสุดการรับประทาน',
    'Standard Dining (Default)': 'รับประทานที่ร้านตามปกติ (ค่าเริ่มต้น)',
    'Walk-In Dining Log': 'บันทึกลูกค้าหน้าร้าน (Walk-in)',
    'Notification Center': 'ศูนย์การแจ้งเตือน',
    'Single Sign-On': 'ระบบล็อกอินแบบจุดเดียว (SSO)',
    'Welcome Aboard!': 'ยินดีต้อนรับสู่ TableGreet!',
    'Select your TableGreet plan': 'เลือกแพ็กเกจ TableGreet ที่เหมาะกับคุณ',
    'Free Plan Activation': 'เปิดใช้งานแพ็กเกจฟรี',
    'Venue Created!': 'สร้างร้านเรียบร้อยแล้ว!',
    'Upgrade for Unlimited': 'อัปเกรดเพื่อใช้งานได้ไม่จำกัด',
    'Auto-Tag Menu': 'แท็กเมนูอัตโนมัติ',
    'Start Auto-Tagging': 'เริ่มการแท็กอัตโนมัติ',
    'Launch Auto-Tagger': 'เปิดตัวช่วยแท็กอัตโนมัติ',
    'Launch PDF Importer': 'นำเข้าเมนูจากไฟล์ PDF',
    'Analyzing Menu...': 'กำลังวิเคราะห์เมนู...',
    'Analyzing Recipe...': 'กำลังวิเคราะห์สูตรอาหาร...',
    'Manual Availability': 'การจัดการสถานะความพร้อมแบบกำหนดเอง',
    'Technical Breakdown': 'รายละเอียดเชิงเทคนิค',
    'Dietary Taxonomy': 'หมวดหมู่อาหารและสารก่อภูมิแพ้',
    'Dietary & Allergen AI': 'AI ตรวจสอบสารก่อภูมิแพ้และประเภทอาหาร',
    'Inherit from Category': 'ใช้นโยบายตามหมวดหมู่หลัก',
    'Ingredient Library': 'คลังส่วนผสมและวัตถุดิบ',
    'Ingredient Name': 'ชื่อวัตถุดิบ',
    'Menu Organization': 'การจัดระเบียบโครงสร้างเมนู',
    'Inventory Taxonomy Hub': 'ศูนย์กลางการจัดหมวดหมู่อาหาร',
    'Category Intelligence': 'ข้อมูลเชิงลึกหมวดหมู่อาหาร',
    'Service Period': 'ช่วงเวลาให้บริการ',
    'Manual Configuration / All Day': 'ตั้งค่าด้วยตนเอง / ให้บริการตลอดวัน',
    'Operational Department': 'แผนกปฏิบัติการ',
    'Global / No Department': 'ทั่วไป / ไม่ระบุแผนก',
    'Cancel category': 'ยกเลิกหมวดหมู่',
    'New category name...': 'ชื่อหมวดหมู่ใหม่...',
    'Save category': 'บันทึกหมวดหมู่',
    'QR Matrix Only': 'เฉพาะรหัส QR เท่านั้น',
    'Vector & PDF': 'ไฟล์เวกเตอร์และ PDF',
    'Vector QR (SVG)': 'รหัส QR แบบเวกเตอร์ (SVG)',
    'Vector QR (PDF)': 'รหัส QR พร้อมพิมพ์ (PDF)',
    'Save QR Style': 'บันทึกรูปแบบรหัส QR',
    'Share Link': 'แชร์ลิงก์',
    'High Fidelity 300DPI': 'ความละเอียดสูงสำหรับพิมพ์ 300 DPI',
    'Generating 300 DPI PDF...': 'กำลังสร้าง PDF ความละเอียด 300 DPI...',
    'Give your venue a point of view.': 'สร้างเอกลักษณ์เฉพาะตัวให้ร้านของคุณด้วยการออกแบบที่โดดเด่น',
    'Signage Menu Not Found': 'ไม่พบเมนูป้ายดิจิทัล',
    'Scan Menu': 'สแกนดูเมนู',
    'Open on your phone': 'เปิดบนโทรศัพท์ของคุณ',
    'Full Screen': 'เต็มหน้าจอ',
    'Lock TV board': 'ล็อกหน้าจอทีวี',
    'Big-Screen Menu': 'เมนูสำหรับจอแสดงผลขนาดใหญ่',
    'TV Signage': 'ป้ายดิจิทัลบนทีวี',
    'General Information': 'ข้อมูลทั่วไป',
    'Venue Name *': 'ชื่อร้าน *',
    'Public Slug *': 'ชื่อลิงก์สาธารณะ *',
    'Venue Logo': 'โลโก้ร้าน',
    'Hero Cover Image': 'ภาพหน้าปกหลัก',
    'Venue Timezone': 'เขตเวลาของร้าน',
    'Primary Brand Color': 'สีหลักประจำแบรนด์',
    'TableGreet footer credit': 'ข้อความเครดิต TableGreet ที่ส่วนท้าย',
    'White-Label Branding': 'การติดแบรนด์ของตนเองแบบเต็มรูปแบบ',
    'Client DNS Setup': 'การตั้งค่า DNS ของโดเมน',
    'Copy CNAME target': 'คัดลอกเป้าหมาย CNAME',
    'Copy TableGreet URL': 'คัดลอกลิงก์ TableGreet',
    'Included TableGreet URL': 'ลิงก์ TableGreet ที่รวมอยู่ในแพ็กเกจ',
    'Open TableGreet URL': 'เปิดลิงก์ TableGreet',
    'Public menu': 'เมนูสาธารณะสำหรับลูกค้า',
    'Public venue delivery': 'บริการเดลิเวอรีจากเมนูออนไลน์',
    'Zone name': 'ชื่อโซนพื้นที่',
    'Coverage note': 'หมายเหตุขอบเขตพื้นที่บริการ',
    'Default ETA min': 'เวลาประมาณการต่ำสุด (นาที)',
    'Default ETA max': 'เวลาประมาณการสูงสุด (นาที)',
    'Packaging fee': 'ค่าบรรจุภัณฑ์',
    'Courier handled by': 'ผู้ดำเนินการจัดส่ง',
    'Third-party courier': 'ผู้ให้บริการจัดส่งภายนอก',
    'Unified Experience': 'ประสบการณ์แบบครบวงจร',
    'Save WiFi to Venue': 'บันทึก Wi-Fi สำหรับร้าน',
    'Network Name (SSID)': 'ชื่อเครือข่าย Wi-Fi (SSID)',
    'Audio Alert Chime': 'เสียงเตือนการสั่งอาหาร',
    'Time Format': 'รูปแบบเวลา',
    'Auto / Default': 'อัตโนมัติ / ค่าเริ่มต้น',
    'Syncing SEO & Google Data...': 'กำลังซิงค์ข้อมูล SEO และ Google...',
    'Google Connection': 'การเชื่อมต่อ Google',
    'Search Engine Metadata': 'ข้อมูลเมตาสำหรับเสิร์ชเอนจิน',
    'Social card preview': 'ตัวอย่างการแชร์บนโซเชียลมีเดีย',
    'Allow Indexing': 'อนุญาตให้จัดทำดัชนี',
    'Canonical URL Override': 'กำหนด URL หลักด้วยตนเอง',
    'Price Range': 'ระดับราคา',
    'Latitude': 'ละติจูด',
    'Longitude': 'ลองจิจูด',
    'Google Place ID / Location ID': 'รหัสสถานที่ Google (Place ID)',
    'Google Search Preview': 'ตัวอย่างการค้นหาบน Google',
    'Linked Google Location': 'ตำแหน่ง Google ที่เชื่อมต่อแล้ว',
    'SEO Documentation': 'คู่มือการทำ SEO',
    ', and': ' และ ',
    'and': 'และ',
    'Delete Department?': 'ลบแผนกหรือไม่?',
    'Delete Department': 'ลบแผนก',
    'Live URL': 'ลิงก์สาธารณะ',
    'Copy full live URL': 'คัดลอกลิงก์สาธารณะเต็ม',
    'Open live department menu': 'เปิดเมนูแผนกแบบเรียลไทม์',
    'Loading departments...': 'กำลังโหลดแผนก...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'การแบ่งร้านออกเป็นหลายแผนกเป็นฟีเจอร์พรีเมียมที่มีในแพ็กเกจ PRO ขึ้นไป',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'แบ่งเมนูตามห้องอาหาร โต๊ะ บาร์ หรือพื้นที่ทางกายภาพต่างๆ',
    'Targeted Ordering': 'การสั่งอาหารแบบกำหนดเป้าหมาย',
    'Route guests and orders to specific preparation areas.': 'ส่งต่อลูกค้าและรายการสั่งซื้อไปยังพื้นที่จัดเตรียมอาหารที่กำหนด',
    'Operational Departments': 'แผนกปฏิบัติการ',
    'OPERATIONAL DEPARTMENTS': 'แผนกปฏิบัติการ',
    'Venue Segmentation Hub': 'ศูนย์จัดการพื้นที่ร้าน',
    'VENUE SEGMENTATION HUB': 'ศูนย์จัดการพื้นที่ร้าน',
    'Create departments to group your menu sections. For example, a large venue might have a': 'สร้างแผนกเพื่อจัดกลุ่มหมวดหมู่อาหารของคุณ เช่น ร้านอาหารขนาดใหญ่อาจมี',
    'Restaurant': 'ห้องอาหาร',
    'Poolside': 'ริมสระว่ายน้ำ',
    'department, each with its own categories.': 'โดยแต่ละแผนกจะมีหมวดหมู่เมนูของตนเอง',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'แผนกช่วยให้คุณแบ่งพื้นที่ร้านออกเป็นส่วนๆ เช่น ห้องอาหาร บาร์ หรือริมสระน้ำ โดยแต่ละส่วนจะมีหมวดหมู่ของตนเอง',
    'Department name': 'ชื่อแผนก',
    'New department name...': 'ชื่อแผนกใหม่...',
    'No Linked Daypart (Always Available)': 'ไม่ได้เชื่อมกับช่วงเวลาเปิด (พร้อมให้บริการตลอด)',
    'Linked Daypart': 'ช่วงเวลาบริการที่เชื่อมโยง',
    'Localizations': 'ภาษาและการแปล',
    'LOCALIZATIONS': 'ภาษาและการแปล',
    'AI Auto-Translate': 'แปลอัตโนมัติด้วย AI',
    'AI AUTO-TRANSLATE': 'แปลอัตโนมัติด้วย AI',
    'Translated name': 'ชื่อที่แปลแล้ว',
    'No Departments Yet': 'ยังไม่มีแผนก',
    'Add First Department': 'เพิ่มแผนกแรก',
    'Avg Order Value': 'ยอดสั่งซื้อเฉลี่ย',
    'AVG ORDER VALUE': 'ยอดสั่งซื้อเฉลี่ย',
    'item taps': 'ครั้งที่แตะเมนู',
    'ITEM TAPS': 'ครั้งที่แตะเมนู',
    'Avg Rating': 'คะแนนเฉลี่ย',
    'AVG RATING': 'คะแนนเฉลี่ย',
    'Revenue (Last 7 Days)': 'รายได้ (7 วันล่าสุด)',
    'REVENUE (LAST 7 DAYS)': 'รายได้ (7 วันล่าสุด)',
    'Daily breakdown': 'รายละเอียดรายวัน',
    'DAILY BREAKDOWN': 'รายละเอียดรายวัน',
    'Menu Views (Last 7 Days)': 'ยอดดูเมนู (7 วันล่าสุด)',
    'MENU VIEWS (LAST 7 DAYS)': 'ยอดดูเมนู (7 วันล่าสุด)',
    'Guest traffic': 'การหมุนเวียนของลูกค้า',
    'GUEST TRAFFIC': 'การหมุนเวียนของลูกค้า',
    'Most Viewed Items': 'เมนูที่มีผู้เข้าชมมากที่สุด',
    'MOST VIEWED ITEMS': 'เมนูที่มีผู้เข้าชมมากที่สุด',
    'Top Rated Items': 'เมนูที่ได้รับคะแนนสูงสุด',
    'TOP RATED ITEMS': 'เมนูที่ได้รับคะแนนสูงสุด',
    'Waiter Requests': 'การเรียกพนักงาน',
    'WAITER REQUESTS': 'การเรียกพนักงาน',
    'Total': 'รวมทั้งหมด',
    'TOTAL': 'รวมทั้งหมด',
    'Order Status (30 days)': 'สถานะคำสั่งซื้อ (30 วัน)',
    'ORDER STATUS (30 DAYS)': 'สถานะคำสั่งซื้อ (30 วัน)',
    'Recent Guest Feedback': 'ความคิดเห็นล่าสุดจากลูกค้า',
    'RECENT GUEST FEEDBACK': 'ความคิดเห็นล่าสุดจากลูกค้า',
    'Conversion Rate': 'อัตราการสั่งซื้อ',
    'CONVERSION RATE': 'อัตราการสั่งซื้อ',
    'of menu visitors placed an order': 'ของผู้เข้าชมเมนูได้สั่งอาหารจริง',
    'Refreshed': 'อัปเดตแล้ว',
    'REFRESHED': 'อัปเดตแล้ว',
    'Last 30 days': '30 วันล่าสุด',
    'LAST 30 DAYS': '30 วันล่าสุด',
    'cash': 'เงินสด',
    'Cash': 'เงินสด',
    'CASH': 'เงินสด',
    'bill': 'เช็คบิล',
    'Bill': 'เช็คบิล',
    'BILL': 'เช็คบิล',
    'water': 'น้ำดื่ม',
    'Water': 'น้ำดื่ม',
    'WATER': 'น้ำดื่ม',
    'service': 'บริการ',
    'Service': 'บริการ',
    'SERVICE': 'บริการ',
    'No interaction data yet': 'ยังไม่มีข้อมูลการมีส่วนร่วม',
    'No ratings collected yet': 'ยังไม่มีคะแนนรีวิว',
    'No requests in period': 'ไม่มีคำขอในรอบเวลานี้',
    'No feedback collected yet': 'ยังไม่ได้รับข้อเสนอแนะ',
    'No menu views recorded yet': 'ยังไม่มีสถิติการเปิดดูเมนู',
    'Dish Rating Breakdown': 'รายละเอียดคะแนนรายเมนู',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'อันดับเมนูยอดนิยมและความพึงพอใจของลูกค้ามีให้เฉพาะในแพ็กเกจ MAX และ Enterprise เท่านั้น',
    'Customer Sentiment & Reviews': 'ความรู้สึกและรีวิวจากลูกค้า',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'การดูรีวิวจากลูกค้าโดยตรงและการวิเคราะห์ความรู้สึกต้องใช้แพ็กเกจ MAX หรือ Enterprise',
    'Reviews AI': 'AI วิเคราะห์รีวิว',
    'Guest Feedback': 'ข้อเสนอแนะจากลูกค้า',
    'Upgrade to unlock rating analytics →': 'อัปเกรดเพื่อปลดล็อคการวิเคราะห์คะแนนรีวิว →',
    'Upgrade to Max': 'อัปเกรดเป็น Max',
    'UPGRADE TO MAX': 'อัปเกรดเป็น MAX',
    'MAX PLAN': 'แพ็กเกจ MAX',
    'No Data Yet': 'ยังไม่มีข้อมูล',
    'Share your QR code to start collecting real venue insights.': 'แชร์คิวอาร์โค้ดของคุณเพื่อเริ่มรับข้อมูลเชิงลึกจริงของร้าน',
    'Revenue Analytics': 'การวิเคราะห์รายได้',
    'REVENUE ANALYTICS': 'การวิเคราะห์รายได้',
    'Track your earnings, orders, and average ticket values over time.': 'ติดตามรายได้ จำนวนคำสั่งซื้อ และยอดบิลเฉลี่ยตามช่วงเวลา',
    'Operational Velocity': 'ความคล่องตัวในการปฏิบัติงาน',
    'OPERATIONAL VELOCITY': 'ความคล่องตัวในการปฏิบัติงาน',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'ตรวจสอบการหมุนเวียนของลูกค้า เมนูยอดนิยม และภาระงานของพนักงานเสิร์ฟ',
    'Upgrade to Pro': 'อัปเกรดเป็น Pro',
    'UPGRADE TO PRO': 'อัปเกรดเป็น PRO',
    'Loading Insights…': 'กำลังโหลดข้อมูลเชิงลึก…',
    'Loading Insights...': 'กำลังโหลดข้อมูลเชิงลึก...',
    'Telemetry Unavailable': 'ข้อมูลกิจกรรมไม่พร้อมใช้งาน',
    'TELEMETRY UNAVAILABLE': 'ข้อมูลกิจกรรมไม่พร้อมใช้งาน',
    'No Data Captured': 'ยังไม่มีข้อมูลที่บันทึกไว้',
    'NO DATA CAPTURED': 'ยังไม่มีข้อมูลที่บันทึกไว้',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'แชร์คิวอาร์โค้ดของคุณเพื่อเริ่มติดตามสถิติผู้เข้าชมและคำสั่งซื้อแบบเรียลไทม์',
    'INTELLIGENCE STUDIO': 'ศูนย์วิเคราะห์ข้อมูล',
    'Insights Engine Locked': 'ระบบวิเคราะห์ขั้นสูงถูกล็อค',
    'INSIGHTS ENGINE LOCKED': 'ระบบวิเคราะห์ขั้นสูงถูกล็อค',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'การวิเคราะห์แนวโน้มขั้นสูง คำสั่งจัดการเมนูจำนวนมาก และการส่งออกข้อมูล สงวนไว้สำหรับผู้ใช้ระดับ',
    'Unlock Pro Features': 'ปลดล็อคฟีเจอร์ระดับ Pro',
    'UNLOCK PRO FEATURES': 'ปลดล็อคฟีเจอร์ระดับ PRO',
    'MENU VIEWS': 'ยอดดูเมนู',
    'ITEM INTERACTIONS': 'การมีส่วนร่วมกับเมนู',
    'GUEST RATING': 'คะแนนจากลูกค้า',
    'DELIVERY REVENUE': 'รายได้จากการจัดส่ง',
    'AVERAGE DELIVERY ORDER': 'ยอดสั่งจัดส่งเฉลี่ย',
    'AVERAGE DELIVERY FEE': 'ค่าจัดส่งเฉลี่ย',
    'INCLUDING FEES': 'รวมค่าธรรมเนียมแล้ว',
    'ZONE AVERAGE': 'ค่าเฉลี่ยตามพื้นที่',
    'TRAFFIC PROJECTIONS': 'การคาดการณ์การมาเยือนของลูกค้า',
    'TELEMETRY SIGNAL TRACKING': 'การติดตามสัญญาณกิจกรรม',
    'VEGETARIAN': 'มังสวิรัติ',
    'VEGAN': 'วีแกน',
    'SPICY': 'เผ็ด',
    'GLUTEN-FREE': 'ปลอดกลูเตน',
    'Reset: All In-Stock': 'รีเซ็ต: มีสินค้าทั้งหมด',
    'Emergency Stop (All Out)': 'หยุดฉุกเฉิน: สินค้าหมดทั้งหมด',
    'No compliance issues': 'ไม่พบข้อผิดพลาด',
    'NO COMPLIANCE ISSUES': 'ไม่พบข้อผิดพลาด',
    'detected in current menu': 'ในเมนูปัจจุบัน',
    'DETECTED IN CURRENT MENU': 'ในเมนูปัจจุบัน',
    'Telemetry History (24h)': 'ประวัติกิจกรรม (24 ชม.)',
    'TELEMETRY HISTORY (24H)': 'ประวัติกิจกรรม (24 ชม.)',
    'Telemetry History (30d)': 'ประวัติกิจกรรม (30 วัน)',
    'TELEMETRY HISTORY (30D)': 'ประวัติกิจกรรม (30 วัน)',
    'Save': 'บันทึก',
    'Saved': 'บันทึกแล้ว',
    'Saving...': 'กำลังบันทึก...',
    'Save Changes': 'บันทึกการเปลี่ยนแปลง',
    'Save Settings': 'บันทึกการตั้งค่า',
    'Cancel': 'ยกเลิก',
    'Close': 'ปิด',
    'Create': 'สร้าง',
    'Add': 'เพิ่ม',
    'Edit': 'แก้ไข',
    'Delete': 'ลบ',
    'Remove': 'ลบออก',
    'Update': 'อัปเดต',
    'Refresh': 'รีเฟรช',
    'Search': 'ค้นหา',
    'Search...': 'ค้นหา...',
    'Search items...': 'ค้นหาเมนู...',
    'Search categories...': 'ค้นหาหมวดหมู่...',
    'Filter': 'กรอง',
    'All': 'ทั้งหมด',
    'Export': 'ส่งออก',
    'Import': 'นำเข้า',
    'Download': 'ดาวน์โหลด',
    'Upload': 'อัปโหลด',
    'Copy': 'คัดลอก',
    'Copied': 'คัดลอกแล้ว',
    'Copied!': 'คัดลอกแล้ว!',
    'Preview': 'ดูตัวอย่าง',
    'Open': 'เปิด',
    'View': 'ดู',
    'Manage': 'จัดการ',
    'Configure': 'กำหนดค่า',
    'Enable': 'เปิดใช้งาน',
    'Disable': 'ปิดใช้งาน',
    'Enabled': 'เปิดใช้งานแล้ว',
    'Disabled': 'ปิดใช้งานแล้ว',
    'Active': 'ใช้งานอยู่',
    'Inactive': 'ไม่ใช้งาน',
    'Available': 'พร้อมจำหน่าย',
    'Unavailable': 'ไม่พร้อมจำหน่าย',
    'Visible': 'แสดง',
    'Hidden': 'ซ่อน',
    'Required': 'จำเป็น',
    'Optional': 'ไม่บังคับ',
    'Confirm': 'ยืนยัน',
    'Back': 'ย้อนกลับ',
    'Next': 'ถัดไป',
    'Done': 'เสร็จสิ้น',
    'Upgrade': 'อัปเกรด',
    'Sign Out': 'ออกจากระบบ',
    'Exit to Main Admin': 'กลับไปหน้าหลัก',
    'Change Language': 'เปลี่ยนภาษา',
    'Add Category': 'เพิ่มหมวดหมู่',
    'Add Menu Item': 'เพิ่มรายการเมนู',
    'Add Item': 'เพิ่มรายการ',
    'Add Ingredient': 'เพิ่มวัตถุดิบ',
    'Add Department': 'เพิ่มแผนก',
    'Delete Item': 'ลบรายการ',
    'Delete Category': 'ลบหมวดหมู่',
    'Upload Image': 'อัปโหลดรูปภาพ',
    'Upload Photo': 'อัปโหลดรูปภาพ',
    'Select Category': 'เลือกหมวดหมู่',
    'Select Department': 'เลือกแผนก',
    'Add translation...': 'เพิ่มคำแปล...',
    'Clear': 'ล้าง',
    'Reset': 'รีเซ็ต',
    'Apply': 'นำไปใช้',
    'Submit': 'ส่ง',
    'Core & Operations': 'การจัดการและบริการ',
    'Menu & Experience': 'เมนูและประสบการณ์',
    'Enterprise & Security': 'การจัดการและความปลอดภัย',
    'System & Control': 'ระบบและความช่วยเหลือ',
    'Intelligence Studio': 'ศูนย์วิเคราะห์ข้อมูล',
    'Venue Insights': 'ยอดขายและรายงาน',
    'Reservations': 'การจองโต๊ะ',
    'Floor Plan': 'ผังโต๊ะอาหาร',
    'Live Waitlist': 'คิวรอหน้าร้าน',
    'Walk-Ins': 'ลูกค้าที่ไม่ได้จอง',
    'Walk-ins': 'ลูกค้าที่ไม่ได้จอง',
    'Departments': 'แผนกและพื้นที่',
    'Service Hours': 'เวลาให้บริการ',
    'Categories': 'หมวดหมู่',
    'Menu Categories': 'หมวดหมู่เมนู',
    'Menu Items': 'รายการอาหาร',
    'Items': 'รายการ',
    'Ingredients': 'วัตถุดิบ',
    'Loyalty Program': 'โปรแกรมสะสมแต้ม',
    'Campaign Studio': 'โปรโมชั่นพิเศษ',
    'Signage': 'จอแสดงผลดิจิทัล',
    'QR Code': 'คิวอาร์โค้ด',
    'Kitchen Ops': 'จอครัว',
    'Theme Engine': 'สีและการออกแบบ',
    'Governance': 'การกำกับดูแล',
    'Audit Log': 'ประวัติกิจกรรม',
    'API & Webhooks': 'การเชื่อมต่อระบบ',
    'SSO & Security': 'ความปลอดภัยและการเข้าสู่ระบบ',
    'Team': 'พนักงาน',
    'Billing': 'การเรียกเก็บเงินและแพ็กเกจ',
    'SEO & Google': 'การค้นหาบน Google',
    'Notifications': 'การแจ้งเตือน',
    'Support Center': 'ศูนย์ช่วยเหลือ',
    'Settings': 'การตั้งค่าร้าน',
    'Name': 'ชื่อ',
    'Description': 'คำอธิบาย',
    'Price': 'ราคา',
    'Status': 'สถานะ',
    'Actions': 'การจัดการ',
    'Role': 'บทบาท',
    'Email': 'อีเมล',
    'Phone': 'เบอร์โทร',
    'Address': 'ที่อยู่',
    'City': 'เมือง',
    'Currency': 'สกุลเงิน',
    'Language': 'ภาษา',
    'Theme': 'ธีม',
    'General': 'ทั่วไป',
    'Total Revenue': 'ยอดขายรวม',
    'Total Orders': 'คำสั่งซื้อทั้งหมด',
    'Average Order': 'ยอดสั่งซื้อเฉลี่ย',
    'Active Sessions': 'โต๊ะที่กำลังใช้งาน',
    'Recent Orders': 'คำสั่งซื้อล่าสุด',
    'Live Orders': 'คำสั่งซื้อสด',
    'Table': 'โต๊ะ',
    'Date': 'วันที่',
    'Time': 'เวลา',
    'Amount': 'จำนวนเงิน',
    'Customer': 'ลูกค้า',
    'In Stock': 'มีสินค้า',
    'Out of Stock': 'สินค้าหมด',
    'Sold Out': 'จำหน่ายหมดแล้ว',
    'Dine In': 'ทานที่ร้าน',
    'Pickup': 'รับที่ร้าน',
    'Delivery': 'เดลิเวอรี',
    'Paid': 'ชำระเงินแล้ว',
    'Unpaid': 'ยังไม่ชำระเงิน',
    'Pending': 'รอดำเนินการ',
    'Preparing': 'กำลังเตรียม',
    'Ready': 'พร้อมเสิร์ฟ',
    'Completed': 'เสร็จสมบูรณ์',
    'Cancelled': 'ยกเลิกแล้ว',
    'Loading...': 'กำลังโหลด...',
    'No data': 'ไม่มีข้อมูล',
    'No results found': 'ไม่พบผลลัพธ์',
    'No items found': 'ไม่พบรายการ',
    'No translations defined yet': 'ยังไม่มีคำแปลที่กำหนดไว้',
    'Are you sure?': 'คุณแน่ใจหรือไม่?',
    'This action cannot be undone.': 'การดำเนินการนี้ไม่สามารถยกเลิกได้',
    'Settings saved successfully!': 'บันทึกการตั้งค่าเรียบร้อยแล้ว!',
    'Administrator': 'ผู้ดูแลระบบ',
    'Manager': 'ผู้จัดการ',
    'Owner': 'เจ้าของร้าน',
    'Chef': 'เชฟ',
    'Waiter': 'พนักงานเสิร์ฟ',
    'Cashier': 'พนักงานแคชเชียร์',
    'Type a command or search...': 'พิมพ์คำสั่งหรือค้นหา...',
    'Command Palette': 'แผงคำสั่งด่วน',
    'Demand Velocity': 'ความต้องการของเมนู',
    'DEMAND VELOCITY': 'ความต้องการของเมนู',
    'Top Performing Entities': 'เมนูที่ขายดีที่สุด',
    'TOP PERFORMING ENTITIES': 'เมนูที่ขายดีที่สุด',
    'Hourly Load Analysis': 'วิเคราะห์ความหนาแน่นรายชั่วโมง',
    'HOURLY LOAD ANALYSIS': 'วิเคราะห์ความหนาแน่นรายชั่วโมง',
    'Bulk Command Center': 'ศูนย์จัดการข้อมูลพร้อมกัน',
    'BULK COMMAND CENTER': 'ศูนย์จัดการข้อมูลพร้อมกัน',
    'Governance Scan': 'ตรวจสอบความถูกต้องของเมนู',
    'GOVERNANCE SCAN': 'ตรวจสอบความถูกต้องของเมนู',
    'No compliance issues detected in current menu': 'ไม่พบปัญหาในเมนูปัจจุบัน',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'ไม่พบปัญหาในเมนูปัจจุบัน',
    'Procedural Tagging': 'ใส่ป้ายกำกับอัตโนมัติ',
    'PROCEDURAL TAGGING': 'ใส่ป้ายกำกับอัตโนมัติ',
    'Name match...': 'ค้นหาตามชื่อ...',
    '...Name match': 'ค้นหาตามชื่อ...',
    'Healthy': 'เพื่อสุขภาพ',
    'HEALTHY': 'เพื่อสุขภาพ',
    'Apply Tags': 'ใช้ป้ายกำกับ',
    'APPLY TAGS': 'ใช้ป้ายกำกับ',
    'Global Price Shift': 'ปรับราคาทั้งหมดพร้อมกัน',
    'GLOBAL PRICE SHIFT': 'ปรับราคาทั้งหมดพร้อมกัน',
    'Adjust': 'ปรับราคา',
    'ADJUST': 'ปรับราคา',
    'Availability Override': 'จัดการสถานะเมนูพร้อมเสิร์ฟฉุกเฉิน',
    'AVAILABILITY OVERRIDE': 'จัดการสถานะเมนูพร้อมเสิร์ฟฉุกเฉิน',
    'Reset: ALL IN-STOCK': 'รีเซ็ต: เมนูทั้งหมดพร้อมเสิร์ฟ',
    'RESET: ALL IN-STOCK': 'รีเซ็ต: เมนูทั้งหมดพร้อมเสิร์ฟ',
    'EMERGENCY STOP (ALL OUT)': 'หยุดฉุกเฉิน (หมดทุกรายการ)',
    'Integrity Engine': 'ระบบตรวจสอบความสมบูรณ์ของเมนู',
    'INTEGRITY ENGINE': 'ระบบตรวจสอบความสมบูรณ์ของเมนู',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'ระบบอัตโนมัตินี้จะคอยตรวจสอบการแสดงผลของเมนู ความผิดปกติของราคา และความครบถ้วนของรายละเอียดอาหารทั่วทั้งร้านของคุณ',
    'Inventory Audit Export': 'ส่งออกข้อมูลสำหรับตรวจนับสต็อก',
    'INVENTORY AUDIT EXPORT': 'ส่งออกข้อมูลสำหรับตรวจนับสต็อก',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'สร้างและดาวน์โหลดรายงานสรุปประสิทธิภาพของเมนูและข้อมูลสำคัญเพื่อนำไปตรวจสอบแบบออฟไลน์',
    'Enterprise Data Export': 'ส่งออกข้อมูลขั้นสูงสำหรับองค์กร',
    'ENTERPRISE DATA EXPORT': 'ส่งออกข้อมูลขั้นสูงสำหรับองค์กร',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'สร้างและดาวน์โหลดรายงานการดำเนินงานที่ครอบคลุม ทั้งการไหลเวียนของลูกค้า ความต้องการเมนูอาหาร และอัตราการสั่งซื้อ',
    'JSON Snapshot': 'สแนปช็อต JSON',
    'JSON SNAPSHOT': 'สแนปช็อต JSON',
    'Compile CSV Report': 'รวบรวมรายงาน CSV',
    'COMPILE CSV REPORT': 'รวบรวมรายงาน CSV',
    'Date Segment': 'ช่วงวันที่',
    'DATE SEGMENT': 'ช่วงวันที่',
    'Inbound Views': 'ยอดการเปิดชม',
    'INBOUND VIEWS': 'ยอดการเปิดชม',
    'Asset Interactions': 'การโต้ตอบกับเมนู',
    'ASSET INTERACTIONS': 'การโต้ตอบกับเมนู',
    'Telemetry History': 'ประวัติกิจกรรม',
    'TELEMETRY HISTORY': 'ประวัติกิจกรรม',
    'Telemetry History (7d)': 'ประวัติกิจกรรม (7 วัน)',
    'TELEMETRY HISTORY (7D)': 'ประวัติกิจกรรม (7 วัน)',
    'Growth Trajectory': 'แนวโน้มการเติบโต',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'ความถี่ในการโต้ตอบกับเมนูของคุณมีแนวโน้มเพิ่มขึ้นในช่วง 7 วันที่ผ่านมา',
    'Deep Analysis Engine': 'ระบบวิเคราะห์เชิงลึก',
    'DEEP ANALYSIS ENGINE': 'ระบบวิเคราะห์เชิงลึก',
    'Asset Velocity': 'ความนิยมของเมนู',
    'ASSET VELOCITY': 'ความนิยมของเมนู',
    'Traffic Projections': 'การคาดการณ์จำนวนลูกค้า',
    'Telemetry Signal Tracking': 'การติดตามสัญญาณกิจกรรม',
    'Service Hour Update': 'อัปเดตเวลาให้บริการ',
    'Late Night Lounge Hours': 'เวลาเปิดให้บริการเลานจ์รอบดึก',
    'Bar & Lounge Hours': 'เวลาเปิดบาร์และเลานจ์',
    'Breakfast & Brunch Hours': 'เวลาเปิดให้บริการอาหารเช้าและบรันช์',
    'Switch Location': 'เปลี่ยนสาขา',
    'SWITCH LOCATION': 'เปลี่ยนสาขา',
    'Global Admin': 'ผู้ดูแลระบบหลัก',
    'GLOBAL ADMIN': 'ผู้ดูแลระบบหลัก',
    'Staff Access': 'สิทธิ์พนักงาน',
    'STAFF ACCESS': 'สิทธิ์พนักงาน',
    'Venue Overview': 'ภาพรวมสาขา',
    'VENUE OVERVIEW': 'ภาพรวมสาขา',
    'Unified Telemetry Engine': 'ระบบติดตามกิจกรรมแบบเรียลไทม์',
    'UNIFIED TELEMETRY ENGINE': 'ระบบติดตามกิจกรรมแบบเรียลไทม์',
    'Pulse': 'ชีพจรเรียลไทม์',
    'PULSE': 'ชีพจรเรียลไทม์',
    'Insights': 'ข้อมูลเชิงลึก',
    'INSIGHTS': 'ข้อมูลเชิงลึก',
    'Reports': 'รายงาน',
    'REPORTS': 'รายงาน',
    'GOVERNANCE': 'การกำกับดูแล',
    'Multi-Venue': 'หลายสาขา',
    'MULTI-VENUE': 'หลายสาขา',
    'Menu Views': 'ยอดเปิดดูเมนู',
    'Item Interactions': 'การโต้ตอบกับเมนู',
    'Guest Rating': 'คะแนนจากลูกค้า',
    'Revenue (7d)': 'รายได้ (7 วัน)',
    'Revenue (24h)': 'รายได้ (24 ชม.)',
    'Revenue (30d)': 'รายได้ (30 วัน)',
    'Delivery orders (7d)': 'ออเดอร์เดลิเวอรี่ (7 วัน)',
    'Delivery orders (24h)': 'ออเดอร์เดลิเวอรี่ (24 ชม.)',
    'Delivery orders (30d)': 'ออเดอร์เดลิเวอรี่ (30 วัน)',
    'Delivery revenue': 'รายได้จากเดลิเวอรี่',
    'Average delivery order': 'ยอดออเดอร์เดลิเวอรี่เฉลี่ย',
    'Average delivery fee': 'ค่าจัดส่งเฉลี่ย',
    'Zone average': 'ค่าเฉลี่ยของโซน',
    'Including fees': 'รวมค่าธรรมเนียมแล้ว',
    'No deliveries': 'ไม่มีการจัดส่ง',
    'No orders': 'ไม่มีออเดอร์',
    'No reviews': 'ไม่มีรีวิว',
    'Live': 'สด',
    'LIVE': 'สด',
    'reviews': 'รีวิว',
    'orders': 'ออเดอร์',
    'deliveries': 'การจัดส่ง',
    'of orders': 'ของออเดอร์ทั้งหมด',
    'rate': 'อัตรา',
    'Recent Activity': 'กิจกรรมล่าสุด',
    'Real-time Venue Telemetry': 'ติดตามความเคลื่อนไหวในร้านแบบสดๆ',
    'REAL-TIME VENUE TELEMETRY': 'ติดตามความเคลื่อนไหวในร้านแบบสดๆ',
    'Rapid Pulse View': 'มุมมองชีพจรด่วน',
    'Anonymous Guest': 'ลูกค้าทั่วไป',
    'scanned menu': 'สแกนดูเมนู',
    'viewed item': 'ดูรายละเอียดเมนู',
    'interacted with': 'มีปฏิสัมพันธ์กับ',
    'placed order': 'ส่งคำสั่งซื้อ',
    'searched menu': 'ค้นหาในเมนู',
    'Main Landing': 'หน้าแรก',
    'Trace': 'ตรวจสอบแล้ว',
    'TRACE': 'ตรวจสอบแล้ว',
    'Gaining Telemetry...': 'กำลังเชื่อมต่อข้อมูล...',
    'No recent traffic detected': 'ตรวจไม่พบกิจกรรมล่าสุด',
    'Activities will appear here as guests scan your QR code': 'กิจกรรมจะปรากฏที่นี่เมื่อลูกค้าสแกนคิวอาร์โค้ดของคุณ',
    'Analyze All Logs': 'วิเคราะห์บันทึกทั้งหมด',
    'Just now': 'เมื่อสักครู่',
    'less than a minute ago': 'ไม่ถึงหนึ่งนาทีที่แล้ว',
    '1 minute ago': '1 นาทีที่แล้ว',
    '1 hour ago': '1 ชั่วโมงที่แล้ว',
    'about 1 hour ago': 'ประมาณ 1 ชั่วโมงที่แล้ว',
    '1 day ago': '1 วันที่แล้ว',
    'DAY AGO 1': '1 วันที่แล้ว',
    '2 days ago': '2 วันที่แล้ว',
    'DAY AGO 2': '2 วันที่แล้ว',
    '3 days ago': '3 วันที่แล้ว',
    'DAY AGO 3': '3 วันที่แล้ว',
    '4 days ago': '4 วันที่แล้ว',
    'DAY AGO 4': '4 วันที่แล้ว',
    '5 days ago': '5 วันที่แล้ว',
    'DAY AGO 5': '5 วันที่แล้ว',
    '6 days ago': '6 วันที่แล้ว',
    'DAY AGO 6': '6 วันที่แล้ว',
    '7 days ago': '7 วันที่แล้ว',
    'DAY AGO 7': '7 วันที่แล้ว',
    'about 1 month ago': 'ประมาณ 1 เดือนที่แล้ว',
    'Monday': 'วันจันทร์',
    'Tuesday': 'วันอังคาร',
    'Wednesday': 'วันพุธ',
    'Thursday': 'วันพฤหัสบดี',
    'Friday': 'วันศุกร์',
    'Saturday': 'วันเสาร์',
    'Sunday': 'วันอาทิตย์',
    'Mon': 'จันทร์',
    'Tue': 'อังคาร',
    'Wed': 'พุธ',
    'Thu': 'พฤหัส',
    'Fri': 'ศุกร์',
    'Sat': 'เสาร์',
    'Sun': 'อาทิตย์',
    'Opening Hours': 'เวลาทำการ',
    'Start Time': 'เวลาเริ่ม',
    'End Time': 'เวลาสิ้นสุด',
    'Opening Time': 'เวลาเปิดร้าน',
    'Closing Time': 'เวลาปิดร้าน',
    'All Day': 'ตลอดทั้งวัน',
    'Closed': 'ปิด',
    'Active Days': 'วันทำการ',
    'Active Schedule': 'ตารางเวลาที่เปิดใช้',
    'Always Available': 'พร้อมให้บริการตลอด',
    'Always Visible': 'แสดงตลอดเวลา',
    'New Category': 'หมวดหมู่ใหม่',
    'Category': 'หมวดหมู่',
    'Category Name': 'ชื่อหมวดหมู่',
    'Category Description': 'คำอธิบายหมวดหมู่',
    'Display Order': 'ลำดับการแสดง',
    'Item Name': 'ชื่อเมนู',
    'Item Description': 'คำอธิบายเมนู',
    'Base Price': 'ราคาเริ่มต้น',
    'Preparation Time': 'เวลาเตรียมอาหาร',
    'Calories': 'แคลอรี่',
    'Featured Item': 'เมนูแนะนำ',
    'Chef\'s Recommendation': 'เชฟแนะนำ',
    'Spicy': 'เผ็ด',
    'Vegetarian': 'มังสวิรัติ',
    'Vegan': 'วีแกน / เจ',
    'Gluten-Free': 'ปลอดกลูเตน',
    'Halal': 'ฮาลาล',
    'Kosher': 'โคเชอร์',
    'Contains Nuts': 'มีส่วนผสมของถั่ว',
    'Dairy-Free': 'ไม่มีนมวัว',
    'Allergen Exclusions': 'ข้อมูลสารก่อภูมิแพ้',
    'Table Number': 'หมายเลขโต๊ะ',
    'Assigned Table': 'โต๊ะที่กำหนด',
    'Seats': 'จำนวนที่นั่ง',
    'Capacity': 'ความจุ',
    'Indoor': 'โซนในร้าน',
    'Outdoor': 'โซนนอกร้าน',
    'Terrace': 'ระเบียง',
    'Bar': 'บาร์',
    'Add Table': 'เพิ่มโต๊ะ',
    'Edit Table': 'แก้ไขโต๊ะ',
    'Delete Table': 'ลบโต๊ะ',
    'Bookings': 'รายการจอง',
    'Add to Waitlist': 'เพิ่มลงในคิวรอ',
    'Guest Name': 'ชื่อลูกค้า',
    'Party Size': 'จำนวนลูกค้า',
    'Arrival Time': 'เวลาที่มาถึง',
    'Special Requests': 'คำขอพิเศษ',
    'Confirmed': 'ยืนยันแล้ว',
    'No Show': 'ไม่มาตามนัด',
    'Add Staff Member': 'เพิ่มพนักงาน',
    'Invite Staff': 'เชิญพนักงาน',
    'Email Address': 'ที่อยู่อีเมล',
    'Phone Number': 'เบอร์โทรศัพท์',
    'Download QR Code': 'ดาวน์โหลดคิวอาร์โค้ด',
    'Print Table Stands': 'พิมพ์ป้ายตั้งโต๊ะ',
    'Today': 'วันนี้',
    'Yesterday': 'เมื่อวาน',
    'This Week': 'สัปดาห์นี้',
    'This Month': 'เดือนนี้',
    'Last 30 Days': '30 วันที่ผ่านมา',
    'Access Denied': 'การเข้าถึงถูกปฏิเสธ',
    'Advanced Settings': 'การตั้งค่าขั้นสูง',
    'Change Image': 'เปลี่ยนรูปภาพ',
    'Remove Image': 'ลบรูปภาพ',
    'Announcement Banner': 'แถบประกาศ',
    'Please fill in all required fields.': 'กรุณากรอกข้อมูลในช่องที่จำเป็นให้ครบถ้วน',
    'Changes saved successfully': 'บันทึกการเปลี่ยนแปลงสำเร็จแล้ว',
    '(You)': '(คุณ)',
    'AI Auto-Tag': 'แท็กอัตโนมัติด้วย AI',
    'Analyzing:': 'กำลังวิเคราะห์:',
    'Apple (Fruit/Vegan)': 'แอปเปิ้ล (ผลไม้/วีแกน)',
    'Cancel editing': 'ยกเลิกการแก้ไข',
    'Capacity:': 'ความจุ:',
    'Carrot (Veggie)': 'แครอท (ผัก)',
    'CircleDot (Organic)': 'จุดวงกลม (ออร์แกนิก)',
    'Coffee (Caffeine)': 'กาแฟ (คาเฟอีน)',
    'Configuration': 'การกำหนดค่า',
    'Copy verification record name': 'คัดลอกชื่อเรคคอร์ดการยืนยัน',
    'Copy verification record value': 'คัดลอกค่าเรคคอร์ดการยืนยัน',
    'Critical exception intercepted': 'ตรวจพบข้อผิดพลาดร้ายแรง',
    'Kernel Panic': 'ข้อผิดพลาดของระบบ',
    'DNA (Organic/Bio)': 'DNA (ออร์แกนิก/ชีวภาพ)',
    'Egg (Ovo/Allergen)': 'ไข่ (สารก่อภูมิแพ้)',
    'Empty': 'ว่างเปล่า',
    'Engraving...': 'กำลังสร้าง...',
    'Flame (Spicy)': 'เปลวไฟ (เผ็ด)',
    'Free': 'ฟรี',
    'From': 'จาก',
    'Generating': 'กำลังสร้าง',
    'Governance & Traceability Studio': 'ศูนย์จัดการและประวัติกิจกรรม',
    'Greet': 'ยินดีต้อนรับ',
    'Heart (Healthy)': 'หัวใจ (เพื่อสุขภาพ)',
    'In-App': 'ในแอป',
    'Infinity SVG': 'เวกเตอร์ SVG (ไม่จำกัด)',
    'Leaf (Default)': 'ใบไม้ (ค่าเริ่มต้น)',
    'MAX Feature': 'ฟีเจอร์ระดับองค์กร',
    'Milk (Dairy)': 'นม (ผลิตภัณฑ์จากนม)',
    'Network': 'เครือข่าย',
    'Notify SMS': 'การแจ้งเตือน SMS',
    'Order': 'คำสั่งซื้อ',
    'Original': 'ต้นฉบับ',
    'Overview': 'ภาพรวม',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 บทบาทมาตรฐาน',
    'Shield (Safety)': 'โล่ (ความปลอดภัย)',
    'Soup (Warm/Soup)': 'ซุป (ร้อน/ซุป)',
    'Standard Pro View': 'มุมมองมาตรฐาน',
    'Star': 'ดาว',
    'Synchronized with Venue Identity': 'ปรับแต่งให้เข้ากับเอกลักษณ์ของร้าน',
    'Synchronizing Security Context...': 'กำลังเชื่อมต่ออย่างปลอดภัย...',
    'Targeted Timing': 'ช่วงเวลาให้บริการ',
    'Targeting': 'กลุ่มเป้าหมาย',
    'To': 'ถึง',
    'Toggle Navigation': 'สลับเมนูนำทาง',
    'Venue': 'ร้าน',
    'Venue Governance Suite': 'การจัดการและการตั้งค่าร้าน',
    'Volume:': 'ระดับเสียง:',
    'WHITEBOARD NOTE...': 'บันทึกถึงพนักงาน...',
    'Warning (Alert)': 'คำเตือน (การแจ้งเตือน)',
    'Wheat (Flour/Gluten)': 'ข้าวสาลี (แป้ง/กลูเตน)',
    'Wine (Alcohol)': 'ไวน์ (แอลกอฮอล์)',
    'YouTube/Vimeo/Direct Link': 'ลิงก์วิดีโอ (YouTube, Vimeo หรือ MP4)',
    'Your name': 'ชื่อของคุณ',
    '2. Table Number': '2. หมายเลขโต๊ะ',
    'for Morocco or': 'สำหรับโมร็อกโก หรือ',
    'in': 'ใน',
    'to': 'ถึง',
    'e.g. 12 or Patio-1': 'เช่น 12 หรือ ระเบียง-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'เช่น เฟตตูชินีโฮมเมด, เห็ดทรัฟเฟิลดำ, พาร์มิจิอาโนบ่ม 36 เดือน...',
    'e.g. Head Sommelier & Wine Lead': 'เช่น หัวหน้าซอมเมอลิเยร์',
    'e.g. London': 'เช่น ลอนดอน',
    'e.g. SOMMELIER, HOST, BARISTA': 'เช่น ซอมเมอลิเยร์, พนักงานต้อนรับ, บาริสต้า',
    'e.g. Skyline Lounge': 'เช่น สกายไลน์ เลานจ์',
    'e.g. TRUFFLE OIL': 'เช่น น้ำมันทรัฟเฟิล',
    'e.g. The Grand Cafe': 'เช่น เดอะ แกรนด์ คาเฟ่',
    'e.g. Window preference, high chair': 'เช่น ริมหน้าต่าง, เก้าอี้เด็ก',
    'Copied to clipboard!': 'คัดลอกไปยังคลิปบอร์ดแล้ว!',
    'Copy failed.': 'การคัดลอกล้มเหลว',
    'Link copied!': 'คัดลอกลิงก์แล้ว!',
    'Logo uploaded': 'อัปโหลดโลโก้แล้ว',
    'Cover image uploaded': 'อัปโหลดรูปภาพหน้าปกแล้ว',
    'Profile saved and synced successfully': 'บันทึกและซิงค์ข้อมูลร้านเรียบร้อยแล้ว',
    'Category Created': 'สร้างหมวดหมู่แล้ว',
    'Category Updated': 'อัปเดตหมวดหมู่แล้ว',
    'Category Deleted': 'ลบหมวดหมู่แล้ว',
    'Item Created': 'เพิ่มรายการแล้ว',
    'Item Updated': 'อัปเดตรายการแล้ว',
    'Item Deleted': 'ลบรายการแล้ว',
    'Menu Published': 'เผยแพร่เมนูแล้ว',
    'Delete Category?': 'ลบหมวดหมู่นี้หรือไม่?',
    'Delete Menu Item?': 'ลบรายการนี้หรือไม่?',
    'Delete Ingredient?': 'ลบส่วนผสมนี้หรือไม่?',
    'Delete Service Period?': 'ลบช่วงเวลาให้บริการนี้หรือไม่?',
    'Remove team member?': 'นำสมาชิกในทีมคนนี้ออกหรือไม่?',
    'Cancel invitation?': 'ยกเลิกคำเชิญหรือไม่?',
    'Revoke API key?': 'เพิกถอนคีย์ API หรือไม่?',
    'Delete webhook endpoint?': 'ลบปลายทางเว็บฮุกนี้หรือไม่?',
    'Clear all notifications?': 'ล้างการแจ้งเตือนทั้งหมดหรือไม่?',
    'Disconnect Stripe account?': 'ยกเลิกการเชื่อมต่อบัญชี Stripe หรือไม่?',
    'Disconnect Google Business Profile?': 'ยกเลิกการเชื่อมต่อข้อมูลธุรกิจ Google หรือไม่?',
    'Campaigns published successfully!': 'เผยแพร่แคมเปญเรียบร้อยแล้ว!',
    'Changes successfully synchronized with Google Business Profile!': 'ซิงค์การเปลี่ยนแปลงกับ Google Business เรียบร้อยแล้ว!',
    'Google Business Profile successfully connected!': 'เชื่อมต่อข้อมูลธุรกิจ Google สำเร็จแล้ว!',
    'Google Business Profile disconnected.': 'ยกเลิกการเชื่อมต่อข้อมูลธุรกิจ Google แล้ว',
    'Google Post draft saved successfully!': 'บันทึกฉบับร่างโพสต์ Google สำเร็จแล้ว!',
    'Post draft deleted.': 'ลบฉบับร่างโพสต์แล้ว',
    'Review response published successfully!': 'เผยแพร่คำตอบรีวิวสำเร็จแล้ว!',
    'SEO configuration saved successfully!': 'บันทึกการตั้งค่า SEO เรียบร้อยแล้ว!',
    'SEO metadata translated across all languages!': 'แปลข้อมูลเมตา SEO ครบทุกภาษาแล้ว!',
    'Venue profile & bio translated across all languages!': 'แปลโปรไฟล์และคำแนะนำร้านครบทุกภาษาแล้ว!',
    'Reservation configuration saved successfully': 'บันทึกการตั้งค่าการจองเรียบร้อยแล้ว',
    'Stripe settings saved successfully.': 'บันทึกการตั้งค่า Stripe สำเร็จแล้ว',
    'Print-Ready PDF downloaded successfully': 'ดาวน์โหลด PDF พร้อมพิมพ์เรียบร้อยแล้ว',
    'Vector QR PDF downloaded successfully': 'ดาวน์โหลด QR Code เวกเตอร์ PDF เรียบร้อยแล้ว',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'สร้างแพ็กเกจสิ่งพิมพ์ 300 DPI สำเร็จแล้ว',
    'At least one language must be enabled.': 'ต้องเปิดใช้งานอย่างน้อยหนึ่งภาษา',
    'Enter a non-zero percentage before adjusting prices.': 'กรุณากรอกเปอร์เซ็นต์ที่ไม่ใช่ศูนย์ก่อนปรับราคา',
    'Please enter a Venue Name before translating.': 'กรุณากรอกชื่อร้านก่อนเริ่มการแปล',
    'Please enter an item name before running dietary and allergen analysis.': 'กรุณากรอกชื่อรายการก่อนวิเคราะห์สารก่อภูมิแพ้และข้อมูลโภชนาการ',
    'Please write a reply before submitting.': 'กรุณาเขียนคำตอบก่อนส่ง',
    'Table 3 Called Waiter': 'โต๊ะ 3 เรียกพนักงาน',
    'Table Call Bell': 'กริ่งเรียกประจำโต๊ะ',
    'Waiter Calls': 'การเรียกพนักงาน',
    'Waiter Dispatch & Calls': 'การจ่ายงานและเรียกพนักงาน',
    'Kitchen Display & Whiteboard': 'จอแสดงผลครัวและกระดานข้อความ',
    'Customer Orders': 'คำสั่งซื้อของลูกค้า',
    'Bakery': 'เบเกอรี่และขนมอบ',
    'Bar / Pub': 'บาร์ / ผับ',
    'Café / Coffee Shop': 'คาเฟ่ / ร้านกาแฟ',
    'Fast Food': 'ฟาสต์ฟู้ด / อาหารจานด่วน',
    'Gourmet / Fine Dining': 'ร้านอาหารหรูหรา / ไฟน์ไดนิ่ง',
    'Hotel': 'โรงแรม',
    'General Local Business': 'ธุรกิจท้องถิ่นทั่วไป',
    'Classic': 'คลาสสิก',
    'Clean': 'เรียบง่ายและสะอาดตา',
    'Modern': 'โมเดิร์น',
    'Elegant': 'หรูหราสง่างาม',
    'Energetic': 'มีชีวิตชีวา',
    'Glass': 'กระจกโปร่งแสง',
    'Playful & Casual': 'เป็นกันเองและสดใส',
    'Sophisticated & Gourmet': 'ประณีตระดับกูร์เมต์',
    'Warm & Welcoming': 'อบอุ่นและเป็นกันเอง',
    'Executive': 'ระดับผู้บริหาร',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'ต้องอัปเกรด: เมนูหลายภาษามีให้บริการในแพ็กเกจ Pro',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'ต้องอัปเกรด: การสั่งอาหารและการเรียกพนักงานไม่มีในแพ็กเกจ Starter',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'การส่งออกเวกเตอร์ SVG เป็นฟีเจอร์สำหรับ Pro และ Max กรุณาอัปเกรดแพ็กเกจ',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'การส่งออก PDF พร้อมพิมพ์เป็นฟีเจอร์สำหรับ Pro และ Max กรุณาอัปเกรดแพ็กเกจ',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'แพ็กเกจ Pro จำกัดแคมเปญที่ใช้งานอยู่ไม่เกิน 3 แคมเปญ กรุณาหยุดแคมเปญอื่นชั่วคราวหรืออัปเกรดเป็น MAX',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'แพ็กเกจ Starter รองรับสูงสุด 20 โต๊ะ (โต๊ะ 1–20) อัปเกรดเป็น PRO สำหรับจำนวนโต๊ะไม่จำกัด',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'แพ็กเกจ Starter รองรับโต๊ะแบบตัวเลข (1–20) ชื่อโต๊ะแบบกำหนดเอง (เช่น ระเบียง, VIP) ต้องใช้ PRO',
    'Add Menu Categories & Items': 'เพิ่มหมวดหมู่และรายการอาหาร',
    'Add Menu Items': 'เพิ่มรายการในเมนู',
    'Organize your menu sections and food items.': 'จัดหมวดหมู่เมนูและรายการอาหารของคุณให้เป็นระเบียบ',
    'Add and manage your food & beverage offerings.': 'เพิ่มและจัดการรายการอาหารและเครื่องดื่มของคุณ',
    'Customize Venue Branding': 'ปรับแต่งแบรนด์และเอกลักษณ์ของร้าน',
    'Set your brand colors, logo, and presentation.': 'กำหนดสีประจำร้าน โลโก้ และรูปแบบการนำเสนอ',
    'Get Your QR Code & Go Live': 'รับ QR Code ของร้านและเริ่มใช้งานได้ทันที',
    'Download table QR codes and preview your live menu.': 'ดาวน์โหลด QR Code ประจำโต๊ะและดูตัวอย่างเมนูออนไลน์ของคุณ',
    'Account & Venue Created Successfully!': 'สร้างบัญชีและร้านเรียบร้อยแล้ว!',
    '⚡ Off-Peak Hours': '⚡ ช่วงเวลาลูกค้าน้อย',
    '🍽️ Lunch Crowd': '🍽️ ลูกค้าช่วงพักกลางวัน',
    '🚀 Weekend Rush': '🚀 ช่วงพีคสุดสัปดาห์',
    '🌟 Seasonal Special': '🌟 เมนูพิเศษตามฤดูกาล',
    '📣 Engagement Buzz': '📣 สร้างการมีส่วนร่วมและบอกต่อ',
    'Festive & Energetic': 'รื่นเริงและสนุกสนาน',
    'Urgent & High-Conversion': 'โปรด่วนจำกัดเวลา',
    'Generated 3 tailored campaign strategies': 'สร้างกลยุทธ์แคมเปญที่เหมาะกับร้านคุณ 3 รูปแบบแล้ว',
    'Could not generate campaign ideas. Please try again.': 'ไม่สามารถสร้างไอเดียแคมเปญได้ กรุณาลองใหม่อีกครั้ง',
    'Venue Themes': 'ธีมการแสดงผลของร้าน',
    'Choose the character of your public menu': 'เลือกรูปแบบและเอกลักษณ์ของเมนูออนไลน์ของคุณ',
    'Glassmorphism & Depth': 'เอฟเฟกต์กระจกโปร่งแสงและมิติภาพ',
    'Independent XML Sitemap': 'XML ไซต์แมปอิสระ',
    'Structured Schema & Sync': 'สคีมาข้อมูลและระบบซิงค์',
    'Improve how your venue appears on Google and in local search results.': 'เพิ่มการมองเห็นร้านของคุณบน Google และการค้นหาในละแวกใกล้เคียง',
    'Terms of Service': 'ข้อกำหนดในการให้บริการ',
    'Confirm your email': 'ยืนยันที่อยู่อีเมลของคุณ',
    'Check your spam / junk folder': 'ตรวจสอบโฟลเดอร์อีเมลขยะหรือสแปมของคุณ',
    'Commercial pre-press print production package': 'แพ็กเกจไฟล์สำหรับโรงพิมพ์ระดับมืออาชีพ',
    'Over-bleed safety margin': 'ระยะตัดตกเพื่อความปลอดภัย',
    'Crop Marks': 'มาร์กสำหรับตัดขอบ',
    'Guillotine cutting guides': 'แนวตัดสำหรับเครื่องตัดกระดาษ',
    'Global Banners': 'แบนเนอร์ประกาศหลัก',
    'Featured Items': 'เมนูแนะนำพิเศษ',
    'AI Assistant': 'ผู้ช่วย AI',
    'Custom Role Schemas': 'โครงสร้างบทบาทแบบกำหนดเอง',
    'Notification Preferences': 'การตั้งค่าการแจ้งเตือน',
    'Preferences saved automatically': 'บันทึกการตั้งค่าโดยอัตโนมัติแล้ว',
    'Menu Extractor (PDF & Images)': 'ระบบแยกข้อมูลเมนูจาก PDF และรูปภาพ',
    'Multi-Language Translation': 'ระบบแปลภาษาหลากหลาย',
    'Start Menu Translation': 'เริ่มแปลเมนูอาหาร',
    'Menu Items Hub': 'ศูนย์จัดการรายการอาหาร',
    'Open Menu Items': 'เปิดหน้ารายการอาหาร',
    'Enterprise Traceability': 'ระบบตรวจสอบประวัติการทำงานระดับองค์กร',
    'Full History': 'ประวัติฉบับเต็ม',
    'Billing & Plans': 'การเรียกเก็บเงินและแพ็กเกจ',
    'Subscription Governance': 'การจัดการแพ็กเกจสมาชิก',
    'Platform Usage': 'การใช้งานแพลตฟอร์ม',
    'Menu Page Views': 'ยอดเข้าชมหน้าเมนู',
    'Enterprise Identity': 'ระบบระบุตัวตนระดับองค์กร',
    'Access Governance': 'การควบคุมสิทธิ์การเข้าถึง',
    'Required Fields *': 'ช่องที่ต้องกรอก *',
    'Enabled Languages': 'ภาษาที่เปิดใช้งาน',
    'Venue Info & Bio Localizations': 'การแปลข้อมูลและคำอธิบายร้านค้า',
    'Public Address': 'ที่อยู่สาธารณะของร้าน',
    'Public URL Slug': 'ส่วนท้ายลิงก์ URL ของร้าน',
    'Contact Phone': 'เบอร์โทรศัพท์ติดต่อ',
    'Business Address': 'ที่ตั้งสถานประกอบการ',
    'No Categories Yet': 'ยังไม่มีหมวดหมู่',
    'No Custom Roles Configured': 'ยังไม่ได้กำหนดบทบาทแบบกำหนดเอง',
    'No Google reviews retrieved yet': 'ยังไม่พบรีวิวจาก Google',
    'No Guests Found': 'ไม่พบข้อมูลลูกค้า',
    'No Menu Items Yet': 'ยังไม่มีรายการในเมนู',
    'No Promotions Configured': 'ยังไม่มีโปรโมชั่นที่กำหนดไว้',
    'No Service Periods Yet': 'ยังไม่มีช่วงเวลาให้บริการที่กำหนด',
    'No Support Tickets': 'ไม่มีคำร้องขอความช่วยเหลือ',
    'No Tables on Floor Plan': 'ยังไม่มีโต๊ะในผังร้าน',
    'No Team Members Found': 'ไม่พบสมาชิกในทีม',
    'No Upcoming Events': 'ไม่มีกิจกรรมที่กำลังจะมาถึง',
    'No Walk-Ins Recorded': 'ไม่มีบันทึกลูกค้าวอล์กอิน',
    'No items are available right now': 'ไม่มีรายการที่พร้อมให้บริการในขณะนี้',
    'No notifications found': 'ไม่พบการแจ้งเตือน',
    'No reservations matching your filters': 'ไม่พบการจองที่ตรงกับตัวกรองของคุณ',
    'No tables currently occupied': 'ไม่มีโต๊ะที่กำลังใช้งานอยู่ในขณะนี้',
    'No venue selected': 'ไม่ได้เลือกร้านค้า',
    'Waitlist': 'คิวรอโต๊ะ',
    'Waitlist is Empty': 'ไม่มีคิวรอในขณะนี้',
    'Waves (Gluten)': 'รวงข้าว (กลูเตน)',
    'We sent a temporary verification code to': 'เราได้ส่งรหัสยืนยันชั่วคราวไปยัง',
    'Website URL': 'ลิงก์เว็บไซต์ของร้าน',
    'What the customer receives': 'สิ่งที่ลูกค้าจะได้รับ',
    'White-label control is available on eligible plans.': 'ฟีเจอร์ติดแบรนด์ของตัวเองมีให้บริการในแพ็กเกจที่กำหนด',
    'Why this works:': 'เหตุผลที่กลยุทธ์นี้ได้ผล:',
    'WiFi Broadcast': 'การแชร์ Wi-Fi ให้ลูกค้า',
    'WiFi Password': 'รหัสผ่าน Wi-Fi',
    'Window seat, dietary request, birthday celebration...': 'โต๊ะริมหน้าต่าง, อาหารพิเศษ, ฉลองวันเกิด...',
    'Your Published Response': 'คำตอบของคุณที่เผยแพร่แล้ว',
    'Your Venues': 'ร้านค้าของคุณ',
    '🎉 Ready to publish!': '🎉 พร้อมเผยแพร่แล้ว!',
    'e.g. Grill, Salad, Pastry': 'เช่น สเต๊กย่าง, สลัด, ขนมอบ',
    'e.g. Guest called, emergency': 'เช่น ลูกค้าโทรแจ้งเหตุฉุกเฉิน',
    'e.g. Italian, Moroccan, Burgers': 'เช่น อาหารอิตาเลียน, โมร็อกโก, เบอร์เกอร์',
    'e.g. Mains, Pasta, Starters': 'เช่น จานหลัก, พาสต้า, อาหารเรียกน้ำย่อย',
    'e.g. Signature Truffle Pasta': 'เช่น พาสต้าทรัฟเฟิลสูตรซิกเนเจอร์',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'เช่น กาแฟยามบ่ายชุดพิเศษ, แฮปปี้อาวร์ลด 15%',
    'e.g., Breakfast, Happy Hour': 'เช่น อาหารเช้า, แฮปปี้อาวร์',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'เช่น ร่วมดื่มด่ำกับดนตรีแจ๊สสดทุกคืนวันศุกร์ เวลา 20.00 น.!',
  },
  id: {
    '123 Main St, City': 'Jl. Utama No. 123, Kota',
    'Balance': 'Saldo Poin',
    'Lifetime': 'Total Akumulasi',
    'Source': 'Sumber',
    'Menu': 'Menu',
    'Advanced': 'Tingkat Lanjut',
    'Core Suite': 'Fitur Utama',
    'Privacy': 'Privasi',
    'Support': 'Bantuan & Dukungan',
    'Save Layout': 'Simpan Tata Letak Meja',
    'Shape:': 'Bentuk Meja:',
    'SEARCH LIBRARY...': 'CARI DALAM PUSTAKA...',
    'Search library...': 'Cari dalam pustaka...',
    'None': 'Tidak Ada',
    'Type': 'Tipe',
    'Target': 'Target',
    'Value / Target': 'Nilai / Target',
    'London (GMT/BST)': 'London (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tokyo (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Warna Awal',
    'Depth Shade': 'Bayangan Kedalaman',
    'Matrix Geometry': 'Geometri Titik QR',
    'Eye Architecture': 'Bentuk Sudut QR',
    'Brand Centerpiece': 'Logo di Tengah QR',
    'Scannability Shield': 'Pelindung Kemudahan Pindai',
    'Environment Blur': 'Buram Latar Belakang',
    'Color Target:': 'Target Warna:',
    'DPI:': 'DPI (Resolusi):',
    '3mm Bleed Box': 'Margin Potong 3mm',
    '300 DPI': '300 DPI (Resolusi Tinggi)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Protokol SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'ID Entitas IdP (Entity ID)',
    'SSO URL': 'URL Masuk SSO',
    'X.509 Certificate': 'Sertifikat Keamanan X.509',
    'ACS URL': 'URL Layanan ACS',
    'Entity ID': 'ID Entitas',
    'Navigate': 'Navigasi',
    'Select': 'Pilih',
    'OmniSearch v1.0': 'Pencarian Universal OmniSearch',
    'Back to order review': 'Kembali ke peninjauan pesanan',
    'Return to Intelligence Studio': 'Kembali ke Studio Analitik',
    'View Full Notification Center': 'Lihat Semua Notifikasi',
    'Read all': 'Baca Semua',
    'Cancel department': 'Batalkan departemen',
    'Save department': 'Simpan departemen',
    'Venue Segmentation': 'Segmentasi Lokasi',
    'Collaborate on your menu together.': 'Berkolaborasi mengelola menu bersama tim Anda.',
    'Timeline': 'Garis Waktu',
    'Calendar': 'Kalender Reservasi',
    'Book': 'Pesan Meja',
    'Duration': 'Durasi Makan',
    'Time & Duration': 'Waktu & Durasi',
    'Seating Preference': 'Preferensi Tempat Duduk',
    'Section': 'Bagian Ruangan',
    'Min Capacity': 'Kapasitas Minimum',
    'Max Capacity': 'Kapasitas Maksimum',
    'Phone (Optional)': 'Nomor Telepon (Opsional)',
    'Name or Walk-in': 'Nama atau Tamu Langsung',
    'Next day': 'Hari berikutnya',
    'Next month': 'Bulan depan',
    'Live Connection': 'Koneksi Langsung',
    'Live Inventory': 'Inventaris Langsung',
    'Main Venue (Full Menu)': 'Tempat Utama (Menu Lengkap)',
    'Preview Menu': 'Pratinjau Menu',
    'Manual Setup Guide': 'Panduan Pengaturan Manual',
    'Connected & Active': 'Terhubung & Aktif',
    'CTA Action': 'Tombol Aksi Utama (CTA)',
    'Phone Contact': 'Nomor Kontak',
    'Menu URL': 'Alamat Web Menu',
    'Google Location': 'Lokasi Google Maps',
    'Field': 'Kolom',
    'Linked': 'Tertaut',
    'Synchronizing Hub...': 'Menyinkronkan Pusat Data...',
    'Data Integrity': 'Integritas Data',
    'Station Governance Suite': 'Pengelolaan Stasiun Layanan',
    'Open audit log': 'Buka Log Audit',
    'Signal / Time': 'Peristiwa / Waktu',
    'Identity': 'Pengguna / Identitas',
    'Invitation Invalid': 'Tautan undangan tidak valid atau kedaluwarsa',
    'Invited To': 'Diundang untuk bergabung dengan',
    'Team Limit Reached': 'Batas Anggota Tim Tercapai',
    'Venue Limit Reached': 'Batas Tempat Usaha Tercapai',
    'Venue Setup Required': 'Diperlukan Pengaturan Awal Tempat Usaha',
    'Create Venue': 'Buat Tempat Baru',
    'TableGreet Onboarding': 'Panduan Awal TableGreet',
    'No payment required': 'Tidak perlu pembayaran',
    'Selected Plan': 'Paket yang Dipilih',
    'Payment Method': 'Metode Pembayaran',
    'Invoice ID': 'Nomor Faktur',
    'Verified': 'Terverifikasi',
    'Accent color': 'Warna Aksen',
    'Set the voice and overall light level.': 'Atur nada dan tingkat pencahayaan secara keseluruhan.',
    'Layout architecture': 'Arsitektur Tata Letak',
    'Navigation & motion': 'Navigasi & Animasi',
    'Loading preview engine': 'Memuat mesin pratinjau',
    'Real Live Preview': 'Pratinjau Nyata Langsung',
    'Included with Pro & Max': 'Termasuk dalam Pro & Max',
    'Daypart Scheduling': 'Penjadwalan Waktu Harian',
    'Operational Daypart Architecture': 'Arsitektur Jam Operasional',
    'Service Protocol': 'Protokol Layanan',
    'Configuration Engine': 'Mesin Konfigurasi',
    'Linked Department': 'Departemen Terhubung',
    'Team Hub': 'Pusat Tim',
    'Team Size': 'Ukuran Tim',
    'Joined': 'Bergabung pada',
    'Cancel Invitation': 'Batalkan Undangan',
    'Initialize New Organization': 'Inisialisasi Organisasi Baru',
    'Operational': 'Beroperasi Normal',
    'Pro Feature': 'Fitur Pro',
    'Current State': 'Status Saat Ini',
    'Open on the big TV': 'Buka di TV Layar Besar',
    'Menu Board': 'Papan Menu Digital',
    'Reward': 'Hadiah',
    'Reward name': 'Nama Hadiah',
    'Cancel & refund': 'Batalkan & Kembalikan Dana',
    'Code': 'Kode',
    'Find': 'Temukan',
    'Complete': 'Selesai',
    'Seated': 'Di Meja',
    'No-Show': 'Tidak Hadir',
    'Walk-in': 'Tamu Langsung',
    'Party': 'Jumlah Tamu',
    'Turnover': 'Perputaran Meja',
    'We couldn\'t load the menu': 'Tidak dapat memuat menu',
    'Venue Not Found': 'Tempat Tidak Ditemukan',
    'Live Availability': 'Ketersediaan Langsung',
    'Multi-Language Suite': 'Paket Multi-Bahasa',
    'Key name': 'Nama Kunci',
    'Revoke API key': 'Cabut Kunci API',
    'Delete webhook': 'Hapus Webhook',
    'Identity Provider': 'Penyedia Identitas (IdP)',
    'Provider': 'Penyedia',
    'Mandatory MFA': 'Wajib Verifikasi Dua Langkah (MFA)',
    'Account Identity': 'Identitas Akun',
    'Login Email': 'Email Masuk',
    'New Login Email': 'Email Masuk Baru',
    'Phone Login': 'Masuk dengan Nomor Ponsel',
    'SMS Verification Code': 'Kode Verifikasi SMS',
    'Integration Method': 'Metode Integrasi',
    'Stripe Connect': 'Pembayaran Stripe Connect',
    'Stripe Secret Key': 'Kunci Rahasia Stripe',
    'Stripe Webhook Secret': 'Rahasia Webhook Stripe',
    'Client-Owned': 'Dikelola Mandiri',
    'Verify connection': 'Verifikasi Koneksi',
    'Setup Health': 'Kondisi Pengaturan',
    'Privacy Protected': 'Privasi Terlindungi',
    'Privacy Policy': 'Kebijakan Privasi',
    'Continue': 'Lanjutkan',
    'Sign In': 'Masuk',
    'Didn\'t get it?': 'Belum menerima?',
    'Logged In Account': 'Akun Sedang Masuk',
    'Annual': 'Tahunan',
    'Unread': 'Belum Dibaca',
    'Starred': 'Berbintang',
    'High / Urgent': 'Tinggi / Mendesak',
    'Mark Read': 'Tandai Sudah Dibaca',
    'Delete notification': 'Hapus Notifikasi',
    'Urgent': 'Mendesak',
    'High': 'Tinggi',
    'Normal': 'Normal',
    'Low': 'Rendah',
    'Select a Ticket': 'Pilih Tiket Dukungan',
    'Urgent (Immediate attention)': 'Mendesak (Perhatian Segera)',
    'Type your reply here...': 'Ketik balasan Anda di sini...',
    'Print Card (PDF)': 'Cetak Kartu Meja (PDF)',
    'Advanced Printing Engine': 'Mesin Cetak Canggih',
    'Multi-Venue Governance': 'Tata Kelola Multi-Cabang',
    'Network Revenue': 'Pendapatan Total Jaringan',
    'Live Network Total': 'Total Jaringan Langsung',
    'Audit Log Hub': 'Pusat Log Audit',
    'Export Log': 'Ekspor Log',
    'CUSTOM ROLE SCHEMAS': 'SKEMA PERAN KUSTOM',
    'Unique internal role identifier.': 'Pengenal peran internal yang unik.',
    'Human-friendly role label.': 'Label peran yang mudah dipahami tim.',
    'Marketing Hub': 'Pusat Pemasaran',
    'Revenue Generation Suite': 'Paket Peningkatan Pendapatan',
    'Active Campaign Limit Reached (3/3)': 'Batas Kampanye Aktif Tercapai (3/3)',
    'Banner Content': 'Konten Banner',
    'Style Profile': 'Profil Gaya',
    'Action URL (Optional)': 'Tautan Aksi (Opsional)',
    'Promotion Label': 'Label Promosi',
    'Target Selection': 'Pemilihan Target',
    'Edit Promotion': 'Edit Promosi',
    'Delete Promotion': 'Hapus Promosi',
    'Loyalty Engine': 'Program Loyalitas Pelanggan',
    'Reward catalog': 'Katalog Hadiah',
    'Add reward': 'Tambah Hadiah',
    'Loyalty ID': 'ID Loyalitas',
    'No point activity yet': 'Belum ada aktivitas poin',
    'Scan or enter QR value': 'Pindai atau masukkan nilai QR',
    'TableGreet Admin': 'Admin TableGreet',
    'Capacity Pacing': 'Pengaturan Arus Kapasitas',
    'Cancellation Policy': 'Kebijakan Pembatalan',
    'Booking Experience': 'Pengalaman Reservasi',
    'Floor Occupancy': 'Tingkat Keterisian Meja',
    'Currently Seated': 'Sedang Duduk Saat Ini',
    'Seat Party': 'Dudukkan Tamu',
    'Seat Table': 'Dudukkan di Meja',
    'Select Table': 'Pilih Meja',
    'Table / Capacity': 'Meja / Kapasitas',
    'Table Name/Number': 'Nama/Nomor Meja',
    'Mark Arrived': 'Tandai Sudah Datang',
    'Mark No-Show': 'Tandai Tidak Hadir',
    'Complete Dining': 'Selesaikan Makan',
    'Standard Dining (Default)': 'Makan Standar (Bawaan)',
    'Walk-In Dining Log': 'Catatan Tamu Tanpa Reservasi',
    'Notification Center': 'Pusat Notifikasi',
    'Single Sign-On': 'Masuk Tunggal (SSO)',
    'Welcome Aboard!': 'Selamat Datang di TableGreet!',
    'Select your TableGreet plan': 'Pilih paket TableGreet Anda',
    'Free Plan Activation': 'Aktivasi Paket Gratis',
    'Venue Created!': 'Tempat Berhasil Dibuat!',
    'Upgrade for Unlimited': 'Tingkatkan untuk Tanpa Batas',
    'Auto-Tag Menu': 'Beri Tag Menu Otomatis',
    'Start Auto-Tagging': 'Mulai Pemberian Tag Otomatis',
    'Launch Auto-Tagger': 'Buka Pembuat Tag Otomatis',
    'Launch PDF Importer': 'Buka Pengimpor PDF',
    'Analyzing Menu...': 'Menganalisis Menu...',
    'Analyzing Recipe...': 'Menganalisis Resep...',
    'Manual Availability': 'Ketersediaan Manual',
    'Technical Breakdown': 'Rincian Teknis',
    'Dietary Taxonomy': 'Kategori Diet & Alergen',
    'Dietary & Allergen AI': 'AI Diet & Alergen',
    'Inherit from Category': 'Ikuti Pengaturan Kategori',
    'Ingredient Library': 'Pustaka Bahan',
    'Ingredient Name': 'Nama Bahan',
    'Menu Organization': 'Pengorganisasian Menu',
    'Inventory Taxonomy Hub': 'Pusat Taksonomi Menu',
    'Category Intelligence': 'Wawasan Cerdas Kategori',
    'Service Period': 'Periode Layanan',
    'Manual Configuration / All Day': 'Konfigurasi Manual / Sepanjang Hari',
    'Operational Department': 'Departemen Operasional',
    'Global / No Department': 'Global / Tanpa Departemen',
    'Cancel category': 'Batalkan kategori',
    'New category name...': 'Nama kategori baru...',
    'Save category': 'Simpan kategori',
    'QR Matrix Only': 'Hanya Kode QR',
    'Vector & PDF': 'Vektor & PDF',
    'Vector QR (SVG)': 'QR Vektor (SVG)',
    'Vector QR (PDF)': 'QR Siap Cetak (PDF)',
    'Save QR Style': 'Simpan Gaya QR',
    'Share Link': 'Bagikan Tautan',
    'High Fidelity 300DPI': 'Kualitas Cetak Tinggi 300 DPI',
    'Generating 300 DPI PDF...': 'Membuat PDF 300 DPI...',
    'Give your venue a point of view.': 'Beri tempat Anda identitas visual yang khas.',
    'Signage Menu Not Found': 'Menu Papan Digital Tidak Ditemukan',
    'Scan Menu': 'Pindai Menu',
    'Open on your phone': 'Buka di ponsel Anda',
    'Full Screen': 'Layar Penuh',
    'Lock TV board': 'Kunci Layar TV',
    'Big-Screen Menu': 'Menu Layar Besar',
    'TV Signage': 'Papan Tampilan TV',
    'General Information': 'Informasi Umum',
    'Venue Name *': 'Nama Tempat *',
    'Public Slug *': 'Tautan Publik *',
    'Venue Logo': 'Logo Tempat',
    'Hero Cover Image': 'Gambar Sampul Utama',
    'Venue Timezone': 'Zona Waktu Lokasi',
    'Primary Brand Color': 'Warna Utama Merek',
    'TableGreet footer credit': 'Kredit TableGreet di footer',
    'White-Label Branding': 'Branding Label Putih',
    'Client DNS Setup': 'Pengaturan DNS Domain',
    'Copy CNAME target': 'Salin target CNAME',
    'Copy TableGreet URL': 'Salin URL TableGreet',
    'Included TableGreet URL': 'URL TableGreet Bawaan',
    'Open TableGreet URL': 'Buka tautan TableGreet',
    'Public menu': 'Menu Publik Pelanggan',
    'Public venue delivery': 'Pengantaran Pesanan Menu Publik',
    'Zone name': 'Nama Zona',
    'Coverage note': 'Catatan Cakupan Area',
    'Default ETA min': 'Estimasi Waktu Minimum (menit)',
    'Default ETA max': 'Estimasi Waktu Maksimum (menit)',
    'Packaging fee': 'Biaya Pengemasan',
    'Courier handled by': 'Pengiriman Dikelola Oleh',
    'Third-party courier': 'Kurir Pihak Ketiga',
    'Unified Experience': 'Pengalaman Terpadu',
    'Save WiFi to Venue': 'Simpan Wi-Fi Lokasi',
    'Network Name (SSID)': 'Nama Jaringan (SSID)',
    'Audio Alert Chime': 'Nada Dering Notifikasi',
    'Time Format': 'Format Waktu',
    'Auto / Default': 'Otomatis / Bawaan',
    'Syncing SEO & Google Data...': 'Menyinkronkan data SEO & Google...',
    'Google Connection': 'Koneksi Google',
    'Search Engine Metadata': 'Metadata Mesin Pencari',
    'Social card preview': 'Pratinjau Kartu Media Sosial',
    'Allow Indexing': 'Izinkan Pengindeksan',
    'Canonical URL Override': 'Ganti URL Kanonikal',
    'Price Range': 'Rentang Harga',
    'Latitude': 'Garis Lintang',
    'Longitude': 'Garis Bujur',
    'Google Place ID / Location ID': 'ID Tempat Google (Place ID)',
    'Google Search Preview': 'Pratinjau Pencarian Google',
    'Linked Google Location': 'Lokasi Google Tertaut',
    'SEO Documentation': 'Dokumentasi SEO',
    ', and': ', dan',
    'and': 'dan',
    'Delete Department?': 'Hapus Departemen?',
    'Delete Department': 'Hapus Departemen',
    'Live URL': 'URL Langsung',
    'Copy full live URL': 'Salin URL lengkap langsung',
    'Open live department menu': 'Buka menu departemen langsung',
    'Loading departments...': 'Memuat departemen...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Segmentasi lokasi multi-departemen adalah fitur premium yang tersedia di paket PRO ke atas.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Bagi menu Anda berdasarkan ruangan, meja, bar, atau lokasi fisik.',
    'Targeted Ordering': 'Pemesanan Tertarget',
    'Route guests and orders to specific preparation areas.': 'Arahkan tamu dan pesanan ke area persiapan tertentu.',
    'Operational Departments': 'Departemen Operasional',
    'OPERATIONAL DEPARTMENTS': 'DEPARTEMEN OPERASIONAL',
    'Venue Segmentation Hub': 'Pusat Segmentasi Lokasi',
    'VENUE SEGMENTATION HUB': 'PUSAT SEGMENTASI LOKASI',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Buat departemen untuk mengelompokkan menu Anda. Contohnya, tempat usaha besar dapat memiliki',
    'Restaurant': 'Restoran',
    'Poolside': 'Area Kolam Renang',
    'department, each with its own categories.': ', masing-masing dengan kategorinya sendiri.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Departemen memungkinkan Anda membagi lokasi menjadi beberapa area seperti Restoran, Bar, atau Kolam Renang — masing-masing dengan kategorinya sendiri.',
    'Department name': 'Nama Departemen',
    'New department name...': 'Nama departemen baru...',
    'No Linked Daypart (Always Available)': 'Tidak Terkait Jam Operasional (Selalu Tersedia)',
    'Linked Daypart': 'Jam Layanan Terkait',
    'Localizations': 'Lokalisasi Bahasa',
    'LOCALIZATIONS': 'LOKALISASI BAHASA',
    'AI Auto-Translate': 'Terjemahan Otomatis AI',
    'AI AUTO-TRANSLATE': 'TERJEMAHAN OTOMATIS AI',
    'Translated name': 'Nama Terjemahan',
    'No Departments Yet': 'Belum Ada Departemen',
    'Add First Department': 'Tambah Departemen Pertama',
    'Avg Order Value': 'Rata-rata Nilai Pesanan',
    'AVG ORDER VALUE': 'RATA-RATA NILAI PESANAN',
    'item taps': 'ketukan menu',
    'ITEM TAPS': 'KETUKAN MENU',
    'Avg Rating': 'Penilaian Rata-rata',
    'AVG RATING': 'PENILAIAN RATA-RATA',
    'Revenue (Last 7 Days)': 'Pendapatan (7 Hari Terakhir)',
    'REVENUE (LAST 7 DAYS)': 'PENDAPATAN (7 HARI TERAKHIR)',
    'Daily breakdown': 'Rincian Harian',
    'DAILY BREAKDOWN': 'RINCIAN HARIAN',
    'Menu Views (Last 7 Days)': 'Tayangan Menu (7 Hari Terakhir)',
    'MENU VIEWS (LAST 7 DAYS)': 'TAYANGAN MENU (7 HARI TERAKHIR)',
    'Guest traffic': 'Lalu Lintas Tamu',
    'GUEST TRAFFIC': 'LALU LINTAS TAMU',
    'Most Viewed Items': 'Menu Paling Banyak Dilihat',
    'MOST VIEWED ITEMS': 'MENU PALING BANYAK DILIHAT',
    'Top Rated Items': 'Menu dengan Penilaian Tertinggi',
    'TOP RATED ITEMS': 'MENU DENGAN PENILAIAN TERTINGGI',
    'Waiter Requests': 'Panggilan Pelayan',
    'WAITER REQUESTS': 'PANGGILAN PELAYAN',
    'Total': 'Total',
    'TOTAL': 'TOTAL',
    'Order Status (30 days)': 'Status Pesanan (30 Hari)',
    'ORDER STATUS (30 DAYS)': 'STATUS PESANAN (30 HARI)',
    'Recent Guest Feedback': 'Umpan Balik Tamu Terbaru',
    'RECENT GUEST FEEDBACK': 'UMPAN BALIK TAMU TERBARU',
    'Conversion Rate': 'Tingkat Konversi',
    'CONVERSION RATE': 'TINGKAT KONVERSI',
    'of menu visitors placed an order': 'pengunjung menu berhasil memesan',
    'Refreshed': 'Diperbarui',
    'REFRESHED': 'DIPERBARUI',
    'Last 30 days': '30 hari terakhir',
    'LAST 30 DAYS': '30 HARI TERAKHIR',
    'cash': 'tunai',
    'Cash': 'Tunai',
    'CASH': 'TUNAI',
    'bill': 'tagihan',
    'Bill': 'Tagihan',
    'BILL': 'TAGIHAN',
    'water': 'air minum',
    'Water': 'Air Minum',
    'WATER': 'AIR MINUM',
    'service': 'layanan',
    'Service': 'Layanan',
    'SERVICE': 'LAYANAN',
    'No interaction data yet': 'Belum ada data interaksi',
    'No ratings collected yet': 'Belum ada penilaian yang dikumpulkan',
    'No requests in period': 'Tidak ada permintaan pada periode ini',
    'No feedback collected yet': 'Belum ada umpan balik yang diterima',
    'No menu views recorded yet': 'Belum ada tayangan menu yang tercatat',
    'Dish Rating Breakdown': 'Rincian Penilaian Menu',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Peringkat menu dan kepuasan tamu hanya tersedia di paket MAX dan Enterprise.',
    'Customer Sentiment & Reviews': 'Sentimen & Ulasan Pelanggan',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Ulasan langsung dari tamu dan analisis sentimen memerlukan langganan MAX atau Enterprise.',
    'Reviews AI': 'AI Analisis Ulasan',
    'Guest Feedback': 'Umpan Balik Tamu',
    'Upgrade to unlock rating analytics →': 'Tingkatkan untuk membuka analitik penilaian →',
    'Upgrade to Max': 'Tingkatkan ke Max',
    'UPGRADE TO MAX': 'TINGKATKAN KE MAX',
    'MAX PLAN': 'PAKET MAX',
    'No Data Yet': 'Belum Ada Data',
    'Share your QR code to start collecting real venue insights.': 'Bagikan kode QR Anda untuk mulai mengumpulkan analitik nyata bisnis Anda.',
    'Revenue Analytics': 'Analisis Pendapatan',
    'REVENUE ANALYTICS': 'ANALISIS PENDAPATAN',
    'Track your earnings, orders, and average ticket values over time.': 'Pantau pendapatan, pesanan, dan nilai rata-rata tagihan dari waktu ke waktu.',
    'Operational Velocity': 'Kecepatan Operasional',
    'OPERATIONAL VELOCITY': 'KECEPATAN OPERASIONAL',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Pantau kunjungan tamu, menu favorit, dan beban kerja panggilan pelayan.',
    'Upgrade to Pro': 'Upgrade ke Pro',
    'UPGRADE TO PRO': 'TINGKATKAN KE PRO',
    'Loading Insights…': 'Memuat Analisis…',
    'Loading Insights...': 'Memuat Analisis...',
    'Telemetry Unavailable': 'Data aktivitas tidak tersedia',
    'TELEMETRY UNAVAILABLE': 'DATA AKTIVITAS TIDAK TERSEDIA',
    'No Data Captured': 'Belum ada data tercatat',
    'NO DATA CAPTURED': 'BELUM ADA DATA TERCATAT',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Bagikan kode QR Anda untuk mulai memantau kunjungan dan pesanan secara real-time.',
    'INTELLIGENCE STUDIO': 'STUDIO ANALISIS',
    'Insights Engine Locked': 'Analisis Lanjutan Terkunci',
    'INSIGHTS ENGINE LOCKED': 'ANALISIS LANJUTAN TERKUNCI',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Analisis tren lanjutan, perintah massal, dan ekspor data dikhususkan untuk pengguna',
    'Unlock Pro Features': 'Buka Fitur Pro',
    'UNLOCK PRO FEATURES': 'BUKA FITUR PRO',
    'MENU VIEWS': 'TAYANGAN MENU',
    'ITEM INTERACTIONS': 'INTERAKSI MENU',
    'GUEST RATING': 'PENILAIAN TAMU',
    'DELIVERY REVENUE': 'PENDAPATAN PENGIRIMAN',
    'AVERAGE DELIVERY ORDER': 'RATA-RATA PESANAN PENGIRIMAN',
    'AVERAGE DELIVERY FEE': 'RATA-RATA BIAYA PENGIRIMAN',
    'INCLUDING FEES': 'TERMASUK BIAYA',
    'ZONE AVERAGE': 'RATA-RATA ZONA',
    'TRAFFIC PROJECTIONS': 'PROYEKSI KUNJUNGAN TAMU',
    'TELEMETRY SIGNAL TRACKING': 'PELACAKAN SINYAL AKTIVITAS',
    'VEGETARIAN': 'VEGETARIAN',
    'VEGAN': 'VEGAN',
    'SPICY': 'PEDAS',
    'GLUTEN-FREE': 'BEBAS GLUTEN',
    'Reset: All In-Stock': 'Atur Ulang: Semua Tersedia',
    'Emergency Stop (All Out)': 'Hentikan Darurat: Semua Habis',
    'No compliance issues': 'Tidak ada kendala ditemukan',
    'NO COMPLIANCE ISSUES': 'TIDAK ADA KENDALA DITEMUKAN',
    'detected in current menu': 'dalam menu saat ini',
    'DETECTED IN CURRENT MENU': 'DALAM MENU SAAT INI',
    'Telemetry History (24h)': 'Riwayat Aktivitas (24 jam)',
    'TELEMETRY HISTORY (24H)': 'RIWAYAT AKTIVITAS (24 JAM)',
    'Telemetry History (30d)': 'Riwayat Aktivitas (30 hari)',
    'TELEMETRY HISTORY (30D)': 'RIWAYAT AKTIVITAS (30 HARI)',
    'Save': 'Simpan',
    'Saved': 'Tersimpan',
    'Saving...': 'Menyimpan...',
    'Save Changes': 'Simpan perubahan',
    'Save Settings': 'Simpan pengaturan',
    'Cancel': 'Batal',
    'Close': 'Tutup',
    'Create': 'Buat',
    'Add': 'Tambah',
    'Edit': 'Edit',
    'Delete': 'Hapus',
    'Remove': 'Hapus',
    'Update': 'Perbarui',
    'Refresh': 'Muat ulang',
    'Search': 'Cari',
    'Search...': 'Cari...',
    'Search items...': 'Cari menu...',
    'Search categories...': 'Cari kategori...',
    'Filter': 'Filter',
    'All': 'Semua',
    'Export': 'Ekspor',
    'Import': 'Impor',
    'Download': 'Unduh',
    'Upload': 'Unggah',
    'Copy': 'Salin',
    'Copied': 'Tersalin',
    'Copied!': 'Tersalin!',
    'Preview': 'Pratinjau',
    'Open': 'Buka',
    'View': 'Lihat',
    'Manage': 'Kelola',
    'Configure': 'Konfigurasi',
    'Enable': 'Aktifkan',
    'Disable': 'Nonaktifkan',
    'Enabled': 'Aktif',
    'Disabled': 'Nonaktif',
    'Active': 'Aktif',
    'Inactive': 'Tidak aktif',
    'Available': 'Tersedia',
    'Unavailable': 'Habis',
    'Visible': 'Terlihat',
    'Hidden': 'Tersembunyi',
    'Required': 'Wajib',
    'Optional': 'Opsional',
    'Confirm': 'Konfirmasi',
    'Back': 'Kembali',
    'Next': 'Lanjut',
    'Done': 'Selesai',
    'Upgrade': 'Upgrade',
    'Sign Out': 'Keluar',
    'Exit to Main Admin': 'Kembali ke Admin Utama',
    'Change Language': 'Ubah Bahasa',
    'Add Category': 'Tambah Kategori',
    'Add Menu Item': 'Tambah Menu',
    'Add Item': 'Tambah Item',
    'Add Ingredient': 'Tambah Bahan',
    'Add Department': 'Tambah Departemen',
    'Delete Item': 'Hapus Menu',
    'Delete Category': 'Hapus Kategori',
    'Upload Image': 'Unggah Gambar',
    'Upload Photo': 'Unggah Foto',
    'Select Category': 'Pilih Kategori',
    'Select Department': 'Pilih Departemen',
    'Add translation...': 'Tambah terjemahan...',
    'Clear': 'Hapus',
    'Reset': 'Atur ulang',
    'Apply': 'Terapkan',
    'Submit': 'Kirim',
    'Core & Operations': 'Operasional',
    'Menu & Experience': 'Menu & Pengalaman',
    'Enterprise & Security': 'Manajemen & Keamanan',
    'System & Control': 'Pengaturan & Bantuan',
    'Intelligence Studio': 'Studio Analisis',
    'Venue Insights': 'Laporan Penjualan',
    'Reservations': 'Reservasi',
    'Floor Plan': 'Denah Meja',
    'Live Waitlist': 'Daftar Tunggu',
    'Walk-Ins': 'Tamu Langsung',
    'Walk-ins': 'Tamu Langsung',
    'Departments': 'Departemen',
    'Service Hours': 'Jam Layanan',
    'Categories': 'Kategori',
    'Menu Categories': 'Kategori Menu',
    'Menu Items': 'Daftar Menu',
    'Items': 'Item',
    'Ingredients': 'Bahan-bahan',
    'Loyalty Program': 'Program Loyalitas',
    'Campaign Studio': 'Promo & Penawaran',
    'Signage': 'Tampilan Layar',
    'QR Code': 'Kode QR',
    'Kitchen Ops': 'Layar Dapur',
    'Theme Engine': 'Desain & Warna',
    'Governance': 'Tata Kelola',
    'Audit Log': 'Riwayat Aktivitas',
    'API & Webhooks': 'Integrasi',
    'SSO & Security': 'Keamanan & Masuk',
    'Team': 'Karyawan',
    'Billing': 'Langganan & Tagihan',
    'SEO & Google': 'Pencarian Google',
    'Notifications': 'Notifikasi',
    'Support Center': 'Pusat Bantuan',
    'Settings': 'Pengaturan Restoran',
    'Name': 'Nama',
    'Description': 'Deskripsi',
    'Price': 'Harga',
    'Status': 'Status',
    'Actions': 'Aksi',
    'Role': 'Peran',
    'Email': 'Email',
    'Phone': 'Telepon',
    'Address': 'Alamat',
    'City': 'Kota',
    'Currency': 'Mata Uang',
    'Language': 'Bahasa',
    'Theme': 'Tema',
    'General': 'Umum',
    'Total Revenue': 'Total Pendapatan',
    'Total Orders': 'Total Pesanan',
    'Average Order': 'Rata-rata Pesanan',
    'Active Sessions': 'Sesi Aktif',
    'Recent Orders': 'Pesanan Terbaru',
    'Live Orders': 'Pesanan Langsung',
    'Table': 'Meja',
    'Date': 'Tanggal',
    'Time': 'Waktu',
    'Amount': 'Jumlah',
    'Customer': 'Pelanggan',
    'In Stock': 'Tersedia',
    'Out of Stock': 'Habis',
    'Sold Out': 'Habis Terjual',
    'Dine In': 'Makan di Tempat',
    'Pickup': 'Ambil Sendiri',
    'Delivery': 'Pengiriman',
    'Paid': 'Lunas',
    'Unpaid': 'Belum Lunas',
    'Pending': 'Menunggu',
    'Preparing': 'Sedang Disiapkan',
    'Ready': 'Siap',
    'Completed': 'Selesai',
    'Cancelled': 'Dibatalkan',
    'Loading...': 'Memuat...',
    'No data': 'Tidak ada data',
    'No results found': 'Tidak ada hasil ditemukan',
    'No items found': 'Tidak ada menu ditemukan',
    'No translations defined yet': 'Belum ada terjemahan',
    'Are you sure?': 'Apakah Anda yakin?',
    'This action cannot be undone.': 'Tindakan ini tidak dapat dibatalkan.',
    'Settings saved successfully!': 'Pengaturan berhasil disimpan!',
    'Administrator': 'Administrator',
    'Manager': 'Manajer',
    'Owner': 'Pemilik',
    'Chef': 'Koki',
    'Waiter': 'Pelayan',
    'Cashier': 'Kasir',
    'Type a command or search...': 'Ketik perintah atau cari...',
    'Command Palette': 'Menu Cepat',
    'Demand Velocity': 'Tingkat Permintaan',
    'DEMAND VELOCITY': 'TINGKAT PERMINTAAN',
    'Top Performing Entities': 'Menu Terlaris',
    'TOP PERFORMING ENTITIES': 'MENU TERLARIS',
    'Hourly Load Analysis': 'Analisis Kepadatan Jam demi Jam',
    'HOURLY LOAD ANALYSIS': 'ANALISIS KEPADATAN JAM DEMI JAM',
    'Bulk Command Center': 'Pusat Tindakan Massal',
    'BULK COMMAND CENTER': 'PUSAT TINDAKAN MASSAL',
    'Governance Scan': 'Pemeriksaan Menu',
    'GOVERNANCE SCAN': 'PEMERIKSAAN MENU',
    'No compliance issues detected in current menu': 'Tidak ada masalah yang terdeteksi pada menu saat ini',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'TIDAK ADA MASALAH YANG TERDETEKSI PADA MENU SAAT INI',
    'Procedural Tagging': 'Pemberian Label Otomatis',
    'PROCEDURAL TAGGING': 'PEMBERIAN LABEL OTOMATIS',
    'Name match...': 'Cocokkan nama...',
    '...Name match': 'Cocokkan nama...',
    'Healthy': 'Sehat',
    'HEALTHY': 'SEHAT',
    'Apply Tags': 'Terapkan Label',
    'APPLY TAGS': 'TERAPKAN LABEL',
    'Global Price Shift': 'Penyesuaian Harga Global',
    'GLOBAL PRICE SHIFT': 'PENYESUAIAN HARGA GLOBAL',
    'Adjust': 'Sesuaikan',
    'ADJUST': 'SESUAIKAN',
    'Availability Override': 'Kontrol Ketersediaan Instan',
    'AVAILABILITY OVERRIDE': 'KONTROL KETERSEDIAAN INSTAN',
    'Reset: ALL IN-STOCK': 'Reset: SEMUA TERSEDIA',
    'RESET: ALL IN-STOCK': 'RESET: SEMUA TERSEDIA',
    'EMERGENCY STOP (ALL OUT)': 'Berhenti Darurat (SEMUA HABIS)',
    'Integrity Engine': 'Mesin Integritas Menu',
    'INTEGRITY ENGINE': 'MESIN INTEGRITAS MENU',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Sistem cerdas ini memantau ketersediaan menu, anomali harga, dan kelengkapan deskripsi di seluruh restoran Anda.',
    'Inventory Audit Export': 'Ekspor Audit Inventaris',
    'INVENTORY AUDIT EXPORT': 'EKSPOR AUDIT INVENTARIS',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Buat ringkasan komprehensif tentang performa menu dan metadata untuk audit offline.',
    'Enterprise Data Export': 'Ekspor Data Lanjutan',
    'ENTERPRISE DATA EXPORT': 'EKSPOR DATA LANJUTAN',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Buat dan unduh laporan kinerja lengkap dalam format standar. Mencakup arus pengunjung, permintaan menu, dan konversi.',
    'JSON Snapshot': 'Snapshot JSON',
    'JSON SNAPSHOT': 'SNAPSHOT JSON',
    'Compile CSV Report': 'Buat Laporan CSV',
    'COMPILE CSV REPORT': 'BUAT LAPORAN CSV',
    'Date Segment': 'Segmen Tanggal',
    'DATE SEGMENT': 'SEGMEN TANGGAL',
    'Inbound Views': 'Jumlah Tayangan Masuk',
    'INBOUND VIEWS': 'JUMLAH TAYANGAN MASUK',
    'Asset Interactions': 'Interaksi Menu',
    'ASSET INTERACTIONS': 'INTERAKSI MENU',
    'Telemetry History': 'Riwayat Aktivitas',
    'TELEMETRY HISTORY': 'RIWAYAT AKTIVITAS',
    'Telemetry History (7d)': 'Riwayat Aktivitas (7h)',
    'TELEMETRY HISTORY (7D)': 'RIWAYAT AKTIVITAS (7H)',
    'Growth Trajectory': 'Tren Pertumbuhan',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Frekuensi interaksi tamu dengan menu Anda meningkat dalam periode 7 hari terakhir.',
    'Deep Analysis Engine': 'Mesin Analisis Mendalam',
    'DEEP ANALYSIS ENGINE': 'MESIN ANALISIS MENDALAM',
    'Asset Velocity': 'Performa Menu Terpopuler',
    'ASSET VELOCITY': 'PERFORMA MENU TERPOPULER',
    'Traffic Projections': 'Proyeksi Kunjungan',
    'Telemetry Signal Tracking': 'Pelacakan Sinyal Aktivitas',
    'Service Hour Update': 'Pembaruan Jam Layanan',
    'Late Night Lounge Hours': 'Jam Lounge Malam',
    'Bar & Lounge Hours': 'Jam Operasional Bar & Lounge',
    'Breakfast & Brunch Hours': 'Jam Sarapan & Brunch',
    'Switch Location': 'Ganti Lokasi',
    'SWITCH LOCATION': 'GANTI LOKASI',
    'Global Admin': 'Admin Utama',
    'GLOBAL ADMIN': 'ADMIN UTAMA',
    'Staff Access': 'Akses Staf',
    'STAFF ACCESS': 'AKSES STAF',
    'Venue Overview': 'Ringkasan Restoran',
    'VENUE OVERVIEW': 'RINGKASAN RESTORAN',
    'Unified Telemetry Engine': 'Pusat Pantauan Operasional',
    'UNIFIED TELEMETRY ENGINE': 'PUSAT PANTAUAN OPERASIONAL',
    'Pulse': 'Aktivitas Langsung',
    'PULSE': 'AKTIVITAS LANGSUNG',
    'Insights': 'Wawasan Bisnis',
    'INSIGHTS': 'WAWASAN BISNIS',
    'Reports': 'Laporan',
    'REPORTS': 'LAPORAN',
    'GOVERNANCE': 'TATA KELOLA',
    'Multi-Venue': 'Multi-Cabang',
    'MULTI-VENUE': 'MULTI-CABANG',
    'Menu Views': 'Tayangan Menu',
    'Item Interactions': 'Interaksi Menu',
    'Guest Rating': 'Penilaian Tamu',
    'Revenue (7d)': 'Pendapatan (7h)',
    'Revenue (24h)': 'Pendapatan (24j)',
    'Revenue (30d)': 'Pendapatan (30h)',
    'Delivery orders (7d)': 'Pesanan Pengiriman (7h)',
    'Delivery orders (24h)': 'Pesanan Pengiriman (24j)',
    'Delivery orders (30d)': 'Pesanan Pengiriman (30h)',
    'Delivery revenue': 'Pendapatan Pengiriman',
    'Average delivery order': 'Rata-rata Pesanan Pengiriman',
    'Average delivery fee': 'Rata-rata Biaya Kirim',
    'Zone average': 'Rata-rata Wilayah',
    'Including fees': 'Termasuk Biaya',
    'No deliveries': 'Tidak Ada Pengiriman',
    'No orders': 'Tidak Ada Pesanan',
    'No reviews': 'Belum Ada Ulasan',
    'Live': 'Langsung',
    'LIVE': 'LANGSUNG',
    'reviews': 'ulasan',
    'orders': 'pesanan',
    'deliveries': 'pengiriman',
    'of orders': 'dari pesanan',
    'rate': 'tingkat',
    'Recent Activity': 'Aktivitas Terbaru',
    'Real-time Venue Telemetry': 'Pantauan Restoran Real-time',
    'REAL-TIME VENUE TELEMETRY': 'PANTAUAN RESTORAN REAL-TIME',
    'Rapid Pulse View': 'Tampilan Cepat Langsung',
    'Anonymous Guest': 'Tamu Anonim',
    'scanned menu': 'memindai menu',
    'viewed item': 'melihat menu',
    'interacted with': 'berinteraksi dengan',
    'placed order': 'membuat pesanan',
    'searched menu': 'mencari di menu',
    'Main Landing': 'Halaman Utama',
    'Trace': 'Terlacak',
    'TRACE': 'TERLACAK',
    'Gaining Telemetry...': 'Menghubungkan Data Aktivitas...',
    'No recent traffic detected': 'Tidak ada aktivitas baru-baru ini',
    'Activities will appear here as guests scan your QR code': 'Aktivitas akan muncul di sini saat tamu memindai kode QR Anda',
    'Analyze All Logs': 'Analisis Semua Log',
    'Just now': 'Baru saja',
    'less than a minute ago': 'kurang dari semenit yang lalu',
    '1 minute ago': '1 menit yang lalu',
    '1 hour ago': '1 jam yang lalu',
    'about 1 hour ago': 'sekitar 1 jam yang lalu',
    '1 day ago': '1 hari yang lalu',
    'DAY AGO 1': '1 hari yang lalu',
    '2 days ago': '2 hari yang lalu',
    'DAY AGO 2': '2 hari yang lalu',
    '3 days ago': '3 hari yang lalu',
    'DAY AGO 3': '3 hari yang lalu',
    '4 days ago': '4 hari yang lalu',
    'DAY AGO 4': '4 hari yang lalu',
    '5 days ago': '5 hari yang lalu',
    'DAY AGO 5': '5 hari yang lalu',
    '6 days ago': '6 hari yang lalu',
    'DAY AGO 6': '6 hari yang lalu',
    '7 days ago': '7 hari yang lalu',
    'DAY AGO 7': '7 hari yang lalu',
    'about 1 month ago': 'sekitar 1 bulan yang lalu',
    'Monday': 'Senin',
    'Tuesday': 'Selasa',
    'Wednesday': 'Rabu',
    'Thursday': 'Kamis',
    'Friday': 'Jumat',
    'Saturday': 'Sabtu',
    'Sunday': 'Minggu',
    'Mon': 'Sen',
    'Tue': 'Sel',
    'Wed': 'Rab',
    'Thu': 'Kam',
    'Fri': 'Jum',
    'Sat': 'Sab',
    'Sun': 'Min',
    'Opening Hours': 'Jam Buka',
    'Start Time': 'Waktu Mulai',
    'End Time': 'Waktu Selesai',
    'Opening Time': 'Waktu Buka',
    'Closing Time': 'Waktu Tutup',
    'All Day': 'Sepanjang Hari',
    'Closed': 'Tutup',
    'Active Days': 'Hari Aktif',
    'Active Schedule': 'Jadwal Aktif',
    'Always Available': 'Selalu Tersedia',
    'Always Visible': 'Selalu Terlihat',
    'New Category': 'Kategori Baru',
    'Category': 'Kategori',
    'Category Name': 'Nama Kategori',
    'Category Description': 'Deskripsi Kategori',
    'Display Order': 'Urutan Tampilan',
    'Item Name': 'Nama Menu',
    'Item Description': 'Deskripsi Menu',
    'Base Price': 'Harga Dasar',
    'Preparation Time': 'Waktu Penyiapan',
    'Calories': 'Kalori',
    'Featured Item': 'Menu Unggulan',
    'Chef\'s Recommendation': 'Rekomendasi Koki',
    'Spicy': 'Pedas',
    'Vegetarian': 'Vegetarian',
    'Vegan': 'Vegan',
    'Gluten-Free': 'Bebas Gluten',
    'Halal': 'Halal',
    'Kosher': 'Kosher',
    'Contains Nuts': 'Mengandung Kacang',
    'Dairy-Free': 'Bebas Susu',
    'Allergen Exclusions': 'Informasi Alergen',
    'Table Number': 'Nomor Meja',
    'Assigned Table': 'Meja yang Ditentukan',
    'Seats': 'Kursi',
    'Capacity': 'Kapasitas',
    'Indoor': 'Dalam Ruangan',
    'Outdoor': 'Luar Ruangan',
    'Terrace': 'Teras',
    'Bar': 'Bar',
    'Add Table': 'Tambah Meja',
    'Edit Table': 'Ubah Meja',
    'Delete Table': 'Hapus Meja',
    'Bookings': 'Pemesanan',
    'Add to Waitlist': 'Tambah ke Antrean',
    'Guest Name': 'Nama Tamu',
    'Party Size': 'Jumlah Orang',
    'Arrival Time': 'Waktu Tiba',
    'Special Requests': 'Permintaan Khusus',
    'Confirmed': 'Terkonfirmasi',
    'No Show': 'Tidak Hadir',
    'Add Staff Member': 'Tambah Karyawan',
    'Invite Staff': 'Undang Karyawan',
    'Email Address': 'Alamat Email',
    'Phone Number': 'Nomor Telepon',
    'Download QR Code': 'Unduh Kode QR',
    'Print Table Stands': 'Cetak Stand Meja',
    'Today': 'Hari Ini',
    'Yesterday': 'Kemarin',
    'This Week': 'Minggu Ini',
    'This Month': 'Bulan Ini',
    'Last 30 Days': '30 Hari Terakhir',
    'Access Denied': 'Akses Ditolak',
    'Advanced Settings': 'Pengaturan Lanjutan',
    'Change Image': 'Ganti Gambar',
    'Remove Image': 'Hapus Gambar',
    'Announcement Banner': 'Spanduk Pengumuman',
    'Please fill in all required fields.': 'Harap isi semua kolom yang wajib diisi.',
    'Changes saved successfully': 'Perubahan berhasil disimpan',
    '(You)': '(Anda)',
    'AI Auto-Tag': 'Penandaan Otomatis AI',
    'Analyzing:': 'Menganalisis:',
    'Apple (Fruit/Vegan)': 'Apel (Buah/Vegan)',
    'Cancel editing': 'Batalkan pengeditan',
    'Capacity:': 'Kapasitas:',
    'Carrot (Veggie)': 'Wortel (Sayuran)',
    'CircleDot (Organic)': 'Titik lingkaran (Organik)',
    'Coffee (Caffeine)': 'Kopi (Kafein)',
    'Configuration': 'Konfigurasi',
    'Copy verification record name': 'Salin nama data verifikasi',
    'Copy verification record value': 'Salin nilai data verifikasi',
    'Critical exception intercepted': 'Pengecualian kritis dicegat',
    'Kernel Panic': 'Kesalahan Sistem',
    'DNA (Organic/Bio)': 'DNA (Organik/Bio)',
    'Egg (Ovo/Allergen)': 'Telur (Ovo/Alergen)',
    'Empty': 'Kosong',
    'Engraving...': 'Membuat...',
    'Flame (Spicy)': 'Api (Pedas)',
    'Free': 'Gratis',
    'From': 'Dari',
    'Generating': 'Membuat',
    'Governance & Traceability Studio': 'Pusat Manajemen & Penelusuran Aktivitas',
    'Greet': 'Selamat Datang',
    'Heart (Healthy)': 'Hati (Sehat)',
    'In-App': 'Dalam Aplikasi',
    'Infinity SVG': 'Vektor SVG (Tak Terbatas)',
    'Leaf (Default)': 'Daun (Bawaan)',
    'MAX Feature': 'Fitur Enterprise',
    'Milk (Dairy)': 'Susu (Olahan Susu)',
    'Network': 'Jaringan',
    'Notify SMS': 'Notifikasi SMS',
    'Order': 'Pesanan',
    'Original': 'Asli',
    'Overview': 'Ringkasan',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 Peran Standar',
    'Shield (Safety)': 'Perisai (Keamanan)',
    'Soup (Warm/Soup)': 'Sup (Hangat/Sup)',
    'Standard Pro View': 'Tampilan Standar',
    'Star': 'Bintang',
    'Synchronized with Venue Identity': 'Disesuaikan dengan identitas tempat Anda',
    'Synchronizing Security Context...': 'Menghubungkan secara aman...',
    'Targeted Timing': 'Jadwal Layanan',
    'Targeting': 'Berlaku untuk',
    'To': 'Hingga',
    'Toggle Navigation': 'Beralih Navigasi',
    'Venue': 'Tempat',
    'Venue Governance Suite': 'Pengelolaan & Pengaturan Tempat',
    'Volume:': 'Volume:',
    'WHITEBOARD NOTE...': 'Catatan untuk staf...',
    'Warning (Alert)': 'Peringatan (Pemberitahuan)',
    'Wheat (Flour/Gluten)': 'Gandum (Tepung/Gluten)',
    'Wine (Alcohol)': 'Anggur (Alkohol)',
    'YouTube/Vimeo/Direct Link': 'Tautan Video (YouTube, Vimeo, atau MP4)',
    'Your name': 'Nama Anda',
    '2. Table Number': '2. Nomor Meja',
    'for Morocco or': 'untuk Maroko, atau',
    'in': 'dalam',
    'to': 'ke',
    'e.g. 12 or Patio-1': 'cth. 12 atau Teras-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'cth. Fettuccine buatan tangan, truffle hitam musim dingin, keju parmesan 36 bulan...',
    'e.g. Head Sommelier & Wine Lead': 'cth. Kepala Sommelier',
    'e.g. London': 'cth. London',
    'e.g. SOMMELIER, HOST, BARISTA': 'cth. SOMMELIER, HOST, BARISTA',
    'e.g. Skyline Lounge': 'cth. Skyline Lounge',
    'e.g. TRUFFLE OIL': 'cth. MINYAK TRUFFLE',
    'e.g. The Grand Cafe': 'cth. Grand Cafe',
    'e.g. Window preference, high chair': 'cth. Dekat jendela, kursi bayi',
    'Copied to clipboard!': 'Disalin ke papan klip!',
    'Copy failed.': 'Gagal menyalin.',
    'Link copied!': 'Tautan disalin!',
    'Logo uploaded': 'Logo berhasil diunggah',
    'Cover image uploaded': 'Gambar sampul berhasil diunggah',
    'Profile saved and synced successfully': 'Profil berhasil disimpan dan disinkronkan',
    'Category Created': 'Kategori Dibuat',
    'Category Updated': 'Kategori Diperbarui',
    'Category Deleted': 'Kategori Dihapus',
    'Item Created': 'Item Dibuat',
    'Item Updated': 'Item Diperbarui',
    'Item Deleted': 'Item Dihapus',
    'Menu Published': 'Menu Diterbitkan',
    'Delete Category?': 'Hapus Kategori?',
    'Delete Menu Item?': 'Hapus Item Menu Ini?',
    'Delete Ingredient?': 'Hapus Bahan Ini?',
    'Delete Service Period?': 'Hapus Periode Layanan?',
    'Remove team member?': 'Hapus anggota tim ini?',
    'Cancel invitation?': 'Batalkan undangan?',
    'Revoke API key?': 'Cabut kunci API?',
    'Delete webhook endpoint?': 'Hapus endpoint webhook?',
    'Clear all notifications?': 'Hapus semua notifikasi?',
    'Disconnect Stripe account?': 'Putuskan sambungan akun Stripe?',
    'Disconnect Google Business Profile?': 'Putuskan Profil Bisnis Google?',
    'Campaigns published successfully!': 'Kampanye berhasil diterbitkan!',
    'Changes successfully synchronized with Google Business Profile!': 'Perubahan berhasil disinkronkan dengan Profil Bisnis Google!',
    'Google Business Profile successfully connected!': 'Profil Bisnis Google berhasil terhubung!',
    'Google Business Profile disconnected.': 'Profil Bisnis Google terputus.',
    'Google Post draft saved successfully!': 'Draf postingan Google berhasil disimpan!',
    'Post draft deleted.': 'Draf postingan dihapus.',
    'Review response published successfully!': 'Tanggapan ulasan berhasil diterbitkan!',
    'SEO configuration saved successfully!': 'Konfigurasi SEO berhasil disimpan!',
    'SEO metadata translated across all languages!': 'Metadata SEO diterjemahkan ke semua bahasa!',
    'Venue profile & bio translated across all languages!': 'Profil dan bio tempat diterjemahkan ke semua bahasa!',
    'Reservation configuration saved successfully': 'Pengaturan reservasi berhasil disimpan',
    'Stripe settings saved successfully.': 'Pengaturan Stripe berhasil disimpan.',
    'Print-Ready PDF downloaded successfully': 'PDF Siap Cetak berhasil diunduh',
    'Vector QR PDF downloaded successfully': 'PDF QR Vektor berhasil diunduh',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Paket Pra-Cetak 300 DPI berhasil dibuat',
    'At least one language must be enabled.': 'Minimal satu bahasa harus diaktifkan.',
    'Enter a non-zero percentage before adjusting prices.': 'Masukkan persentase bukan nol sebelum menyesuaikan harga.',
    'Please enter a Venue Name before translating.': 'Harap masukkan Nama Tempat sebelum menerjemahkan.',
    'Please enter an item name before running dietary and allergen analysis.': 'Harap masukkan nama item sebelum menjalankan analisis alergen.',
    'Please write a reply before submitting.': 'Harap tulis balasan sebelum mengirimkan.',
    'Table 3 Called Waiter': 'Meja 3 Memanggil Pelayan',
    'Table Call Bell': 'Bel Panggilan Meja',
    'Waiter Calls': 'Panggilan Pelayan',
    'Waiter Dispatch & Calls': 'Disposisi & Panggilan Pelayan',
    'Kitchen Display & Whiteboard': 'Tampilan Dapur & Papan Catatan',
    'Customer Orders': 'Pesanan Pelanggan',
    'Bakery': 'Toko Roti / Bakery',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Kafe / Kedai Kopi',
    'Fast Food': 'Makanan Cepat Saji',
    'Gourmet / Fine Dining': 'Restoran Mewah / Fine Dining',
    'Hotel': 'Hotel',
    'General Local Business': 'Usaha Lokal Umum',
    'Classic': 'Klasik',
    'Clean': 'Bersih & Rapi',
    'Modern': 'Modern',
    'Elegant': 'Elegan',
    'Energetic': 'Penuh Energi',
    'Glass': 'Efek Kaca',
    'Playful & Casual': 'Santai & Menyenangkan',
    'Sophisticated & Gourmet': 'Canggih & Gourmet',
    'Warm & Welcoming': 'Hangat & Ramah',
    'Executive': 'Eksekutif',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Perlu peningkatan: Menu multi-bahasa tersedia pada paket Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Perlu peningkatan: Fitur pemesanan dan panggilan pelayan tidak tersedia di paket Starter.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Ekspor SVG Vektor adalah fitur Pro & Max. Harap tingkatkan paket Anda.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Ekspor PDF Siap Cetak adalah fitur Pro & Max. Harap tingkatkan paket Anda.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Paket Pro dibatasi hingga 3 kampanye aktif. Jeda kampanye lain atau tingkatkan ke MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Paket Starter mencakup hingga 20 meja (Meja 1–20). Tingkatkan ke PRO untuk meja tanpa batas.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Paket Starter mendukung meja bernomor (1–20). Nama meja kustom (cth. Teras, VIP) memerlukan PRO.',
    'Add Menu Categories & Items': 'Tambah Kategori & Item Menu',
    'Add Menu Items': 'Tambah Item Menu',
    'Organize your menu sections and food items.': 'Atur bagian menu dan hidangan Anda dengan rapi.',
    'Add and manage your food & beverage offerings.': 'Tambahkan dan kelola menu makanan & minuman Anda.',
    'Customize Venue Branding': 'Sesuaikan Branding Tempat',
    'Set your brand colors, logo, and presentation.': 'Atur warna brand, logo, dan gaya tampilan Anda.',
    'Get Your QR Code & Go Live': 'Dapatkan Kode QR & Mulai Beroperasi',
    'Download table QR codes and preview your live menu.': 'Unduh kode QR meja dan pratinjau menu langsung Anda.',
    'Account & Venue Created Successfully!': 'Akun & Tempat Berhasil Dibuat!',
    '⚡ Off-Peak Hours': '⚡ Jam Tidak Sibuk',
    '🍽️ Lunch Crowd': '🍽️ Pengunjung Makan Siang',
    '🚀 Weekend Rush': '🚀 Keramaian Akhir Pekan',
    '🌟 Seasonal Special': '🌟 Spesial Musiman',
    '📣 Engagement Buzz': '📣 Interaksi & Promosi',
    'Festive & Energetic': 'Meriah & Semarak',
    'Urgent & High-Conversion': 'Mendesak & Efektif',
    'Generated 3 tailored campaign strategies': '3 strategi kampanye khusus berhasil dibuat',
    'Could not generate campaign ideas. Please try again.': 'Tidak dapat menghasilkan ide kampanye. Silakan coba lagi.',
    'Venue Themes': 'Tema Tempat',
    'Choose the character of your public menu': 'Pilih karakter visual untuk menu publik Anda',
    'Glassmorphism & Depth': 'Efek Kaca & Kedalaman Visual',
    'Independent XML Sitemap': 'Peta Situs XML Independen',
    'Structured Schema & Sync': 'Data Terstruktur & Sinkronisasi',
    'Improve how your venue appears on Google and in local search results.': 'Tingkatkan tampilan tempat Anda di Google dan hasil pencarian lokal.',
    'Terms of Service': 'Ketentuan Layanan',
    'Confirm your email': 'Konfirmasi email Anda',
    'Check your spam / junk folder': 'Periksa folder spam atau email sampah Anda',
    'Commercial pre-press print production package': 'Paket Produksi Cetak Pra-Cetak Komersial',
    'Over-bleed safety margin': 'Margin Keamanan Potong (Bleed)',
    'Crop Marks': 'Tanda Potong',
    'Guillotine cutting guides': 'Panduan Pemotongan Kertas',
    'Global Banners': 'Banner Global',
    'Featured Items': 'Menu Unggulan',
    'AI Assistant': 'Asisten AI',
    'Custom Role Schemas': 'Skema Peran Kustom',
    'Notification Preferences': 'Preferensi Notifikasi',
    'Preferences saved automatically': 'Preferensi disimpan secara otomatis',
    'Menu Extractor (PDF & Images)': 'Ekstraktor Menu (PDF & Gambar)',
    'Multi-Language Translation': 'Penerjemahan Multi-Bahasa',
    'Start Menu Translation': 'Mulai Terjemahkan Menu',
    'Menu Items Hub': 'Pusat Item Menu',
    'Open Menu Items': 'Buka Item Menu',
    'Enterprise Traceability': 'Penelusuran Jejak Tingkat Lanjut',
    'Full History': 'Riwayat Lengkap',
    'Billing & Plans': 'Penagihan & Paket',
    'Subscription Governance': 'Tata Kelola Berlangganan',
    'Platform Usage': 'Penggunaan Platform',
    'Menu Page Views': 'Tayangan Halaman Menu',
    'Enterprise Identity': 'Identitas Perusahaan',
    'Access Governance': 'Tata Kelola Akses',
    'Required Fields *': 'Kolom Wajib *',
    'Enabled Languages': 'Bahasa yang Diaktifkan',
    'Venue Info & Bio Localizations': 'Lokalisasi Info & Bio Tempat',
    'Public Address': 'Alamat Publik',
    'Public URL Slug': 'Slug URL Publik',
    'Contact Phone': 'Nomor Telepon Kontak',
    'Business Address': 'Alamat Usaha',
    'No Categories Yet': 'Belum Ada Kategori',
    'No Custom Roles Configured': 'Belum Ada Peran Kustom',
    'No Google reviews retrieved yet': 'Belum ada ulasan Google yang diambil',
    'No Guests Found': 'Tidak Ada Pengunjung Ditemukan',
    'No Menu Items Yet': 'Belum Ada Item Menu',
    'No Promotions Configured': 'Belum Ada Promosi Dikonfigurasi',
    'No Service Periods Yet': 'Belum Ada Periode Layanan',
    'No Support Tickets': 'Tidak Ada Tiket Dukungan',
    'No Tables on Floor Plan': 'Belum Ada Meja di Denah Lantai',
    'No Team Members Found': 'Tidak Ada Anggota Tim Ditemukan',
    'No Upcoming Events': 'Tidak Ada Acara Mendatang',
    'No Walk-Ins Recorded': 'Tidak Ada Tamu Langsung Datang',
    'No items are available right now': 'Tidak ada menu yang tersedia saat ini',
    'No notifications found': 'Tidak ada pemberitahuan ditemukan',
    'No reservations matching your filters': 'Tidak ada reservasi yang cocok dengan filter Anda',
    'No tables currently occupied': 'Tidak ada meja yang sedang terisi saat ini',
    'No venue selected': 'Tidak ada tempat yang dipilih',
    'Waitlist': 'Daftar Tunggu',
    'Waitlist is Empty': 'Daftar Tunggu Kosong',
    'Waves (Gluten)': 'Bulir Gandum (Gluten)',
    'We sent a temporary verification code to': 'Kami telah mengirimkan kode verifikasi sementara ke',
    'Website URL': 'URL Situs Web',
    'What the customer receives': 'Manfaat yang diterima pelanggan',
    'White-label control is available on eligible plans.': 'Kontrol white-label tersedia pada paket yang memenuhi syarat.',
    'Why this works:': 'Mengapa strategi ini berhasil:',
    'WiFi Broadcast': 'Pemberitahuan WiFi Tamu',
    'WiFi Password': 'Kata Sandi WiFi',
    'Window seat, dietary request, birthday celebration...': 'Meja dekat jendela, preferensi diet, perayaan ulang tahun...',
    'Your Published Response': 'Tanggapan Anda yang Telah Diterbitkan',
    'Your Venues': 'Tempat Anda',
    '🎉 Ready to publish!': '🎉 Siap diterbitkan!',
    'e.g. Grill, Salad, Pastry': 'cth. Panggangan, Salad, Kue',
    'e.g. Guest called, emergency': 'cth. Tamu menelepon, keadaan darurat',
    'e.g. Italian, Moroccan, Burgers': 'cth. Italia, Maroko, Burger',
    'e.g. Mains, Pasta, Starters': 'cth. Hidangan Utama, Pasta, Makanan Pembuka',
    'e.g. Signature Truffle Pasta': 'cth. Pasta Truffle Khas',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'cth. Spesial Kopi Sore, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'cth. Sarapan, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'cth. Nikmati pertunjukan jazz langsung setiap Jumat malam pukul 20:00!',
  },
  pl: {
    '123 Main St, City': 'ul. Główna 123, Miasto',
    'Balance': 'Saldo punktów',
    'Lifetime': 'Łącznie zdobyte',
    'Source': 'Źródło',
    'Menu': 'Menu',
    'Advanced': 'Zaawansowane',
    'Core Suite': 'Podstawowy pakiet',
    'Privacy': 'Prywatność',
    'Support': 'Pomoc techniczna',
    'Save Layout': 'Zapisz układ sali',
    'Shape:': 'Kształt:',
    'SEARCH LIBRARY...': 'SZUKAJ W BIBLIOTECE...',
    'Search library...': 'Szukaj w bibliotece...',
    'None': 'Brak',
    'Type': 'Typ',
    'Target': 'Cel',
    'Value / Target': 'Wartość / Cel',
    'London (GMT/BST)': 'Londyn (GMT/BST)',
    'New York (EST/EDT)': 'Nowy Jork (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubaj (GST)',
    'Tokyo (JST)': 'Tokio (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Kolor początkowy',
    'Depth Shade': 'Cień głębi',
    'Matrix Geometry': 'Geometria wzoru kodu QR',
    'Eye Architecture': 'Wzór narożników kodu QR',
    'Brand Centerpiece': 'Logo w środku kodu QR',
    'Scannability Shield': 'Zabezpieczenie czytelności skanowania',
    'Environment Blur': 'Rozmycie tła',
    'Color Target:': 'Profil kolorów:',
    'DPI:': 'DPI (Rozdzielczość):',
    '3mm Bleed Box': 'Spad drukarski 3 mm',
    '300 DPI': '300 DPI (Wysoka rozdzielczość)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'Protokół SAML 2.0',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'Identyfikator encji IdP (Entity ID)',
    'SSO URL': 'Adres URL logowania SSO',
    'X.509 Certificate': 'Certyfikat bezpieczeństwa X.509',
    'ACS URL': 'Adres URL usługi ACS',
    'Entity ID': 'Identyfikator encji',
    'Navigate': 'Nawiguj',
    'Select': 'Wybierz',
    'OmniSearch v1.0': 'Wyszukiwarka uniwersalna OmniSearch',
    'Back to order review': 'Wróć do podsumowania zamówienia',
    'Return to Intelligence Studio': 'Wróć do studia analiz',
    'View Full Notification Center': 'Zobacz pełne centrum powiadomień',
    'Read all': 'Przeczytano wszystko',
    'Cancel department': 'Anuluj dział',
    'Save department': 'Zapisz dział',
    'Venue Segmentation': 'Segmentacja lokalu',
    'Collaborate on your menu together.': 'Współpracujcie nad menu całym zespołem.',
    'Timeline': 'Oś czasu',
    'Calendar': 'Kalendarz rezerwacji',
    'Book': 'Zarezerwuj',
    'Duration': 'Czas trwania rezerwacji',
    'Time & Duration': 'Godzina i czas trwania',
    'Seating Preference': 'Preferencje dotyczące stolika',
    'Section': 'Strefa sali',
    'Min Capacity': 'Minimalna liczba osób',
    'Max Capacity': 'Maksymalna liczba osób',
    'Phone (Optional)': 'Numer telefonu (Opcjonalnie)',
    'Name or Walk-in': 'Imię gościa lub klient z ulicy',
    'Next day': 'Następny dzień',
    'Next month': 'Następny miesiąc',
    'Live Connection': 'Połączenie na żywo',
    'Live Inventory': 'Stany na żywo',
    'Main Venue (Full Menu)': 'Lokal główny (Pełne menu)',
    'Preview Menu': 'Podgląd menu',
    'Manual Setup Guide': 'Instrukcja ręcznej konfiguracji',
    'Connected & Active': 'Połączony i aktywny',
    'CTA Action': 'Przycisk wezwania do działania (CTA)',
    'Phone Contact': 'Telefon kontaktowy',
    'Menu URL': 'Adres internetowy menu',
    'Google Location': 'Lokalizacja Google',
    'Field': 'Pole',
    'Linked': 'Połączono',
    'Synchronizing Hub...': 'Synchronizowanie danych...',
    'Data Integrity': 'Spójność i integralność danych',
    'Station Governance Suite': 'Zarządzanie stanowiskami pracy',
    'Open audit log': 'Otwórz dziennik audytu',
    'Signal / Time': 'Zdarzenie / Czas',
    'Identity': 'Użytkownik / Tożsamość',
    'Invitation Invalid': 'Zaproszenie jest nieprawidłowe lub wygasło',
    'Invited To': 'Zaproszono do',
    'Team Limit Reached': 'Osiągnięto limit członków zespołu',
    'Venue Limit Reached': 'Osiągnięto limit lokali',
    'Venue Setup Required': 'Wymagana konfiguracja początkowa lokalu',
    'Create Venue': 'Utwórz lokal',
    'TableGreet Onboarding': 'Wprowadzenie do TableGreet',
    'No payment required': 'Płatność nie jest wymagana',
    'Selected Plan': 'Wybrany plan',
    'Payment Method': 'Metoda płatności',
    'Invoice ID': 'Numer faktury',
    'Verified': 'Zweryfikowano',
    'Accent color': 'Kolor akcentujący',
    'Set the voice and overall light level.': 'Ustaw styl i ogólny poziom jasności.',
    'Layout architecture': 'Architektura układu',
    'Navigation & motion': 'Nawigacja i animacje',
    'Loading preview engine': 'Ładowanie podglądu',
    'Real Live Preview': 'Rzeczywisty podgląd na żywo',
    'Included with Pro & Max': 'Dostępne w planach Pro i Max',
    'Daypart Scheduling': 'Harmonogram pór dnia',
    'Operational Daypart Architecture': 'Architektura pór operacyjnych',
    'Service Protocol': 'Protokół obsługi',
    'Configuration Engine': 'Silnik konfiguracji',
    'Linked Department': 'Powiązany dział',
    'Team Hub': 'Centrum zespołu',
    'Team Size': 'Liczba członków zespołu',
    'Joined': 'Dołączył',
    'Cancel Invitation': 'Anuluj zaproszenie',
    'Initialize New Organization': 'Zainicjuj nową organizację',
    'Operational': 'W pełni sprawny',
    'Pro Feature': 'Funkcja Pro',
    'Current State': 'Stan obecny',
    'Open on the big TV': 'Otwórz na dużym ekranie TV',
    'Menu Board': 'Elektroniczna tablica menu',
    'Reward': 'Nagroda',
    'Reward name': 'Nazwa nagrody',
    'Cancel & refund': 'Anuluj i zwróć środki',
    'Code': 'Kod',
    'Find': 'Znajdź',
    'Complete': 'Zakończone',
    'Seated': 'Przy stoliku',
    'No-Show': 'Nie przybył',
    'Walk-in': 'Gość bez rezerwacji',
    'Party': 'Liczba osób',
    'Turnover': 'Rotacja stolików',
    'We couldn\'t load the menu': 'Nie udało się załadować menu',
    'Venue Not Found': 'Nie znaleziono lokalu',
    'Live Availability': 'Dostępność na żywo',
    'Multi-Language Suite': 'Moduł wielojęzyczny',
    'Key name': 'Nazwa klucza',
    'Revoke API key': 'Unieważnij klucz API',
    'Delete webhook': 'Usuń webhook',
    'Identity Provider': 'Dostawca tożsamości (IdP)',
    'Provider': 'Dostawca',
    'Mandatory MFA': 'Wymagane uwierzytelnianie wielopoziomowe (MFA)',
    'Account Identity': 'Tożsamość konta',
    'Login Email': 'Adres e-mail do logowania',
    'New Login Email': 'Nowy adres e-mail do logowania',
    'Phone Login': 'Logowanie numerem telefonu',
    'SMS Verification Code': 'Kod weryfikacyjny SMS',
    'Integration Method': 'Metoda integracji',
    'Stripe Connect': 'Płatności Stripe Connect',
    'Stripe Secret Key': 'Klucz prywatny Stripe',
    'Stripe Webhook Secret': 'Klucz webhooka Stripe',
    'Client-Owned': 'Własność klienta',
    'Verify connection': 'Sprawdź połączenie',
    'Setup Health': 'Stan konfiguracji',
    'Privacy Protected': 'Prywatność chroniona',
    'Privacy Policy': 'Polityka prywatności',
    'Continue': 'Kontynuuj',
    'Sign In': 'Zaloguj się',
    'Didn\'t get it?': 'Nie otrzymałeś?',
    'Logged In Account': 'Zalogowane konto',
    'Annual': 'Rocznie',
    'Unread': 'Nieprzeczytane',
    'Starred': 'Oznaczone gwiazdką',
    'High / Urgent': 'Wysoki / Pilny',
    'Mark Read': 'Oznacz jako przeczytane',
    'Delete notification': 'Usuń powiadomienie',
    'Urgent': 'Pilne',
    'High': 'Wysoki',
    'Normal': 'Normalny',
    'Low': 'Niski',
    'Select a Ticket': 'Wybierz zgłoszenie',
    'Urgent (Immediate attention)': 'Pilne (Wymaga natychmiastowej uwagi)',
    'Type your reply here...': 'Wpisz swoją odpowiedź tutaj...',
    'Print Card (PDF)': 'Drukuj stand na stolik (PDF)',
    'Advanced Printing Engine': 'Zaawansowany silnik druku',
    'Multi-Venue Governance': 'Zarządzanie wieloma lokalami',
    'Network Revenue': 'Przychody całej sieci',
    'Live Network Total': 'Łącznie w sieci na żywo',
    'Audit Log Hub': 'Dziennik audytu i kontroli',
    'Export Log': 'Eksportuj dziennik',
    'CUSTOM ROLE SCHEMAS': 'SCHEMATY RÓL NIESTANDARDOWYCH',
    'Unique internal role identifier.': 'Unikalny wewnętrzny identyfikator roli.',
    'Human-friendly role label.': 'Czytelna nazwa roli dla zespołu.',
    'Marketing Hub': 'Centrum marketingu',
    'Revenue Generation Suite': 'Zestaw narzędzi wzrostu przychodów',
    'Active Campaign Limit Reached (3/3)': 'Osiągnięto limit aktywnych kampanii (3/3)',
    'Banner Content': 'Treść banera',
    'Style Profile': 'Styl wizualny',
    'Action URL (Optional)': 'Link do działania (Opcjonalnie)',
    'Promotion Label': 'Etykieta promocji',
    'Target Selection': 'Wybór pozycji objętych promocją',
    'Edit Promotion': 'Edytuj promocję',
    'Delete Promotion': 'Usuń promocję',
    'Loyalty Engine': 'Program lojalnościowy',
    'Reward catalog': 'Katalog nagród',
    'Add reward': 'Dodaj nagrodę',
    'Loyalty ID': 'Numer karty stałego klienta',
    'No point activity yet': 'Brak historii punktów',
    'Scan or enter QR value': 'Zeskanuj lub wpisz kod QR',
    'TableGreet Admin': 'Panel TableGreet',
    'Capacity Pacing': 'Regulacja tempa rezerwacji',
    'Cancellation Policy': 'Zasady anulowania rezerwacji',
    'Booking Experience': 'Rezerwacja stolika',
    'Floor Occupancy': 'Obłożenie sali',
    'Currently Seated': 'Obecnie przy stolikach',
    'Seat Party': 'Posadź gości',
    'Seat Table': 'Przydziel stolik',
    'Select Table': 'Wybierz stolik',
    'Table / Capacity': 'Stolik / Miejsca',
    'Table Name/Number': 'Nazwa lub numer stolika',
    'Mark Arrived': 'Oznacz jako przybyły',
    'Mark No-Show': 'Oznacz jako nieobecny',
    'Complete Dining': 'Zakończ obsługę stolika',
    'Standard Dining (Default)': 'Standardowa sala (Domyślnie)',
    'Walk-In Dining Log': 'Księga gości bez rezerwacji',
    'Notification Center': 'Centrum powiadomień',
    'Single Sign-On': 'Pojedyncze logowanie (SSO)',
    'Welcome Aboard!': 'Witamy w TableGreet!',
    'Select your TableGreet plan': 'Wybierz plan TableGreet',
    'Free Plan Activation': 'Aktywacja darmowego planu',
    'Venue Created!': 'Lokal został pomyślnie utworzony!',
    'Upgrade for Unlimited': 'Uaktualnij do wersji bez limitów',
    'Auto-Tag Menu': 'Automatyczne tagowanie menu',
    'Start Auto-Tagging': 'Rozpocznij automatyczne tagowanie',
    'Launch Auto-Tagger': 'Uruchom automatyczne tagowanie',
    'Launch PDF Importer': 'Importuj menu z pliku PDF',
    'Analyzing Menu...': 'Analizowanie menu...',
    'Analyzing Recipe...': 'Analizowanie przepisu...',
    'Manual Availability': 'Ręczna dostępność',
    'Technical Breakdown': 'Szczegóły techniczne',
    'Dietary Taxonomy': 'Diety i alergeny',
    'Dietary & Allergen AI': 'Sztuczna inteligencja dla diet i alergenów',
    'Inherit from Category': 'Dziedzicz z kategorii',
    'Ingredient Library': 'Biblioteka składników',
    'Ingredient Name': 'Nazwa składnika',
    'Menu Organization': 'Organizacja menu',
    'Inventory Taxonomy Hub': 'Centrum taksonomii menu',
    'Category Intelligence': 'Inteligentna analiza kategorii',
    'Service Period': 'Pora serwowania',
    'Manual Configuration / All Day': 'Konfiguracja ręczna / Cały dzień',
    'Operational Department': 'Dział operacyjny',
    'Global / No Department': 'Ogólne / Brak działu',
    'Cancel category': 'Anuluj kategorię',
    'New category name...': 'Nazwa nowej kategorii...',
    'Save category': 'Zapisz kategorię',
    'QR Matrix Only': 'Tylko kod QR',
    'Vector & PDF': 'Wektory i PDF',
    'Vector QR (SVG)': 'Wektorowy kod QR (SVG)',
    'Vector QR (PDF)': 'Drukarski kod QR (PDF)',
    'Save QR Style': 'Zapisz styl kodu QR',
    'Share Link': 'Udostępnij link',
    'High Fidelity 300DPI': 'Wysoka rozdzielczość 300 DPI',
    'Generating 300 DPI PDF...': 'Generowanie pliku PDF 300 DPI...',
    'Give your venue a point of view.': 'Nadaj swojemu lokalowi wyjątkowy styl wizualny.',
    'Signage Menu Not Found': 'Nie znaleziono menu dla ekranu',
    'Scan Menu': 'Zeskanuj menu',
    'Open on your phone': 'Otwórz na telefonie',
    'Full Screen': 'Pełny ekran',
    'Lock TV board': 'Zablokuj ekran TV',
    'Big-Screen Menu': 'Menu na duży ekran',
    'TV Signage': 'Cyfrowe ekrany TV',
    'General Information': 'Informacje ogólne',
    'Venue Name *': 'Nazwa lokalu *',
    'Public Slug *': 'Publiczny identyfikator linku *',
    'Venue Logo': 'Logo lokalu',
    'Hero Cover Image': 'Główne zdjęcie w tle',
    'Venue Timezone': 'Strefa czasowa lokalu',
    'Primary Brand Color': 'Główny kolor marki',
    'TableGreet footer credit': 'Podpis TableGreet w stopce',
    'White-Label Branding': 'Branding White-Label',
    'Client DNS Setup': 'Konfiguracja DNS domeny',
    'Copy CNAME target': 'Kopiuj cel CNAME',
    'Copy TableGreet URL': 'Kopiuj link TableGreet',
    'Included TableGreet URL': 'Standardowy link TableGreet',
    'Open TableGreet URL': 'Otwórz link TableGreet',
    'Public menu': 'Publiczne menu dla gości',
    'Public venue delivery': 'Dostawa zamówień z menu online',
    'Zone name': 'Nazwa strefy',
    'Coverage note': 'Informacja o obszarze dostawy',
    'Default ETA min': 'Minimalny szacowany czas (min)',
    'Default ETA max': 'Maksymalny szacowany czas (min)',
    'Packaging fee': 'Opłata za opakowanie',
    'Courier handled by': 'Dostawa realizowana przez',
    'Third-party courier': 'Zewnętrzna firma kurierska',
    'Unified Experience': 'Spójne doświadczenie',
    'Save WiFi to Venue': 'Zapisz Wi-Fi dla lokalu',
    'Network Name (SSID)': 'Nazwa sieci Wi-Fi (SSID)',
    'Audio Alert Chime': 'Dźwięk powiadomienia',
    'Time Format': 'Format czasu',
    'Auto / Default': 'Automatycznie / Domyślnie',
    'Syncing SEO & Google Data...': 'Synchronizowanie danych SEO i Google...',
    'Google Connection': 'Połączenie z Google',
    'Search Engine Metadata': 'Metadane dla wyszukiwarek',
    'Social card preview': 'Podgląd w mediach społecznościowych',
    'Allow Indexing': 'Zezwalaj na indeksowanie',
    'Canonical URL Override': 'Nadpisanie kanonicznego adresu URL',
    'Price Range': 'Przedział cenowy',
    'Latitude': 'Szerokość geograficzna',
    'Longitude': 'Długość geograficzna',
    'Google Place ID / Location ID': 'Identyfikator miejsca Google',
    'Google Search Preview': 'Podgląd w wyszukiwarce Google',
    'Linked Google Location': 'Połączona lokalizacja Google',
    'SEO Documentation': 'Dokumentacja SEO',
    ', and': ', oraz',
    'and': 'oraz',
    'Delete Department?': 'Usunąć dział?',
    'Delete Department': 'Usuń dział',
    'Live URL': 'Link publiczny',
    'Copy full live URL': 'Kopiuj pełny link publiczny',
    'Open live department menu': 'Otwórz menu działu na żywo',
    'Loading departments...': 'Ładowanie działów...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Segmentacja lokalu na wiele działów to funkcja premium dostępna w planie PRO i wyższych.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Podziel menu według sal, stolików, barów lub stref w lokalu.',
    'Targeted Ordering': 'Ukierunkowane zamówienia',
    'Route guests and orders to specific preparation areas.': 'Kieruj gości i zamówienia do wyznaczonych stref przygotowania.',
    'Operational Departments': 'Działy operacyjne',
    'OPERATIONAL DEPARTMENTS': 'DZIAŁY OPERACYJNE',
    'Venue Segmentation Hub': 'Centrum segmentacji lokalu',
    'VENUE SEGMENTATION HUB': 'CENTRUM SEGMENTACJI LOKALU',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Twórz działy, aby grupować sekcje menu. Na przykład duży lokal może mieć',
    'Restaurant': 'Restauracja',
    'Poolside': 'Strefa basenowa',
    'department, each with its own categories.': ', każdy z własnymi kategoriami.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Działy pozwalają podzielić lokal na sekcje, takie jak Restauracja, Bar czy Basen — każda z osobnymi kategoriami.',
    'Department name': 'Nazwa działu',
    'New department name...': 'Nazwa nowego działu...',
    'No Linked Daypart (Always Available)': 'Brak powiązanych godzin (zawsze dostępne)',
    'Linked Daypart': 'Powiązany przedział godzinowy',
    'Localizations': 'Lokalizacje',
    'LOCALIZATIONS': 'LOKALIZACJE',
    'AI Auto-Translate': 'Automatyczne tłumaczenie AI',
    'AI AUTO-TRANSLATE': 'AUTOMATYCZNE TŁUMACZENIE AI',
    'Translated name': 'Przetłumaczona nazwa',
    'No Departments Yet': 'Brak działów',
    'Add First Department': 'Dodaj pierwszy dział',
    'Avg Order Value': 'Średnia wartość zamówienia',
    'AVG ORDER VALUE': 'ŚREDNIA WARTOŚĆ ZAMÓWIENIA',
    'item taps': 'kliknięć w dania',
    'ITEM TAPS': 'KLIKNIĘĆ W DANIA',
    'Avg Rating': 'Średnia ocena',
    'AVG RATING': 'ŚREDNIA OCENA',
    'Revenue (Last 7 Days)': 'Przychody (ostatnie 7 dni)',
    'REVENUE (LAST 7 DAYS)': 'PRZYCHODY (OSTATNIE 7 DNI)',
    'Daily breakdown': 'Zestawienie dzienne',
    'DAILY BREAKDOWN': 'ZESTAWIENIE DZIENNE',
    'Menu Views (Last 7 Days)': 'Wyświetlenia menu (ostatnie 7 dni)',
    'MENU VIEWS (LAST 7 DAYS)': 'WYŚWIETLENIA MENU (OSTATNIE 7 DNI)',
    'Guest traffic': 'Ruch gości',
    'GUEST TRAFFIC': 'RUCH GOŚCI',
    'Most Viewed Items': 'Najczęściej oglądane dania',
    'MOST VIEWED ITEMS': 'NAJCZĘŚCIEJ OGLĄDANE DANIA',
    'Top Rated Items': 'Najwyżej oceniane dania',
    'TOP RATED ITEMS': 'NAJWYŻEJ OCENIANE DANIA',
    'Waiter Requests': 'Wezwania kelnera',
    'WAITER REQUESTS': 'WEZWANIA KELNERA',
    'Total': 'Łącznie',
    'TOTAL': 'ŁĄCZNIE',
    'Order Status (30 days)': 'Status zamówień (30 dni)',
    'ORDER STATUS (30 DAYS)': 'STATUS ZAMÓWIEŃ (30 DNI)',
    'Recent Guest Feedback': 'Najnowsze opinie gości',
    'RECENT GUEST FEEDBACK': 'NAJNOWSZE OPINIE GOŚCI',
    'Conversion Rate': 'Współczynnik konwersji',
    'CONVERSION RATE': 'WSPÓŁCZYNNIK KONWERSJI',
    'of menu visitors placed an order': 'gości przeglądających menu złożyło zamówienie',
    'Refreshed': 'Zaktualizowano',
    'REFRESHED': 'ZAKTUALIZOWANO',
    'Last 30 days': 'Ostatnie 30 dni',
    'LAST 30 DAYS': 'OSTATNIE 30 DNI',
    'cash': 'gotówka',
    'Cash': 'Gotówka',
    'CASH': 'GOTÓWKA',
    'bill': 'rachunek',
    'Bill': 'Rachunek',
    'BILL': 'RACHUNEK',
    'water': 'woda',
    'Water': 'Woda',
    'WATER': 'WODA',
    'service': 'obsługa',
    'Service': 'Obsługa',
    'SERVICE': 'OBSŁUGA',
    'No interaction data yet': 'Brak zarejestrowanych interakcji',
    'No ratings collected yet': 'Brak zebranych ocen',
    'No requests in period': 'Brak wezwań w tym okresie',
    'No feedback collected yet': 'Brak zebranych opinii',
    'No menu views recorded yet': 'Brak zarejestrowanych wyświetleń menu',
    'Dish Rating Breakdown': 'Szczegółowa ocena dań',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Ranking dań i wskaźniki satysfakcji gości są dostępne wyłącznie w planach MAX i Enterprise.',
    'Customer Sentiment & Reviews': 'Opinie i satysfakcja gości',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Bezpośrednie recenzje gości i analiza nastrojów wymagają subskrypcji MAX lub Enterprise.',
    'Reviews AI': 'AI do analizy opinii',
    'Guest Feedback': 'Opinie gości',
    'Upgrade to unlock rating analytics →': 'Uaktualnij plan, aby odblokować analitykę ocen →',
    'Upgrade to Max': 'Uaktualnij do Max',
    'UPGRADE TO MAX': 'UAKTUALNIJ DO MAX',
    'MAX PLAN': 'PLAN MAX',
    'No Data Yet': 'Brak danych',
    'Share your QR code to start collecting real venue insights.': 'Udostępnij swój kod QR, aby zbierać rzetelne statystyki lokalu.',
    'Revenue Analytics': 'Analityka przychodów',
    'REVENUE ANALYTICS': 'ANALITYKA PRZYCHODÓW',
    'Track your earnings, orders, and average ticket values over time.': 'Śledź przychody, liczbę zamówień i średnią wartość rachunku w czasie.',
    'Operational Velocity': 'Tempo operacyjne',
    'OPERATIONAL VELOCITY': 'TEMPO OPERACYJNE',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Monitoruj ruch gości, popularność dań i obciążenie obsługi kelnerskiej.',
    'Upgrade to Pro': 'Uaktualnij do Pro',
    'UPGRADE TO PRO': 'UAKTUALNIJ DO PRO',
    'Loading Insights…': 'Ładowanie analiz…',
    'Loading Insights...': 'Ładowanie analiz...',
    'Telemetry Unavailable': 'Dane aktywności niedostępne',
    'TELEMETRY UNAVAILABLE': 'DANE AKTYWNOŚCI NIEDOSTĘPNE',
    'No Data Captured': 'Brak zarejestrowanych danych',
    'NO DATA CAPTURED': 'BRAK ZAREJESTROWANYCH DANYCH',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Udostępnij swój kod QR, aby zbierać dane o gościach i zamówieniach w czasie rzeczywistym.',
    'INTELLIGENCE STUDIO': 'CENTRUM ANALITYKI',
    'Insights Engine Locked': 'Zaawansowana analityka zablokowana',
    'INSIGHTS ENGINE LOCKED': 'ZAAWANSOWANA ANALITYKA ZABLOKOWANA',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Zaawansowana analiza trendów, operacje masowe i eksport danych są dostępne dla użytkowników',
    'Unlock Pro Features': 'Odblokuj funkcje Pro',
    'UNLOCK PRO FEATURES': 'ODBLOKUJ FUNKCJE PRO',
    'MENU VIEWS': 'WYŚWIETLENIA MENU',
    'ITEM INTERACTIONS': 'INTERAKCJE Z DANIE',
    'GUEST RATING': 'OCENA GOŚCI',
    'DELIVERY REVENUE': 'PRZYCHÓD Z DOSTAW',
    'AVERAGE DELIVERY ORDER': 'ŚREDNIE ZAMÓWIENIE Z DOSTAWĄ',
    'AVERAGE DELIVERY FEE': 'ŚREDNIA OPŁATA ZA DOSTAWĘ',
    'INCLUDING FEES': 'W TYM OPŁATY',
    'ZONE AVERAGE': 'ŚREDNIA DLA STREFY',
    'TRAFFIC PROJECTIONS': 'PROGNOZY RUCHU GOŚCI',
    'TELEMETRY SIGNAL TRACKING': 'ŚLEDZENIE SYGNAŁÓW AKTYWNOŚCI',
    'VEGETARIAN': 'WEGETARIAŃSKIE',
    'VEGAN': 'WEGAŃSKIE',
    'SPICY': 'PIKANTNE',
    'GLUTEN-FREE': 'BEZGLUTENOWE',
    'Reset: All In-Stock': 'Resetuj: Wszystko na stanie',
    'Emergency Stop (All Out)': 'Zatrzymanie awaryjne: Wszystko wyprzedane',
    'No compliance issues': 'Nie wykryto żadnych problemów',
    'NO COMPLIANCE ISSUES': 'NIE WYKRYTO ŻADNYCH PROBLEMÓW',
    'detected in current menu': 'w aktualnym menu',
    'DETECTED IN CURRENT MENU': 'W AKTUALNYM MENU',
    'Telemetry History (24h)': 'Historia aktywności (24h)',
    'TELEMETRY HISTORY (24H)': 'HISTORIA AKTYWNOŚCI (24H)',
    'Telemetry History (30d)': 'Historia aktywności (30 dni)',
    'TELEMETRY HISTORY (30D)': 'HISTORIA AKTYWNOŚCI (30 DNI)',
    'Save': 'Zapisz',
    'Saved': 'Zapisano',
    'Saving...': 'Zapisywanie...',
    'Save Changes': 'Zapisz zmiany',
    'Save Settings': 'Zapisz ustawienia',
    'Cancel': 'Anuluj',
    'Close': 'Zamknij',
    'Create': 'Utwórz',
    'Add': 'Dodaj',
    'Edit': 'Edytuj',
    'Delete': 'Usuń',
    'Remove': 'Usuń',
    'Update': 'Aktualizuj',
    'Refresh': 'Odśwież',
    'Search': 'Szukaj',
    'Search...': 'Szukaj...',
    'Search items...': 'Szukaj dań...',
    'Search categories...': 'Szukaj kategorii...',
    'Filter': 'Filtruj',
    'All': 'Wszystko',
    'Export': 'Eksportuj',
    'Import': 'Importuj',
    'Download': 'Pobierz',
    'Upload': 'Prześlij',
    'Copy': 'Kopiuj',
    'Copied': 'Skopiowano',
    'Copied!': 'Skopiowano!',
    'Preview': 'Podgląd',
    'Open': 'Otwarte',
    'View': 'Zobacz',
    'Manage': 'Zarządzaj',
    'Configure': 'Konfiguruj',
    'Enable': 'Włącz',
    'Disable': 'Wyłącz',
    'Enabled': 'Włączone',
    'Disabled': 'Wyłączone',
    'Active': 'Aktywny',
    'Inactive': 'Nieaktywny',
    'Available': 'Dostępne',
    'Unavailable': 'Niedostępne',
    'Visible': 'Widoczny',
    'Hidden': 'Ukryty',
    'Required': 'Wymagane',
    'Optional': 'Opcjonalne',
    'Confirm': 'Potwierdź',
    'Back': 'Wstecz',
    'Next': 'Dalej',
    'Done': 'Gotowe',
    'Upgrade': 'Uaktualnij',
    'Sign Out': 'Wyloguj',
    'Exit to Main Admin': 'Wróć do głównego panelu',
    'Change Language': 'Zmień język',
    'Add Category': 'Dodaj kategorię',
    'Add Menu Item': 'Dodaj danie',
    'Add Item': 'Dodaj pozycję',
    'Add Ingredient': 'Dodaj składnik',
    'Add Department': 'Dodaj dział',
    'Delete Item': 'Usuń danie',
    'Delete Category': 'Usuń kategorię',
    'Upload Image': 'Prześlij zdjęcie',
    'Upload Photo': 'Prześlij zdjęcie',
    'Select Category': 'Wybierz kategorię',
    'Select Department': 'Wybierz dział',
    'Add translation...': 'Dodaj tłumaczenie...',
    'Clear': 'Wyczyść',
    'Reset': 'Resetuj',
    'Apply': 'Zastosuj',
    'Submit': 'Wyślij',
    'Core & Operations': 'Operacje i obsługa',
    'Menu & Experience': 'Menu i wrażenia',
    'Enterprise & Security': 'Zarządzanie i zespół',
    'System & Control': 'Ustawienia i pomoc',
    'Intelligence Studio': 'Centrum analityki',
    'Venue Insights': 'Raporty i sprzedaż',
    'Reservations': 'Rezerwacje',
    'Floor Plan': 'Plan sali',
    'Live Waitlist': 'Lista oczekujących',
    'Walk-Ins': 'Goście z ulicy',
    'Walk-ins': 'Goście z ulicy',
    'Departments': 'Działy',
    'Service Hours': 'Godziny serwisu',
    'Categories': 'Kategorie',
    'Menu Categories': 'Kategorie menu',
    'Menu Items': 'Pozycje menu',
    'Items': 'Pozycje',
    'Ingredients': 'Składniki',
    'Loyalty Program': 'Program lojalnościowy',
    'Campaign Studio': 'Promocje i rabaty',
    'Signage': 'Ekrany cyfrowe',
    'QR Code': 'Kody QR',
    'Kitchen Ops': 'Ekran kuchni',
    'Theme Engine': 'Wygląd i kolory',
    'Governance': 'Zarządzanie siecią',
    'Audit Log': 'Dziennik aktywności',
    'API & Webhooks': 'Integracje',
    'SSO & Security': 'Bezpieczeństwo i logowanie',
    'Team': 'Pracownicy',
    'Billing': 'Rozliczenia i faktury',
    'SEO & Google': 'Widoczność w Google',
    'Notifications': 'Powiadomienia',
    'Support Center': 'Centrum pomocy',
    'Settings': 'Ustawienia lokalu',
    'Name': 'Nazwa',
    'Description': 'Opis',
    'Price': 'Cena',
    'Status': 'Status',
    'Actions': 'Akcje',
    'Role': 'Rola',
    'Email': 'E-mail',
    'Phone': 'Telefon',
    'Address': 'Adres',
    'City': 'Miasto',
    'Currency': 'Waluta',
    'Language': 'Język',
    'Theme': 'Motyw',
    'General': 'Ogólne',
    'Total Revenue': 'Łączny przychód',
    'Total Orders': 'Wszystkie zamówienia',
    'Average Order': 'Średnie zamówienie',
    'Active Sessions': 'Aktywne stoliki',
    'Recent Orders': 'Ostatnie zamówienia',
    'Live Orders': 'Zamówienia na żywo',
    'Table': 'Stolik',
    'Date': 'Data',
    'Time': 'Godzina',
    'Amount': 'Kwota',
    'Customer': 'Klient',
    'In Stock': 'W magazynie',
    'Out of Stock': 'Wyprzedane',
    'Sold Out': 'Wyprzedane',
    'Dine In': 'Na miejscu',
    'Pickup': 'Odbiór osobisty',
    'Delivery': 'Dostawa',
    'Paid': 'Opłacone',
    'Unpaid': 'Nieopłacone',
    'Pending': 'Oczekujące',
    'Preparing': 'W przygotowaniu',
    'Ready': 'Gotowe',
    'Completed': 'Zakończone',
    'Cancelled': 'Anulowane',
    'Loading...': 'Ładowanie...',
    'No data': 'Brak danych',
    'No results found': 'Nie znaleziono wyników',
    'No items found': 'Nie znaleziono pozycji',
    'No translations defined yet': 'Brak dodanych tłumaczeń',
    'Are you sure?': 'Czy na pewno?',
    'This action cannot be undone.': 'Ta operacja jest nieodwracalna.',
    'Settings saved successfully!': 'Ustawienia zostały pomyślnie zapisane!',
    'Administrator': 'Administrator',
    'Manager': 'Kierownik',
    'Owner': 'Właściciel',
    'Chef': 'Szef kuchni',
    'Waiter': 'Kelner',
    'Cashier': 'Kasjer',
    'Type a command or search...': 'Wpisz polecenie lub szukaj...',
    'Command Palette': 'Paleta poleceń',
    'Demand Velocity': 'Dynamika popytu',
    'DEMAND VELOCITY': 'DYNAMIKA POPYTU',
    'Top Performing Entities': 'Najpopularniejsze pozycje',
    'TOP PERFORMING ENTITIES': 'NAJPOPULARNIEJSZE POZYCJE',
    'Hourly Load Analysis': 'Godzinowa analiza obłożenia',
    'HOURLY LOAD ANALYSIS': 'GODZINOWA ANALIZA OBŁOŻENIA',
    'Bulk Command Center': 'Centrum operacji masowych',
    'BULK COMMAND CENTER': 'CENTRUM OPERACJI MASOWYCH',
    'Governance Scan': 'Skanowanie menu',
    'GOVERNANCE SCAN': 'SKANOWANIE MENU',
    'No compliance issues detected in current menu': 'Nie wykryto żadnych problemów w bieżącym menu',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'NIE WYKRYTO ŻADNYCH PROBLEMÓW W BIEŻĄCYM MENU',
    'Procedural Tagging': 'Automatyczne tagowanie',
    'PROCEDURAL TAGGING': 'AUTOMATYCZNE TAGOWANIE',
    'Name match...': 'Dopasuj nazwę...',
    '...Name match': 'Dopasuj nazwę...',
    'Healthy': 'Zdrowe',
    'HEALTHY': 'ZDROWE',
    'Apply Tags': 'Zastosuj tagi',
    'APPLY TAGS': 'ZASTOSUJ TAGI',
    'Global Price Shift': 'Globalna zmiana cen',
    'GLOBAL PRICE SHIFT': 'GLOBALNA ZMIANA CEN',
    'Adjust': 'Zastosuj',
    'ADJUST': 'ZASTOSUJ',
    'Availability Override': 'Natychmiastowa zmiana dostępności',
    'AVAILABILITY OVERRIDE': 'NATYCHMIASTOWA ZMIANA DOSTĘPNOŚCI',
    'Reset: ALL IN-STOCK': 'Zresetuj: WSZYSTKO DOSTĘPNE',
    'RESET: ALL IN-STOCK': 'ZRESETUJ: WSZYSTKO DOSTĘPNE',
    'EMERGENCY STOP (ALL OUT)': 'Zatrzymanie awaryjne (WSZYSTKO WYPRZEDANE)',
    'Integrity Engine': 'Silnik integralności menu',
    'INTEGRITY ENGINE': 'SILNIK INTEGRALNOŚCI MENU',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Zautomatyzowany system monitoruje dostępność dań, anomalie cenowe i poprawność opisów w całym lokalu.',
    'Inventory Audit Export': 'Eksport audytu menu',
    'INVENTORY AUDIT EXPORT': 'EKSPORT AUDYTU MENU',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Wygeneruj kompleksowy raport wydajności menu i metadanych do weryfikacji offline.',
    'Enterprise Data Export': 'Zaawansowany eksport danych',
    'ENTERPRISE DATA EXPORT': 'ZAAWANSOWANY EKSPORT DANYCH',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Generuj i pobieraj kompleksowe audyty wydajności w standardowych formatach. Zawiera ruch gości, popyt na dania i konwersję.',
    'JSON Snapshot': 'Migawka JSON',
    'JSON SNAPSHOT': 'MIGAWKA JSON',
    'Compile CSV Report': 'Kompiluj raport CSV',
    'COMPILE CSV REPORT': 'KOMPILUJ RAPORT CSV',
    'Date Segment': 'Przedział daty',
    'DATE SEGMENT': 'PRZEDZIAŁ DATY',
    'Inbound Views': 'Liczba wyświetleń',
    'INBOUND VIEWS': 'LICZBA WYŚWIETLEŃ',
    'Asset Interactions': 'Interakcje z daniami',
    'ASSET INTERACTIONS': 'INTERAKCJE Z DANIAMI',
    'Telemetry History': 'Historia aktywności',
    'TELEMETRY HISTORY': 'HISTORIA AKTYWNOŚCI',
    'Telemetry History (7d)': 'Historia aktywności (7 dni)',
    'TELEMETRY HISTORY (7D)': 'HISTORIA AKTYWNOŚCI (7 DNI)',
    'Growth Trajectory': 'Trajektoria wzrostu',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Częstotliwość interakcji z menu wykazuje tendencję wzrostową w ostatnich 7 dniach.',
    'Deep Analysis Engine': 'Zaawansowana analiza',
    'DEEP ANALYSIS ENGINE': 'ZAAWANSOWANA ANALIZA',
    'Asset Velocity': 'Popularność dań',
    'ASSET VELOCITY': 'POPULARNOŚĆ DAŃ',
    'Traffic Projections': 'Prognozy frekwencji',
    'Telemetry Signal Tracking': 'Śledzenie sygnałów aktywności',
    'Service Hour Update': 'Aktualizacja godzin serwisu',
    'Late Night Lounge Hours': 'Nocne godziny lounge',
    'Bar & Lounge Hours': 'Godziny baru i salonu',
    'Breakfast & Brunch Hours': 'Godziny śniadań i brunchu',
    'Switch Location': 'Zmień lokalizację',
    'SWITCH LOCATION': 'ZMIEŃ LOKALIZACJĘ',
    'Global Admin': 'Główny administrator',
    'GLOBAL ADMIN': 'GŁÓWNY ADMINISTRATOR',
    'Staff Access': 'Dostęp dla personelu',
    'STAFF ACCESS': 'DOSTĘP DLA PERSONELU',
    'Venue Overview': 'Przegląd lokalu',
    'VENUE OVERVIEW': 'PRZEGLĄD LOKALU',
    'Unified Telemetry Engine': 'Zintegrowany monitor aktywności',
    'UNIFIED TELEMETRY ENGINE': 'ZINTEGROWANY MONITOR AKTYWNOŚCI',
    'Pulse': 'Na żywo',
    'PULSE': 'NA ŻYWO',
    'Insights': 'Wnioski',
    'INSIGHTS': 'WNIOSKI',
    'Reports': 'Raporty',
    'REPORTS': 'RAPORTY',
    'GOVERNANCE': 'ZARZĄDZANIE SIECIĄ',
    'Multi-Venue': 'Wiele lokali',
    'MULTI-VENUE': 'WIELE LOKALI',
    'Menu Views': 'Wyświetlenia menu',
    'Item Interactions': 'Interakcje z daniami',
    'Guest Rating': 'Ocena gości',
    'Revenue (7d)': 'Przychód (7 dni)',
    'Revenue (24h)': 'Przychód (24h)',
    'Revenue (30d)': 'Przychód (30 dni)',
    'Delivery orders (7d)': 'Zamówienia z dostawą (7 dni)',
    'Delivery orders (24h)': 'Zamówienia z dostawą (24h)',
    'Delivery orders (30d)': 'Zamówienia z dostawą (30 dni)',
    'Delivery revenue': 'Przychód z dostaw',
    'Average delivery order': 'Średnie zamówienie z dostawą',
    'Average delivery fee': 'Średnia opłata za dostawę',
    'Zone average': 'Średnia strefy',
    'Including fees': 'Wliczając opłaty',
    'No deliveries': 'Brak dostaw',
    'No orders': 'Brak zamówień',
    'No reviews': 'Brak opinii',
    'Live': 'Na żywo',
    'LIVE': 'NA ŻYWO',
    'reviews': 'opinii',
    'orders': 'zamówień',
    'deliveries': 'dostaw',
    'of orders': 'zamówień',
    'rate': 'wskaźnik',
    'Recent Activity': 'Ostatnia aktywność',
    'Real-time Venue Telemetry': 'Aktywność lokalu w czasie rzeczywistym',
    'REAL-TIME VENUE TELEMETRY': 'AKTYWNOŚĆ LOKALU W CZASIE RZECZYWISTYM',
    'Rapid Pulse View': 'Szybki podgląd na żywo',
    'Anonymous Guest': 'Anonimowy gość',
    'scanned menu': 'zeskanował menu',
    'viewed item': 'zobaczył danie',
    'interacted with': 'wszedł w interakcję z',
    'placed order': 'złożył zamówienie',
    'searched menu': 'szukał w menu',
    'Main Landing': 'Strona główna',
    'Trace': 'Śledzenie',
    'TRACE': 'ŚLEDZENIE',
    'Gaining Telemetry...': 'Pobieranie aktywności...',
    'No recent traffic detected': 'Brak ostatniej aktywności',
    'Activities will appear here as guests scan your QR code': 'Aktywności pojawią się tutaj, gdy goście zeskanują Twój kod QR',
    'Analyze All Logs': 'Analizuj wszystkie wpisy',
    'Just now': 'Przed chwilą',
    'less than a minute ago': 'mniej niż minutę temu',
    '1 minute ago': '1 minutę temu',
    '1 hour ago': '1 godzinę temu',
    'about 1 hour ago': 'około 1 godzinę temu',
    '1 day ago': '1 dzień temu',
    'DAY AGO 1': '1 dzień temu',
    '2 days ago': '2 dni temu',
    'DAY AGO 2': '2 dni temu',
    '3 days ago': '3 dni temu',
    'DAY AGO 3': '3 dni temu',
    '4 days ago': '4 dni temu',
    'DAY AGO 4': '4 dni temu',
    '5 days ago': '5 dni temu',
    'DAY AGO 5': '5 dni temu',
    '6 days ago': '6 dni temu',
    'DAY AGO 6': '6 dni temu',
    '7 days ago': '7 dni temu',
    'DAY AGO 7': '7 dni temu',
    'about 1 month ago': 'około 1 miesiąc temu',
    'Monday': 'Poniedziałek',
    'Tuesday': 'Wtorek',
    'Wednesday': 'Środa',
    'Thursday': 'Czwartek',
    'Friday': 'Piątek',
    'Saturday': 'Sobota',
    'Sunday': 'Niedziela',
    'Mon': 'Pn',
    'Tue': 'Wt',
    'Wed': 'Śr',
    'Thu': 'Czw',
    'Fri': 'Pt',
    'Sat': 'Sob',
    'Sun': 'Ndz',
    'Opening Hours': 'Godziny otwarcia',
    'Start Time': 'Godzina rozpoczęcia',
    'End Time': 'Godzina zakończenia',
    'Opening Time': 'Godzina otwarcia',
    'Closing Time': 'Godzina zamknięcia',
    'All Day': 'Cały dzień',
    'Closed': 'Zamknięte',
    'Active Days': 'Aktywne dni',
    'Active Schedule': 'Aktywny harmonogram',
    'Always Available': 'Zawsze dostępne',
    'Always Visible': 'Zawsze widoczne',
    'New Category': 'Nowa kategoria',
    'Category': 'Kategoria',
    'Category Name': 'Nazwa kategorii',
    'Category Description': 'Opis kategorii',
    'Display Order': 'Kolejność wyświetlania',
    'Item Name': 'Nazwa dania',
    'Item Description': 'Opis dania',
    'Base Price': 'Cena bazowa',
    'Preparation Time': 'Czas przygotowania',
    'Calories': 'Kalorie',
    'Featured Item': 'Wyróżnione danie',
    'Chef\'s Recommendation': 'Wybór szefa kuchni',
    'Spicy': 'Ostre',
    'Vegetarian': 'Wegetariańskie',
    'Vegan': 'Wegańskie',
    'Gluten-Free': 'Bezglutenowe',
    'Halal': 'Halal',
    'Kosher': 'Koszerna',
    'Contains Nuts': 'Zawiera orzechy',
    'Dairy-Free': 'Bez nabiału',
    'Allergen Exclusions': 'Informacje o alergenach',
    'Table Number': 'Numer stolika',
    'Assigned Table': 'Przypisany stolik',
    'Seats': 'Miejsca',
    'Capacity': 'Pojemność',
    'Indoor': 'Wewnątrz',
    'Outdoor': 'Na zewnątrz',
    'Terrace': 'Taras',
    'Bar': 'Bar',
    'Add Table': 'Dodaj stolik',
    'Edit Table': 'Edytuj stolik',
    'Delete Table': 'Usuń stolik',
    'Bookings': 'Rezerwacje',
    'Add to Waitlist': 'Dodaj do listy oczekujących',
    'Guest Name': 'Imię gościa',
    'Party Size': 'Liczba gości',
    'Arrival Time': 'Godzina przybycia',
    'Special Requests': 'Specjalne życzenia',
    'Confirmed': 'Potwierdzone',
    'No Show': 'Nie pojawił się',
    'Add Staff Member': 'Dodaj pracownika',
    'Invite Staff': 'Zaproś pracownika',
    'Email Address': 'Adres e-mail',
    'Phone Number': 'Numer telefonu',
    'Download QR Code': 'Pobierz kod QR',
    'Print Table Stands': 'Drukuj stojaki na stolik',
    'Today': 'Dzisiaj',
    'Yesterday': 'Wczoraj',
    'This Week': 'W tym tygodniu',
    'This Month': 'W tym miesiącu',
    'Last 30 Days': 'Ostatnie 30 dni',
    'Access Denied': 'Odmowa dostępu',
    'Advanced Settings': 'Ustawienia zaawansowane',
    'Change Image': 'Zmień zdjęcie',
    'Remove Image': 'Usuń zdjęcie',
    'Announcement Banner': 'Baner ogłoszeń',
    'Please fill in all required fields.': 'Proszę wypełnić wszystkie wymagane pola.',
    'Changes saved successfully': 'Zmiany zostały pomyślnie zapisane',
    '(You)': '(Ty)',
    'AI Auto-Tag': 'Automatyczne tagowanie AI',
    'Analyzing:': 'Analizowanie:',
    'Apple (Fruit/Vegan)': 'Jabłko (Owoc/Wegańskie)',
    'Cancel editing': 'Anuluj edycję',
    'Capacity:': 'Pojemność:',
    'Carrot (Veggie)': 'Marchewka (Warzywa)',
    'CircleDot (Organic)': 'Okrągły punkt (Organiczny)',
    'Coffee (Caffeine)': 'Kawa (Kofeina)',
    'Configuration': 'Konfiguracja',
    'Copy verification record name': 'Kopiuj nazwę rekordu weryfikacyjnego',
    'Copy verification record value': 'Kopiuj wartość rekordu weryfikacyjnego',
    'Critical exception intercepted': 'Przechwycono błąd krytyczny',
    'Kernel Panic': 'Błąd Systemu',
    'DNA (Organic/Bio)': 'DNA (Organiczny/Bio)',
    'Egg (Ovo/Allergen)': 'Jajko (Alergen)',
    'Empty': 'Puste',
    'Engraving...': 'Generowanie...',
    'Flame (Spicy)': 'Płomień (Ostre)',
    'Free': 'Darmowe',
    'From': 'Od',
    'Generating': 'Generowanie',
    'Governance & Traceability Studio': 'Centrum zarządzania i historii',
    'Greet': 'Witamy',
    'Heart (Healthy)': 'Serce (Zdrowe)',
    'In-App': 'W aplikacji',
    'Infinity SVG': 'Wektor SVG (Nieograniczony)',
    'Leaf (Default)': 'Liść (Domyślny)',
    'MAX Feature': 'Funkcja Enterprise',
    'Milk (Dairy)': 'Mleko (Nabiał)',
    'Network': 'Sieć',
    'Notify SMS': 'Powiadomienie SMS',
    'Order': 'Zamówienie',
    'Original': 'Oryginał',
    'Overview': 'Przegląd',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 standardowe role',
    'Shield (Safety)': 'Tarcza (Bezpieczeństwo)',
    'Soup (Warm/Soup)': 'Zupa (Ciepłe/Zupa)',
    'Standard Pro View': 'Widok standardowy',
    'Star': 'Gwiazdka',
    'Synchronized with Venue Identity': 'Dopasowane do tożsamości Twojego lokalu',
    'Synchronizing Security Context...': 'Nawiązywanie bezpiecznego połączenia...',
    'Targeted Timing': 'Godziny serwowania',
    'Targeting': 'Dotyczy',
    'To': 'Do',
    'Toggle Navigation': 'Przełącz nawigację',
    'Venue': 'Lokal',
    'Venue Governance Suite': 'Zarządzanie i ustawienia lokalu',
    'Volume:': 'Głośność:',
    'WHITEBOARD NOTE...': 'Notatka dla zespołu...',
    'Warning (Alert)': 'Ostrzeżenie (Alert)',
    'Wheat (Flour/Gluten)': 'Pszenica (Mąka/Gluten)',
    'Wine (Alcohol)': 'Wino (Alkohol)',
    'YouTube/Vimeo/Direct Link': 'Link do wideo (YouTube, Vimeo lub MP4)',
    'Your name': 'Twoje imię',
    '2. Table Number': '2. Numer stolika',
    'for Morocco or': 'dla Maroka lub',
    'in': 'w',
    'to': 'do',
    'e.g. 12 or Patio-1': 'np. 12 lub Taras-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 'np. Ręcznie robione fettuccine, czarna trufla zimowa, parmezan dojrzewający 36 miesięcy...',
    'e.g. Head Sommelier & Wine Lead': 'np. Główny sommelier',
    'e.g. London': 'np. Londyn',
    'e.g. SOMMELIER, HOST, BARISTA': 'np. SOMMELIER, HOST, BARYSTA',
    'e.g. Skyline Lounge': 'np. Panorama Lounge',
    'e.g. TRUFFLE OIL': 'np. OLIWA TRUFLOWA',
    'e.g. The Grand Cafe': 'np. Wielka Kawiarnia',
    'e.g. Window preference, high chair': 'np. Przy oknie, krzesełko dziecięce',
    'Copied to clipboard!': 'Skopiowano do schowka!',
    'Copy failed.': 'Kopiowanie nie powiodło się.',
    'Link copied!': 'Link skopiowany!',
    'Logo uploaded': 'Logo przesłane',
    'Cover image uploaded': 'Zdjęcie w tle przesłane',
    'Profile saved and synced successfully': 'Profil zapisany i zsynchronizowany pomyślnie',
    'Category Created': 'Kategoria utworzona',
    'Category Updated': 'Kategoria zaktualizowana',
    'Category Deleted': 'Kategoria usunięta',
    'Item Created': 'Pozycja utworzona',
    'Item Updated': 'Pozycja zaktualizowana',
    'Item Deleted': 'Pozycja usunięta',
    'Menu Published': 'Menu opublikowane',
    'Delete Category?': 'Usunąć kategorię?',
    'Delete Menu Item?': 'Usunąć tę pozycję?',
    'Delete Ingredient?': 'Usunąć ten składnik?',
    'Delete Service Period?': 'Usunąć okres serwisu?',
    'Remove team member?': 'Usunąć członka zespołu?',
    'Cancel invitation?': 'Anulować zaproszenie?',
    'Revoke API key?': 'Unieważnić klucz API?',
    'Delete webhook endpoint?': 'Usunąć punkt końcowy webhooka?',
    'Clear all notifications?': 'Wyczyścić wszystkie powiadomienia?',
    'Disconnect Stripe account?': 'Odłączyć konto Stripe?',
    'Disconnect Google Business Profile?': 'Odłączyć Profil Firmy w Google?',
    'Campaigns published successfully!': 'Kampanie opublikowane pomyślnie!',
    'Changes successfully synchronized with Google Business Profile!': 'Zmiany pomyślnie zsynchronizowane z Profilem Firmy w Google!',
    'Google Business Profile successfully connected!': 'Profil Firmy w Google pomyślnie połączony!',
    'Google Business Profile disconnected.': 'Profil Firmy w Google został odłączony.',
    'Google Post draft saved successfully!': 'Szkic posta w Google zapisany pomyślnie!',
    'Post draft deleted.': 'Szkic posta usunięty.',
    'Review response published successfully!': 'Odpowiedź na opinię opublikowana pomyślnie!',
    'SEO configuration saved successfully!': 'Konfiguracja SEO zapisana pomyślnie!',
    'SEO metadata translated across all languages!': 'Metadane SEO przetłumaczone na wszystkie języki!',
    'Venue profile & bio translated across all languages!': 'Profil i opis lokalu przetłumaczone na wszystkie języki!',
    'Reservation configuration saved successfully': 'Ustawienia rezerwacji zapisane pomyślnie',
    'Stripe settings saved successfully.': 'Ustawienia Stripe zapisane pomyślnie.',
    'Print-Ready PDF downloaded successfully': 'PDF gotowy do druku pobrany pomyślnie',
    'Vector QR PDF downloaded successfully': 'Wektorowy kod QR PDF pobrany pomyślnie',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Pakiet do druku 300 DPI wygenerowany pomyślnie',
    'At least one language must be enabled.': 'Co najmniej jeden język musi być włączony.',
    'Enter a non-zero percentage before adjusting prices.': 'Wprowadź wartość procentową różną od zera przed zmianą cen.',
    'Please enter a Venue Name before translating.': 'Wprowadź nazwę lokalu przed rozpoczęciem tłumaczenia.',
    'Please enter an item name before running dietary and allergen analysis.': 'Wprowadź nazwę pozycji przed analizą alergenów i diety.',
    'Please write a reply before submitting.': 'Napisz odpowiedź przed przesłaniem.',
    'Table 3 Called Waiter': 'Stolik 3 wezwał kelnera',
    'Table Call Bell': 'Dzwonek przywołania stolika',
    'Waiter Calls': 'Wezwania kelnera',
    'Waiter Dispatch & Calls': 'Dyspozycja i wezwania kelnerów',
    'Kitchen Display & Whiteboard': 'Ekran kuchenny i tablica notatek',
    'Customer Orders': 'Zamówienia klientów',
    'Bakery': 'Piekarnia / Cukiernia',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Kawiarnia',
    'Fast Food': 'Fast food',
    'Gourmet / Fine Dining': 'Wykwintna kuchnia / Fine dining',
    'Hotel': 'Hotel',
    'General Local Business': 'Ogólna działalność lokalna',
    'Classic': 'Klasyczny',
    'Clean': 'Czysty',
    'Modern': 'Nowoczesny',
    'Elegant': 'Elegancki',
    'Energetic': 'Energiczny',
    'Glass': 'Efekt szkła',
    'Playful & Casual': 'Swobodny i wesoły',
    'Sophisticated & Gourmet': 'Wyrafinowany i wykwintny',
    'Warm & Welcoming': 'Ciepły i gościnny',
    'Executive': 'Biznesowy',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Wymagane uaktualnienie: Menu wielojęzyczne jest dostępne w planie Pro.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Wymagane uaktualnienie: Zamawianie i wezwania kelnera nie są dostępne w planie Starter.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Eksport wektorów SVG to funkcja planów Pro i Max. Zaktualizuj plan, aby pobrać wektory.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Eksport PDF do druku to funkcja planów Pro i Max. Zaktualizuj plan, aby pobrać pliki PDF.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Plan Pro jest ograniczony do 3 aktywnych kampanii. Wstrzymaj inną kampanię lub uaktualnij do MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Plan Starter obejmuje do 20 stolików (Stoliki 1–20). Uaktualnij do PRO, aby mieć nieograniczoną liczbę stolików.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Plan Starter obsługuje stoliki numeryczne (1–20). Niestandardowe nazwy (np. Taras, VIP) wymagają planu PRO.',
    'Add Menu Categories & Items': 'Dodaj kategorie i pozycje menu',
    'Add Menu Items': 'Dodaj pozycje do menu',
    'Organize your menu sections and food items.': 'Uporządkuj sekcje menu i serwowane dania.',
    'Add and manage your food & beverage offerings.': 'Dodawaj i zarządzaj ofertą dań oraz napojów.',
    'Customize Venue Branding': 'Dostosuj tożsamość wizualną lokalu',
    'Set your brand colors, logo, and presentation.': 'Ustaw kolory marki, logo oraz styl prezentacji.',
    'Get Your QR Code & Go Live': 'Pobierz kod QR i uruchom menu online',
    'Download table QR codes and preview your live menu.': 'Pobierz kody QR na stoliki i sprawdź podgląd menu na żywo.',
    'Account & Venue Created Successfully!': 'Konto i lokal utworzone pomyślnie!',
    '⚡ Off-Peak Hours': '⚡ Godziny poza szczytem',
    '🍽️ Lunch Crowd': '🍽️ Goście obiadowi',
    '🚀 Weekend Rush': '🚀 Weekendowy szczyt',
    '🌟 Seasonal Special': '🌟 Oferta sezonowa',
    '📣 Engagement Buzz': '📣 Zaangażowanie gości',
    'Festive & Energetic': 'Świąteczny i energiczny',
    'Urgent & High-Conversion': 'Limitowany czasowo i skuteczny',
    'Generated 3 tailored campaign strategies': 'Wygenerowano 3 dopasowane strategie kampanii',
    'Could not generate campaign ideas. Please try again.': 'Nie udało się wygenerować pomysłów na kampanię. Spróbuj ponownie.',
    'Venue Themes': 'Motywy lokalu',
    'Choose the character of your public menu': 'Wybierz charakter wizualny swojego menu online',
    'Glassmorphism & Depth': 'Efekt szkła i głębia',
    'Independent XML Sitemap': 'Niezależna mapa witryny XML',
    'Structured Schema & Sync': 'Dane strukturalne i synchronizacja',
    'Improve how your venue appears on Google and in local search results.': 'Popraw widoczność swojego lokalu w Google i wynikach wyszukiwania lokalnego.',
    'Terms of Service': 'Warunki korzystania',
    'Confirm your email': 'Potwierdź swój adres e-mail',
    'Check your spam / junk folder': 'Sprawdź folder ze spamem lub wiadomościami-śmieciami',
    'Commercial pre-press print production package': 'Pakiet do profesjonalnego druku poligraficznego',
    'Over-bleed safety margin': 'Margines bezpieczeństwa na spad',
    'Crop Marks': 'Znaczniki cięcia',
    'Guillotine cutting guides': 'Linie cięcia gilotyną',
    'Global Banners': 'Bannery globalne',
    'Featured Items': 'Wyróżnione pozycje',
    'AI Assistant': 'Asystent AI',
    'Custom Role Schemas': 'Niestandardowe schematy ról',
    'Notification Preferences': 'Preferencje powiadomień',
    'Preferences saved automatically': 'Preferencje zapisane automatycznie',
    'Menu Extractor (PDF & Images)': 'Ekstraktor menu (PDF i zdjęcia)',
    'Multi-Language Translation': 'Tłumaczenie wielojęzyczne',
    'Start Menu Translation': 'Rozpocznij tłumaczenie menu',
    'Menu Items Hub': 'Centrum pozycji menu',
    'Open Menu Items': 'Otwórz pozycje menu',
    'Enterprise Traceability': 'Śledzenie aktywności i audyt',
    'Full History': 'Pełna historia',
    'Billing & Plans': 'Rozliczenia i plany',
    'Subscription Governance': 'Zarządzanie subskrypcją',
    'Platform Usage': 'Zużycie zasobów',
    'Menu Page Views': 'Wyświetlenia stron menu',
    'Enterprise Identity': 'Tożsamość przedsiębiorstwa',
    'Access Governance': 'Zarządzanie dostępem',
    'Required Fields *': 'Pola wymagane *',
    'Enabled Languages': 'Włączone języki',
    'Venue Info & Bio Localizations': 'Tłumaczenia opisu i informacji o lokalu',
    'Public Address': 'Adres publiczny',
    'Public URL Slug': 'Publiczny identyfikator URL',
    'Contact Phone': 'Telefon kontaktowy',
    'Business Address': 'Adres działalności',
    'No Categories Yet': 'Brak kategorii',
    'No Custom Roles Configured': 'Brak skonfigurowanych ról niestandardowych',
    'No Google reviews retrieved yet': 'Nie pobrano jeszcze żadnych opinii z Google',
    'No Guests Found': 'Nie znaleziono gości',
    'No Menu Items Yet': 'Brak pozycji w menu',
    'No Promotions Configured': 'Brak skonfigurowanych promocji',
    'No Service Periods Yet': 'Brak zdefiniowanych okresów serwisu',
    'No Support Tickets': 'Brak zgłoszeń pomocy',
    'No Tables on Floor Plan': 'Brak stolików na planie sali',
    'No Team Members Found': 'Nie znaleziono członków zespołu',
    'No Upcoming Events': 'Brak nadchodzących wydarzeń',
    'No Walk-Ins Recorded': 'Brak zarejestrowanych gości z ulicy',
    'No items are available right now': 'Obecnie brak dostępnych pozycji',
    'No notifications found': 'Brak powiadomień',
    'No reservations matching your filters': 'Brak rezerwacji pasujących do wybranych filtrów',
    'No tables currently occupied': 'Żaden stolik nie jest obecnie zajęty',
    'No venue selected': 'Nie wybrano żadnego lokalu',
    'Waitlist': 'Lista oczekujących',
    'Waitlist is Empty': 'Lista oczekujących jest pusta',
    'Waves (Gluten)': 'Kłosy (Gluten)',
    'We sent a temporary verification code to': 'Wysłaliśmy tymczasowy kod weryfikacyjny na adres',
    'Website URL': 'Adres strony internetowej',
    'What the customer receives': 'Co otrzymuje gość',
    'White-label control is available on eligible plans.': 'Personalizacja white-label jest dostępna w wybranych planach.',
    'Why this works:': 'Dlaczego to działa:',
    'WiFi Broadcast': 'Udostępnianie Wi-Fi',
    'WiFi Password': 'Hasło do Wi-Fi',
    'Window seat, dietary request, birthday celebration...': 'Stolik przy oknie, życzenia dietetyczne, urodziny...',
    'Your Published Response': 'Twoja opublikowana odpowiedź',
    'Your Venues': 'Twoje lokale',
    '🎉 Ready to publish!': '🎉 Gotowe do publikacji!',
    'e.g. Grill, Salad, Pastry': 'np. Grill, Sałatki, Cukiernia',
    'e.g. Guest called, emergency': 'np. Telefon od gościa, sytuacja awaryjna',
    'e.g. Italian, Moroccan, Burgers': 'np. Włoska, Marokańska, Burgery',
    'e.g. Mains, Pasta, Starters': 'np. Dania główne, Makarony, Przystawki',
    'e.g. Signature Truffle Pasta': 'np. Firmowy makaron truflowy',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 'np. Popołudniowa kawa ze zniżką, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 'np. Śniadanie, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 'np. Zapraszamy na jazz na żywo w każdy piątek o 20:00!',
  },
  sv: {
    '123 Main St, City': 'Storgatan 123, Stad',
    'Balance': 'Poängsaldo',
    'Lifetime': 'Totalt intjänade',
    'Source': 'Källa',
    'Menu': 'Meny',
    'Advanced': 'Avancerat',
    'Core Suite': 'Grundläggande funktioner',
    'Privacy': 'Integritet',
    'Support': 'Kundsupport',
    'Save Layout': 'Spara bordsplacering',
    'Shape:': 'Bordsform:',
    'SEARCH LIBRARY...': 'SÖK I BIBLIOTEKET...',
    'Search library...': 'Sök i biblioteket...',
    'None': 'Ingen',
    'Type': 'Typ',
    'Target': 'Mål',
    'Value / Target': 'Värde / Mål',
    'London (GMT/BST)': 'London (GMT/BST)',
    'New York (EST/EDT)': 'New York (EST/EDT)',
    'Chicago (CST/CDT)': 'Chicago (CST/CDT)',
    'Denver (MST/MDT)': 'Denver (MST/MDT)',
    'Dubai (GST)': 'Dubai (GST)',
    'Tokyo (JST)': 'Tokyo (JST)',
    'Sydney (AEST/AEDT)': 'Sydney (AEST/AEDT)',
    'Start Color': 'Startfärg',
    'Depth Shade': 'Djupskugga',
    'Matrix Geometry': 'QR-kodens punktmönster',
    'Eye Architecture': 'QR-kodens hörndesign',
    'Brand Centerpiece': 'Logotyp i mitten av QR-koden',
    'Scannability Shield': 'Skydd för avläsningsbarhet',
    'Environment Blur': 'Bakgrundsoskärpa',
    'Color Target:': 'Färgprofil:',
    'DPI:': 'DPI (Upplösning):',
    '3mm Bleed Box': '3 mm utfallsmarginal',
    '300 DPI': '300 DPI (Hög upplösning)',
    'PRO+': 'PRO+',
    'SAML 2.0': 'SAML 2.0-protokoll',
    'OpenID Connect': 'OpenID Connect (OIDC)',
    'IdP Entity ID': 'IdP Entity ID',
    'SSO URL': 'SSO-inloggningsadress',
    'X.509 Certificate': 'X.509-säkerhetscertifikat',
    'ACS URL': 'ACS URL (Assertion Consumer Service)',
    'Entity ID': 'Entitets-ID',
    'Navigate': 'Navigera',
    'Select': 'Välj',
    'OmniSearch v1.0': 'Universalsökning OmniSearch',
    'Back to order review': 'Tillbaka till ordergranskning',
    'Return to Intelligence Studio': 'Tillbaka till analysstudion',
    'View Full Notification Center': 'Visa alla aviseringar',
    'Read all': 'Markera alla som lästa',
    'Cancel department': 'Avbryt avdelning',
    'Save department': 'Spara avdelning',
    'Venue Segmentation': 'Sektionsindelning',
    'Collaborate on your menu together.': 'Samarbeta kring menyn tillsammans.',
    'Timeline': 'Tidslinje',
    'Calendar': 'Bokningskalender',
    'Book': 'Boka bord',
    'Duration': 'Sittningstid',
    'Time & Duration': 'Tid och varaktighet',
    'Seating Preference': 'Platsönskemål',
    'Section': 'Sektionszon',
    'Min Capacity': 'Minsta antal gäster',
    'Max Capacity': 'Maximalt antal gäster',
    'Phone (Optional)': 'Telefon (Valfritt)',
    'Name or Walk-in': 'Namn eller spontangäst',
    'Next day': 'Nästa dag',
    'Next month': 'Nästa månad',
    'Live Connection': 'Realtidsanslutning',
    'Live Inventory': 'Realtidslager',
    'Main Venue (Full Menu)': 'Huvudverksamhet (Fullständig meny)',
    'Preview Menu': 'Förhandsgranska meny',
    'Manual Setup Guide': 'Manuell installationsguide',
    'Connected & Active': 'Ansluten och aktiv',
    'CTA Action': 'Uppmaningsknapp (CTA)',
    'Phone Contact': 'Kontakttelefon',
    'Menu URL': 'Webbadress till meny',
    'Google Location': 'Google-plats',
    'Field': 'Fält',
    'Linked': 'Kopplad',
    'Synchronizing Hub...': 'Synkroniserar data...',
    'Data Integrity': 'Dataintegritet',
    'Station Governance Suite': 'Hantering av arbetsstationer',
    'Open audit log': 'Öppna granskningslogg',
    'Signal / Time': 'Händelse / Tid',
    'Identity': 'Användare / Identitet',
    'Invitation Invalid': 'Inbjudningslänken är ogiltig eller har löpt ut',
    'Invited To': 'Inbjuden till',
    'Team Limit Reached': 'Gränsen för teammedlemmar har nåtts',
    'Venue Limit Reached': 'Gränsen för antal verksamheter har nåtts',
    'Venue Setup Required': 'Grundinställning krävs',
    'Create Venue': 'Skapa verksamhet',
    'TableGreet Onboarding': 'TableGreet-introduktion',
    'No payment required': 'Ingen betalning krävs',
    'Selected Plan': 'Vald plan',
    'Payment Method': 'Betalningsmetod',
    'Invoice ID': 'Fakturanummer',
    'Verified': 'Verifierad',
    'Accent color': 'Accentfärg',
    'Set the voice and overall light level.': 'Ange ton och övergripande ljusnivå.',
    'Layout architecture': 'Layoutarkitektur',
    'Navigation & motion': 'Navigering och animationer',
    'Loading preview engine': 'Laddar förhandsvisningsmotor',
    'Real Live Preview': 'Realtidsförhandsvisning',
    'Included with Pro & Max': 'Ingår i Pro och Max',
    'Daypart Scheduling': 'Schemaläggning för dagdelar',
    'Operational Daypart Architecture': 'Arkitektur för driftstider',
    'Service Protocol': 'Serveringsprotokoll',
    'Configuration Engine': 'Konfigurationsmotor',
    'Linked Department': 'Kopplad avdelning',
    'Team Hub': 'Teamcenter',
    'Team Size': 'Teamstorlek',
    'Joined': 'Gick med',
    'Cancel Invitation': 'Avbryt inbjudan',
    'Initialize New Organization': 'Initiera ny organisation',
    'Operational': 'I full drift',
    'Pro Feature': 'Pro-funktion',
    'Current State': 'Aktuellt tillstånd',
    'Open on the big TV': 'Öppna på stor TV-skärm',
    'Menu Board': 'Digital menytavla',
    'Reward': 'Belöning',
    'Reward name': 'Belöningsnamn',
    'Cancel & refund': 'Avbryt och återbetala',
    'Code': 'Kod',
    'Find': 'Sök',
    'Complete': 'Slutförd',
    'Seated': 'Sitter till bords',
    'No-Show': 'Uteblev',
    'Walk-in': 'Spontangäst',
    'Party': 'Sällskap',
    'Turnover': 'Bordsomsättning',
    'We couldn\'t load the menu': 'Kunde inte ladda menyn',
    'Venue Not Found': 'Verksamheten hittades inte',
    'Live Availability': 'Realtidstillgänglighet',
    'Multi-Language Suite': 'Flerspråksmodul',
    'Key name': 'Nyckelnamn',
    'Revoke API key': 'Återkalla API-nyckel',
    'Delete webhook': 'Ta bort webhook',
    'Identity Provider': 'Identitetsleverantör (IdP)',
    'Provider': 'Leverantör',
    'Mandatory MFA': 'Obligatorisk tvåfaktorsautentisering (MFA)',
    'Account Identity': 'Kontoidentitet',
    'Login Email': 'Inloggnings-e-post',
    'New Login Email': 'Ny inloggnings-e-post',
    'Phone Login': 'Inloggning med telefon',
    'SMS Verification Code': 'SMS-verifieringskod',
    'Integration Method': 'Integrationsmetod',
    'Stripe Connect': 'Stripe Connect-betalningar',
    'Stripe Secret Key': 'Hemlig Stripe-nyckel',
    'Stripe Webhook Secret': 'Stripe-webhookhemlighet',
    'Client-Owned': 'I egen ägo',
    'Verify connection': 'Verifiera anslutning',
    'Setup Health': 'Konfigurationsstatus',
    'Privacy Protected': 'Integritetsskyddad',
    'Privacy Policy': 'Integritetspolicy',
    'Continue': 'Fortsätt',
    'Sign In': 'Logga in',
    'Didn\'t get it?': 'Fick du ingen kod?',
    'Logged In Account': 'Inloggat konto',
    'Annual': 'Årsvis',
    'Unread': 'Olästa',
    'Starred': 'Stjärnmärkta',
    'High / Urgent': 'Hög / Brådskande',
    'Mark Read': 'Markera som läst',
    'Delete notification': 'Ta bort avisering',
    'Urgent': 'Brådskande',
    'High': 'Hög',
    'Normal': 'Normal',
    'Low': 'Låg',
    'Select a Ticket': 'Välj ett ärende',
    'Urgent (Immediate attention)': 'Brådskande (Kräver omedelbar åtgärd)',
    'Type your reply here...': 'Skriv ditt svar här...',
    'Print Card (PDF)': 'Skriv ut bordskort (PDF)',
    'Advanced Printing Engine': 'Professionell utskriftsmotor',
    'Multi-Venue Governance': 'Styrning av flera filialer',
    'Network Revenue': 'Nätverkets totala intäkter',
    'Live Network Total': 'Realtidstotal för nätverket',
    'Audit Log Hub': 'Nav för granskningsloggar',
    'Export Log': 'Exportera logg',
    'CUSTOM ROLE SCHEMAS': 'ANPASSADE ROLLSCHEMAN',
    'Unique internal role identifier.': 'Unikt internt roll-ID.',
    'Human-friendly role label.': 'Lättförståelig rollbeteckning för teamet.',
    'Marketing Hub': 'Marknadsföringscenter',
    'Revenue Generation Suite': 'Verktyg för intäktsökning',
    'Active Campaign Limit Reached (3/3)': 'Gräns för aktiva kampanjer nådd (3/3)',
    'Banner Content': 'Bannerinnehåll',
    'Style Profile': 'Stilprofil',
    'Action URL (Optional)': 'Åtgärdslänk (Valfritt)',
    'Promotion Label': 'Erbjudandeetikett',
    'Target Selection': 'Målurval',
    'Edit Promotion': 'Redigera erbjudande',
    'Delete Promotion': 'Ta bort erbjudande',
    'Loyalty Engine': 'Lojalitetsprogram',
    'Reward catalog': 'Belöningskatalog',
    'Add reward': 'Lägg till belöning',
    'Loyalty ID': 'Lojalitets-ID',
    'No point activity yet': 'Ingen poängaktivitet ännu',
    'Scan or enter QR value': 'Skanna eller ange QR-kod',
    'TableGreet Admin': 'TableGreet-administration',
    'Capacity Pacing': 'Kapacitetsstyrning',
    'Cancellation Policy': 'Avbokningsregler',
    'Booking Experience': 'Bokningsupplevelse',
    'Floor Occupancy': 'Beläggningsgrad',
    'Currently Seated': 'Sitter till bords nu',
    'Seat Party': 'Placera gäster',
    'Seat Table': 'Anvisa bord',
    'Select Table': 'Välj bord',
    'Table / Capacity': 'Bord / Kapacitet',
    'Table Name/Number': 'Bordets namn eller nummer',
    'Mark Arrived': 'Markera som anländ',
    'Mark No-Show': 'Markera utebliven ankomst',
    'Complete Dining': 'Avsluta måltid',
    'Standard Dining (Default)': 'Standardservering (Standard)',
    'Walk-In Dining Log': 'Logg för spontangäster',
    'Notification Center': 'Aviseringscenter',
    'Single Sign-On': 'Enkel inloggning (SSO)',
    'Welcome Aboard!': 'Välkommen till TableGreet!',
    'Select your TableGreet plan': 'Välj din TableGreet-plan',
    'Free Plan Activation': 'Aktivering av gratisplan',
    'Venue Created!': 'Verksamheten har skapats!',
    'Upgrade for Unlimited': 'Uppgradera för obegränsat',
    'Auto-Tag Menu': 'Märk menyn automatiskt',
    'Start Auto-Tagging': 'Starta automatisk märkning',
    'Launch Auto-Tagger': 'Starta automatisk taggare',
    'Launch PDF Importer': 'Starta PDF-importör',
    'Analyzing Menu...': 'Analyserar menyn...',
    'Analyzing Recipe...': 'Analyserar receptet...',
    'Manual Availability': 'Manuell tillgänglighet',
    'Technical Breakdown': 'Teknisk specifikation',
    'Dietary Taxonomy': 'Kost- och allergenkategorier',
    'Dietary & Allergen AI': 'AI för kost och allergener',
    'Inherit from Category': 'Ärv från kategori',
    'Ingredient Library': 'Ingrediensbibliotek',
    'Ingredient Name': 'Ingrediensnamn',
    'Menu Organization': 'Menyuppbyggnad',
    'Inventory Taxonomy Hub': 'Kategoriseringscenter',
    'Category Intelligence': 'Kategoriinsikter',
    'Service Period': 'Serveringsperiod',
    'Manual Configuration / All Day': 'Manuell inställning / Hela dagen',
    'Operational Department': 'Verksamhetsavdelning',
    'Global / No Department': 'Global / Ingen avdelning',
    'Cancel category': 'Avbryt kategori',
    'New category name...': 'Nytt kategorinamn...',
    'Save category': 'Spara kategori',
    'QR Matrix Only': 'Endast QR-kod',
    'Vector & PDF': 'Vektor och PDF',
    'Vector QR (SVG)': 'Vektor-QR-kod (SVG)',
    'Vector QR (PDF)': 'Tryckfärdig QR-kod (PDF)',
    'Save QR Style': 'Spara QR-design',
    'Share Link': 'Dela länk',
    'High Fidelity 300DPI': 'Hög upplösning 300 DPI för tryck',
    'Generating 300 DPI PDF...': 'Skapar 300 DPI PDF...',
    'Give your venue a point of view.': 'Ge ditt ställe en unik visuell identitet.',
    'Signage Menu Not Found': 'Digital skärmmeny hittades inte',
    'Scan Menu': 'Skanna meny',
    'Open on your phone': 'Öppna i mobilen',
    'Full Screen': 'Helskärm',
    'Lock TV board': 'Lås TV-skärm',
    'Big-Screen Menu': 'Storskärmsmeny',
    'TV Signage': 'TV-skyltning',
    'General Information': 'Allmän information',
    'Venue Name *': 'Verksamhetsnamn *',
    'Public Slug *': 'Offentlig webbadress *',
    'Venue Logo': 'Verksamhetens logotyp',
    'Hero Cover Image': 'Huvudomslagsbild',
    'Venue Timezone': 'Verksamhetens tidszon',
    'Primary Brand Color': 'Huvudsaklig varumärkesfärg',
    'TableGreet footer credit': 'TableGreet-text i sidfoten',
    'White-Label Branding': 'White-label varumärkesprofilering',
    'Client DNS Setup': 'DNS-inställning för domän',
    'Copy CNAME target': 'Kopiera CNAME-mål',
    'Copy TableGreet URL': 'Kopiera TableGreet-adress',
    'Included TableGreet URL': 'Medföljande TableGreet-länk',
    'Open TableGreet URL': 'Öppna TableGreet-länk',
    'Public menu': 'Offentlig gästmeny',
    'Public venue delivery': 'Hemleverans via onlinemenyn',
    'Zone name': 'Områdesnamn',
    'Coverage note': 'Information om leveransområde',
    'Default ETA min': 'Minsta beräknade tid (min)',
    'Default ETA max': 'Största beräknade tid (min)',
    'Packaging fee': 'Förpackningsavgift',
    'Courier handled by': 'Bud hanteras av',
    'Third-party courier': 'Extern budfirma',
    'Unified Experience': 'Enhetlig upplevelse',
    'Save WiFi to Venue': 'Spara gäst-Wi-Fi',
    'Network Name (SSID)': 'Nätverksnamn (SSID)',
    'Audio Alert Chime': 'Ljudsignal vid avisering',
    'Time Format': 'Tidsformat',
    'Auto / Default': 'Automatiskt / Standard',
    'Syncing SEO & Google Data...': 'Synkroniserar SEO- och Google-data...',
    'Google Connection': 'Google-koppling',
    'Search Engine Metadata': 'Metadata för sökmotorer',
    'Social card preview': 'Förhandsgranskning för sociala medier',
    'Allow Indexing': 'Tillåt indexering',
    'Canonical URL Override': 'Åsidosätt kanonisk URL',
    'Price Range': 'Prisklass',
    'Latitude': 'Breddgrad',
    'Longitude': 'Längdgrad',
    'Google Place ID / Location ID': 'Google plats-ID (Place ID)',
    'Google Search Preview': 'Förhandsvisning i Google-sökning',
    'Linked Google Location': 'Länkad Google-plats',
    'SEO Documentation': 'SEO-dokumentation',
    ', and': ', och',
    'and': 'och',
    'Delete Department?': 'Ta bort avdelning?',
    'Delete Department': 'Ta bort avdelning',
    'Live URL': 'Live-länk',
    'Copy full live URL': 'Kopiera hela live-länken',
    'Open live department menu': 'Öppna avdelningens livemeny',
    'Loading departments...': 'Laddar avdelningar...',
    'Multi-department venue segmentations are premium features available on PRO and above.': 'Indelning i flera avdelningar är en premiumfunktion tillgänglig på PRO och uppåt.',
    'Segment your menu by rooms, tables, bars, or physical locations.': 'Dela in din meny efter rum, bord, barer eller fysiska platser.',
    'Targeted Ordering': 'Målinriktad beställning',
    'Route guests and orders to specific preparation areas.': 'Dirigera gäster och beställningar till specifika beredningsområden.',
    'Operational Departments': 'Verksamhetsavdelningar',
    'OPERATIONAL DEPARTMENTS': 'VERKSAMHETSAVDELNINGAR',
    'Venue Segmentation Hub': 'Nav för sektionsindelning',
    'VENUE SEGMENTATION HUB': 'NAV FÖR SEKTIONSINDELNING',
    'Create departments to group your menu sections. For example, a large venue might have a': 'Skapa avdelningar för att gruppera dina menyer. Ett stort ställe kan till exempel ha',
    'Restaurant': 'Restaurang',
    'Poolside': 'Poolområde',
    'department, each with its own categories.': ', var och en med sina egna kategorier.',
    'Departments let you split your venue into sections like Restaurant, Bar, or Poolside — each with its own categories.': 'Avdelningar låter dig dela in ditt ställe i sektioner som Restaurang, Bar eller Poolområde – var och en med sina egna kategorier.',
    'Department name': 'Avdelningsnamn',
    'New department name...': 'Nytt avdelningsnamn...',
    'No Linked Daypart (Always Available)': 'Ingen kopplad servicetid (alltid tillgänglig)',
    'Linked Daypart': 'Kopplad servicetid',
    'Localizations': 'Översättningar',
    'LOCALIZATIONS': 'ÖVERSÄTTNINGAR',
    'AI Auto-Translate': 'Automatisk AI-översättning',
    'AI AUTO-TRANSLATE': 'AUTOMATISK AI-ÖVERSÄTTNING',
    'Translated name': 'Översatt namn',
    'No Departments Yet': 'Inga avdelningar ännu',
    'Add First Department': 'Lägg till första avdelningen',
    'Avg Order Value': 'Genomsnittligt orderbelopp',
    'AVG ORDER VALUE': 'GENOMSNITTLIGT ORDERBELOPP',
    'item taps': 'klick på rätter',
    'ITEM TAPS': 'KLICK PÅ RÄTTER',
    'Avg Rating': 'Genomsnittsbetyg',
    'AVG RATING': 'GENOMSNITTSBETYG',
    'Revenue (Last 7 Days)': 'Intäkter (senaste 7 dagarna)',
    'REVENUE (LAST 7 DAYS)': 'INTÄKTER (SENASTE 7 DAGARNA)',
    'Daily breakdown': 'Daglig uppdelning',
    'DAILY BREAKDOWN': 'DAGLIG UPPDELNING',
    'Menu Views (Last 7 Days)': 'Menyvisningar (senaste 7 dagarna)',
    'MENU VIEWS (LAST 7 DAYS)': 'MENYVISNINGAR (SENASTE 7 DAGARNA)',
    'Guest traffic': 'Gästflöde',
    'GUEST TRAFFIC': 'GÄSTFLÖDE',
    'Most Viewed Items': 'Mest visade rätter',
    'MOST VIEWED ITEMS': 'MEST VISADE RÄTTER',
    'Top Rated Items': 'Högst betygsatta rätter',
    'TOP RATED ITEMS': 'HÖGST BETYGSATTA RÄTTER',
    'Waiter Requests': 'Servitörsanrop',
    'WAITER REQUESTS': 'SERVITÖRSANROP',
    'Total': 'Totalt',
    'TOTAL': 'TOTALT',
    'Order Status (30 days)': 'Orderstatus (30 dagar)',
    'ORDER STATUS (30 DAYS)': 'ORDERSTATUS (30 DAGAR)',
    'Recent Guest Feedback': 'Senaste gästrecensioner',
    'RECENT GUEST FEEDBACK': 'SENASTE GÄSTRECENSIONER',
    'Conversion Rate': 'Konverteringsgrad',
    'CONVERSION RATE': 'KONVERTERINGSGRAD',
    'of menu visitors placed an order': 'av menybesökarna lade en beställning',
    'Refreshed': 'Uppdaterad',
    'REFRESHED': 'UPPDATERAD',
    'Last 30 days': 'Senaste 30 dagarna',
    'LAST 30 DAYS': 'SENASTE 30 DAGARNA',
    'cash': 'kontant',
    'Cash': 'Kontant',
    'CASH': 'KONTANT',
    'bill': 'notan',
    'Bill': 'Notan',
    'BILL': 'NOTAN',
    'water': 'vatten',
    'Water': 'Vatten',
    'WATER': 'VATTEN',
    'service': 'service',
    'Service': 'Service',
    'SERVICE': 'SERVICE',
    'No interaction data yet': 'Inga interaktionsdata ännu',
    'No ratings collected yet': 'Inga betyg insamlade ännu',
    'No requests in period': 'Inga anrop under perioden',
    'No feedback collected yet': 'Ingen feedback insamlad ännu',
    'No menu views recorded yet': 'Inga menyvisningar registrerade ännu',
    'Dish Rating Breakdown': 'Betygsuppdelning per rätt',
    'Score rankings and menu item customer satisfaction metrics are exclusively available on MAX and Enterprise plans.': 'Rättsrankning och kundnöjdhet är exklusivt tillgängliga i MAX- och Enterprise-planer.',
    'Customer Sentiment & Reviews': 'Kundnöjdhet och recensioner',
    'Direct guest reviews, sentiment intelligence, and diner feedback feeds require a MAX or Enterprise subscription.': 'Direkta gästrevisioner och sentimentanalys kräver ett MAX- eller Enterprise-abonnemang.',
    'Reviews AI': 'Recensions-AI',
    'Guest Feedback': 'Gästfeedback',
    'Upgrade to unlock rating analytics →': 'Uppgradera för att låsa upp betygsanalys →',
    'Upgrade to Max': 'Uppgradera till Max',
    'UPGRADE TO MAX': 'UPPGRADERA TILL MAX',
    'MAX PLAN': 'MAX-PLAN',
    'No Data Yet': 'Inga data ännu',
    'Share your QR code to start collecting real venue insights.': 'Dela din QR-kod för att börja samla in verkliga verksamhetsinsikter.',
    'Revenue Analytics': 'Intäktsanalys',
    'REVENUE ANALYTICS': 'INTÄKTSANALYS',
    'Track your earnings, orders, and average ticket values over time.': 'Följ dina intäkter, beställningar och genomsnittliga kvitton över tid.',
    'Operational Velocity': 'Operativ hastighet',
    'OPERATIONAL VELOCITY': 'OPERATIV HASTIGHET',
    'Monitor guest traffic, popular menu items, and waiter assistance load.': 'Övervaka gästflöde, populära rätter och belastningen på serveringspersonalen.',
    'Upgrade to Pro': 'Uppgradera till Pro',
    'UPGRADE TO PRO': 'UPPGRADERA TILL PRO',
    'Loading Insights…': 'Läser in insikter…',
    'Loading Insights...': 'Läser in insikter...',
    'Telemetry Unavailable': 'Aktivitetsdata inte tillgänglig',
    'TELEMETRY UNAVAILABLE': 'AKTIVITETSDATA INTE TILLGÄNGLIG',
    'No Data Captured': 'Inga data insamlade',
    'NO DATA CAPTURED': 'INGA DATA INSAMLADE',
    'Share your QR code to start collecting enterprise-grade analytics in real-time.': 'Dela din QR-kod för att börja samla in besöks- och beställningsdata i realtid.',
    'INTELLIGENCE STUDIO': 'ANALYSCENTER',
    'Insights Engine Locked': 'Avancerad analys låst',
    'INSIGHTS ENGINE LOCKED': 'AVANCERAD ANALYS LÅST',
    'Advanced trend analysis, bulk governance commands, and data exports are reserved for': 'Avancerad trendanalys, massåtgärder och dataexport är reserverade för',
    'Unlock Pro Features': 'Lås upp Pro-funktioner',
    'UNLOCK PRO FEATURES': 'LÅS UPP PRO-FUNKTIONER',
    'MENU VIEWS': 'MENYVISNINGAR',
    'ITEM INTERACTIONS': 'MENYINTERAKTIONER',
    'GUEST RATING': 'GÄSTBETYG',
    'DELIVERY REVENUE': 'LEVERANSINTÄKTER',
    'AVERAGE DELIVERY ORDER': 'GENOMSNITTLIG LEVERANSORDER',
    'AVERAGE DELIVERY FEE': 'GENOMSNITTLIG LEVERANSAVGIFT',
    'INCLUDING FEES': 'INKLUSIVE AVGIFTER',
    'ZONE AVERAGE': 'ZONGENOMSNITT',
    'TRAFFIC PROJECTIONS': 'BESÖKSPROGNOSER',
    'TELEMETRY SIGNAL TRACKING': 'SPÅRNING AV AKTIVITETSSIGNALER',
    'VEGETARIAN': 'VEGETARISKT',
    'VEGAN': 'VEGANSKT',
    'SPICY': 'STARK',
    'GLUTEN-FREE': 'GLUTENFRITT',
    'Reset: All In-Stock': 'Återställ: Allt i lager',
    'Emergency Stop (All Out)': 'Nödstopp: Allt slutsålt',
    'No compliance issues': 'Inga problem upptäckta',
    'NO COMPLIANCE ISSUES': 'INGA PROBLEM UPPTÄCKTA',
    'detected in current menu': 'i aktuell meny',
    'DETECTED IN CURRENT MENU': 'I AKTUELL MENY',
    'Telemetry History (24h)': 'Aktivitetshistorik (24h)',
    'TELEMETRY HISTORY (24H)': 'AKTIVITETSHISTORIK (24H)',
    'Telemetry History (30d)': 'Aktivitetshistorik (30d)',
    'TELEMETRY HISTORY (30D)': 'AKTIVITETSHISTORIK (30D)',
    'Save': 'Spara',
    'Saved': 'Sparad',
    'Saving...': 'Sparar...',
    'Save Changes': 'Spara ändringar',
    'Save Settings': 'Spara inställningar',
    'Cancel': 'Avbryt',
    'Close': 'Stäng',
    'Create': 'Skapa',
    'Add': 'Lägg till',
    'Edit': 'Redigera',
    'Delete': 'Radera',
    'Remove': 'Ta bort',
    'Update': 'Uppdatera',
    'Refresh': 'Uppdatera',
    'Search': 'Sök',
    'Search...': 'Sök...',
    'Search items...': 'Sök objekt...',
    'Search categories...': 'Sök kategorier...',
    'Filter': 'Filtrera',
    'All': 'Alla',
    'Export': 'Exportera',
    'Import': 'Importera',
    'Download': 'Ladda ner',
    'Upload': 'Ladda upp',
    'Copy': 'Kopiera',
    'Copied': 'Kopierad',
    'Copied!': 'Kopierat!',
    'Preview': 'Förhandsgranska',
    'Open': 'Öppet',
    'View': 'Visa',
    'Manage': 'Hantera',
    'Configure': 'Konfigurera',
    'Enable': 'Aktivera',
    'Disable': 'Inaktivera',
    'Enabled': 'Aktiverad',
    'Disabled': 'Inaktiverad',
    'Active': 'Aktiv',
    'Inactive': 'Inaktiv',
    'Available': 'Tillgänglig',
    'Unavailable': 'Otillgänglig',
    'Visible': 'Synlig',
    'Hidden': 'Dold',
    'Required': 'Obligatorisk',
    'Optional': 'Valfritt',
    'Confirm': 'Bekräfta',
    'Back': 'Tillbaka',
    'Next': 'Nästa',
    'Done': 'Klart',
    'Upgrade': 'Uppgradera',
    'Sign Out': 'Logga ut',
    'Exit to Main Admin': 'Tillbaka till huvudpanelen',
    'Change Language': 'Byt språk',
    'Add Category': 'Lägg till kategori',
    'Add Menu Item': 'Lägg till menyobjekt',
    'Add Item': 'Lägg till objekt',
    'Add Ingredient': 'Lägg till ingrediens',
    'Add Department': 'Lägg till avdelning',
    'Delete Item': 'Ta bort objekt',
    'Delete Category': 'Ta bort kategori',
    'Upload Image': 'Ladda upp bild',
    'Upload Photo': 'Ladda upp foto',
    'Select Category': 'Välj kategori',
    'Select Department': 'Välj avdelning',
    'Add translation...': 'Lägg till översättning...',
    'Clear': 'Rensa',
    'Reset': 'Återställ',
    'Apply': 'Tillämpa',
    'Submit': 'Skicka',
    'Core & Operations': 'Drift & Servering',
    'Menu & Experience': 'Meny & Upplevelse',
    'Enterprise & Security': 'Hantering & Säkerhet',
    'System & Control': 'Inställningar & Hjälp',
    'Intelligence Studio': 'Analyscenter',
    'Venue Insights': 'Försäljningsrapporter',
    'Reservations': 'Bokningar',
    'Floor Plan': 'Bordskarta',
    'Live Waitlist': 'Väntelista',
    'Walk-Ins': 'Drop-in gäster',
    'Walk-ins': 'Drop-in gäster',
    'Departments': 'Avdelningar',
    'Service Hours': 'Servicetider',
    'Categories': 'Kategorier',
    'Menu Categories': 'Menykategorier',
    'Menu Items': 'Menyobjekt',
    'Items': 'Objekt',
    'Ingredients': 'Ingredienser',
    'Loyalty Program': 'Lojalitetsprogram',
    'Campaign Studio': 'Kampanjer & Erbjudanden',
    'Signage': 'TV-skärmar',
    'QR Code': 'QR-koder',
    'Kitchen Ops': 'Köksskärm',
    'Theme Engine': 'Design & Färger',
    'Governance': 'Hantering',
    'Audit Log': 'Aktivitetslogg',
    'API & Webhooks': 'Integrationer',
    'SSO & Security': 'Säkerhet & Inloggning',
    'Team': 'Personal',
    'Billing': 'Fakturering & Abonnemang',
    'SEO & Google': 'Google-sökbarhet',
    'Notifications': 'Aviseringar',
    'Support Center': 'Hjälpcenter',
    'Settings': 'Restauranginställningar',
    'Name': 'Namn',
    'Description': 'Beskrivning',
    'Price': 'Pris',
    'Status': 'Status',
    'Actions': 'Åtgärder',
    'Role': 'Roll',
    'Email': 'E-post',
    'Phone': 'Telefon',
    'Address': 'Adress',
    'City': 'Stad',
    'Currency': 'Valuta',
    'Language': 'Språk',
    'Theme': 'Tema',
    'General': 'Allmänt',
    'Total Revenue': 'Total omsättning',
    'Total Orders': 'Totalt antal beställningar',
    'Average Order': 'Genomsnittlig beställning',
    'Active Sessions': 'Aktiva bord',
    'Recent Orders': 'Senaste beställningar',
    'Live Orders': 'Livebeställningar',
    'Table': 'Bord',
    'Date': 'Datum',
    'Time': 'Tid',
    'Amount': 'Belopp',
    'Customer': 'Kund',
    'In Stock': 'I lager',
    'Out of Stock': 'Slut i lager',
    'Sold Out': 'Utsålt',
    'Dine In': 'Äta här',
    'Pickup': 'Hämta',
    'Delivery': 'Leverans',
    'Paid': 'Betald',
    'Unpaid': 'Obetald',
    'Pending': 'Väntande',
    'Preparing': 'Tillagas',
    'Ready': 'Redo',
    'Completed': 'Avslutad',
    'Cancelled': 'Avbruten',
    'Loading...': 'Laddar...',
    'No data': 'Inga data',
    'No results found': 'Inga resultat hittades',
    'No items found': 'Inga objekt hittades',
    'No translations defined yet': 'Inga översättningar angivna än',
    'Are you sure?': 'Är du säker?',
    'This action cannot be undone.': 'Denna åtgärd kan inte ångras.',
    'Settings saved successfully!': 'Inställningarna har sparats!',
    'Administrator': 'Administratör',
    'Manager': 'Chef',
    'Owner': 'Ägare',
    'Chef': 'Kock',
    'Waiter': 'Servitör',
    'Cashier': 'Kassör',
    'Type a command or search...': 'Skriv ett kommando eller sök...',
    'Command Palette': 'Kommandopalett',
    'Demand Velocity': 'Efterfrågans dynamik',
    'DEMAND VELOCITY': 'EFTERFRÅGANS DYNAMIK',
    'Top Performing Entities': 'Bäst presterande rätter',
    'TOP PERFORMING ENTITIES': 'BÄST PRESTERANDE RÄTTER',
    'Hourly Load Analysis': 'Belastningsanalys per timme',
    'HOURLY LOAD ANALYSIS': 'BELASTNINGSANALYS PER TIMME',
    'Bulk Command Center': 'Snabbhantering av menyer',
    'BULK COMMAND CENTER': 'SNABBHANTERING AV MENYER',
    'Governance Scan': 'Menykontroll',
    'GOVERNANCE SCAN': 'MENYKONTROLL',
    'No compliance issues detected in current menu': 'Inga problem upptäcktes i den aktuella menyn',
    'NO COMPLIANCE ISSUES DETECTED IN CURRENT MENU': 'INGA PROBLEM UPPTÄCKTES I DEN AKTUELLA MENYN',
    'Procedural Tagging': 'Automatisk taggning',
    'PROCEDURAL TAGGING': 'AUTOMATISK TAGGNING',
    'Name match...': 'Matcha namn...',
    '...Name match': 'Matcha namn...',
    'Healthy': 'Hälsosamt',
    'HEALTHY': 'HÄLSOSAMT',
    'Apply Tags': 'Tillämpa taggar',
    'APPLY TAGS': 'TILLÄMPA TAGGAR',
    'Global Price Shift': 'Allmän prisjustering',
    'GLOBAL PRICE SHIFT': 'ALLMÄN PRISJUSTERING',
    'Adjust': 'Justera',
    'ADJUST': 'JUSTERA',
    'Availability Override': 'Snabbhantering av tillgänglighet',
    'AVAILABILITY OVERRIDE': 'SNABBHANTERING AV TILLGÄNGLIGHET',
    'Reset: ALL IN-STOCK': 'Återställ: ALLT I LAGER',
    'RESET: ALL IN-STOCK': 'ÅTERSTÄLL: ALLT I LAGER',
    'EMERGENCY STOP (ALL OUT)': 'Nödstopp (ALLT SLUT I LAGER)',
    'Integrity Engine': 'System för menyintegritet',
    'INTEGRITY ENGINE': 'SYSTEM FÖR MENYINTEGRITET',
    'Automated governance monitors item visibility, pricing anomalies, and descriptive health across your entire venue ecosystem.': 'Detta system övervakar rätternas synlighet, prisavvikelser och beskrivningarnas kvalitet i hela din verksamhet.',
    'Inventory Audit Export': 'Exportera för inventering',
    'INVENTORY AUDIT EXPORT': 'EXPORTERA FÖR INVENTERING',
    'Generate a comprehensive menu performance and metadata snapshot for offline auditing.': 'Skapa en fullständig sammanställning av menyresultat och metadata för offline-granskning.',
    'Enterprise Data Export': 'Avancerad dataexport',
    'ENTERPRISE DATA EXPORT': 'AVANCERAD DATAEXPORT',
    'Generate and download comprehensive performance audits in industry-standard formats. Includes traffic flow, asset demand velocity, and conversion telemetry.': 'Skapa och ladda ner omfattande resultatrapporter i standardformat. Inkluderar gästflöde, efterfrågan på rätter och konvertering.',
    'JSON Snapshot': 'JSON-ögonblicksbild',
    'JSON SNAPSHOT': 'JSON-ÖGONBLICKSBILD',
    'Compile CSV Report': 'Skapa CSV-rapport',
    'COMPILE CSV REPORT': 'SKAPA CSV-RAPPORT',
    'Date Segment': 'Datumperiod',
    'DATE SEGMENT': 'DATUMPERIOD',
    'Inbound Views': 'Inkommande visningar',
    'INBOUND VIEWS': 'INKOMMANDE VISNINGAR',
    'Asset Interactions': 'Interaktioner med rätter',
    'ASSET INTERACTIONS': 'INTERAKTIONER MED RÄTTER',
    'Telemetry History': 'Aktivitetshistorik',
    'TELEMETRY HISTORY': 'AKTIVITETSHISTORIK',
    'Telemetry History (7d)': 'Aktivitetshistorik (7d)',
    'TELEMETRY HISTORY (7D)': 'AKTIVITETSHISTORIK (7D)',
    'Growth Trajectory': 'Tillväxttrend',
    'Your menu interaction frequency is trending upward in the current 7-day rolling window.': 'Interaktionsfrekvensen med din meny har en uppåtgående trend under de senaste 7 dagarna.',
    'Deep Analysis Engine': 'Fördjupad analys',
    'DEEP ANALYSIS ENGINE': 'FÖRDJUPAD ANALYS',
    'Asset Velocity': 'Rätternas popularitet',
    'ASSET VELOCITY': 'RÄTTERNAS POPULARITET',
    'Traffic Projections': 'Besöksprognoser',
    'Telemetry Signal Tracking': 'Spårning av aktivitetssignaler',
    'Service Hour Update': 'Uppdatering av servicetider',
    'Late Night Lounge Hours': 'Öppettider för nattlounge',
    'Bar & Lounge Hours': 'Bar- och loungeöppettider',
    'Breakfast & Brunch Hours': 'Frukost- och brunchtider',
    'Switch Location': 'Byt plats',
    'SWITCH LOCATION': 'BYT PLATS',
    'Global Admin': 'Huvudansvarig',
    'GLOBAL ADMIN': 'HUVUDANSVARIG',
    'Staff Access': 'Personalåtkomst',
    'STAFF ACCESS': 'PERSONALÅTKOMST',
    'Venue Overview': 'Platsöversikt',
    'VENUE OVERVIEW': 'PLATSÖVERSIKT',
    'Unified Telemetry Engine': 'Central aktivitetspanel',
    'UNIFIED TELEMETRY ENGINE': 'CENTRAL AKTIVITETSPANEL',
    'Pulse': 'Realtidspuls',
    'PULSE': 'REALTIDSPULS',
    'Insights': 'Insikter',
    'INSIGHTS': 'INSIKTER',
    'Reports': 'Rapporter',
    'REPORTS': 'RAPPORTER',
    'GOVERNANCE': 'HANTERING',
    'Multi-Venue': 'Flera enheter',
    'MULTI-VENUE': 'FLERA ENHETER',
    'Menu Views': 'Menyvisningar',
    'Item Interactions': 'Interaktioner med rätter',
    'Guest Rating': 'Gästbetyg',
    'Revenue (7d)': 'Omsättning (7d)',
    'Revenue (24h)': 'Omsättning (24t)',
    'Revenue (30d)': 'Omsättning (30d)',
    'Delivery orders (7d)': 'Leveransbeställningar (7d)',
    'Delivery orders (24h)': 'Leveransbeställningar (24t)',
    'Delivery orders (30d)': 'Leveransbeställningar (30d)',
    'Delivery revenue': 'Leveransintäkter',
    'Average delivery order': 'Genomsnittlig leveransorder',
    'Average delivery fee': 'Genomsnittlig leveransavgift',
    'Zone average': 'Zongensnitt',
    'Including fees': 'Inklusive avgifter',
    'No deliveries': 'Inga leveranser',
    'No orders': 'Inga beställningar',
    'No reviews': 'Inga recensioner',
    'Live': 'Live',
    'LIVE': 'LIVE',
    'reviews': 'recensioner',
    'orders': 'beställningar',
    'deliveries': 'leveranser',
    'of orders': 'av beställningar',
    'rate': 'frekvens',
    'Recent Activity': 'Senaste aktivitet',
    'Real-time Venue Telemetry': 'Platsaktivitet i realtid',
    'REAL-TIME VENUE TELEMETRY': 'PLATSAKTIVITET I REALTID',
    'Rapid Pulse View': 'Snabb livevy',
    'Anonymous Guest': 'Anonym gäst',
    'scanned menu': 'skannade menyn',
    'viewed item': 'visade rätten',
    'interacted with': 'interagerade med',
    'placed order': 'lade en beställning',
    'searched menu': 'sökte i menyn',
    'Main Landing': 'Startsida',
    'Trace': 'Verifierad',
    'TRACE': 'VERIFIERAD',
    'Gaining Telemetry...': 'Hämtar aktivitetsdata...',
    'No recent traffic detected': 'Ingen aktuell aktivitet upptäckt',
    'Activities will appear here as guests scan your QR code': 'Aktiviteter visas här så snart gäster skannar din QR-kod',
    'Analyze All Logs': 'Analysera hela loggen',
    'Just now': 'Just nu',
    'less than a minute ago': 'mindre än en minut sedan',
    '1 minute ago': 'för 1 minut sedan',
    '1 hour ago': 'för 1 timme sedan',
    'about 1 hour ago': 'ungefär 1 timme sedan',
    '1 day ago': 'för 1 dag sedan',
    'DAY AGO 1': 'för 1 dag sedan',
    '2 days ago': 'för 2 dagar sedan',
    'DAY AGO 2': 'för 2 dagar sedan',
    '3 days ago': 'för 3 dagar sedan',
    'DAY AGO 3': 'för 3 dagar sedan',
    '4 days ago': 'för 4 dagar sedan',
    'DAY AGO 4': 'för 4 dagar sedan',
    '5 days ago': 'för 5 dagar sedan',
    'DAY AGO 5': 'för 5 dagar sedan',
    '6 days ago': 'för 6 dagar sedan',
    'DAY AGO 6': 'för 6 dagar sedan',
    '7 days ago': 'för 7 dagar sedan',
    'DAY AGO 7': 'för 7 dagar sedan',
    'about 1 month ago': 'ungefär 1 månad sedan',
    'Monday': 'Måndag',
    'Tuesday': 'Tisdag',
    'Wednesday': 'Onsdag',
    'Thursday': 'Torsdag',
    'Friday': 'Fredag',
    'Saturday': 'Lördag',
    'Sunday': 'Söndag',
    'Mon': 'Mån',
    'Tue': 'Tis',
    'Wed': 'Ons',
    'Thu': 'Tor',
    'Fri': 'Fre',
    'Sat': 'Lör',
    'Sun': 'Sön',
    'Opening Hours': 'Öppettider',
    'Start Time': 'Starttid',
    'End Time': 'Sluttid',
    'Opening Time': 'Öppningstid',
    'Closing Time': 'Stängningstid',
    'All Day': 'Hela dagen',
    'Closed': 'Stängt',
    'Active Days': 'Aktiva dagar',
    'Active Schedule': 'Aktivt schema',
    'Always Available': 'Alltid tillgänglig',
    'Always Visible': 'Alltid synlig',
    'New Category': 'Ny kategori',
    'Category': 'Kategori',
    'Category Name': 'Kategorinamn',
    'Category Description': 'Kategoribeskrivning',
    'Display Order': 'Visningsordning',
    'Item Name': 'Rättens namn',
    'Item Description': 'Beskrivning av rätten',
    'Base Price': 'Grundpris',
    'Preparation Time': 'Tillagningstid',
    'Calories': 'Kalorier',
    'Featured Item': 'Utvald rätt',
    'Chef\'s Recommendation': 'Köksmästarens val',
    'Spicy': 'Stark',
    'Vegetarian': 'Vegetarisk',
    'Vegan': 'Vegansk',
    'Gluten-Free': 'Glutenfri',
    'Halal': 'Halal',
    'Kosher': 'Kosher',
    'Contains Nuts': 'Innehåller nötter',
    'Dairy-Free': 'Mjölkfri',
    'Allergen Exclusions': 'Allergener',
    'Table Number': 'Bordsnummer',
    'Assigned Table': 'Tilldelat bord',
    'Seats': 'Sittplatser',
    'Capacity': 'Kapacitet',
    'Indoor': 'Inomhus',
    'Outdoor': 'Utomhus',
    'Terrace': 'Terrass',
    'Bar': 'Bar',
    'Add Table': 'Lägg till bord',
    'Edit Table': 'Redigera bord',
    'Delete Table': 'Ta bort bord',
    'Bookings': 'Bokningar',
    'Add to Waitlist': 'Lägg till på väntelistan',
    'Guest Name': 'Gästens namn',
    'Party Size': 'Antal personer',
    'Arrival Time': 'Ankomsttid',
    'Special Requests': 'Särskilda önskemål',
    'Confirmed': 'Bekräftad',
    'No Show': 'Uteblev',
    'Add Staff Member': 'Lägg till personal',
    'Invite Staff': 'Bjud in personal',
    'Email Address': 'E-postadress',
    'Phone Number': 'Telefonnummer',
    'Download QR Code': 'Ladda ner QR-kod',
    'Print Table Stands': 'Skriv ut bordsryttare',
    'Today': 'Idag',
    'Yesterday': 'Igår',
    'This Week': 'Denna vecka',
    'This Month': 'Denna månad',
    'Last 30 Days': 'Senaste 30 dagarna',
    'Access Denied': 'Åtkomst nekad',
    'Advanced Settings': 'Avancerade inställningar',
    'Change Image': 'Byt bild',
    'Remove Image': 'Ta bort bild',
    'Announcement Banner': 'Meddelandeband',
    'Please fill in all required fields.': 'Vänligen fyll i alla obligatoriska fält.',
    'Changes saved successfully': 'Ändringarna sparades',
    '(You)': '(Du)',
    'AI Auto-Tag': 'AI-autotaggning',
    'Analyzing:': 'Analyserar:',
    'Apple (Fruit/Vegan)': 'Äpple (Frukt/Vegansk)',
    'Cancel editing': 'Avbryt redigering',
    'Capacity:': 'Kapacitet:',
    'Carrot (Veggie)': 'Morot (Grönsak)',
    'CircleDot (Organic)': 'Cirkelpunkt (Ekologiskt)',
    'Coffee (Caffeine)': 'Kaffe (Koffein)',
    'Configuration': 'Konfiguration',
    'Copy verification record name': 'Kopiera postnamn för verifiering',
    'Copy verification record value': 'Kopiera postvärde för verifiering',
    'Critical exception intercepted': 'Kritiskt systemfel upptäckt',
    'Kernel Panic': 'Systemfel',
    'DNA (Organic/Bio)': 'DNA (Ekologiskt/Bio)',
    'Egg (Ovo/Allergen)': 'Ägg (Ovo/Allergen)',
    'Empty': 'Tom',
    'Engraving...': 'Genererar...',
    'Flame (Spicy)': 'Flamma (Starkt)',
    'Free': 'Gratis',
    'From': 'Från',
    'Generating': 'Genererar',
    'Governance & Traceability Studio': 'Verksamhets- och aktivitetscenter',
    'Greet': 'Välkommen',
    'Heart (Healthy)': 'Hjärta (Hälsosamt)',
    'In-App': 'I appen',
    'Infinity SVG': 'Vektor-SVG (Oändlig)',
    'Leaf (Default)': 'Blad (Standard)',
    'MAX Feature': 'Enterprise-funktion',
    'Milk (Dairy)': 'Mjölk (Mejeri)',
    'Network': 'Nätverk',
    'Notify SMS': 'SMS-avisering',
    'Order': 'Beställning',
    'Original': 'Original',
    'Overview': 'Översikt',
    'PRO: 4 PREDEFINED ROLES': 'Pro: 4 standardroller',
    'Shield (Safety)': 'Sköld (Säkerhet)',
    'Soup (Warm/Soup)': 'Soppa (Varm/Soppa)',
    'Standard Pro View': 'Standardvy',
    'Star': 'Stjärna',
    'Synchronized with Venue Identity': 'Anpassad efter verksamhetens profil',
    'Synchronizing Security Context...': 'Upprättar säker anslutning...',
    'Targeted Timing': 'Servicetider',
    'Targeting': 'Gäller för',
    'To': 'Till',
    'Toggle Navigation': 'Växla navigering',
    'Venue': 'Verksamhet',
    'Venue Governance Suite': 'Verksamhetshantering och inställningar',
    'Volume:': 'Volym:',
    'WHITEBOARD NOTE...': 'Anteckning till personalen...',
    'Warning (Alert)': 'Varning (Larm)',
    'Wheat (Flour/Gluten)': 'Vete (Mjöl/Gluten)',
    'Wine (Alcohol)': 'Vin (Alkohol)',
    'YouTube/Vimeo/Direct Link': 'Videolänk (YouTube, Vimeo eller MP4)',
    'Your name': 'Ditt namn',
    '2. Table Number': '2. Bordsnummer',
    'for Morocco or': 'för Marocko eller',
    'in': 'i',
    'to': 'till',
    'e.g. 12 or Patio-1': 't.ex. 12 eller Uteservering-1',
    'e.g. Handmade Fettuccine, Black Winter Truffle, 36-Month Aged Parmigiano...': 't.ex. Handgjord fettuccine, svart vintertryffel, 36 månaders parmesan...',
    'e.g. Head Sommelier & Wine Lead': 't.ex. Chefsommelier',
    'e.g. London': 't.ex. London',
    'e.g. SOMMELIER, HOST, BARISTA': 't.ex. SOMMELIER, VÄRD, BARISTA',
    'e.g. Skyline Lounge': 't.ex. Skyline Lounge',
    'e.g. TRUFFLE OIL': 't.ex. TRYFFELOLJA',
    'e.g. The Grand Cafe': 't.ex. Stora Caféet',
    'e.g. Window preference, high chair': 't.ex. Gärna vid fönstret, barnstol',
    'Copied to clipboard!': 'Kopierat till urklipp!',
    'Copy failed.': 'Kopiering misslyckades.',
    'Link copied!': 'Länk kopierad!',
    'Logo uploaded': 'Logotyp uppladdad',
    'Cover image uploaded': 'Omslagsbild uppladdad',
    'Profile saved and synced successfully': 'Profilen har sparats och synkroniserats',
    'Category Created': 'Kategori skapad',
    'Category Updated': 'Kategori uppdaterad',
    'Category Deleted': 'Kategori raderad',
    'Item Created': 'Artikel skapad',
    'Item Updated': 'Artikel uppdaterad',
    'Item Deleted': 'Artikel raderad',
    'Menu Published': 'Meny publicerad',
    'Delete Category?': 'Radera kategori?',
    'Delete Menu Item?': 'Radera denna artikel?',
    'Delete Ingredient?': 'Radera denna ingrediens?',
    'Delete Service Period?': 'Radera serviceperiod?',
    'Remove team member?': 'Ta bort teammedlem?',
    'Cancel invitation?': 'Avbryt inbjudan?',
    'Revoke API key?': 'Återkalla API-nyckel?',
    'Delete webhook endpoint?': 'Radera webhook-slutpunkt?',
    'Clear all notifications?': 'Rensa alla aviseringar?',
    'Disconnect Stripe account?': 'Koppla från Stripe-konto?',
    'Disconnect Google Business Profile?': 'Koppla från Google Företagsprofil?',
    'Campaigns published successfully!': 'Kampanjer har publicerats!',
    'Changes successfully synchronized with Google Business Profile!': 'Ändringarna har synkroniserats med din Google Företagsprofil!',
    'Google Business Profile successfully connected!': 'Google Företagsprofil har anslutits!',
    'Google Business Profile disconnected.': 'Google Företagsprofil har kopplats från.',
    'Google Post draft saved successfully!': 'Utkast till Google-inlägg har sparats!',
    'Post draft deleted.': 'Inläggsutkast raderat.',
    'Review response published successfully!': 'Svaret på recensionen har publicerats!',
    'SEO configuration saved successfully!': 'SEO-inställningarna har sparats!',
    'SEO metadata translated across all languages!': 'SEO-metadata har översatts till alla språk!',
    'Venue profile & bio translated across all languages!': 'Verksamhetsprofil och beskrivning har översatts till alla språk!',
    'Reservation configuration saved successfully': 'Bokningsinställningarna har sparats',
    'Stripe settings saved successfully.': 'Stripe-inställningarna har sparats.',
    'Print-Ready PDF downloaded successfully': 'Tryckfärdig PDF har laddats ner',
    'Vector QR PDF downloaded successfully': 'Vektor-QR PDF har laddats ner',
    'Enterprise 300 DPI Pre-Press Package generated successfully': 'Tryckpaket (300 DPI) har genererats',
    'At least one language must be enabled.': 'Minst ett språk måste vara aktiverat.',
    'Enter a non-zero percentage before adjusting prices.': 'Ange en procentsats skild från noll innan du justerar priser.',
    'Please enter a Venue Name before translating.': 'Ange ett verksamhetsnamn innan du översätter.',
    'Please enter an item name before running dietary and allergen analysis.': 'Ange ett artikelnamn innan du kör allergenanalys.',
    'Please write a reply before submitting.': 'Skriv ett svar innan du skickar.',
    'Table 3 Called Waiter': 'Bord 3 kallade på servitör',
    'Table Call Bell': 'Bordsklocka',
    'Waiter Calls': 'Servitörsanrop',
    'Waiter Dispatch & Calls': 'Servitörsdirigering och anrop',
    'Kitchen Display & Whiteboard': 'Köksskärm och anslagstavla',
    'Customer Orders': 'Kundbeställningar',
    'Bakery': 'Bageri / Konditori',
    'Bar / Pub': 'Bar / Pub',
    'Café / Coffee Shop': 'Café / Kaffebar',
    'Fast Food': 'Snabbmat',
    'Gourmet / Fine Dining': 'Gourmet / Fine dining',
    'Hotel': 'Hotell',
    'General Local Business': 'Lokal verksamhet',
    'Classic': 'Klassisk',
    'Clean': 'Ren',
    'Modern': 'Modern',
    'Elegant': 'Elegant',
    'Energetic': 'Energisk',
    'Glass': 'Glaseffekt',
    'Playful & Casual': 'Lekfull och avslappnad',
    'Sophisticated & Gourmet': 'Sofistikerad och gourmet',
    'Warm & Welcoming': 'Varm och välkomnande',
    'Executive': 'Exklusiv',
    'Upgrade required: Multi-language menus are available on the Pro plan.': 'Uppgradering krävs: Flerspråkiga menyer finns i Pro-planen.',
    'Upgrade required: Ordering and Waiter Requests are not available on the Starter plan.': 'Uppgradering krävs: Beställningar och servitörsanrop är inte tillgängliga i Starter-planen.',
    'Vector SVG export is a Pro & Max feature. Please upgrade to export scalable vectors.': 'Vektor-SVG-export är en Pro & Max-funktion. Uppgradera för att exportera vektorer.',
    'Print-Ready PDF export is a Pro & Max feature. Please upgrade to export PDF files.': 'Tryckfärdig PDF-export är en Pro & Max-funktion. Uppgradera för att exportera PDF-filer.',
    'PRO plan is limited to 3 active campaigns. Pause another campaign or upgrade to MAX for unlimited campaigns.': 'Pro-planen är begränsad till 3 aktiva kampanjer. Pausa en annan kampanj eller uppgradera till MAX.',
    'Starter plan includes up to 20 tables (Tables 1–20). Upgrade to PRO for unlimited tables.': 'Starter-planen inkluderar upp till 20 bord (Bord 1–20). Uppgradera till PRO för obegränsat antal bord.',
    'Starter plan supports numeric tables (1–20). Custom table names (e.g., Patio, VIP) require PRO.': 'Starter-planen stöder numrerade bord (1–20). Anpassade bordsnamn (t.ex. Uteservering, VIP) kräver PRO.',
    'Add Menu Categories & Items': 'Lägg till kategorier och artiklar',
    'Add Menu Items': 'Lägg till menyartiklar',
    'Organize your menu sections and food items.': 'Strukturera dina menysektioner och maträtter.',
    'Add and manage your food & beverage offerings.': 'Lägg till och hantera ditt utbud av mat och dryck.',
    'Customize Venue Branding': 'Anpassa verksamhetens varumärke',
    'Set your brand colors, logo, and presentation.': 'Ställ in dina varumärkesfärger, logotyp och presentation.',
    'Get Your QR Code & Go Live': 'Hämta din QR-kod och gå live',
    'Download table QR codes and preview your live menu.': 'Ladda ner bords-QR-koder och förhandsgranska din livemeny.',
    'Account & Venue Created Successfully!': 'Konto och verksamhet har skapats!',
    '⚡ Off-Peak Hours': '⚡ Lugnare tider',
    '🍽️ Lunch Crowd': '🍽️ Lunchrusning',
    '🚀 Weekend Rush': '🚀 Helgrush',
    '🌟 Seasonal Special': '🌟 Säsongsaktuellt',
    '📣 Engagement Buzz': '📣 Engagemang och omtal',
    'Festive & Energetic': 'Festlig och energisk',
    'Urgent & High-Conversion': 'Tidsbegränsad och säljande',
    'Generated 3 tailored campaign strategies': '3 skräddarsydda kampanjstrategier har tagits fram',
    'Could not generate campaign ideas. Please try again.': 'Kunde inte skapa kampanjidéer. Försök igen.',
    'Venue Themes': 'Verksamhetsteman',
    'Choose the character of your public menu': 'Välj stil och karaktär för din publika meny',
    'Glassmorphism & Depth': 'Glaseffekt och djup',
    'Independent XML Sitemap': 'Oberoende XML-webbplatskarta',
    'Structured Schema & Sync': 'Strukturerad data och synkronisering',
    'Improve how your venue appears on Google and in local search results.': 'Förbättra hur din verksamhet visas på Google och i lokala sökresultat.',
    'Terms of Service': 'Användarvillkor',
    'Confirm your email': 'Bekräfta din e-postadress',
    'Check your spam / junk folder': 'Kontrollera din skräppostmapp',
    'Commercial pre-press print production package': 'Professionellt tryckpaket för tryckeri',
    'Over-bleed safety margin': 'Utfallssäkerhetsmarginal',
    'Crop Marks': 'Skärmärken',
    'Guillotine cutting guides': 'Skärguider för giljotin',
    'Global Banners': 'Globala banners',
    'Featured Items': 'Utvalda rätter',
    'AI Assistant': 'AI-assistent',
    'Custom Role Schemas': 'Anpassade rollscheman',
    'Notification Preferences': 'Aviseringsinställningar',
    'Preferences saved automatically': 'Inställningarna sparades automatiskt',
    'Menu Extractor (PDF & Images)': 'Menyextraherare (PDF & bilder)',
    'Multi-Language Translation': 'Flerspråkig översättning',
    'Start Menu Translation': 'Starta menyöversättning',
    'Menu Items Hub': 'Menyartikelcenter',
    'Open Menu Items': 'Öppna menyartiklar',
    'Enterprise Traceability': 'Spårbarhet och historik',
    'Full History': 'Fullständig historik',
    'Billing & Plans': 'Fakturering och planer',
    'Subscription Governance': 'Abonnemangshantering',
    'Platform Usage': 'Plattformsnivå och användning',
    'Menu Page Views': 'Sidvisningar för menyn',
    'Enterprise Identity': 'Företagsidentitet',
    'Access Governance': 'Behörighetsstyrning',
    'Required Fields *': 'Obligatoriska fält *',
    'Enabled Languages': 'Aktiverade språk',
    'Venue Info & Bio Localizations': 'Översättningar av verksamhetsinfo och presentation',
    'Public Address': 'Publik adress',
    'Public URL Slug': 'Publik URL-identifierare',
    'Contact Phone': 'Kontakttelefon',
    'Business Address': 'Verksamhetsadress',
    'No Categories Yet': 'Inga kategorier ännu',
    'No Custom Roles Configured': 'Inga anpassade roller konfigurerade',
    'No Google reviews retrieved yet': 'Inga Google-recensioner hämtade ännu',
    'No Guests Found': 'Inga gäster hittades',
    'No Menu Items Yet': 'Inga menyartiklar ännu',
    'No Promotions Configured': 'Inga kampanjer konfigurerade',
    'No Service Periods Yet': 'Inga serviceperioder ännu',
    'No Support Tickets': 'Inga supportärenden',
    'No Tables on Floor Plan': 'Inga bord på bordskartan',
    'No Team Members Found': 'Inga teammedlemmar hittades',
    'No Upcoming Events': 'Inga kommande evenemang',
    'No Walk-Ins Recorded': 'Inga drop-in-gäster registrerade',
    'No items are available right now': 'Inga artiklar är tillgängliga just nu',
    'No notifications found': 'Inga aviseringar hittades',
    'No reservations matching your filters': 'Inga bokningar matchar dina filter',
    'No tables currently occupied': 'Inga bord är upptagna för tillfället',
    'No venue selected': 'Ingen verksamhet vald',
    'Waitlist': 'Väntelista',
    'Waitlist is Empty': 'Väntelistan är tom',
    'Waves (Gluten)': 'Ax (Gluten)',
    'We sent a temporary verification code to': 'Vi har skickat en tillfällig verifieringskod till',
    'Website URL': 'Webbplatsadress',
    'What the customer receives': 'Vad kunden får',
    'White-label control is available on eligible plans.': 'White-label-funktion är tillgänglig i utvalda planer.',
    'Why this works:': 'Varför detta fungerar:',
    'WiFi Broadcast': 'Gäst-Wi-Fi-delning',
    'WiFi Password': 'Wi-Fi-lösenord',
    'Window seat, dietary request, birthday celebration...': 'Bord vid fönstret, kostönskemål, födelsedagsfirande...',
    'Your Published Response': 'Ditt publicerade svar',
    'Your Venues': 'Dina verksamheter',
    '🎉 Ready to publish!': '🎉 Redo att publiceras!',
    'e.g. Grill, Salad, Pastry': 't.ex. Grill, Sallader, Bakverk',
    'e.g. Guest called, emergency': 't.ex. Gästen ringde, nödsituation',
    'e.g. Italian, Moroccan, Burgers': 't.ex. Italienskt, Marockanskt, Burgare',
    'e.g. Mains, Pasta, Starters': 't.ex. Varmrätter, Pasta, Förrätter',
    'e.g. Signature Truffle Pasta': 't.ex. Signaturpasta med tryffel',
    'e.g., Afternoon Coffee Special, Happy Hour 15%': 't.ex. Eftermiddagsfika, Happy Hour 15%',
    'e.g., Breakfast, Happy Hour': 't.ex. Frukost, Happy Hour',
    'e.g., Join us for Live Jazz every Friday night at 8 PM!': 't.ex. Upplev livejazz varje fredagskväll kl. 20:00!',
  },
};

// Canonical index to map any translated text back to its original English phrase
const canonicalMap = new Map<string, string>();

for (const lang of Object.keys(adminPhraseTranslations) as Language[]) {
  const dict = adminPhraseTranslations[lang];
  for (const [canonicalEn, translation] of Object.entries(dict)) {
    if (!canonicalMap.has(canonicalEn.toLowerCase())) {
      canonicalMap.set(canonicalEn.toLowerCase(), canonicalEn);
    }
    if (translation && !canonicalMap.has(translation.toLowerCase())) {
      canonicalMap.set(translation.toLowerCase(), canonicalEn);
    }
  }
}

/**
 * Resolves any translated venue admin phrase back to its canonical English key.
 * Enables clean bidirectional translations when switching languages dynamically.
 */
export const getCanonicalEnglishPhrase = (phrase: string): string => {
  if (!phrase) return '';
  const trimmed = phrase.trim();
  return canonicalMap.get(trimmed.toLowerCase()) || trimmed;
};
